import React, { Children, isValidElement, useState, useMemo, useEffect, cloneElement, useLayoutEffect, useRef, forwardRef, useReducer, createRef, useCallback, memo, createElement } from 'react';
import styled, { css } from 'styled-components';
import { createPortal } from 'react-dom';
import { imageBuilders } from 'artisn/builders';
import { getProductTotals } from 'artisn/products';

const AccordionStyled = styled.div`
  .Accordion {
  }
`;

const PocketStyled = styled.div`
  .Pocket {
    &__header {
      display: flex;
      flex-wrap: nowrap;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;

      &__title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    &__content {
      display: none;

      &--open {
        display: block;
      }
    }

    &__icon {
      transform: rotate(0);
      transition: transform 0.4s ease;

      &--rotate {
        transform: rotate(180deg);
      }
    }
  }
`;

const getChildren = (children) => {
  const newChildren = Children.map(children, (child) => child);
  if (!newChildren)
    return [];
  return newChildren.filter((child) => !!child);
};
const defaultFunction = () => {
};
const defaultObject = {};
const defaultPromises = () => new Promise(defaultFunction);
const filteredChildren = (children, type) => Children.map(children, (child) => {
  if (!isValidElement(child))
    return;
  if ((child == null ? void 0 : child.type) !== type)
    return;
  return child;
});
const createPortalDOMNode = (name, id) => {
  if (typeof window === "undefined")
    return null;
  const newPortalNode = document.createElement("div");
  const portalNodeName = name ? `-${name}` : "";
  const portalNodeId = id != null ? id : `artisn-ui${portalNodeName}-${new Date().getTime()}`;
  newPortalNode.setAttribute("id", portalNodeId);
  document.body.appendChild(newPortalNode);
  return newPortalNode;
};

function _extends$L() { _extends$L = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$L.apply(this, arguments); }
var ChevronBottomSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$L({
  width: "10",
  height: "6",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M0 .815L.786 0 5 4.37 9.214 0 10 .815 5 6 0 .815z",
  fill: "#111"
})));

const Pocket = (props) => {
  const { className, children, defaultOpened, isOpened: forceOpened } = props;
  const { title, icon, onChange = defaultFunction } = props;
  const [opened, setOpened] = useState(defaultOpened);
  const selectedIsOpened = forceOpened != null ? forceOpened : opened;
  const contentExtraClass = selectedIsOpened ? "Pocket__content--open" : "";
  const contentClasses = `Pocket__content ${contentExtraClass}`;
  const iconClass = !icon ? "Pocket__icon" : "";
  const iconClassRotate = selectedIsOpened ? "Pocket__icon--rotate" : "";
  const Icon = icon != null ? icon : ChevronBottomSVG;
  const onClick = (opened2) => {
    setOpened(opened2);
    onChange(opened2);
  };
  return /* @__PURE__ */ React.createElement(PocketStyled, {
    className: `Pocket ${className}`
  }, /* @__PURE__ */ React.createElement("header", {
    className: "Pocket__header",
    onClick: () => onClick(!selectedIsOpened)
  }, /* @__PURE__ */ React.createElement("div", {
    className: "Pocket__header__title"
  }, title), /* @__PURE__ */ React.createElement(Icon, {
    className: `${iconClass} ${iconClassRotate}`
  })), children ? /* @__PURE__ */ React.createElement("div", {
    className: contentClasses
  }, children) : null);
};
Pocket.defaultProps = {
  className: "",
  onChange: defaultFunction
};

var __defProp$I = Object.defineProperty;
var __defProps$v = Object.defineProperties;
var __getOwnPropDescs$v = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$I = Object.getOwnPropertySymbols;
var __hasOwnProp$I = Object.prototype.hasOwnProperty;
var __propIsEnum$I = Object.prototype.propertyIsEnumerable;
var __defNormalProp$I = (obj, key, value) => key in obj ? __defProp$I(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$I = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$I.call(b, prop))
      __defNormalProp$I(a, prop, b[prop]);
  if (__getOwnPropSymbols$I)
    for (var prop of __getOwnPropSymbols$I(b)) {
      if (__propIsEnum$I.call(b, prop))
        __defNormalProp$I(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$v = (a, b) => __defProps$v(a, __getOwnPropDescs$v(b));
const Accordion = (props) => {
  const { children, single, onChange = defaultFunction } = props;
  const { defaultOpenedIds = [], className } = props;
  const [openedSections, setOpenedSections] = useState(defaultOpenedIds);
  const newChildren = useMemo(() => {
    var _a;
    return (_a = filteredChildren(children, Pocket)) != null ? _a : [];
  }, [children]);
  const changeHandler = (id, opened) => {
    setOpenedSections((prev) => {
      if (single && opened) {
        return [id];
      } else if (single && !opened) {
        return [];
      } else if (!single && opened) {
        return [...prev, id];
      }
      return prev.filter((section) => section !== id);
    });
  };
  useEffect(() => {
    onChange(openedSections);
  }, [onChange, openedSections]);
  useEffect(() => {
    const original = newChildren.map((item, index) => {
      var _a;
      return (_a = item.props.id) != null ? _a : index;
    });
    const unique = [...new Set(original)];
    if (original.length === unique.length)
      return;
    console.warn("More than 1 of the sections uses the same key, make sure each section has a unique key");
  }, [newChildren]);
  return /* @__PURE__ */ React.createElement(AccordionStyled, {
    className: `Accordion ${className}`
  }, newChildren.map((child, index) => {
    var _a;
    const originalProps = __spreadValues$I({}, child.props);
    delete originalProps.isOpened;
    delete originalProps.defaultOpened;
    delete originalProps.onChange;
    const id = (_a = originalProps.id) != null ? _a : index;
    const defaultOpenedId = defaultOpenedIds[0];
    const filteredOpenedIds = single ? [defaultOpenedId] : defaultOpenedIds;
    const OpenedSection = openedSections[0];
    const filteredOpenedSections = single ? [OpenedSection] : openedSections;
    const props2 = __spreadProps$v(__spreadValues$I({}, originalProps), {
      defaultOpened: filteredOpenedIds.includes(id),
      isOpened: filteredOpenedSections.includes(id),
      onChange: (opened) => changeHandler(id, opened)
    });
    return cloneElement(child, props2);
  }));
};
Accordion.Section = Pocket;
Accordion.defaultProps = {
  className: "",
  defaultOpenedIds: [],
  onChange: defaultFunction
};

const BreadcrumbStyled = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;

  .Breadcrumb {
  }
`;

const BreadcrumbItemStyled = styled.a`
  font-size: 16px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  .BreadcrumbItem {
  }
`;

const BreadcrumbItem = (props) => {
  const { children, className, onClick } = props;
  const onClickHandler = (event) => {
    event.preventDefault();
    onClick == null ? void 0 : onClick(event);
  };
  return /* @__PURE__ */ React.createElement(BreadcrumbItemStyled, {
    className: `BreadcrumbItem ${className}`,
    onClick: onClickHandler
  }, children);
};
BreadcrumbItem.defaultProps = {
  className: ""
};

const addSeparator = (children, separator) => {
  if (!Array.isArray(children))
    return children;
  const newChildren = [];
  children.forEach((child, index) => {
    newChildren.push(child);
    if (index === children.length - 1) {
      return;
    }
    newChildren.push(separator);
  });
  if (!isValidElement(separator)) {
    return newChildren;
  }
  return Children.map(newChildren, (child, index) => {
    if (!isValidElement(child)) {
      return child;
    }
    return cloneElement(child, {
      key: index
    });
  });
};

const Breadcrumb = (props) => {
  const { children, className, separator } = props;
  return /* @__PURE__ */ React.createElement(BreadcrumbStyled, {
    className: `Breadcrumb ${className}`
  }, addSeparator(filteredChildren(children, BreadcrumbItem), separator));
};
Breadcrumb.defaultProps = {
  separator: "/",
  className: ""
};
Breadcrumb.Item = BreadcrumbItem;

function _extends$K() {
  _extends$K = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$K.apply(this, arguments);
}

function _assertThisInitialized$2(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _setPrototypeOf$2(o, p) {
  _setPrototypeOf$2 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf$2(o, p);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf$2(subClass, superClass);
}

function _getPrototypeOf$2(o) {
  _getPrototypeOf$2 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf$2(o);
}

function _isNativeFunction(fn) {
  return Function.toString.call(fn).indexOf("[native code]") !== -1;
}

function _isNativeReflectConstruct$1() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _construct(Parent, args, Class) {
  if (_isNativeReflectConstruct$1()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf$2(instance, Class.prototype);
      return instance;
    };
  }

  return _construct.apply(null, arguments);
}

function _wrapNativeSuper(Class) {
  var _cache = typeof Map === "function" ? new Map() : undefined;

  _wrapNativeSuper = function _wrapNativeSuper(Class) {
    if (Class === null || !_isNativeFunction(Class)) return Class;

    if (typeof Class !== "function") {
      throw new TypeError("Super expression must either be null or a function");
    }

    if (typeof _cache !== "undefined") {
      if (_cache.has(Class)) return _cache.get(Class);

      _cache.set(Class, Wrapper);
    }

    function Wrapper() {
      return _construct(Class, arguments, _getPrototypeOf$2(this).constructor);
    }

    Wrapper.prototype = Object.create(Class.prototype, {
      constructor: {
        value: Wrapper,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
    return _setPrototypeOf$2(Wrapper, Class);
  };

  return _wrapNativeSuper(Class);
}

// based on https://github.com/styled-components/styled-components/blob/fcf6f3804c57a14dd7984dfab7bc06ee2edca044/src/utils/error.js

/**
 * Parse errors.md and turn it into a simple hash of code: message
 * @private
 */
var ERRORS = {
  "1": "Passed invalid arguments to hsl, please pass multiple numbers e.g. hsl(360, 0.75, 0.4) or an object e.g. rgb({ hue: 255, saturation: 0.4, lightness: 0.75 }).\n\n",
  "2": "Passed invalid arguments to hsla, please pass multiple numbers e.g. hsla(360, 0.75, 0.4, 0.7) or an object e.g. rgb({ hue: 255, saturation: 0.4, lightness: 0.75, alpha: 0.7 }).\n\n",
  "3": "Passed an incorrect argument to a color function, please pass a string representation of a color.\n\n",
  "4": "Couldn't generate valid rgb string from %s, it returned %s.\n\n",
  "5": "Couldn't parse the color string. Please provide the color as a string in hex, rgb, rgba, hsl or hsla notation.\n\n",
  "6": "Passed invalid arguments to rgb, please pass multiple numbers e.g. rgb(255, 205, 100) or an object e.g. rgb({ red: 255, green: 205, blue: 100 }).\n\n",
  "7": "Passed invalid arguments to rgba, please pass multiple numbers e.g. rgb(255, 205, 100, 0.75) or an object e.g. rgb({ red: 255, green: 205, blue: 100, alpha: 0.75 }).\n\n",
  "8": "Passed invalid argument to toColorString, please pass a RgbColor, RgbaColor, HslColor or HslaColor object.\n\n",
  "9": "Please provide a number of steps to the modularScale helper.\n\n",
  "10": "Please pass a number or one of the predefined scales to the modularScale helper as the ratio.\n\n",
  "11": "Invalid value passed as base to modularScale, expected number or em string but got \"%s\"\n\n",
  "12": "Expected a string ending in \"px\" or a number passed as the first argument to %s(), got \"%s\" instead.\n\n",
  "13": "Expected a string ending in \"px\" or a number passed as the second argument to %s(), got \"%s\" instead.\n\n",
  "14": "Passed invalid pixel value (\"%s\") to %s(), please pass a value like \"12px\" or 12.\n\n",
  "15": "Passed invalid base value (\"%s\") to %s(), please pass a value like \"12px\" or 12.\n\n",
  "16": "You must provide a template to this method.\n\n",
  "17": "You passed an unsupported selector state to this method.\n\n",
  "18": "minScreen and maxScreen must be provided as stringified numbers with the same units.\n\n",
  "19": "fromSize and toSize must be provided as stringified numbers with the same units.\n\n",
  "20": "expects either an array of objects or a single object with the properties prop, fromSize, and toSize.\n\n",
  "21": "expects the objects in the first argument array to have the properties `prop`, `fromSize`, and `toSize`.\n\n",
  "22": "expects the first argument object to have the properties `prop`, `fromSize`, and `toSize`.\n\n",
  "23": "fontFace expects a name of a font-family.\n\n",
  "24": "fontFace expects either the path to the font file(s) or a name of a local copy.\n\n",
  "25": "fontFace expects localFonts to be an array.\n\n",
  "26": "fontFace expects fileFormats to be an array.\n\n",
  "27": "radialGradient requries at least 2 color-stops to properly render.\n\n",
  "28": "Please supply a filename to retinaImage() as the first argument.\n\n",
  "29": "Passed invalid argument to triangle, please pass correct pointingDirection e.g. 'right'.\n\n",
  "30": "Passed an invalid value to `height` or `width`. Please provide a pixel based unit.\n\n",
  "31": "The animation shorthand only takes 8 arguments. See the specification for more information: http://mdn.io/animation\n\n",
  "32": "To pass multiple animations please supply them in arrays, e.g. animation(['rotate', '2s'], ['move', '1s'])\nTo pass a single animation please supply them in simple values, e.g. animation('rotate', '2s')\n\n",
  "33": "The animation shorthand arrays can only have 8 elements. See the specification for more information: http://mdn.io/animation\n\n",
  "34": "borderRadius expects a radius value as a string or number as the second argument.\n\n",
  "35": "borderRadius expects one of \"top\", \"bottom\", \"left\" or \"right\" as the first argument.\n\n",
  "36": "Property must be a string value.\n\n",
  "37": "Syntax Error at %s.\n\n",
  "38": "Formula contains a function that needs parentheses at %s.\n\n",
  "39": "Formula is missing closing parenthesis at %s.\n\n",
  "40": "Formula has too many closing parentheses at %s.\n\n",
  "41": "All values in a formula must have the same unit or be unitless.\n\n",
  "42": "Please provide a number of steps to the modularScale helper.\n\n",
  "43": "Please pass a number or one of the predefined scales to the modularScale helper as the ratio.\n\n",
  "44": "Invalid value passed as base to modularScale, expected number or em/rem string but got %s.\n\n",
  "45": "Passed invalid argument to hslToColorString, please pass a HslColor or HslaColor object.\n\n",
  "46": "Passed invalid argument to rgbToColorString, please pass a RgbColor or RgbaColor object.\n\n",
  "47": "minScreen and maxScreen must be provided as stringified numbers with the same units.\n\n",
  "48": "fromSize and toSize must be provided as stringified numbers with the same units.\n\n",
  "49": "Expects either an array of objects or a single object with the properties prop, fromSize, and toSize.\n\n",
  "50": "Expects the objects in the first argument array to have the properties prop, fromSize, and toSize.\n\n",
  "51": "Expects the first argument object to have the properties prop, fromSize, and toSize.\n\n",
  "52": "fontFace expects either the path to the font file(s) or a name of a local copy.\n\n",
  "53": "fontFace expects localFonts to be an array.\n\n",
  "54": "fontFace expects fileFormats to be an array.\n\n",
  "55": "fontFace expects a name of a font-family.\n\n",
  "56": "linearGradient requries at least 2 color-stops to properly render.\n\n",
  "57": "radialGradient requries at least 2 color-stops to properly render.\n\n",
  "58": "Please supply a filename to retinaImage() as the first argument.\n\n",
  "59": "Passed invalid argument to triangle, please pass correct pointingDirection e.g. 'right'.\n\n",
  "60": "Passed an invalid value to `height` or `width`. Please provide a pixel based unit.\n\n",
  "61": "Property must be a string value.\n\n",
  "62": "borderRadius expects a radius value as a string or number as the second argument.\n\n",
  "63": "borderRadius expects one of \"top\", \"bottom\", \"left\" or \"right\" as the first argument.\n\n",
  "64": "The animation shorthand only takes 8 arguments. See the specification for more information: http://mdn.io/animation.\n\n",
  "65": "To pass multiple animations please supply them in arrays, e.g. animation(['rotate', '2s'], ['move', '1s'])\\nTo pass a single animation please supply them in simple values, e.g. animation('rotate', '2s').\n\n",
  "66": "The animation shorthand arrays can only have 8 elements. See the specification for more information: http://mdn.io/animation.\n\n",
  "67": "You must provide a template to this method.\n\n",
  "68": "You passed an unsupported selector state to this method.\n\n",
  "69": "Expected a string ending in \"px\" or a number passed as the first argument to %s(), got %s instead.\n\n",
  "70": "Expected a string ending in \"px\" or a number passed as the second argument to %s(), got %s instead.\n\n",
  "71": "Passed invalid pixel value %s to %s(), please pass a value like \"12px\" or 12.\n\n",
  "72": "Passed invalid base value %s to %s(), please pass a value like \"12px\" or 12.\n\n",
  "73": "Please provide a valid CSS variable.\n\n",
  "74": "CSS variable not found and no default was provided.\n\n",
  "75": "important requires a valid style object, got a %s instead.\n\n",
  "76": "fromSize and toSize must be provided as stringified numbers with the same units as minScreen and maxScreen.\n\n",
  "77": "remToPx expects a value in \"rem\" but you provided it in \"%s\".\n\n",
  "78": "base must be set in \"px\" or \"%\" but you set it in \"%s\".\n"
};
/**
 * super basic version of sprintf
 * @private
 */

function format$2() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  var a = args[0];
  var b = [];
  var c;

  for (c = 1; c < args.length; c += 1) {
    b.push(args[c]);
  }

  b.forEach(function (d) {
    a = a.replace(/%[a-z]/, d);
  });
  return a;
}
/**
 * Create an error file out of errors.md for development and a simple web link to the full errors
 * in production mode.
 * @private
 */


var PolishedError = /*#__PURE__*/function (_Error) {
  _inheritsLoose(PolishedError, _Error);

  function PolishedError(code) {
    var _this;

    if (process.env.NODE_ENV === 'production') {
      _this = _Error.call(this, "An error occurred. See https://github.com/styled-components/polished/blob/main/src/internalHelpers/errors.md#" + code + " for more information.") || this;
    } else {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }

      _this = _Error.call(this, format$2.apply(void 0, [ERRORS[code]].concat(args))) || this;
    }

    return _assertThisInitialized$2(_this);
  }

  return PolishedError;
}( /*#__PURE__*/_wrapNativeSuper(Error));

function colorToInt(color) {
  return Math.round(color * 255);
}

function convertToInt(red, green, blue) {
  return colorToInt(red) + "," + colorToInt(green) + "," + colorToInt(blue);
}

function hslToRgb(hue, saturation, lightness, convert) {
  if (convert === void 0) {
    convert = convertToInt;
  }

  if (saturation === 0) {
    // achromatic
    return convert(lightness, lightness, lightness);
  } // formulae from https://en.wikipedia.org/wiki/HSL_and_HSV


  var huePrime = (hue % 360 + 360) % 360 / 60;
  var chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  var secondComponent = chroma * (1 - Math.abs(huePrime % 2 - 1));
  var red = 0;
  var green = 0;
  var blue = 0;

  if (huePrime >= 0 && huePrime < 1) {
    red = chroma;
    green = secondComponent;
  } else if (huePrime >= 1 && huePrime < 2) {
    red = secondComponent;
    green = chroma;
  } else if (huePrime >= 2 && huePrime < 3) {
    green = chroma;
    blue = secondComponent;
  } else if (huePrime >= 3 && huePrime < 4) {
    green = secondComponent;
    blue = chroma;
  } else if (huePrime >= 4 && huePrime < 5) {
    red = secondComponent;
    blue = chroma;
  } else if (huePrime >= 5 && huePrime < 6) {
    red = chroma;
    blue = secondComponent;
  }

  var lightnessModification = lightness - chroma / 2;
  var finalRed = red + lightnessModification;
  var finalGreen = green + lightnessModification;
  var finalBlue = blue + lightnessModification;
  return convert(finalRed, finalGreen, finalBlue);
}

var namedColorMap = {
  aliceblue: 'f0f8ff',
  antiquewhite: 'faebd7',
  aqua: '00ffff',
  aquamarine: '7fffd4',
  azure: 'f0ffff',
  beige: 'f5f5dc',
  bisque: 'ffe4c4',
  black: '000',
  blanchedalmond: 'ffebcd',
  blue: '0000ff',
  blueviolet: '8a2be2',
  brown: 'a52a2a',
  burlywood: 'deb887',
  cadetblue: '5f9ea0',
  chartreuse: '7fff00',
  chocolate: 'd2691e',
  coral: 'ff7f50',
  cornflowerblue: '6495ed',
  cornsilk: 'fff8dc',
  crimson: 'dc143c',
  cyan: '00ffff',
  darkblue: '00008b',
  darkcyan: '008b8b',
  darkgoldenrod: 'b8860b',
  darkgray: 'a9a9a9',
  darkgreen: '006400',
  darkgrey: 'a9a9a9',
  darkkhaki: 'bdb76b',
  darkmagenta: '8b008b',
  darkolivegreen: '556b2f',
  darkorange: 'ff8c00',
  darkorchid: '9932cc',
  darkred: '8b0000',
  darksalmon: 'e9967a',
  darkseagreen: '8fbc8f',
  darkslateblue: '483d8b',
  darkslategray: '2f4f4f',
  darkslategrey: '2f4f4f',
  darkturquoise: '00ced1',
  darkviolet: '9400d3',
  deeppink: 'ff1493',
  deepskyblue: '00bfff',
  dimgray: '696969',
  dimgrey: '696969',
  dodgerblue: '1e90ff',
  firebrick: 'b22222',
  floralwhite: 'fffaf0',
  forestgreen: '228b22',
  fuchsia: 'ff00ff',
  gainsboro: 'dcdcdc',
  ghostwhite: 'f8f8ff',
  gold: 'ffd700',
  goldenrod: 'daa520',
  gray: '808080',
  green: '008000',
  greenyellow: 'adff2f',
  grey: '808080',
  honeydew: 'f0fff0',
  hotpink: 'ff69b4',
  indianred: 'cd5c5c',
  indigo: '4b0082',
  ivory: 'fffff0',
  khaki: 'f0e68c',
  lavender: 'e6e6fa',
  lavenderblush: 'fff0f5',
  lawngreen: '7cfc00',
  lemonchiffon: 'fffacd',
  lightblue: 'add8e6',
  lightcoral: 'f08080',
  lightcyan: 'e0ffff',
  lightgoldenrodyellow: 'fafad2',
  lightgray: 'd3d3d3',
  lightgreen: '90ee90',
  lightgrey: 'd3d3d3',
  lightpink: 'ffb6c1',
  lightsalmon: 'ffa07a',
  lightseagreen: '20b2aa',
  lightskyblue: '87cefa',
  lightslategray: '789',
  lightslategrey: '789',
  lightsteelblue: 'b0c4de',
  lightyellow: 'ffffe0',
  lime: '0f0',
  limegreen: '32cd32',
  linen: 'faf0e6',
  magenta: 'f0f',
  maroon: '800000',
  mediumaquamarine: '66cdaa',
  mediumblue: '0000cd',
  mediumorchid: 'ba55d3',
  mediumpurple: '9370db',
  mediumseagreen: '3cb371',
  mediumslateblue: '7b68ee',
  mediumspringgreen: '00fa9a',
  mediumturquoise: '48d1cc',
  mediumvioletred: 'c71585',
  midnightblue: '191970',
  mintcream: 'f5fffa',
  mistyrose: 'ffe4e1',
  moccasin: 'ffe4b5',
  navajowhite: 'ffdead',
  navy: '000080',
  oldlace: 'fdf5e6',
  olive: '808000',
  olivedrab: '6b8e23',
  orange: 'ffa500',
  orangered: 'ff4500',
  orchid: 'da70d6',
  palegoldenrod: 'eee8aa',
  palegreen: '98fb98',
  paleturquoise: 'afeeee',
  palevioletred: 'db7093',
  papayawhip: 'ffefd5',
  peachpuff: 'ffdab9',
  peru: 'cd853f',
  pink: 'ffc0cb',
  plum: 'dda0dd',
  powderblue: 'b0e0e6',
  purple: '800080',
  rebeccapurple: '639',
  red: 'f00',
  rosybrown: 'bc8f8f',
  royalblue: '4169e1',
  saddlebrown: '8b4513',
  salmon: 'fa8072',
  sandybrown: 'f4a460',
  seagreen: '2e8b57',
  seashell: 'fff5ee',
  sienna: 'a0522d',
  silver: 'c0c0c0',
  skyblue: '87ceeb',
  slateblue: '6a5acd',
  slategray: '708090',
  slategrey: '708090',
  snow: 'fffafa',
  springgreen: '00ff7f',
  steelblue: '4682b4',
  tan: 'd2b48c',
  teal: '008080',
  thistle: 'd8bfd8',
  tomato: 'ff6347',
  turquoise: '40e0d0',
  violet: 'ee82ee',
  wheat: 'f5deb3',
  white: 'fff',
  whitesmoke: 'f5f5f5',
  yellow: 'ff0',
  yellowgreen: '9acd32'
};
/**
 * Checks if a string is a CSS named color and returns its equivalent hex value, otherwise returns the original color.
 * @private
 */

function nameToHex(color) {
  if (typeof color !== 'string') return color;
  var normalizedColorName = color.toLowerCase();
  return namedColorMap[normalizedColorName] ? "#" + namedColorMap[normalizedColorName] : color;
}

var hexRegex = /^#[a-fA-F0-9]{6}$/;
var hexRgbaRegex = /^#[a-fA-F0-9]{8}$/;
var reducedHexRegex = /^#[a-fA-F0-9]{3}$/;
var reducedRgbaHexRegex = /^#[a-fA-F0-9]{4}$/;
var rgbRegex = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;
var rgbaRegex = /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([-+]?[0-9]*[.]?[0-9]+)\s*\)$/i;
var hslRegex = /^hsl\(\s*(\d{0,3}[.]?[0-9]+)\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*\)$/i;
var hslaRegex = /^hsla\(\s*(\d{0,3}[.]?[0-9]+)\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*([-+]?[0-9]*[.]?[0-9]+)\s*\)$/i;
/**
 * Returns an RgbColor or RgbaColor object. This utility function is only useful
 * if want to extract a color component. With the color util `toColorString` you
 * can convert a RgbColor or RgbaColor object back to a string.
 *
 * @example
 * // Assigns `{ red: 255, green: 0, blue: 0 }` to color1
 * const color1 = parseToRgb('rgb(255, 0, 0)');
 * // Assigns `{ red: 92, green: 102, blue: 112, alpha: 0.75 }` to color2
 * const color2 = parseToRgb('hsla(210, 10%, 40%, 0.75)');
 */

function parseToRgb(color) {
  if (typeof color !== 'string') {
    throw new PolishedError(3);
  }

  var normalizedColor = nameToHex(color);

  if (normalizedColor.match(hexRegex)) {
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[2], 16),
      green: parseInt("" + normalizedColor[3] + normalizedColor[4], 16),
      blue: parseInt("" + normalizedColor[5] + normalizedColor[6], 16)
    };
  }

  if (normalizedColor.match(hexRgbaRegex)) {
    var alpha = parseFloat((parseInt("" + normalizedColor[7] + normalizedColor[8], 16) / 255).toFixed(2));
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[2], 16),
      green: parseInt("" + normalizedColor[3] + normalizedColor[4], 16),
      blue: parseInt("" + normalizedColor[5] + normalizedColor[6], 16),
      alpha: alpha
    };
  }

  if (normalizedColor.match(reducedHexRegex)) {
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[1], 16),
      green: parseInt("" + normalizedColor[2] + normalizedColor[2], 16),
      blue: parseInt("" + normalizedColor[3] + normalizedColor[3], 16)
    };
  }

  if (normalizedColor.match(reducedRgbaHexRegex)) {
    var _alpha = parseFloat((parseInt("" + normalizedColor[4] + normalizedColor[4], 16) / 255).toFixed(2));

    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[1], 16),
      green: parseInt("" + normalizedColor[2] + normalizedColor[2], 16),
      blue: parseInt("" + normalizedColor[3] + normalizedColor[3], 16),
      alpha: _alpha
    };
  }

  var rgbMatched = rgbRegex.exec(normalizedColor);

  if (rgbMatched) {
    return {
      red: parseInt("" + rgbMatched[1], 10),
      green: parseInt("" + rgbMatched[2], 10),
      blue: parseInt("" + rgbMatched[3], 10)
    };
  }

  var rgbaMatched = rgbaRegex.exec(normalizedColor.substring(0, 50));

  if (rgbaMatched) {
    return {
      red: parseInt("" + rgbaMatched[1], 10),
      green: parseInt("" + rgbaMatched[2], 10),
      blue: parseInt("" + rgbaMatched[3], 10),
      alpha: parseFloat("" + rgbaMatched[4])
    };
  }

  var hslMatched = hslRegex.exec(normalizedColor);

  if (hslMatched) {
    var hue = parseInt("" + hslMatched[1], 10);
    var saturation = parseInt("" + hslMatched[2], 10) / 100;
    var lightness = parseInt("" + hslMatched[3], 10) / 100;
    var rgbColorString = "rgb(" + hslToRgb(hue, saturation, lightness) + ")";
    var hslRgbMatched = rgbRegex.exec(rgbColorString);

    if (!hslRgbMatched) {
      throw new PolishedError(4, normalizedColor, rgbColorString);
    }

    return {
      red: parseInt("" + hslRgbMatched[1], 10),
      green: parseInt("" + hslRgbMatched[2], 10),
      blue: parseInt("" + hslRgbMatched[3], 10)
    };
  }

  var hslaMatched = hslaRegex.exec(normalizedColor.substring(0, 50));

  if (hslaMatched) {
    var _hue = parseInt("" + hslaMatched[1], 10);

    var _saturation = parseInt("" + hslaMatched[2], 10) / 100;

    var _lightness = parseInt("" + hslaMatched[3], 10) / 100;

    var _rgbColorString = "rgb(" + hslToRgb(_hue, _saturation, _lightness) + ")";

    var _hslRgbMatched = rgbRegex.exec(_rgbColorString);

    if (!_hslRgbMatched) {
      throw new PolishedError(4, normalizedColor, _rgbColorString);
    }

    return {
      red: parseInt("" + _hslRgbMatched[1], 10),
      green: parseInt("" + _hslRgbMatched[2], 10),
      blue: parseInt("" + _hslRgbMatched[3], 10),
      alpha: parseFloat("" + hslaMatched[4])
    };
  }

  throw new PolishedError(5);
}

/**
 * Reduces hex values if possible e.g. #ff8866 to #f86
 * @private
 */
var reduceHexValue = function reduceHexValue(value) {
  if (value.length === 7 && value[1] === value[2] && value[3] === value[4] && value[5] === value[6]) {
    return "#" + value[1] + value[3] + value[5];
  }

  return value;
};

function numberToHex(value) {
  var hex = value.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

/**
 * Returns a string value for the color. The returned result is the smallest possible hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: rgb(255, 205, 100),
 *   background: rgb({ red: 255, green: 205, blue: 100 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${rgb(255, 205, 100)};
 *   background: ${rgb({ red: 255, green: 205, blue: 100 })};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#ffcd64";
 *   background: "#ffcd64";
 * }
 */
function rgb(value, green, blue) {
  if (typeof value === 'number' && typeof green === 'number' && typeof blue === 'number') {
    return reduceHexValue("#" + numberToHex(value) + numberToHex(green) + numberToHex(blue));
  } else if (typeof value === 'object' && green === undefined && blue === undefined) {
    return reduceHexValue("#" + numberToHex(value.red) + numberToHex(value.green) + numberToHex(value.blue));
  }

  throw new PolishedError(6);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible rgba or hex notation.
 *
 * Can also be used to fade a color by passing a hex value or named CSS color along with an alpha value.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: rgba(255, 205, 100, 0.7),
 *   background: rgba({ red: 255, green: 205, blue: 100, alpha: 0.7 }),
 *   background: rgba(255, 205, 100, 1),
 *   background: rgba('#ffffff', 0.4),
 *   background: rgba('black', 0.7),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${rgba(255, 205, 100, 0.7)};
 *   background: ${rgba({ red: 255, green: 205, blue: 100, alpha: 0.7 })};
 *   background: ${rgba(255, 205, 100, 1)};
 *   background: ${rgba('#ffffff', 0.4)};
 *   background: ${rgba('black', 0.7)};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(255,205,100,0.7)";
 *   background: "rgba(255,205,100,0.7)";
 *   background: "#ffcd64";
 *   background: "rgba(255,255,255,0.4)";
 *   background: "rgba(0,0,0,0.7)";
 * }
 */
function rgba(firstValue, secondValue, thirdValue, fourthValue) {
  if (typeof firstValue === 'string' && typeof secondValue === 'number') {
    var rgbValue = parseToRgb(firstValue);
    return "rgba(" + rgbValue.red + "," + rgbValue.green + "," + rgbValue.blue + "," + secondValue + ")";
  } else if (typeof firstValue === 'number' && typeof secondValue === 'number' && typeof thirdValue === 'number' && typeof fourthValue === 'number') {
    return fourthValue >= 1 ? rgb(firstValue, secondValue, thirdValue) : "rgba(" + firstValue + "," + secondValue + "," + thirdValue + "," + fourthValue + ")";
  } else if (typeof firstValue === 'object' && secondValue === undefined && thirdValue === undefined && fourthValue === undefined) {
    return firstValue.alpha >= 1 ? rgb(firstValue.red, firstValue.green, firstValue.blue) : "rgba(" + firstValue.red + "," + firstValue.green + "," + firstValue.blue + "," + firstValue.alpha + ")";
  }

  throw new PolishedError(7);
}

// Type definitions taken from https://github.com/gcanti/flow-static-land/blob/master/src/Fun.js
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-redeclare
function curried(f, length, acc) {
  return function fn() {
    // eslint-disable-next-line prefer-rest-params
    var combined = acc.concat(Array.prototype.slice.call(arguments));
    return combined.length >= length ? f.apply(this, combined) : curried(f, length, combined);
  };
} // eslint-disable-next-line no-redeclare


function curry(f) {
  // eslint-disable-line no-redeclare
  return curried(f, f.length, []);
}

function guard(lowerBoundary, upperBoundary, value) {
  return Math.max(lowerBoundary, Math.min(upperBoundary, value));
}

/**
 * Decreases the opacity of a color. Its range for the amount is between 0 to 1.
 *
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: transparentize(0.1, '#fff');
 *   background: transparentize(0.2, 'hsl(0, 0%, 100%)'),
 *   background: transparentize('0.5', 'rgba(255, 0, 0, 0.8)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${transparentize(0.1, '#fff')};
 *   background: ${transparentize(0.2, 'hsl(0, 0%, 100%)')},
 *   background: ${transparentize('0.5', 'rgba(255, 0, 0, 0.8)')},
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(255,255,255,0.9)";
 *   background: "rgba(255,255,255,0.8)";
 *   background: "rgba(255,0,0,0.3)";
 * }
 */

function transparentize(amount, color) {
  if (color === 'transparent') return color;
  var parsedColor = parseToRgb(color);
  var alpha = typeof parsedColor.alpha === 'number' ? parsedColor.alpha : 1;

  var colorWithAlpha = _extends$K({}, parsedColor, {
    alpha: guard(0, 1, +(alpha * 100 - parseFloat(amount) * 100).toFixed(2) / 100)
  });

  return rgba(colorWithAlpha);
} // prettier-ignore


var curriedTransparentize = /*#__PURE__*/curry
/* ::<number | string, string, string> */
(transparentize);

const getItemPositioning = (defaultPosition, position) => {
  const options = [
    "top-left",
    "top-center",
    "top-right",
    "bottom-left",
    "bottom-center",
    "bottom-right",
    "right",
    "left",
    "center"
  ];
  if (position && options.includes(position)) {
    return position;
  } else {
    console.error(`The position ${position} is not a valid option`);
  }
  return defaultPosition;
};
const getScreenMaxWidth = () => {
  return "calc(100vw - (100vw - 100%) - env(safe-area-inset-left) - env(safe-area-inset-right))";
};
const getScreenMaxHeight = () => {
  return "calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))";
};

const openStateStyles = (props) => {
  if (props.opened) {
    return css`
      top: 0;
      left: 0;
    `;
  }
  return css`
    top: -100vh;
    left: -100vw;
  `;
};
const BackdropStyled = styled.div`
  width: ${getScreenMaxWidth()};
  height: ${getScreenMaxHeight()};
  position: fixed;
  z-index: 2;
  ${openStateStyles};
  background-color: ${(props) => curriedTransparentize(1 - props.opacity, props.color)};
  content-visibility: auto;
  isolation: isolate;

  .Backdrop {
  }
`;

const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

const usePortal = (name = "") => {
  const portalRef = useRef(null);
  const [portalNode, setPortalNode] = useState(null);
  useIsomorphicLayoutEffect(() => {
    if (portalNode)
      return;
    const newPortalNode = createPortalDOMNode(name);
    if (!newPortalNode)
      return;
    portalRef.current = newPortalNode;
    setPortalNode(newPortalNode);
  }, [portalNode]);
  useIsomorphicLayoutEffect(() => {
    const node = portalRef.current;
    return () => {
      if (typeof window === "undefined" || !node)
        return;
      document.body.removeChild(node);
    };
  }, []);
  return portalNode;
};

const Backdrop = (props) => {
  const { children, className, opened, onClick, name } = props;
  const { allowScroll = false, opacity = 0.2, color = "#000000" } = props;
  const portalNode = usePortal(name);
  const clickHandler = (e) => {
    const { currentTarget, target } = e;
    if (currentTarget !== target) {
      return;
    }
    onClick == null ? void 0 : onClick();
  };
  useIsomorphicLayoutEffect(() => {
    const body = document.querySelector("body");
    if (!body) {
      return;
    }
    const restore = () => {
      const windowScrollY = -window.scrollY;
      const scrollY = allowScroll ? windowScrollY.toString() : body.style.top;
      body.style.position = "";
      body.style.top = "";
      body.style.overflowY = "";
      window.scrollTo(0, parseInt(scrollY || "0", 10) * -1);
    };
    if (opened) {
      const scrollY = window.scrollY;
      body.style.top = `-${scrollY}px`;
      body.style.bottom = "0px";
      body.style.right = "0px";
      body.style.left = "0px";
      body.style.overflowY = allowScroll ? "" : "scroll";
      body.style.position = allowScroll ? "" : "fixed";
    }
    return restore;
  }, [opened]);
  if (!portalNode)
    return null;
  return createPortal(/* @__PURE__ */ React.createElement(BackdropStyled, {
    className: `Backdrop ${className}`,
    onClick: clickHandler,
    opened,
    color,
    opacity
  }, children), portalNode);
};
Backdrop.defaultProps = {
  className: "",
  allowScroll: false,
  name: "",
  color: "#000000",
  opacity: 0.2
};

const leftPosition = (props) => {
  const { position } = props;
  const left = position.includes("left");
  const center = position.includes("center");
  if (left)
    return "-8px";
  if (center)
    return "50%";
  return "initial";
};
const topPosition = (props) => {
  const { position } = props;
  const top = position.includes("top");
  const center = !position.includes("bottom") && position.includes("center") || position.includes("right") && !position.includes("-") || position.includes("left") && !position.includes("-");
  if (top)
    return "-8px";
  if (center)
    return "50%";
  return "initial";
};
const centerPosition = (props) => {
  const { position } = props;
  const center = position.includes("center") && !position.includes("-");
  const top = position.includes("top-center") || position.includes("bottom-center");
  const positionX = position.includes("right") && !position.includes("-") || position.includes("left") && !position.includes("-");
  if (center)
    return "translate(-50%, -50%)";
  if (top)
    return "translateX(-50%)";
  if (positionX)
    return "translateY(-50%)";
  return "translate(0)";
};
const BadgeStyled = styled.div`
  position: relative;

  .Badge {
    &__value {
      background-color: var(--palette-primary, #fb5058);
      min-width: 16px;
      height: 16px;
      display: flex;
      justify-content: center;
      align-items: center;
      color: #ffffff;
      font-size: 8px;
      font-weight: 600;
      padding: 0 6px;
      border-radius: 10px;
      position: absolute;
      top: ${topPosition};
      right: ${(props) => props.position.includes("right") ? "-8px" : "initial"};
      bottom: ${(props) => props.position.includes("bottom") ? "-8px" : "initial"};
      left: ${leftPosition};
      transform: ${centerPosition};
      z-index: 2;
    }
  }
`;

const Badge = forwardRef((props, ref) => {
  const { className, position = "top-right", title, children, value } = props;
  if (Children.count(children) > 1) {
    console.error("You must provide only one children element");
    return null;
  }
  return /* @__PURE__ */ React.createElement(BadgeStyled, {
    className: `Badge ${className}`,
    position: getItemPositioning("top-right", position),
    title,
    ref
  }, /* @__PURE__ */ React.createElement("div", {
    className: "Badge__value"
  }, value), children);
});
Badge.defaultProps = {
  className: "",
  position: "top-right"
};

const mapBorder = (props) => {
  switch (props.buttonTypes) {
    case "FILLED":
      return css`
        border: 1px solid var(--palette-primary, #4611cf);

        &:disabled {
          border: 1px solid #d4d4d4;
        }
      `;
    case "BORDER":
      return css`
        border: 1px solid #d4d4d4;

        &:hover {
          border-color: ${props.isLoading ? "#d4d4d4" : "var(--palette-primary, #4611cf)"};
        }

        &:disabled {
          border: 1px solid #d4d4d4;
        }
      `;
    default:
      return css`
        border: none;
      `;
  }
};
const mapBgColor = (props) => {
  switch (props.buttonTypes) {
    case "FILLED":
      return css`
        background-color: var(--palette-primary, #4611cf);

        &:disabled {
          background-color: #fafafa;
        }
      `;
    case "BORDER":
      return css`
        background-color: transparent;

        &:disabled {
          background-color: #fafafa;
        }
      `;
    case "LINK":
      return css`
        background-color: transparent;
      `;
    default:
      return css`
        background-color: #ffffff;
      `;
  }
};
const mapColor = (props) => {
  switch (props.buttonTypes) {
    case "FILLED":
      return css`
        color: #ffffff;

        &:disabled {
          color: #d4d4d4;
        }
      `;
    case "BORDER":
      return css`
        color: #000000d9;

        &:hover {
          color: ${props.isLoading ? "#000000d9" : "var(--palette-primary, #4611cf)"};
        }

        &:disabled {
          color: #d4d4d4;
        }
      `;
    case "LINK":
      return css`
        color: #000000d9;

        &:disabled {
          color: #d4d4d4;
        }
      `;
    default:
      return css`
        color: #ffffff;

        &:disabled {
          color: #d4d4d4;
        }
      `;
  }
};
const mapShadowStyles = (props) => {
  switch (props.buttonTypes) {
    case "FILLED":
      return css`
        box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);

        &:hover {
          box-shadow: ${props.isLoading ? "1px 1px 2px rgba(0, 0, 0, 0.3)" : "1px 2px 4px rgba(0, 0, 0, 0.5)"};

          &:disabled {
            box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
          }
        }

        &:active {
          box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);
        }
      `;
    case "BORDER":
      return css`
        box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);

        &:hover {
          box-shadow: ${props.isLoading ? "1px 1px 2px rgba(0, 0, 0, 0.1)" : "1px 2px 4px rgba(0, 0, 0, 0.2)"};

          &:disabled {
            box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
          }
        }

        &:active {
          box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2);
        }
      `;
    case "LINK":
    default:
      return css`
        box-shadow: none;

        &:hover {
          box-shadow: none;
        }

        &:active {
          box-shadow: none;
        }
      `;
  }
};
const mapFill = (props) => {
  switch (props.buttonTypes) {
    case "BORDER":
    case "LINK":
      return css`
        fill: #000000;
      `;
    default:
      return css`
        fill: #ffffff;
      `;
  }
};
const mapFillParent = (props) => {
  switch (props.buttonTypes) {
    case "BORDER":
      return css`
        &:hover .Button__loading__icon path {
          fill: ${props.isLoading ? "initial" : "var(--palette-primary, #4611cf)"};
        }
      `;
    default:
      return css``;
  }
};
const ButtonStyled$1 = styled.button`
  align-items: center;
  outline: none;
  cursor: ${(props) => props.isLoading ? "default" : "pointer"};
  ${mapColor}
  ${mapBgColor};
  ${mapBorder};
  display: flex;
  justify-content: center;
  opacity: ${(props) => props.isLoading ? "0.6" : "1"};
  padding: 0.8rem 1.2em;
  transition: all 0.4s;
  ${mapShadowStyles}
  ${mapFillParent}
  font-family: inherit;

  &:disabled {
    cursor: default;
  }

  & > *:not(:first-child) {
    margin-left: 0.8rem;
  }

  .Button {
    &__wrapper {
      display: flex;

      & > *:not(:first-child) {
        margin-left: 0.8rem;
      }
    }

    &__loading {
      align-items: center;
      display: flex;
      animation: rotate 1s infinite;

      &__icon {
        height: 1.2em;
        width: 1.2em;

        path {
          ${mapFill}
        }
      }
    }
  }

  @keyframes rotate {
    0% {
      transform: rotateZ(0deg);
    }
    100% {
      transform: rotateZ(360deg);
    }
  }
`;

function _extends$J() { _extends$J = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$J.apply(this, arguments); }
var LoadingSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$J({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 472.615 472.615"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M236.308 0c-13.153 0-23.817 10.663-23.817 23.817v82.961c0 13.154 10.663 23.817 23.817 23.817s23.817-10.662 23.817-23.817V23.817C260.125 10.664 249.461 0 236.308 0zM161.558 127.875l-58.662-58.662c-9.301-9.301-24.381-9.301-33.683 0-9.301 9.301-9.301 24.381 0 33.682l58.663 58.663c9.301 9.302 24.381 9.302 33.682 0 9.301-9.302 9.301-24.382 0-33.683zM106.779 212.491H23.817C10.663 212.491 0 223.154 0 236.308s10.663 23.817 23.817 23.817h82.962c13.154 0 23.817-10.663 23.817-23.817-.001-13.154-10.664-23.817-23.817-23.817zM161.558 311.057c-9.301-9.302-24.381-9.302-33.682 0L69.213 369.72c-9.301 9.301-9.301 24.381 0 33.682 9.302 9.301 24.382 9.301 33.683 0l58.662-58.662c9.301-9.301 9.301-24.381 0-33.683zM236.308 342.02c-13.153 0-23.817 10.663-23.817 23.817v82.961c0 13.154 10.663 23.817 23.817 23.817s23.817-10.663 23.817-23.817v-82.961c0-13.153-10.664-23.817-23.817-23.817zM403.402 369.721l-58.662-58.663c-9.302-9.302-24.381-9.302-33.683 0-9.301 9.302-9.301 24.382 0 33.683l58.663 58.662c9.301 9.301 24.381 9.301 33.682 0s9.301-24.382 0-33.682zM448.799 212.491h-82.962c-13.154 0-23.818 10.662-23.818 23.817 0 13.154 10.663 23.817 23.818 23.817h82.962c13.154 0 23.817-10.663 23.817-23.817-.001-13.154-10.664-23.817-23.817-23.817zM403.402 69.213c-9.301-9.301-24.381-9.301-33.682 0l-58.663 58.662c-9.301 9.301-9.301 24.381 0 33.683 9.302 9.302 24.381 9.302 33.683 0l58.662-58.663c9.301-9.301 9.301-24.381 0-33.682z"
})));

var __defProp$H = Object.defineProperty;
var __defProps$u = Object.defineProperties;
var __getOwnPropDescs$u = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$H = Object.getOwnPropertySymbols;
var __hasOwnProp$H = Object.prototype.hasOwnProperty;
var __propIsEnum$H = Object.prototype.propertyIsEnumerable;
var __defNormalProp$H = (obj, key, value) => key in obj ? __defProp$H(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$H = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$H.call(b, prop))
      __defNormalProp$H(a, prop, b[prop]);
  if (__getOwnPropSymbols$H)
    for (var prop of __getOwnPropSymbols$H(b)) {
      if (__propIsEnum$H.call(b, prop))
        __defNormalProp$H(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$u = (a, b) => __defProps$u(a, __getOwnPropDescs$u(b));
var __objRest$p = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$H.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$H)
    for (var prop of __getOwnPropSymbols$H(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$H.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Button$1 = forwardRef((props, ref) => {
  const _a = props, {
    onClick = defaultFunction,
    type = "FILLED",
    className,
    disabled = false,
    children,
    loadingIcon,
    isLoading: forceLoading,
    htmlType
  } = _a, rest = __objRest$p(_a, [
    "onClick",
    "type",
    "className",
    "disabled",
    "children",
    "loadingIcon",
    "isLoading",
    "htmlType"
  ]);
  const [loading, setLoading] = useState(false);
  const isMounted = useRef(true);
  const clickHandler = async (e) => {
    if (loading || forceLoading)
      return;
    setLoading(forceLoading != null ? forceLoading : true);
    await onClick(e);
    if (!isMounted.current) {
      return;
    }
    setLoading(forceLoading != null ? forceLoading : false);
  };
  useEffect(() => {
    if (typeof forceLoading === "undefined")
      return;
    setLoading(forceLoading);
  }, [forceLoading]);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const renderLoadingIcon = () => {
    var _a2, _b;
    let icon = null;
    if (typeof loadingIcon === "string") {
      icon = /* @__PURE__ */ React.createElement("img", {
        className: "Button__loading__icon",
        src: `${loadingIcon}`,
        alt: "loading"
      });
    } else if (loadingIcon) {
      const LoadingIcon = React.cloneElement(loadingIcon, {
        className: `${(_b = (_a2 = loadingIcon.props) == null ? void 0 : _a2.className) != null ? _b : ""} Button__loading__icon`
      });
      icon = LoadingIcon;
    } else {
      icon = /* @__PURE__ */ React.createElement(LoadingSVG, {
        className: "Button__loading__icon"
      });
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "Button__loading"
    }, icon);
  };
  const renderChildren = () => {
    if (typeof children === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        className: "Button__wrapper"
      }, children);
    }
    return children;
  };
  return /* @__PURE__ */ React.createElement(ButtonStyled$1, __spreadProps$u(__spreadValues$H({
    className: `Button ${className}`,
    onClick: clickHandler,
    buttonTypes: type,
    disabled,
    isLoading: loading,
    type: htmlType
  }, rest), {
    ref
  }), loading ? renderLoadingIcon() : null, renderChildren());
});
Button$1.defaultProps = {
  className: "",
  type: "FILLED",
  onClick: defaultFunction,
  disabled: false
};

const ButtonGroupStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  position: relative;

  .ButtonGroup {
  }
`;

const ButtonStyled = styled(Button$1)`
  cursor: pointer;
  border: 1px solid #d4d4d4;
  z-index: 0;
  position: relative;

  &:not(:first-child) {
    margin-left: -1px;
  }

  &.Button--active {
    border: 1px solid #000;
    z-index: 1;
  }

  .Button {
  }
`;

var __defProp$G = Object.defineProperty;
var __getOwnPropSymbols$G = Object.getOwnPropertySymbols;
var __hasOwnProp$G = Object.prototype.hasOwnProperty;
var __propIsEnum$G = Object.prototype.propertyIsEnumerable;
var __defNormalProp$G = (obj, key, value) => key in obj ? __defProp$G(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$G = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$G.call(b, prop))
      __defNormalProp$G(a, prop, b[prop]);
  if (__getOwnPropSymbols$G)
    for (var prop of __getOwnPropSymbols$G(b)) {
      if (__propIsEnum$G.call(b, prop))
        __defNormalProp$G(a, prop, b[prop]);
    }
  return a;
};
var __objRest$o = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$G.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$G)
    for (var prop of __getOwnPropSymbols$G(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$G.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Button = forwardRef((props, ref) => {
  const _a = props, { className, __active__ } = _a, rest = __objRest$o(_a, ["className", "__active__"]);
  return /* @__PURE__ */ React.createElement(ButtonStyled, __spreadValues$G({
    className: `Button ${className} ${__active__ && "Button--active"}`,
    type: "BORDER",
    role: "radio",
    ref,
    "aria-checked": __active__
  }, rest));
});
Button.defaultProps = {
  className: ""
};

const ButtonGroup = (props) => {
  const { children, className, onChange = defaultFunction } = props;
  const { defaultActive, active, ariaLabelledBy } = props;
  const [internalActive, setInternalActive] = useState(defaultActive != null ? defaultActive : 0);
  const renderChildren = () => {
    return Children.map(filteredChildren(children, Button), (child, index) => {
      var _a, _b;
      if (!isValidElement(child)) {
        return;
      }
      const originalOnClick = (_a = child.props.onClick) != null ? _a : defaultFunction;
      const enhancedOnClick = (e) => {
        var _a2;
        const newValue = child.props.value;
        e.target.value = newValue;
        onChange(e);
        originalOnClick(e);
        setInternalActive((_a2 = child.props.id) != null ? _a2 : index);
      };
      return cloneElement(child, {
        onClick: enhancedOnClick,
        __active__: ((_b = child.props.id) != null ? _b : index) === (active != null ? active : internalActive)
      });
    });
  };
  return /* @__PURE__ */ React.createElement(ButtonGroupStyled, {
    className: `ButtonGroup ${className}`,
    "aria-labelledby": ariaLabelledBy
  }, renderChildren());
};
ButtonGroup.Button = Button;
ButtonGroup.defaultProps = {
  className: ""
};

const CardFormStyled = styled.form`
  width: 100%;

  .CardForm {
  }
`;

const TextInputStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .TextInput {
    &__label {
      color: #11111180;
      padding-bottom: 4px;
      font-size: 12px;
    }

    &__input {
      width: 100%;
      padding: 0 12px;
      font-family: inherit;
      font-size: 16px;
      line-height: 2.4;
      color: #111111;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      outline: none;

      &:hover {
        border-color: var(--palette-primary, #4611cf);
      }

      &:focus {
        border-color: var(--palette-primary, #4611cf);
        outline: 0;
        box-shadow: 0 0 0 2px #1890ff33;
      }
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

var __defProp$F = Object.defineProperty;
var __defProps$t = Object.defineProperties;
var __getOwnPropDescs$t = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$F = Object.getOwnPropertySymbols;
var __hasOwnProp$F = Object.prototype.hasOwnProperty;
var __propIsEnum$F = Object.prototype.propertyIsEnumerable;
var __defNormalProp$F = (obj, key, value) => key in obj ? __defProp$F(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$F = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$F.call(b, prop))
      __defNormalProp$F(a, prop, b[prop]);
  if (__getOwnPropSymbols$F)
    for (var prop of __getOwnPropSymbols$F(b)) {
      if (__propIsEnum$F.call(b, prop))
        __defNormalProp$F(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$t = (a, b) => __defProps$t(a, __getOwnPropDescs$t(b));
var __objRest$n = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$F.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$F)
    for (var prop of __getOwnPropSymbols$F(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$F.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const TextInput = forwardRef((props, ref) => {
  const _a = props, { label, name, id, className, errorMessage } = _a, rest = __objRest$n(_a, ["label", "name", "id", "className", "errorMessage"]);
  const errorMessageId = `${id != null ? id : name}-error`;
  const renderLabel = () => {
    if (!label)
      return null;
    return /* @__PURE__ */ React.createElement("label", {
      className: "TextInput__label",
      htmlFor: id != null ? id : name
    }, label);
  };
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "TextInput__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "TextInput__errorMessage"
    }, errorMessage);
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("TextInput: When using the label tag consider passing the 'name' prop");
  }, [name, label]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn("Switch: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, errorMessage]);
  return /* @__PURE__ */ React.createElement(TextInputStyled, {
    className: `TextInput ${className}`
  }, renderLabel(), /* @__PURE__ */ React.createElement("input", __spreadProps$t(__spreadValues$F({
    className: "TextInput__input",
    id: id != null ? id : name,
    type: "text",
    name,
    maxLength: 80,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId
  }, rest), {
    ref
  })), renderErrorMessage());
});
TextInput.defaultProps = {
  className: ""
};

const CardHolderStyled = styled(TextInput)`
  .CardHolder {
  }
`;

const letters = new RegExp(/[A-zÀ-ž]+/);
const numbers = new RegExp(/[0-9]/);
const specialCharacters = new RegExp(/[!-/:-@[-`{-~]/);
const containsALetter = (value) => {
  return letters.test(value);
};
const containsANumber = (value) => {
  return numbers.test(value);
};
const containsASpecialCharacter = (value) => {
  return specialCharacters.test(value);
};
const containsMoreThanNumbers = (value) => {
  const format = new RegExp(/\D+/g);
  return format.test(value);
};

function _extends$I() { _extends$I = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$I.apply(this, arguments); }
var AmexSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$I({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M33.75 26.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V9.75a3 3 0 013-3h25.5a3 3 0 013 3v16.5z",
  fill: "#1976D2"
}), /*#__PURE__*/React.createElement("path", {
  d: "M16.691 15l-1.584 3.512L13.529 15h-2.021v5.044L9.256 15H7.55l-2.3 5.236h1.361l.503-1.169h2.574l.512 1.169h2.599v-3.889l1.724 3.889h1.172l1.764-3.822v3.822h1.291V15h-2.059zm-9.09 2.936l.77-1.83.8 1.83H7.6zm20.811-.376l2.338-2.546h-1.663l-1.495 1.623L26.145 15H21v5.236h4.976l1.57-1.733 1.53 1.747h1.657l-2.32-2.69zm-3.116 1.577h-3.008v-1.036h2.878v-.992h-2.878v-.981l3.175.009 1.27 1.423-1.437 1.577z",
  fill: "#fff"
})));

function _extends$H() { _extends$H = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$H.apply(this, arguments); }
var DankortSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$H({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M24.159 8.74h-12.39a9.292 9.292 0 100 18.583h12.39a9.291 9.291 0 009.291-9.292 9.292 9.292 0 00-9.291-9.292z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M26.324 17.469l4.12 4.9a7.599 7.599 0 001.354-4.338c0-1.7-.558-3.271-1.5-4.541l-3.974 3.979zM14.65 13.076c3.077 0 5.395.783 5.633 3.375l3.238-3.375h6.447a7.627 7.627 0 00-5.81-2.685H11.77a7.627 7.627 0 00-5.812 2.685h8.692zM9.581 16.344l-1.098 3.02h4.432c1.31 0 1.707-.542 2-1.587.29-1.032-.439-1.433-1.334-1.433h-4zM23.548 22.78l-3.424-4.327c-.634 2.95-2.706 4.327-6.592 4.327H5.79a7.63 7.63 0 005.98 2.891h12.39a7.629 7.629 0 005.979-2.89h-6.59z",
  fill: "#DE0000"
})));

function _extends$G() { _extends$G = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$G.apply(this, arguments); }
var DinersSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$G({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M33.75 26.25c0 1.65-1.35 3-3 3H5.25c-1.65 0-3-1.35-3-3V9.75c0-1.65 1.35-3 3-3h25.5c1.65 0 3 1.35 3 3v16.5z",
  fill: "#CFD8DC"
}), /*#__PURE__*/React.createElement("path", {
  d: "M21.75 12h-7.5c-3.3 0-6 2.7-6 6s2.7 6 6 6h7.5c3.3 0 6-2.7 6-6s-2.7-6-6-6z",
  fill: "#1565C0"
}), /*#__PURE__*/React.createElement("path", {
  d: "M14.25 13.5a4.513 4.513 0 00-4.5 4.5c0 2.475 2.025 4.5 4.5 4.5s4.5-2.025 4.5-4.5-2.025-4.5-4.5-4.5zm-3 4.5c0-1.425.975-2.55 2.25-2.925v5.775A2.906 2.906 0 0111.25 18zM15 20.925V15.15c1.275.3 2.25 1.5 2.25 2.925 0 1.35-.975 2.475-2.25 2.85z",
  fill: "#fff"
})));

function _extends$F() { _extends$F = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$F.apply(this, arguments); }
var DiscoverSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$F({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M33.75 26.25c0 1.65-1.35 3-3 3H5.25c-1.65 0-3-1.35-3-3V9.75c0-1.65 1.35-3 3-3h25.5c1.65 0 3 1.35 3 3v16.5z",
  fill: "#E1E7EA"
}), /*#__PURE__*/React.createElement("path", {
  d: "M33.75 26.25c0 1.65-1.35 3-3 3H12S29.7 26.4 33.75 18v8.25zM16.5 18c0 1.275.975 2.25 2.25 2.25S21 19.275 21 18s-.975-2.25-2.25-2.25-2.25.975-2.25 2.25z",
  fill: "#FF6D00"
}), /*#__PURE__*/React.createElement("path", {
  d: "M8.4 15.75h.825v4.5H8.4v-4.5zM12.9 18c0 1.275.975 2.25 2.25 2.25.375 0 .675-.075 1.05-.225v-.975c-.3.3-.6.45-1.05.45-.825 0-1.425-.6-1.425-1.5 0-.825.6-1.5 1.425-1.5.375 0 .675.15 1.05.45v-.975c-.375-.15-.675-.3-1.05-.3C13.875 15.75 12.9 16.8 12.9 18zm10.05.675l-1.2-2.925h-.9l1.875 4.5h.45l1.875-4.5h-.9l-1.2 2.925zm2.475 1.575h2.4v-.75H26.25v-1.2h1.5v-.75h-1.5V16.5h1.575v-.75h-2.4v4.5zm5.7-3.15c0-.825-.525-1.35-1.5-1.35H28.35v4.5h.825v-1.8h.075l1.2 1.8h1.05l-1.35-1.875c.6-.15.975-.6.975-1.275zm-1.725.75h-.225V16.5h.225c.525 0 .825.225.825.675 0 .375-.225.675-.825.675zm-23.625-2.1H4.5v4.5h1.2c1.875 0 2.325-1.575 2.325-2.25.075-1.35-.9-2.25-2.25-2.25zM5.55 19.5h-.225v-3h.3c1.125 0 1.575.75 1.575 1.5 0 .3-.075 1.5-1.65 1.5zm5.925-2.025c-.525-.225-.675-.3-.675-.525 0-.3.3-.45.6-.45.225 0 .45.075.675.375l.45-.6c-.375-.375-.75-.525-1.275-.525-.75 0-1.35.525-1.35 1.275 0 .6.3.9 1.05 1.2.45.15.825.3.825.675 0 .375-.3.6-.675.6a.975.975 0 01-.9-.6l-.525.525c.375.6.825.825 1.5.825.9 0 1.5-.6 1.5-1.425 0-.675-.3-.975-1.2-1.35z",
  fill: "#000"
})));

function _extends$E() { _extends$E = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$E.apply(this, arguments); }
var EloSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$E({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M3.68 8h28.14c.928 0 1.68.752 1.68 1.68v16.422a1.68 1.68 0 01-1.68 1.68H3.68A1.68 1.68 0 012 26.102V9.68C2 8.752 2.752 8 3.68 8z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M8.33 15.165a2.88 2.88 0 013.734 2.159l1.973-.403a4.898 4.898 0 00-6.346-3.667l.638 1.911z",
  fill: "#FFF100"
}), /*#__PURE__*/React.createElement("path", {
  d: "M5.998 21.566l1.336-1.512a2.873 2.873 0 01-.97-2.159c0-.857.374-1.63.97-2.154l-1.336-1.508a4.885 4.885 0 00-1.65 3.666c0 1.458.638 2.768 1.65 3.667z",
  fill: "#00A3DF"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12.063 18.475a2.88 2.88 0 01-2.822 2.301c-.32 0-.626-.05-.916-.147l-.638 1.911a4.894 4.894 0 006.35-3.662l-1.974-.403z",
  fill: "#EE4023"
}), /*#__PURE__*/React.createElement("path", {
  d: "M20.614 19.827a1.747 1.747 0 01-2.163.231l-.655 1.042a2.995 2.995 0 003.68-.39l-.862-.883zm-1.184-4.246a2.99 2.99 0 00-2.55 4.616l5.41-2.314a2.99 2.99 0 00-2.86-2.302zm-1.793 3.175a1.71 1.71 0 01-.013-.21 1.759 1.759 0 011.785-1.73c.53.008 1 .248 1.315.626l-3.087 1.314zm6.354-4.519v5.767l1 .415-.475 1.139-.991-.412a1.106 1.106 0 01-.487-.412 1.29 1.29 0 01-.193-.714v-5.783h1.146zM27.599 16.904c.176-.059.361-.088.559-.088.852 0 1.558.605 1.722 1.407l1.205-.248a2.987 2.987 0 00-3.872-2.239l.386 1.168zm-1.424 3.902l.815-.92a1.752 1.752 0 01-.592-1.319c0-.525.23-.995.592-1.314l-.815-.92a2.984 2.984 0 000 4.473zm3.705-1.882a1.76 1.76 0 01-2.281 1.315l-.39 1.167a2.987 2.987 0 003.877-2.234l-1.206-.248z",
  fill: "#fff"
})));

function _extends$D() { _extends$D = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$D.apply(this, arguments); }
var HipercardSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$D({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M31.279 8H4.22C2.996 8 2 8.973 2 10.17v15.42c0 1.196.996 2.17 2.221 2.17H31.28c1.225 0 2.221-.974 2.221-2.17V10.17c0-1.197-.996-2.17-2.221-2.17z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M15.482 23.876l-11.508.004v-.027c0-.016.018-.114.04-.22a51.283 51.283 0 00.155-.748 507.52 507.52 0 00.398-1.947l.07-.341c.02-.088.044-.207.054-.265l.076-.375c.032-.15.066-.319.077-.376.01-.057.045-.226.077-.375.031-.15.066-.319.076-.376l.077-.376c.032-.149.066-.315.076-.368l.062-.306.1-.48a98.5 98.5 0 00.207-1.023 170.536 170.536 0 01.223-1.078 146.505 146.505 0 00.154-.751 64.3 64.3 0 00.084-.404c.024-.119.064-.3.09-.403.025-.104.06-.229.079-.278.018-.05.069-.158.112-.24l.08-.15.072-.094c.04-.051.11-.133.157-.182a2.449 2.449 0 01.336-.283 2.638 2.638 0 01.752-.345c.077-.024.22-.059.32-.078.099-.02.255-.046.348-.058l.168-.023 11.515-.004 11.515-.005v.028c0 .015-.019.114-.041.22a57.408 57.408 0 00-.16.768 95.552 95.552 0 00-.16.78 125.075 125.075 0 00-.155.75 64.986 64.986 0 00-.152.745 85.439 85.439 0 01-.16.772 617.156 617.156 0 00-.161.786 48.273 48.273 0 00-.153.744 364.805 364.805 0 01-.237 1.155c-.024.119-.06.288-.077.376l-.076.368a206.098 206.098 0 01-.238 1.162l-.083.403a338.82 338.82 0 01-.237 1.147l-.042.193-.05.134a2.802 2.802 0 01-.092.217 2.349 2.349 0 01-.21.33c-.024.031-.097.108-.162.173l-.117.118-.113.08c-.062.044-.13.09-.149.1l-.166.085a3.11 3.11 0 01-.226.102 3.57 3.57 0 01-.584.159c-.074.016-.225.04-.334.052l-.199.024-11.508.004zm-4.094-2.973h.3l.008-.022a.222.222 0 00.008-.063c0-.023.01-.106.022-.184a88.369 88.369 0 01.138-.872c.019-.119.041-.27.05-.337a.492.492 0 01.026-.122c.004 0 .024.03.043.066l.034.065.078.078.077.078.095.038.095.038.113.014.114.015.143-.009.144-.009.156-.04.155-.04.084-.038c.046-.021.127-.067.181-.102l.098-.063.09-.09c.05-.05.121-.13.157-.179a.786.786 0 00.066-.095 1.546 1.546 0 00.138-.263 4.35 4.35 0 00.114-.306l.054-.167.027-.144c.016-.08.036-.211.044-.292l.016-.148-.009-.125-.01-.125-.028-.126-.03-.125-.046-.094-.047-.093-.091-.097-.091-.096-.108-.055-.107-.056-.119-.028-.118-.028-.118-.01-.119-.008-.131.015-.132.015-.108.028-.108.027-.093.044a1.679 1.679 0 00-.167.09.842.842 0 00-.091.065.439.439 0 01-.056.043l-.036.023.025-.122c.014-.067.026-.13.026-.14v-.017h-.51l-.045.282a97.193 97.193 0 01-.476 2.744 98.656 98.656 0 01-.168.89c-.023.115-.045.223-.05.24l-.007.032h.3zm1.289-1.609l-.116.007-.076-.013-.076-.014-.078-.035-.077-.035-.054-.051-.054-.052-.03-.07a.985.985 0 01-.05-.144l-.017-.075.007-.12.007-.12.036-.187.079-.432c.023-.133.059-.33.079-.436l.036-.192.08-.066c.044-.037.114-.086.155-.11l.074-.041.098-.03.098-.03.125-.008.125-.008.102.022.102.022.072.033.072.033.06.06.061.061.033.065a.932.932 0 01.056.153l.023.09-.007.264-.007.264-.036.157a3.3 3.3 0 01-.083.292l-.048.136-.058.11c-.032.06-.081.142-.11.182a.784.784 0 01-.2.193l-.05.038-.118.04-.119.04-.116.007zm2.892.383l.153.002.154-.014a5.226 5.226 0 00.63-.095l.17-.037.008-.063c.005-.034.024-.137.043-.228l.034-.166-.008-.007-.007-.008-.052.026a2.352 2.352 0 01-.198.073l-.146.048-.154.03-.153.03h-.488l-.083-.027a1.295 1.295 0 01-.15-.06l-.067-.032-.055-.05-.055-.05-.043-.074-.043-.075-.027-.094-.027-.094v-.348l.024-.155.024-.155.21-.01.209-.01.775.005.774.005.024-.1c.012-.054.032-.166.043-.25l.02-.151.001-.127v-.128l-.022-.095-.022-.095-.036-.063a.62.62 0 00-.29-.267l-.07-.037-.121-.029-.122-.03-.15-.013-.15-.015-.139.01-.14.008-.166.032-.167.033-.105.041a1.373 1.373 0 00-.348.195 1.25 1.25 0 00-.365.455 3.286 3.286 0 00-.079.167 3.11 3.11 0 00-.189.697l-.022.14v.415l.022.09a1.15 1.15 0 00.113.306.788.788 0 00.22.236.994.994 0 00.152.089l.082.037.114.028c.063.015.158.033.21.04.054.006.166.013.25.014zm.262-1.96c-.385 0-.7-.003-.7-.006s.016-.052.036-.108c.02-.056.056-.14.079-.185l.041-.083.093-.092.092-.091.09-.044c.05-.023.116-.05.147-.057.03-.008.118-.018.194-.023l.14-.01.107.014.107.014.077.036.078.035.043.046a.504.504 0 01.062.083l.02.037.012.081.013.082-.015.135-.014.136h-.702zM20 19.677l.153.002.174-.021a3.254 3.254 0 00.499-.093.772.772 0 00.118-.04.674.674 0 00.037-.14 9.19 9.19 0 00.04-.214.385.385 0 00.008-.09 1.22 1.22 0 01-.175.072l-.134.051-.19.037-.192.037-.162-.006-.163-.005-.092-.032-.092-.032-.071-.062-.072-.063-.047-.086-.047-.086-.023-.115-.022-.115v-.299l.027-.18.028-.181.029-.097.04-.14c.006-.022.031-.088.056-.145.026-.058.07-.147.1-.198l.054-.092.066-.069.066-.069.07-.043.07-.043.09-.028a1.66 1.66 0 01.195-.044l.105-.016.153.007.153.008.181.036.181.037.074.027.077.027a.884.884 0 00.022-.114l.038-.231a.57.57 0 00.015-.122.768.768 0 00-.12-.02 9.678 9.678 0 00-.382-.028l-.267-.015-.188.014-.188.015-.14.029-.139.03-.09.035a1.298 1.298 0 00-.27.15 1.922 1.922 0 00-.146.133l-.107.104-.06.088c-.032.048-.089.148-.125.222l-.067.135-.05.146c-.029.08-.068.212-.087.292l-.036.146-.013.132-.013.132v.348l.013.101.013.101.042.105.042.104.048.075.048.075.077.075.076.076.081.042.081.043.107.03c.058.016.143.035.188.041.045.007.15.013.235.014zm2.29-.005l.188-.006.119-.03.118-.03.077-.036a1.187 1.187 0 00.422-.347.369.369 0 01.06-.07c.002.003-.005.067-.016.143-.01.077-.02.178-.02.226v.087h.511l.008-.198.008-.199.036-.257a20.445 20.445 0 01.148-.897c.021-.123.056-.314.077-.424l.037-.202.002-.174v-.175l-.039-.08-.04-.08-.053-.052-.053-.051-.088-.045-.088-.044-.13-.03-.131-.029-.18-.013-.18-.014-.233.015a8.343 8.343 0 00-.4.034l-.166.02h-.114l-.114.001-.017.08c-.009.044-.034.149-.056.233a1.213 1.213 0 00-.035.156.916.916 0 00.135-.03c.072-.02.225-.052.34-.071l.209-.036.188-.007.188-.008.121.021.121.021.08.04.082.04.045.067.045.068v.106l-.001.106-.024.117-.024.117-.501.001-.501.002-.164.045a1.88 1.88 0 00-.293.1.79.79 0 00-.12.067l-.106.066-.084.084a1.292 1.292 0 00-.13.153 1.303 1.303 0 00-.094.177l-.046.108-.021.122-.022.121v.255l.02.106.021.106.038.07c.02.038.059.093.084.123l.047.055.084.05.083.051.095.027c.053.015.133.033.178.04l.082.013.188-.005zm.175-.39l-.112.006-.074-.018a.78.78 0 01-.136-.052l-.064-.032-.034-.04a.646.646 0 01-.064-.099l-.03-.057-.006-.134-.006-.134.024-.08c.013-.045.043-.123.068-.173l.044-.09.084-.084.085-.084.09-.043.089-.043.111-.027.112-.026h.696l.022.009.022.009-.018.11a2.765 2.765 0 01-.13.479c-.024.057-.042.108-.042.113a.744.744 0 01-.051.093l-.05.086-.084.082a.561.561 0 01-.095.083.26.26 0 00-.063.035l-.051.035-.113.035-.113.036-.111.006zm4.533.395l.133.001.125-.02c.069-.011.157-.03.195-.04.038-.01.107-.039.153-.062l.084-.043.072-.063.072-.064.075-.1c.042-.056.08-.11.084-.122l.008-.021-.007.07a2.3 2.3 0 01-.02.139 2.564 2.564 0 00-.02.163l-.01.094h.547v-.188l.041-.355c.023-.195.057-.454.075-.577.019-.122.047-.298.064-.39l.064-.368a54.078 54.078 0 01.159-.877l.076-.41a66.02 66.02 0 01.19-.97v-.01h-.597l-.009.086a9.594 9.594 0 01-.05.351 79.887 79.887 0 01-.133.812l-.008.046-.023-.01a2.172 2.172 0 00-.156-.043l-.132-.033-.181-.014-.181-.014-.154.014-.153.014-.139.036-.14.035-.132.064-.132.065-.097.072-.098.072-.084.09c-.046.05-.113.132-.149.182l-.064.092-.076.158a4.194 4.194 0 00-.113.254c-.02.054-.056.173-.08.264l-.042.167-.02.216-.022.216.016.174.016.174.018.062c.01.035.032.096.048.137l.03.073.053.073.054.073.061.05.062.05.083.041a1.143 1.143 0 00.346.1c.047.007.145.013.218.014zm.238-.395l-.105.006-.071-.013a.779.779 0 01-.14-.042l-.067-.03-.057-.046-.057-.047-.043-.08-.042-.082-.02-.09-.019-.09.002-.182.002-.18.022-.16.022-.16.035-.112c.02-.06.035-.119.035-.128 0-.01.02-.06.043-.111a3.85 3.85 0 01.085-.178 1.46 1.46 0 01.1-.16.93.93 0 01.25-.241l.063-.037.11-.038.109-.038h.46l.132.036c.072.02.15.044.171.052l.04.016-.008.038-.037.205a49.14 49.14 0 01-.147.8 5.462 5.462 0 01-.111.469 1.148 1.148 0 01-.14.296.799.799 0 01-.09.106 1.177 1.177 0 01-.124.094l-.077.051-.11.035-.111.035-.105.006zm-20.499.327h.338l.025-.15.058-.35.076-.46a138.295 138.295 0 01.148-.862c.019-.102.037-.19.042-.194l.008-.008h1.82l.01.01.01.01-.016.08a47.487 47.487 0 00-.142.762c-.024.13-.058.315-.077.41a55.07 55.07 0 00-.132.735v.017h.68l.015-.087c.009-.048.022-.134.03-.191.008-.058.027-.18.043-.272a158.69 158.69 0 01.152-.897c.024-.142.058-.348.078-.459l.075-.417a122.018 122.018 0 00.24-1.301c.023-.13.053-.282.066-.337l.023-.101h-.688l-.01.066c-.004.036-.021.138-.036.226l-.062.348a41.921 41.921 0 00-.14.843l-.023.155-.505.01-.505.01-.423-.01a19.997 19.997 0 01-.426-.012 1.181 1.181 0 01.018-.12c.01-.065.032-.178.047-.25l.062-.32c.02-.104.042-.232.05-.285a3.54 3.54 0 01.034-.199c.012-.055.034-.172.05-.26.015-.087.031-.168.036-.18l.008-.022h-.682l-.022.121a9.767 9.767 0 00-.036.213l-.056.354-.077.48-.077.48a29.327 29.327 0 01-.16.912 333.208 333.208 0 01-.155.8c-.02.091-.04.198-.047.236-.006.038-.028.148-.048.245s-.035.186-.035.198v.023h.338zm3.746 0h.3l.008-.022a4.145 4.145 0 01.05-.379 127.288 127.288 0 01.162-.976c.02-.111.053-.296.075-.41l.076-.397c.019-.104.057-.3.085-.437a3.97 3.97 0 01.057-.261l.007-.012-.303.003-.303.004-.025.181c-.014.1-.044.297-.067.438l-.078.48c-.02.123-.054.32-.077.438l-.076.404a58.624 58.624 0 01-.182.915l-.008.031h.3zm7.007 0h.298l.009-.126c.004-.069.021-.208.037-.31l.071-.447c.024-.146.067-.388.097-.538a4.496 4.496 0 01.096-.41.73.73 0 01.05-.127c.027-.06.078-.15.113-.202l.063-.094.085-.079.085-.078.105-.05.105-.05h.16l.16.002.08.023c.042.013.082.023.086.023.005 0 .009-.014.009-.032 0-.018.015-.11.034-.204.02-.094.035-.175.035-.18a.366.366 0 00-.1-.039 1.285 1.285 0 00-.172-.04l-.07-.008-.09.016c-.05.009-.123.028-.164.043a.926.926 0 00-.15.076 1.083 1.083 0 00-.282.278l-.092.125-.02.028.012-.055c.007-.031.026-.131.041-.223.017-.092.033-.178.037-.191l.007-.025h-.531v.034c0 .018-.016.13-.035.247a162.55 162.55 0 00-.146.909 24.528 24.528 0 01-.154.841 96.308 96.308 0 01-.166.85v.013h.297zm7.06 0h.303v-.186l.035-.23a42.658 42.658 0 01.125-.78c.01-.066.038-.206.06-.314a3.26 3.26 0 01.087-.337c0-.01.026-.072.057-.139l.057-.121.076-.1.076-.101.083-.063a1.21 1.21 0 01.163-.1l.08-.04.174.002.174.001.068.022.069.022.011-.007.012-.007v-.044c.001-.025.016-.116.034-.202l.032-.158-.036-.014a2.075 2.075 0 00-.107-.036l-.07-.022h-.25l-.102.036-.102.036-.077.046-.077.047-.1.104-.1.104-.053.081a.38.38 0 01-.057.077 2.37 2.37 0 01.035-.206c.022-.111.04-.216.04-.233l.001-.032h-.515v.01l-.029.197a60.264 60.264 0 01-.23 1.393 140.681 140.681 0 01-.159.828l-.067.337-.026.129h.304zM11.07 16.214h.085l.067-.022.067-.023.06-.06.06-.06.03-.081.032-.08.001-.119.001-.118-.027-.046-.028-.046-.049-.03-.048-.03h-.21l-.071.032-.071.031-.042.045-.041.044-.033.076-.032.077-.006.12-.006.118.03.06.03.059.059.026.058.027h.084z",
  fill: "#B3131B"
})));

function _extends$C() { _extends$C = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$C.apply(this, arguments); }
var JbcSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$C({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M31.42 28.41H4.08A2.08 2.08 0 012 26.332V10.08A2.08 2.08 0 014.08 8h27.34c1.15 0 2.08.93 2.08 2.08V26.33a2.08 2.08 0 01-2.08 2.08z",
  fill: "#F9F9F9"
}), /*#__PURE__*/React.createElement("path", {
  d: "M9.892 15.303v.149c0 1.124.003 2.248-.002 3.372-.002.436-.14.822-.484 1.113-.29.246-.638.347-1.011.36-.842.027-1.633-.19-2.398-.52-.081-.035-.16-.078-.243-.108-.073-.026-.089-.071-.089-.144.002-2.443 0-4.885.003-7.328.001-1.467.963-2.667 2.393-2.982.233-.05.478-.068.717-.068 1.397-.003 2.794.005 4.19.007.083 0 .137.002.137.115-.003 5.014 0 10.027-.005 15.04-.001.854-.34 1.576-.962 2.157-.577.539-1.27.801-2.057.804-1.425.005-2.85.003-4.275.004h-.132V20.92c.179.039.356.079.534.114.588.117 1.18.207 1.78.226.512.016 1.027.024 1.54-.002.635-.033 1.257-.154 1.829-.455.731-.386 1.125-.996 1.14-1.82.02-1.194.012-2.389.017-3.583 0-.063-.006-.107-.09-.107-.823.004-1.646.004-2.468.005-.017 0-.034.003-.064.006z",
  fill: "#0152CC"
}), /*#__PURE__*/React.createElement("path", {
  d: "M14.035 20.317c.094.069.177.134.265.192.522.347 1.106.532 1.717.643.839.153 1.685.162 2.532.102.702-.05 1.395-.175 2.087-.342V19.64c-.027.01-.053.015-.076.027a6.016 6.016 0 01-1.798.58c-.62.088-1.236.105-1.83-.136-.687-.278-1.062-.8-1.163-1.524a2.5 2.5 0 01.074-1.105c.234-.75.775-1.152 1.52-1.304.703-.144 1.398-.057 2.076.143.371.11.726.274 1.087.415.035.014.069.031.11.05.004-.033.007-.054.007-.074l.001-1.117c0-.061-.011-.092-.08-.109-1.05-.245-2.11-.384-3.188-.352-.781.023-1.553.11-2.294.382a3.553 3.553 0 00-1.044.588c-.004-.038-.01-.064-.01-.09 0-1.279-.005-2.559.002-3.838.007-1.455.972-2.651 2.39-2.963.226-.05.464-.07.696-.07 1.414-.007 2.829-.003 4.243-.007.106 0 .127.033.127.132-.002 4.967-.001 9.933-.002 14.9 0 .88-.293 1.645-.93 2.261-.5.483-1.1.762-1.795.827a4.422 4.422 0 01-.414.018h-4.31v-6.957z",
  fill: "#D22F2F"
}), /*#__PURE__*/React.createElement("path", {
  d: "M22.398 15.297c-.002-.046-.005-.084-.005-.122 0-.993-.003-1.986 0-2.978.004-1.464.965-2.666 2.393-2.98.233-.051.478-.07.717-.07 1.396-.004 2.793.002 4.19.005.137 0 .137.001.137.144 0 4.966-.006 9.933.005 14.9.003 1.4-.89 2.526-2.047 2.915a2.99 2.99 0 01-.948.16c-1.443.002-2.886 0-4.328.001h-.114c-.002-.048-.005-.085-.005-.123 0-1.968 0-3.935-.003-5.903 0-.103.029-.13.13-.13 1.48.005 2.957.006 4.436.008.378 0 .75-.032 1.1-.184.434-.187.76-.48.898-.944a1.313 1.313 0 00-.513-1.471c-.32-.23-.682-.337-1.067-.378a.134.134 0 01-.074-.025c.095-.024.192-.044.286-.073.486-.154.864-.436 1.009-.944.195-.687-.093-1.365-.892-1.657-.326-.12-.667-.152-1.012-.152h-4.293z",
  fill: "#25BA2F"
}), /*#__PURE__*/React.createElement("path", {
  d: "M23.88 20.154V18.59c.03-.003.058-.008.085-.008.553 0 1.106-.003 1.659 0 .38.002.681.224.775.561.145.52-.198 1.002-.747 1.017-.577.016-1.155.004-1.733.003-.01 0-.02-.005-.039-.01zM23.88 17.656v-1.429c.03-.004.057-.01.084-.01.51 0 1.02-.004 1.53.002.35.003.61.199.7.509.137.468-.174.92-.66.932-.528.013-1.056.005-1.583.005-.02 0-.041-.005-.07-.01z",
  fill: "#25BA2F"
})));

function _extends$B() { _extends$B = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$B.apply(this, arguments); }
var LaserSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$B({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M15.049 25.014l.205.678h-.425l.22-.678zM33.5 8.81v18.792a.81.81 0 01-.81.811H2.81a.811.811 0 01-.81-.811V8.81A.81.81 0 012.81 8h29.88a.81.81 0 01.81.81zm-22.819 1.607h6.01l7.616.298.452-.778-8.825.297h-5.252v.183zm2.513 15.701h-.744v-1.563h-.474v1.881h1.136c.014-.104.066-.206.082-.318zm2.799.334c-.004-.014-.006-.028-.01-.039-.2-.6-.4-1.223-.6-1.823-.012-.035-.03-.04-.061-.04-.147.002-.461.003-.608 0-.051-.001.082.07.066.119-.18.512-.35.968-.53 1.48-.033.092-.065.197-.098.293l.418.009.022-.038c.041-.127.084-.253.128-.379.006-.016.01-.037.024-.037.193-.003.405-.002.6-.002l.023.065.104.352.014.04h.508zm1.905-1.063c-.1-.048-.201-.094-.301-.143a.855.855 0 01-.144-.084c-.063-.048-.08-.116-.061-.194a.153.153 0 01.141-.123.768.768 0 01.182-.005c.105.012.209.031.318.047l.076-.314c-.118-.02-.23-.045-.345-.057a1.06 1.06 0 00-.514.059c-.42.167-.44.723-.172.927.075.057.16.1.243.145.083.044.173.078.254.126.097.058.123.129.099.23-.02.081-.083.134-.189.136a1.908 1.908 0 01-.315-.02c-.087-.012-.171-.043-.26-.066-.03.103-.058.206-.09.313.203.074.405.114.615.11a.975.975 0 00.485-.128c.296-.172.4-.76-.022-.96zm2.776.73h-.793v-.495h.648v-.312h-.647v-.446h.683c.015-.106.032-.209.046-.312h-1.209v1.881h1.225l.047-.316zm2.791.337l-.558-.954a.241.241 0 00.026-.008.138.138 0 00.026-.01c.147-.055.26-.147.3-.304.058-.23.008-.465-.245-.563a.769.769 0 00-.168-.051c-.039-.007-.097-.012-.136-.016l-.847.002v1.889h.478v-1.58a.674.674 0 01.313.023c.069.025.115.075.128.149.033.173-.017.327-.243.347-.04.004-.139.004-.181.003l.609 1.072.498.001zm1.354-3.411H17.51l.173-.298-.617.298h-6.386v.183h6.323l.227.297.173-.297h7.415v-.183zm0-.916h-6.79l.154-.267-.69.267h-6.811v.183h6.646l.403.328.191-.328h6.896v-.183zm0-.915H18.51l.174-.297-1.175.297h-6.829v.183h6.821l.73.297.172-.297h6.414v-.183zm0-.915h-5.807l.174-.297-1.579.297h-6.925v.183h6.958l1.093.298.172-.298h5.914V20.3zm0-.915h-5.307l.174-.297-2.233.297h-6.771v.183h6.87l1.68.297.173-.297h5.413v-.183zm0-.915h-4.806l.173-.297-2.885.297h-6.62v.183h6.782l2.27.297.173-.297h4.913v-.183zm0-.915h-4.307l.174-.297-3.54.297H10.68v.183h6.695l2.857.298.173-.298h4.413v-.183zm0-.915h-3.806l.174-.297-4.194.297h-6.311v.183h6.606l3.445.297.174-.297h3.912v-.183zm0-.915h-3.306l.173-.297-4.846.297H10.68v.184h6.52l4.032.297.174-.297h3.412v-.184zm0-.915h-2.784l.152-.259-4.796.26h-6.709v.182h5.838l5.215.336.195-.336h2.89l-.002-.183zm0-.914h-2.234l.174-.298-6.213.298h-5.864v.183h6.361l5.262.297.174-.297h2.34v-.183zm0-.916h-1.733l.173-.297-6.865.297h-5.712v.183h6.274l5.85.298.173-.298h1.84v-.183zm0-.915h-1.234l.174-.297-7.518.297h-5.558v.183h6.185l6.439.297.173-.297h1.34v-.183h-.002zm0-.915h-.733l.174-.297-8.173.297h-5.404v.183h6.097l7.027.297.173-.297h.84l-.002-.183z",
  fill: "#000"
})));

function _extends$A() { _extends$A = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$A.apply(this, arguments); }
var MaestroSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$A({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M24.75 8.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z",
  fill: "#F44336"
}), /*#__PURE__*/React.createElement("path", {
  d: "M21 18h-6c0-.512.051-1.01.127-1.5h5.745a9.583 9.583 0 00-.348-1.5h-5.049c.17-.52.382-1.021.63-1.5h3.789a9.662 9.662 0 00-.97-1.5h-1.849A9.81 9.81 0 0118 10.975a9.725 9.725 0 00-.522-.475A9.75 9.75 0 001.5 18a9.75 9.75 0 009.75 9.75A9.71 9.71 0 0018.924 24h-1.849a9.726 9.726 0 01-.97-1.5h3.789c.25-.479.462-.98.63-1.5h-5.049a9.674 9.674 0 01-.348-1.5h5.746c.076-.49.127-.988.127-1.5z",
  fill: "#2196F3"
})));

function _extends$z() { _extends$z = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$z.apply(this, arguments); }
var MastercardSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$z({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M24 7.5a10.5 10.5 0 100 21 10.5 10.5 0 000-21z",
  fill: "#FF9800"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12 7.5a10.5 10.5 0 100 21 10.5 10.5 0 000-21z",
  fill: "#D50000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M13.5 18c0 3.566 1.782 6.712 4.5 8.61A10.482 10.482 0 0022.5 18c0-3.566-1.782-6.713-4.5-8.61A10.482 10.482 0 0013.5 18z",
  fill: "#FF3D00"
})));

function _extends$y() { _extends$y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$y.apply(this, arguments); }
var MirSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$y({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#clip0)",
  fillRule: "evenodd",
  clipRule: "evenodd"
}, /*#__PURE__*/React.createElement("path", {
  d: "M24.72 18.028v4.888h2.715v-2.897h3.078a3.01 3.01 0 002.81-1.991H24.72z",
  fill: "#37A72E"
}), /*#__PURE__*/React.createElement("path", {
  d: "M24.358 14c.158 1.998 2.048 3.575 4.357 3.575h4.721A3.01 3.01 0 0030.502 14h-6.144z",
  fill: "url(#paint0_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M20.339 17.528l.002-.005a.163.163 0 01.305.075v5.273h2.716V14h-2.715c-.345.011-.734.264-.878.574l-2.161 4.635a.183.183 0 01-.025.052v.002h-.001a.18.18 0 01-.33-.104V14h-2.715v8.87h2.715c.34-.018.72-.268.863-.574l2.218-4.756a.184.184 0 01.006-.012zM10.199 17.68l-1.587 5.19H6.658L5.07 17.677a.179.179 0 00-.354.035v5.16H2V14h3.025c.5 0 1.023.388 1.17.865l1.32 4.323c.067.215.173.215.239 0l1.322-4.323c.146-.478.67-.865 1.169-.865h3.024v8.87h-2.715v-5.159-.002a.179.179 0 00-.355-.03z",
  fill: "#37A72E"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
  id: "paint0_linear",
  x1: "24.358",
  y1: "15.788",
  x2: "33.489",
  y2: "15.788",
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "#00A0E5"
}), /*#__PURE__*/React.createElement("stop", {
  offset: "1",
  stopColor: "#0077C3"
})), /*#__PURE__*/React.createElement("clipPath", {
  id: "clip0"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  transform: "translate(2 14)",
  d: "M0 0h31.5v8.916H0z"
})))));

function _extends$x() { _extends$x = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$x.apply(this, arguments); }
var TroySVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$x({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#clip0)",
  fillRule: "evenodd",
  clipRule: "evenodd"
}, /*#__PURE__*/React.createElement("path", {
  d: "M30.871 13.333c-.439 0-.923.245-1.116.684L27.7 18.724l-.74-4.707c-.086-.44-.385-.684-.858-.684h-2.601l2.224 7.816c.041.15.05.316.02.49-.117.653-.74 1.182-1.393 1.182h-1.456c-.37 0-.614.229-.733.748l-.324 1.937h2.566c1.338 0 2.91-.673 3.958-2.532l5.136-9.641H30.87zM5.622 11c.768 0 1.14.306 1.004 1.076l-.221 1.255H8.17l-.39 2.204H6.016l-.48 2.728c-.167.951.774 1.078 1.314 1.078.107 0 .197-.003.26-.007l-.43 2.458c-.134.014-.271.033-.562.033-1.34 0-3.874-.36-3.376-3.187l.546-3.103H2l.387-2.204h1.274L4.071 11h1.551z",
  fill: "#323E48"
}), /*#__PURE__*/React.createElement("path", {
  d: "M20.452 13.432L20 16.002a1.723 1.723 0 01-.572 3.246l-.454 2.569a4.288 4.288 0 001.479-8.385zM18.434 19.073a1.724 1.724 0 01-.942-1.536c0-.878.663-1.607 1.515-1.71l.453-2.568a4.29 4.29 0 00-1.478 8.385l.452-2.571z",
  fill: "#00ADBB"
}), /*#__PURE__*/React.createElement("path", {
  d: "M9.191 13.332h1.517c.768 0 1.138.307 1.002 1.078l-.166.932c.566-1.147 1.791-2.093 3.048-2.093.165 0 .324.032.324.032l-.49 2.78s-.22-.052-.564-.052c-.67 0-1.803.212-2.433 1.471-.15.31-.266.688-.346 1.143l-.549 3.121H7.71l1.482-8.412z",
  fill: "#323E48"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "clip0"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  transform: "translate(2 11)",
  d: "M0 0h31.5v14.506H0z"
})))));

function _extends$w() { _extends$w = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$w.apply(this, arguments); }
var UnionpaySVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$w({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M31.82 8H3.68A1.68 1.68 0 002 9.68v16.422c0 .928.752 1.68 1.68 1.68h28.14a1.68 1.68 0 001.68-1.68V9.68A1.68 1.68 0 0031.82 8z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M10.476 10.31h5.98c.835 0 1.357.69 1.161 1.532l-2.786 12.074c-.196.843-1.034 1.532-1.869 1.532h-5.98c-.835 0-1.356-.69-1.16-1.532l2.785-12.074c.196-.847 1.03-1.532 1.865-1.532h.004z",
  fill: "#D10429"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M15.934 10.31h6.88c.834 0 .456.69.26 1.532l-2.786 12.074c-.195.843-.134 1.532-.969 1.532h-6.88c-.839 0-1.356-.69-1.156-1.532l2.786-12.074c.195-.847 1.03-1.532 1.869-1.532h-.005z",
  fill: "#022E64"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M22.572 10.31h5.98c.835 0 1.357.69 1.161 1.532l-2.786 12.074c-.195.843-1.034 1.532-1.869 1.532h-5.98c-.84 0-1.356-.69-1.16-1.532l2.785-12.074c.196-.847 1.03-1.532 1.865-1.532h.004z",
  fill: "#076F74"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M21.568 21.693h.565l.16-.549h-.56l-.165.549zm.452-1.51l-.196.65s.213-.11.33-.145c.118-.027.292-.049.292-.049l.135-.452h-.565l.004-.004zm.282-.93l-.186.623s.208-.097.326-.128c.117-.03.29-.04.29-.04l.136-.451h-.561l-.005-.005zm1.248 0l-.73 2.435h.195l-.152.505h-.195l-.048.154h-.696l.048-.154h-1.408l.14-.465h.142l.74-2.476.147-.5h.709l-.074.25s.187-.136.37-.184c.177-.048 1.203-.066 1.203-.066l-.152.496h-.243l.004.004z",
  fill: "#FEFEFE"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M23.84 18.752h.756l.009.285c-.004.049.035.07.126.07h.152l-.139.47h-.409c-.352.026-.486-.127-.478-.298l-.013-.523-.004-.004zm.093 2.234h-.721l.121-.416h.826l.117-.382h-.812l.139-.47h2.26l-.14.47h-.76l-.117.382h.76l-.126.416h-.821l-.148.176h.335l.082.527c.01.052.01.087.026.11.018.017.118.026.174.026h.1l-.156.513h-.257c-.039 0-.1-.004-.182-.004-.078-.009-.13-.053-.183-.08a.244.244 0 01-.134-.18l-.079-.526-.373.518a.636.636 0 01-.552.29h-.522l.14-.457h.199a.308.308 0 00.148-.044.21.21 0 00.108-.092l.548-.777zm-7.892-1.142h1.908l-.139.46h-.76l-.118.392h.783l-.144.473h-.778l-.191.637c-.022.07.187.079.26.079l.392-.053-.157.527h-.878a.833.833 0 01-.2-.027.204.204 0 01-.139-.1c-.03-.053-.082-.093-.047-.207l.252-.842h-.435l.143-.483h.435l.117-.39h-.434l.139-.462-.009-.004zm1.318-.833h.783l-.144.478H16.93l-.117.101c-.052.049-.065.031-.13.066a.767.767 0 01-.353.092h-.343l.14-.46h.103a.406.406 0 00.179-.027.368.368 0 00.113-.15l.195-.359h.778l-.135.263v-.004zm1.475.79s.213-.197.578-.259c.083-.018.604-.009.604-.009l.078-.263h-1.1l-.16.535v-.004zm1.034.202h-1.09l-.066.224h.948c.113-.014.134.004.143-.005l.07-.22h-.005zm-1.416-1.247h.664l-.095.338s.209-.17.356-.232c.148-.053.478-.106.478-.106l1.078-.004-.37 1.238c-.06.21-.134.346-.177.412a.466.466 0 01-.183.171.63.63 0 01-.252.084c-.07.004-.183.009-.33.009h-1.04l-.29.978c-.026.097-.04.145-.022.172.013.021.052.048.1.048l.456-.044-.156.535h-.513a6.78 6.78 0 01-.365-.008c-.078-.01-.161 0-.218-.044-.047-.044-.121-.101-.117-.158.005-.053.026-.14.061-.264l.934-3.125z",
  fill: "#FEFEFE"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M20.39 20.726l-.062.298c-.026.093-.047.163-.117.224a.479.479 0 01-.356.128l-.37.017-.004.334c-.004.092.022.083.035.1.017.018.03.023.048.031l.117-.008.352-.018-.148.492h-.404c-.283 0-.496-.01-.56-.062-.07-.044-.079-.096-.079-.193l.026-1.308h.648l-.009.268h.156a.243.243 0 00.113-.018c.022-.013.035-.035.044-.066l.065-.21h.509l-.005-.009zm-9.214-6.552l-.44 2.04c-.09.392-.156.672-.373.852a.666.666 0 01-.443.154c-.274 0-.43-.136-.457-.395l-.004-.088.083-.526s.434-1.765.512-1.997c.005-.014.005-.022.005-.027-.848.009-1 0-1.009-.013l-.026.127-.443 1.989-.04.166-.073.553c0 .163.03.299.096.408.204.36.782.413 1.108.413.421 0 .817-.092 1.082-.254.465-.277.587-.712.691-1.093l.052-.198s.448-1.83.526-2.067c.005-.013.005-.022.009-.026-.617.004-.795 0-.856-.014v-.004zm2.479 3.638a9.165 9.165 0 00-.76.013l-.014-.026c.03-.136.066-.268.092-.404l.043-.184c.065-.285.126-.623.135-.724.008-.062.026-.215-.148-.215-.074 0-.148.035-.226.07-.043.154-.126.584-.17.777-.086.412-.09.46-.13.662l-.026.027a9.446 9.446 0 00-.774.013l-.017-.03c.06-.247.117-.492.174-.738.148-.663.187-.917.226-1.255l.03-.018c.348-.048.43-.061.809-.136l.03.035-.056.21c.065-.039.126-.078.19-.109.18-.088.375-.114.483-.114.165 0 .348.048.422.241.07.171.026.382-.07.8l-.047.21c-.096.465-.114.548-.166.864l-.034.027.004.004zm1.22.001c-.182 0-.3-.004-.412 0-.113 0-.222.01-.392.014l-.008-.014-.009-.017c.048-.176.07-.237.096-.299a2.55 2.55 0 00.086-.302c.053-.233.087-.396.11-.54.025-.136.038-.255.056-.391l.013-.009.013-.013.413-.061c.117-.018.239-.04.426-.075l.008.018.005.017-.105.435c-.034.145-.07.29-.1.434-.065.307-.095.421-.108.505-.018.079-.022.118-.048.276l-.018.014-.017.013-.008-.005zm1.93-1.078c-.013.079-.083.373-.174.496-.065.092-.139.15-.226.15-.026 0-.174 0-.178-.225 0-.11.022-.223.048-.346.078-.356.174-.65.413-.65.187 0 .2.22.117.575zm.787.035c.104-.465.021-.685-.079-.816-.156-.202-.434-.268-.721-.268-.174 0-.583.018-.904.316-.23.215-.34.51-.4.79-.065.285-.139.799.33.992.144.061.352.079.487.079.343 0 .696-.097.96-.378.205-.228.296-.57.331-.715h-.004zm7.326 1.095a9.58 9.58 0 00-.804.014l-.022-.027c.091-.346.183-.698.265-1.049.104-.456.13-.65.165-.917l.026-.022c.361-.053.461-.066.84-.136l.008.03c-.07.29-.135.576-.204.861-.14.601-.187.909-.24 1.224l-.034.027v-.005z",
  fill: "#FEFEFE"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M24.393 16.794c-.018.074-.087.373-.178.496a.415.415 0 01-.292.145c-.026 0-.17 0-.178-.22 0-.11.022-.224.048-.347.078-.346.174-.64.413-.64.187 0 .27.215.187.57v-.004zm.717.035c.104-.465-.322-.04-.387-.193-.104-.242-.04-.724-.456-.887-.161-.066-.54.018-.86.316-.227.21-.34.505-.4.786-.066.28-.14.799.325.979a.854.854 0 00.418.074c.469-.026.825-.742 1.09-1.023.205-.223.24.084.27-.052zm-5.458.983a9.064 9.064 0 00-.756.013l-.013-.026c.03-.136.065-.268.096-.404l.039-.184c.065-.285.13-.623.134-.724.01-.062.026-.215-.143-.215a.563.563 0 00-.226.07c-.04.154-.126.584-.17.777-.082.412-.09.46-.13.662l-.026.027a9.446 9.446 0 00-.774.013l-.017-.03c.06-.247.117-.492.174-.738.148-.663.182-.917.226-1.255l.026-.018c.348-.048.435-.061.808-.136l.03.035-.051.21a1.173 1.173 0 01.67-.224c.164 0 .342.05.42.242.07.171.022.382-.073.8l-.048.21c-.1.465-.113.548-.165.864l-.035.027.004.004zm2.604-3.634l-.252.005c-.652.009-.913.004-1.017-.009a6.497 6.497 0 01-.026.132s-.235 1.088-.235 1.093l-.582 2.427c.57-.01.8-.01.9.004.021-.11.152-.755.156-.755l.117-.491s.035-.049.07-.07h.052c.491 0 1.043 0 1.478-.321.295-.22.495-.549.587-.944.021-.096.039-.21.039-.329a.702.702 0 00-.117-.421c-.222-.312-.661-.316-1.17-.32zm.326 1.137a.79.79 0 01-.408.544c-.165.084-.365.092-.574.092h-.135l.009-.052s.248-1.089.248-1.084l.008-.057.005-.044.1.009.521.043c.2.08.287.281.226.55zm5.343.364l-.03-.035c-.37.075-.44.088-.778.136l-.027.026c0 .005-.004.01-.004.018v-.004c-.252.592-.248.465-.452.93 0-.022 0-.035-.004-.057l-.052-1.01-.03-.035c-.392.075-.4.088-.757.136l-.026.027c-.005.013-.005.026-.005.04l.005.004c.043.232.035.18.078.544.022.18.048.36.07.535.034.299.056.444.1.896-.244.404-.3.557-.535.913l.013.035c.352-.013.43-.013.691-.013l.057-.066c.195-.426 1.69-3.016 1.69-3.016l-.004-.004zm-12.714.29c.2-.14.226-.333.056-.434-.17-.1-.47-.07-.669.07-.2.137-.222.33-.052.435.165.097.465.07.665-.07z",
  fill: "#FEFEFE"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M26.18 18.756l-.29.505a.573.573 0 01-.535.303l-.465-.009.135-.456h.091a.246.246 0 00.109-.018c.026-.009.039-.026.06-.052l.174-.277h.726l-.004.004z",
  fill: "#FEFEFE"
})));

function _extends$v() { _extends$v = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$v.apply(this, arguments); }
var VisaSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$v({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M33.75 26.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V9.75a3 3 0 013-3h25.5a3 3 0 013 3v16.5z",
  fill: "#1565C0"
}), /*#__PURE__*/React.createElement("path", {
  d: "M11.39 14.25l-1.97 5.874s-.5-2.485-.55-2.797c-1.121-2.558-2.775-2.416-2.775-2.416l1.95 7.589v-.002h2.37l3.279-8.248h-2.305zm1.877 8.25h2.153l1.302-8.25h-2.18l-1.275 8.25zm15.239-8.25H26.24l-3.532 8.25h2.139l.44-1.178h2.698l.23 1.178h1.959l-1.668-8.25zm-2.621 5.496l1.172-3.118.614 3.118h-1.786zm-6.108-3.091c0-.455.373-.793 1.444-.793.696 0 1.494.505 1.494.505l.349-1.732s-1.018-.386-2.018-.386c-2.264 0-3.432 1.083-3.432 2.454 0 2.48 2.984 2.14 2.984 3.413 0 .219-.173.724-1.416.724-1.246 0-2.07-.457-2.07-.457l-.37 1.662s.797.454 2.337.454c1.544 0 3.686-1.155 3.686-2.814 0-1.995-2.988-2.14-2.988-3.03z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M9.159 18.709l-.725-3.561s-.327-.772-1.18-.772h-3.33s4.247 1.254 5.235 4.333z",
  fill: "#FFC107"
})));

function _extends$u() { _extends$u = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$u.apply(this, arguments); }
var VisaelectronSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$u({
  width: "36",
  height: "36",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M34.871 27.419c0 .621-.5 1.128-1.122 1.128H2.251a1.125 1.125 0 01-1.123-1.128V8.575c0-.621.501-1.122 1.123-1.122h31.498c.622 0 1.122.5 1.122 1.122v18.844z",
  fill: "#075FA0"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M12.49 19.329c2.556-1.091 6.138-2.087 10.887-1.091l.044-1.205c-5.002-.545-8.598 1.053-10.93 2.181v.115z",
  fill: "#FBAA28"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M7.88 20.216h2.41v.723H8.794l-.006.672h1.49v.69H8.78l.007.673h1.47v.723h-2.37l-.007-3.48zM10.892 19.924h.876l.006 3.773h-.875l-.007-3.773zM14.786 23.412c-.298.253-.755.38-1.154.38-.92 0-1.338-.653-1.338-1.306 0-.767.57-1.414 1.363-1.414.964 0 1.268.685 1.268 1.554h-1.756c0 .196.127.532.608.532.305 0 .476-.095.717-.254l.292.508zm-.647-1.3c0-.26-.127-.495-.419-.495-.279 0-.488.248-.545.495h.964zM17.581 23.526c-.24.152-.64.266-.931.266-.806 0-1.383-.463-1.383-1.3 0-.856.57-1.42 1.44-1.42.304 0 .665.095.862.222l-.285.57a1.142 1.142 0 00-.413-.095c-.437 0-.729.26-.729.679 0 .361.286.647.653.647.165 0 .336-.076.476-.146l.31.577zM17.905 21.16h.4l-.007-.855h.843l-.019.856h.723l-.019.634h-.717c0 .355-.025.729-.025.944 0 .222.057.324.254.324.12 0 .222-.057.317-.12l.196.595c-.26.159-.57.254-.868.254-.71 0-.749-.45-.749-1.033 0-.311.026-.622.026-.964h-.374l.019-.634zM20.295 21.16h.875v.362h.006c.133-.216.305-.431.647-.431.095 0 .197.013.286.032l-.115.868a1.055 1.055 0 00-.31-.05c-.292 0-.52.24-.52.291l-.02 1.465h-.874l.025-2.536zM23.693 21.072c.939 0 1.42.596 1.42 1.357 0 .767-.481 1.363-1.42 1.363-.931 0-1.42-.596-1.42-1.363 0-.76.489-1.357 1.42-1.357zm0 2.054c.419 0 .546-.361.546-.697 0-.33-.127-.691-.546-.691-.418 0-.539.361-.539.69 0 .337.12.698.54.698zM25.59 21.16h.875v.28c.202-.197.513-.368.823-.368.54 0 .831.298.831.989 0 .52-.006 1.078-.012 1.636h-.876c.007-.488.013-.977.013-1.503 0-.21-.057-.368-.317-.368-.146 0-.342.121-.462.235v1.636h-.895l.02-2.536zM15.09 15.936l1.103-3.645h.99l-1.11 3.645h-.983zM14.646 12.29L13.34 14.7h-.02c.02-.323-.024-.723-.03-.945l-.096-1.464h-1.661l-.02.095c.426 0 .68.215.749.653l.323 2.897h1.027l2.068-3.645h-1.034zM22.324 15.936l-.025-.545h-1.237l-.247.545h-1.072l1.94-3.639h1.32l.33 3.64h-1.01zm-.114-2.15c-.007-.272-.019-.64 0-.861h-.013c-.063.183-.317.729-.431.995l-.368.799h.863l-.05-.932zM18.222 16.038c-.698 0-1.16-.216-1.49-.419l.47-.716c.29.165.525.355 1.064.355.172 0 .337-.045.432-.203.139-.24-.032-.368-.419-.583l-.19-.127c-.571-.393-.818-.76-.552-1.408.178-.418.628-.729 1.376-.729a2.2 2.2 0 011.287.444l-.545.634c-.273-.222-.501-.336-.76-.336-.21 0-.369.076-.426.19-.101.203.032.342.336.527l.229.146c.69.437.855.893.684 1.324-.298.736-.887.9-1.496.9z",
  fill: "#fff"
})));

const defaultFormat = /(\d{1,4})/g;
const creditCardValidations = [
  {
    type: "amex",
    pattern: /^3[47]/,
    format: /(\d{1,4})(\d{1,6})?(\d{1,5})?/,
    length: [15],
    cvcLength: [4],
    luhn: true
  },
  {
    type: "dankort",
    pattern: /^5019/,
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "dinersclub",
    pattern: /^(36|38|30[0-5])/,
    format: /(\d{1,4})(\d{1,6})?(\d{1,4})?/,
    length: [14],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "discover",
    pattern: /^(6011|65|64[4-9]|622)/,
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "elo",
    pattern: /^401178|^401179|^431274|^438935|^451416|^457393|^457631|^457632|^504175|^627780|^636297|^636369|^636368|^(506699|5067[0-6]\d|50677[0-8])|^(50900\d|5090[1-9]\d|509[1-9]\d{2})|^65003[1-3]|^(65003[5-9]|65004\d|65005[0-1])|^(65040[5-9]|6504[1-3]\d)|^(65048[5-9]|65049\d|6505[0-2]\d|65053[0-8])|^(65054[1-9]|6505[5-8]\d|65059[0-8])|^(65070\d|65071[0-8])|^65072[0-7]|^(65090[1-9]|65091\d|650920)|^(65165[2-9]|6516[6-7]\d)|^(65500\d|65501\d)|^(65502[1-9]|6550[3-4]\d|65505[0-8])|^(65092[1-9]|65097[0-8])/,
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "hipercard",
    pattern: /^(384100|384140|384160|606282|637095|637568|60(?!11))/,
    format: defaultFormat,
    length: [14, 15, 16, 17, 18, 19],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "jcb",
    pattern: /^(308[8-9]|309[0-3]|3094[0]{4}|309[6-9]|310[0-2]|311[2-9]|3120|315[8-9]|333[7-9]|334[0-9]|35)/,
    format: defaultFormat,
    length: [16, 19],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "laser",
    pattern: /^(6706|6771|6709)/,
    format: defaultFormat,
    length: [16, 19],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "maestro",
    pattern: /^(50|5[6-9]|6007|6220|6304|6703|6708|6759|676[1-3])/,
    format: defaultFormat,
    length: [12, 13, 14, 15, 16, 17, 18, 19],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "mastercard",
    pattern: /^(5[1-5]|677189)|^(222[1-9]|2[3-6]\d{2}|27[0-1]\d|2720)/,
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "mir",
    pattern: /^220[0-4][0-9][0-9]\d{10}$/,
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "troy",
    pattern: /^9792/,
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "unionpay",
    pattern: /^62/,
    format: defaultFormat,
    length: [16, 17, 18, 19],
    cvcLength: [3],
    luhn: false
  },
  {
    type: "visaelectron",
    pattern: /^4(026|17500|405|508|844|91[37])/,
    format: defaultFormat,
    length: [16],
    cvcLength: [3],
    luhn: true
  },
  {
    type: "visa",
    pattern: /^4/,
    format: defaultFormat,
    length: [13, 16],
    cvcLength: [3],
    luhn: true
  }
];
const validateCard = (value) => {
  const newValue = value.replace(/\D/g, "");
  let sum = 0;
  let shouldDouble = false;
  for (let i = newValue.length - 1; i >= 0; i--) {
    let digit = parseInt(newValue.charAt(i));
    if (shouldDouble) {
      if ((digit *= 2) > 9)
        digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  const valid = sum % 10 === 0;
  let accepted = false;
  let issuer;
  for (const card of creditCardValidations) {
    const { pattern, type } = card;
    const regex = pattern;
    if (regex.test(value)) {
      accepted = true;
      issuer = type;
      break;
    }
  }
  return {
    valid: valid && accepted,
    issuer
  };
};
const renderIssuer = (issuer) => {
  switch (issuer) {
    case "amex":
      return /* @__PURE__ */ React.createElement(AmexSVG, null);
    case "dankort":
      return /* @__PURE__ */ React.createElement(DankortSVG, null);
    case "dinersclub":
      return /* @__PURE__ */ React.createElement(DinersSVG, null);
    case "discover":
      return /* @__PURE__ */ React.createElement(DiscoverSVG, null);
    case "elo":
      return /* @__PURE__ */ React.createElement(EloSVG, null);
    case "hipercard":
      return /* @__PURE__ */ React.createElement(HipercardSVG, null);
    case "jcb":
      return /* @__PURE__ */ React.createElement(JbcSVG, null);
    case "laser":
      return /* @__PURE__ */ React.createElement(LaserSVG, null);
    case "maestro":
      return /* @__PURE__ */ React.createElement(MaestroSVG, null);
    case "mastercard":
      return /* @__PURE__ */ React.createElement(MastercardSVG, null);
    case "mir":
      return /* @__PURE__ */ React.createElement(MirSVG, null);
    case "troy":
      return /* @__PURE__ */ React.createElement(TroySVG, null);
    case "unionpay":
      return /* @__PURE__ */ React.createElement(UnionpaySVG, null);
    case "visa":
      return /* @__PURE__ */ React.createElement(VisaSVG, null);
    case "visaelectron":
      return /* @__PURE__ */ React.createElement(VisaelectronSVG, null);
    default:
      return null;
  }
};
const formatCardNumber = (card, issuer) => {
  let nextValue = "";
  switch (issuer) {
    case "amex": {
      const segment1 = card.slice(0, 4);
      const segment2 = card.slice(4, 10);
      const segment3 = card.slice(10, 15);
      nextValue = `${segment1} ${segment2} ${segment3}`;
      break;
    }
    case "dinersclub": {
      const segment1 = card.slice(0, 4);
      const segment2 = card.slice(4, 10);
      const segment3 = card.slice(10, 14);
      nextValue = `${segment1} ${segment2} ${segment3}`;
      break;
    }
    default: {
      const segment1 = card.slice(0, 4);
      const segment2 = card.slice(4, 8);
      const segment3 = card.slice(8, 12);
      const segment4 = card.slice(12, 19);
      nextValue = `${segment1} ${segment2} ${segment3} ${segment4}`;
      break;
    }
  }
  return nextValue.trim();
};

var __defProp$E = Object.defineProperty;
var __defProps$s = Object.defineProperties;
var __getOwnPropDescs$s = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$E = Object.getOwnPropertySymbols;
var __hasOwnProp$E = Object.prototype.hasOwnProperty;
var __propIsEnum$E = Object.prototype.propertyIsEnumerable;
var __defNormalProp$E = (obj, key, value) => key in obj ? __defProp$E(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$E = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$E.call(b, prop))
      __defNormalProp$E(a, prop, b[prop]);
  if (__getOwnPropSymbols$E)
    for (var prop of __getOwnPropSymbols$E(b)) {
      if (__propIsEnum$E.call(b, prop))
        __defNormalProp$E(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$s = (a, b) => __defProps$s(a, __getOwnPropDescs$s(b));
const DEFAULT_YEAR_FORMAT = "YY";
const isFormValid = (state) => {
  return __spreadProps$s(__spreadValues$E({}, state), { valid: !state.invalidFields.length });
};
const checkField = (state, name, valid) => {
  let invalidFields = state.invalidFields.filter((field) => field !== name);
  if (!valid) {
    invalidFields = [...invalidFields, name];
  }
  const newState = __spreadProps$s(__spreadValues$E({}, state), { invalidFields });
  return isFormValid(newState);
};
const isCardHolderValid = (cardHolder) => {
  let message = void 0;
  if (containsASpecialCharacter(cardHolder)) {
    message = "Usar caracteres especiales no est\xE1 permitido";
  }
  if (containsANumber(cardHolder)) {
    message = "Usar n\xFAmeros no est\xE1 permitido";
  }
  if (cardHolder.length < 2) {
    message = "Nombre inv\xE1lido";
  }
  return {
    valid: !message,
    message
  };
};
const isCardNumberValid = (cardNumber) => {
  let message = void 0;
  const { valid } = validateCard(cardNumber);
  if (!valid) {
    message = "El n\xFAmero de tarjeta no es v\xE1lido";
  }
  if (containsALetter(cardNumber)) {
    message = "Usar letras no est\xE1 permitido";
  }
  if (containsASpecialCharacter(cardNumber)) {
    message = "Usar caracteres especiales no est\xE1 permitido";
  }
  return {
    valid: !message,
    message
  };
};
const isExpirationMonthValid = (expirationMonth, expirationYear) => {
  let message = void 0;
  if (containsMoreThanNumbers(expirationMonth)) {
    message = "Solo debe contener n\xFAmeros";
  }
  if (expirationMonth.length !== 2) {
    message = "Debe contener 2 d\xEDgitos";
  }
  const year = new Date().getFullYear();
  const yearInt = parseInt(expirationYear, 10);
  const selectedYear = expirationYear.length === 2 ? yearInt + 2e3 : yearInt;
  const month = new Date().getMonth() + 1;
  const selectedMonth = parseInt(expirationMonth, 10);
  if (selectedMonth > 12 || selectedMonth < 1) {
    message = "Mes inv\xE1lido";
  }
  if (!message && selectedYear < year) {
    message = "Mes vencido";
  }
  if (!message && selectedYear === year && month > selectedMonth) {
    message = "Mes vencido";
  }
  return {
    valid: !message,
    message
  };
};
const isExpirationYearValid = (expirationYear, format) => {
  let message = void 0;
  if (containsMoreThanNumbers(expirationYear)) {
    message = "Solo debe contener n\xFAmeros";
  }
  if (expirationYear.length !== format.length) {
    message = `Debe contener ${format.length} d\xEDgitos`;
  }
  const year = new Date().getFullYear();
  const yearInt = parseInt(expirationYear, 10);
  const selectedYear = format.length === 2 ? yearInt + 2e3 : yearInt;
  if (!message && selectedYear < year) {
    message = "A\xF1o vencido";
  }
  return {
    valid: !message,
    message
  };
};
const isCVVValid = (cvv, maxLength) => {
  let message = void 0;
  if (containsMoreThanNumbers(cvv)) {
    message = "Solo debe contener n\xFAmeros";
  }
  if (cvv.length !== maxLength) {
    message = `Debe contener ${maxLength} d\xEDgitos`;
  }
  return {
    valid: !message,
    message
  };
};
const setRef = (ref, childRef) => {
  return (instance) => {
    ref.current = instance;
    childRef(instance);
    return instance;
  };
};

var __defProp$D = Object.defineProperty;
var __getOwnPropSymbols$D = Object.getOwnPropertySymbols;
var __hasOwnProp$D = Object.prototype.hasOwnProperty;
var __propIsEnum$D = Object.prototype.propertyIsEnumerable;
var __defNormalProp$D = (obj, key, value) => key in obj ? __defProp$D(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$D = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$D.call(b, prop))
      __defNormalProp$D(a, prop, b[prop]);
  if (__getOwnPropSymbols$D)
    for (var prop of __getOwnPropSymbols$D(b)) {
      if (__propIsEnum$D.call(b, prop))
        __defNormalProp$D(a, prop, b[prop]);
    }
  return a;
};
var __objRest$m = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$D.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$D)
    for (var prop of __getOwnPropSymbols$D(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$D.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const CardHolder = forwardRef((props, ref) => {
  var _b;
  const _a = props, {
    className,
    onChange,
    __formValues__,
    __formName__,
    __setRef__,
    onBlur,
    onFocus,
    errorMessage
  } = _a, rest = __objRest$m(_a, [
    "className",
    "onChange",
    "__formValues__",
    "__formName__",
    "__setRef__",
    "onBlur",
    "onFocus",
    "errorMessage"
  ]);
  const value = (_b = __formValues__ == null ? void 0 : __formValues__.cardHolder) != null ? _b : "";
  const name = `cardHolder${__formName__ ? ` ${__formName__}` : ""}`;
  const [error, setError] = useState();
  const changeHandler = (e) => {
    let newValue = e.target.value.replace(/[^A-zÀ-ž\s]+/g, "");
    newValue = newValue.replace(/[\^_]+/g, "");
    e.target.value = newValue;
    const { message } = isCardHolderValid(newValue);
    setError(message);
    onChange == null ? void 0 : onChange(e);
  };
  const blurHandler = (e) => {
    const { message } = isCardHolderValid(__formValues__.cardHolder);
    setError(message);
    onBlur == null ? void 0 : onBlur(e);
  };
  const focusHandler = (e) => {
    setError(void 0);
    onFocus == null ? void 0 : onFocus(e);
  };
  useEffect(() => {
    if (!errorMessage)
      return;
    setError(errorMessage);
  }, [errorMessage]);
  return /* @__PURE__ */ React.createElement(CardHolderStyled, __spreadValues$D({
    className: `CardHolder ${className}`,
    name,
    placeholder: "Nombre en tarjeta",
    value,
    onChange: changeHandler,
    pattern: "([A-z\xC0-\u017E\\s]){2,}",
    errorMessage: error,
    onBlur: blurHandler,
    onFocus: focusHandler,
    required: true,
    minLength: 2,
    ref: typeof ref === "function" ? __setRef__ : ref
  }, rest));
});
CardHolder.defaultProps = {
  className: "",
  label: "Nombre en tarjeta"
};

const CardNumberStyled = styled.div`
  position: relative;

  .CardNumber {
    &__input {
    }

    &__issuer {
      position: absolute;
      bottom: ${(props) => props.bottom}px;
      right: 8px;
      transform: translateY(50%);
      display: flex;
      align-items: center;
      z-index: 1;
    }
  }
`;

var __defProp$C = Object.defineProperty;
var __getOwnPropSymbols$C = Object.getOwnPropertySymbols;
var __hasOwnProp$C = Object.prototype.hasOwnProperty;
var __propIsEnum$C = Object.prototype.propertyIsEnumerable;
var __defNormalProp$C = (obj, key, value) => key in obj ? __defProp$C(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$C = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$C.call(b, prop))
      __defNormalProp$C(a, prop, b[prop]);
  if (__getOwnPropSymbols$C)
    for (var prop of __getOwnPropSymbols$C(b)) {
      if (__propIsEnum$C.call(b, prop))
        __defNormalProp$C(a, prop, b[prop]);
    }
  return a;
};
var __objRest$l = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$C.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$C)
    for (var prop of __getOwnPropSymbols$C(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$C.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const CardNumber = forwardRef((props, ref) => {
  var _b;
  const _a = props, {
    className,
    onChange,
    __formValues__,
    __formName__,
    __setRef__,
    onBlur,
    onFocus,
    errorMessage
  } = _a, rest = __objRest$l(_a, [
    "className",
    "onChange",
    "__formValues__",
    "__formName__",
    "__setRef__",
    "onBlur",
    "onFocus",
    "errorMessage"
  ]);
  const { label } = props;
  const value = (_b = __formValues__ == null ? void 0 : __formValues__.cardNumber) != null ? _b : "";
  const name = `cardNumber${__formName__ ? ` ${__formName__}` : ""}`;
  const [error, setError] = useState();
  const [bottom, setBottom] = useState(0);
  const wrapperRef = useRef(null);
  const [issuer, setIssuer] = useState(validateCard(value).issuer);
  const changeHandler = (e) => {
    const value2 = e.target.value;
    const { issuer: cardIssuer } = validateCard(value2);
    const digits = value2.replace(/\D+/g, "");
    const nextValue = formatCardNumber(digits, cardIssuer);
    e.target.value = nextValue;
    const { message } = isCardNumberValid(nextValue);
    setError(message ? message : void 0);
    onChange == null ? void 0 : onChange(e);
    setIssuer(cardIssuer);
  };
  const blurHandler = (e) => {
    const { message } = isCardNumberValid(__formValues__.cardNumber);
    setError(message ? message : void 0);
    onBlur == null ? void 0 : onBlur(e);
  };
  const focusHandler = (e) => {
    setError(void 0);
    onFocus == null ? void 0 : onFocus(e);
  };
  useIsomorphicLayoutEffect(() => {
    var _a2;
    const wrapper = wrapperRef.current;
    if (!wrapper)
      return;
    const [input] = wrapper.children[0].getElementsByTagName("input");
    const inputHeight = input.getBoundingClientRect().height;
    const errorClassName = "TextInput__errorMessage";
    const errorNode = wrapper.getElementsByClassName(errorClassName)[0];
    const errorHeight = (_a2 = errorNode == null ? void 0 : errorNode.getBoundingClientRect().height) != null ? _a2 : 0;
    setBottom(inputHeight / 2 + errorHeight);
  }, [error, label, errorMessage]);
  useEffect(() => {
    if (!errorMessage)
      return;
    setError(errorMessage);
  }, [errorMessage]);
  return /* @__PURE__ */ React.createElement(CardNumberStyled, {
    className: `CardNumber ${className}`,
    bottom,
    ref: wrapperRef
  }, /* @__PURE__ */ React.createElement(TextInput, __spreadValues$C({
    className: "CardNumber__input",
    name,
    placeholder: "0000 0000 0000 0000",
    value,
    onChange: changeHandler,
    onBlur: blurHandler,
    onFocus: focusHandler,
    errorMessage: error,
    required: true,
    ref: typeof ref === "function" ? __setRef__ : ref
  }, rest)), /* @__PURE__ */ React.createElement("div", {
    className: "CardNumber__issuer"
  }, renderIssuer(issuer)));
});
CardNumber.defaultProps = {
  className: "",
  label: "N\xFAmero de tarjeta"
};

const ExpirationMonthStyled = styled(TextInput)`
  .ExpirationMonth {
  }
`;

var __defProp$B = Object.defineProperty;
var __getOwnPropSymbols$B = Object.getOwnPropertySymbols;
var __hasOwnProp$B = Object.prototype.hasOwnProperty;
var __propIsEnum$B = Object.prototype.propertyIsEnumerable;
var __defNormalProp$B = (obj, key, value) => key in obj ? __defProp$B(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$B = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$B.call(b, prop))
      __defNormalProp$B(a, prop, b[prop]);
  if (__getOwnPropSymbols$B)
    for (var prop of __getOwnPropSymbols$B(b)) {
      if (__propIsEnum$B.call(b, prop))
        __defNormalProp$B(a, prop, b[prop]);
    }
  return a;
};
var __objRest$k = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$B.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$B)
    for (var prop of __getOwnPropSymbols$B(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$B.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const ExpirationMonth = forwardRef((props, ref) => {
  var _b;
  const _a = props, {
    className,
    onChange,
    __formValues__,
    __formName__,
    __setRef__,
    onBlur,
    onFocus,
    errorMessage
  } = _a, rest = __objRest$k(_a, [
    "className",
    "onChange",
    "__formValues__",
    "__formName__",
    "__setRef__",
    "onBlur",
    "onFocus",
    "errorMessage"
  ]);
  const value = (_b = __formValues__ == null ? void 0 : __formValues__.expirationMonth) != null ? _b : "";
  const name = `expirationMonth${__formName__ ? ` ${__formName__}` : ""}`;
  const [error, setError] = useState();
  const changeHandler = (e) => {
    const value2 = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = value2;
    const { message } = isExpirationMonthValid(value2, __formValues__.expirationYear);
    setError(message);
    onChange == null ? void 0 : onChange(e);
  };
  const blurHandler = (e) => {
    const { message } = isExpirationMonthValid(__formValues__.expirationMonth, __formValues__.expirationYear);
    setError(message);
    onBlur == null ? void 0 : onBlur(e);
  };
  const focusHandler = (e) => {
    setError(void 0);
    onFocus == null ? void 0 : onFocus(e);
  };
  useEffect(() => {
    if (!errorMessage)
      return;
    setError(errorMessage);
  }, [errorMessage]);
  return /* @__PURE__ */ React.createElement(ExpirationMonthStyled, __spreadValues$B({
    className: `ExpirationMonth ${className}`,
    value,
    name,
    onChange: changeHandler,
    placeholder: "MM",
    maxLength: 2,
    pattern: "[0-9]*",
    inputMode: "numeric",
    onFocus: focusHandler,
    onBlur: blurHandler,
    errorMessage: error,
    required: true,
    ref: typeof ref === "function" ? __setRef__ : ref
  }, rest));
});
ExpirationMonth.defaultProps = {
  className: "",
  label: "Mes de expiraci\xF3n"
};

const ExpirationYearStyled = styled(TextInput)`
  .ExpirationYear {
  }
`;

var __defProp$A = Object.defineProperty;
var __getOwnPropSymbols$A = Object.getOwnPropertySymbols;
var __hasOwnProp$A = Object.prototype.hasOwnProperty;
var __propIsEnum$A = Object.prototype.propertyIsEnumerable;
var __defNormalProp$A = (obj, key, value) => key in obj ? __defProp$A(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$A = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$A.call(b, prop))
      __defNormalProp$A(a, prop, b[prop]);
  if (__getOwnPropSymbols$A)
    for (var prop of __getOwnPropSymbols$A(b)) {
      if (__propIsEnum$A.call(b, prop))
        __defNormalProp$A(a, prop, b[prop]);
    }
  return a;
};
var __objRest$j = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$A.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$A)
    for (var prop of __getOwnPropSymbols$A(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$A.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const ExpirationYear = forwardRef((props, ref) => {
  var _b;
  const _a = props, {
    className,
    onChange,
    format,
    __formValues__,
    __formName__,
    __setRef__,
    onBlur,
    onFocus,
    errorMessage
  } = _a, rest = __objRest$j(_a, [
    "className",
    "onChange",
    "format",
    "__formValues__",
    "__formName__",
    "__setRef__",
    "onBlur",
    "onFocus",
    "errorMessage"
  ]);
  const value = (_b = __formValues__ == null ? void 0 : __formValues__.expirationYear) != null ? _b : "";
  const placeholderFormat = format === DEFAULT_YEAR_FORMAT ? "YY" : "YYYY";
  const maxLengthFormat = format === DEFAULT_YEAR_FORMAT ? 2 : 4;
  const name = `expirationYear${__formName__ ? ` ${__formName__}` : ""}`;
  const [error, setError] = useState();
  const changeHandler = (e) => {
    const value2 = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = value2;
    const { message } = isExpirationYearValid(value2, format);
    setError(message);
    onChange == null ? void 0 : onChange(e);
  };
  const blurHandler = (e) => {
    const { message } = isExpirationYearValid(__formValues__.expirationYear, format);
    setError(message);
    onBlur == null ? void 0 : onBlur(e);
  };
  const focusHandler = (e) => {
    setError(void 0);
    onFocus == null ? void 0 : onFocus(e);
  };
  useEffect(() => {
    if (!errorMessage)
      return;
    setError(errorMessage);
  }, [errorMessage]);
  return /* @__PURE__ */ React.createElement(ExpirationYearStyled, __spreadValues$A({
    className: `ExpirationYear ${className}`,
    value,
    name,
    onChange: changeHandler,
    placeholder: placeholderFormat,
    maxLength: maxLengthFormat,
    pattern: "[0-9]*",
    inputMode: "numeric",
    onFocus: focusHandler,
    onBlur: blurHandler,
    errorMessage: error,
    required: true,
    ref: typeof ref === "function" ? __setRef__ : ref
  }, rest));
});
ExpirationYear.defaultProps = {
  className: "",
  format: DEFAULT_YEAR_FORMAT,
  label: "A\xF1o de expiraci\xF3n"
};

const CVVStyled = styled.div`
  .CVV {
  }
`;

const focusStyle = (props) => {
  if (!props.isFocused)
    return;
  return css`
    border-color: var(--palette-primary, #4611cf);
    outline: none;
    box-shadow: 0 0 0 2px #1890ff33;
  `;
};
const PasswordInputStyled = styled.div`
  width: 100%;

  .PasswordInput {
    &__label {
      display: block;
      color: #11111180;
      padding-bottom: 4px;
      font-size: 12px;
    }

    &__container {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      padding: 0;
      overflow: hidden;
      ${focusStyle}

      &:hover {
        border-color: var(--palette-primary, #4611cf);
      }
    }

    &__input {
      width: 100%;
      padding-left: 12px;
      padding-right: ${({ removeIcon }) => removeIcon ? "12px" : "48px"};
      font-family: inherit;
      font-size: 16px;
      line-height: 2.5;
      color: #111111;
      border: none;
      outline: none;
    }

    &__icon {
      position: absolute;
      right: 12px;
      top: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      cursor: pointer;
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

const getBlur = (props) => {
  const { width = 0, height = 0 } = props;
  const average = (width + height) / 2;
  return `blur(${Math.ceil(average * 0.01)}px)`;
};
const blurStyles = (props) => {
  if (!props.isLoading || props.placeholder !== "blur")
    return "";
  return css`
    object-fit: contain;
    filter: ${getBlur};
    -webkit-filter: ${getBlur};
    background-size: cover;
    background-image: url("${(props2) => props2.bluredImgURL}");
    background-position: 0% 0%;
    opacity: 1;
  `;
};
const ImageStyled = styled.div`
  width: ${(props) => props.width ? `${props.width}px` : "auto"};
  height: ${(props) => props.height ? `${props.height}px` : "auto"};
  border-radius: 0.4rem;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  .Image {
    &__img {
      max-width: 100%;
      object-fit: contain;
      opacity: ${(props) => props.isLoading ? 0 : 1};
      ${blurStyles}
    }

    &__placeholder {
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
`;

var __defProp$z = Object.defineProperty;
var __defProps$r = Object.defineProperties;
var __getOwnPropDescs$r = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$z = Object.getOwnPropertySymbols;
var __hasOwnProp$z = Object.prototype.hasOwnProperty;
var __propIsEnum$z = Object.prototype.propertyIsEnumerable;
var __defNormalProp$z = (obj, key, value) => key in obj ? __defProp$z(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$z = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$z.call(b, prop))
      __defNormalProp$z(a, prop, b[prop]);
  if (__getOwnPropSymbols$z)
    for (var prop of __getOwnPropSymbols$z(b)) {
      if (__propIsEnum$z.call(b, prop))
        __defNormalProp$z(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$r = (a, b) => __defProps$r(a, __getOwnPropDescs$r(b));
const getResizedImageUrl = (image, config) => {
  if (typeof image === "string")
    return image;
  if (!config)
    return;
  const { bucket, cloudFrontUrl, key } = image;
  if (!bucket || !cloudFrontUrl || !key)
    return image.url;
  const height = typeof config.height !== "undefined" ? Math.round(config.height) : void 0;
  const width = typeof config.width !== "undefined" ? Math.round(config.width) : void 0;
  const stringToEncode = JSON.stringify({
    bucket,
    key,
    edits: {
      resize: __spreadProps$r(__spreadValues$z({
        background: {
          r: 255,
          g: 255,
          b: 255,
          alpha: 1
        }
      }, config), {
        height,
        width
      })
    }
  });
  const buffer = Buffer.from(stringToEncode);
  const base64Params = buffer.toString("base64");
  return `${cloudFrontUrl}/${base64Params}`;
};

var __defProp$y = Object.defineProperty;
var __defProps$q = Object.defineProperties;
var __getOwnPropDescs$q = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$y = Object.getOwnPropertySymbols;
var __hasOwnProp$y = Object.prototype.hasOwnProperty;
var __propIsEnum$y = Object.prototype.propertyIsEnumerable;
var __defNormalProp$y = (obj, key, value) => key in obj ? __defProp$y(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$y = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$y.call(b, prop))
      __defNormalProp$y(a, prop, b[prop]);
  if (__getOwnPropSymbols$y)
    for (var prop of __getOwnPropSymbols$y(b)) {
      if (__propIsEnum$y.call(b, prop))
        __defNormalProp$y(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$q = (a, b) => __defProps$q(a, __getOwnPropDescs$q(b));
imageBuilders.buildCDNImage();
const createBluredImageConfig = (config) => {
  if (!config)
    return;
  return __spreadProps$q(__spreadValues$y({}, config), {
    width: Math.floor(config.width * 0.2),
    height: Math.floor(config.height * 0.2)
  });
};

var __defProp$x = Object.defineProperty;
var __defProps$p = Object.defineProperties;
var __getOwnPropDescs$p = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$x = Object.getOwnPropertySymbols;
var __hasOwnProp$x = Object.prototype.hasOwnProperty;
var __propIsEnum$x = Object.prototype.propertyIsEnumerable;
var __defNormalProp$x = (obj, key, value) => key in obj ? __defProp$x(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$x = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$x.call(b, prop))
      __defNormalProp$x(a, prop, b[prop]);
  if (__getOwnPropSymbols$x)
    for (var prop of __getOwnPropSymbols$x(b)) {
      if (__propIsEnum$x.call(b, prop))
        __defNormalProp$x(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$p = (a, b) => __defProps$p(a, __getOwnPropDescs$p(b));
var __objRest$i = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$x.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$x)
    for (var prop of __getOwnPropSymbols$x(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$x.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Image$1 = forwardRef((props, ref) => {
  const _a = props, {
    image,
    alt,
    className,
    config,
    errorImage,
    placeholder = "skeleton",
    placeholderNode,
    width,
    height
  } = _a, rest = __objRest$i(_a, [
    "image",
    "alt",
    "className",
    "config",
    "errorImage",
    "placeholder",
    "placeholderNode",
    "width",
    "height"
  ]);
  const { width: widthCDN, height: heightCDN } = config != null ? config : {};
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const internalRef = useRef(null);
  const showErrorImage = !imgLoading && (imgError || !image);
  const imgErrorURL = showErrorImage || !image ? errorImage : void 0;
  const imgUrl = getResizedImageUrl(image, config);
  const bluredImgURL = getResizedImageUrl(image, createBluredImageConfig(config));
  const loadHandler = async (error) => {
    const instance = internalRef.current;
    if (instance && !error) {
      await instance.decode();
    }
    setImgLoading(false);
  };
  const imgLoadHandler = async (event) => {
    var _a2;
    loadHandler(false);
    (_a2 = rest.onLoad) == null ? void 0 : _a2.call(rest, event);
  };
  const imgErrorHandler = (event) => {
    var _a2;
    loadHandler(true);
    setImgError(true);
    (_a2 = rest.onError) == null ? void 0 : _a2.call(rest, event);
  };
  const renderPlaceholder = () => {
    if (placeholder === "none" || placeholder === "blur") {
      return null;
    }
    if (placeholder === "skeleton") {
      return /* @__PURE__ */ React.createElement("div", {
        className: "Image__placeholder loading-shine"
      });
    }
    if (isValidElement(placeholderNode)) {
      return /* @__PURE__ */ React.createElement("div", {
        className: "Image__placeholder"
      }, placeholderNode);
    }
    console.error("Cannot display this placeholder image type");
  };
  const setRef = (instance) => {
    var _a2;
    if (typeof ref === "function") {
      ref(instance);
      internalRef.current = instance;
      if ((_a2 = internalRef.current) == null ? void 0 : _a2.complete) {
        loadHandler(false);
      }
      return instance;
    }
    throw Error("setRef should never be called without a ref function");
  };
  useIsomorphicLayoutEffect(() => {
    var _a2;
    let instance = internalRef.current;
    if (!instance && typeof ref !== "function") {
      internalRef.current = (_a2 = ref == null ? void 0 : ref.current) != null ? _a2 : null;
      instance = internalRef.current;
    }
    if (!instance || !instance.complete)
      return;
    loadHandler(false);
  }, []);
  return /* @__PURE__ */ React.createElement(ImageStyled, {
    className: `Image ${className}`,
    width: width != null ? width : widthCDN,
    height: height != null ? height : heightCDN,
    isLoading: imgLoading,
    placeholder,
    bluredImgURL
  }, /* @__PURE__ */ React.createElement("img", __spreadProps$p(__spreadValues$x({
    className: "Image__img",
    decoding: "async"
  }, rest), {
    ref: typeof ref === "function" ? setRef : ref,
    src: imgErrorURL != null ? imgErrorURL : imgUrl,
    alt,
    width: width != null ? width : widthCDN,
    height: height != null ? height : heightCDN,
    onLoad: imgLoadHandler,
    onError: imgErrorHandler
  })), imgLoading ? renderPlaceholder() : null);
});
Image$1.defaultProps = {
  className: "",
  loading: "lazy",
  placeholder: "skeleton"
};

const iconDefaultMeasures = { height: 20, width: 20 };

function _extends$t() { _extends$t = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$t.apply(this, arguments); }
var VisibilityOffSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$t({
  xmlns: "http://www.w3.org/2000/svg",
  height: "24",
  width: "24"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M0 0h24v24H0zm0 0h24v24H0zm0 0h24v24H0zm0 0h24v24H0z",
  fill: "none"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 001 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
})));

function _extends$s() { _extends$s = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$s.apply(this, arguments); }
var VisibilitySVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$s({
  xmlns: "http://www.w3.org/2000/svg",
  height: "24",
  width: "24"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M0 0h24v24H0z",
  fill: "none"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
})));

var __defProp$w = Object.defineProperty;
var __defProps$o = Object.defineProperties;
var __getOwnPropDescs$o = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$w = Object.getOwnPropertySymbols;
var __hasOwnProp$w = Object.prototype.hasOwnProperty;
var __propIsEnum$w = Object.prototype.propertyIsEnumerable;
var __defNormalProp$w = (obj, key, value) => key in obj ? __defProp$w(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$w = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$w.call(b, prop))
      __defNormalProp$w(a, prop, b[prop]);
  if (__getOwnPropSymbols$w)
    for (var prop of __getOwnPropSymbols$w(b)) {
      if (__propIsEnum$w.call(b, prop))
        __defNormalProp$w(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$o = (a, b) => __defProps$o(a, __getOwnPropDescs$o(b));
var __objRest$h = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$w.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$w)
    for (var prop of __getOwnPropSymbols$w(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$w.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const PasswordInput = forwardRef((props, ref) => {
  const _a = props, {
    className,
    label,
    id,
    name,
    removeIcon,
    hiddenIcon,
    visibleIcon,
    errorMessage
  } = _a, rest = __objRest$h(_a, [
    "className",
    "label",
    "id",
    "name",
    "removeIcon",
    "hiddenIcon",
    "visibleIcon",
    "errorMessage"
  ]);
  const [isFocused, setIsFocused] = useState(false);
  const [isVisiblePassword, setIsVisiblePassword] = useState(false);
  const errorMessageId = `${id != null ? id : name}-error`;
  const renderLabel = () => {
    if (!label)
      return null;
    return /* @__PURE__ */ React.createElement("label", {
      className: "PasswordInput__label",
      htmlFor: id != null ? id : name
    }, label);
  };
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "PasswordInput__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "PasswordInput__errorMessage"
    }, errorMessage);
  };
  const togglePasswordVisibility = () => {
    setIsVisiblePassword((prevState) => !prevState);
  };
  const renderVisibleIcon = () => {
    if (!visibleIcon) {
      return /* @__PURE__ */ React.createElement(VisibilitySVG, null);
    }
    if (typeof visibleIcon === "string" && visibleIcon.length > 0) {
      return /* @__PURE__ */ React.createElement(Image$1, {
        alt: "password input icon",
        config: iconDefaultMeasures,
        image: visibleIcon
      });
    }
    return visibleIcon;
  };
  const renderHiddenIcon = () => {
    if (!hiddenIcon) {
      return /* @__PURE__ */ React.createElement(VisibilityOffSVG, null);
    }
    if (typeof hiddenIcon === "string" && hiddenIcon.length > 0) {
      return /* @__PURE__ */ React.createElement(Image$1, {
        alt: "password input icon",
        config: iconDefaultMeasures,
        image: hiddenIcon
      });
    }
    return hiddenIcon;
  };
  const focusHandler = (e) => {
    var _a2;
    setIsFocused(true);
    (_a2 = rest.onFocus) == null ? void 0 : _a2.call(rest, e);
  };
  const blurHandler = (e) => {
    var _a2;
    setIsFocused(false);
    (_a2 = rest.onBlur) == null ? void 0 : _a2.call(rest, e);
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("When using the label tag consider passing the 'name' prop");
  }, [name, label]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn("PasswordInput: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, errorMessage]);
  return /* @__PURE__ */ React.createElement(PasswordInputStyled, {
    className: `PasswordInput ${className}`,
    isFocused,
    removeIcon
  }, renderLabel(), /* @__PURE__ */ React.createElement("div", {
    className: "PasswordInput__container"
  }, /* @__PURE__ */ React.createElement("input", __spreadProps$o(__spreadValues$w({
    className: "PasswordInput__input",
    id: id != null ? id : name,
    type: isVisiblePassword ? "text" : "password",
    name,
    maxLength: 80,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId
  }, rest), {
    ref,
    onFocus: focusHandler,
    onBlur: blurHandler
  })), !removeIcon ? /* @__PURE__ */ React.createElement("figure", {
    className: "PasswordInput__icon",
    onClick: togglePasswordVisibility
  }, isVisiblePassword ? renderVisibleIcon() : renderHiddenIcon()) : null), renderErrorMessage());
});
PasswordInput.defaultProps = {
  className: "",
  removeIcon: false
};

var __defProp$v = Object.defineProperty;
var __defProps$n = Object.defineProperties;
var __getOwnPropDescs$n = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$v = Object.getOwnPropertySymbols;
var __hasOwnProp$v = Object.prototype.hasOwnProperty;
var __propIsEnum$v = Object.prototype.propertyIsEnumerable;
var __defNormalProp$v = (obj, key, value) => key in obj ? __defProp$v(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$v = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$v.call(b, prop))
      __defNormalProp$v(a, prop, b[prop]);
  if (__getOwnPropSymbols$v)
    for (var prop of __getOwnPropSymbols$v(b)) {
      if (__propIsEnum$v.call(b, prop))
        __defNormalProp$v(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$n = (a, b) => __defProps$n(a, __getOwnPropDescs$n(b));
var __objRest$g = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$v.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$v)
    for (var prop of __getOwnPropSymbols$v(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$v.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const CVV = forwardRef((props, ref) => {
  var _b, _c;
  const _a = props, {
    className,
    isHidden,
    onChange,
    __formValues__,
    __formName__,
    __setRef__,
    onBlur,
    onFocus,
    errorMessage
  } = _a, rest = __objRest$g(_a, [
    "className",
    "isHidden",
    "onChange",
    "__formValues__",
    "__formName__",
    "__setRef__",
    "onBlur",
    "onFocus",
    "errorMessage"
  ]);
  const value = (_b = __formValues__ == null ? void 0 : __formValues__.cvv) != null ? _b : "";
  const cardNumber = (_c = __formValues__ == null ? void 0 : __formValues__.cardNumber) != null ? _c : "";
  const issuer = validateCard(cardNumber).issuer;
  const name = `cvv${__formName__ ? ` ${__formName__}` : ""}`;
  const [error, setError] = useState();
  const maxLength = issuer === "amex" ? 4 : 3;
  const changeHandler = (e) => {
    let newValue = e.target.value.replace(/[^0-9]/g, "");
    newValue = issuer !== "amex" ? newValue.slice(0, 3) : newValue;
    e.target.value = newValue;
    const { message } = isCVVValid(newValue, maxLength);
    setError(message);
    onChange == null ? void 0 : onChange(e);
  };
  const blurHandler = (e) => {
    const { message } = isCVVValid(__formValues__.cvv, maxLength);
    setError(message);
    onBlur == null ? void 0 : onBlur(e);
  };
  const focusHandler = (e) => {
    setError(void 0);
    onFocus == null ? void 0 : onFocus(e);
  };
  const commonProps = {
    placeholder: `000${maxLength === 4 ? "0" : ""}`,
    value,
    name,
    minLength: 3,
    maxLength,
    onChange: changeHandler,
    pattern: "[0-9]*",
    inputMode: "numeric",
    onBlur: blurHandler,
    onFocus: focusHandler,
    errorMessage: error,
    required: true
  };
  useEffect(() => {
    if (!errorMessage)
      return;
    setError(errorMessage);
  }, [errorMessage]);
  return /* @__PURE__ */ React.createElement(CVVStyled, {
    className: `CVV ${className}`
  }, isHidden ? /* @__PURE__ */ React.createElement(PasswordInput, __spreadValues$v(__spreadProps$n(__spreadValues$v({}, commonProps), {
    ref: typeof ref === "function" ? __setRef__ : ref
  }), rest)) : /* @__PURE__ */ React.createElement(TextInput, __spreadValues$v(__spreadProps$n(__spreadValues$v({}, commonProps), {
    ref: typeof ref === "function" ? __setRef__ : ref
  }), rest)));
});
CVV.defaultProps = {
  className: "",
  isHidden: true,
  removeIcon: true,
  label: "C\xF3digo de seguridad (CVV)"
};

var __defProp$u = Object.defineProperty;
var __defProps$m = Object.defineProperties;
var __getOwnPropDescs$m = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$u = Object.getOwnPropertySymbols;
var __hasOwnProp$u = Object.prototype.hasOwnProperty;
var __propIsEnum$u = Object.prototype.propertyIsEnumerable;
var __defNormalProp$u = (obj, key, value) => key in obj ? __defProp$u(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$u = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$u.call(b, prop))
      __defNormalProp$u(a, prop, b[prop]);
  if (__getOwnPropSymbols$u)
    for (var prop of __getOwnPropSymbols$u(b)) {
      if (__propIsEnum$u.call(b, prop))
        __defNormalProp$u(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$m = (a, b) => __defProps$m(a, __getOwnPropDescs$m(b));
const initialState$2 = {
  cardHolder: "",
  cardNumber: "",
  expirationMonth: "",
  expirationYear: "",
  cvv: "",
  issuer: void 0,
  valid: false,
  invalidFields: []
};
const cardFormReducer = (state, action) => {
  switch (action.type) {
    case "CARD_HOLDER":
      return cardHolderAction(state, action.payload);
    case "CARD_NUMBER":
      return cardNumberAction(state, action.payload);
    case "EXPIRATION_MONTH":
      return expirationMonthAction(state, action.payload);
    case "EXPIRATION_YEAR":
      return expirationYearAction(state, action.payload);
    case "CVV":
      return cvvAction(state, action.payload);
    case "VALIDATE":
      return validateAction(state, action.payload);
    case "RESET":
      return __spreadValues$u({}, initialState$2);
    default:
      throw new Error(`Action ${action.type} is not a recognized CardForm action`);
  }
};
const cardHolderAction = (state, payload) => {
  const currentState = __spreadProps$m(__spreadValues$u({}, state), { cardHolder: payload });
  const { valid } = isCardHolderValid(payload);
  return checkField(currentState, "cardHolder", valid);
};
const cardNumberAction = (state, payload) => {
  const { issuer } = validateCard(payload);
  const currentState = __spreadProps$m(__spreadValues$u({}, state), { cardNumber: payload, issuer });
  const { valid } = isCardNumberValid(payload);
  return checkField(currentState, "cardNumber", valid);
};
const expirationMonthAction = (state, payload) => {
  const currentState = __spreadProps$m(__spreadValues$u({}, state), { expirationMonth: payload });
  const { valid } = isExpirationMonthValid(payload, currentState.expirationYear);
  return checkField(currentState, "expirationMonth", valid);
};
const expirationYearAction = (state, payload) => {
  const { year, format } = payload;
  const currentState = __spreadProps$m(__spreadValues$u({}, state), { expirationYear: year });
  const { valid } = isExpirationYearValid(year, format);
  return checkField(currentState, "expirationYear", valid);
};
const cvvAction = (state, payload) => {
  const issuer = validateCard(state.cardNumber).issuer;
  const maxLength = issuer === "amex" ? 4 : 3;
  const currentState = __spreadProps$m(__spreadValues$u({}, state), { cvv: payload });
  const { valid } = isCVVValid(payload, maxLength);
  return checkField(currentState, "cvv", valid);
};
const validateAction = (state, payload) => {
  const { cvv, cardHolder, cardNumber } = state;
  const { expirationMonth, expirationYear } = state;
  const { format } = payload;
  state = cardHolderAction(state, cardHolder);
  state = cardNumberAction(state, cardNumber);
  state = expirationYearAction(state, { year: expirationYear, format });
  state = expirationMonthAction(state, expirationMonth);
  state = cvvAction(state, cvv);
  return __spreadValues$u({}, state);
};

var __defProp$t = Object.defineProperty;
var __getOwnPropSymbols$t = Object.getOwnPropertySymbols;
var __hasOwnProp$t = Object.prototype.hasOwnProperty;
var __propIsEnum$t = Object.prototype.propertyIsEnumerable;
var __defNormalProp$t = (obj, key, value) => key in obj ? __defProp$t(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$t = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$t.call(b, prop))
      __defNormalProp$t(a, prop, b[prop]);
  if (__getOwnPropSymbols$t)
    for (var prop of __getOwnPropSymbols$t(b)) {
      if (__propIsEnum$t.call(b, prop))
        __defNormalProp$t(a, prop, b[prop]);
    }
  return a;
};
var __objRest$f = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$t.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$t)
    for (var prop of __getOwnPropSymbols$t(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$t.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const CardForm = (props) => {
  const _a = props, { className, children, onChange, initialValues, onSubmit } = _a, rest = __objRest$f(_a, ["className", "children", "onChange", "initialValues", "onSubmit"]);
  const fieldsRefs = useRef([]);
  const childrenRef = useRef(getChildren(children));
  const enhancedState = useMemo(() => __spreadValues$t(__spreadValues$t({}, initialState$2), initialValues), [initialValues]);
  const [state, dispatch] = useReducer(cardFormReducer, enhancedState);
  const knownChildren = [
    CardHolder,
    CardNumber,
    ExpirationMonth,
    ExpirationYear,
    CVV
  ];
  const renderChildren = () => {
    const filteredChildren = getChildren(children);
    if (!filteredChildren.length) {
      console.error(`${CardForm.name}: CardForm it's being used without any fields`);
    }
    return filteredChildren.map((child, index) => {
      if (!isValidElement(child)) {
        return child;
      }
      const isKnownChildren = knownChildren.some((item) => item === child.type);
      if (!isKnownChildren)
        return child;
      const changeHandler = (e) => {
        var _a2, _b, _c;
        (_b = (_a2 = child.props).onChange) == null ? void 0 : _b.call(_a2, e);
        const payload = e.target.value;
        switch (child.type) {
          case CardHolder:
            dispatch({ type: "CARD_HOLDER", payload });
            break;
          case CardNumber:
            dispatch({ type: "CARD_NUMBER", payload });
            break;
          case ExpirationMonth: {
            dispatch({ type: "EXPIRATION_MONTH", payload });
            break;
          }
          case ExpirationYear: {
            const format = (_c = child.props.format) != null ? _c : DEFAULT_YEAR_FORMAT;
            dispatch({
              type: "EXPIRATION_YEAR",
              payload: { year: payload, format }
            });
            break;
          }
          case CVV:
            dispatch({ type: "CVV", payload });
            break;
          default:
            throw new Error(`${CardForm.name}: Child of type ${child.type} is not a valid item`);
        }
      };
      const newRef = createRef();
      const childRef = child.ref;
      fieldsRefs.current[index] = newRef;
      return cloneElement(child, {
        onChange: changeHandler,
        __setRef__: setRef(newRef, childRef),
        __formValues__: state,
        __formName__: props.id
      });
    });
  };
  const getYearFormat = () => {
    var _a2;
    const expirationYearNode = childrenRef.current.find((child) => {
      if (!isValidElement(child)) {
        return false;
      }
      return child.type === ExpirationYear;
    });
    return (_a2 = expirationYearNode == null ? void 0 : expirationYearNode.props.format) != null ? _a2 : DEFAULT_YEAR_FORMAT;
  };
  const methods = useMemo(() => {
    return {
      resetFields: () => dispatch({ type: "RESET" })
    };
  }, []);
  const submitHandler = (e) => {
    var _a2, _b, _c, _d;
    e.preventDefault();
    for (const ref of fieldsRefs.current) {
      if (!ref)
        continue;
      (_b = (_a2 = ref.current) == null ? void 0 : _a2.focus) == null ? void 0 : _b.call(_a2);
      (_d = (_c = ref.current) == null ? void 0 : _c.blur) == null ? void 0 : _d.call(_c);
    }
    const format = getYearFormat();
    const currentState = validateAction(state, { format });
    onSubmit(currentState, methods);
  };
  useEffect(() => {
    onChange == null ? void 0 : onChange(state, methods);
  }, [onChange, state, methods]);
  useEffect(() => {
    const format = getYearFormat();
    dispatch({ type: "VALIDATE", payload: { format } });
  }, []);
  return /* @__PURE__ */ React.createElement(CardFormStyled, __spreadValues$t({
    className: `CardForm ${className}`,
    onSubmit: submitHandler,
    noValidate: true
  }, rest), renderChildren());
};
CardForm.defaultProps = {
  className: ""
};
CardForm.CardHolder = CardHolder;
CardForm.CardNumber = CardNumber;
CardForm.ExpirationMonth = ExpirationMonth;
CardForm.ExpirationYear = ExpirationYear;
CardForm.CVV = CVV;

const CarouselStyled = styled.div`
  position: relative;

  .Carousel {
    &__gallery {
      height: 100%;
      display: grid;
      grid-template-columns: repeat(
        ${(props) => props.numberOfSlides},
        min(100vw, 100%)
      );
      grid-template-rows: 1fr;
      grid-auto-flow: column;
      overflow: hidden;
      scroll-snap-type: both mandatory;
      scroll-padding: 10px;
    }

    &__active {
      scroll-snap-type: unset;
    }

    &__item {
      scroll-snap-align: center;
      display: inline-flex;
      justify-content: center;
      align-items: center;
    }

    &__inner-item {
      max-width: 100%;
      max-height: 100%;
    }
  }
`;

const ControlsStyled = styled.div`
  .Controls {
    &__arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background-color: hsla(0, 0%, 75%, 0.5);
      border-radius: 4px;

      &--left {
        left: 1%;
      }

      &--right {
        right: 1%;
      }
    }

    &__icon {
      path {
        fill: hsl(0, 0%, 0%);
      }
    }
  }
`;

const ClickableStyled = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: ${(props) => props.padding}px;

  .Clickable {
  }
`;

const Clickable = (props) => {
  const { className, children, padding = 16, onClick } = props;
  if (!children) {
    throw new Error(`${Clickable.displayName}: You need to specify children`);
  }
  return /* @__PURE__ */ React.createElement(ClickableStyled, {
    className: `Clickable ${className}`,
    onClick,
    padding
  }, children);
};
Clickable.defaultProps = {
  className: "",
  padding: 16
};

function _extends$r() { _extends$r = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$r.apply(this, arguments); }
var ArrowLeftSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$r({
  width: "6",
  height: "13",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M5.185.662l.815.92-4.37 4.924L6 11.431l-.815.92L0 6.505 5.185.662z",
  fill: "#fff"
})));

function _extends$q() { _extends$q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$q.apply(this, arguments); }
var ArrowRightSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$q({
  width: "6",
  height: "13",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M.815 12.35L0 11.433l4.37-4.926L0 1.581.815.662 6 6.506.815 12.351z",
  fill: "#fff"
})));

const Controls = (props) => {
  const { prevIcon, nextIcon, snapTo, currentSlide, infinite } = props;
  const { numberOfSlides } = props;
  const snapToLeft = (e) => {
    e.stopPropagation();
    if (currentSlide === 1) {
      if (!infinite)
        return;
      snapTo(numberOfSlides);
      return;
    }
    snapTo(currentSlide - 1);
  };
  const snapToRight = (e) => {
    e.stopPropagation();
    if (currentSlide === numberOfSlides) {
      if (!infinite)
        return;
      snapTo(1);
      return;
    }
    snapTo(currentSlide + 1);
  };
  if (numberOfSlides === 1)
    return null;
  return /* @__PURE__ */ React.createElement(ControlsStyled, {
    className: "Controls"
  }, /* @__PURE__ */ React.createElement(Clickable, {
    className: "Controls__arrow Controls__arrow--left",
    onClick: snapToLeft
  }, prevIcon != null ? prevIcon : /* @__PURE__ */ React.createElement(ArrowLeftSVG, {
    className: "Controls__icon"
  })), /* @__PURE__ */ React.createElement(Clickable, {
    className: "Controls__arrow Controls__arrow--right",
    onClick: snapToRight
  }, nextIcon != null ? nextIcon : /* @__PURE__ */ React.createElement(ArrowRightSVG, {
    className: "Controls__icon"
  })));
};
Controls.defaultProps = {};

const PaginationStyled = styled.ul`
  position: absolute;
  right: 0;
  bottom: 8px;
  left: 0;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;

  .Pagination {
    &__dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #ffffff;
      cursor: pointer;
      list-style: none;
      opacity: 0.8;
      transition: opacity 0.2s;
      margin-bottom: 8px;

      &:not(:first-child) {
        margin-left: 12px;
      }

      &:hover,
      &:focus {
        opacity: 1;
      }

      &--active {
        opacity: 1;
      }
    }
  }
`;

const Pagination = (props) => {
  const { numberOfSlides, currentSlide, snapTo } = props;
  const activeClass = "Pagination__dot--active";
  const clickHandler = (e, slide) => {
    e.stopPropagation();
    snapTo(slide);
  };
  if (numberOfSlides === 1)
    return null;
  return /* @__PURE__ */ React.createElement(PaginationStyled, {
    className: "Pagination"
  }, Array(numberOfSlides).fill("").map((_, index) => /* @__PURE__ */ React.createElement("li", {
    className: `Pagination__dot ${index + 1 === currentSlide ? activeClass : ""}`,
    key: index,
    onClick: (e) => clickHandler(e, index + 1)
  })));
};
Pagination.defaultProps = {};

var __defProp$s = Object.defineProperty;
var __defProps$l = Object.defineProperties;
var __getOwnPropDescs$l = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$s = Object.getOwnPropertySymbols;
var __hasOwnProp$s = Object.prototype.hasOwnProperty;
var __propIsEnum$s = Object.prototype.propertyIsEnumerable;
var __defNormalProp$s = (obj, key, value) => key in obj ? __defProp$s(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$s = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$s.call(b, prop))
      __defNormalProp$s(a, prop, b[prop]);
  if (__getOwnPropSymbols$s)
    for (var prop of __getOwnPropSymbols$s(b)) {
      if (__propIsEnum$s.call(b, prop))
        __defNormalProp$s(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$l = (a, b) => __defProps$l(a, __getOwnPropDescs$l(b));
var __objRest$e = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$s.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$s)
    for (var prop of __getOwnPropSymbols$s(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$s.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Carousel = (props) => {
  const { className, children, pagination, controls, scrollSpeed = 3 } = props;
  const { animated, infinite, onChange, prevIcon, nextIcon } = props;
  const { initialSlide, draggable } = props;
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isMoving, setIsMoving] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  const carouselRef = useRef(null);
  const numberOfSlides = useRef(Children.count(children));
  const animationTimeout = useRef();
  const movingTimeout = useRef();
  const activeClass = isDown ? "Carousel__active" : "";
  const snapTo = useCallback((slide) => {
    const carousel = carouselRef.current;
    if (!carousel)
      return;
    const { width: carouselWidth } = carousel.getBoundingClientRect();
    carousel.scrollTo({
      left: carouselWidth * (slide - 1),
      behavior: "smooth"
    });
    setCurrentSlide(slide);
    onChange == null ? void 0 : onChange(slide);
  }, [onChange]);
  const pressStart = (pageX) => {
    const carousel = carouselRef.current;
    if (!carousel || !draggable)
      return;
    setIsDown(true);
    setStartX(pageX - carousel.offsetLeft);
    setScrollLeft(carousel.scrollLeft);
  };
  const animate = useCallback(() => {
    const isLastSlide = currentSlide === numberOfSlides.current;
    if (isLastSlide && !infinite)
      return;
    const newSlide = isLastSlide ? 1 : currentSlide + 1;
    snapTo(newSlide);
  }, [currentSlide, infinite, snapTo]);
  const startAnimation = useCallback(() => {
    const isNotNumber = typeof animated !== "number";
    if (!animated || isNotNumber || animated < 0 || isMoving || hasEntered)
      return;
    if (animationTimeout.current)
      clearTimeout(animationTimeout.current);
    animationTimeout.current = setTimeout(animate, animated);
  }, [animate, animated, hasEntered, isMoving]);
  const suspendAnimation = () => {
    if (!animationTimeout.current)
      return;
    clearTimeout(animationTimeout.current);
    animationTimeout.current = void 0;
  };
  const motionHandler = useCallback((pageX) => {
    const carousel = carouselRef.current;
    if (!isDown || !carousel)
      return;
    setIsMoving(true);
    const x = pageX - carousel.offsetLeft;
    const walk = (x - startX) * scrollSpeed;
    carousel.scrollLeft = scrollLeft - walk;
  }, [isDown, scrollLeft, scrollSpeed, startX]);
  const endMotionHandler = () => {
    const carousel = carouselRef.current;
    if (!isDown || !carousel)
      return;
    const { width: carouselWidth } = carousel.getBoundingClientRect();
    setCurrentSlide((prev) => {
      if (carousel.scrollLeft >= carouselWidth * (prev - 1 / 2)) {
        return prev + 1;
      }
      if (carousel.scrollLeft <= carouselWidth * (prev - 3 / 2)) {
        return prev - 1;
      }
      return prev;
    });
    if (movingTimeout.current)
      clearTimeout(movingTimeout.current);
    if (!infinite) {
      setIsDown(false);
      movingTimeout.current = setTimeout(() => setIsMoving(false), 100);
      return;
    }
    if (!isMoving) {
      setIsDown(false);
      return;
    }
    if (carousel.scrollLeft === carouselWidth * (numberOfSlides.current - 1)) {
      snapTo(1);
    }
    if (carousel.scrollLeft === 0) {
      snapTo(numberOfSlides.current);
    }
    setIsDown(false);
    movingTimeout.current = setTimeout(() => setIsMoving(false), 100);
  };
  const mouseDownHandler = (e) => {
    pressStart(e.pageX);
  };
  const touchStartHandler = (e) => {
    pressStart(e.changedTouches.item(0).pageX);
  };
  const mouseLeaveHandler = () => {
    endMotionHandler();
    setHasEntered(false);
    startAnimation();
  };
  const mouseUpHandler = (e) => {
    e.preventDefault();
    endMotionHandler();
  };
  const touchEndHandler = () => {
    endMotionHandler();
    startAnimation();
  };
  const mouseMoveHandler = useCallback((e) => {
    e.preventDefault();
    motionHandler(e.pageX);
  }, [motionHandler]);
  const touchMoveHandler = useCallback((e) => {
    e.preventDefault();
    motionHandler(e.changedTouches.item(0).pageX);
  }, [motionHandler]);
  const mouseEnterHandler = useCallback((e) => {
    setHasEntered(true);
    suspendAnimation();
  }, []);
  const clickCaptureHandler = useCallback((e) => {
    if (!isMoving)
      return;
    e.preventDefault();
    e.stopPropagation();
  }, [isMoving]);
  const renderChildren = useCallback(() => {
    var _a;
    const newChildren = Children.map(children, (child) => {
      if (!isValidElement(child))
        return;
      const { props: childProps } = child;
      const _a2 = childProps, { className: childClassName } = _a2, rest = __objRest$e(_a2, ["className"]);
      return /* @__PURE__ */ React.createElement("li", {
        className: "Carousel__item",
        onClickCapture: clickCaptureHandler
      }, cloneElement(child, __spreadProps$l(__spreadValues$s({}, rest), {
        className: `Carousel__inner-item ${childClassName ? childClassName : ""}`
      })));
    });
    numberOfSlides.current = (_a = newChildren == null ? void 0 : newChildren.length) != null ? _a : Children.count(children);
    return newChildren;
  }, [children, clickCaptureHandler]);
  useEffect(() => {
    startAnimation();
    return () => {
      if (!animationTimeout.current)
        return;
      clearTimeout(animationTimeout.current);
    };
  }, [startAnimation]);
  useEffect(() => {
    if (!initialSlide || typeof initialSlide !== "number")
      return;
    let slide = initialSlide;
    if (initialSlide > numberOfSlides.current)
      slide = numberOfSlides.current;
    if (initialSlide < 0)
      slide = 1;
    snapTo(slide);
    return () => {
      if (!movingTimeout.current)
        return;
      clearTimeout(movingTimeout.current);
    };
  }, [initialSlide, snapTo]);
  if (!children)
    return null;
  if (numberOfSlides.current === 0)
    return null;
  return /* @__PURE__ */ React.createElement(CarouselStyled, {
    className: `Carousel ${className}`,
    numberOfSlides: numberOfSlides.current
  }, /* @__PURE__ */ React.createElement("ul", {
    className: `Carousel__gallery ${activeClass}`,
    ref: carouselRef,
    onTouchStart: touchStartHandler,
    onTouchEnd: touchEndHandler,
    onTouchMove: touchMoveHandler,
    onMouseDown: mouseDownHandler,
    onMouseLeave: mouseLeaveHandler,
    onMouseUp: mouseUpHandler,
    onMouseMove: mouseMoveHandler,
    onMouseEnter: mouseEnterHandler
  }, renderChildren()), controls ? /* @__PURE__ */ React.createElement(Controls, {
    infinite,
    numberOfSlides: numberOfSlides.current,
    currentSlide,
    snapTo,
    prevIcon,
    nextIcon
  }) : null, pagination ? /* @__PURE__ */ React.createElement(Pagination, {
    numberOfSlides: numberOfSlides.current,
    currentSlide,
    snapTo
  }) : null);
};
Carousel.defaultProps = {
  className: "",
  controls: true,
  pagination: true,
  scrollSpeed: 3,
  infinite: false,
  draggable: true
};

const CommentBoxStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .CommentBox {
    &__label {
      color: #11111180;
      padding-bottom: 4px;
      font-size: 12px;
    }

    &__textarea {
      resize: ${(props) => !props.resizable ? "none" : "vertical"};
      font-family: inherit;
      padding: 8px 12px;
      font-size: 16px;
      color: #111111;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      outline: none;

      &:hover {
        border-color: var(--palette-primary, #4611cf);
      }

      &:focus {
        border-color: var(--palette-primary, #4611cf);
        outline: 0;
        box-shadow: 0 0 0 2px #1890ff33;
      }
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

var __defProp$r = Object.defineProperty;
var __defProps$k = Object.defineProperties;
var __getOwnPropDescs$k = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$r = Object.getOwnPropertySymbols;
var __hasOwnProp$r = Object.prototype.hasOwnProperty;
var __propIsEnum$r = Object.prototype.propertyIsEnumerable;
var __defNormalProp$r = (obj, key, value) => key in obj ? __defProp$r(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$r = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$r.call(b, prop))
      __defNormalProp$r(a, prop, b[prop]);
  if (__getOwnPropSymbols$r)
    for (var prop of __getOwnPropSymbols$r(b)) {
      if (__propIsEnum$r.call(b, prop))
        __defNormalProp$r(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$k = (a, b) => __defProps$k(a, __getOwnPropDescs$k(b));
var __objRest$d = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$r.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$r)
    for (var prop of __getOwnPropSymbols$r(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$r.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const CommentBox = forwardRef((props, ref) => {
  const _a = props, {
    className,
    label,
    resizable = false,
    name,
    id,
    errorMessage
  } = _a, rest = __objRest$d(_a, [
    "className",
    "label",
    "resizable",
    "name",
    "id",
    "errorMessage"
  ]);
  const errorMessageId = `${id != null ? id : name}-error`;
  const renderLabel = () => {
    if (!label)
      return null;
    return /* @__PURE__ */ React.createElement("label", {
      className: "CommentBox__label",
      htmlFor: id != null ? id : name
    }, label);
  };
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "CommentBox__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "CommentBox__errorMessage"
    }, errorMessage);
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("CommentBox: When using the label tag consider passing the 'name' prop");
  }, [label, name]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn("CommentBox: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, errorMessage]);
  return /* @__PURE__ */ React.createElement(CommentBoxStyled, {
    className: `CommentBox ${className}`,
    resizable
  }, renderLabel(), /* @__PURE__ */ React.createElement("textarea", __spreadProps$k(__spreadValues$r({
    className: "CommentBox__textarea",
    id: id != null ? id : name,
    name,
    rows: 4,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId
  }, rest), {
    ref
  })), renderErrorMessage());
});
CommentBox.defaultProps = {
  className: "",
  resizable: false
};

const CounterStyled = styled.div`
  display: flex;
  align-items: center;

  .Counter {
  }
`;

const useCounter = (config) => {
  const { onChange = defaultFunction, initialValue = 0, min = 0 } = config;
  const { onAdd = defaultFunction, onSubtract = defaultFunction } = config;
  const { max = 100, steps = 1 } = config;
  const [quantity, setQuantity] = useState(initialValue);
  const add = useCallback(() => {
    if (quantity >= max)
      return;
    setQuantity((prev) => {
      const newQuantity = prev + steps;
      onChange(newQuantity);
      onAdd(newQuantity);
      return newQuantity;
    });
  }, [quantity, max, steps, onChange, onAdd]);
  const subtract = useCallback(() => {
    if (quantity <= min)
      return;
    setQuantity((prev) => {
      const newQuantity = prev - steps;
      onChange(newQuantity);
      onSubtract(newQuantity);
      return newQuantity;
    });
  }, [quantity, min, steps, onChange, onSubtract]);
  useEffect(() => {
    setQuantity(initialValue);
  }, [initialValue]);
  return useMemo(() => ({
    add,
    subtract,
    quantity
  }), [add, subtract, quantity]);
};

function _extends$p() { _extends$p = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$p.apply(this, arguments); }
var Add = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$p({
  width: "10",
  height: "10",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M4.357 4.357V.5h1.286v3.857H9.5v1.286H5.643V9.5H4.357V5.643H.5V4.357h3.857z",
  fill: "#fff"
})));

function _extends$o() { _extends$o = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$o.apply(this, arguments); }
var Minus = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$o({
  width: "10",
  height: "2",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M9.5 1.642h-9V.356h9v1.286z",
  fill: "#fff"
})));

const Counter = (props) => {
  const { className, disabled = false, steps = 1 } = props;
  const { min = 0, max = 100, initialValue = 0 } = props;
  const { onChange = defaultFunction, onAdd = defaultFunction } = props;
  const { onSubtract = defaultFunction } = props;
  const { minDisabled: minDisabledProp = false } = props;
  const { maxDisabled: maxDisabledProp = false } = props;
  const config = {
    min,
    max,
    steps,
    initialValue,
    onChange,
    onAdd,
    onSubtract
  };
  const { add, subtract, quantity } = useCounter(config);
  const minDisabled = quantity <= min || disabled || minDisabledProp;
  const maxDisabled = quantity >= max || disabled || maxDisabledProp;
  return /* @__PURE__ */ React.createElement(CounterStyled, {
    className: `Counter ${className}`
  }, /* @__PURE__ */ React.createElement(Button$1, {
    className: "Counter__button",
    onClick: subtract,
    disabled: minDisabled,
    type: "BORDER"
  }, /* @__PURE__ */ React.createElement(Minus, null)), /* @__PURE__ */ React.createElement("div", {
    className: "Counter__quantity"
  }, quantity), /* @__PURE__ */ React.createElement(Button$1, {
    className: "Counter__button",
    onClick: add,
    disabled: maxDisabled,
    type: "BORDER"
  }, /* @__PURE__ */ React.createElement(Add, null)));
};
Counter.defaultProps = {
  className: "",
  steps: 1,
  min: 0,
  max: 100,
  initialValue: 0,
  onAdd: defaultFunction,
  onSubtract: defaultFunction,
  onChange: defaultFunction,
  minDisabled: false,
  maxDisabled: false
};

const showCustomIcon$1 = ({ icon }) => {
  if (!icon)
    return;
  return css`
    &::-webkit-calendar-picker-indicator {
      color: #00000000;
      opacity: 1;
      display: block;
      background: url(${icon}) no-repeat;
      background-size: cover;
      width: 20px;
      height: 20px;
      border-width: 0px;
    }
  `;
};
const DateInputStyled = styled.div`
  width: 100%;

  .DateInput {
    &__label {
      display: block;
      color: #11111180;
      padding-bottom: 4px;
      font-size: 12px;
    }

    &__input {
      width: 100%;
      padding: 0 12px;
      font-family: inherit;
      font-size: 16px;
      line-height: 2.3;
      color: ${(props) => props.value ? "#000000" : "#1111117a"};
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      outline: none;

      &:hover {
        border-color: var(--palette-primary, #4611cf);
      }

      &::placeholder {
        color: #1111117a;
        opacity: 1;
      }

      ${showCustomIcon$1}
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

var __defProp$q = Object.defineProperty;
var __defProps$j = Object.defineProperties;
var __getOwnPropDescs$j = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$q = Object.getOwnPropertySymbols;
var __hasOwnProp$q = Object.prototype.hasOwnProperty;
var __propIsEnum$q = Object.prototype.propertyIsEnumerable;
var __defNormalProp$q = (obj, key, value) => key in obj ? __defProp$q(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$q = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$q.call(b, prop))
      __defNormalProp$q(a, prop, b[prop]);
  if (__getOwnPropSymbols$q)
    for (var prop of __getOwnPropSymbols$q(b)) {
      if (__propIsEnum$q.call(b, prop))
        __defNormalProp$q(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$j = (a, b) => __defProps$j(a, __getOwnPropDescs$j(b));
var __objRest$c = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$q.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$q)
    for (var prop of __getOwnPropSymbols$q(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$q.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const DateInput = forwardRef((props, ref) => {
  const _a = props, { className, label, name, value, id, icon, errorMessage } = _a, rest = __objRest$c(_a, ["className", "label", "name", "value", "id", "icon", "errorMessage"]);
  const errorMessageId = `${id != null ? id : name}-error`;
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "DateInput__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "DateInput__errorMessage"
    }, errorMessage);
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("DateInput: When using the label tag consider passing the 'name' prop");
  }, [label, name]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn("DateInput: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, errorMessage]);
  return /* @__PURE__ */ React.createElement(DateInputStyled, {
    className: `DateInput ${className}`,
    icon,
    value
  }, label ? /* @__PURE__ */ React.createElement("label", {
    className: "DateInput__label",
    htmlFor: id != null ? id : name
  }, label) : null, /* @__PURE__ */ React.createElement("input", __spreadProps$j(__spreadValues$q({
    className: "DateInput__input",
    id: id != null ? id : name,
    type: "date",
    name,
    value,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId
  }, rest), {
    ref
  })), renderErrorMessage());
});
DateInput.defaultProps = {
  className: ""
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var dayjs_min = {exports: {}};

(function (module, exports) {
!function(t,e){module.exports=e();}(commonjsGlobal,(function(){var t=1e3,e=6e4,n=36e5,r="millisecond",i="second",s="minute",u="hour",a="day",o="week",f="month",h="quarter",c="year",d="date",$="Invalid Date",l=/^(\d{4})[-/]?(\d{1,2})?[-/]?(\d{0,2})[Tt\s]*(\d{1,2})?:?(\d{1,2})?:?(\d{1,2})?[.:]?(\d+)?$/,y=/\[([^\]]+)]|Y{1,4}|M{1,4}|D{1,2}|d{1,4}|H{1,2}|h{1,2}|a|A|m{1,2}|s{1,2}|Z{1,2}|SSS/g,M={name:"en",weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_")},m=function(t,e,n){var r=String(t);return !r||r.length>=e?t:""+Array(e+1-r.length).join(n)+t},g={s:m,z:function(t){var e=-t.utcOffset(),n=Math.abs(e),r=Math.floor(n/60),i=n%60;return (e<=0?"+":"-")+m(r,2,"0")+":"+m(i,2,"0")},m:function t(e,n){if(e.date()<n.date())return -t(n,e);var r=12*(n.year()-e.year())+(n.month()-e.month()),i=e.clone().add(r,f),s=n-i<0,u=e.clone().add(r+(s?-1:1),f);return +(-(r+(n-i)/(s?i-u:u-i))||0)},a:function(t){return t<0?Math.ceil(t)||0:Math.floor(t)},p:function(t){return {M:f,y:c,w:o,d:a,D:d,h:u,m:s,s:i,ms:r,Q:h}[t]||String(t||"").toLowerCase().replace(/s$/,"")},u:function(t){return void 0===t}},D="en",v={};v[D]=M;var p=function(t){return t instanceof _},S=function(t,e,n){var r;if(!t)return D;if("string"==typeof t)v[t]&&(r=t),e&&(v[t]=e,r=t);else {var i=t.name;v[i]=t,r=i;}return !n&&r&&(D=r),r||!n&&D},w=function(t,e){if(p(t))return t.clone();var n="object"==typeof e?e:{};return n.date=t,n.args=arguments,new _(n)},O=g;O.l=S,O.i=p,O.w=function(t,e){return w(t,{locale:e.$L,utc:e.$u,x:e.$x,$offset:e.$offset})};var _=function(){function M(t){this.$L=S(t.locale,null,!0),this.parse(t);}var m=M.prototype;return m.parse=function(t){this.$d=function(t){var e=t.date,n=t.utc;if(null===e)return new Date(NaN);if(O.u(e))return new Date;if(e instanceof Date)return new Date(e);if("string"==typeof e&&!/Z$/i.test(e)){var r=e.match(l);if(r){var i=r[2]-1||0,s=(r[7]||"0").substring(0,3);return n?new Date(Date.UTC(r[1],i,r[3]||1,r[4]||0,r[5]||0,r[6]||0,s)):new Date(r[1],i,r[3]||1,r[4]||0,r[5]||0,r[6]||0,s)}}return new Date(e)}(t),this.$x=t.x||{},this.init();},m.init=function(){var t=this.$d;this.$y=t.getFullYear(),this.$M=t.getMonth(),this.$D=t.getDate(),this.$W=t.getDay(),this.$H=t.getHours(),this.$m=t.getMinutes(),this.$s=t.getSeconds(),this.$ms=t.getMilliseconds();},m.$utils=function(){return O},m.isValid=function(){return !(this.$d.toString()===$)},m.isSame=function(t,e){var n=w(t);return this.startOf(e)<=n&&n<=this.endOf(e)},m.isAfter=function(t,e){return w(t)<this.startOf(e)},m.isBefore=function(t,e){return this.endOf(e)<w(t)},m.$g=function(t,e,n){return O.u(t)?this[e]:this.set(n,t)},m.unix=function(){return Math.floor(this.valueOf()/1e3)},m.valueOf=function(){return this.$d.getTime()},m.startOf=function(t,e){var n=this,r=!!O.u(e)||e,h=O.p(t),$=function(t,e){var i=O.w(n.$u?Date.UTC(n.$y,e,t):new Date(n.$y,e,t),n);return r?i:i.endOf(a)},l=function(t,e){return O.w(n.toDate()[t].apply(n.toDate("s"),(r?[0,0,0,0]:[23,59,59,999]).slice(e)),n)},y=this.$W,M=this.$M,m=this.$D,g="set"+(this.$u?"UTC":"");switch(h){case c:return r?$(1,0):$(31,11);case f:return r?$(1,M):$(0,M+1);case o:var D=this.$locale().weekStart||0,v=(y<D?y+7:y)-D;return $(r?m-v:m+(6-v),M);case a:case d:return l(g+"Hours",0);case u:return l(g+"Minutes",1);case s:return l(g+"Seconds",2);case i:return l(g+"Milliseconds",3);default:return this.clone()}},m.endOf=function(t){return this.startOf(t,!1)},m.$set=function(t,e){var n,o=O.p(t),h="set"+(this.$u?"UTC":""),$=(n={},n[a]=h+"Date",n[d]=h+"Date",n[f]=h+"Month",n[c]=h+"FullYear",n[u]=h+"Hours",n[s]=h+"Minutes",n[i]=h+"Seconds",n[r]=h+"Milliseconds",n)[o],l=o===a?this.$D+(e-this.$W):e;if(o===f||o===c){var y=this.clone().set(d,1);y.$d[$](l),y.init(),this.$d=y.set(d,Math.min(this.$D,y.daysInMonth())).$d;}else $&&this.$d[$](l);return this.init(),this},m.set=function(t,e){return this.clone().$set(t,e)},m.get=function(t){return this[O.p(t)]()},m.add=function(r,h){var d,$=this;r=Number(r);var l=O.p(h),y=function(t){var e=w($);return O.w(e.date(e.date()+Math.round(t*r)),$)};if(l===f)return this.set(f,this.$M+r);if(l===c)return this.set(c,this.$y+r);if(l===a)return y(1);if(l===o)return y(7);var M=(d={},d[s]=e,d[u]=n,d[i]=t,d)[l]||1,m=this.$d.getTime()+r*M;return O.w(m,this)},m.subtract=function(t,e){return this.add(-1*t,e)},m.format=function(t){var e=this,n=this.$locale();if(!this.isValid())return n.invalidDate||$;var r=t||"YYYY-MM-DDTHH:mm:ssZ",i=O.z(this),s=this.$H,u=this.$m,a=this.$M,o=n.weekdays,f=n.months,h=function(t,n,i,s){return t&&(t[n]||t(e,r))||i[n].substr(0,s)},c=function(t){return O.s(s%12||12,t,"0")},d=n.meridiem||function(t,e,n){var r=t<12?"AM":"PM";return n?r.toLowerCase():r},l={YY:String(this.$y).slice(-2),YYYY:this.$y,M:a+1,MM:O.s(a+1,2,"0"),MMM:h(n.monthsShort,a,f,3),MMMM:h(f,a),D:this.$D,DD:O.s(this.$D,2,"0"),d:String(this.$W),dd:h(n.weekdaysMin,this.$W,o,2),ddd:h(n.weekdaysShort,this.$W,o,3),dddd:o[this.$W],H:String(s),HH:O.s(s,2,"0"),h:c(1),hh:c(2),a:d(s,u,!0),A:d(s,u,!1),m:String(u),mm:O.s(u,2,"0"),s:String(this.$s),ss:O.s(this.$s,2,"0"),SSS:O.s(this.$ms,3,"0"),Z:i};return r.replace(y,(function(t,e){return e||l[t]||i.replace(":","")}))},m.utcOffset=function(){return 15*-Math.round(this.$d.getTimezoneOffset()/15)},m.diff=function(r,d,$){var l,y=O.p(d),M=w(r),m=(M.utcOffset()-this.utcOffset())*e,g=this-M,D=O.m(this,M);return D=(l={},l[c]=D/12,l[f]=D,l[h]=D/3,l[o]=(g-m)/6048e5,l[a]=(g-m)/864e5,l[u]=g/n,l[s]=g/e,l[i]=g/t,l)[y]||g,$?D:O.a(D)},m.daysInMonth=function(){return this.endOf(f).$D},m.$locale=function(){return v[this.$L]},m.locale=function(t,e){if(!t)return this.$L;var n=this.clone(),r=S(t,e,!0);return r&&(n.$L=r),n},m.clone=function(){return O.w(this.$d,this)},m.toDate=function(){return new Date(this.valueOf())},m.toJSON=function(){return this.isValid()?this.toISOString():null},m.toISOString=function(){return this.$d.toISOString()},m.toString=function(){return this.$d.toUTCString()},M}(),b=_.prototype;return w.prototype=b,[["$ms",r],["$s",i],["$m",s],["$H",u],["$W",a],["$M",f],["$y",c],["$D",d]].forEach((function(t){b[t[1]]=function(e){return this.$g(e,t[0],t[1])};})),w.extend=function(t,e){return t.$i||(t(e,_,w),t.$i=!0),w},w.locale=S,w.isDayjs=p,w.unix=function(t){return w(1e3*t)},w.en=v[D],w.Ls=v,w.p={},w}));
}(dayjs_min));

var dayjs = dayjs_min.exports;

const showCustomIcon = (props) => {
  if (!props.icon)
    return;
  return css`
    &::-webkit-calendar-picker-indicator {
      color: transparent;
      opacity: 1;
      display: block;
      background: url(${props.icon}) no-repeat;
      background-size: cover;
      width: 20px;
      height: 20px;
      border-width: 0px;
    }
  `;
};
const DateOfBirthInputStyled = styled.div`
  width: 100%;

  .DateOfBirthInput {
    &__label {
      display: block;
      color: #11111180;
      padding-bottom: 4px;
      font-size: 12px;
    }

    &__input {
      width: 100%;
      padding: 0 12px;
      font-family: inherit;
      font-size: 16px;
      line-height: 2.3;
      color: ${(props) => props.value ? "#000000" : "#1111117a"};
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      outline: none;

      &:hover {
        border-color: var(--palette-primary, #4611cf);
      }

      &::placeholder {
        color: #1111117a;
        opacity: 1;
      }

      ${showCustomIcon}
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

const CONSTANTS = {
  BREAKPOINTS: {
    mobile: 420,
    tablet: 768,
    desktop: 1024,
    wide: 1440,
    hd: 2560
  },
  DATE: {
    DATE_FORMAT: "YYYY-MM-DD"
  }
};

var __defProp$p = Object.defineProperty;
var __defProps$i = Object.defineProperties;
var __getOwnPropDescs$i = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$p = Object.getOwnPropertySymbols;
var __hasOwnProp$p = Object.prototype.hasOwnProperty;
var __propIsEnum$p = Object.prototype.propertyIsEnumerable;
var __defNormalProp$p = (obj, key, value) => key in obj ? __defProp$p(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$p = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$p.call(b, prop))
      __defNormalProp$p(a, prop, b[prop]);
  if (__getOwnPropSymbols$p)
    for (var prop of __getOwnPropSymbols$p(b)) {
      if (__propIsEnum$p.call(b, prop))
        __defNormalProp$p(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$i = (a, b) => __defProps$i(a, __getOwnPropDescs$i(b));
var __objRest$b = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$p.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$p)
    for (var prop of __getOwnPropSymbols$p(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$p.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const { DATE_FORMAT } = CONSTANTS.DATE;
const DateOfBirthInput = forwardRef((props, ref) => {
  const _a = props, {
    className,
    label,
    name,
    id,
    icon,
    minAge,
    maxAge,
    value,
    errorMessage
  } = _a, rest = __objRest$b(_a, [
    "className",
    "label",
    "name",
    "id",
    "icon",
    "minAge",
    "maxAge",
    "value",
    "errorMessage"
  ]);
  const [minimumDate, setMinimumDate] = useState("");
  const [maximumDate, setMaximumDate] = useState("");
  const errorMessageId = `${id != null ? id : name}-error`;
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "DateOfBirthInput__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "DateOfBirthInput__errorMessage"
    }, errorMessage);
  };
  useEffect(() => {
    if (minAge && maxAge) {
      const maxDate = dayjs().subtract(minAge, "year");
      const minDate = dayjs().subtract(maxAge + 1, "year").add(1, "day");
      setMinimumDate(minDate.format(DATE_FORMAT));
      setMaximumDate(maxDate.format(DATE_FORMAT));
      return;
    }
    if (minAge && minAge < 0 || maxAge && maxAge < 0) {
      console.warn(`DateOfBirthInput: The values of 'minAge' and 'maxAge' cannot be less than 0`);
      setMaximumDate(dayjs().format(DATE_FORMAT));
      return;
    }
    if (minAge) {
      const maxDate = dayjs().subtract(minAge, "year");
      setMaximumDate(maxDate.format(DATE_FORMAT));
      return;
    }
    if (maxAge) {
      const minDate = dayjs().subtract(maxAge + 1, "year").add(1, "day");
      setMinimumDate(minDate.format(DATE_FORMAT));
      setMaximumDate(dayjs().format(DATE_FORMAT));
      return;
    }
    setMaximumDate(dayjs().format(DATE_FORMAT));
  }, [minAge, maxAge]);
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("DateOfBirthInput: When using the label tag consider passing the 'name' prop");
  }, [name, label]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn("DateOfBirthInput: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, errorMessage]);
  return /* @__PURE__ */ React.createElement(DateOfBirthInputStyled, {
    className: `DateOfBirthInput ${className}`,
    icon,
    value
  }, label ? /* @__PURE__ */ React.createElement("label", {
    className: "DateOfBirthInput__label",
    htmlFor: id != null ? id : name
  }, label) : null, /* @__PURE__ */ React.createElement("input", __spreadProps$i(__spreadValues$p({
    className: "DateOfBirthInput__input",
    id: id != null ? id : name,
    type: "date",
    name,
    min: minimumDate,
    max: maximumDate,
    value,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId
  }, rest), {
    ref
  })), renderErrorMessage());
});
DateOfBirthInput.defaultProps = {
  className: ""
};

const DocumentInputStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .DocumentInput {
    &__label {
      color: #11111180;
      padding-bottom: 4px;
      font-size: 12px;
    }

    &__input {
      width: 100%;
      padding: 0 12px;
      font-family: inherit;
      font-size: 16px;
      line-height: 2.4;
      color: #111111;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      outline: none;

      &:hover {
        border-color: var(--palette-primary, #4611cf);
      }

      &:focus {
        border-color: var(--palette-primary, #4611cf);
        outline: 0;
        box-shadow: 0 0 0 2px #1890ff33;
      }
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

const idCoefficients = [2, 1, 2, 1, 2, 1, 2, 1, 2];
const legalPersonRUCCoefficients = [4, 3, 2, 7, 6, 5, 4, 3, 2];
const publicCompanyRUCCoefficients = [3, 2, 7, 6, 5, 4, 3, 2];
const validateCI = (document) => {
  if (validateProvince(document.substring(0, 2)) && isIDNaturalPerson(document) && validateID(document, idCoefficients)) {
    return;
  }
  return "C\xE9dula de identidad inv\xE1lida";
};
const validateRUC = (document) => {
  const validateNaturalRUC = isRUCNaturalPerson(document) && validateID(document, idCoefficients);
  const validateLegalRUC = isRUCLegalPerson(document) && validateRUCByCoefficients(document, legalPersonRUCCoefficients, document.charAt(9));
  const validateCompanyRUC = isRUCLegalCompany(document) && validateRUCByCoefficients(document, publicCompanyRUCCoefficients, document.charAt(8));
  if (validateNaturalRUC || validateLegalRUC || validateCompanyRUC)
    return;
  return "RUC inv\xE1lido";
};
const validateCI_CO = (document) => {
  if (/^\d{6,11}$/.test(document))
    return;
  return "C\xE9dula de ciudadan\xEDa inv\xE1lida";
};
const validateCE = (document) => {
  if (/^\d{5,7}$/.test(document))
    return;
  return "Cedula de extranjer\xEDa inv\xE1lida";
};
const validateNIT = (document) => {
  if (/^\d{7,10}$/.test(document))
    return;
  return "NIT inv\xE1lido";
};
const validatePassport = (document) => {
  if (document.length > 20)
    return "Pasaporte inv\xE1lido";
};
const validateRut = (document) => {
  if (document.length <= 20)
    return "RUT inv\xE1lido";
};
const isIDNaturalPerson = (identification) => {
  return identification.length === 10;
};
const isRUCNaturalPerson = (identification) => {
  return identification.length === 13 && identification.charAt(2) !== "6" && identification.charAt(2) !== "9" && identification.substring(10, 13) === "001";
};
const lastRUCDigits = (identification) => {
  return identification.length === 13 && identification.substring(10, 13) === "001";
};
const isRUCLegalPerson = (identification) => {
  return lastRUCDigits(identification) && identification.charAt(2) === "9";
};
const isRUCLegalCompany = (identification) => {
  return lastRUCDigits(identification) && identification.charAt(2) === "6";
};
const validateID = (identification, coefficients) => {
  const id = identification;
  let addDigitsByCoefficient = 0;
  let value = 0;
  for (let i = 0; i < coefficients.length; i++) {
    const digit = Number(id.charAt(i)) * 1;
    value = coefficients[i] * digit;
    if (value > 9) {
      value = value - 9;
    }
    addDigitsByCoefficient = addDigitsByCoefficient + value;
  }
  let module = addDigitsByCoefficient % 10;
  module = module === 0 ? 10 : module;
  const result = 10 - module;
  const lastDigit = Number(id.charAt(9)) * 1;
  return result === lastDigit;
};
const validateRUCByCoefficients = (identification, coefficients, checkDigit) => {
  const id = identification;
  const verifier = Number(checkDigit) * 1;
  let addTotalsDigitsByCoefficient = 0;
  let digit = 0;
  let value = 0;
  for (let i = 0; i < coefficients.length; i++) {
    digit = Number(id.charAt(i)) * 1;
    value = coefficients[i] * digit;
    addTotalsDigitsByCoefficient = addTotalsDigitsByCoefficient + value;
  }
  const module = addTotalsDigitsByCoefficient % 11;
  let result = 0;
  if (module !== 0) {
    result = 11 - module;
  }
  return result === verifier;
};
const validateProvince = (identification) => {
  return parseInt(identification) <= 0 || parseInt(identification) > 24;
};
const validateCODocuments = (documentType, value) => {
  switch (documentType) {
    case "PASSPORT":
      return validatePassport(value);
    case "RUT":
      return validateRut(value);
    case "NIT":
      return validateNIT(value);
    case "CE":
      return validateCE(value);
    case "CC":
      return validateCI_CO(value);
    default:
      console.error("DocumentInput: The type of document entered is invalid for the selected country.");
      return "DocumentInput: The type of document entered is invalid for the selected country.";
  }
};
const validateECDocuments = (documentType, value) => {
  switch (documentType) {
    case "PASSPORT":
      validatePassport(value);
      break;
    case "CI":
      return validateCI(value);
    case "RUC":
      return validateRUC(value);
    default:
      console.error("DocumentInput: The type of document entered is invalid for the selected country.");
      return "DocumentInput: The type of document entered is invalid for the selected country.";
  }
};
const validateDocument = (countryCode, documentType, value) => {
  switch (countryCode) {
    case "AR":
      return validatePassport(value);
    case "BO":
      return validatePassport(value);
    case "BR":
      return validatePassport(value);
    case "CL":
      return validatePassport(value);
    case "CO":
      return validateCODocuments(documentType, value);
    case "EC":
      return validateECDocuments(documentType, value);
    case "PY":
      return validatePassport(value);
    case "PE":
      return validatePassport(value);
    case "UY":
      return validatePassport(value);
    case "VE":
      return validatePassport(value);
    case "US":
      return validatePassport(value);
    default:
      console.error("DocumentInput: The country code entered is not currently supported.");
      return "DocumentInput: The country code entered is not currently supported.";
  }
};

var __defProp$o = Object.defineProperty;
var __defProps$h = Object.defineProperties;
var __getOwnPropDescs$h = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$o = Object.getOwnPropertySymbols;
var __hasOwnProp$o = Object.prototype.hasOwnProperty;
var __propIsEnum$o = Object.prototype.propertyIsEnumerable;
var __defNormalProp$o = (obj, key, value) => key in obj ? __defProp$o(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$o = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$o.call(b, prop))
      __defNormalProp$o(a, prop, b[prop]);
  if (__getOwnPropSymbols$o)
    for (var prop of __getOwnPropSymbols$o(b)) {
      if (__propIsEnum$o.call(b, prop))
        __defNormalProp$o(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$h = (a, b) => __defProps$h(a, __getOwnPropDescs$h(b));
var __objRest$a = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$o.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$o)
    for (var prop of __getOwnPropSymbols$o(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$o.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const DocumentInput = forwardRef((props, ref) => {
  const _a = props, {
    label,
    name,
    id,
    className,
    errorMessage,
    value,
    countryCode,
    documentType
  } = _a, rest = __objRest$a(_a, [
    "label",
    "name",
    "id",
    "className",
    "errorMessage",
    "value",
    "countryCode",
    "documentType"
  ]);
  const [validationError, setValidationError] = useState("");
  const errorMessageId = `${id != null ? id : name}-error`;
  const selectedError = errorMessage != null ? errorMessage : validationError;
  const renderLabel = () => {
    if (!label)
      return null;
    return /* @__PURE__ */ React.createElement("label", {
      className: "DocumentInput__label",
      htmlFor: id != null ? id : name
    }, label);
  };
  const renderErrorMessage = () => {
    if (!selectedError)
      return null;
    if (typeof selectedError === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "DocumentInput__errorMessage"
      }, selectedError);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "DocumentInput__errorMessage"
    }, selectedError);
  };
  const blurHandler = (event) => {
    const value2 = event.target.value;
    const errorMessage2 = validateDocument(countryCode, documentType, value2);
    if (errorMessage2)
      setValidationError(errorMessage2);
  };
  const focusHandler = () => {
    setValidationError("");
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("DocumentInput: When using the label tag consider passing the 'name' prop");
  }, [name, label]);
  useEffect(() => {
    if (name || id || !selectedError)
      return;
    console.warn("DocumentInput: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, selectedError]);
  return /* @__PURE__ */ React.createElement(DocumentInputStyled, {
    className: `DocumentInput ${className}`
  }, renderLabel(), /* @__PURE__ */ React.createElement("input", __spreadProps$h(__spreadValues$o({
    className: "DocumentInput__input",
    id: id != null ? id : name,
    type: "text",
    name,
    minLength: 6,
    maxLength: 80,
    value,
    "aria-invalid": selectedError ? "true" : "false",
    "aria-errormessage": errorMessageId,
    onBlur: blurHandler,
    onFocus: focusHandler
  }, rest), {
    ref
  })), renderErrorMessage());
});
DocumentInput.defaultProps = {
  className: ""
};

const DocumentTypeSelectStyled = styled.div`
  width: 100%;
  pointer-events: ${(props) => props.disabled ? "none" : "auto"};

  .DocumentTypeSelect {
    &__label {
      display: block;
      color: #11111180;
      padding-bottom: 4px;
      font-size: 12px;
    }

    &__selectContainer {
      width: 100%;
      padding: 0 12px;
      font-size: 16px;
      line-height: 2.4;
      border: 1px solid #d9d9d9;
      border-radius: 4px;

      &:hover {
        border-color: var(--palette-primary, #4611cf);
      }
    }

    &__select {
      width: 100%;
      font-family: inherit;
      font-size: inherit;
      color: ${(props) => props.disabled ? "#cccccc" : "inherit"};
      border: none;
      outline: none;
      cursor: pointer;
      background: inherit;

      &::placeholder {
        color: #1111117a;
        opacity: 1;
      }
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

const passportDocumentType = {
  value: "PASSPORT",
  displayName: "Pasaporte"
};
const documentTypesByCountry = {
  AR: [passportDocumentType],
  BO: [passportDocumentType],
  BR: [passportDocumentType],
  CL: [passportDocumentType],
  CO: [
    { value: "RUT", displayName: "RUT" },
    { value: "NIT", displayName: "NIT" },
    passportDocumentType,
    { value: "CE", displayName: "C\xE9dula de extranjer\xEDa" },
    { value: "CC", displayName: "C\xE9dula de ciudadan\xEDa" }
  ],
  EC: [
    passportDocumentType,
    { value: "CI", displayName: "C\xE9dula de identidad" },
    { value: "RUC", displayName: "RUC" }
  ],
  PY: [passportDocumentType],
  PE: [passportDocumentType],
  UY: [passportDocumentType],
  VE: [passportDocumentType],
  US: [passportDocumentType]
};

var __defProp$n = Object.defineProperty;
var __defProps$g = Object.defineProperties;
var __getOwnPropDescs$g = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$n = Object.getOwnPropertySymbols;
var __hasOwnProp$n = Object.prototype.hasOwnProperty;
var __propIsEnum$n = Object.prototype.propertyIsEnumerable;
var __defNormalProp$n = (obj, key, value) => key in obj ? __defProp$n(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$n = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$n.call(b, prop))
      __defNormalProp$n(a, prop, b[prop]);
  if (__getOwnPropSymbols$n)
    for (var prop of __getOwnPropSymbols$n(b)) {
      if (__propIsEnum$n.call(b, prop))
        __defNormalProp$n(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$g = (a, b) => __defProps$g(a, __getOwnPropDescs$g(b));
var __objRest$9 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$n.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$n)
    for (var prop of __getOwnPropSymbols$n(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$n.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const DocumentTypeSelect = forwardRef((props, ref) => {
  const _a = props, {
    className,
    id,
    label,
    name,
    value,
    errorMessage,
    countryCode,
    disabled
  } = _a, rest = __objRest$9(_a, [
    "className",
    "id",
    "label",
    "name",
    "value",
    "errorMessage",
    "countryCode",
    "disabled"
  ]);
  const errorMessageId = `${id != null ? id : name}-error`;
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "DocumentTypeSelect__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "DocumentTypeSelect__errorMessage"
    }, errorMessage);
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("DocumentTypeSelect: When using the label tag consider passing the 'name' prop");
  }, [name, label]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn("DocumentTypeSelect: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, errorMessage]);
  return /* @__PURE__ */ React.createElement(DocumentTypeSelectStyled, {
    className: `DocumentTypeSelect ${className}`,
    disabled
  }, label ? /* @__PURE__ */ React.createElement("label", {
    className: "DocumentTypeSelect__label",
    htmlFor: id != null ? id : name
  }, label) : null, /* @__PURE__ */ React.createElement("div", {
    className: "DocumentTypeSelect__selectContainer"
  }, /* @__PURE__ */ React.createElement("select", __spreadProps$g(__spreadValues$n({
    id: name,
    className: "DocumentTypeSelect__select",
    name,
    value,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId
  }, rest), {
    ref
  }), /* @__PURE__ */ React.createElement("option", {
    value: ""
  }, "Selecciona un tipo de identificaci\xF3n"), documentTypesByCountry[countryCode].map((documentType, index) => /* @__PURE__ */ React.createElement("option", {
    key: index,
    value: documentType.value
  }, documentType.displayName)))), renderErrorMessage());
});
DocumentTypeSelect.defaultProps = {
  className: ""
};

const mapClosedPosition = (props) => {
  switch (props.position) {
    case "left":
      return `translateX(calc(${getScreenMaxWidth()} * -1))`;
    case "right":
    default:
      return `translateX(${getScreenMaxWidth()})`;
  }
};
const initialPosition = (props) => {
  switch (props.position) {
    case "left":
      return css`
        left: 0;
      `;
    case "right":
    default:
      return css`
        right: 0;
      `;
  }
};
const DrawerStyled = styled.div`
  min-height: 8px;
  min-width: 8px;
  position: fixed;
  top: 0;
  ${initialPosition};
  transition: transform 0.2s ease-out;
  background-color: #fff;
  display: flex;
  flex-direction: column;

  &.Drawer {
    &__opened {
      transform: translateX(0);
      box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2);
    }

    &__closed {
      transform: ${mapClosedPosition};
      box-shadow: none;
    }
  }
`;

var __defProp$m = Object.defineProperty;
var __defProps$f = Object.defineProperties;
var __getOwnPropDescs$f = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$m = Object.getOwnPropertySymbols;
var __hasOwnProp$m = Object.prototype.hasOwnProperty;
var __propIsEnum$m = Object.prototype.propertyIsEnumerable;
var __defNormalProp$m = (obj, key, value) => key in obj ? __defProp$m(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$m = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$m.call(b, prop))
      __defNormalProp$m(a, prop, b[prop]);
  if (__getOwnPropSymbols$m)
    for (var prop of __getOwnPropSymbols$m(b)) {
      if (__propIsEnum$m.call(b, prop))
        __defNormalProp$m(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$f = (a, b) => __defProps$f(a, __getOwnPropDescs$f(b));
const Drawer = (props) => {
  const { className, children, opened, onClose } = props;
  const { closeOnClickOutside, position = "right", backdropConfig } = props;
  const openedClass = opened ? "Drawer__opened" : "Drawer__closed";
  const onBackdropClicked = () => {
    var _a;
    if (closeOnClickOutside) {
      closeHandler();
      (_a = backdropConfig == null ? void 0 : backdropConfig.onClick) == null ? void 0 : _a.call(backdropConfig);
    }
  };
  const closeHandler = () => {
    onClose();
  };
  if (!opened)
    return null;
  return /* @__PURE__ */ React.createElement(Backdrop, __spreadProps$f(__spreadValues$m({
    name: "Drawer",
    onClick: onBackdropClicked
  }, backdropConfig), {
    opened: true
  }), /* @__PURE__ */ React.createElement(DrawerStyled, {
    className: `Drawer ${className} ${openedClass}`,
    position
  }, children));
};
Drawer.defaultProps = {
  className: "",
  opened: true,
  closeOnClickOutside: true,
  position: "right"
};

const DropdownStyled = styled.div`
  .Dropdown {
  }
`;

const useWindowSize = () => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useIsomorphicLayoutEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const updateSize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);
  return size;
};

const initialMeasures = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  height: 0,
  width: 0,
  x: 0,
  y: 0
};

const mapPosition = (props) => {
  const { measures, position, width } = props;
  const elementWidth = width != null ? width : measures.width;
  const widthRest = width ? width - measures.width : 0;
  const rightElementWidth = measures.left + measures.width;
  const leftElementWidth = measures.left;
  switch (position) {
    case "left":
      return css`
        left: ${leftElementWidth}px;
        width: ${elementWidth}px;
      `;
    case "right":
      return css`
        left: ${rightElementWidth - elementWidth}px;
        width: ${elementWidth}px;
      `;
    case "center":
      return css`
        left: ${leftElementWidth - widthRest / 2}px;
        width: ${elementWidth}px;
      `;
  }
};
const OverlayStyled = styled.div`
  position: absolute;
  z-index: 3;
  top: ${(props) => props.measures.y + props.measures.height}px;
  min-height: 8px;
  ${mapPosition}
  isolation: isolate;

  .Overlay {
  }
`;

var __defProp$l = Object.defineProperty;
var __defProps$e = Object.defineProperties;
var __getOwnPropDescs$e = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$l = Object.getOwnPropertySymbols;
var __hasOwnProp$l = Object.prototype.hasOwnProperty;
var __propIsEnum$l = Object.prototype.propertyIsEnumerable;
var __defNormalProp$l = (obj, key, value) => key in obj ? __defProp$l(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$l = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$l.call(b, prop))
      __defNormalProp$l(a, prop, b[prop]);
  if (__getOwnPropSymbols$l)
    for (var prop of __getOwnPropSymbols$l(b)) {
      if (__propIsEnum$l.call(b, prop))
        __defNormalProp$l(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$e = (a, b) => __defProps$e(a, __getOwnPropDescs$e(b));
const Overlay = forwardRef((props, ref) => {
  var _a;
  const { children, className, parentRef, position } = props;
  const { width: widthProp, isFixed, target, backdropConfig } = props;
  const { width } = useWindowSize();
  const targetIsFocus = target == null ? void 0 : target.includes("focus");
  const targetIsHover = target == null ? void 0 : target.includes("hover");
  const noShowBackdrop = targetIsFocus || targetIsHover;
  const overlayRef = useRef(null);
  const [parentMeasures, setParentMeasures] = useState(initialMeasures);
  const portalNode = usePortal("Overlay");
  useIsomorphicLayoutEffect(() => {
    var _a2;
    if (!(parentRef == null ? void 0 : parentRef.current))
      return;
    const measuresRect = (_a2 = parentRef.current) == null ? void 0 : _a2.getBoundingClientRect();
    if (!measuresRect)
      return;
    const measures = measuresRect == null ? void 0 : measuresRect.toJSON();
    if (!isFixed) {
      measures.y = measures.y + window.scrollY;
    }
    setParentMeasures(measures);
  }, [width]);
  if (!portalNode) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, !noShowBackdrop ? /* @__PURE__ */ React.createElement(Backdrop, __spreadProps$e(__spreadValues$l({
    color: "transparent",
    opacity: 0,
    name: "Dropdown-backdrop"
  }, backdropConfig), {
    allowScroll: true,
    opened: true
  })) : null, createPortal(/* @__PURE__ */ React.createElement(OverlayStyled, {
    className: `Overlay ${className}`,
    measures: parentMeasures,
    position,
    width: widthProp != null ? widthProp : (_a = overlayRef == null ? void 0 : overlayRef.current) == null ? void 0 : _a.getBoundingClientRect().width
  }, Children.map(children, (child) => {
    if (!isValidElement(child)) {
      return child;
    }
    return cloneElement(child, {
      ref: overlayRef
    });
  })), portalNode));
});
Overlay.defaultProps = {
  className: ""
};

const Dropdown = (props) => {
  const { overlay, children, target = ["click"], className = "" } = props;
  const { position = "left", width, showDropdown } = props;
  const { isFixed = false, backdropConfig } = props;
  const dropdownRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const showLocalOverlay = typeof showDropdown !== "undefined" ? showDropdown : showOverlay;
  const onMouseEnter = target.includes("hover") ? () => setShowOverlay(true) : defaultFunction;
  const onMouseLeave = target.includes("hover") ? () => setShowOverlay(false) : defaultFunction;
  const onClick = target.includes("click") ? () => setShowOverlay((prev) => !prev) : defaultFunction;
  const onFocus = target.includes("focus") ? () => setShowOverlay(true) : defaultFunction;
  const onBlur = target.includes("focus") ? () => setShowOverlay(false) : defaultFunction;
  return /* @__PURE__ */ React.createElement(DropdownStyled, {
    className: `Dropdown ${className}`,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    ref: dropdownRef
  }, children, showLocalOverlay ? /* @__PURE__ */ React.createElement(Overlay, {
    parentRef: dropdownRef,
    position,
    width,
    target,
    isFixed,
    backdropConfig
  }, overlay) : null);
};
Dropdown.defaultProps = {
  className: "",
  target: ["click"],
  position: "left"
};

const EmailInputStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .EmailInput {
    &__label {
      color: #11111180;
      padding-bottom: 0.4rem;
      font-size: 1.2rem;
    }

    &__input {
      width: 100%;
      padding: 0 12px;
      font-size: 16px;
      line-height: 2.4;
      color: #111111;
      border: 0.1rem solid #d9d9d9;
      border-radius: 0.4rem;
      outline: none;
      font-family: inherit;

      &:hover {
        border-color: var(--palette-primary, #4611cf);
      }

      &:focus {
        border-color: var(--palette-primary, #4611cf);
        outline: 0;
        box-shadow: 0 0 0 0.2rem #1890ff33;
      }
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

var __defProp$k = Object.defineProperty;
var __defProps$d = Object.defineProperties;
var __getOwnPropDescs$d = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$k = Object.getOwnPropertySymbols;
var __hasOwnProp$k = Object.prototype.hasOwnProperty;
var __propIsEnum$k = Object.prototype.propertyIsEnumerable;
var __defNormalProp$k = (obj, key, value) => key in obj ? __defProp$k(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$k = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$k.call(b, prop))
      __defNormalProp$k(a, prop, b[prop]);
  if (__getOwnPropSymbols$k)
    for (var prop of __getOwnPropSymbols$k(b)) {
      if (__propIsEnum$k.call(b, prop))
        __defNormalProp$k(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$d = (a, b) => __defProps$d(a, __getOwnPropDescs$d(b));
var __objRest$8 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$k.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$k)
    for (var prop of __getOwnPropSymbols$k(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$k.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const EmailInput = forwardRef((props, ref) => {
  const _a = props, { label, name, id, className, errorMessage } = _a, rest = __objRest$8(_a, ["label", "name", "id", "className", "errorMessage"]);
  const errorMessageId = `${id != null ? id : name}-error`;
  const renderLabel = () => {
    if (!label)
      return null;
    return /* @__PURE__ */ React.createElement("label", {
      className: "EmailInput__label",
      htmlFor: id != null ? id : name
    }, label);
  };
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "EmailInput__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "EmailInput__errorMessage"
    }, errorMessage);
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("EmailInput: When using the label tag consider passing the 'name' prop");
  }, [name, label]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn("EmailInput: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, errorMessage]);
  return /* @__PURE__ */ React.createElement(EmailInputStyled, {
    className: `EmailInput ${className}`
  }, renderLabel(), /* @__PURE__ */ React.createElement("input", __spreadProps$d(__spreadValues$k({
    className: "EmailInput__input",
    id: id != null ? id : name,
    type: "email",
    name,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId,
    minLength: 6,
    maxLength: 80
  }, rest), {
    ref
  })), renderErrorMessage());
});
EmailInput.defaultProps = {
  className: ""
};

const LightboxImageStyled = styled.div`
  .LightboxImage {
    &__controls {
      display: flex;
    }

    &__icon {
      width: 20px;
      height: 20px;
    }

    &__image {
    }
  }
`;

const ModalStyled = styled.div`
  position: fixed;
  background-color: ${(props) => {
  var _a;
  return (_a = props.color) != null ? _a : "#ffffff";
}};
  width: ${(props) => `${props.width}px`};
  height: ${(props) => `${props.height}px`};
  max-height: ${getScreenMaxHeight()};
  max-width: ${getScreenMaxWidth()};
  border-radius: 4px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  isolation: isolate;

  .Modal {
    &__header {
      display: flex;
      flex-wrap: nowrap;
      justify-content: space-between;
    }

    &__close-icon {
      cursor: pointer;
      position: absolute;
      display: flex;
      align-items: center;
      top: 16px;
      right: 16px;
      z-index: 1;
    }

    &__body {
      flex: 1;
      overflow: auto;
      scrollbar-color: rgba(0, 0, 0, 0.5) rgba(0, 0, 0, 0);
      scrollbar-width: thin;

      &::-webkit-scrollbar {
        -webkit-appearance: none;
      }

      &::-webkit-scrollbar:vertical {
        width: 8px;
      }

      &::-webkit-scrollbar:horizontal {
        height: 8px;
      }

      &::-webkit-scrollbar-button:increment,
      .contenedor::-webkit-scrollbar-button {
        display: none;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #9b9b9b;
        border-radius: 24px;
        border: 2px solid #ffffff;
      }

      &::-webkit-scrollbar-track {
        border-radius: 8px;
      }
    }

    &__footer {
    }
  }
`;
var Styles = memo(ModalStyled);

var __defProp$j = Object.defineProperty;
var __defProps$c = Object.defineProperties;
var __getOwnPropDescs$c = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$j = Object.getOwnPropertySymbols;
var __hasOwnProp$j = Object.prototype.hasOwnProperty;
var __propIsEnum$j = Object.prototype.propertyIsEnumerable;
var __defNormalProp$j = (obj, key, value) => key in obj ? __defProp$j(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$j = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$j.call(b, prop))
      __defNormalProp$j(a, prop, b[prop]);
  if (__getOwnPropSymbols$j)
    for (var prop of __getOwnPropSymbols$j(b)) {
      if (__propIsEnum$j.call(b, prop))
        __defNormalProp$j(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$c = (a, b) => __defProps$c(a, __getOwnPropDescs$c(b));
const Modal = forwardRef((props, ref) => {
  const { className, footer, onClose = defaultFunction } = props;
  const { header, children, closeIcon, opened } = props;
  const { closeOnClickOutside = true, backdropConfig } = props;
  const { width, height, color } = props;
  const [internalOpened, setInternalOpened] = useState(false);
  const show = typeof opened === "undefined" ? internalOpened : opened;
  const onBackdropClicked = () => {
    var _a;
    if (!closeOnClickOutside)
      return;
    closeHandler();
    (_a = backdropConfig == null ? void 0 : backdropConfig.onClick) == null ? void 0 : _a.call(backdropConfig);
  };
  const closeHandler = () => {
    onClose();
    setInternalOpened(false);
  };
  if (!show)
    return null;
  return /* @__PURE__ */ React.createElement(Backdrop, __spreadProps$c(__spreadValues$j({
    onClick: onBackdropClicked,
    name: "Modal"
  }, backdropConfig), {
    opened: true
  }), /* @__PURE__ */ React.createElement(Styles, {
    className: `Modal ${className}`,
    width,
    height,
    color,
    ref
  }, header ? /* @__PURE__ */ React.createElement("header", {
    className: "Modal__header"
  }, header) : null, closeIcon ? /* @__PURE__ */ React.createElement("div", {
    className: "Modal__close-icon",
    onClick: closeHandler
  }, closeIcon) : null, /* @__PURE__ */ React.createElement("main", {
    className: "Modal__body"
  }, children), footer ? /* @__PURE__ */ React.createElement("footer", {
    className: "Modal__footer"
  }, footer) : null));
});
Modal.defaultProps = {
  className: "",
  onClose: defaultFunction,
  closeOnClickOutside: true
};

function _extends$n() { _extends$n = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$n.apply(this, arguments); }
var CrossCircleSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$n({
  width: "20",
  height: "20",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M10 0C4.47 0 0 4.47 0 10s4.47 10 10 10 10-4.47 10-10S15.53 0 10 0zm4.3 14.3a.996.996 0 01-1.41 0L10 11.41 7.11 14.3a.996.996 0 11-1.41-1.41L8.59 10 5.7 7.11A.996.996 0 117.11 5.7L10 8.59l2.89-2.89a.996.996 0 111.41 1.41L11.41 10l2.89 2.89c.38.38.38 1.02 0 1.41z",
  fill: "#6B6B6B"
})));

var __defProp$i = Object.defineProperty;
var __defProps$b = Object.defineProperties;
var __getOwnPropDescs$b = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$i = Object.getOwnPropertySymbols;
var __hasOwnProp$i = Object.prototype.hasOwnProperty;
var __propIsEnum$i = Object.prototype.propertyIsEnumerable;
var __defNormalProp$i = (obj, key, value) => key in obj ? __defProp$i(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$i = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$i.call(b, prop))
      __defNormalProp$i(a, prop, b[prop]);
  if (__getOwnPropSymbols$i)
    for (var prop of __getOwnPropSymbols$i(b)) {
      if (__propIsEnum$i.call(b, prop))
        __defNormalProp$i(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$b = (a, b) => __defProps$b(a, __getOwnPropDescs$b(b));
var __objRest$7 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$i.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$i)
    for (var prop of __getOwnPropSymbols$i(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$i.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const LightboxImage = (props) => {
  const _a = props, {
    className,
    onClose = defaultFunction,
    images,
    imageConfig,
    opened,
    backdropConfig,
    value,
    onChange
  } = _a, rest = __objRest$7(_a, [
    "className",
    "onClose",
    "images",
    "imageConfig",
    "opened",
    "backdropConfig",
    "value",
    "onChange"
  ]);
  const [internalOpened, setInternalOpened] = useState(true);
  const [internalValue, setInternalValue] = useState(0);
  const show = typeof opened === "undefined" ? internalOpened : opened;
  const actualValue = value != null ? value : internalValue;
  const currentImage = useMemo(() => {
    return images[actualValue];
  }, [images, actualValue]);
  const newBackdropConfig = useMemo(() => {
    return __spreadValues$i({
      name: "LightboxImage",
      opacity: 0.6
    }, backdropConfig);
  }, [backdropConfig]);
  const closeHandler = () => {
    onClose();
    setInternalOpened(false);
  };
  const previousHandler = useCallback(() => {
    if (actualValue === 0) {
      const newValue2 = images.length - 1;
      onChange == null ? void 0 : onChange(newValue2);
      setInternalValue(newValue2);
      return;
    }
    const newValue = actualValue - 1;
    onChange == null ? void 0 : onChange(newValue);
    setInternalValue(newValue);
  }, [actualValue, images.length, onChange]);
  const nextHandler = useCallback(() => {
    if (actualValue === images.length - 1) {
      onChange == null ? void 0 : onChange(0);
      setInternalValue(0);
      return;
    }
    const newValue = actualValue + 1;
    onChange == null ? void 0 : onChange(newValue);
    setInternalValue(newValue);
  }, [actualValue, images.length, onChange]);
  const keyUpHandler = useCallback((e) => {
    if (!show)
      return;
    if (e.key === "ArrowLeft")
      previousHandler();
    if (e.key === "ArrowRight")
      nextHandler();
  }, [show, previousHandler, nextHandler]);
  useIsomorphicLayoutEffect(() => {
    document.body.addEventListener("keyup", keyUpHandler);
    return () => {
      document.body.removeEventListener("keyup", keyUpHandler);
    };
  }, [keyUpHandler]);
  if (images.length === 0) {
    console.error(`${LightboxImage.displayName}: There are no images to display`);
    return null;
  } else if (actualValue >= images.length) {
    console.error(`${LightboxImage.displayName}: The value ${actualValue} is invalid because there are just ${images.length} images`);
  }
  return /* @__PURE__ */ React.createElement(Modal, __spreadProps$b(__spreadValues$i({
    backdropConfig: newBackdropConfig,
    closeIcon: /* @__PURE__ */ React.createElement(CrossCircleSVG, null),
    onClose: closeHandler,
    color: "transparent"
  }, rest), {
    opened: show
  }), /* @__PURE__ */ React.createElement(LightboxImageStyled, {
    className: `LightboxImage ${className}`
  }, /* @__PURE__ */ React.createElement("div", {
    className: "LightboxImage__controls"
  }, /* @__PURE__ */ React.createElement(Clickable, {
    onClick: previousHandler
  }, /* @__PURE__ */ React.createElement(ArrowLeftSVG, {
    className: "LightboxImage__icon",
    viewBox: "0 0 6 13"
  })), /* @__PURE__ */ React.createElement(Clickable, {
    onClick: nextHandler
  }, /* @__PURE__ */ React.createElement(ArrowRightSVG, {
    className: "LightboxImage__icon",
    viewBox: "0 0 6 13"
  }))), /* @__PURE__ */ React.createElement(Image$1, __spreadProps$b(__spreadValues$i({
    className: "LightboxImage__image"
  }, imageConfig), {
    image: currentImage
  }))));
};
LightboxImage.defaultProps = {
  className: "",
  onClose: defaultFunction
};

const InfiniteScrollStyled = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  .InfiniteScroll {
    &__anchor {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
    }
  }
`;

const defaultObserverConfig = {
  rootMargin: "0px",
  threshold: 1
};

const InfiniteScroll = (props) => {
  const { children, className, onEndReached = defaultFunction } = props;
  const { observerConfig = defaultObserverConfig, disconnected } = props;
  const anchorRef = useRef(null);
  const resolved = useRef(true);
  const callback = useCallback(async (entries, _) => {
    for (const entry of entries) {
      const { isIntersecting } = entry;
      if (!isIntersecting || !resolved.current)
        break;
      resolved.current = false;
      await onEndReached();
      resolved.current = true;
    }
  }, [onEndReached]);
  useIsomorphicLayoutEffect(() => {
    const observer = new IntersectionObserver(callback, observerConfig);
    const target = anchorRef.current;
    if (!target)
      return;
    if (disconnected)
      return () => observer.unobserve(target);
    observer.observe(target);
    return () => observer.unobserve(target);
  }, [children, callback, observerConfig, disconnected]);
  return /* @__PURE__ */ React.createElement(InfiniteScrollStyled, {
    className: `InfiniteScroll ${className}`
  }, children, /* @__PURE__ */ React.createElement("div", {
    className: "InfiniteScroll__anchor",
    ref: anchorRef
  }));
};
InfiniteScroll.defaultProps = {
  className: "",
  onEndReached: defaultFunction,
  disconnected: false
};

const MaxWidthStyled = styled.div`
  --max: min(
    ${(props) => props.maxSize + props.minPadding * 2}px,
    ${getScreenMaxWidth}
  );

  margin: 0 auto;
  max-width: var(--max);
  min-width: min(var(--max), ${(props) => props.stretch ? 100 : 0}%);
  position: relative;
  display: inherit;

  .MaxWidth {
    &__inner {
      padding: 0 ${(props) => props.minPadding}px;
      min-width: ${(props) => props.stretch ? 100 : 0}%;
      position: relative;
      display: inherit;
    }
  }
`;

const MaxWidth = (props) => {
  const { className, size = 1184, children, minPadding = 16 } = props;
  const { stretch = true } = props;
  return /* @__PURE__ */ React.createElement(MaxWidthStyled, {
    className: `MaxWidth ${className}`,
    maxSize: size,
    minPadding,
    stretch
  }, /* @__PURE__ */ React.createElement("div", {
    className: "MaxWidth__inner"
  }, children));
};
MaxWidth.defaultProps = {
  className: "",
  size: 1184,
  minPadding: 16,
  stretch: true
};

const NotificationsContainerStyled = styled.div`
  position: fixed;
  // TODO: Change to a portal
  z-index: 9999;
  pointer-events: none;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  .NotificationsContainer {
    &__container {
      position: absolute;
      pointer-events: none;

      & > *:not(:last-child) {
        margin-bottom: 20px;
      }

      &--top-left {
        left: ${(props) => {
  var _a;
  return (_a = props.left) != null ? _a : 20;
}}px;
        top: ${(props) => {
  var _a;
  return (_a = props.top) != null ? _a : 20;
}}px;
      }

      &--top-right {
        right: ${(props) => {
  var _a;
  return (_a = props.right) != null ? _a : 20;
}}px;
        top: ${(props) => {
  var _a;
  return (_a = props.top) != null ? _a : 20;
}}px;
      }

      &--top-center {
        top: ${(props) => {
  var _a;
  return (_a = props.top) != null ? _a : 20;
}}px;
        left: 50%;
        transform: translateX(-50%);
      }

      &--bottom-left {
        left: ${(props) => {
  var _a;
  return (_a = props.left) != null ? _a : 20;
}}px;
        bottom: ${(props) => {
  var _a;
  return (_a = props.bottom) != null ? _a : 20;
}}px;
      }

      &--bottom-right {
        right: ${(props) => {
  var _a;
  return (_a = props.right) != null ? _a : 20;
}}px;
        bottom: ${(props) => {
  var _a;
  return (_a = props.bottom) != null ? _a : 20;
}}px;
      }

      &--bottom-center {
        bottom: ${(props) => {
  var _a;
  return (_a = props.bottom) != null ? _a : 20;
}}px;
        left: 50%;
        transform: translateX(-50%);
      }
    }

    &__item {
      position: relative;
      transition: 0.2s;
      pointer-events: all;
    }
  }
`;

class Notification {
  constructor() {
    this.initialized = false;
  }
  register(methods) {
    if (this.initialized) {
      console.error(`Notifications Container initializer has been called more than once.
        You must ensure this component will not re-render otherwise unexpected
        behavior may happen.
        `);
      return;
    }
    this.initialized = true;
    const { create, destroy, dismiss } = methods;
    this.create = create;
    this.destroy = destroy;
    this.dismiss = dismiss;
  }
}
var Notification$1 = new Notification();

var __defProp$h = Object.defineProperty;
var __defProps$a = Object.defineProperties;
var __getOwnPropDescs$a = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$h = Object.getOwnPropertySymbols;
var __hasOwnProp$h = Object.prototype.hasOwnProperty;
var __propIsEnum$h = Object.prototype.propertyIsEnumerable;
var __defNormalProp$h = (obj, key, value) => key in obj ? __defProp$h(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$h = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$h.call(b, prop))
      __defNormalProp$h(a, prop, b[prop]);
  if (__getOwnPropSymbols$h)
    for (var prop of __getOwnPropSymbols$h(b)) {
      if (__propIsEnum$h.call(b, prop))
        __defNormalProp$h(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$a = (a, b) => __defProps$a(a, __getOwnPropDescs$a(b));
const initialState$1 = {
  "bottom-center": [],
  "bottom-left": [],
  "bottom-right": [],
  "top-center": [],
  "top-left": [],
  "top-right": []
};
const notificationsContainerReducer = (state, action) => {
  switch (action.type) {
    case "ADD":
      return addNodeAction(state, action.payload);
    case "REMOVE":
      return removedNodeAction(state, action.payload);
    case "RESET":
      return __spreadValues$h({}, initialState$1);
    default:
      throw new Error(`Action ${action.type} is not a recognized NotificationsContainer
        action`);
  }
};
const addNodeAction = (state, payload) => {
  const { position, method } = payload;
  const container = state[position];
  if (!container) {
    console.warn(`You are attempting to access a container ${position} that doesn't exist.`);
    return state;
  }
  if (method === "start") {
    return __spreadProps$a(__spreadValues$h({}, state), { [position]: [__spreadValues$h({}, payload), ...container] });
  } else {
    return __spreadProps$a(__spreadValues$h({}, state), { [position]: [...container, __spreadValues$h({}, payload)] });
  }
};
const removedNodeAction = (state, payload) => {
  const { id, position } = payload;
  const container = state[position];
  const nodes = container.filter((item) => item.id !== id);
  return __spreadProps$a(__spreadValues$h({}, state), { [position]: nodes });
};

var __defProp$g = Object.defineProperty;
var __defProps$9 = Object.defineProperties;
var __getOwnPropDescs$9 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$g = Object.getOwnPropertySymbols;
var __hasOwnProp$g = Object.prototype.hasOwnProperty;
var __propIsEnum$g = Object.prototype.propertyIsEnumerable;
var __defNormalProp$g = (obj, key, value) => key in obj ? __defProp$g(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$g = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$g.call(b, prop))
      __defNormalProp$g(a, prop, b[prop]);
  if (__getOwnPropSymbols$g)
    for (var prop of __getOwnPropSymbols$g(b)) {
      if (__propIsEnum$g.call(b, prop))
        __defNormalProp$g(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$9 = (a, b) => __defProps$9(a, __getOwnPropDescs$9(b));
const NotificationsContainer = (props) => {
  const { top, left, right, bottom } = props;
  const notificationCount = useRef(0);
  const [state, dispatch] = useReducer(notificationsContainerReducer, initialState$1);
  const timersRefs = useRef({});
  const spawnNotification = (options) => {
    const { id = (++notificationCount.current).toString() } = options;
    dispatch({
      type: "ADD",
      payload: __spreadProps$9(__spreadValues$g({}, options), { id })
    });
  };
  const destroyNotification = () => {
    dispatch({
      type: "RESET"
    });
  };
  const dismissNotification = (id, position) => {
    dispatch({
      type: "REMOVE",
      payload: {
        id,
        position
      }
    });
  };
  const renderNotifications = (position) => {
    return state[position].map((notification, index) => {
      const { node, dismissOnClick = true, id, position: position2 } = notification;
      const { onClick, duration = 5e3 } = notification;
      if (!isValidElement(node))
        return null;
      let enhancedNode = node;
      const currentTimer = timersRefs.current[`${position2}-${id}`];
      if (duration && duration > 0 && !currentTimer) {
        const timer = setTimeout(() => {
          Notification$1.dismiss(id, position2);
          clearTimeout(timersRefs.current[`${position2}-${id}`]);
          delete timersRefs.current[`${position2}-${id}`];
        }, duration);
        timersRefs.current[`${position2}-${id}`] = timer;
      }
      const clickHandler = (e) => {
        onClick == null ? void 0 : onClick(e);
        if (!dismissOnClick)
          return;
        Notification$1.dismiss(id, position2);
      };
      if (typeof node.type !== "string") {
        enhancedNode = cloneElement(node, {
          dismiss: () => Notification$1.dismiss(id, position2)
        });
      }
      return /* @__PURE__ */ React.createElement("div", {
        key: index,
        className: "NotificationsContainer__item",
        onClick: clickHandler
      }, enhancedNode);
    });
  };
  useIsomorphicLayoutEffect(() => {
    Notification$1.register({
      create: spawnNotification,
      destroy: destroyNotification,
      dismiss: dismissNotification
    });
  }, []);
  return /* @__PURE__ */ React.createElement(NotificationsContainerStyled, {
    className: "NotificationsContainer",
    top,
    left,
    right,
    bottom
  }, /* @__PURE__ */ React.createElement("div", {
    className: "NotificationsContainer__container NotificationsContainer__container--top-left"
  }, renderNotifications("top-left")), /* @__PURE__ */ React.createElement("div", {
    className: "NotificationsContainer__container NotificationsContainer__container--top-right"
  }, renderNotifications("top-right")), /* @__PURE__ */ React.createElement("div", {
    className: "NotificationsContainer__container NotificationsContainer__container--top-center"
  }, renderNotifications("top-center")), /* @__PURE__ */ React.createElement("div", {
    className: "NotificationsContainer__container NotificationsContainer__container--bottom-left"
  }, renderNotifications("bottom-left")), /* @__PURE__ */ React.createElement("div", {
    className: "NotificationsContainer__container NotificationsContainer__container--bottom-right"
  }, renderNotifications("bottom-right")), /* @__PURE__ */ React.createElement("div", {
    className: "NotificationsContainer__container NotificationsContainer__container--bottom-center"
  }, renderNotifications("bottom-center")));
};
NotificationsContainer.defaultProps = {};
var NotificationsContainer$1 = memo(NotificationsContainer);

var version = 4;
var country_calling_codes = {
	"1": [
		"US",
		"AG",
		"AI",
		"AS",
		"BB",
		"BM",
		"BS",
		"CA",
		"DM",
		"DO",
		"GD",
		"GU",
		"JM",
		"KN",
		"KY",
		"LC",
		"MP",
		"MS",
		"PR",
		"SX",
		"TC",
		"TT",
		"VC",
		"VG",
		"VI"
	],
	"7": [
		"RU",
		"KZ"
	],
	"20": [
		"EG"
	],
	"27": [
		"ZA"
	],
	"30": [
		"GR"
	],
	"31": [
		"NL"
	],
	"32": [
		"BE"
	],
	"33": [
		"FR"
	],
	"34": [
		"ES"
	],
	"36": [
		"HU"
	],
	"39": [
		"IT",
		"VA"
	],
	"40": [
		"RO"
	],
	"41": [
		"CH"
	],
	"43": [
		"AT"
	],
	"44": [
		"GB",
		"GG",
		"IM",
		"JE"
	],
	"45": [
		"DK"
	],
	"46": [
		"SE"
	],
	"47": [
		"NO",
		"SJ"
	],
	"48": [
		"PL"
	],
	"49": [
		"DE"
	],
	"51": [
		"PE"
	],
	"52": [
		"MX"
	],
	"53": [
		"CU"
	],
	"54": [
		"AR"
	],
	"55": [
		"BR"
	],
	"56": [
		"CL"
	],
	"57": [
		"CO"
	],
	"58": [
		"VE"
	],
	"60": [
		"MY"
	],
	"61": [
		"AU",
		"CC",
		"CX"
	],
	"62": [
		"ID"
	],
	"63": [
		"PH"
	],
	"64": [
		"NZ"
	],
	"65": [
		"SG"
	],
	"66": [
		"TH"
	],
	"81": [
		"JP"
	],
	"82": [
		"KR"
	],
	"84": [
		"VN"
	],
	"86": [
		"CN"
	],
	"90": [
		"TR"
	],
	"91": [
		"IN"
	],
	"92": [
		"PK"
	],
	"93": [
		"AF"
	],
	"94": [
		"LK"
	],
	"95": [
		"MM"
	],
	"98": [
		"IR"
	],
	"211": [
		"SS"
	],
	"212": [
		"MA",
		"EH"
	],
	"213": [
		"DZ"
	],
	"216": [
		"TN"
	],
	"218": [
		"LY"
	],
	"220": [
		"GM"
	],
	"221": [
		"SN"
	],
	"222": [
		"MR"
	],
	"223": [
		"ML"
	],
	"224": [
		"GN"
	],
	"225": [
		"CI"
	],
	"226": [
		"BF"
	],
	"227": [
		"NE"
	],
	"228": [
		"TG"
	],
	"229": [
		"BJ"
	],
	"230": [
		"MU"
	],
	"231": [
		"LR"
	],
	"232": [
		"SL"
	],
	"233": [
		"GH"
	],
	"234": [
		"NG"
	],
	"235": [
		"TD"
	],
	"236": [
		"CF"
	],
	"237": [
		"CM"
	],
	"238": [
		"CV"
	],
	"239": [
		"ST"
	],
	"240": [
		"GQ"
	],
	"241": [
		"GA"
	],
	"242": [
		"CG"
	],
	"243": [
		"CD"
	],
	"244": [
		"AO"
	],
	"245": [
		"GW"
	],
	"246": [
		"IO"
	],
	"247": [
		"AC"
	],
	"248": [
		"SC"
	],
	"249": [
		"SD"
	],
	"250": [
		"RW"
	],
	"251": [
		"ET"
	],
	"252": [
		"SO"
	],
	"253": [
		"DJ"
	],
	"254": [
		"KE"
	],
	"255": [
		"TZ"
	],
	"256": [
		"UG"
	],
	"257": [
		"BI"
	],
	"258": [
		"MZ"
	],
	"260": [
		"ZM"
	],
	"261": [
		"MG"
	],
	"262": [
		"RE",
		"YT"
	],
	"263": [
		"ZW"
	],
	"264": [
		"NA"
	],
	"265": [
		"MW"
	],
	"266": [
		"LS"
	],
	"267": [
		"BW"
	],
	"268": [
		"SZ"
	],
	"269": [
		"KM"
	],
	"290": [
		"SH",
		"TA"
	],
	"291": [
		"ER"
	],
	"297": [
		"AW"
	],
	"298": [
		"FO"
	],
	"299": [
		"GL"
	],
	"350": [
		"GI"
	],
	"351": [
		"PT"
	],
	"352": [
		"LU"
	],
	"353": [
		"IE"
	],
	"354": [
		"IS"
	],
	"355": [
		"AL"
	],
	"356": [
		"MT"
	],
	"357": [
		"CY"
	],
	"358": [
		"FI",
		"AX"
	],
	"359": [
		"BG"
	],
	"370": [
		"LT"
	],
	"371": [
		"LV"
	],
	"372": [
		"EE"
	],
	"373": [
		"MD"
	],
	"374": [
		"AM"
	],
	"375": [
		"BY"
	],
	"376": [
		"AD"
	],
	"377": [
		"MC"
	],
	"378": [
		"SM"
	],
	"380": [
		"UA"
	],
	"381": [
		"RS"
	],
	"382": [
		"ME"
	],
	"383": [
		"XK"
	],
	"385": [
		"HR"
	],
	"386": [
		"SI"
	],
	"387": [
		"BA"
	],
	"389": [
		"MK"
	],
	"420": [
		"CZ"
	],
	"421": [
		"SK"
	],
	"423": [
		"LI"
	],
	"500": [
		"FK"
	],
	"501": [
		"BZ"
	],
	"502": [
		"GT"
	],
	"503": [
		"SV"
	],
	"504": [
		"HN"
	],
	"505": [
		"NI"
	],
	"506": [
		"CR"
	],
	"507": [
		"PA"
	],
	"508": [
		"PM"
	],
	"509": [
		"HT"
	],
	"590": [
		"GP",
		"BL",
		"MF"
	],
	"591": [
		"BO"
	],
	"592": [
		"GY"
	],
	"593": [
		"EC"
	],
	"594": [
		"GF"
	],
	"595": [
		"PY"
	],
	"596": [
		"MQ"
	],
	"597": [
		"SR"
	],
	"598": [
		"UY"
	],
	"599": [
		"CW",
		"BQ"
	],
	"670": [
		"TL"
	],
	"672": [
		"NF"
	],
	"673": [
		"BN"
	],
	"674": [
		"NR"
	],
	"675": [
		"PG"
	],
	"676": [
		"TO"
	],
	"677": [
		"SB"
	],
	"678": [
		"VU"
	],
	"679": [
		"FJ"
	],
	"680": [
		"PW"
	],
	"681": [
		"WF"
	],
	"682": [
		"CK"
	],
	"683": [
		"NU"
	],
	"685": [
		"WS"
	],
	"686": [
		"KI"
	],
	"687": [
		"NC"
	],
	"688": [
		"TV"
	],
	"689": [
		"PF"
	],
	"690": [
		"TK"
	],
	"691": [
		"FM"
	],
	"692": [
		"MH"
	],
	"850": [
		"KP"
	],
	"852": [
		"HK"
	],
	"853": [
		"MO"
	],
	"855": [
		"KH"
	],
	"856": [
		"LA"
	],
	"880": [
		"BD"
	],
	"886": [
		"TW"
	],
	"960": [
		"MV"
	],
	"961": [
		"LB"
	],
	"962": [
		"JO"
	],
	"963": [
		"SY"
	],
	"964": [
		"IQ"
	],
	"965": [
		"KW"
	],
	"966": [
		"SA"
	],
	"967": [
		"YE"
	],
	"968": [
		"OM"
	],
	"970": [
		"PS"
	],
	"971": [
		"AE"
	],
	"972": [
		"IL"
	],
	"973": [
		"BH"
	],
	"974": [
		"QA"
	],
	"975": [
		"BT"
	],
	"976": [
		"MN"
	],
	"977": [
		"NP"
	],
	"992": [
		"TJ"
	],
	"993": [
		"TM"
	],
	"994": [
		"AZ"
	],
	"995": [
		"GE"
	],
	"996": [
		"KG"
	],
	"998": [
		"UZ"
	]
};
var countries = {
	AC: [
		"247",
		"00",
		"(?:[01589]\\d|[46])\\d{4}",
		[
			5,
			6
		]
	],
	AD: [
		"376",
		"00",
		"(?:1|6\\d)\\d{7}|[135-9]\\d{5}",
		[
			6,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"[135-9]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			]
		]
	],
	AE: [
		"971",
		"00",
		"(?:[4-7]\\d|9[0-689])\\d{7}|800\\d{2,9}|[2-4679]\\d{7}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{2,9})",
				"$1 $2",
				[
					"60|8"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[236]|[479][2-8]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{5})",
				"$1 $2 $3",
				[
					"[479]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			]
		],
		"0"
	],
	AF: [
		"93",
		"00",
		"[2-7]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-7]"
				],
				"0$1"
			]
		],
		"0"
	],
	AG: [
		"1",
		"011",
		"(?:268|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([457]\\d{6})$",
		"268$1",
		0,
		"268"
	],
	AI: [
		"1",
		"011",
		"(?:264|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2457]\\d{6})$",
		"264$1",
		0,
		"264"
	],
	AL: [
		"355",
		"00",
		"(?:700\\d\\d|900)\\d{3}|8\\d{5,7}|(?:[2-5]|6\\d)\\d{7}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{3,4})",
				"$1 $2",
				[
					"80|9"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"4[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2358][2-5]|4"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[23578]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"6"
				],
				"0$1"
			]
		],
		"0"
	],
	AM: [
		"374",
		"00",
		"(?:[1-489]\\d|55|60|77)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[89]0"
				],
				"0 $1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"2|3[12]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"1|47"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[3-9]"
				],
				"0$1"
			]
		],
		"0"
	],
	AO: [
		"244",
		"00",
		"[29]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[29]"
				]
			]
		]
	],
	AR: [
		"54",
		"00",
		"(?:11|[89]\\d\\d)\\d{8}|[2368]\\d{9}",
		[
			10,
			11
		],
		[
			[
				"(\\d{4})(\\d{2})(\\d{4})",
				"$1 $2-$3",
				[
					"2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9])",
					"2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8]))|2(?:2[24-9]|3[1-59]|47)",
					"2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5[56][46]|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]",
					"2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|58|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|54(?:4|5[13-7]|6[89])|86[3-6]))|2(?:2[24-9]|3[1-59]|47)|38(?:[58][78]|7[378])|3(?:454|85[56])[46]|3(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]"
				],
				"0$1",
				1
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2-$3",
				[
					"1"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[68]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2-$3",
				[
					"[23]"
				],
				"0$1",
				1
			],
			[
				"(\\d)(\\d{4})(\\d{2})(\\d{4})",
				"$2 15-$3-$4",
				[
					"9(?:2[2-469]|3[3-578])",
					"9(?:2(?:2[024-9]|3[0-59]|47|6[245]|9[02-8])|3(?:3[28]|4[03-9]|5[2-46-8]|7[1-578]|8[2-9]))",
					"9(?:2(?:[23]02|6(?:[25]|4[6-8])|9(?:[02356]|4[02568]|72|8[23]))|3(?:3[28]|4(?:[04679]|3[5-8]|5[4-68]|8[2379])|5(?:[2467]|3[237]|8[2-5])|7[1-578]|8(?:[2469]|3[2578]|5[4-8]|7[36-8]|8[5-8])))|92(?:2[24-9]|3[1-59]|47)",
					"9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3[78]|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8[23])|7[1-578]|8(?:[2469]|3[278]|5(?:[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4[35][56]|58[45]|8(?:[38]5|54|76))[4-6]",
					"9(?:2(?:[23]02|6(?:[25]|4(?:64|[78]))|9(?:[02356]|4(?:[0268]|5[2-6])|72|8[23]))|3(?:3[28]|4(?:[04679]|3(?:5(?:4[0-25689]|[56])|[78])|5(?:4[46]|8)|8[2379])|5(?:[2467]|3[237]|8(?:[23]|4(?:[45]|60)|5(?:4[0-39]|5|64)))|7[1-578]|8(?:[2469]|3[278]|5(?:4(?:4|5[13-7]|6[89])|[56][46]|[78])|7[378]|8(?:6[3-6]|[78]))))|92(?:2[24-9]|3[1-59]|47)|93(?:4(?:36|5[56])|8(?:[38]5|76))[4-6]"
				],
				"0$1",
				0,
				"$1 $2 $3-$4"
			],
			[
				"(\\d)(\\d{2})(\\d{4})(\\d{4})",
				"$2 15-$3-$4",
				[
					"91"
				],
				"0$1",
				0,
				"$1 $2 $3-$4"
			],
			[
				"(\\d{3})(\\d{3})(\\d{5})",
				"$1-$2-$3",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{4})",
				"$2 15-$3-$4",
				[
					"9"
				],
				"0$1",
				0,
				"$1 $2 $3-$4"
			]
		],
		"0",
		0,
		"0?(?:(11|2(?:2(?:02?|[13]|2[13-79]|4[1-6]|5[2457]|6[124-8]|7[1-4]|8[13-6]|9[1267])|3(?:02?|1[467]|2[03-6]|3[13-8]|[49][2-6]|5[2-8]|[67])|4(?:7[3-578]|9)|6(?:[0136]|2[24-6]|4[6-8]?|5[15-8])|80|9(?:0[1-3]|[19]|2\\d|3[1-6]|4[02568]?|5[2-4]|6[2-46]|72?|8[23]?))|3(?:3(?:2[79]|6|8[2578])|4(?:0[0-24-9]|[12]|3[5-8]?|4[24-7]|5[4-68]?|6[02-9]|7[126]|8[2379]?|9[1-36-8])|5(?:1|2[1245]|3[237]?|4[1-46-9]|6[2-4]|7[1-6]|8[2-5]?)|6[24]|7(?:[069]|1[1568]|2[15]|3[145]|4[13]|5[14-8]|7[2-57]|8[126])|8(?:[01]|2[15-7]|3[2578]?|4[13-6]|5[4-8]?|6[1-357-9]|7[36-8]?|8[5-8]?|9[124])))15)?",
		"9$1"
	],
	AS: [
		"1",
		"011",
		"(?:[58]\\d\\d|684|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([267]\\d{6})$",
		"684$1",
		0,
		"684"
	],
	AT: [
		"43",
		"00",
		"1\\d{3,12}|2\\d{6,12}|43(?:(?:0\\d|5[02-9])\\d{3,9}|2\\d{4,5}|[3467]\\d{4}|8\\d{4,6}|9\\d{4,7})|5\\d{4,12}|8\\d{7,12}|9\\d{8,12}|(?:[367]\\d|4[0-24-9])\\d{4,11}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d)(\\d{3,12})",
				"$1 $2",
				[
					"1(?:11|[2-9])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})",
				"$1 $2",
				[
					"517"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3,5})",
				"$1 $2",
				[
					"5[079]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,10})",
				"$1 $2",
				[
					"(?:31|4)6|51|6(?:5[0-3579]|[6-9])|7(?:20|32|8)|[89]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3,9})",
				"$1 $2",
				[
					"[2-467]|5[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4,7})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			]
		],
		"0"
	],
	AU: [
		"61",
		"001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011",
		"1(?:[0-79]\\d{7}(?:\\d(?:\\d{2})?)?|8[0-24-9]\\d{7})|[2-478]\\d{8}|1\\d{4,7}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			12
		],
		[
			[
				"(\\d{2})(\\d{3,4})",
				"$1 $2",
				[
					"16"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,4})",
				"$1 $2 $3",
				[
					"16"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"14|4"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[2378]"
				],
				"(0$1)"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1(?:30|[89])"
				]
			]
		],
		"0",
		0,
		"0|(183[12])",
		0,
		0,
		0,
		[
			[
				"(?:(?:2(?:[0-26-9]\\d|3[0-8]|4[02-9]|5[0135-9])|3(?:[0-3589]\\d|4[0-578]|6[1-9]|7[0-35-9])|7(?:[013-57-9]\\d|2[0-8]))\\d{3}|8(?:51(?:0(?:0[03-9]|[12479]\\d|3[2-9]|5[0-8]|6[1-9]|8[0-7])|1(?:[0235689]\\d|1[0-69]|4[0-589]|7[0-47-9])|2(?:0[0-79]|[18][13579]|2[14-9]|3[0-46-9]|[4-6]\\d|7[89]|9[0-4]))|(?:6[0-8]|[78]\\d)\\d{3}|9(?:[02-9]\\d{3}|1(?:(?:[0-58]\\d|6[0135-9])\\d|7(?:0[0-24-9]|[1-9]\\d)|9(?:[0-46-9]\\d|5[0-79])))))\\d{3}",
				[
					9
				]
			],
			[
				"4(?:83[0-38]|93[0-6])\\d{5}|4(?:[0-3]\\d|4[047-9]|5[0-25-9]|6[06-9]|7[02-9]|8[0-24-9]|9[0-27-9])\\d{6}",
				[
					9
				]
			],
			[
				"180(?:0\\d{3}|2)\\d{3}",
				[
					7,
					10
				]
			],
			[
				"190[0-26]\\d{6}",
				[
					10
				]
			],
			0,
			0,
			0,
			[
				"163\\d{2,6}",
				[
					5,
					6,
					7,
					8,
					9
				]
			],
			[
				"14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}",
				[
					9
				]
			],
			[
				"13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}",
				[
					6,
					8,
					10,
					12
				]
			]
		],
		"0011"
	],
	AW: [
		"297",
		"00",
		"(?:[25-79]\\d\\d|800)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[25-9]"
				]
			]
		]
	],
	AX: [
		"358",
		"00|99(?:[01469]|5(?:[14]1|3[23]|5[59]|77|88|9[09]))",
		"2\\d{4,9}|35\\d{4,5}|(?:60\\d\\d|800)\\d{4,6}|7\\d{5,11}|(?:[14]\\d|3[0-46-9]|50)\\d{4,8}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		"18",
		0,
		"00"
	],
	AZ: [
		"994",
		"00",
		"365\\d{6}|(?:[124579]\\d|60|88)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"90"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1[28]|2|365|46",
					"1[28]|2|365[45]|46",
					"1[28]|2|365(?:4|5[02])|46"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[13-9]"
				],
				"0$1"
			]
		],
		"0"
	],
	BA: [
		"387",
		"00",
		"6\\d{8}|(?:[35689]\\d|49|70)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6[1-3]|[7-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2-$3",
				[
					"[3-5]|6[56]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"6"
				],
				"0$1"
			]
		],
		"0"
	],
	BB: [
		"1",
		"011",
		"(?:246|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-9]\\d{6})$",
		"246$1",
		0,
		"246"
	],
	BD: [
		"880",
		"00",
		"[1-469]\\d{9}|8[0-79]\\d{7,8}|[2-79]\\d{8}|[2-9]\\d{7}|[3-9]\\d{6}|[57-9]\\d{5}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{4,6})",
				"$1-$2",
				[
					"31[5-8]|[459]1"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,7})",
				"$1-$2",
				[
					"3(?:[67]|8[013-9])|4(?:6[168]|7|[89][18])|5(?:6[128]|9)|6(?:28|4[14]|5)|7[2-589]|8(?:0[014-9]|[12])|9[358]|(?:3[2-5]|4[235]|5[2-578]|6[0389]|76|8[3-7]|9[24])1|(?:44|66)[01346-9]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3,6})",
				"$1-$2",
				[
					"[13-9]|22"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{7,8})",
				"$1-$2",
				[
					"2"
				],
				"0$1"
			]
		],
		"0"
	],
	BE: [
		"32",
		"00",
		"4\\d{8}|[1-9]\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:80|9)0"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[239]|4[23]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[15-8]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"4"
				],
				"0$1"
			]
		],
		"0"
	],
	BF: [
		"226",
		"00",
		"[025-7]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[025-7]"
				]
			]
		]
	],
	BG: [
		"359",
		"00",
		"[2-7]\\d{6,7}|[89]\\d{6,8}|2\\d{5}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d)(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"43[1-6]|70[1-9]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[356]|4[124-7]|7[1-9]|8[1-6]|9[1-7]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:70|8)0"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})",
				"$1 $2 $3",
				[
					"43[1-7]|7"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[48]|9[08]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			]
		],
		"0"
	],
	BH: [
		"973",
		"00",
		"[136-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[13679]|8[047]"
				]
			]
		]
	],
	BI: [
		"257",
		"00",
		"(?:[267]\\d|31)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2367]"
				]
			]
		]
	],
	BJ: [
		"229",
		"00",
		"[25689]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[25689]"
				]
			]
		]
	],
	BL: [
		"590",
		"00",
		"(?:590|(?:69|80)\\d|976)\\d{6}",
		[
			9
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"590(?:2[7-9]|5[12]|87)\\d{4}"
			],
			[
				"69(?:0\\d\\d|1(?:2[2-9]|3[0-5]))\\d{4}"
			],
			[
				"80[0-5]\\d{6}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"976[01]\\d{5}"
			]
		]
	],
	BM: [
		"1",
		"011",
		"(?:441|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-8]\\d{6})$",
		"441$1",
		0,
		"441"
	],
	BN: [
		"673",
		"00",
		"[2-578]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-578]"
				]
			]
		]
	],
	BO: [
		"591",
		"00(?:1\\d)?",
		"(?:[2-467]\\d\\d|8001)\\d{5}",
		[
			8,
			9
		],
		[
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"[23]|4[46]"
				]
			],
			[
				"(\\d{8})",
				"$1",
				[
					"[67]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		"0",
		0,
		"0(1\\d)?"
	],
	BQ: [
		"599",
		"00",
		"(?:[34]1|7\\d)\\d{5}",
		[
			7
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"[347]"
	],
	BR: [
		"55",
		"00(?:1[245]|2[1-35]|31|4[13]|[56]5|99)",
		"(?:[1-46-9]\\d\\d|5(?:[0-46-9]\\d|5[0-24679]))\\d{8}|[1-9]\\d{9}|[3589]\\d{8}|[34]\\d{7}",
		[
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"300|4(?:0[02]|37)",
					"4(?:02|37)0|[34]00"
				]
			],
			[
				"(\\d{3})(\\d{2,3})(\\d{4})",
				"$1 $2 $3",
				[
					"(?:[358]|90)0"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2-$3",
				[
					"(?:[14689][1-9]|2[12478]|3[1-578]|5[13-5]|7[13-579])[2-57]"
				],
				"($1)"
			],
			[
				"(\\d{2})(\\d{5})(\\d{4})",
				"$1 $2-$3",
				[
					"[16][1-9]|[2-57-9]"
				],
				"($1)"
			]
		],
		"0",
		0,
		"(?:0|90)(?:(1[245]|2[1-35]|31|4[13]|[56]5|99)(\\d{10,11}))?",
		"$2"
	],
	BS: [
		"1",
		"011",
		"(?:242|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([3-8]\\d{6})$",
		"242$1",
		0,
		"242"
	],
	BT: [
		"975",
		"00",
		"[17]\\d{7}|[2-8]\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-68]|7[246]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1[67]|7"
				]
			]
		]
	],
	BW: [
		"267",
		"00",
		"(?:0800|(?:[37]|800)\\d)\\d{6}|(?:[2-6]\\d|90)\\d{5}",
		[
			7,
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"90"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[24-6]|3[15-79]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[37]"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		]
	],
	BY: [
		"375",
		"810",
		"(?:[12]\\d|33|44|902)\\d{7}|8(?:0[0-79]\\d{5,7}|[1-7]\\d{9})|8(?:1[0-489]|[5-79]\\d)\\d{7}|8[1-79]\\d{6,7}|8[0-79]\\d{5}|8\\d{5}",
		[
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"800"
				],
				"8 $1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,4})",
				"$1 $2 $3",
				[
					"800"
				],
				"8 $1"
			],
			[
				"(\\d{4})(\\d{2})(\\d{3})",
				"$1 $2-$3",
				[
					"1(?:5[169]|6[3-5]|7[179])|2(?:1[35]|2[34]|3[3-5])",
					"1(?:5[169]|6(?:3[1-3]|4|5[125])|7(?:1[3-9]|7[0-24-6]|9[2-7]))|2(?:1[35]|2[34]|3[3-5])"
				],
				"8 0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"1(?:[56]|7[467])|2[1-3]"
				],
				"8 0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[1-4]"
				],
				"8 0$1"
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"8 $1"
			]
		],
		"8",
		0,
		"0|80?",
		0,
		0,
		0,
		0,
		"8~10"
	],
	BZ: [
		"501",
		"00",
		"(?:0800\\d|[2-8])\\d{6}",
		[
			7,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[2-8]"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{4})(\\d{3})",
				"$1-$2-$3-$4",
				[
					"0"
				]
			]
		]
	],
	CA: [
		"1",
		"011",
		"(?:[2-8]\\d|90)\\d{8}",
		[
			10
		],
		0,
		"1",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:2(?:04|[23]6|[48]9|50)|3(?:06|43|6[578])|4(?:03|1[68]|3[178]|50|74)|5(?:06|1[49]|48|79|8[17])|6(?:04|13|39|47|72)|7(?:0[59]|78|8[02])|8(?:[06]7|19|25|73)|90[25])[2-9]\\d{6}"
			],
			[
				""
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|(?:5(?:00|2[12]|33|44|66|77|88)|622)[2-9]\\d{6}"
			],
			0,
			0,
			0,
			[
				"600[2-9]\\d{6}"
			]
		]
	],
	CC: [
		"61",
		"001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011",
		"1(?:[0-79]\\d{8}(?:\\d{2})?|8[0-24-9]\\d{7})|[148]\\d{8}|1\\d{5,7}",
		[
			6,
			7,
			8,
			9,
			10,
			12
		],
		0,
		"0",
		0,
		"0|([59]\\d{7})$",
		"8$1",
		0,
		0,
		[
			[
				"8(?:51(?:0(?:02|31|60|89)|1(?:18|76)|223)|91(?:0(?:1[0-2]|29)|1(?:[28]2|50|79)|2(?:10|64)|3(?:[06]8|22)|4[29]8|62\\d|70[23]|959))\\d{3}",
				[
					9
				]
			],
			[
				"4(?:83[0-38]|93[0-6])\\d{5}|4(?:[0-3]\\d|4[047-9]|5[0-25-9]|6[06-9]|7[02-9]|8[0-24-9]|9[0-27-9])\\d{6}",
				[
					9
				]
			],
			[
				"180(?:0\\d{3}|2)\\d{3}",
				[
					7,
					10
				]
			],
			[
				"190[0-26]\\d{6}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			[
				"14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}",
				[
					9
				]
			],
			[
				"13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}",
				[
					6,
					8,
					10,
					12
				]
			]
		],
		"0011"
	],
	CD: [
		"243",
		"00",
		"[189]\\d{8}|[1-68]\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"88"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"[1-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			]
		],
		"0"
	],
	CF: [
		"236",
		"00",
		"(?:[27]\\d{3}|8776)\\d{4}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[278]"
				]
			]
		]
	],
	CG: [
		"242",
		"00",
		"222\\d{6}|(?:0\\d|80)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[02]"
				]
			]
		]
	],
	CH: [
		"41",
		"00",
		"8\\d{11}|[2-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8[047]|90"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-79]|81"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	CI: [
		"225",
		"00",
		"[02]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d)(\\d{5})",
				"$1 $2 $3 $4",
				[
					"2"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"0"
				]
			]
		]
	],
	CK: [
		"682",
		"00",
		"[2-578]\\d{4}",
		[
			5
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1 $2",
				[
					"[2-578]"
				]
			]
		]
	],
	CL: [
		"56",
		"(?:0|1(?:1[0-69]|2[02-5]|5[13-58]|69|7[0167]|8[018]))0",
		"12300\\d{6}|6\\d{9,10}|[2-9]\\d{8}",
		[
			9,
			10,
			11
		],
		[
			[
				"(\\d{5})(\\d{4})",
				"$1 $2",
				[
					"219",
					"2196"
				],
				"($1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"44"
				]
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2[1-3]"
				],
				"($1)"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"9[2-9]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"3[2-5]|[47]|5[1-3578]|6[13-57]|8(?:0[1-9]|[1-9])"
				],
				"($1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"60|8"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"60"
				]
			]
		]
	],
	CM: [
		"237",
		"00",
		"[26]\\d{8}|88\\d{6,7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"88"
				]
			],
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"[26]|88"
				]
			]
		]
	],
	CN: [
		"86",
		"00|1(?:[12]\\d|79)\\d\\d00",
		"1[127]\\d{8,9}|2\\d{9}(?:\\d{2})?|[12]\\d{6,7}|86\\d{6}|(?:1[03-689]\\d|6)\\d{7,9}|(?:[3-579]\\d|8[0-57-9])\\d{6,9}",
		[
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{5,6})",
				"$1 $2",
				[
					"(?:10|2[0-57-9])[19]",
					"(?:10|2[0-57-9])(?:10|9[56])",
					"(?:10|2[0-57-9])(?:100|9[56])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"3(?:[157]|35|49|9[1-68])|4(?:[17]|2[179]|6[47-9]|8[23])|5(?:[1357]|2[37]|4[36]|6[1-46]|80)|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]|4[13]|5[1-5])|(?:4[35]|59|85)[1-9]",
					"(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[1-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))[19]",
					"85[23](?:10|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:10|9[56])",
					"85[23](?:100|95)|(?:3(?:[157]\\d|35|49|9[1-68])|4(?:[17]\\d|2[179]|[35][1-9]|6[47-9]|8[23])|5(?:[1357]\\d|2[37]|4[36]|6[1-46]|80|9[1-9])|6(?:3[1-5]|6[0238]|9[12])|7(?:01|[1579]\\d|2[248]|3[014-9]|4[3-6]|6[023689])|8(?:1[236-8]|2[5-7]|[37]\\d|5[14-9]|8[36-8]|9[1-8])|9(?:0[1-3689]|1[1-79]|[379]\\d|4[13]|5[1-5]))(?:100|9[56])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"(?:4|80)0"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"10|2(?:[02-57-9]|1[1-9])",
					"10|2(?:[02-57-9]|1[1-9])",
					"10[0-79]|2(?:[02-57-9]|1[1-79])|(?:10|21)8(?:0[1-9]|[1-9])"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"3(?:[3-59]|7[02-68])|4(?:[26-8]|3[3-9]|5[2-9])|5(?:3[03-9]|[468]|7[028]|9[2-46-9])|6|7(?:[0-247]|3[04-9]|5[0-4689]|6[2368])|8(?:[1-358]|9[1-7])|9(?:[013479]|5[1-5])|(?:[34]1|55|79|87)[02-9]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{7,8})",
				"$1 $2",
				[
					"9"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"80"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[3-578]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"1[3-9]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"[12]"
				],
				"0$1",
				1
			]
		],
		"0",
		0,
		"0|(1(?:[12]\\d|79)\\d\\d)",
		0,
		0,
		0,
		0,
		"00"
	],
	CO: [
		"57",
		"00(?:4(?:[14]4|56)|[579])",
		"(?:(?:1\\d|[36])\\d{3}|9101)\\d{6}|[124-8]\\d{7}",
		[
			8,
			10,
			11
		],
		[
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"[146][2-9]|[2578]"
				],
				"($1)"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1 $2",
				[
					"6"
				],
				"($1)"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1 $2",
				[
					"[39]"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{7})",
				"$1-$2-$3",
				[
					"1"
				],
				"0$1",
				0,
				"$1 $2 $3"
			]
		],
		"0",
		0,
		"0([3579]|4(?:[14]4|56))?"
	],
	CR: [
		"506",
		"00",
		"(?:8\\d|90)\\d{8}|(?:[24-8]\\d{3}|3005)\\d{4}",
		[
			8,
			10
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2-7]|8[3-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[89]"
				]
			]
		],
		0,
		0,
		"(19(?:0[0-2468]|1[09]|20|66|77|99))"
	],
	CU: [
		"53",
		"119",
		"[27]\\d{6,7}|[34]\\d{5,7}|(?:5|8\\d\\d)\\d{7}",
		[
			6,
			7,
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{4,6})",
				"$1 $2",
				[
					"2[1-4]|[34]"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{6,7})",
				"$1 $2",
				[
					"7"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"5"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1 $2",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	CV: [
		"238",
		"0",
		"(?:[2-59]\\d\\d|800)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[2-589]"
				]
			]
		]
	],
	CW: [
		"599",
		"00",
		"(?:[34]1|60|(?:7|9\\d)\\d)\\d{5}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[3467]"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"9[4-8]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		"[69]"
	],
	CX: [
		"61",
		"001[14-689]|14(?:1[14]|34|4[17]|[56]6|7[47]|88)0011",
		"1(?:[0-79]\\d{8}(?:\\d{2})?|8[0-24-9]\\d{7})|[148]\\d{8}|1\\d{5,7}",
		[
			6,
			7,
			8,
			9,
			10,
			12
		],
		0,
		"0",
		0,
		"0|([59]\\d{7})$",
		"8$1",
		0,
		0,
		[
			[
				"8(?:51(?:0(?:01|30|59|88)|1(?:17|46|75)|2(?:22|35))|91(?:00[6-9]|1(?:[28]1|49|78)|2(?:09|63)|3(?:12|26|75)|4(?:56|97)|64\\d|7(?:0[01]|1[0-2])|958))\\d{3}",
				[
					9
				]
			],
			[
				"4(?:83[0-38]|93[0-6])\\d{5}|4(?:[0-3]\\d|4[047-9]|5[0-25-9]|6[06-9]|7[02-9]|8[0-24-9]|9[0-27-9])\\d{6}",
				[
					9
				]
			],
			[
				"180(?:0\\d{3}|2)\\d{3}",
				[
					7,
					10
				]
			],
			[
				"190[0-26]\\d{6}",
				[
					10
				]
			],
			0,
			0,
			0,
			0,
			[
				"14(?:5(?:1[0458]|[23][458])|71\\d)\\d{4}",
				[
					9
				]
			],
			[
				"13(?:00\\d{6}(?:\\d{2})?|45[0-4]\\d{3})|13\\d{4}",
				[
					6,
					8,
					10,
					12
				]
			]
		],
		"0011"
	],
	CY: [
		"357",
		"00",
		"(?:[279]\\d|[58]0)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[257-9]"
				]
			]
		]
	],
	CZ: [
		"420",
		"00",
		"(?:[2-578]\\d|60)\\d{7}|9\\d{8,11}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-8]|9[015-7]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"96"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"9"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"9"
				]
			]
		]
	],
	DE: [
		"49",
		"00",
		"[2579]\\d{5,14}|49(?:[34]0|69|8\\d)\\d\\d?|49(?:37|49|60|7[089]|9\\d)\\d{1,3}|49(?:1\\d|2[02-9]|3[2-689]|7[1-7])\\d{1,8}|(?:1|[368]\\d|4[0-8])\\d{3,13}|49(?:[05]\\d|[23]1|[46][1-8])\\d{1,9}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12,
			13,
			14,
			15
		],
		[
			[
				"(\\d{2})(\\d{3,13})",
				"$1 $2",
				[
					"3[02]|40|[68]9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,12})",
				"$1 $2",
				[
					"2(?:0[1-389]|1[124]|2[18]|3[14])|3(?:[35-9][15]|4[015])|906|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1",
					"2(?:0[1-389]|12[0-8])|3(?:[35-9][15]|4[015])|906|2(?:[13][14]|2[18])|(?:2[4-9]|4[2-9]|[579][1-9]|[68][1-8])1"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{2,11})",
				"$1 $2",
				[
					"[24-6]|3(?:[3569][02-46-9]|4[2-4679]|7[2-467]|8[2-46-8])|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]",
					"[24-6]|3(?:3(?:0[1-467]|2[127-9]|3[124578]|7[1257-9]|8[1256]|9[145])|4(?:2[135]|4[13578]|9[1346])|5(?:0[14]|2[1-3589]|6[1-4]|7[13468]|8[13568])|6(?:2[1-489]|3[124-6]|6[13]|7[12579]|8[1-356]|9[135])|7(?:2[1-7]|4[145]|6[1-5]|7[1-4])|8(?:21|3[1468]|6|7[1467]|8[136])|9(?:0[12479]|2[1358]|4[134679]|6[1-9]|7[136]|8[147]|9[1468]))|70[2-8]|8(?:0[2-9]|[1-8])|90[7-9]|[79][1-9]|3[68]4[1347]|3(?:47|60)[1356]|3(?:3[46]|46|5[49])[1246]|3[4579]3[1357]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"138"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{2,10})",
				"$1 $2",
				[
					"3"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5,11})",
				"$1 $2",
				[
					"181"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{4,10})",
				"$1 $2 $3",
				[
					"1(?:3|80)|9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7,8})",
				"$1 $2",
				[
					"1[67]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7,12})",
				"$1 $2",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{6})",
				"$1 $2",
				[
					"185",
					"1850",
					"18500"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{7})",
				"$1 $2",
				[
					"18[68]"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{6})",
				"$1 $2",
				[
					"15[0568]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{7})",
				"$1 $2",
				[
					"15[1279]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{8})",
				"$1 $2",
				[
					"18"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{7,8})",
				"$1 $2 $3",
				[
					"1(?:6[023]|7)"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{2})(\\d{7})",
				"$1 $2 $3",
				[
					"15[279]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{8})",
				"$1 $2 $3",
				[
					"15"
				],
				"0$1"
			]
		],
		"0"
	],
	DJ: [
		"253",
		"00",
		"(?:2\\d|77)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[27]"
				]
			]
		]
	],
	DK: [
		"45",
		"00",
		"[2-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-9]"
				]
			]
		]
	],
	DM: [
		"1",
		"011",
		"(?:[58]\\d\\d|767|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-7]\\d{6})$",
		"767$1",
		0,
		"767"
	],
	DO: [
		"1",
		"011",
		"(?:[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		0,
		0,
		0,
		"8001|8[024]9"
	],
	DZ: [
		"213",
		"00",
		"(?:[1-4]|[5-79]\\d|80)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[1-4]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-8]"
				],
				"0$1"
			]
		],
		"0"
	],
	EC: [
		"593",
		"00",
		"1\\d{9,10}|(?:[2-7]|9\\d)\\d{7}",
		[
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2-$3",
				[
					"[2-7]"
				],
				"(0$1)",
				0,
				"$1-$2-$3"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		"0"
	],
	EE: [
		"372",
		"00",
		"8\\d{9}|[4578]\\d{7}|(?:[3-8]\\d|90)\\d{5}",
		[
			7,
			8,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[369]|4[3-8]|5(?:[0-2]|5[0-478]|6[45])|7[1-9]|88",
					"[369]|4[3-8]|5(?:[02]|1(?:[0-8]|95)|5[0-478]|6(?:4[0-4]|5[1-589]))|7[1-9]|88"
				]
			],
			[
				"(\\d{4})(\\d{3,4})",
				"$1 $2",
				[
					"[45]|8(?:00|[1-49])",
					"[45]|8(?:00[1-9]|[1-49])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		]
	],
	EG: [
		"20",
		"00",
		"[189]\\d{8,9}|[24-6]\\d{8}|[135]\\d{7}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{7,8})",
				"$1 $2",
				[
					"[23]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{6,7})",
				"$1 $2",
				[
					"1[35]|[4-6]|8[2468]|9[235-7]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[189]"
				],
				"0$1"
			]
		],
		"0"
	],
	EH: [
		"212",
		"00",
		"[5-8]\\d{8}",
		[
			9
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		"528[89]"
	],
	ER: [
		"291",
		"00",
		"[178]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[178]"
				],
				"0$1"
			]
		],
		"0"
	],
	ES: [
		"34",
		"00",
		"[5-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[89]00"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-9]"
				]
			]
		]
	],
	ET: [
		"251",
		"00",
		"(?:11|[2-59]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-59]"
				],
				"0$1"
			]
		],
		"0"
	],
	FI: [
		"358",
		"00|99(?:[01469]|5(?:[14]1|3[23]|5[59]|77|88|9[09]))",
		"[1-35689]\\d{4}|7\\d{10,11}|(?:[124-7]\\d|3[0-46-9])\\d{8}|[1-9]\\d{5,8}",
		[
			5,
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d)(\\d{4,9})",
				"$1 $2",
				[
					"[2568][1-8]|3(?:0[1-9]|[1-9])|9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3,7})",
				"$1 $2",
				[
					"[12]00|[368]|70[07-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4,8})",
				"$1 $2",
				[
					"[1245]|7[135]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{6,10})",
				"$1 $2",
				[
					"7"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		"1[03-79]|[2-9]",
		0,
		"00"
	],
	FJ: [
		"679",
		"0(?:0|52)",
		"45\\d{5}|(?:0800\\d|[235-9])\\d{6}",
		[
			7,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[235-9]|45"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"0"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"00"
	],
	FK: [
		"500",
		"00",
		"[2-7]\\d{4}",
		[
			5
		]
	],
	FM: [
		"691",
		"00",
		"(?:[39]\\d\\d|820)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[389]"
				]
			]
		]
	],
	FO: [
		"298",
		"00",
		"[2-9]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{6})",
				"$1",
				[
					"[2-9]"
				]
			]
		],
		0,
		0,
		"(10(?:01|[12]0|88))"
	],
	FR: [
		"33",
		"00",
		"[1-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0 $1"
			],
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"[1-79]"
				],
				"0$1"
			]
		],
		"0"
	],
	GA: [
		"241",
		"00",
		"(?:[067]\\d|11)\\d{6}|[2-7]\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-7]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"11|[67]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"0"
				]
			]
		],
		0,
		0,
		"0(11\\d{6}|60\\d{6}|61\\d{6}|6[256]\\d{6}|7[47]\\d{6}|76\\d{6})",
		"$1"
	],
	GB: [
		"44",
		"00",
		"[1-357-9]\\d{9}|[18]\\d{8}|8\\d{6}",
		[
			7,
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"800",
					"8001",
					"80011",
					"800111",
					"8001111"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"845",
					"8454",
					"84546",
					"845464"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"800"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{4,5})",
				"$1 $2",
				[
					"1(?:38|5[23]|69|76|94)",
					"1(?:(?:38|69)7|5(?:24|39)|768|946)",
					"1(?:3873|5(?:242|39[4-6])|(?:697|768)[347]|9467)"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5,6})",
				"$1 $2",
				[
					"1(?:[2-69][02-9]|[78])"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[25]|7(?:0|6[02-9])",
					"[25]|7(?:0|6(?:[03-9]|2[356]))"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1389]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"(?:1(?:1(?:3(?:[0-58]\\d\\d|73[0235])|4(?:[0-5]\\d\\d|69[7-9]|70[0359])|(?:5[0-26-9]|[78][0-49])\\d\\d|6(?:[0-4]\\d\\d|50[0-24-69]))|2(?:(?:0[024-9]|2[3-9]|3[3-79]|4[1-689]|[58][02-9]|6[0-47-9]|7[013-9]|9\\d)\\d\\d|1(?:[0-7]\\d\\d|8(?:[02]\\d|1[0-278])))|(?:3(?:0\\d|1[0-8]|[25][02-9]|3[02-579]|[468][0-46-9]|7[1-35-79]|9[2-578])|4(?:0[03-9]|[137]\\d|[28][02-57-9]|4[02-69]|5[0-8]|[69][0-79])|5(?:0[1-35-9]|[16]\\d|2[024-9]|3[015689]|4[02-9]|5[03-9]|7[0-35-9]|8[0-468]|9[0-57-9])|6(?:0[034689]|1\\d|2[0-35689]|[38][013-9]|4[1-467]|5[0-69]|6[13-9]|7[0-8]|9[0-24578])|7(?:0[0246-9]|2\\d|3[0236-8]|4[03-9]|5[0-46-9]|6[013-9]|7[0-35-9]|8[024-9]|9[02-9])|8(?:0[35-9]|2[1-57-9]|3[02-578]|4[0-578]|5[124-9]|6[2-69]|7\\d|8[02-9]|9[02569])|9(?:0[02-589]|[18]\\d|2[02-689]|3[1-57-9]|4[2-9]|5[0-579]|6[2-47-9]|7[0-24578]|9[2-57]))\\d\\d)|2(?:0[013478]|3[0189]|4[017]|8[0-46-9]|9[0-2])\\d{3})\\d{4}|1(?:2(?:0(?:46[1-4]|87[2-9])|545[1-79]|76(?:2\\d|3[1-8]|6[1-6])|9(?:7(?:2[0-4]|3[2-5])|8(?:2[2-8]|7[0-47-9]|8[3-5])))|3(?:6(?:38[2-5]|47[23])|8(?:47[04-9]|64[0157-9]))|4(?:044[1-7]|20(?:2[23]|8\\d)|6(?:0(?:30|5[2-57]|6[1-8]|7[2-8])|140)|8(?:052|87[1-3]))|5(?:2(?:4(?:3[2-79]|6\\d)|76\\d)|6(?:26[06-9]|686))|6(?:06(?:4\\d|7[4-79])|295[5-7]|35[34]\\d|47(?:24|61)|59(?:5[08]|6[67]|74)|9(?:55[0-4]|77[23]))|7(?:26(?:6[13-9]|7[0-7])|(?:442|688)\\d|50(?:2[0-3]|[3-68]2|76))|8(?:27[56]\\d|37(?:5[2-5]|8[239])|843[2-58])|9(?:0(?:0(?:6[1-8]|85)|52\\d)|3583|4(?:66[1-8]|9(?:2[01]|81))|63(?:23|3[1-4])|9561))\\d{3}",
				[
					9,
					10
				]
			],
			[
				"7(?:457[0-57-9]|700[01]|911[028])\\d{5}|7(?:[1-3]\\d\\d|4(?:[0-46-9]\\d|5[0-689])|5(?:0[0-8]|[13-9]\\d|2[0-35-9])|7(?:0[1-9]|[1-7]\\d|8[02-9]|9[0-689])|8(?:[014-9]\\d|[23][0-8])|9(?:[024-9]\\d|1[02-9]|3[0-689]))\\d{6}",
				[
					10
				]
			],
			[
				"80[08]\\d{7}|800\\d{6}|8001111"
			],
			[
				"(?:8(?:4[2-5]|7[0-3])|9(?:[01]\\d|8[2-49]))\\d{7}|845464\\d",
				[
					7,
					10
				]
			],
			[
				"70\\d{8}",
				[
					10
				]
			],
			0,
			[
				"(?:3[0347]|55)\\d{8}",
				[
					10
				]
			],
			[
				"76(?:464|652)\\d{5}|76(?:0[0-2]|2[356]|34|4[01347]|5[49]|6[0-369]|77|81|9[139])\\d{6}",
				[
					10
				]
			],
			[
				"56\\d{8}",
				[
					10
				]
			]
		],
		0,
		" x"
	],
	GD: [
		"1",
		"011",
		"(?:473|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-9]\\d{6})$",
		"473$1",
		0,
		"473"
	],
	GE: [
		"995",
		"00",
		"(?:[3-57]\\d\\d|800)\\d{6}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"70"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"32"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[57]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[348]"
				],
				"0$1"
			]
		],
		"0"
	],
	GF: [
		"594",
		"00",
		"(?:[56]94|80\\d|976)\\d{6}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[569]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	GG: [
		"44",
		"00",
		"(?:1481|[357-9]\\d{3})\\d{6}|8\\d{6}(?:\\d{2})?",
		[
			7,
			9,
			10
		],
		0,
		"0",
		0,
		"0|([25-9]\\d{5})$",
		"1481$1",
		0,
		0,
		[
			[
				"1481[25-9]\\d{5}",
				[
					10
				]
			],
			[
				"7(?:(?:781|839)\\d|911[17])\\d{5}",
				[
					10
				]
			],
			[
				"80[08]\\d{7}|800\\d{6}|8001111"
			],
			[
				"(?:8(?:4[2-5]|7[0-3])|9(?:[01]\\d|8[0-3]))\\d{7}|845464\\d",
				[
					7,
					10
				]
			],
			[
				"70\\d{8}",
				[
					10
				]
			],
			0,
			[
				"(?:3[0347]|55)\\d{8}",
				[
					10
				]
			],
			[
				"76(?:464|652)\\d{5}|76(?:0[0-2]|2[356]|34|4[01347]|5[49]|6[0-369]|77|81|9[139])\\d{6}",
				[
					10
				]
			],
			[
				"56\\d{8}",
				[
					10
				]
			]
		]
	],
	GH: [
		"233",
		"00",
		"(?:[235]\\d{3}|800)\\d{5}",
		[
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[235]"
				],
				"0$1"
			]
		],
		"0"
	],
	GI: [
		"350",
		"00",
		"(?:[25]\\d\\d|606)\\d{5}",
		[
			8
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"2"
				]
			]
		]
	],
	GL: [
		"299",
		"00",
		"(?:19|[2-689]\\d|70)\\d{4}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"19|[2-9]"
				]
			]
		]
	],
	GM: [
		"220",
		"00",
		"[2-9]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-9]"
				]
			]
		]
	],
	GN: [
		"224",
		"00",
		"722\\d{6}|(?:3|6\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"3"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[67]"
				]
			]
		]
	],
	GP: [
		"590",
		"00",
		"(?:590|(?:69|80)\\d|976)\\d{6}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[569]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"590(?:0[1-68]|1[0-2]|2[0-68]|3[1289]|4[0-24-9]|5[3-579]|6[0189]|7[08]|8[0-689]|9\\d)\\d{4}"
			],
			[
				"69(?:0\\d\\d|1(?:2[2-9]|3[0-5]))\\d{4}"
			],
			[
				"80[0-5]\\d{6}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"976[01]\\d{5}"
			]
		]
	],
	GQ: [
		"240",
		"00",
		"222\\d{6}|(?:3\\d|55|[89]0)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[235]"
				]
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"[89]"
				]
			]
		]
	],
	GR: [
		"30",
		"00",
		"5005000\\d{3}|8\\d{9,11}|(?:[269]\\d|70)\\d{8}",
		[
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"21|7"
				]
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"2(?:2|3[2-57-9]|4[2-469]|5[2-59]|6[2-9]|7[2-69]|8[2-49])|5"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2689]"
				]
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{5})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		]
	],
	GT: [
		"502",
		"00",
		"(?:1\\d{3}|[2-7])\\d{7}",
		[
			8,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2-7]"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		]
	],
	GU: [
		"1",
		"011",
		"(?:[58]\\d\\d|671|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([3-9]\\d{6})$",
		"671$1",
		0,
		"671"
	],
	GW: [
		"245",
		"00",
		"[49]\\d{8}|4\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"40"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[49]"
				]
			]
		]
	],
	GY: [
		"592",
		"001",
		"(?:862\\d|9008)\\d{3}|(?:[2-46]\\d|77)\\d{5}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-46-9]"
				]
			]
		]
	],
	HK: [
		"852",
		"00(?:30|5[09]|[126-9]?)",
		"8[0-46-9]\\d{6,7}|9\\d{4}(?:\\d(?:\\d(?:\\d{4})?)?)?|(?:[235-79]\\d|46)\\d{6}",
		[
			5,
			6,
			7,
			8,
			9,
			11
		],
		[
			[
				"(\\d{3})(\\d{2,5})",
				"$1 $2",
				[
					"900",
					"9003"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2-7]|8[1-4]|9(?:0[1-9]|[1-8])"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"9"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"00"
	],
	HN: [
		"504",
		"00",
		"8\\d{10}|[237-9]\\d{7}",
		[
			8,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"[237-9]"
				]
			]
		]
	],
	HR: [
		"385",
		"00",
		"(?:[24-69]\\d|3[0-79])\\d{7}|80\\d{5,7}|[1-79]\\d{7}|6\\d{5,6}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"6[01]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[67]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-5]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	HT: [
		"509",
		"00",
		"[2-489]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-489]"
				]
			]
		]
	],
	HU: [
		"36",
		"00",
		"[235-7]\\d{8}|[1-9]\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"(06 $1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[27][2-9]|3[2-7]|4[24-9]|5[2-79]|6|8[2-57-9]|9[2-69]"
				],
				"(06 $1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-9]"
				],
				"06 $1"
			]
		],
		"06"
	],
	ID: [
		"62",
		"00[89]",
		"(?:(?:00[1-9]|8\\d)\\d{4}|[1-36])\\d{6}|00\\d{10}|[1-9]\\d{8,10}|[2-9]\\d{7}",
		[
			7,
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"15"
				]
			],
			[
				"(\\d{2})(\\d{5,9})",
				"$1 $2",
				[
					"2[124]|[36]1"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{5,7})",
				"$1 $2",
				[
					"800"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5,8})",
				"$1 $2",
				[
					"[2-79]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{3})",
				"$1-$2-$3",
				[
					"8[1-35-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6,8})",
				"$1 $2",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"804"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"80"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})(\\d{4,5})",
				"$1-$2-$3",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	IE: [
		"353",
		"00",
		"(?:1\\d|[2569])\\d{6,8}|4\\d{6,9}|7\\d{8}|8\\d{8,9}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"2[24-9]|47|58|6[237-9]|9[35-9]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[45]0"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2569]|4[1-69]|7[14]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"70"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"81"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[78]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"4"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	IL: [
		"972",
		"0(?:0|1[2-9])",
		"1\\d{6}(?:\\d{3,5})?|[57]\\d{8}|[1-489]\\d{7}",
		[
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{4})(\\d{3})",
				"$1-$2",
				[
					"125"
				]
			],
			[
				"(\\d{4})(\\d{2})(\\d{2})",
				"$1-$2-$3",
				[
					"121"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[2-489]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"[57]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1-$2-$3",
				[
					"12"
				]
			],
			[
				"(\\d{4})(\\d{6})",
				"$1-$2",
				[
					"159"
				]
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{3})",
				"$1-$2-$3-$4",
				[
					"1[7-9]"
				]
			],
			[
				"(\\d{3})(\\d{1,2})(\\d{3})(\\d{4})",
				"$1-$2 $3-$4",
				[
					"15"
				]
			]
		],
		"0"
	],
	IM: [
		"44",
		"00",
		"1624\\d{6}|(?:[3578]\\d|90)\\d{8}",
		[
			10
		],
		0,
		"0",
		0,
		"0|([25-8]\\d{5})$",
		"1624$1",
		0,
		"74576|(?:16|7[56])24"
	],
	IN: [
		"91",
		"00",
		"(?:000800|[2-9]\\d\\d)\\d{7}|1\\d{7,12}",
		[
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d{8})",
				"$1",
				[
					"5(?:0|2[23]|3[03]|[67]1|88)",
					"5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|888)",
					"5(?:0|2(?:21|3)|3(?:0|3[23])|616|717|8888)"
				],
				0,
				1
			],
			[
				"(\\d{4})(\\d{4,5})",
				"$1 $2",
				[
					"180",
					"1800"
				],
				0,
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"140"
				],
				0,
				1
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"11|2[02]|33|4[04]|79[1-7]|80[2-46]",
					"11|2[02]|33|4[04]|79(?:[1-6]|7[19])|80(?:[2-4]|6[0-589])",
					"11|2[02]|33|4[04]|79(?:[124-6]|3(?:[02-9]|1[0-24-9])|7(?:1|9[1-6]))|80(?:[2-4]|6[0-589])"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:2[0-249]|3[0-25]|4[145]|[68]|7[1257])|2(?:1[257]|3[013]|4[01]|5[0137]|6[0158]|78|8[1568])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|5[12]|[78]1)|6(?:12|[2-4]1|5[17]|6[13]|80)|7(?:12|3[134]|4[47]|61|88)|8(?:16|2[014]|3[126]|6[136]|7[078]|8[34]|91)|(?:43|59|75)[15]|(?:1[59]|29|67|72)[14]",
					"1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|674|7(?:(?:2[14]|3[34]|5[15])[2-6]|61[346]|88[0-8])|8(?:70[2-6]|84[235-7]|91[3-7])|(?:1(?:29|60|8[06])|261|552|6(?:12|[2-47]1|5[17]|6[13]|80)|7(?:12|31|4[47])|8(?:16|2[014]|3[126]|6[136]|7[78]|83))[2-7]",
					"1(?:2[0-24]|3[0-25]|4[145]|[59][14]|6[1-9]|7[1257]|8[1-57-9])|2(?:1[257]|3[013]|4[01]|5[0137]|6[058]|78|8[1568]|9[14])|3(?:26|4[1-3]|5[34]|6[01489]|7[02-46]|8[159])|4(?:1[36]|2[1-47]|3[15]|5[12]|6[0-26-9]|7[0-24-9]|8[013-57]|9[014-7])|5(?:1[025]|22|[36][25]|4[28]|[578]1|9[15])|6(?:12(?:[2-6]|7[0-8])|74[2-7])|7(?:(?:2[14]|5[15])[2-6]|3171|61[346]|88(?:[2-7]|82))|8(?:70[2-6]|84(?:[2356]|7[19])|91(?:[3-6]|7[19]))|73[134][2-6]|(?:74[47]|8(?:16|2[014]|3[126]|6[136]|7[78]|83))(?:[2-6]|7[19])|(?:1(?:29|60|8[06])|261|552|6(?:[2-4]1|5[17]|6[13]|7(?:1|4[0189])|80)|7(?:12|88[01]))[2-7]"
				],
				"0$1",
				1
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2[2457-9]|3[2-5]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1[013-9]|28|3[129]|4[1-35689]|5[29]|6[02-5]|70)|807",
					"1(?:[2-479]|5[0235-9])|[2-5]|6(?:1[1358]|2(?:[2457]|84|95)|3(?:[2-4]|55)|4[235-7]|5[2-689]|6[24578]|7[235689]|8[1-6])|7(?:1(?:[013-8]|9[6-9])|28[6-8]|3(?:17|2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4|5[0-367])|70[13-7])|807[19]",
					"1(?:[2-479]|5(?:[0236-9]|5[013-9]))|[2-5]|6(?:2(?:84|95)|355|83)|73179|807(?:1|9[1-3])|(?:1552|6(?:1[1358]|2[2457]|3[2-4]|4[235-7]|5[2-689]|6[24578]|7[235689]|8[124-6])\\d|7(?:1(?:[013-8]\\d|9[6-9])|28[6-8]|3(?:2[0-49]|9[2-57])|4(?:1[2-4]|[29][0-7]|3[0-8]|[56]\\d|8[0-24-7])|5(?:2[1-3]|9[0-6])|6(?:0[5689]|2[5-9]|3[02-8]|4\\d|5[0-367])|70[13-7]))[2-7]"
				],
				"0$1",
				1
			],
			[
				"(\\d{5})(\\d{5})",
				"$1 $2",
				[
					"[6-9]"
				],
				"0$1",
				1
			],
			[
				"(\\d{4})(\\d{2,4})(\\d{4})",
				"$1 $2 $3",
				[
					"1(?:6|8[06])",
					"1(?:6|8[06]0)"
				],
				0,
				1
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"18"
				],
				0,
				1
			]
		],
		"0"
	],
	IO: [
		"246",
		"00",
		"3\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"3"
				]
			]
		]
	],
	IQ: [
		"964",
		"00",
		"(?:1|7\\d\\d)\\d{7}|[2-6]\\d{7,8}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			]
		],
		"0"
	],
	IR: [
		"98",
		"00",
		"[1-9]\\d{9}|(?:[1-8]\\d\\d|9)\\d{3,4}",
		[
			4,
			5,
			6,
			7,
			10
		],
		[
			[
				"(\\d{4,5})",
				"$1",
				[
					"96"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4,5})",
				"$1 $2",
				[
					"(?:1[137]|2[13-68]|3[1458]|4[145]|5[1468]|6[16]|7[1467]|8[13467])[12689]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-8]"
				],
				"0$1"
			]
		],
		"0"
	],
	IS: [
		"354",
		"00|1(?:0(?:01|[12]0)|100)",
		"(?:38\\d|[4-9])\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[4-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"3"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"00"
	],
	IT: [
		"39",
		"00",
		"0\\d{5,10}|1\\d{8,10}|3(?:[0-8]\\d{7,10}|9\\d{7,8})|55\\d{8}|8\\d{5}(?:\\d{2,4})?",
		[
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d{4,6})",
				"$1 $2",
				[
					"0[26]"
				]
			],
			[
				"(\\d{3})(\\d{3,6})",
				"$1 $2",
				[
					"0[13-57-9][0159]|8(?:03|4[17]|9[2-5])",
					"0[13-57-9][0159]|8(?:03|4[17]|9(?:2|3[04]|[45][0-4]))"
				]
			],
			[
				"(\\d{4})(\\d{2,6})",
				"$1 $2",
				[
					"0(?:[13-579][2-46-8]|8[236-8])"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"894"
				]
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[26]|5"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"1(?:44|[679])|[38]"
				]
			],
			[
				"(\\d{3})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"0[13-57-9][0159]|14"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{5})",
				"$1 $2 $3",
				[
					"0[26]"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4,5})",
				"$1 $2 $3",
				[
					"3"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			[
				"0669[0-79]\\d{1,6}|0(?:1(?:[0159]\\d|[27][1-5]|31|4[1-4]|6[1356]|8[2-57])|2\\d\\d|3(?:[0159]\\d|2[1-4]|3[12]|[48][1-6]|6[2-59]|7[1-7])|4(?:[0159]\\d|[23][1-9]|4[245]|6[1-5]|7[1-4]|81)|5(?:[0159]\\d|2[1-5]|3[2-6]|4[1-79]|6[4-6]|7[1-578]|8[3-8])|6(?:[0-57-9]\\d|6[0-8])|7(?:[0159]\\d|2[12]|3[1-7]|4[2-46]|6[13569]|7[13-6]|8[1-59])|8(?:[0159]\\d|2[3-578]|3[1-356]|[6-8][1-5])|9(?:[0159]\\d|[238][1-5]|4[12]|6[1-8]|7[1-6]))\\d{2,7}"
			],
			[
				"3[1-9]\\d{8}|3[2-9]\\d{7}",
				[
					9,
					10
				]
			],
			[
				"80(?:0\\d{3}|3)\\d{3}",
				[
					6,
					9
				]
			],
			[
				"(?:0878\\d{3}|89(?:2\\d|3[04]|4(?:[0-4]|[5-9]\\d\\d)|5[0-4]))\\d\\d|(?:1(?:44|6[346])|89(?:38|5[5-9]|9))\\d{6}",
				[
					6,
					8,
					9,
					10
				]
			],
			[
				"1(?:78\\d|99)\\d{6}",
				[
					9,
					10
				]
			],
			0,
			0,
			0,
			[
				"55\\d{8}",
				[
					10
				]
			],
			[
				"84(?:[08]\\d{3}|[17])\\d{3}",
				[
					6,
					9
				]
			]
		]
	],
	JE: [
		"44",
		"00",
		"1534\\d{6}|(?:[3578]\\d|90)\\d{8}",
		[
			10
		],
		0,
		"0",
		0,
		"0|([0-24-8]\\d{5})$",
		"1534$1",
		0,
		0,
		[
			[
				"1534[0-24-8]\\d{5}"
			],
			[
				"7(?:(?:(?:50|82)9|937)\\d|7(?:00[378]|97[7-9]))\\d{5}"
			],
			[
				"80(?:07(?:35|81)|8901)\\d{4}"
			],
			[
				"(?:8(?:4(?:4(?:4(?:05|42|69)|703)|5(?:041|800))|7(?:0002|1206))|90(?:066[59]|1810|71(?:07|55)))\\d{4}"
			],
			[
				"701511\\d{4}"
			],
			0,
			[
				"(?:3(?:0(?:07(?:35|81)|8901)|3\\d{4}|4(?:4(?:4(?:05|42|69)|703)|5(?:041|800))|7(?:0002|1206))|55\\d{4})\\d{4}"
			],
			[
				"76(?:464|652)\\d{5}|76(?:0[0-2]|2[356]|34|4[01347]|5[49]|6[0-369]|77|81|9[139])\\d{6}"
			],
			[
				"56\\d{8}"
			]
		]
	],
	JM: [
		"1",
		"011",
		"(?:[58]\\d\\d|658|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		0,
		0,
		0,
		"658|876"
	],
	JO: [
		"962",
		"00",
		"(?:(?:[2689]|7\\d)\\d|32|53)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2356]|87"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"70"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			]
		],
		"0"
	],
	JP: [
		"81",
		"010",
		"00[1-9]\\d{6,14}|[257-9]\\d{9}|(?:00|[1-9]\\d\\d)\\d{6}",
		[
			8,
			9,
			10,
			11,
			12,
			13,
			14,
			15,
			16,
			17
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1-$2-$3",
				[
					"(?:12|57|99)0"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d)(\\d{4})",
				"$1-$2-$3",
				[
					"1(?:26|3[79]|4[56]|5[4-68]|6[3-5])|499|5(?:76|97)|746|8(?:3[89]|47|51|63)|9(?:49|80|9[16])",
					"1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:76|97)9|7468|8(?:3(?:8[7-9]|96)|477|51[2-9]|636)|9(?:496|802|9(?:1[23]|69))|1(?:45|58)[67]",
					"1(?:267|3(?:7[247]|9[278])|466|5(?:47|58|64)|6(?:3[245]|48|5[4-68]))|499[2468]|5(?:769|979[2-69])|7468|8(?:3(?:8[7-9]|96[2457-9])|477|51[2-9]|636[457-9])|9(?:496|802|9(?:1[23]|69))|1(?:45|58)[67]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"60"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1-$2-$3",
				[
					"[36]|4(?:2[09]|7[01])",
					"[36]|4(?:2(?:0|9[02-69])|7(?:0[019]|1))"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"1(?:1|5[45]|77|88|9[69])|2(?:2[1-37]|3[0-269]|4[59]|5|6[24]|7[1-358]|8[1369]|9[0-38])|4(?:[28][1-9]|3[0-57]|[45]|6[248]|7[2-579]|9[29])|5(?:2|3[045]|4[0-369]|5[29]|8[02389]|9[0-389])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9[2-6])|8(?:2[124589]|3[27-9]|49|51|6|7[0-468]|8[68]|9[019])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9[1-489])",
					"1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2(?:[127]|3[014-9])|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9[19])|62|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|8[1-9])|5(?:2|3[045]|4[0-369]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0-2469])|49|51|6(?:[0-24]|36|5[0-3589]|72|9[01459])|7[0-468]|8[68])|9(?:[23][1-9]|4[15]|5[138]|6[1-3]|7[156]|8[189]|9(?:[1289]|3[34]|4[0178]))|(?:49|55|83)[29]|(?:264|837)[016-9]|2(?:57|93)[015-9]|(?:25[0468]|422|838)[01]|(?:47[59]|59[89]|8(?:6[68]|9))[019]",
					"1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2[127]|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|9(?:17|99))|6(?:2|4[016-9])|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|9[29])|5(?:2|3[045]|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9[0169])|3(?:[29]|7(?:[017-9]|6[6-8]))|49|51|6(?:[0-24]|36[23]|5(?:[0-389]|5[23])|6(?:[01]|9[178])|72|9[0145])|7[0-468]|8[68])|9(?:4[15]|5[138]|7[156]|8[189]|9(?:[1289]|3(?:31|4[357])|4[0178]))|(?:8294|96)[1-3]|2(?:57|93)[015-9]|(?:223|8699)[014-9]|(?:25[0468]|422|838)[01]|(?:48|8292|9[23])[1-9]|(?:47[59]|59[89]|8(?:68|9))[019]",
					"1(?:1|5(?:4[018]|5[017])|77|88|9[69])|2(?:2[127]|3[0-269]|4[59]|5(?:[1-3]|5[0-69]|7[015-9]|9(?:17|99))|6(?:2|4[016-9])|7(?:[1-35]|8[0189])|8(?:[16]|3[0134]|9[0-5])|9(?:[028]|17|3[015-9]))|4(?:2(?:[13-79]|8[014-6])|3[0-57]|[45]|6[248]|7[2-47]|9[29])|5(?:2|3[045]|4[0-369]|5[29]|8[02389]|9[0-3])|7(?:2[02-46-9]|34|[58]|6[0249]|7[57]|9(?:[23]|4[0-59]|5[01569]|6[0167]))|8(?:2(?:[1258]|4[0-39]|9(?:[019]|4[1-3]|6(?:[0-47-9]|5[01346-9])))|3(?:[29]|7(?:[017-9]|6[6-8]))|49|51|6(?:[0-24]|36[23]|5(?:[0-389]|5[23])|6(?:[01]|9[178])|72|9[0145])|7[0-468]|8[68])|9(?:4[15]|5[138]|6[1-3]|7[156]|8[189]|9(?:[1289]|3(?:31|4[357])|4[0178]))|(?:223|8699)[014-9]|(?:25[0468]|422|838)[01]|(?:48|829(?:2|66)|9[23])[1-9]|(?:47[59]|59[89]|8(?:68|9))[019]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1-$2-$3",
				[
					"[14]|[289][2-9]|5[3-9]|7[2-4679]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"800"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1-$2-$3",
				[
					"[257-9]"
				],
				"0$1"
			]
		],
		"0"
	],
	KE: [
		"254",
		"000",
		"(?:[17]\\d\\d|900)\\d{6}|(?:2|80)0\\d{6,7}|[4-6]\\d{6,8}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{5,7})",
				"$1 $2",
				[
					"[24-6]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"[17]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			]
		],
		"0"
	],
	KG: [
		"996",
		"00",
		"8\\d{9}|(?:[235-8]\\d|99)\\d{7}",
		[
			9,
			10
		],
		[
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"3(?:1[346]|[24-79])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[235-79]|88"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d)(\\d{2,3})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	KH: [
		"855",
		"00[14-9]",
		"1\\d{9}|[1-9]\\d{7,8}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		"0"
	],
	KI: [
		"686",
		"00",
		"(?:[37]\\d|6[0-79])\\d{6}|(?:[2-48]\\d|50)\\d{3}",
		[
			5,
			8
		],
		0,
		"0"
	],
	KM: [
		"269",
		"00",
		"[3478]\\d{6}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[3478]"
				]
			]
		]
	],
	KN: [
		"1",
		"011",
		"(?:[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-7]\\d{6})$",
		"869$1",
		0,
		"869"
	],
	KP: [
		"850",
		"00|99",
		"85\\d{6}|(?:19\\d|[2-7])\\d{7}",
		[
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-7]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			]
		],
		"0"
	],
	KR: [
		"82",
		"00(?:[125689]|3(?:[46]5|91)|7(?:00|27|3|55|6[126]))",
		"00[1-9]\\d{8,11}|(?:[12]|5\\d{3})\\d{7}|[13-6]\\d{9}|(?:[1-6]\\d|80)\\d{7}|[3-6]\\d{4,5}|(?:00|7)0\\d{8}",
		[
			5,
			6,
			8,
			9,
			10,
			11,
			12,
			13,
			14
		],
		[
			[
				"(\\d{2})(\\d{3,4})",
				"$1-$2",
				[
					"(?:3[1-3]|[46][1-4]|5[1-5])1"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"1"
				]
			],
			[
				"(\\d)(\\d{3,4})(\\d{4})",
				"$1-$2-$3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1-$2-$3",
				[
					"60|8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1-$2-$3",
				[
					"[1346]|5[1-5]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1-$2-$3",
				[
					"[57]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{5})(\\d{4})",
				"$1-$2-$3",
				[
					"5"
				],
				"0$1"
			]
		],
		"0",
		0,
		"0(8(?:[1-46-8]|5\\d\\d))?"
	],
	KW: [
		"965",
		"00",
		"18\\d{5}|(?:[2569]\\d|41)\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d{4})(\\d{3,4})",
				"$1 $2",
				[
					"[169]|2(?:[235]|4[1-35-9])|52"
				]
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[245]"
				]
			]
		]
	],
	KY: [
		"1",
		"011",
		"(?:345|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-9]\\d{6})$",
		"345$1",
		0,
		"345"
	],
	KZ: [
		"7",
		"810",
		"(?:33622|8\\d{8})\\d{5}|[78]\\d{9}",
		[
			10,
			14
		],
		0,
		"8",
		0,
		0,
		0,
		0,
		"33|7",
		0,
		"8~10"
	],
	LA: [
		"856",
		"00",
		"[23]\\d{9}|3\\d{8}|(?:[235-8]\\d|41)\\d{6}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2[13]|3[14]|[4-8]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"30[013-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"[23]"
				],
				"0$1"
			]
		],
		"0"
	],
	LB: [
		"961",
		"00",
		"[27-9]\\d{7}|[13-9]\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[13-69]|7(?:[2-57]|62|8[0-7]|9[04-9])|8[02-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[27-9]"
				]
			]
		],
		"0"
	],
	LC: [
		"1",
		"011",
		"(?:[58]\\d\\d|758|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-8]\\d{6})$",
		"758$1",
		0,
		"758"
	],
	LI: [
		"423",
		"00",
		"[68]\\d{8}|(?:[2378]\\d|90)\\d{5}",
		[
			7,
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[2379]|8(?:0[09]|7)",
					"[2379]|8(?:0(?:02|9)|7)"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"69"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			]
		],
		"0",
		0,
		"0|(1001)"
	],
	LK: [
		"94",
		"00",
		"[1-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[1-689]"
				],
				"0$1"
			]
		],
		"0"
	],
	LR: [
		"231",
		"00",
		"(?:2|33|5\\d|77|88)\\d{7}|[4-6]\\d{6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[4-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[3578]"
				],
				"0$1"
			]
		],
		"0"
	],
	LS: [
		"266",
		"00",
		"(?:[256]\\d\\d|800)\\d{5}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2568]"
				]
			]
		]
	],
	LT: [
		"370",
		"00",
		"(?:[3469]\\d|52|[78]0)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"52[0-7]"
				],
				"(8-$1)",
				1
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[7-9]"
				],
				"8 $1",
				1
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"37|4(?:[15]|6[1-8])"
				],
				"(8-$1)",
				1
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[3-6]"
				],
				"(8-$1)",
				1
			]
		],
		"8",
		0,
		"[08]"
	],
	LU: [
		"352",
		"00",
		"35[013-9]\\d{4,8}|6\\d{8}|35\\d{2,4}|(?:[2457-9]\\d|3[0-46-9])\\d{2,9}",
		[
			4,
			5,
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1 $2",
				[
					"2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"2(?:0[2-689]|[2-9])|[3-57]|8(?:0[2-9]|[13-9])|9(?:0[89]|[2-579])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"20[2-689]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})",
				"$1 $2 $3 $4",
				[
					"2(?:[0367]|4[3-8])"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"80[01]|90[015]"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"20"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})(\\d{1,2})",
				"$1 $2 $3 $4 $5",
				[
					"2(?:[0367]|4[3-8])"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{1,5})",
				"$1 $2 $3 $4",
				[
					"[3-57]|8[13-9]|9(?:0[89]|[2-579])|(?:2|80)[2-9]"
				]
			]
		],
		0,
		0,
		"(15(?:0[06]|1[12]|[35]5|4[04]|6[26]|77|88|99)\\d)"
	],
	LV: [
		"371",
		"00",
		"(?:[268]\\d|90)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[269]|8[01]"
				]
			]
		]
	],
	LY: [
		"218",
		"00",
		"[2-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{7})",
				"$1-$2",
				[
					"[2-9]"
				],
				"0$1"
			]
		],
		"0"
	],
	MA: [
		"212",
		"00",
		"[5-8]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{5})(\\d{4})",
				"$1-$2",
				[
					"5(?:29|38)",
					"5(?:29|38)[89]",
					"5(?:29|38)[89]0"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"5[45]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5})",
				"$1-$2",
				[
					"5(?:2[2-489]|3[5-9]|9)|892",
					"5(?:2(?:[2-49]|8[235-9])|3[5-9]|9)|892"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1-$2",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1-$2",
				[
					"[5-7]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"5(?:29(?:[189][05]|2[29]|3[01])|38[89][05])\\d{4}|5(?:2(?:[0-25-7]\\d|3[1-578]|4[02-46-8]|8[0235-7]|90)|3(?:[0-47]\\d|5[02-9]|6[02-8]|80|9[3-9])|(?:4[067]|5[03])\\d)\\d{5}"
			],
			[
				"(?:6(?:[0-79]\\d|8[0-247-9])|7(?:[01]\\d|6[1267]|7[0-57]))\\d{6}"
			],
			[
				"80\\d{7}"
			],
			[
				"89\\d{7}"
			],
			0,
			0,
			0,
			0,
			[
				"592(?:4[0-2]|93)\\d{4}"
			]
		]
	],
	MC: [
		"377",
		"00",
		"(?:[3489]|6\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"4"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[389]"
				]
			],
			[
				"(\\d)(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4 $5",
				[
					"6"
				],
				"0$1"
			]
		],
		"0"
	],
	MD: [
		"373",
		"00",
		"(?:[235-7]\\d|[89]0)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"22|3"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[25-7]"
				],
				"0$1"
			]
		],
		"0"
	],
	ME: [
		"382",
		"00",
		"(?:20|[3-79]\\d)\\d{6}|80\\d{6,7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[2-9]"
				],
				"0$1"
			]
		],
		"0"
	],
	MF: [
		"590",
		"00",
		"(?:590|(?:69|80)\\d|976)\\d{6}",
		[
			9
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"590(?:0[079]|[14]3|[27][79]|30|5[0-268]|87)\\d{4}"
			],
			[
				"69(?:0\\d\\d|1(?:2[2-9]|3[0-5]))\\d{4}"
			],
			[
				"80[0-5]\\d{6}"
			],
			0,
			0,
			0,
			0,
			0,
			[
				"976[01]\\d{5}"
			]
		]
	],
	MG: [
		"261",
		"00",
		"[23]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{3})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[23]"
				],
				"0$1"
			]
		],
		"0",
		0,
		"0|([24-9]\\d{6})$",
		"20$1"
	],
	MH: [
		"692",
		"011",
		"329\\d{4}|(?:[256]\\d|45)\\d{5}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[2-6]"
				]
			]
		],
		"1"
	],
	MK: [
		"389",
		"00",
		"[2-578]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[347]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[58]"
				],
				"0$1"
			]
		],
		"0"
	],
	ML: [
		"223",
		"00",
		"[24-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[24-9]"
				]
			]
		]
	],
	MM: [
		"95",
		"00",
		"1\\d{5,7}|95\\d{6}|(?:[4-7]|9[0-46-9])\\d{6,8}|(?:2|8\\d)\\d{5,8}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"16|2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[45]|6(?:0[23]|[1-689]|7[235-7])|7(?:[0-4]|5[2-7])|8[1-6]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[12]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[4-7]|8[1-35]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{4,6})",
				"$1 $2 $3",
				[
					"9(?:2[0-4]|[35-9]|4[137-9])"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"92"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{5})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			]
		],
		"0"
	],
	MN: [
		"976",
		"001",
		"[12]\\d{7,9}|[57-9]\\d{7}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[12]1"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[57-9]"
				]
			],
			[
				"(\\d{3})(\\d{5,6})",
				"$1 $2",
				[
					"[12]2[1-3]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5,6})",
				"$1 $2",
				[
					"[12](?:27|3[2-8]|4[2-68]|5[1-4689])",
					"[12](?:27|3[2-8]|4[2-68]|5[1-4689])[0-3]"
				],
				"0$1"
			],
			[
				"(\\d{5})(\\d{4,5})",
				"$1 $2",
				[
					"[12]"
				],
				"0$1"
			]
		],
		"0"
	],
	MO: [
		"853",
		"00",
		"0800\\d{3}|(?:28|[68]\\d)\\d{6}",
		[
			7,
			8
		],
		[
			[
				"(\\d{4})(\\d{3})",
				"$1 $2",
				[
					"0"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[268]"
				]
			]
		]
	],
	MP: [
		"1",
		"011",
		"[58]\\d{9}|(?:67|90)0\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-9]\\d{6})$",
		"670$1",
		0,
		"670"
	],
	MQ: [
		"596",
		"00",
		"(?:69|80)\\d{7}|(?:59|97)6\\d{6}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[569]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	MR: [
		"222",
		"00",
		"(?:[2-4]\\d\\d|800)\\d{5}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2-48]"
				]
			]
		]
	],
	MS: [
		"1",
		"011",
		"(?:[58]\\d\\d|664|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([34]\\d{6})$",
		"664$1",
		0,
		"664"
	],
	MT: [
		"356",
		"00",
		"3550\\d{4}|(?:[2579]\\d\\d|800)\\d{5}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2357-9]"
				]
			]
		]
	],
	MU: [
		"230",
		"0(?:0|[24-7]0|3[03])",
		"(?:5|8\\d\\d)\\d{7}|[2-468]\\d{6}",
		[
			7,
			8,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-46]|8[013]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"5"
				]
			],
			[
				"(\\d{5})(\\d{5})",
				"$1 $2",
				[
					"8"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"020"
	],
	MV: [
		"960",
		"0(?:0|19)",
		"(?:800|9[0-57-9]\\d)\\d{7}|[34679]\\d{6}",
		[
			7,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[3467]|9[13-9]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"00"
	],
	MW: [
		"265",
		"00",
		"(?:[19]\\d|[23]1|77|88)\\d{7}|1\\d{6}",
		[
			7,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[2-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[137-9]"
				],
				"0$1"
			]
		],
		"0"
	],
	MX: [
		"52",
		"0[09]",
		"1(?:(?:44|99)[1-9]|65[0-689])\\d{7}|(?:1(?:[017]\\d|[235][1-9]|4[0-35-9]|6[0-46-9]|8[1-79]|9[1-8])|[2-9]\\d)\\d{8}",
		[
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"33|5[56]|81"
				],
				0,
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2-9]"
				],
				0,
				1
			],
			[
				"(\\d)(\\d{2})(\\d{4})(\\d{4})",
				"$2 $3 $4",
				[
					"1(?:33|5[56]|81)"
				],
				0,
				1
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{4})",
				"$2 $3 $4",
				[
					"1"
				],
				0,
				1
			]
		],
		"01",
		0,
		"0(?:[12]|4[45])|1",
		0,
		0,
		0,
		0,
		"00"
	],
	MY: [
		"60",
		"00",
		"1\\d{8,9}|(?:3\\d|[4-9])\\d{7}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1-$2 $3",
				[
					"[4-79]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1-$2 $3",
				[
					"1(?:[02469]|[378][1-9])|8"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1-$2 $3",
				[
					"3"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{4})",
				"$1-$2-$3-$4",
				[
					"1[36-8]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1-$2 $3",
				[
					"15"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1-$2 $3",
				[
					"1"
				],
				"0$1"
			]
		],
		"0"
	],
	MZ: [
		"258",
		"00",
		"(?:2|8\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2|8[2-79]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		]
	],
	NA: [
		"264",
		"00",
		"[68]\\d{7,8}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"88"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"6"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"87"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	NC: [
		"687",
		"00",
		"[2-57-9]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1.$2.$3",
				[
					"[2-57-9]"
				]
			]
		]
	],
	NE: [
		"227",
		"00",
		"[027-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"08"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[089]|2[013]|7[04]"
				]
			]
		]
	],
	NF: [
		"672",
		"00",
		"[13]\\d{5}",
		[
			6
		],
		[
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"1[0-3]"
				]
			],
			[
				"(\\d)(\\d{5})",
				"$1 $2",
				[
					"[13]"
				]
			]
		],
		0,
		0,
		"([0-258]\\d{4})$",
		"3$1"
	],
	NG: [
		"234",
		"009",
		"(?:[124-7]|9\\d{3})\\d{6}|[1-9]\\d{7}|[78]\\d{9,13}",
		[
			7,
			8,
			10,
			11,
			12,
			13,
			14
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"78"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[12]|9(?:0[3-9]|[1-9])"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[3-7]|8[2-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[7-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})(\\d{4,5})",
				"$1 $2 $3",
				[
					"[78]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5})(\\d{5,6})",
				"$1 $2 $3",
				[
					"[78]"
				],
				"0$1"
			]
		],
		"0"
	],
	NI: [
		"505",
		"00",
		"(?:1800|[25-8]\\d{3})\\d{4}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[125-8]"
				]
			]
		]
	],
	NL: [
		"31",
		"00",
		"(?:[124-7]\\d\\d|3(?:[02-9]\\d|1[0-8]))\\d{6}|[89]\\d{6,9}|1\\d{4,5}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{4,7})",
				"$1 $2",
				[
					"[89]0"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"66"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{8})",
				"$1 $2",
				[
					"6"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1[16-8]|2[259]|3[124]|4[17-9]|5[124679]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-57-9]"
				],
				"0$1"
			]
		],
		"0"
	],
	NO: [
		"47",
		"00",
		"(?:0|[2-9]\\d{3})\\d{4}",
		[
			5,
			8
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"[489]|59"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[235-7]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		"[02-689]|7[0-8]"
	],
	NP: [
		"977",
		"00",
		"(?:1\\d|9)\\d{9}|[1-9]\\d{7}",
		[
			8,
			10,
			11
		],
		[
			[
				"(\\d)(\\d{7})",
				"$1-$2",
				[
					"1[2-6]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1-$2",
				[
					"1[01]|[2-8]|9(?:[1-579]|6[2-6])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1-$2",
				[
					"9"
				]
			]
		],
		"0"
	],
	NR: [
		"674",
		"00",
		"(?:444|(?:55|8\\d)\\d|666)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[4-68]"
				]
			]
		]
	],
	NU: [
		"683",
		"00",
		"(?:[47]|888\\d)\\d{3}",
		[
			4,
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"8"
				]
			]
		]
	],
	NZ: [
		"64",
		"0(?:0|161)",
		"[29]\\d{7,9}|50\\d{5}(?:\\d{2,3})?|6[0-35-9]\\d{6}|7\\d{7,8}|8\\d{4,9}|(?:11\\d|[34])\\d{7}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{3,8})",
				"$1 $2",
				[
					"8[1-579]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"50[036-8]|[89]0",
					"50(?:[0367]|88)|[89]0"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1-$2 $3",
				[
					"24|[346]|7[2-57-9]|9[2-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2(?:10|74)|[59]|80"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"1|2[028]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,5})",
				"$1 $2 $3",
				[
					"2(?:[169]|7[0-35-9])|7|86"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		0,
		"00"
	],
	OM: [
		"968",
		"00",
		"(?:1505|[279]\\d{3}|500)\\d{4}|800\\d{5,6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{4,6})",
				"$1 $2",
				[
					"[58]"
				]
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"2"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[179]"
				]
			]
		]
	],
	PA: [
		"507",
		"00",
		"(?:00800|8\\d{3})\\d{6}|[68]\\d{7}|[1-57-9]\\d{6}",
		[
			7,
			8,
			10,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[1-57-9]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1-$2",
				[
					"[68]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		]
	],
	PE: [
		"51",
		"19(?:1[124]|77|90)00",
		"(?:[14-8]|9\\d)\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"80"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"1"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[4-8]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				]
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		" Anexo "
	],
	PF: [
		"689",
		"00",
		"4\\d{5}(?:\\d{2})?|8\\d{7,8}",
		[
			6,
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"44"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"4|8[7-9]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				]
			]
		]
	],
	PG: [
		"675",
		"00|140[1-3]",
		"(?:180|[78]\\d{3})\\d{4}|(?:[2-589]\\d|64)\\d{5}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"18|[2-69]|85"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[78]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"00"
	],
	PH: [
		"63",
		"00",
		"(?:[2-7]|9\\d)\\d{8}|2\\d{5}|(?:1800|8)\\d{7,9}",
		[
			6,
			8,
			9,
			10,
			11,
			12,
			13
		],
		[
			[
				"(\\d)(\\d{5})",
				"$1 $2",
				[
					"2"
				],
				"(0$1)"
			],
			[
				"(\\d{4})(\\d{4,6})",
				"$1 $2",
				[
					"3(?:23|39|46)|4(?:2[3-6]|[35]9|4[26]|76)|544|88[245]|(?:52|64|86)2",
					"3(?:230|397|461)|4(?:2(?:35|[46]4|51)|396|4(?:22|63)|59[347]|76[15])|5(?:221|446)|642[23]|8(?:622|8(?:[24]2|5[13]))"
				],
				"(0$1)"
			],
			[
				"(\\d{5})(\\d{4})",
				"$1 $2",
				[
					"346|4(?:27|9[35])|883",
					"3469|4(?:279|9(?:30|56))|8834"
				],
				"(0$1)"
			],
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[3-7]|8[2-8]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{4})(\\d{1,2})(\\d{3})(\\d{4})",
				"$1 $2 $3 $4",
				[
					"1"
				]
			]
		],
		"0"
	],
	PK: [
		"92",
		"00",
		"122\\d{6}|[24-8]\\d{10,11}|9(?:[013-9]\\d{8,10}|2(?:[01]\\d\\d|2(?:[06-8]\\d|1[01]))\\d{7})|(?:[2-8]\\d{3}|92(?:[0-7]\\d|8[1-9]))\\d{6}|[24-9]\\d{8}|[89]\\d{7}",
		[
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{2,7})",
				"$1 $2 $3",
				[
					"[89]0"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"1"
				]
			],
			[
				"(\\d{3})(\\d{6,7})",
				"$1 $2",
				[
					"2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:2[2-8]|3[27-9]|4[2-6]|6[3569]|9[25-8])",
					"9(?:2[3-8]|98)|(?:2(?:3[2358]|4[2-4]|9[2-8])|45[3479]|54[2-467]|60[468]|72[236]|8(?:2[2-689]|3[23578]|4[3478]|5[2356])|9(?:22|3[27-9]|4[2-6]|6[3569]|9[25-7]))[2-9]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{7,8})",
				"$1 $2",
				[
					"(?:2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91)[2-9]"
				],
				"(0$1)"
			],
			[
				"(\\d{5})(\\d{5})",
				"$1 $2",
				[
					"58"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{7})",
				"$1 $2",
				[
					"3"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"2[125]|4[0-246-9]|5[1-35-7]|6[1-8]|7[14]|8[16]|91"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"[24-9]"
				],
				"(0$1)"
			]
		],
		"0"
	],
	PL: [
		"48",
		"00",
		"6\\d{5}(?:\\d{2})?|8\\d{9}|[1-9]\\d{6}(?:\\d{2})?",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{5})",
				"$1",
				[
					"19"
				]
			],
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"11|64"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{3})",
				"$1 $2 $3",
				[
					"(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])1",
					"(?:1[2-8]|2[2-69]|3[2-4]|4[1-468]|5[24-689]|6[1-3578]|7[14-7]|8[1-79]|9[145])19"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"64"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"21|39|45|5[0137]|6[0469]|7[02389]|8(?:0[14]|8)"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"1[2-8]|[2-7]|8[1-79]|9[145]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		]
	],
	PM: [
		"508",
		"00",
		"(?:[45]|80\\d\\d)\\d{5}",
		[
			6,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[45]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	PR: [
		"1",
		"011",
		"(?:[589]\\d\\d|787)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		0,
		0,
		0,
		"787|939"
	],
	PS: [
		"970",
		"00",
		"[2489]2\\d{6}|(?:1\\d|5)\\d{8}",
		[
			8,
			9,
			10
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[2489]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		"0"
	],
	PT: [
		"351",
		"00",
		"1693\\d{5}|(?:[26-9]\\d|30)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2[12]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"16|[236-9]"
				]
			]
		]
	],
	PW: [
		"680",
		"01[12]",
		"(?:[24-8]\\d\\d|345|900)\\d{4}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-9]"
				]
			]
		]
	],
	PY: [
		"595",
		"00",
		"59\\d{4,6}|9\\d{5,10}|(?:[2-46-8]\\d|5[0-8])\\d{4,7}",
		[
			6,
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{3})(\\d{3,6})",
				"$1 $2",
				[
					"[2-9]0"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"[26]1|3[289]|4[1246-8]|7[1-3]|8[1-36]"
				],
				"(0$1)"
			],
			[
				"(\\d{3})(\\d{4,5})",
				"$1 $2",
				[
					"2[279]|3[13-5]|4[359]|5|6(?:[34]|7[1-46-8])|7[46-8]|85"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2[14-68]|3[26-9]|4[1246-8]|6(?:1|75)|7[1-35]|8[1-36]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"87"
				]
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"9(?:[5-79]|8[1-6])"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-8]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"9"
				]
			]
		],
		"0"
	],
	QA: [
		"974",
		"00",
		"[2-7]\\d{7}|800\\d{4}(?:\\d{2})?|2\\d{6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"2[126]|8"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[2-7]"
				]
			]
		]
	],
	RE: [
		"262",
		"00",
		"9769\\d{5}|(?:26|[68]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2689]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		"26[23]|69|[89]"
	],
	RO: [
		"40",
		"00",
		"(?:[2378]\\d|90)\\d{7}|[23]\\d{5}",
		[
			6,
			9
		],
		[
			[
				"(\\d{3})(\\d{3})",
				"$1 $2",
				[
					"2[3-6]",
					"2[3-6]\\d9"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"219|31"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[23]1"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[237-9]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		" int "
	],
	RS: [
		"381",
		"00",
		"38[02-9]\\d{6,9}|6\\d{7,9}|90\\d{4,8}|38\\d{5,6}|(?:7\\d\\d|800)\\d{3,9}|(?:[12]\\d|3[0-79])\\d{5,10}",
		[
			6,
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{3})(\\d{3,9})",
				"$1 $2",
				[
					"(?:2[389]|39)0|[7-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{5,10})",
				"$1 $2",
				[
					"[1-36]"
				],
				"0$1"
			]
		],
		"0"
	],
	RU: [
		"7",
		"810",
		"8\\d{13}|[347-9]\\d{9}",
		[
			10,
			14
		],
		[
			[
				"(\\d{4})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"7(?:1[0-8]|2[1-9])",
					"7(?:1(?:[0-6]2|7|8[27])|2(?:1[23]|[2-9]2))",
					"7(?:1(?:[0-6]2|7|8[27])|2(?:13[03-69]|62[013-9]))|72[1-57-9]2"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{5})(\\d)(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"7(?:1[0-68]|2[1-9])",
					"7(?:1(?:[06][3-6]|[18]|2[35]|[3-5][3-5])|2(?:[13][3-5]|[24-689]|7[457]))",
					"7(?:1(?:0(?:[356]|4[023])|[18]|2(?:3[013-9]|5)|3[45]|43[013-79]|5(?:3[1-8]|4[1-7]|5)|6(?:3[0-35-9]|[4-6]))|2(?:1(?:3[178]|[45])|[24-689]|3[35]|7[457]))|7(?:14|23)4[0-8]|71(?:33|45)[1-79]"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[349]|8(?:[02-7]|1[1-8])"
				],
				"8 ($1)",
				1
			],
			[
				"(\\d{4})(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"8"
				],
				"8 ($1)"
			]
		],
		"8",
		0,
		0,
		0,
		0,
		"3[04-689]|[489]",
		0,
		"8~10"
	],
	RW: [
		"250",
		"00",
		"(?:06|[27]\\d\\d|[89]00)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[7-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				]
			]
		],
		"0"
	],
	SA: [
		"966",
		"00",
		"92\\d{7}|(?:[15]|8\\d)\\d{8}",
		[
			9,
			10
		],
		[
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"9"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"5"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"81"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			]
		],
		"0"
	],
	SB: [
		"677",
		"0[01]",
		"(?:[1-6]|[7-9]\\d\\d)\\d{4}",
		[
			5,
			7
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"7|8[4-9]|9(?:[1-8]|9[0-8])"
				]
			]
		]
	],
	SC: [
		"248",
		"010|0[0-2]",
		"8000\\d{3}|(?:[249]\\d|64)\\d{5}",
		[
			7
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[246]|9[57]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"00"
	],
	SD: [
		"249",
		"00",
		"[19]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[19]"
				],
				"0$1"
			]
		],
		"0"
	],
	SE: [
		"46",
		"00",
		"(?:[26]\\d\\d|9)\\d{9}|[1-9]\\d{8}|[1-689]\\d{7}|[1-4689]\\d{6}|2\\d{5}",
		[
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{2,3})(\\d{2})",
				"$1-$2 $3",
				[
					"20"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"9(?:00|39|44)"
				],
				"0$1",
				0,
				"$1 $2"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})",
				"$1-$2 $3",
				[
					"[12][136]|3[356]|4[0246]|6[03]|90[1-9]"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d)(\\d{2,3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"8"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2,3})(\\d{2})",
				"$1-$2 $3",
				[
					"1[2457]|2(?:[247-9]|5[0138])|3[0247-9]|4[1357-9]|5[0-35-9]|6(?:[125689]|4[02-57]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d{3})(\\d{2,3})(\\d{3})",
				"$1-$2 $3",
				[
					"9(?:00|39|44)"
				],
				"0$1",
				0,
				"$1 $2 $3"
			],
			[
				"(\\d{2})(\\d{2,3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"1[13689]|2[0136]|3[1356]|4[0246]|54|6[03]|90[1-9]"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"10|7"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"8"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4",
				[
					"[13-5]|2(?:[247-9]|5[0138])|6(?:[124-689]|7[0-2])|9(?:[125-8]|3[02-5]|4[0-3])"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{3})",
				"$1-$2 $3 $4",
				[
					"9"
				],
				"0$1",
				0,
				"$1 $2 $3 $4"
			],
			[
				"(\\d{3})(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1-$2 $3 $4 $5",
				[
					"[26]"
				],
				"0$1",
				0,
				"$1 $2 $3 $4 $5"
			]
		],
		"0"
	],
	SG: [
		"65",
		"0[0-3]\\d",
		"(?:(?:1\\d|8)\\d\\d|7000)\\d{7}|[3689]\\d{7}",
		[
			8,
			10,
			11
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[369]|8(?:0[1-4]|[1-9])"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				]
			],
			[
				"(\\d{4})(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"7"
				]
			],
			[
				"(\\d{4})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		]
	],
	SH: [
		"290",
		"00",
		"(?:[256]\\d|8)\\d{3}",
		[
			4,
			5
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"[256]"
	],
	SI: [
		"386",
		"00|10(?:22|66|88|99)",
		"[1-7]\\d{7}|8\\d{4,7}|90\\d{4,6}",
		[
			5,
			6,
			7,
			8
		],
		[
			[
				"(\\d{2})(\\d{3,6})",
				"$1 $2",
				[
					"8[09]|9"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"59|8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[37][01]|4[0139]|51|6"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[1-57]"
				],
				"(0$1)"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		0,
		"00"
	],
	SJ: [
		"47",
		"00",
		"0\\d{4}|(?:[489]\\d|[57]9)\\d{6}",
		[
			5,
			8
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"79"
	],
	SK: [
		"421",
		"00",
		"[2-689]\\d{8}|[2-59]\\d{6}|[2-5]\\d{5}",
		[
			6,
			7,
			9
		],
		[
			[
				"(\\d)(\\d{2})(\\d{3,4})",
				"$1 $2 $3",
				[
					"21"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{2})(\\d{2,3})",
				"$1 $2 $3",
				[
					"[3-5][1-8]1",
					"[3-5][1-8]1[67]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{3})(\\d{2})",
				"$1/$2 $3 $4",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[689]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1/$2 $3 $4",
				[
					"[3-5]"
				],
				"0$1"
			]
		],
		"0"
	],
	SL: [
		"232",
		"00",
		"(?:[237-9]\\d|66)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"[236-9]"
				],
				"(0$1)"
			]
		],
		"0"
	],
	SM: [
		"378",
		"00",
		"(?:0549|[5-7]\\d)\\d{6}",
		[
			8,
			10
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[5-7]"
				]
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"0"
				]
			]
		],
		0,
		0,
		"([89]\\d{5})$",
		"0549$1"
	],
	SN: [
		"221",
		"00",
		"(?:[378]\\d|93)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[379]"
				]
			]
		]
	],
	SO: [
		"252",
		"00",
		"[346-9]\\d{8}|[12679]\\d{7}|[1-5]\\d{6}|[1348]\\d{5}",
		[
			6,
			7,
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"8[125]"
				]
			],
			[
				"(\\d{6})",
				"$1",
				[
					"[134]"
				]
			],
			[
				"(\\d)(\\d{6})",
				"$1 $2",
				[
					"[15]|2[0-79]|3[0-46-8]|4[0-7]"
				]
			],
			[
				"(\\d)(\\d{7})",
				"$1 $2",
				[
					"24|[67]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[3478]|64|90"
				]
			],
			[
				"(\\d{2})(\\d{5,7})",
				"$1 $2",
				[
					"1|28|6(?:0[5-7]|[1-35-9])|9[2-9]"
				]
			]
		],
		"0"
	],
	SR: [
		"597",
		"00",
		"(?:[2-5]|68|[78]\\d)\\d{5}",
		[
			6,
			7
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1-$2-$3",
				[
					"56"
				]
			],
			[
				"(\\d{3})(\\d{3})",
				"$1-$2",
				[
					"[2-5]"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1-$2",
				[
					"[6-8]"
				]
			]
		]
	],
	SS: [
		"211",
		"00",
		"[19]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[19]"
				],
				"0$1"
			]
		],
		"0"
	],
	ST: [
		"239",
		"00",
		"(?:22|9\\d)\\d{5}",
		[
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[29]"
				]
			]
		]
	],
	SV: [
		"503",
		"00",
		"[267]\\d{7}|[89]00\\d{4}(?:\\d{4})?",
		[
			7,
			8,
			11
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[89]"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[267]"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				]
			]
		]
	],
	SX: [
		"1",
		"011",
		"7215\\d{6}|(?:[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|(5\\d{6})$",
		"721$1",
		0,
		"721"
	],
	SY: [
		"963",
		"00",
		"[1-39]\\d{8}|[1-5]\\d{7}",
		[
			8,
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[1-5]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1",
				1
			]
		],
		"0"
	],
	SZ: [
		"268",
		"00",
		"0800\\d{4}|(?:[237]\\d|900)\\d{6}",
		[
			8,
			9
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[0237]"
				]
			],
			[
				"(\\d{5})(\\d{4})",
				"$1 $2",
				[
					"9"
				]
			]
		]
	],
	TA: [
		"290",
		"00",
		"8\\d{3}",
		[
			4
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"8"
	],
	TC: [
		"1",
		"011",
		"(?:[58]\\d\\d|649|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-479]\\d{6})$",
		"649$1",
		0,
		"649"
	],
	TD: [
		"235",
		"00|16",
		"(?:22|[69]\\d|77)\\d{6}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[2679]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"00"
	],
	TG: [
		"228",
		"00",
		"[279]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[279]"
				]
			]
		]
	],
	TH: [
		"66",
		"00[1-9]",
		"(?:001800|[2-57]|[689]\\d)\\d{7}|1\\d{7,9}",
		[
			8,
			9,
			10,
			13
		],
		[
			[
				"(\\d)(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[13-9]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		"0"
	],
	TJ: [
		"992",
		"810",
		"(?:00|[1-57-9]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{6})(\\d)(\\d{2})",
				"$1 $2 $3",
				[
					"331",
					"3317"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[34]7|91[78]"
				]
			],
			[
				"(\\d{4})(\\d)(\\d{4})",
				"$1 $2 $3",
				[
					"3[1-5]"
				]
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[0-57-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"8~10"
	],
	TK: [
		"690",
		"00",
		"[2-47]\\d{3,6}",
		[
			4,
			5,
			6,
			7
		]
	],
	TL: [
		"670",
		"00",
		"7\\d{7}|(?:[2-47]\\d|[89]0)\\d{5}",
		[
			7,
			8
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[2-489]|70"
				]
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"7"
				]
			]
		]
	],
	TM: [
		"993",
		"810",
		"[1-6]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"12"
				],
				"(8 $1)"
			],
			[
				"(\\d{3})(\\d)(\\d{2})(\\d{2})",
				"$1 $2-$3-$4",
				[
					"[1-5]"
				],
				"(8 $1)"
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"6"
				],
				"8 $1"
			]
		],
		"8",
		0,
		0,
		0,
		0,
		0,
		0,
		"8~10"
	],
	TN: [
		"216",
		"00",
		"[2-57-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-57-9]"
				]
			]
		]
	],
	TO: [
		"676",
		"00",
		"(?:0800|(?:[5-8]\\d\\d|999)\\d)\\d{3}|[2-8]\\d{4}",
		[
			5,
			7
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1-$2",
				[
					"[2-4]|50|6[09]|7[0-24-69]|8[05]"
				]
			],
			[
				"(\\d{4})(\\d{3})",
				"$1 $2",
				[
					"0"
				]
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[5-9]"
				]
			]
		]
	],
	TR: [
		"90",
		"00",
		"4\\d{6}|8\\d{11,12}|(?:[2-58]\\d\\d|900)\\d{7}",
		[
			7,
			10,
			12,
			13
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"512|8[01589]|90"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"5(?:[0-59]|61)",
					"5(?:[0-59]|616)",
					"5(?:[0-59]|6161)"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[24][1-8]|3[1-9]"
				],
				"(0$1)",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{6,7})",
				"$1 $2 $3",
				[
					"80"
				],
				"0$1",
				1
			]
		],
		"0"
	],
	TT: [
		"1",
		"011",
		"(?:[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-46-8]\\d{6})$",
		"868$1",
		0,
		"868"
	],
	TV: [
		"688",
		"00",
		"(?:2|7\\d\\d|90)\\d{4}",
		[
			5,
			6,
			7
		],
		[
			[
				"(\\d{2})(\\d{3})",
				"$1 $2",
				[
					"2"
				]
			],
			[
				"(\\d{2})(\\d{4})",
				"$1 $2",
				[
					"90"
				]
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"7"
				]
			]
		]
	],
	TW: [
		"886",
		"0(?:0[25-79]|19)",
		"[2-689]\\d{8}|7\\d{9,10}|[2-8]\\d{7}|2\\d{6}",
		[
			7,
			8,
			9,
			10,
			11
		],
		[
			[
				"(\\d{2})(\\d)(\\d{4})",
				"$1 $2 $3",
				[
					"202"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[258]0"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"[23568]|4(?:0[02-48]|[1-47-9])|7[1-9]",
					"[23568]|4(?:0[2-48]|[1-47-9])|(?:400|7)[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[49]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{4})(\\d{4,5})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		0,
		0,
		"#"
	],
	TZ: [
		"255",
		"00[056]",
		"(?:[26-8]\\d|41|90)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[24]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[67]"
				],
				"0$1"
			]
		],
		"0"
	],
	UA: [
		"380",
		"00",
		"[89]\\d{9}|[3-9]\\d{8}",
		[
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"6[12][29]|(?:3[1-8]|4[136-8]|5[12457]|6[49])2|(?:56|65)[24]",
					"6[12][29]|(?:35|4[1378]|5[12457]|6[49])2|(?:56|65)[24]|(?:3[1-46-8]|46)2[013-9]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"4[45][0-5]|5(?:0|6[37])|6(?:[12][018]|[36-8])|7|89|9[1-9]|(?:48|57)[0137-9]",
					"4[45][0-5]|5(?:0|6(?:3[14-7]|7))|6(?:[12][018]|[36-8])|7|89|9[1-9]|(?:48|57)[0137-9]"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"[3-6]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[89]"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		0,
		"0~0"
	],
	UG: [
		"256",
		"00[057]",
		"800\\d{6}|(?:[29]0|[347]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{4})(\\d{5})",
				"$1 $2",
				[
					"202",
					"2024"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{6})",
				"$1 $2",
				[
					"[27-9]|4(?:6[45]|[7-9])"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"[34]"
				],
				"0$1"
			]
		],
		"0"
	],
	US: [
		"1",
		"011",
		"[2-9]\\d{9}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"($1) $2-$3",
				[
					"[2-9]"
				],
				0,
				1,
				"$1-$2-$3"
			]
		],
		"1",
		0,
		0,
		0,
		0,
		0,
		[
			[
				"5(?:05(?:[2-57-9]\\d\\d|6(?:[0-35-9]\\d|44))|82(?:2(?:0[0-3]|[268]2)|3(?:0[02]|33)|4(?:00|4[24]|65|82)|5(?:00|29|83)|6(?:00|66|82)|777|8(?:00|88)|9(?:00|9[89])))\\d{4}|(?:2(?:0[1-35-9]|1[02-9]|2[03-589]|3[149]|4[08]|5[1-46]|6[0279]|7[0269]|8[13])|3(?:0[1-57-9]|1[02-9]|2[01356]|3[0-24679]|4[167]|5[12]|6[014]|8[056])|4(?:0[124-9]|1[02-579]|2[3-5]|3[0245]|4[023578]|58|6[39]|7[0589]|8[04])|5(?:0[1-47-9]|1[0235-8]|20|3[0149]|4[01]|5[19]|6[1-47]|7[0-5]|8[056])|6(?:0[1-35-9]|1[024-9]|2[03689]|[34][016]|5[0179]|6[0-279]|78|8[0-29])|7(?:0[1-46-8]|1[2-9]|2[04-7]|3[1247]|4[037]|5[47]|6[02359]|7[0-59]|8[156])|8(?:0[1-68]|1[02-8]|2[08]|3[0-289]|4[03578]|5[046-9]|6[02-5]|7[028])|9(?:0[1346-9]|1[02-9]|2[0589]|3[0146-8]|4[01579]|5[12469]|7[0-389]|8[04-69]))[2-9]\\d{6}"
			],
			[
				""
			],
			[
				"8(?:00|33|44|55|66|77|88)[2-9]\\d{6}"
			],
			[
				"900[2-9]\\d{6}"
			],
			[
				"52(?:3(?:[2-46-9][02-9]\\d|5(?:[02-46-9]\\d|5[0-46-9]))|4(?:[2-478][02-9]\\d|5(?:[034]\\d|2[024-9]|5[0-46-9])|6(?:0[1-9]|[2-9]\\d)|9(?:[05-9]\\d|2[0-5]|49)))\\d{4}|52[34][2-9]1[02-9]\\d{4}|5(?:00|2[12]|33|44|66|77|88)[2-9]\\d{6}"
			]
		]
	],
	UY: [
		"598",
		"0(?:0|1[3-9]\\d)",
		"4\\d{9}|[1249]\\d{7}|(?:[49]\\d|80)\\d{5}",
		[
			7,
			8,
			10
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"405|8|90"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"9"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[124]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"4"
				],
				"0$1"
			]
		],
		"0",
		0,
		0,
		0,
		0,
		0,
		0,
		"00",
		" int. "
	],
	UZ: [
		"998",
		"810",
		"(?:33|55|[679]\\d|88)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[35-9]"
				],
				"8 $1"
			]
		],
		"8",
		0,
		0,
		0,
		0,
		0,
		0,
		"8~10"
	],
	VA: [
		"39",
		"00",
		"0\\d{5,10}|3[0-8]\\d{7,10}|55\\d{8}|8\\d{5}(?:\\d{2,4})?|(?:1\\d|39)\\d{7,8}",
		[
			6,
			7,
			8,
			9,
			10,
			11
		],
		0,
		0,
		0,
		0,
		0,
		0,
		"06698"
	],
	VC: [
		"1",
		"011",
		"(?:[58]\\d\\d|784|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-7]\\d{6})$",
		"784$1",
		0,
		"784"
	],
	VE: [
		"58",
		"00",
		"[68]00\\d{7}|(?:[24]\\d|[59]0)\\d{8}",
		[
			10
		],
		[
			[
				"(\\d{3})(\\d{7})",
				"$1-$2",
				[
					"[24-689]"
				],
				"0$1"
			]
		],
		"0"
	],
	VG: [
		"1",
		"011",
		"(?:284|[58]\\d\\d|900)\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-578]\\d{6})$",
		"284$1",
		0,
		"284"
	],
	VI: [
		"1",
		"011",
		"[58]\\d{9}|(?:34|90)0\\d{7}",
		[
			10
		],
		0,
		"1",
		0,
		"1|([2-9]\\d{6})$",
		"340$1",
		0,
		"340"
	],
	VN: [
		"84",
		"00",
		"[12]\\d{9}|[135-9]\\d{8}|[16]\\d{7}|[16-8]\\d{6}",
		[
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"80"
				],
				"0$1",
				1
			],
			[
				"(\\d{4})(\\d{4,6})",
				"$1 $2",
				[
					"1"
				],
				0,
				1
			],
			[
				"(\\d{2})(\\d{3})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"[69]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[3578]"
				],
				"0$1",
				1
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"2[48]"
				],
				"0$1",
				1
			],
			[
				"(\\d{3})(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"2"
				],
				"0$1",
				1
			]
		],
		"0"
	],
	VU: [
		"678",
		"00",
		"[57-9]\\d{6}|(?:[238]\\d|48)\\d{3}",
		[
			5,
			7
		],
		[
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"[57-9]"
				]
			]
		]
	],
	WF: [
		"681",
		"00",
		"(?:40|72)\\d{4}|8\\d{5}(?:\\d{3})?",
		[
			6,
			9
		],
		[
			[
				"(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3",
				[
					"[478]"
				]
			],
			[
				"(\\d{3})(\\d{2})(\\d{2})(\\d{2})",
				"$1 $2 $3 $4",
				[
					"8"
				]
			]
		]
	],
	WS: [
		"685",
		"0",
		"(?:[2-6]|8\\d{5})\\d{4}|[78]\\d{6}|[68]\\d{5}",
		[
			5,
			6,
			7,
			10
		],
		[
			[
				"(\\d{5})",
				"$1",
				[
					"[2-5]|6[1-9]"
				]
			],
			[
				"(\\d{3})(\\d{3,7})",
				"$1 $2",
				[
					"[68]"
				]
			],
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"7"
				]
			]
		]
	],
	XK: [
		"383",
		"00",
		"[23]\\d{7,8}|(?:4\\d\\d|[89]00)\\d{5}",
		[
			8,
			9
		],
		[
			[
				"(\\d{3})(\\d{5})",
				"$1 $2",
				[
					"[89]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[2-4]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[23]"
				],
				"0$1"
			]
		],
		"0"
	],
	YE: [
		"967",
		"00",
		"(?:1|7\\d)\\d{7}|[1-7]\\d{6}",
		[
			7,
			8,
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"[1-6]|7[24-68]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			]
		],
		"0"
	],
	YT: [
		"262",
		"00",
		"80\\d{7}|(?:26|63)9\\d{6}",
		[
			9
		],
		0,
		"0",
		0,
		0,
		0,
		0,
		"269|63"
	],
	ZA: [
		"27",
		"00",
		"[1-79]\\d{8}|8\\d{4,9}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{2})(\\d{3,4})",
				"$1 $2",
				[
					"8[1-4]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{2,3})",
				"$1 $2 $3",
				[
					"8[1-4]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"860"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"[1-9]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"8"
				],
				"0$1"
			]
		],
		"0"
	],
	ZM: [
		"260",
		"00",
		"(?:63|80)0\\d{6}|(?:21|[79]\\d)\\d{7}",
		[
			9
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[28]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"[79]"
				],
				"0$1"
			]
		],
		"0"
	],
	ZW: [
		"263",
		"00",
		"2(?:[0-57-9]\\d{6,8}|6[0-24-9]\\d{6,7})|[38]\\d{9}|[35-8]\\d{8}|[3-6]\\d{7}|[1-689]\\d{6}|[1-3569]\\d{5}|[1356]\\d{4}",
		[
			5,
			6,
			7,
			8,
			9,
			10
		],
		[
			[
				"(\\d{3})(\\d{3,5})",
				"$1 $2",
				[
					"2(?:0[45]|2[278]|[49]8)|3(?:[09]8|17)|6(?:[29]8|37|75)|[23][78]|(?:33|5[15]|6[68])[78]"
				],
				"0$1"
			],
			[
				"(\\d)(\\d{3})(\\d{2,4})",
				"$1 $2 $3",
				[
					"[49]"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{4})",
				"$1 $2",
				[
					"80"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{7})",
				"$1 $2",
				[
					"24|8[13-59]|(?:2[05-79]|39|5[45]|6[15-8])2",
					"2(?:02[014]|4|[56]20|[79]2)|392|5(?:42|525)|6(?:[16-8]21|52[013])|8[13-59]"
				],
				"(0$1)"
			],
			[
				"(\\d{2})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"7"
				],
				"0$1"
			],
			[
				"(\\d{3})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"2(?:1[39]|2[0157]|[378]|[56][14])|3(?:12|29)",
					"2(?:1[39]|2[0157]|[378]|[56][14])|3(?:123|29)"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{6})",
				"$1 $2",
				[
					"8"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3,5})",
				"$1 $2",
				[
					"1|2(?:0[0-36-9]|12|29|[56])|3(?:1[0-689]|[24-6])|5(?:[0236-9]|1[2-4])|6(?:[013-59]|7[0-46-9])|(?:33|55|6[68])[0-69]|(?:29|3[09]|62)[0-79]"
				],
				"0$1"
			],
			[
				"(\\d{2})(\\d{3})(\\d{3,4})",
				"$1 $2 $3",
				[
					"29[013-9]|39|54"
				],
				"0$1"
			],
			[
				"(\\d{4})(\\d{3,5})",
				"$1 $2",
				[
					"(?:25|54)8",
					"258|5483"
				],
				"0$1"
			]
		],
		"0"
	]
};
var nonGeographic = {
	"800": [
		"800",
		0,
		"(?:005|[1-9]\\d\\d)\\d{5}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"\\d"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			[
				"(?:005|[1-9]\\d\\d)\\d{5}"
			]
		]
	],
	"808": [
		"808",
		0,
		"[1-9]\\d{7}",
		[
			8
		],
		[
			[
				"(\\d{4})(\\d{4})",
				"$1 $2",
				[
					"[1-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"[1-9]\\d{7}"
			]
		]
	],
	"870": [
		"870",
		0,
		"7\\d{11}|[35-7]\\d{8}",
		[
			9,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"[35-7]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			[
				"(?:[356]|774[45])\\d{8}|7[6-8]\\d{7}"
			]
		]
	],
	"878": [
		"878",
		0,
		"10\\d{10}",
		[
			12
		],
		[
			[
				"(\\d{2})(\\d{5})(\\d{5})",
				"$1 $2 $3",
				[
					"1"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"10\\d{10}"
			]
		]
	],
	"881": [
		"881",
		0,
		"[0-36-9]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d)(\\d{3})(\\d{5})",
				"$1 $2 $3",
				[
					"[0-36-9]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			[
				"[0-36-9]\\d{8}"
			]
		]
	],
	"882": [
		"882",
		0,
		"[13]\\d{6}(?:\\d{2,5})?|285\\d{9}|(?:[19]\\d|49)\\d{6}",
		[
			7,
			8,
			9,
			10,
			11,
			12
		],
		[
			[
				"(\\d{2})(\\d{5})",
				"$1 $2",
				[
					"16|342"
				]
			],
			[
				"(\\d{2})(\\d{6})",
				"$1 $2",
				[
					"4"
				]
			],
			[
				"(\\d{2})(\\d{2})(\\d{4})",
				"$1 $2 $3",
				[
					"[19]"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{3})",
				"$1 $2 $3",
				[
					"3[23]"
				]
			],
			[
				"(\\d{2})(\\d{3,4})(\\d{4})",
				"$1 $2 $3",
				[
					"1"
				]
			],
			[
				"(\\d{2})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"34[57]"
				]
			],
			[
				"(\\d{3})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"34"
				]
			],
			[
				"(\\d{2})(\\d{4,5})(\\d{5})",
				"$1 $2 $3",
				[
					"[1-3]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			[
				"342\\d{4}|(?:337|49)\\d{6}|3(?:2|47|7\\d{3})\\d{7}",
				[
					7,
					8,
					9,
					10,
					12
				]
			],
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"1(?:3(?:0[0347]|[13][0139]|2[035]|4[013568]|6[0459]|7[06]|8[15-8]|9[0689])\\d{4}|6\\d{5,10})|(?:(?:285\\d\\d|3(?:45|[69]\\d{3}))\\d|9[89])\\d{6}"
			]
		]
	],
	"883": [
		"883",
		0,
		"(?:210|370\\d\\d)\\d{7}|51\\d{7}(?:\\d{3})?",
		[
			9,
			10,
			12
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3",
				[
					"510"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{4})",
				"$1 $2 $3",
				[
					"2"
				]
			],
			[
				"(\\d{4})(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"51[13]"
				]
			],
			[
				"(\\d{3})(\\d{3})(\\d{3})(\\d{3})",
				"$1 $2 $3 $4",
				[
					"[35]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"(?:210|(?:370[1-9]|51[013]0)\\d)\\d{7}|5100\\d{5}"
			]
		]
	],
	"888": [
		"888",
		0,
		"\\d{11}",
		[
			11
		],
		[
			[
				"(\\d{3})(\\d{3})(\\d{5})",
				"$1 $2 $3"
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			0,
			0,
			0,
			[
				"\\d{11}"
			]
		]
	],
	"979": [
		"979",
		0,
		"[1359]\\d{8}",
		[
			9
		],
		[
			[
				"(\\d)(\\d{4})(\\d{4})",
				"$1 $2 $3",
				[
					"[1359]"
				]
			]
		],
		0,
		0,
		0,
		0,
		0,
		0,
		[
			0,
			0,
			0,
			[
				"[1359]\\d{8}"
			]
		]
	]
};
var metadata$1 = {
	version: version,
	country_calling_codes: country_calling_codes,
	countries: countries,
	nonGeographic: nonGeographic
};

var propTypes = {exports: {}};

var reactIs = {exports: {}};

var reactIs_production_min = {};

/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var b="function"===typeof Symbol&&Symbol.for,c=b?Symbol.for("react.element"):60103,d=b?Symbol.for("react.portal"):60106,e=b?Symbol.for("react.fragment"):60107,f=b?Symbol.for("react.strict_mode"):60108,g=b?Symbol.for("react.profiler"):60114,h=b?Symbol.for("react.provider"):60109,k=b?Symbol.for("react.context"):60110,l=b?Symbol.for("react.async_mode"):60111,m=b?Symbol.for("react.concurrent_mode"):60111,n=b?Symbol.for("react.forward_ref"):60112,p=b?Symbol.for("react.suspense"):60113,q=b?
Symbol.for("react.suspense_list"):60120,r=b?Symbol.for("react.memo"):60115,t=b?Symbol.for("react.lazy"):60116,v=b?Symbol.for("react.block"):60121,w=b?Symbol.for("react.fundamental"):60117,x=b?Symbol.for("react.responder"):60118,y=b?Symbol.for("react.scope"):60119;
function z(a){if("object"===typeof a&&null!==a){var u=a.$$typeof;switch(u){case c:switch(a=a.type,a){case l:case m:case e:case g:case f:case p:return a;default:switch(a=a&&a.$$typeof,a){case k:case n:case t:case r:case h:return a;default:return u}}case d:return u}}}function A(a){return z(a)===m}reactIs_production_min.AsyncMode=l;reactIs_production_min.ConcurrentMode=m;reactIs_production_min.ContextConsumer=k;reactIs_production_min.ContextProvider=h;reactIs_production_min.Element=c;reactIs_production_min.ForwardRef=n;reactIs_production_min.Fragment=e;reactIs_production_min.Lazy=t;reactIs_production_min.Memo=r;reactIs_production_min.Portal=d;
reactIs_production_min.Profiler=g;reactIs_production_min.StrictMode=f;reactIs_production_min.Suspense=p;reactIs_production_min.isAsyncMode=function(a){return A(a)||z(a)===l};reactIs_production_min.isConcurrentMode=A;reactIs_production_min.isContextConsumer=function(a){return z(a)===k};reactIs_production_min.isContextProvider=function(a){return z(a)===h};reactIs_production_min.isElement=function(a){return "object"===typeof a&&null!==a&&a.$$typeof===c};reactIs_production_min.isForwardRef=function(a){return z(a)===n};reactIs_production_min.isFragment=function(a){return z(a)===e};reactIs_production_min.isLazy=function(a){return z(a)===t};
reactIs_production_min.isMemo=function(a){return z(a)===r};reactIs_production_min.isPortal=function(a){return z(a)===d};reactIs_production_min.isProfiler=function(a){return z(a)===g};reactIs_production_min.isStrictMode=function(a){return z(a)===f};reactIs_production_min.isSuspense=function(a){return z(a)===p};
reactIs_production_min.isValidElementType=function(a){return "string"===typeof a||"function"===typeof a||a===e||a===m||a===g||a===f||a===p||a===q||"object"===typeof a&&null!==a&&(a.$$typeof===t||a.$$typeof===r||a.$$typeof===h||a.$$typeof===k||a.$$typeof===n||a.$$typeof===w||a.$$typeof===x||a.$$typeof===y||a.$$typeof===v)};reactIs_production_min.typeOf=z;

var reactIs_development = {};

/** @license React v16.13.1
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */



if (process.env.NODE_ENV !== "production") {
  (function() {

// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
// nor polyfill, then a plain number is used for performance.
var hasSymbol = typeof Symbol === 'function' && Symbol.for;
var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
// (unstable) APIs that have been removed. Can we remove the symbols?

var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for('react.block') : 0xead9;
var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

function isValidElementType(type) {
  return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
  type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
}

function typeOf(object) {
  if (typeof object === 'object' && object !== null) {
    var $$typeof = object.$$typeof;

    switch ($$typeof) {
      case REACT_ELEMENT_TYPE:
        var type = object.type;

        switch (type) {
          case REACT_ASYNC_MODE_TYPE:
          case REACT_CONCURRENT_MODE_TYPE:
          case REACT_FRAGMENT_TYPE:
          case REACT_PROFILER_TYPE:
          case REACT_STRICT_MODE_TYPE:
          case REACT_SUSPENSE_TYPE:
            return type;

          default:
            var $$typeofType = type && type.$$typeof;

            switch ($$typeofType) {
              case REACT_CONTEXT_TYPE:
              case REACT_FORWARD_REF_TYPE:
              case REACT_LAZY_TYPE:
              case REACT_MEMO_TYPE:
              case REACT_PROVIDER_TYPE:
                return $$typeofType;

              default:
                return $$typeof;
            }

        }

      case REACT_PORTAL_TYPE:
        return $$typeof;
    }
  }

  return undefined;
} // AsyncMode is deprecated along with isAsyncMode

var AsyncMode = REACT_ASYNC_MODE_TYPE;
var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
var ContextConsumer = REACT_CONTEXT_TYPE;
var ContextProvider = REACT_PROVIDER_TYPE;
var Element = REACT_ELEMENT_TYPE;
var ForwardRef = REACT_FORWARD_REF_TYPE;
var Fragment = REACT_FRAGMENT_TYPE;
var Lazy = REACT_LAZY_TYPE;
var Memo = REACT_MEMO_TYPE;
var Portal = REACT_PORTAL_TYPE;
var Profiler = REACT_PROFILER_TYPE;
var StrictMode = REACT_STRICT_MODE_TYPE;
var Suspense = REACT_SUSPENSE_TYPE;
var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

function isAsyncMode(object) {
  {
    if (!hasWarnedAboutDeprecatedIsAsyncMode) {
      hasWarnedAboutDeprecatedIsAsyncMode = true; // Using console['warn'] to evade Babel and ESLint

      console['warn']('The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
    }
  }

  return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
}
function isConcurrentMode(object) {
  return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
}
function isContextConsumer(object) {
  return typeOf(object) === REACT_CONTEXT_TYPE;
}
function isContextProvider(object) {
  return typeOf(object) === REACT_PROVIDER_TYPE;
}
function isElement(object) {
  return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
}
function isForwardRef(object) {
  return typeOf(object) === REACT_FORWARD_REF_TYPE;
}
function isFragment(object) {
  return typeOf(object) === REACT_FRAGMENT_TYPE;
}
function isLazy(object) {
  return typeOf(object) === REACT_LAZY_TYPE;
}
function isMemo(object) {
  return typeOf(object) === REACT_MEMO_TYPE;
}
function isPortal(object) {
  return typeOf(object) === REACT_PORTAL_TYPE;
}
function isProfiler(object) {
  return typeOf(object) === REACT_PROFILER_TYPE;
}
function isStrictMode(object) {
  return typeOf(object) === REACT_STRICT_MODE_TYPE;
}
function isSuspense(object) {
  return typeOf(object) === REACT_SUSPENSE_TYPE;
}

reactIs_development.AsyncMode = AsyncMode;
reactIs_development.ConcurrentMode = ConcurrentMode;
reactIs_development.ContextConsumer = ContextConsumer;
reactIs_development.ContextProvider = ContextProvider;
reactIs_development.Element = Element;
reactIs_development.ForwardRef = ForwardRef;
reactIs_development.Fragment = Fragment;
reactIs_development.Lazy = Lazy;
reactIs_development.Memo = Memo;
reactIs_development.Portal = Portal;
reactIs_development.Profiler = Profiler;
reactIs_development.StrictMode = StrictMode;
reactIs_development.Suspense = Suspense;
reactIs_development.isAsyncMode = isAsyncMode;
reactIs_development.isConcurrentMode = isConcurrentMode;
reactIs_development.isContextConsumer = isContextConsumer;
reactIs_development.isContextProvider = isContextProvider;
reactIs_development.isElement = isElement;
reactIs_development.isForwardRef = isForwardRef;
reactIs_development.isFragment = isFragment;
reactIs_development.isLazy = isLazy;
reactIs_development.isMemo = isMemo;
reactIs_development.isPortal = isPortal;
reactIs_development.isProfiler = isProfiler;
reactIs_development.isStrictMode = isStrictMode;
reactIs_development.isSuspense = isSuspense;
reactIs_development.isValidElementType = isValidElementType;
reactIs_development.typeOf = typeOf;
  })();
}

if (process.env.NODE_ENV === 'production') {
  reactIs.exports = reactIs_production_min;
} else {
  reactIs.exports = reactIs_development;
}

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/
/* eslint-disable no-unused-vars */
var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var hasOwnProperty$1 = Object.prototype.hasOwnProperty;
var propIsEnumerable = Object.prototype.propertyIsEnumerable;

function toObject(val) {
	if (val === null || val === undefined) {
		throw new TypeError('Object.assign cannot be called with null or undefined');
	}

	return Object(val);
}

function shouldUseNative() {
	try {
		if (!Object.assign) {
			return false;
		}

		// Detect buggy property enumeration order in older V8 versions.

		// https://bugs.chromium.org/p/v8/issues/detail?id=4118
		var test1 = new String('abc');  // eslint-disable-line no-new-wrappers
		test1[5] = 'de';
		if (Object.getOwnPropertyNames(test1)[0] === '5') {
			return false;
		}

		// https://bugs.chromium.org/p/v8/issues/detail?id=3056
		var test2 = {};
		for (var i = 0; i < 10; i++) {
			test2['_' + String.fromCharCode(i)] = i;
		}
		var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
			return test2[n];
		});
		if (order2.join('') !== '0123456789') {
			return false;
		}

		// https://bugs.chromium.org/p/v8/issues/detail?id=3056
		var test3 = {};
		'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
			test3[letter] = letter;
		});
		if (Object.keys(Object.assign({}, test3)).join('') !==
				'abcdefghijklmnopqrst') {
			return false;
		}

		return true;
	} catch (err) {
		// We don't expect any of the above to throw, but better to be safe.
		return false;
	}
}

var objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
	var from;
	var to = toObject(target);
	var symbols;

	for (var s = 1; s < arguments.length; s++) {
		from = Object(arguments[s]);

		for (var key in from) {
			if (hasOwnProperty$1.call(from, key)) {
				to[key] = from[key];
			}
		}

		if (getOwnPropertySymbols) {
			symbols = getOwnPropertySymbols(from);
			for (var i = 0; i < symbols.length; i++) {
				if (propIsEnumerable.call(from, symbols[i])) {
					to[symbols[i]] = from[symbols[i]];
				}
			}
		}
	}

	return to;
};

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret$3 = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';

var ReactPropTypesSecret_1 = ReactPropTypesSecret$3;

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var printWarning$1 = function() {};

if (process.env.NODE_ENV !== 'production') {
  var ReactPropTypesSecret$2 = ReactPropTypesSecret_1;
  var loggedTypeFailures = {};
  var has$1 = Function.call.bind(Object.prototype.hasOwnProperty);

  printWarning$1 = function(text) {
    var message = 'Warning: ' + text;
    if (typeof console !== 'undefined') {
      console.error(message);
    }
    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };
}

/**
 * Assert that the values match with the type specs.
 * Error messages are memorized and will only be shown once.
 *
 * @param {object} typeSpecs Map of name to a ReactPropType
 * @param {object} values Runtime values that need to be type-checked
 * @param {string} location e.g. "prop", "context", "child context"
 * @param {string} componentName Name of the component for error messages.
 * @param {?Function} getStack Returns the component stack.
 * @private
 */
function checkPropTypes$1(typeSpecs, values, location, componentName, getStack) {
  if (process.env.NODE_ENV !== 'production') {
    for (var typeSpecName in typeSpecs) {
      if (has$1(typeSpecs, typeSpecName)) {
        var error;
        // Prop type validation may throw. In case they do, we don't want to
        // fail the render phase where it didn't fail before. So we log it.
        // After these have been cleaned up, we'll let them throw.
        try {
          // This is intentionally an invariant that gets caught. It's the same
          // behavior as without this statement except with a better message.
          if (typeof typeSpecs[typeSpecName] !== 'function') {
            var err = Error(
              (componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' +
              'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.'
            );
            err.name = 'Invariant Violation';
            throw err;
          }
          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret$2);
        } catch (ex) {
          error = ex;
        }
        if (error && !(error instanceof Error)) {
          printWarning$1(
            (componentName || 'React class') + ': type specification of ' +
            location + ' `' + typeSpecName + '` is invalid; the type checker ' +
            'function must return `null` or an `Error` but returned a ' + typeof error + '. ' +
            'You may have forgotten to pass an argument to the type checker ' +
            'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' +
            'shape all require an argument).'
          );
        }
        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
          // Only monitor this failure once because there tends to be a lot of the
          // same error.
          loggedTypeFailures[error.message] = true;

          var stack = getStack ? getStack() : '';

          printWarning$1(
            'Failed ' + location + ' type: ' + error.message + (stack != null ? stack : '')
          );
        }
      }
    }
  }
}

/**
 * Resets warning cache when testing.
 *
 * @private
 */
checkPropTypes$1.resetWarningCache = function() {
  if (process.env.NODE_ENV !== 'production') {
    loggedTypeFailures = {};
  }
};

var checkPropTypes_1 = checkPropTypes$1;

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactIs$1 = reactIs.exports;
var assign = objectAssign;

var ReactPropTypesSecret$1 = ReactPropTypesSecret_1;
var checkPropTypes = checkPropTypes_1;

var has = Function.call.bind(Object.prototype.hasOwnProperty);
var printWarning = function() {};

if (process.env.NODE_ENV !== 'production') {
  printWarning = function(text) {
    var message = 'Warning: ' + text;
    if (typeof console !== 'undefined') {
      console.error(message);
    }
    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };
}

function emptyFunctionThatReturnsNull() {
  return null;
}

var factoryWithTypeCheckers = function(isValidElement, throwOnDirectAccess) {
  /* global Symbol */
  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
  var FAUX_ITERATOR_SYMBOL = '@@iterator'; // Before Symbol spec.

  /**
   * Returns the iterator method function contained on the iterable object.
   *
   * Be sure to invoke the function with the iterable as context:
   *
   *     var iteratorFn = getIteratorFn(myIterable);
   *     if (iteratorFn) {
   *       var iterator = iteratorFn.call(myIterable);
   *       ...
   *     }
   *
   * @param {?object} maybeIterable
   * @return {?function}
   */
  function getIteratorFn(maybeIterable) {
    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);
    if (typeof iteratorFn === 'function') {
      return iteratorFn;
    }
  }

  /**
   * Collection of methods that allow declaration and validation of props that are
   * supplied to React components. Example usage:
   *
   *   var Props = require('ReactPropTypes');
   *   var MyArticle = React.createClass({
   *     propTypes: {
   *       // An optional string prop named "description".
   *       description: Props.string,
   *
   *       // A required enum prop named "category".
   *       category: Props.oneOf(['News','Photos']).isRequired,
   *
   *       // A prop named "dialog" that requires an instance of Dialog.
   *       dialog: Props.instanceOf(Dialog).isRequired
   *     },
   *     render: function() { ... }
   *   });
   *
   * A more formal specification of how these methods are used:
   *
   *   type := array|bool|func|object|number|string|oneOf([...])|instanceOf(...)
   *   decl := ReactPropTypes.{type}(.isRequired)?
   *
   * Each and every declaration produces a function with the same signature. This
   * allows the creation of custom validation functions. For example:
   *
   *  var MyLink = React.createClass({
   *    propTypes: {
   *      // An optional string or URI prop named "href".
   *      href: function(props, propName, componentName) {
   *        var propValue = props[propName];
   *        if (propValue != null && typeof propValue !== 'string' &&
   *            !(propValue instanceof URI)) {
   *          return new Error(
   *            'Expected a string or an URI for ' + propName + ' in ' +
   *            componentName
   *          );
   *        }
   *      }
   *    },
   *    render: function() {...}
   *  });
   *
   * @internal
   */

  var ANONYMOUS = '<<anonymous>>';

  // Important!
  // Keep this list in sync with production version in `./factoryWithThrowingShims.js`.
  var ReactPropTypes = {
    array: createPrimitiveTypeChecker('array'),
    bool: createPrimitiveTypeChecker('boolean'),
    func: createPrimitiveTypeChecker('function'),
    number: createPrimitiveTypeChecker('number'),
    object: createPrimitiveTypeChecker('object'),
    string: createPrimitiveTypeChecker('string'),
    symbol: createPrimitiveTypeChecker('symbol'),

    any: createAnyTypeChecker(),
    arrayOf: createArrayOfTypeChecker,
    element: createElementTypeChecker(),
    elementType: createElementTypeTypeChecker(),
    instanceOf: createInstanceTypeChecker,
    node: createNodeChecker(),
    objectOf: createObjectOfTypeChecker,
    oneOf: createEnumTypeChecker,
    oneOfType: createUnionTypeChecker,
    shape: createShapeTypeChecker,
    exact: createStrictShapeTypeChecker,
  };

  /**
   * inlined Object.is polyfill to avoid requiring consumers ship their own
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
   */
  /*eslint-disable no-self-compare*/
  function is(x, y) {
    // SameValue algorithm
    if (x === y) {
      // Steps 1-5, 7-10
      // Steps 6.b-6.e: +0 != -0
      return x !== 0 || 1 / x === 1 / y;
    } else {
      // Step 6.a: NaN == NaN
      return x !== x && y !== y;
    }
  }
  /*eslint-enable no-self-compare*/

  /**
   * We use an Error-like object for backward compatibility as people may call
   * PropTypes directly and inspect their output. However, we don't use real
   * Errors anymore. We don't inspect their stack anyway, and creating them
   * is prohibitively expensive if they are created too often, such as what
   * happens in oneOfType() for any type before the one that matched.
   */
  function PropTypeError(message) {
    this.message = message;
    this.stack = '';
  }
  // Make `instanceof Error` still work for returned errors.
  PropTypeError.prototype = Error.prototype;

  function createChainableTypeChecker(validate) {
    if (process.env.NODE_ENV !== 'production') {
      var manualPropTypeCallCache = {};
      var manualPropTypeWarningCount = 0;
    }
    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
      componentName = componentName || ANONYMOUS;
      propFullName = propFullName || propName;

      if (secret !== ReactPropTypesSecret$1) {
        if (throwOnDirectAccess) {
          // New behavior only for users of `prop-types` package
          var err = new Error(
            'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
            'Use `PropTypes.checkPropTypes()` to call them. ' +
            'Read more at http://fb.me/use-check-prop-types'
          );
          err.name = 'Invariant Violation';
          throw err;
        } else if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
          // Old behavior for people using React.PropTypes
          var cacheKey = componentName + ':' + propName;
          if (
            !manualPropTypeCallCache[cacheKey] &&
            // Avoid spamming the console because they are often not actionable except for lib authors
            manualPropTypeWarningCount < 3
          ) {
            printWarning(
              'You are manually calling a React.PropTypes validation ' +
              'function for the `' + propFullName + '` prop on `' + componentName  + '`. This is deprecated ' +
              'and will throw in the standalone `prop-types` package. ' +
              'You may be seeing this warning due to a third-party PropTypes ' +
              'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.'
            );
            manualPropTypeCallCache[cacheKey] = true;
            manualPropTypeWarningCount++;
          }
        }
      }
      if (props[propName] == null) {
        if (isRequired) {
          if (props[propName] === null) {
            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
          }
          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
        }
        return null;
      } else {
        return validate(props, propName, componentName, location, propFullName);
      }
    }

    var chainedCheckType = checkType.bind(null, false);
    chainedCheckType.isRequired = checkType.bind(null, true);

    return chainedCheckType;
  }

  function createPrimitiveTypeChecker(expectedType) {
    function validate(props, propName, componentName, location, propFullName, secret) {
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== expectedType) {
        // `propValue` being instance of, say, date/regexp, pass the 'object'
        // check, but we can offer a more precise error message here rather than
        // 'of type `object`'.
        var preciseType = getPreciseType(propValue);

        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createAnyTypeChecker() {
    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
  }

  function createArrayOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
      }
      var propValue = props[propName];
      if (!Array.isArray(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
      }
      for (var i = 0; i < propValue.length; i++) {
        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret$1);
        if (error instanceof Error) {
          return error;
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createElementTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      if (!isValidElement(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createElementTypeTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      if (!ReactIs$1.isValidElementType(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createInstanceTypeChecker(expectedClass) {
    function validate(props, propName, componentName, location, propFullName) {
      if (!(props[propName] instanceof expectedClass)) {
        var expectedClassName = expectedClass.name || ANONYMOUS;
        var actualClassName = getClassName(props[propName]);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createEnumTypeChecker(expectedValues) {
    if (!Array.isArray(expectedValues)) {
      if (process.env.NODE_ENV !== 'production') {
        if (arguments.length > 1) {
          printWarning(
            'Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' +
            'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).'
          );
        } else {
          printWarning('Invalid argument supplied to oneOf, expected an array.');
        }
      }
      return emptyFunctionThatReturnsNull;
    }

    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      for (var i = 0; i < expectedValues.length; i++) {
        if (is(propValue, expectedValues[i])) {
          return null;
        }
      }

      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
        var type = getPreciseType(value);
        if (type === 'symbol') {
          return String(value);
        }
        return value;
      });
      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
    }
    return createChainableTypeChecker(validate);
  }

  function createObjectOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
      }
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
      }
      for (var key in propValue) {
        if (has(propValue, key)) {
          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret$1);
          if (error instanceof Error) {
            return error;
          }
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createUnionTypeChecker(arrayOfTypeCheckers) {
    if (!Array.isArray(arrayOfTypeCheckers)) {
      process.env.NODE_ENV !== 'production' ? printWarning('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
      return emptyFunctionThatReturnsNull;
    }

    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
      var checker = arrayOfTypeCheckers[i];
      if (typeof checker !== 'function') {
        printWarning(
          'Invalid argument supplied to oneOfType. Expected an array of check functions, but ' +
          'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.'
        );
        return emptyFunctionThatReturnsNull;
      }
    }

    function validate(props, propName, componentName, location, propFullName) {
      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
        var checker = arrayOfTypeCheckers[i];
        if (checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret$1) == null) {
          return null;
        }
      }

      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`.'));
    }
    return createChainableTypeChecker(validate);
  }

  function createNodeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      if (!isNode(props[propName])) {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      }
      for (var key in shapeTypes) {
        var checker = shapeTypes[key];
        if (!checker) {
          continue;
        }
        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret$1);
        if (error) {
          return error;
        }
      }
      return null;
    }
    return createChainableTypeChecker(validate);
  }

  function createStrictShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);
      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      }
      // We need to check all keys in case some are required but missing from
      // props.
      var allKeys = assign({}, props[propName], shapeTypes);
      for (var key in allKeys) {
        var checker = shapeTypes[key];
        if (!checker) {
          return new PropTypeError(
            'Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' +
            '\nBad object: ' + JSON.stringify(props[propName], null, '  ') +
            '\nValid keys: ' +  JSON.stringify(Object.keys(shapeTypes), null, '  ')
          );
        }
        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret$1);
        if (error) {
          return error;
        }
      }
      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function isNode(propValue) {
    switch (typeof propValue) {
      case 'number':
      case 'string':
      case 'undefined':
        return true;
      case 'boolean':
        return !propValue;
      case 'object':
        if (Array.isArray(propValue)) {
          return propValue.every(isNode);
        }
        if (propValue === null || isValidElement(propValue)) {
          return true;
        }

        var iteratorFn = getIteratorFn(propValue);
        if (iteratorFn) {
          var iterator = iteratorFn.call(propValue);
          var step;
          if (iteratorFn !== propValue.entries) {
            while (!(step = iterator.next()).done) {
              if (!isNode(step.value)) {
                return false;
              }
            }
          } else {
            // Iterator will provide entry [k,v] tuples rather than values.
            while (!(step = iterator.next()).done) {
              var entry = step.value;
              if (entry) {
                if (!isNode(entry[1])) {
                  return false;
                }
              }
            }
          }
        } else {
          return false;
        }

        return true;
      default:
        return false;
    }
  }

  function isSymbol(propType, propValue) {
    // Native Symbol.
    if (propType === 'symbol') {
      return true;
    }

    // falsy value can't be a Symbol
    if (!propValue) {
      return false;
    }

    // 19.4.3.5 Symbol.prototype[@@toStringTag] === 'Symbol'
    if (propValue['@@toStringTag'] === 'Symbol') {
      return true;
    }

    // Fallback for non-spec compliant Symbols which are polyfilled.
    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
      return true;
    }

    return false;
  }

  // Equivalent of `typeof` but with special handling for array and regexp.
  function getPropType(propValue) {
    var propType = typeof propValue;
    if (Array.isArray(propValue)) {
      return 'array';
    }
    if (propValue instanceof RegExp) {
      // Old webkits (at least until Android 4.0) return 'function' rather than
      // 'object' for typeof a RegExp. We'll normalize this here so that /bla/
      // passes PropTypes.object.
      return 'object';
    }
    if (isSymbol(propType, propValue)) {
      return 'symbol';
    }
    return propType;
  }

  // This handles more types than `getPropType`. Only used for error messages.
  // See `createPrimitiveTypeChecker`.
  function getPreciseType(propValue) {
    if (typeof propValue === 'undefined' || propValue === null) {
      return '' + propValue;
    }
    var propType = getPropType(propValue);
    if (propType === 'object') {
      if (propValue instanceof Date) {
        return 'date';
      } else if (propValue instanceof RegExp) {
        return 'regexp';
      }
    }
    return propType;
  }

  // Returns a string that is postfixed to a warning about an invalid type.
  // For example, "undefined" or "of type array"
  function getPostfixForTypeWarning(value) {
    var type = getPreciseType(value);
    switch (type) {
      case 'array':
      case 'object':
        return 'an ' + type;
      case 'boolean':
      case 'date':
      case 'regexp':
        return 'a ' + type;
      default:
        return type;
    }
  }

  // Returns class name of the object, if any.
  function getClassName(propValue) {
    if (!propValue.constructor || !propValue.constructor.name) {
      return ANONYMOUS;
    }
    return propValue.constructor.name;
  }

  ReactPropTypes.checkPropTypes = checkPropTypes;
  ReactPropTypes.resetWarningCache = checkPropTypes.resetWarningCache;
  ReactPropTypes.PropTypes = ReactPropTypes;

  return ReactPropTypes;
};

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret = ReactPropTypesSecret_1;

function emptyFunction() {}
function emptyFunctionWithReset() {}
emptyFunctionWithReset.resetWarningCache = emptyFunction;

var factoryWithThrowingShims = function() {
  function shim(props, propName, componentName, location, propFullName, secret) {
    if (secret === ReactPropTypesSecret) {
      // It is still safe when called from React.
      return;
    }
    var err = new Error(
      'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
      'Use PropTypes.checkPropTypes() to call them. ' +
      'Read more at http://fb.me/use-check-prop-types'
    );
    err.name = 'Invariant Violation';
    throw err;
  }  shim.isRequired = shim;
  function getShim() {
    return shim;
  }  // Important!
  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.
  var ReactPropTypes = {
    array: shim,
    bool: shim,
    func: shim,
    number: shim,
    object: shim,
    string: shim,
    symbol: shim,

    any: shim,
    arrayOf: getShim,
    element: shim,
    elementType: shim,
    instanceOf: getShim,
    node: shim,
    objectOf: getShim,
    oneOf: getShim,
    oneOfType: getShim,
    shape: getShim,
    exact: getShim,

    checkPropTypes: emptyFunctionWithReset,
    resetWarningCache: emptyFunction
  };

  ReactPropTypes.PropTypes = ReactPropTypes;

  return ReactPropTypes;
};

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

if (process.env.NODE_ENV !== 'production') {
  var ReactIs = reactIs.exports;

  // By explicitly using `prop-types` you are opting into new development behavior.
  // http://fb.me/prop-types-in-prod
  var throwOnDirectAccess = true;
  propTypes.exports = factoryWithTypeCheckers(ReactIs.isElement, throwOnDirectAccess);
} else {
  // By explicitly using `prop-types` you are opting into new production behavior.
  // http://fb.me/prop-types-in-prod
  propTypes.exports = factoryWithThrowingShims();
}

var PropTypes = propTypes.exports;

var classnames$1 = {exports: {}};

/*!
  Copyright (c) 2018 Jed Watson.
  Licensed under the MIT License (MIT), see
  http://jedwatson.github.io/classnames
*/

(function (module) {
/* global define */

(function () {

	var hasOwn = {}.hasOwnProperty;

	function classNames() {
		var classes = [];

		for (var i = 0; i < arguments.length; i++) {
			var arg = arguments[i];
			if (!arg) continue;

			var argType = typeof arg;

			if (argType === 'string' || argType === 'number') {
				classes.push(arg);
			} else if (Array.isArray(arg)) {
				if (arg.length) {
					var inner = classNames.apply(null, arg);
					if (inner) {
						classes.push(inner);
					}
				}
			} else if (argType === 'object') {
				if (arg.toString === Object.prototype.toString) {
					for (var key in arg) {
						if (hasOwn.call(arg, key) && arg[key]) {
							classes.push(key);
						}
					}
				} else {
					classes.push(arg.toString());
				}
			}
		}

		return classes.join(' ');
	}

	if (module.exports) {
		classNames.default = classNames;
		module.exports = classNames;
	} else {
		window.classNames = classNames;
	}
}());
}(classnames$1));

var classnames = classnames$1.exports;

// Edits text `value` (if `operation` is passed) and repositions the `caret` if needed.
//
// Example:
//
// value - '88005553535'
// caret - 2 // starting from 0; is positioned before the first zero
// operation - 'Backspace'
//
// Returns
// {
// 	value: '8005553535'
// 	caret: 1
// }
//
// Currently supports just 'Delete' and 'Backspace' operations
//
function edit(value, caret, operation) {
  switch (operation) {
    case 'Backspace':
      // If there exists the previous character,
      // then erase it and reposition the caret.
      if (caret > 0) {
        // Remove the previous character
        value = value.slice(0, caret - 1) + value.slice(caret); // Position the caret where the previous (erased) character was

        caret--;
      }

      break;

    case 'Delete':
      // Remove current digit (if any)
      value = value.slice(0, caret) + value.slice(caret + 1);
      break;
  }

  return {
    value: value,
    caret: caret
  };
}

// Parses the `text`.
//
// Returns `{ value, caret }` where `caret` is
// the caret position inside `value`
// corresponding to the `caret_position` inside `text`.
//
// The `text` is parsed by feeding each character sequentially to
// `parse_character(character, value)` function
// and appending the result (if it's not `undefined`) to `value`.
//
// Example:
//
// `text` is `8 (800) 555-35-35`,
// `caret_position` is `4` (before the first `0`).
// `parse_character` is `(character, value) =>
//   if (character >= '0' && character <= '9') { return character }`.
//
// then `parse()` outputs `{ value: '88005553535', caret: 2 }`.
//
function parse$1(text, caret_position, parse_character) {
  var value = '';
  var focused_input_character_index = 0;
  var index = 0;

  while (index < text.length) {
    var character = parse_character(text[index], value);

    if (character !== undefined) {
      value += character;

      if (caret_position !== undefined) {
        if (caret_position === index) {
          focused_input_character_index = value.length - 1;
        } else if (caret_position > index) {
          focused_input_character_index = value.length;
        }
      }
    }

    index++;
  } // If caret position wasn't specified


  if (caret_position === undefined) {
    // Then set caret position to "after the last input character"
    focused_input_character_index = value.length;
  }

  var result = {
    value: value,
    caret: focused_input_character_index
  };
  return result;
}

// Counts all occurences of a symbol in a string
function count_occurences(symbol, string) {
  var count = 0; // Using `.split('')` here instead of normal `for ... of`
  // because the importing application doesn't neccessarily include an ES6 polyfill.
  // The `.split('')` approach discards "exotic" UTF-8 characters
  // (the ones consisting of four bytes)
  // but template placeholder characters don't fall into that range
  // so skipping such miscellaneous "exotic" characters
  // won't matter here for just counting placeholder character occurrences.

  for (var _iterator = string.split(''), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var character = _ref;

    if (character === symbol) {
      count++;
    }
  }

  return count;
}

function closeBraces(retained_template, template) {
  var placeholder = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'x';
  var empty_placeholder = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : ' ';
  var cut_before = retained_template.length;
  var opening_braces = count_occurences('(', retained_template);
  var closing_braces = count_occurences(')', retained_template);
  var dangling_braces = opening_braces - closing_braces;

  while (dangling_braces > 0 && cut_before < template.length) {
    retained_template += template[cut_before].replace(placeholder, empty_placeholder);

    if (template[cut_before] === ')') {
      dangling_braces--;
    }

    cut_before++;
  }

  return retained_template;
}

// are denoted by 'x'es (e.g. 'x (xxx) xxx-xx-xx').
//
// Returns a function which takes `value` characters
// and returns the `template` filled with those characters.
// If the `template` can only be partially filled
// then it is cut off.
//
// If `should_close_braces` is `true`,
// then it will also make sure all dangling braces are closed,
// e.g. "8 (8" -> "8 (8  )" (iPhone style phone number input).
//

function template_formatter (template) {
  var placeholder = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'x';
  var should_close_braces = arguments.length > 2 ? arguments[2] : undefined;

  if (!template) {
    return function (value) {
      return {
        text: value
      };
    };
  }

  var characters_in_template = count_occurences(placeholder, template);
  return function (value) {
    if (!value) {
      return {
        text: '',
        template: template
      };
    }

    var value_character_index = 0;
    var filled_in_template = ''; // Using `.split('')` here instead of normal `for ... of`
    // because the importing application doesn't neccessarily include an ES6 polyfill.
    // The `.split('')` approach discards "exotic" UTF-8 characters
    // (the ones consisting of four bytes)
    // but template placeholder characters don't fall into that range
    // and appending UTF-8 characters to a string in parts still works.

    for (var _iterator = template.split(''), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
      var _ref;

      if (_isArray) {
        if (_i >= _iterator.length) break;
        _ref = _iterator[_i++];
      } else {
        _i = _iterator.next();
        if (_i.done) break;
        _ref = _i.value;
      }

      var character = _ref;

      if (character !== placeholder) {
        filled_in_template += character;
        continue;
      }

      filled_in_template += value[value_character_index];
      value_character_index++; // If the last available value character has been filled in,
      // then return the filled in template
      // (either trim the right part or retain it,
      //  if no more character placeholders in there)

      if (value_character_index === value.length) {
        // If there are more character placeholders
        // in the right part of the template
        // then simply trim it.
        if (value.length < characters_in_template) {
          break;
        }
      }
    }

    if (should_close_braces) {
      filled_in_template = closeBraces(filled_in_template, template);
    }

    return {
      text: filled_in_template,
      template: template
    };
  };
}

//
// `{ value, caret }` attribute is the result of `parse()` function call.
//
// Returns `{ text, caret }` where the new `caret` is the caret position
// inside `text` text corresponding to the original `caret` position inside `value`.
//
// `formatter(value)` is a function returning `{ text, template }`.
//
// `text` is the `value` value formatted using `template`.
// It may either cut off the non-filled right part of the `template`
// or it may fill the non-filled character placeholders
// in the right part of the `template` with `spacer`
// which is a space (' ') character by default.
//
// `template` is the template used to format the `value`.
// It can be either a full-length template or a partial template.
//
// `formatter` can also be a string — a `template`
// where character placeholders are denoted by 'x'es.
// In this case `formatter` function is automatically created.
//
// Example:
//
// `value` is '880',
// `caret` is `2` (before the first `0`)
//
// `formatter` is `'880' =>
//   { text: '8 (80 )', template: 'x (xxx) xxx-xx-xx' }`
//
// The result is `{ text: '8 (80 )', caret: 4 }`.
//

function format$1(value, caret, formatter) {
  if (typeof formatter === 'string') {
    formatter = template_formatter(formatter);
  }

  var _ref = formatter(value) || {},
      text = _ref.text,
      template = _ref.template;

  if (text === undefined) {
    text = value;
  }

  if (template) {
    if (caret === undefined) {
      caret = text.length;
    } else {
      var index = 0;
      var found = false;
      var possibly_last_input_character_index = -1;

      while (index < text.length && index < template.length) {
        // Character placeholder found
        if (text[index] !== template[index]) {
          if (caret === 0) {
            found = true;
            caret = index;
            break;
          }

          possibly_last_input_character_index = index;
          caret--;
        }

        index++;
      } // If the caret was positioned after last input character,
      // then the text caret index is just after the last input character.


      if (!found) {
        caret = possibly_last_input_character_index + 1;
      }
    }
  }

  return {
    text: text,
    caret: caret
  };
}

// Gets <input/> selection bounds
function getSelection(element) {
  // If no selection, return nothing
  if (element.selectionStart === element.selectionEnd) {
    return;
  }

  return {
    start: element.selectionStart,
    end: element.selectionEnd
  };
} // Key codes

var Keys = {
  Backspace: 8,
  Delete: 46
}; // Finds out the operation to be intercepted and performed
// based on the key down event `keyCode`.

function getOperation(event) {
  switch (event.keyCode) {
    case Keys.Backspace:
      return 'Backspace';

    case Keys.Delete:
      return 'Delete';
  }
} // Gets <input/> caret position

function getCaretPosition(element) {
  return element.selectionStart;
} // Sets <input/> caret position

function setCaretPosition(element, caret_position) {
  // Sanity check
  if (caret_position === undefined) {
    return;
  } // Set caret position.
  // There has been an issue with caret positioning on Android devices.
  // https://github.com/catamphetamine/input-format/issues/2
  // I was revisiting this issue and looked for similar issues in other libraries.
  // For example, there's [`text-mask`](https://github.com/text-mask/text-mask) library.
  // They've had exactly the same issue when the caret seemingly refused to be repositioned programmatically.
  // The symptoms were the same: whenever the caret passed through a non-digit character of a mask (a whitespace, a bracket, a dash, etc), it looked as if it placed itself one character before its correct position.
  // https://github.com/text-mask/text-mask/issues/300
  // They seem to have found a basic fix for it: calling `input.setSelectionRange()` in a timeout rather than instantly for Android devices.
  // https://github.com/text-mask/text-mask/pull/400/files
  // I've implemented the same workaround here.


  if (isAndroid()) {
    setTimeout(function () {
      return element.setSelectionRange(caret_position, caret_position);
    }, 0);
  } else {
    element.setSelectionRange(caret_position, caret_position);
  }
}

function isAndroid() {
  // `navigator` is not defined when running mocha tests.
  if (typeof navigator !== 'undefined') {
    return ANDROID_USER_AGENT_REG_EXP.test(navigator.userAgent);
  }
}

var ANDROID_USER_AGENT_REG_EXP = /Android/i;

function onChange(event, input, _parse, _format, on_change) {
  formatInputText(input, _parse, _format, undefined, on_change);
} // "Delete" and "Backspace" keys are special
// in a way that they're not handled by the regular `onChange()` handler
// and instead are intercepted and re-applied manually.
// The reason is that normally hitting "Backspace" or "Delete"
// results in erasing a character, but that character might be any character,
// while it would be a better "user experience" if it erased not just any character
// but the closest "meaningful" character.
// For example, if a template is `(xxx) xxx-xxxx`,
// and the `<input/>` value is `(111) 222-3333`,
// then, if a user begins erasing the `3333` part via "Backspace"
// and reaches the "-" character, then it would just erase the "-" character.
// Nothing wrong with that, but it would be a better "user experience"
// if hitting "Backspace" at that position would erase the closest "meaningful"
// character, which would be the rightmost `2`.
// So, what this `onKeyDown()` handler does is it intercepts
// "Backspace" and "Delete" keys and re-applies those operations manually
// following the logic described above.

function onKeyDown(event, input, _parse, _format, on_change) {
  var operation = getOperation(event);

  switch (operation) {
    case 'Delete':
    case 'Backspace':
      // Intercept this operation and perform it manually.
      event.preventDefault();
      var selection = getSelection(input); // If a selection is made, just erase the selected text.

      if (selection) {
        eraseSelection(input, selection);
        return formatInputText(input, _parse, _format, undefined, on_change);
      } // Else, perform the (character erasing) operation manually.


      return formatInputText(input, _parse, _format, operation, on_change);

  }
}
/**
 * Erases the selected text inside an `<input/>`.
 * @param  {DOMElement} input
 * @param  {Selection} selection
 */

function eraseSelection(input, selection) {
  var text = input.value;
  text = text.slice(0, selection.start) + text.slice(selection.end);
  input.value = text;
  setCaretPosition(input, selection.start);
}
/**
 * Parses and re-formats `<input/>` textual value.
 * E.g. when a user enters something into the `<input/>`
 * that raw input must first be parsed and the re-formatted properly.
 * Is called either after some user input (e.g. entered a character, pasted something)
 * or after the user performed an `operation` (e.g. "Backspace", "Delete").
 * @param  {DOMElement} input
 * @param  {Function} parse
 * @param  {Function} format
 * @param  {string} [operation] - The operation that triggered `<input/>` textual value change. E.g. "Backspace", "Delete".
 * @param  {Function} onChange
 */


function formatInputText(input, _parse, _format, operation, on_change) {
  // Parse `<input/>` textual value.
  // Get the `value` and `caret` position.
  var _parse2 = parse$1(input.value, getCaretPosition(input), _parse),
      value = _parse2.value,
      caret = _parse2.caret; // If a user performed an operation ("Backspace", "Delete")
  // then apply that operation and get the new `value` and `caret` position.


  if (operation) {
    var newValueAndCaret = edit(value, caret, operation);
    value = newValueAndCaret.value;
    caret = newValueAndCaret.caret;
  } // Format the `value`.
  // (and reposition the caret accordingly)


  var formatted = format$1(value, caret, _format);
  var text = formatted.text;
  caret = formatted.caret; // Set `<input/>` textual value manually
  // to prevent React from resetting the caret position
  // later inside a subsequent `render()`.
  // Doesn't work for custom `inputComponent`s for some reason.

  input.value = text; // Position the caret properly.

  setCaretPosition(input, caret); // If the `<input/>` textual value did change,
  // then the parsed `value` may have changed too.

  on_change(value);
}

function _extends$m() { _extends$m = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$m.apply(this, arguments); }

function _objectWithoutProperties$7(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$7(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$7(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
//
// <ReactInput
// 	value={this.state.phone}
// 	onChange={phone => this.setState({ phone })}
// 	parse={character => character}
// 	format={value => ({ text: value, template: 'xxxxxxxx' })}/>
//

function Input(_ref, ref) {
  var value = _ref.value,
      parse = _ref.parse,
      format = _ref.format,
      InputComponent = _ref.inputComponent,
      onChange$1 = _ref.onChange,
      onKeyDown$1 = _ref.onKeyDown,
      rest = _objectWithoutProperties$7(_ref, ["value", "parse", "format", "inputComponent", "onChange", "onKeyDown"]);

  var internalRef = useRef();
  var setRef = useCallback(function (instance) {
    internalRef.current = instance;

    if (ref) {
      if (typeof ref === 'function') {
        ref(instance);
      } else {
        ref.current = instance;
      }
    }
  }, [ref]);

  var _onChange = useCallback(function (event) {
    return onChange(event, internalRef.current, parse, format, onChange$1);
  }, [internalRef, parse, format, onChange$1]);

  var _onKeyDown = useCallback(function (event) {
    if (onKeyDown$1) {
      onKeyDown$1(event);
    }

    return onKeyDown(event, internalRef.current, parse, format, onChange$1);
  }, [internalRef, parse, format, onChange$1, onKeyDown$1]);

  return React.createElement(InputComponent, _extends$m({}, rest, {
    ref: setRef,
    value: format(isEmptyValue(value) ? '' : value).text,
    onKeyDown: _onKeyDown,
    onChange: _onChange
  }));
}

Input = React.forwardRef(Input);
Input.propTypes = {
  // Parses a single characher of `<input/>` text.
  parse: PropTypes.func.isRequired,
  // Formats `value` into `<input/>` text.
  format: PropTypes.func.isRequired,
  // Renders `<input/>` by default.
  inputComponent: PropTypes.elementType.isRequired,
  // `<input/>` `type` attribute.
  type: PropTypes.string.isRequired,
  // Is parsed from <input/> text.
  value: PropTypes.string,
  // This handler is called each time `<input/>` text is changed.
  onChange: PropTypes.func.isRequired,
  // Passthrough
  onKeyDown: PropTypes.func,
  onCut: PropTypes.func,
  onPaste: PropTypes.func
};
Input.defaultProps = {
  // Renders `<input/>` by default.
  inputComponent: 'input',
  // `<input/>` `type` attribute.
  type: 'text'
};
var Input$1 = Input;

function isEmptyValue(value) {
  return value === undefined || value === null;
}

function _classCallCheck$9(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

// https://stackoverflow.com/a/46971044/970769
var ParseError = function ParseError(code) {
  _classCallCheck$9(this, ParseError);

  this.name = this.constructor.name;
  this.message = code;
  this.stack = new Error(code).stack;
};
ParseError.prototype = Object.create(Error.prototype);
ParseError.prototype.constructor = ParseError;

// The minimum length of the national significant number.
var MIN_LENGTH_FOR_NSN = 2; // The ITU says the maximum length should be 15,
// but one can find longer numbers in Germany.

var MAX_LENGTH_FOR_NSN = 17; // The maximum length of the country calling code.

var MAX_LENGTH_COUNTRY_CODE = 3; // Digits accepted in phone numbers
// (ascii, fullwidth, arabic-indic, and eastern arabic digits).

var VALID_DIGITS = "0-9\uFF10-\uFF19\u0660-\u0669\u06F0-\u06F9"; // `DASHES` will be right after the opening square bracket of the "character class"

var DASHES = "-\u2010-\u2015\u2212\u30FC\uFF0D";
var SLASHES = "\uFF0F/";
var DOTS = "\uFF0E.";
var WHITESPACE = " \xA0\xAD\u200B\u2060\u3000";
var BRACKETS = "()\uFF08\uFF09\uFF3B\uFF3D\\[\\]"; // export const OPENING_BRACKETS = '(\uFF08\uFF3B\\\['

var TILDES = "~\u2053\u223C\uFF5E"; // Regular expression of acceptable punctuation found in phone numbers. This
// excludes punctuation found as a leading character only. This consists of dash
// characters, white space characters, full stops, slashes, square brackets,
// parentheses and tildes. Full-width variants are also present.

var VALID_PUNCTUATION = "".concat(DASHES).concat(SLASHES).concat(DOTS).concat(WHITESPACE).concat(BRACKETS).concat(TILDES);
var PLUS_CHARS = "+\uFF0B"; // const LEADING_PLUS_CHARS_PATTERN = new RegExp('^[' + PLUS_CHARS + ']+')

// Copy-pasted from:
// https://github.com/substack/semver-compare/blob/master/index.js
//
// Inlining this function because some users reported issues with
// importing from `semver-compare` in a browser with ES6 "native" modules.
//
// Fixes `semver-compare` not being able to compare versions with alpha/beta/etc "tags".
// https://github.com/catamphetamine/libphonenumber-js/issues/381
function compare (a, b) {
  a = a.split('-');
  b = b.split('-');
  var pa = a[0].split('.');
  var pb = b[0].split('.');

  for (var i = 0; i < 3; i++) {
    var na = Number(pa[i]);
    var nb = Number(pb[i]);
    if (na > nb) return 1;
    if (nb > na) return -1;
    if (!isNaN(na) && isNaN(nb)) return 1;
    if (isNaN(na) && !isNaN(nb)) return -1;
  }

  if (a[1] && b[1]) {
    return a[1] > b[1] ? 1 : a[1] < b[1] ? -1 : 0;
  }

  return !a[1] && b[1] ? 1 : a[1] && !b[1] ? -1 : 0;
}

function _typeof$5(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$5 = function _typeof(obj) { return typeof obj; }; } else { _typeof$5 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$5(obj); }

function _classCallCheck$8(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$8(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$8(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$8(Constructor.prototype, protoProps); if (staticProps) _defineProperties$8(Constructor, staticProps); return Constructor; }

var V3 = '1.2.0'; // Moved `001` country code to "nonGeographic" section of metadata.

var V4 = '1.7.35';
var DEFAULT_EXT_PREFIX = ' ext. ';
var CALLING_CODE_REG_EXP = /^\d+$/;
/**
 * See: https://gitlab.com/catamphetamine/libphonenumber-js/blob/master/METADATA.md
 */

var Metadata =
/*#__PURE__*/
function () {
  function Metadata(metadata) {
    _classCallCheck$8(this, Metadata);

    validateMetadata(metadata);
    this.metadata = metadata;
    setVersion.call(this, metadata);
  }

  _createClass$8(Metadata, [{
    key: "getCountries",
    value: function getCountries() {
      return Object.keys(this.metadata.countries).filter(function (_) {
        return _ !== '001';
      });
    }
  }, {
    key: "getCountryMetadata",
    value: function getCountryMetadata(countryCode) {
      return this.metadata.countries[countryCode];
    }
  }, {
    key: "nonGeographic",
    value: function nonGeographic() {
      if (this.v1 || this.v2 || this.v3) return; // `nonGeographical` was a typo.
      // It's present in metadata generated from `1.7.35` to `1.7.37`.

      return this.metadata.nonGeographic || this.metadata.nonGeographical;
    }
  }, {
    key: "hasCountry",
    value: function hasCountry(country) {
      return this.getCountryMetadata(country) !== undefined;
    }
  }, {
    key: "hasCallingCode",
    value: function hasCallingCode(callingCode) {
      if (this.getCountryCodesForCallingCode(callingCode)) {
        return true;
      }

      if (this.nonGeographic()) {
        if (this.nonGeographic()[callingCode]) {
          return true;
        }
      } else {
        // A hacky workaround for old custom metadata (generated before V4).
        var countryCodes = this.countryCallingCodes()[callingCode];

        if (countryCodes && countryCodes.length === 1 && countryCodes[0] === '001') {
          return true;
        }
      }
    }
  }, {
    key: "isNonGeographicCallingCode",
    value: function isNonGeographicCallingCode(callingCode) {
      if (this.nonGeographic()) {
        return this.nonGeographic()[callingCode] ? true : false;
      } else {
        return this.getCountryCodesForCallingCode(callingCode) ? false : true;
      }
    } // Deprecated.

  }, {
    key: "country",
    value: function country(countryCode) {
      return this.selectNumberingPlan(countryCode);
    }
  }, {
    key: "selectNumberingPlan",
    value: function selectNumberingPlan(countryCode, callingCode) {
      // Supports just passing `callingCode` as the first argument.
      if (countryCode && CALLING_CODE_REG_EXP.test(countryCode)) {
        callingCode = countryCode;
        countryCode = null;
      }

      if (countryCode && countryCode !== '001') {
        if (!this.hasCountry(countryCode)) {
          throw new Error("Unknown country: ".concat(countryCode));
        }

        this.numberingPlan = new NumberingPlan(this.getCountryMetadata(countryCode), this);
      } else if (callingCode) {
        if (!this.hasCallingCode(callingCode)) {
          throw new Error("Unknown calling code: ".concat(callingCode));
        }

        this.numberingPlan = new NumberingPlan(this.getNumberingPlanMetadata(callingCode), this);
      } else {
        this.numberingPlan = undefined;
      }

      return this;
    }
  }, {
    key: "getCountryCodesForCallingCode",
    value: function getCountryCodesForCallingCode(callingCode) {
      var countryCodes = this.countryCallingCodes()[callingCode];

      if (countryCodes) {
        // Metadata before V4 included "non-geographic entity" calling codes
        // inside `country_calling_codes` (for example, `"881":["001"]`).
        // Now the semantics of `country_calling_codes` has changed:
        // it's specifically for "countries" now.
        // Older versions of custom metadata will simply skip parsing
        // "non-geographic entity" phone numbers with new versions
        // of this library: it's not considered a bug,
        // because such numbers are extremely rare,
        // and developers extremely rarely use custom metadata.
        if (countryCodes.length === 1 && countryCodes[0].length === 3) {
          return;
        }

        return countryCodes;
      }
    }
  }, {
    key: "getCountryCodeForCallingCode",
    value: function getCountryCodeForCallingCode(callingCode) {
      var countryCodes = this.getCountryCodesForCallingCode(callingCode);

      if (countryCodes) {
        return countryCodes[0];
      }
    }
  }, {
    key: "getNumberingPlanMetadata",
    value: function getNumberingPlanMetadata(callingCode) {
      var countryCode = this.getCountryCodeForCallingCode(callingCode);

      if (countryCode) {
        return this.getCountryMetadata(countryCode);
      }

      if (this.nonGeographic()) {
        var metadata = this.nonGeographic()[callingCode];

        if (metadata) {
          return metadata;
        }
      } else {
        // A hacky workaround for old custom metadata (generated before V4).
        var countryCodes = this.countryCallingCodes()[callingCode];

        if (countryCodes && countryCodes.length === 1 && countryCodes[0] === '001') {
          return this.metadata.countries['001'];
        }
      }
    } // Deprecated.

  }, {
    key: "countryCallingCode",
    value: function countryCallingCode() {
      return this.numberingPlan.callingCode();
    } // Deprecated.

  }, {
    key: "IDDPrefix",
    value: function IDDPrefix() {
      return this.numberingPlan.IDDPrefix();
    } // Deprecated.

  }, {
    key: "defaultIDDPrefix",
    value: function defaultIDDPrefix() {
      return this.numberingPlan.defaultIDDPrefix();
    } // Deprecated.

  }, {
    key: "nationalNumberPattern",
    value: function nationalNumberPattern() {
      return this.numberingPlan.nationalNumberPattern();
    } // Deprecated.

  }, {
    key: "possibleLengths",
    value: function possibleLengths() {
      return this.numberingPlan.possibleLengths();
    } // Deprecated.

  }, {
    key: "formats",
    value: function formats() {
      return this.numberingPlan.formats();
    } // Deprecated.

  }, {
    key: "nationalPrefixForParsing",
    value: function nationalPrefixForParsing() {
      return this.numberingPlan.nationalPrefixForParsing();
    } // Deprecated.

  }, {
    key: "nationalPrefixTransformRule",
    value: function nationalPrefixTransformRule() {
      return this.numberingPlan.nationalPrefixTransformRule();
    } // Deprecated.

  }, {
    key: "leadingDigits",
    value: function leadingDigits() {
      return this.numberingPlan.leadingDigits();
    } // Deprecated.

  }, {
    key: "hasTypes",
    value: function hasTypes() {
      return this.numberingPlan.hasTypes();
    } // Deprecated.

  }, {
    key: "type",
    value: function type(_type) {
      return this.numberingPlan.type(_type);
    } // Deprecated.

  }, {
    key: "ext",
    value: function ext() {
      return this.numberingPlan.ext();
    }
  }, {
    key: "countryCallingCodes",
    value: function countryCallingCodes() {
      if (this.v1) return this.metadata.country_phone_code_to_countries;
      return this.metadata.country_calling_codes;
    } // Deprecated.

  }, {
    key: "chooseCountryByCountryCallingCode",
    value: function chooseCountryByCountryCallingCode(callingCode) {
      return this.selectNumberingPlan(callingCode);
    }
  }, {
    key: "hasSelectedNumberingPlan",
    value: function hasSelectedNumberingPlan() {
      return this.numberingPlan !== undefined;
    }
  }]);

  return Metadata;
}();

var NumberingPlan =
/*#__PURE__*/
function () {
  function NumberingPlan(metadata, globalMetadataObject) {
    _classCallCheck$8(this, NumberingPlan);

    this.globalMetadataObject = globalMetadataObject;
    this.metadata = metadata;
    setVersion.call(this, globalMetadataObject.metadata);
  }

  _createClass$8(NumberingPlan, [{
    key: "callingCode",
    value: function callingCode() {
      return this.metadata[0];
    } // Formatting information for regions which share
    // a country calling code is contained by only one region
    // for performance reasons. For example, for NANPA region
    // ("North American Numbering Plan Administration",
    //  which includes USA, Canada, Cayman Islands, Bahamas, etc)
    // it will be contained in the metadata for `US`.

  }, {
    key: "getDefaultCountryMetadataForRegion",
    value: function getDefaultCountryMetadataForRegion() {
      return this.globalMetadataObject.getNumberingPlanMetadata(this.callingCode());
    } // Is always present.

  }, {
    key: "IDDPrefix",
    value: function IDDPrefix() {
      if (this.v1 || this.v2) return;
      return this.metadata[1];
    } // Is only present when a country supports multiple IDD prefixes.

  }, {
    key: "defaultIDDPrefix",
    value: function defaultIDDPrefix() {
      if (this.v1 || this.v2) return;
      return this.metadata[12];
    }
  }, {
    key: "nationalNumberPattern",
    value: function nationalNumberPattern() {
      if (this.v1 || this.v2) return this.metadata[1];
      return this.metadata[2];
    } // Is always present.

  }, {
    key: "possibleLengths",
    value: function possibleLengths() {
      if (this.v1) return;
      return this.metadata[this.v2 ? 2 : 3];
    }
  }, {
    key: "_getFormats",
    value: function _getFormats(metadata) {
      return metadata[this.v1 ? 2 : this.v2 ? 3 : 4];
    } // For countries of the same region (e.g. NANPA)
    // formats are all stored in the "main" country for that region.
    // E.g. "RU" and "KZ", "US" and "CA".

  }, {
    key: "formats",
    value: function formats() {
      var _this = this;

      var formats = this._getFormats(this.metadata) || this._getFormats(this.getDefaultCountryMetadataForRegion()) || [];
      return formats.map(function (_) {
        return new Format(_, _this);
      });
    }
  }, {
    key: "nationalPrefix",
    value: function nationalPrefix() {
      return this.metadata[this.v1 ? 3 : this.v2 ? 4 : 5];
    }
  }, {
    key: "_getNationalPrefixFormattingRule",
    value: function _getNationalPrefixFormattingRule(metadata) {
      return metadata[this.v1 ? 4 : this.v2 ? 5 : 6];
    } // For countries of the same region (e.g. NANPA)
    // national prefix formatting rule is stored in the "main" country for that region.
    // E.g. "RU" and "KZ", "US" and "CA".

  }, {
    key: "nationalPrefixFormattingRule",
    value: function nationalPrefixFormattingRule() {
      return this._getNationalPrefixFormattingRule(this.metadata) || this._getNationalPrefixFormattingRule(this.getDefaultCountryMetadataForRegion());
    }
  }, {
    key: "_nationalPrefixForParsing",
    value: function _nationalPrefixForParsing() {
      return this.metadata[this.v1 ? 5 : this.v2 ? 6 : 7];
    }
  }, {
    key: "nationalPrefixForParsing",
    value: function nationalPrefixForParsing() {
      // If `national_prefix_for_parsing` is not set explicitly,
      // then infer it from `national_prefix` (if any)
      return this._nationalPrefixForParsing() || this.nationalPrefix();
    }
  }, {
    key: "nationalPrefixTransformRule",
    value: function nationalPrefixTransformRule() {
      return this.metadata[this.v1 ? 6 : this.v2 ? 7 : 8];
    }
  }, {
    key: "_getNationalPrefixIsOptionalWhenFormatting",
    value: function _getNationalPrefixIsOptionalWhenFormatting() {
      return !!this.metadata[this.v1 ? 7 : this.v2 ? 8 : 9];
    } // For countries of the same region (e.g. NANPA)
    // "national prefix is optional when formatting" flag is
    // stored in the "main" country for that region.
    // E.g. "RU" and "KZ", "US" and "CA".

  }, {
    key: "nationalPrefixIsOptionalWhenFormattingInNationalFormat",
    value: function nationalPrefixIsOptionalWhenFormattingInNationalFormat() {
      return this._getNationalPrefixIsOptionalWhenFormatting(this.metadata) || this._getNationalPrefixIsOptionalWhenFormatting(this.getDefaultCountryMetadataForRegion());
    }
  }, {
    key: "leadingDigits",
    value: function leadingDigits() {
      return this.metadata[this.v1 ? 8 : this.v2 ? 9 : 10];
    }
  }, {
    key: "types",
    value: function types() {
      return this.metadata[this.v1 ? 9 : this.v2 ? 10 : 11];
    }
  }, {
    key: "hasTypes",
    value: function hasTypes() {
      // Versions 1.2.0 - 1.2.4: can be `[]`.

      /* istanbul ignore next */
      if (this.types() && this.types().length === 0) {
        return false;
      } // Versions <= 1.2.4: can be `undefined`.
      // Version >= 1.2.5: can be `0`.


      return !!this.types();
    }
  }, {
    key: "type",
    value: function type(_type2) {
      if (this.hasTypes() && getType(this.types(), _type2)) {
        return new Type(getType(this.types(), _type2), this);
      }
    }
  }, {
    key: "ext",
    value: function ext() {
      if (this.v1 || this.v2) return DEFAULT_EXT_PREFIX;
      return this.metadata[13] || DEFAULT_EXT_PREFIX;
    }
  }]);

  return NumberingPlan;
}();

var Format =
/*#__PURE__*/
function () {
  function Format(format, metadata) {
    _classCallCheck$8(this, Format);

    this._format = format;
    this.metadata = metadata;
  }

  _createClass$8(Format, [{
    key: "pattern",
    value: function pattern() {
      return this._format[0];
    }
  }, {
    key: "format",
    value: function format() {
      return this._format[1];
    }
  }, {
    key: "leadingDigitsPatterns",
    value: function leadingDigitsPatterns() {
      return this._format[2] || [];
    }
  }, {
    key: "nationalPrefixFormattingRule",
    value: function nationalPrefixFormattingRule() {
      return this._format[3] || this.metadata.nationalPrefixFormattingRule();
    }
  }, {
    key: "nationalPrefixIsOptionalWhenFormattingInNationalFormat",
    value: function nationalPrefixIsOptionalWhenFormattingInNationalFormat() {
      return !!this._format[4] || this.metadata.nationalPrefixIsOptionalWhenFormattingInNationalFormat();
    }
  }, {
    key: "nationalPrefixIsMandatoryWhenFormattingInNationalFormat",
    value: function nationalPrefixIsMandatoryWhenFormattingInNationalFormat() {
      // National prefix is omitted if there's no national prefix formatting rule
      // set for this country, or when the national prefix formatting rule
      // contains no national prefix itself, or when this rule is set but
      // national prefix is optional for this phone number format
      // (and it is not enforced explicitly)
      return this.usesNationalPrefix() && !this.nationalPrefixIsOptionalWhenFormattingInNationalFormat();
    } // Checks whether national prefix formatting rule contains national prefix.

  }, {
    key: "usesNationalPrefix",
    value: function usesNationalPrefix() {
      return this.nationalPrefixFormattingRule() && // Check that national prefix formatting rule is not a "dummy" one.
      !FIRST_GROUP_ONLY_PREFIX_PATTERN.test(this.nationalPrefixFormattingRule()) // In compressed metadata, `this.nationalPrefixFormattingRule()` is `0`
      // when `national_prefix_formatting_rule` is not present.
      // So, `true` or `false` are returned explicitly here, so that
      // `0` number isn't returned.
      ? true : false;
    }
  }, {
    key: "internationalFormat",
    value: function internationalFormat() {
      return this._format[5] || this.format();
    }
  }]);

  return Format;
}();
/**
 * A pattern that is used to determine if the national prefix formatting rule
 * has the first group only, i.e., does not start with the national prefix.
 * Note that the pattern explicitly allows for unbalanced parentheses.
 */


var FIRST_GROUP_ONLY_PREFIX_PATTERN = /^\(?\$1\)?$/;

var Type =
/*#__PURE__*/
function () {
  function Type(type, metadata) {
    _classCallCheck$8(this, Type);

    this.type = type;
    this.metadata = metadata;
  }

  _createClass$8(Type, [{
    key: "pattern",
    value: function pattern() {
      if (this.metadata.v1) return this.type;
      return this.type[0];
    }
  }, {
    key: "possibleLengths",
    value: function possibleLengths() {
      if (this.metadata.v1) return;
      return this.type[1] || this.metadata.possibleLengths();
    }
  }]);

  return Type;
}();

function getType(types, type) {
  switch (type) {
    case 'FIXED_LINE':
      return types[0];

    case 'MOBILE':
      return types[1];

    case 'TOLL_FREE':
      return types[2];

    case 'PREMIUM_RATE':
      return types[3];

    case 'PERSONAL_NUMBER':
      return types[4];

    case 'VOICEMAIL':
      return types[5];

    case 'UAN':
      return types[6];

    case 'PAGER':
      return types[7];

    case 'VOIP':
      return types[8];

    case 'SHARED_COST':
      return types[9];
  }
}

function validateMetadata(metadata) {
  if (!metadata) {
    throw new Error('[libphonenumber-js] `metadata` argument not passed. Check your arguments.');
  } // `country_phone_code_to_countries` was renamed to
  // `country_calling_codes` in `1.0.18`.


  if (!is_object(metadata) || !is_object(metadata.countries)) {
    throw new Error("[libphonenumber-js] `metadata` argument was passed but it's not a valid metadata. Must be an object having `.countries` child object property. Got ".concat(is_object(metadata) ? 'an object of shape: { ' + Object.keys(metadata).join(', ') + ' }' : 'a ' + type_of(metadata) + ': ' + metadata, "."));
  }
} // Babel transforms `typeof` into some "branches"
// so istanbul will show this as "branch not covered".

/* istanbul ignore next */

var is_object = function is_object(_) {
  return _typeof$5(_) === 'object';
}; // Babel transforms `typeof` into some "branches"
// so istanbul will show this as "branch not covered".

/* istanbul ignore next */


var type_of = function type_of(_) {
  return _typeof$5(_);
};
/**
 * Returns "country calling code" for a country.
 * Throws an error if the country doesn't exist or isn't supported by this library.
 * @param  {string} country
 * @param  {object} metadata
 * @return {string}
 * @example
 * // Returns "44"
 * getCountryCallingCode("GB")
 */

function getCountryCallingCode$1(country, metadata) {
  metadata = new Metadata(metadata);

  if (metadata.hasCountry(country)) {
    return metadata.country(country).countryCallingCode();
  }

  throw new Error("Unknown country: ".concat(country));
}
function isSupportedCountry(country, metadata) {
  // metadata = new Metadata(metadata)
  // return metadata.hasCountry(country)
  return metadata.countries[country] !== undefined;
}

function setVersion(metadata) {
  var version = metadata.version;

  if (typeof version === 'number') {
    this.v1 = version === 1;
    this.v2 = version === 2;
    this.v3 = version === 3;
    this.v4 = version === 4;
  } else {
    if (!version) {
      this.v1 = true;
    } else if (compare(version, V3) === -1) {
      this.v2 = true;
    } else if (compare(version, V4) === -1) {
      this.v3 = true;
    } else {
      this.v4 = true;
    }
  }
} // const ISO_COUNTRY_CODE = /^[A-Z]{2}$/
// function isCountryCode(countryCode) {
// 	return ISO_COUNTRY_CODE.test(countryCodeOrCountryCallingCode)
// }

var RFC3966_EXTN_PREFIX = ';ext=';
/**
 * Helper method for constructing regular expressions for parsing. Creates
 * an expression that captures up to max_length digits.
 * @return {string} RegEx pattern to capture extension digits.
 */

var getExtensionDigitsPattern = function getExtensionDigitsPattern(maxLength) {
  return "([".concat(VALID_DIGITS, "]{1,").concat(maxLength, "})");
};
/**
 * Helper initialiser method to create the regular-expression pattern to match
 * extensions.
 * Copy-pasted from Google's `libphonenumber`:
 * https://github.com/google/libphonenumber/blob/55b2646ec9393f4d3d6661b9c82ef9e258e8b829/javascript/i18n/phonenumbers/phonenumberutil.js#L759-L766
 * @return {string} RegEx pattern to capture extensions.
 */


function createExtensionPattern(purpose) {
  // We cap the maximum length of an extension based on the ambiguity of the way
  // the extension is prefixed. As per ITU, the officially allowed length for
  // extensions is actually 40, but we don't support this since we haven't seen real
  // examples and this introduces many false interpretations as the extension labels
  // are not standardized.

  /** @type {string} */
  var extLimitAfterExplicitLabel = '20';
  /** @type {string} */

  var extLimitAfterLikelyLabel = '15';
  /** @type {string} */

  var extLimitAfterAmbiguousChar = '9';
  /** @type {string} */

  var extLimitWhenNotSure = '6';
  /** @type {string} */

  var possibleSeparatorsBetweenNumberAndExtLabel = "[ \xA0\\t,]*"; // Optional full stop (.) or colon, followed by zero or more spaces/tabs/commas.

  /** @type {string} */

  var possibleCharsAfterExtLabel = "[:\\.\uFF0E]?[ \xA0\\t,-]*";
  /** @type {string} */

  var optionalExtnSuffix = "#?"; // Here the extension is called out in more explicit way, i.e mentioning it obvious
  // patterns like "ext.".

  /** @type {string} */

  var explicitExtLabels = "(?:e?xt(?:ensi(?:o\u0301?|\xF3))?n?|\uFF45?\uFF58\uFF54\uFF4E?|\u0434\u043E\u0431|anexo)"; // One-character symbols that can be used to indicate an extension, and less
  // commonly used or more ambiguous extension labels.

  /** @type {string} */

  var ambiguousExtLabels = "(?:[x\uFF58#\uFF03~\uFF5E]|int|\uFF49\uFF4E\uFF54)"; // When extension is not separated clearly.

  /** @type {string} */

  var ambiguousSeparator = "[- ]+"; // This is the same as possibleSeparatorsBetweenNumberAndExtLabel, but not matching
  // comma as extension label may have it.

  /** @type {string} */

  var possibleSeparatorsNumberExtLabelNoComma = "[ \xA0\\t]*"; // ",," is commonly used for auto dialling the extension when connected. First
  // comma is matched through possibleSeparatorsBetweenNumberAndExtLabel, so we do
  // not repeat it here. Semi-colon works in Iphone and Android also to pop up a
  // button with the extension number following.

  /** @type {string} */

  var autoDiallingAndExtLabelsFound = "(?:,{2}|;)";
  /** @type {string} */

  var rfcExtn = RFC3966_EXTN_PREFIX + getExtensionDigitsPattern(extLimitAfterExplicitLabel);
  /** @type {string} */

  var explicitExtn = possibleSeparatorsBetweenNumberAndExtLabel + explicitExtLabels + possibleCharsAfterExtLabel + getExtensionDigitsPattern(extLimitAfterExplicitLabel) + optionalExtnSuffix;
  /** @type {string} */

  var ambiguousExtn = possibleSeparatorsBetweenNumberAndExtLabel + ambiguousExtLabels + possibleCharsAfterExtLabel + getExtensionDigitsPattern(extLimitAfterAmbiguousChar) + optionalExtnSuffix;
  /** @type {string} */

  var americanStyleExtnWithSuffix = ambiguousSeparator + getExtensionDigitsPattern(extLimitWhenNotSure) + "#";
  /** @type {string} */

  var autoDiallingExtn = possibleSeparatorsNumberExtLabelNoComma + autoDiallingAndExtLabelsFound + possibleCharsAfterExtLabel + getExtensionDigitsPattern(extLimitAfterLikelyLabel) + optionalExtnSuffix;
  /** @type {string} */

  var onlyCommasExtn = possibleSeparatorsNumberExtLabelNoComma + "(?:,)+" + possibleCharsAfterExtLabel + getExtensionDigitsPattern(extLimitAfterAmbiguousChar) + optionalExtnSuffix; // The first regular expression covers RFC 3966 format, where the extension is added
  // using ";ext=". The second more generic where extension is mentioned with explicit
  // labels like "ext:". In both the above cases we allow more numbers in extension than
  // any other extension labels. The third one captures when single character extension
  // labels or less commonly used labels are used. In such cases we capture fewer
  // extension digits in order to reduce the chance of falsely interpreting two
  // numbers beside each other as a number + extension. The fourth one covers the
  // special case of American numbers where the extension is written with a hash
  // at the end, such as "- 503#". The fifth one is exclusively for extension
  // autodialling formats which are used when dialling and in this case we accept longer
  // extensions. The last one is more liberal on the number of commas that acts as
  // extension labels, so we have a strict cap on the number of digits in such extensions.

  return rfcExtn + "|" + explicitExtn + "|" + ambiguousExtn + "|" + americanStyleExtnWithSuffix + "|" + autoDiallingExtn + "|" + onlyCommasExtn;
}

//  Checks we have at least three leading digits, and only valid punctuation,
//  alpha characters and digits in the phone number. Does not include extension
//  data. The symbol 'x' is allowed here as valid punctuation since it is often
//  used as a placeholder for carrier codes, for example in Brazilian phone
//  numbers. We also allow multiple '+' characters at the start.
//
//  Corresponds to the following:
//  [digits]{minLengthNsn}|
//  plus_sign*
//  (([punctuation]|[star])*[digits]){3,}([punctuation]|[star]|[digits]|[alpha])*
//
//  The first reg-ex is to allow short numbers (two digits long) to be parsed if
//  they are entered as "15" etc, but only if there is no punctuation in them.
//  The second expression restricts the number of digits to three or more, but
//  then allows them to be in international form, and to have alpha-characters
//  and punctuation. We split up the two reg-exes here and combine them when
//  creating the reg-ex VALID_PHONE_NUMBER_PATTERN itself so we can prefix it
//  with ^ and append $ to each branch.
//
//  "Note VALID_PUNCTUATION starts with a -,
//   so must be the first in the range" (c) Google devs.
//  (wtf did they mean by saying that; probably nothing)
//

var MIN_LENGTH_PHONE_NUMBER_PATTERN = '[' + VALID_DIGITS + ']{' + MIN_LENGTH_FOR_NSN + '}'; //
// And this is the second reg-exp:
// (see MIN_LENGTH_PHONE_NUMBER_PATTERN for a full description of this reg-exp)
//

var VALID_PHONE_NUMBER = '[' + PLUS_CHARS + ']{0,1}' + '(?:' + '[' + VALID_PUNCTUATION + ']*' + '[' + VALID_DIGITS + ']' + '){3,}' + '[' + VALID_PUNCTUATION + VALID_DIGITS + ']*'; // This regular expression isn't present in Google's `libphonenumber`
// and is only used to determine whether the phone number being input
// is too short for it to even consider it a "valid" number.
// This is just a way to differentiate between a really invalid phone
// number like "abcde" and a valid phone number that a user has just
// started inputting, like "+1" or "1": both these cases would be
// considered `NOT_A_NUMBER` by Google's `libphonenumber`, but this
// library can provide a more detailed error message — whether it's
// really "not a number", or is it just a start of a valid phone number.

var VALID_PHONE_NUMBER_START_REG_EXP = new RegExp('^' + '[' + PLUS_CHARS + ']{0,1}' + '(?:' + '[' + VALID_PUNCTUATION + ']*' + '[' + VALID_DIGITS + ']' + '){1,2}' + '$', 'i');
var VALID_PHONE_NUMBER_WITH_EXTENSION = VALID_PHONE_NUMBER + // Phone number extensions
'(?:' + createExtensionPattern() + ')?'; // The combined regular expression for valid phone numbers:
//

var VALID_PHONE_NUMBER_PATTERN = new RegExp( // Either a short two-digit-only phone number
'^' + MIN_LENGTH_PHONE_NUMBER_PATTERN + '$' + '|' + // Or a longer fully parsed phone number (min 3 characters)
'^' + VALID_PHONE_NUMBER_WITH_EXTENSION + '$', 'i'); // Checks to see if the string of characters could possibly be a phone number at
// all. At the moment, checks to see that the string begins with at least 2
// digits, ignoring any punctuation commonly found in phone numbers. This method
// does not require the number to be normalized in advance - but does assume
// that leading non-number symbols have been removed, such as by the method
// `extract_possible_number`.
//

function isViablePhoneNumber(number) {
  return number.length >= MIN_LENGTH_FOR_NSN && VALID_PHONE_NUMBER_PATTERN.test(number);
} // This is just a way to differentiate between a really invalid phone
// number like "abcde" and a valid phone number that a user has just
// started inputting, like "+1" or "1": both these cases would be
// considered `NOT_A_NUMBER` by Google's `libphonenumber`, but this
// library can provide a more detailed error message — whether it's
// really "not a number", or is it just a start of a valid phone number.

function isViablePhoneNumberStart(number) {
  return VALID_PHONE_NUMBER_START_REG_EXP.test(number);
}

// 1 or more valid digits, for use when parsing.

var EXTN_PATTERN = new RegExp('(?:' + createExtensionPattern() + ')$', 'i'); // Strips any extension (as in, the part of the number dialled after the call is
// connected, usually indicated with extn, ext, x or similar) from the end of
// the number, and returns it.

function extractExtension(number) {
  var start = number.search(EXTN_PATTERN);

  if (start < 0) {
    return {};
  } // If we find a potential extension, and the number preceding this is a viable
  // number, we assume it is an extension.


  var numberWithoutExtension = number.slice(0, start);
  var matches = number.match(EXTN_PATTERN);
  var i = 1;

  while (i < matches.length) {
    if (matches[i]) {
      return {
        number: numberWithoutExtension,
        ext: matches[i]
      };
    }

    i++;
  }
}

// These mappings map a character (key) to a specific digit that should
// replace it for normalization purposes. Non-European digits that
// may be used in phone numbers are mapped to a European equivalent.
//
// E.g. in Iraq they don't write `+442323234` but rather `+٤٤٢٣٢٣٢٣٤`.
//
var DIGITS = {
  '0': '0',
  '1': '1',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
  '6': '6',
  '7': '7',
  '8': '8',
  '9': '9',
  "\uFF10": '0',
  // Fullwidth digit 0
  "\uFF11": '1',
  // Fullwidth digit 1
  "\uFF12": '2',
  // Fullwidth digit 2
  "\uFF13": '3',
  // Fullwidth digit 3
  "\uFF14": '4',
  // Fullwidth digit 4
  "\uFF15": '5',
  // Fullwidth digit 5
  "\uFF16": '6',
  // Fullwidth digit 6
  "\uFF17": '7',
  // Fullwidth digit 7
  "\uFF18": '8',
  // Fullwidth digit 8
  "\uFF19": '9',
  // Fullwidth digit 9
  "\u0660": '0',
  // Arabic-indic digit 0
  "\u0661": '1',
  // Arabic-indic digit 1
  "\u0662": '2',
  // Arabic-indic digit 2
  "\u0663": '3',
  // Arabic-indic digit 3
  "\u0664": '4',
  // Arabic-indic digit 4
  "\u0665": '5',
  // Arabic-indic digit 5
  "\u0666": '6',
  // Arabic-indic digit 6
  "\u0667": '7',
  // Arabic-indic digit 7
  "\u0668": '8',
  // Arabic-indic digit 8
  "\u0669": '9',
  // Arabic-indic digit 9
  "\u06F0": '0',
  // Eastern-Arabic digit 0
  "\u06F1": '1',
  // Eastern-Arabic digit 1
  "\u06F2": '2',
  // Eastern-Arabic digit 2
  "\u06F3": '3',
  // Eastern-Arabic digit 3
  "\u06F4": '4',
  // Eastern-Arabic digit 4
  "\u06F5": '5',
  // Eastern-Arabic digit 5
  "\u06F6": '6',
  // Eastern-Arabic digit 6
  "\u06F7": '7',
  // Eastern-Arabic digit 7
  "\u06F8": '8',
  // Eastern-Arabic digit 8
  "\u06F9": '9' // Eastern-Arabic digit 9

};
function parseDigit(character) {
  return DIGITS[character];
}
/**
 * Parses phone number digits from a string.
 * Drops all punctuation leaving only digits.
 * Also converts wide-ascii and arabic-indic numerals to conventional numerals.
 * E.g. in Iraq they don't write `+442323234` but rather `+٤٤٢٣٢٣٢٣٤`.
 * @param  {string} string
 * @return {string}
 * @example
 * ```js
 * parseDigits('8 (800) 555')
 * // Outputs '8800555'.
 * ```
 */

function parseDigits(string) {
  var result = ''; // Using `.split('')` here instead of normal `for ... of`
  // because the importing application doesn't neccessarily include an ES6 polyfill.
  // The `.split('')` approach discards "exotic" UTF-8 characters
  // (the ones consisting of four bytes) but digits
  // (including non-European ones) don't fall into that range
  // so such "exotic" characters would be discarded anyway.

  for (var _iterator = string.split(''), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var character = _ref;
    var digit = parseDigit(character);

    if (digit) {
      result += digit;
    }
  }

  return result;
}

/**
 * Parses phone number characters from a string.
 * Drops all punctuation leaving only digits and the leading `+` sign (if any).
 * Also converts wide-ascii and arabic-indic numerals to conventional numerals.
 * E.g. in Iraq they don't write `+442323234` but rather `+٤٤٢٣٢٣٢٣٤`.
 * @param  {string} string
 * @return {string}
 * @example
 * ```js
 * // Outputs '8800555'.
 * parseIncompletePhoneNumber('8 (800) 555')
 * // Outputs '+7800555'.
 * parseIncompletePhoneNumber('+7 800 555')
 * ```
 */

function parseIncompletePhoneNumber(string) {
  var result = ''; // Using `.split('')` here instead of normal `for ... of`
  // because the importing application doesn't neccessarily include an ES6 polyfill.
  // The `.split('')` approach discards "exotic" UTF-8 characters
  // (the ones consisting of four bytes) but digits
  // (including non-European ones) don't fall into that range
  // so such "exotic" characters would be discarded anyway.

  for (var _iterator = string.split(''), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var character = _ref;
    result += parsePhoneNumberCharacter(character, result) || '';
  }

  return result;
}
/**
 * Parses next character while parsing phone number digits (including a `+`)
 * from text: discards everything except `+` and digits, and `+` is only allowed
 * at the start of a phone number.
 * For example, is used in `react-phone-number-input` where it uses
 * [`input-format`](https://gitlab.com/catamphetamine/input-format).
 * @param  {string} character - Yet another character from raw input string.
 * @param  {string?} prevParsedCharacters - Previous parsed characters.
 * @param  {object} meta - Optional custom use-case-specific metadata.
 * @return {string?} The parsed character.
 */

function parsePhoneNumberCharacter(character, prevParsedCharacters) {
  // Only allow a leading `+`.
  if (character === '+') {
    // If this `+` is not the first parsed character
    // then discard it.
    if (prevParsedCharacters) {
      return;
    }

    return '+';
  } // Allow digits.


  return parseDigit(character);
}

/**
 * Merges two arrays.
 * @param  {*} a
 * @param  {*} b
 * @return {*}
 */
function mergeArrays(a, b) {
  var merged = a.slice();

  for (var _iterator = b, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var element = _ref;

    if (a.indexOf(element) < 0) {
      merged.push(element);
    }
  }

  return merged.sort(function (a, b) {
    return a - b;
  }); // ES6 version, requires Set polyfill.
  // let merged = new Set(a)
  // for (const element of b) {
  // 	merged.add(i)
  // }
  // return Array.from(merged).sort((a, b) => a - b)
}

function checkNumberLength(nationalNumber, metadata) {
  return checkNumberLengthForType(nationalNumber, undefined, metadata);
} // Checks whether a number is possible for the country based on its length.
// Should only be called for the "new" metadata which has "possible lengths".

function checkNumberLengthForType(nationalNumber, type, metadata) {
  var type_info = metadata.type(type); // There should always be "<possiblePengths/>" set for every type element.
  // This is declared in the XML schema.
  // For size efficiency, where a sub-description (e.g. fixed-line)
  // has the same "<possiblePengths/>" as the "general description", this is missing,
  // so we fall back to the "general description". Where no numbers of the type
  // exist at all, there is one possible length (-1) which is guaranteed
  // not to match the length of any real phone number.

  var possible_lengths = type_info && type_info.possibleLengths() || metadata.possibleLengths(); // let local_lengths    = type_info && type.possibleLengthsLocal() || metadata.possibleLengthsLocal()
  // Metadata before version `1.0.18` didn't contain `possible_lengths`.

  if (!possible_lengths) {
    return 'IS_POSSIBLE';
  }

  if (type === 'FIXED_LINE_OR_MOBILE') {
    // No such country in metadata.

    /* istanbul ignore next */
    if (!metadata.type('FIXED_LINE')) {
      // The rare case has been encountered where no fixedLine data is available
      // (true for some non-geographic entities), so we just check mobile.
      return checkNumberLengthForType(nationalNumber, 'MOBILE', metadata);
    }

    var mobile_type = metadata.type('MOBILE');

    if (mobile_type) {
      // Merge the mobile data in if there was any. "Concat" creates a new
      // array, it doesn't edit possible_lengths in place, so we don't need a copy.
      // Note that when adding the possible lengths from mobile, we have
      // to again check they aren't empty since if they are this indicates
      // they are the same as the general desc and should be obtained from there.
      possible_lengths = mergeArrays(possible_lengths, mobile_type.possibleLengths()); // The current list is sorted; we need to merge in the new list and
      // re-sort (duplicates are okay). Sorting isn't so expensive because
      // the lists are very small.
      // if (local_lengths) {
      // 	local_lengths = mergeArrays(local_lengths, mobile_type.possibleLengthsLocal())
      // } else {
      // 	local_lengths = mobile_type.possibleLengthsLocal()
      // }
    }
  } // If the type doesn't exist then return 'INVALID_LENGTH'.
  else if (type && !type_info) {
      return 'INVALID_LENGTH';
    }

  var actual_length = nationalNumber.length; // In `libphonenumber-js` all "local-only" formats are dropped for simplicity.
  // // This is safe because there is never an overlap beween the possible lengths
  // // and the local-only lengths; this is checked at build time.
  // if (local_lengths && local_lengths.indexOf(nationalNumber.length) >= 0)
  // {
  // 	return 'IS_POSSIBLE_LOCAL_ONLY'
  // }

  var minimum_length = possible_lengths[0];

  if (minimum_length === actual_length) {
    return 'IS_POSSIBLE';
  }

  if (minimum_length > actual_length) {
    return 'TOO_SHORT';
  }

  if (possible_lengths[possible_lengths.length - 1] < actual_length) {
    return 'TOO_LONG';
  } // We skip the first element since we've already checked it.


  return possible_lengths.indexOf(actual_length, 1) >= 0 ? 'IS_POSSIBLE' : 'INVALID_LENGTH';
}

function isPossiblePhoneNumber$2(input, options, metadata) {
  /* istanbul ignore if */
  if (options === undefined) {
    options = {};
  }

  metadata = new Metadata(metadata);

  if (options.v2) {
    if (!input.countryCallingCode) {
      throw new Error('Invalid phone number object passed');
    }

    metadata.selectNumberingPlan(input.countryCallingCode);
  } else {
    if (!input.phone) {
      return false;
    }

    if (input.country) {
      if (!metadata.hasCountry(input.country)) {
        throw new Error("Unknown country: ".concat(input.country));
      }

      metadata.country(input.country);
    } else {
      if (!input.countryCallingCode) {
        throw new Error('Invalid phone number object passed');
      }

      metadata.selectNumberingPlan(input.countryCallingCode);
    }
  }

  if (metadata.possibleLengths()) {
    return isPossibleNumber(input.phone || input.nationalNumber, metadata);
  } else {
    // There was a bug between `1.7.35` and `1.7.37` where "possible_lengths"
    // were missing for "non-geographical" numbering plans.
    // Just assume the number is possible in such cases:
    // it's unlikely that anyone generated their custom metadata
    // in that short period of time (one day).
    // This code can be removed in some future major version update.
    if (input.countryCallingCode && metadata.isNonGeographicCallingCode(input.countryCallingCode)) {
      // "Non-geographic entities" did't have `possibleLengths`
      // due to a bug in metadata generation process.
      return true;
    } else {
      throw new Error('Missing "possibleLengths" in metadata. Perhaps the metadata has been generated before v1.0.18.');
    }
  }
}
function isPossibleNumber(nationalNumber, metadata) {
  //, isInternational) {
  switch (checkNumberLength(nationalNumber, metadata)) {
    case 'IS_POSSIBLE':
      return true;
    // This library ignores "local-only" phone numbers (for simplicity).
    // See the readme for more info on what are "local-only" phone numbers.
    // case 'IS_POSSIBLE_LOCAL_ONLY':
    // 	return !isInternational

    default:
      return false;
  }
}

function _slicedToArray$3(arr, i) { return _arrayWithHoles$3(arr) || _iterableToArrayLimit$3(arr, i) || _nonIterableRest$3(); }

function _nonIterableRest$3() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit$3(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$3(arr) { if (Array.isArray(arr)) return arr; }

/**
 * @param  {string} text - Phone URI (RFC 3966).
 * @return {object} `{ ?number, ?ext }`.
 */

function parseRFC3966(text) {
  var number;
  var ext; // Replace "tel:" with "tel=" for parsing convenience.

  text = text.replace(/^tel:/, 'tel=');

  for (var _iterator = text.split(';'), _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var part = _ref;

    var _part$split = part.split('='),
        _part$split2 = _slicedToArray$3(_part$split, 2),
        name = _part$split2[0],
        value = _part$split2[1];

    switch (name) {
      case 'tel':
        number = value;
        break;

      case 'ext':
        ext = value;
        break;

      case 'phone-context':
        // Only "country contexts" are supported.
        // "Domain contexts" are ignored.
        if (value[0] === '+') {
          number = value + number;
        }

        break;
    }
  } // If the phone number is not viable, then abort.


  if (!isViablePhoneNumber(number)) {
    return {};
  }

  var result = {
    number: number
  };

  if (ext) {
    result.ext = ext;
  }

  return result;
}
/**
 * @param  {object} - `{ ?number, ?extension }`.
 * @return {string} Phone URI (RFC 3966).
 */

function formatRFC3966(_ref2) {
  var number = _ref2.number,
      ext = _ref2.ext;

  if (!number) {
    return '';
  }

  if (number[0] !== '+') {
    throw new Error("\"formatRFC3966()\" expects \"number\" to be in E.164 format.");
  }

  return "tel:".concat(number).concat(ext ? ';ext=' + ext : '');
}

/**
 * Checks whether the entire input sequence can be matched
 * against the regular expression.
 * @return {boolean}
 */
function matchesEntirely(text, regular_expression) {
  // If assigning the `''` default value is moved to the arguments above,
  // code coverage would decrease for some weird reason.
  text = text || '';
  return new RegExp('^(?:' + regular_expression + ')$').test(text);
}

var NON_FIXED_LINE_PHONE_TYPES = ['MOBILE', 'PREMIUM_RATE', 'TOLL_FREE', 'SHARED_COST', 'VOIP', 'PERSONAL_NUMBER', 'PAGER', 'UAN', 'VOICEMAIL']; // Finds out national phone number type (fixed line, mobile, etc)

function getNumberType(input, options, metadata) {
  // If assigning the `{}` default value is moved to the arguments above,
  // code coverage would decrease for some weird reason.
  options = options || {}; // When `parse()` returned `{}`
  // meaning that the phone number is not a valid one.

  if (!input.country) {
    return;
  }

  metadata = new Metadata(metadata);
  metadata.selectNumberingPlan(input.country, input.countryCallingCode);
  var nationalNumber = options.v2 ? input.nationalNumber : input.phone; // The following is copy-pasted from the original function:
  // https://github.com/googlei18n/libphonenumber/blob/3ea547d4fbaa2d0b67588904dfa5d3f2557c27ff/javascript/i18n/phonenumbers/phonenumberutil.js#L2835
  // Is this national number even valid for this country

  if (!matchesEntirely(nationalNumber, metadata.nationalNumberPattern())) {
    return;
  } // Is it fixed line number


  if (isNumberTypeEqualTo(nationalNumber, 'FIXED_LINE', metadata)) {
    // Because duplicate regular expressions are removed
    // to reduce metadata size, if "mobile" pattern is ""
    // then it means it was removed due to being a duplicate of the fixed-line pattern.
    //
    if (metadata.type('MOBILE') && metadata.type('MOBILE').pattern() === '') {
      return 'FIXED_LINE_OR_MOBILE';
    } // v1 metadata.
    // Legacy.
    // Deprecated.


    if (!metadata.type('MOBILE')) {
      return 'FIXED_LINE_OR_MOBILE';
    } // Check if the number happens to qualify as both fixed line and mobile.
    // (no such country in the minimal metadata set)

    /* istanbul ignore if */


    if (isNumberTypeEqualTo(nationalNumber, 'MOBILE', metadata)) {
      return 'FIXED_LINE_OR_MOBILE';
    }

    return 'FIXED_LINE';
  }

  for (var _i = 0, _NON_FIXED_LINE_PHONE = NON_FIXED_LINE_PHONE_TYPES; _i < _NON_FIXED_LINE_PHONE.length; _i++) {
    var type = _NON_FIXED_LINE_PHONE[_i];

    if (isNumberTypeEqualTo(nationalNumber, type, metadata)) {
      return type;
    }
  }
}
function isNumberTypeEqualTo(nationalNumber, type, metadata) {
  type = metadata.type(type);

  if (!type || !type.pattern()) {
    return false;
  } // Check if any possible number lengths are present;
  // if so, we use them to avoid checking
  // the validation pattern if they don't match.
  // If they are absent, this means they match
  // the general description, which we have
  // already checked before a specific number type.


  if (type.possibleLengths() && type.possibleLengths().indexOf(nationalNumber.length) < 0) {
    return false;
  }

  return matchesEntirely(nationalNumber, type.pattern());
}

/**
 * Checks if a given phone number is valid.
 *
 * If the `number` is a string, it will be parsed to an object,
 * but only if it contains only valid phone number characters (including punctuation).
 * If the `number` is an object, it is used as is.
 *
 * The optional `defaultCountry` argument is the default country.
 * I.e. it does not restrict to just that country,
 * e.g. in those cases where several countries share
 * the same phone numbering rules (NANPA, Britain, etc).
 * For example, even though the number `07624 369230`
 * belongs to the Isle of Man ("IM" country code)
 * calling `isValidNumber('07624369230', 'GB', metadata)`
 * still returns `true` because the country is not restricted to `GB`,
 * it's just that `GB` is the default one for the phone numbering rules.
 * For restricting the country see `isValidNumberForRegion()`
 * though restricting a country might not be a good idea.
 * https://github.com/googlei18n/libphonenumber/blob/master/FAQ.md#when-should-i-use-isvalidnumberforregion
 *
 * Examples:
 *
 * ```js
 * isValidNumber('+78005553535', metadata)
 * isValidNumber('8005553535', 'RU', metadata)
 * isValidNumber('88005553535', 'RU', metadata)
 * isValidNumber({ phone: '8005553535', country: 'RU' }, metadata)
 * ```
 */

function isValidNumber(input, options, metadata) {
  // If assigning the `{}` default value is moved to the arguments above,
  // code coverage would decrease for some weird reason.
  options = options || {};
  metadata = new Metadata(metadata); // This is just to support `isValidNumber({})`
  // for cases when `parseNumber()` returns `{}`.

  if (!input.country) {
    return false;
  }

  metadata.selectNumberingPlan(input.country, input.countryCallingCode); // By default, countries only have type regexps when it's required for
  // distinguishing different countries having the same `countryCallingCode`.

  if (metadata.hasTypes()) {
    return getNumberType(input, options, metadata.metadata) !== undefined;
  } // If there are no type regexps for this country in metadata then use
  // `nationalNumberPattern` as a "better than nothing" replacement.


  var national_number = options.v2 ? input.nationalNumber : input.phone;
  return matchesEntirely(national_number, metadata.nationalNumberPattern());
}

//
// E.g. "(999) 111-22-33" -> "999 111 22 33"
//
// For some reason Google's metadata contains `<intlFormat/>`s with brackets and dashes.
// Meanwhile, there's no single opinion about using punctuation in international phone numbers.
//
// For example, Google's `<intlFormat/>` for USA is `+1 213-373-4253`.
// And here's a quote from WikiPedia's "North American Numbering Plan" page:
// https://en.wikipedia.org/wiki/North_American_Numbering_Plan
//
// "The country calling code for all countries participating in the NANP is 1.
// In international format, an NANP number should be listed as +1 301 555 01 00,
// where 301 is an area code (Maryland)."
//
// I personally prefer the international format without any punctuation.
// For example, brackets are remnants of the old age, meaning that the
// phone number part in brackets (so called "area code") can be omitted
// if dialing within the same "area".
// And hyphens were clearly introduced for splitting local numbers into memorizable groups.
// For example, remembering "5553535" is difficult but "555-35-35" is much simpler.
// Imagine a man taking a bus from home to work and seeing an ad with a phone number.
// He has a couple of seconds to memorize that number until it passes by.
// If it were spaces instead of hyphens the man wouldn't necessarily get it,
// but with hyphens instead of spaces the grouping is more explicit.
// I personally think that hyphens introduce visual clutter,
// so I prefer replacing them with spaces in international numbers.
// In the modern age all output is done on displays where spaces are clearly distinguishable
// so hyphens can be safely replaced with spaces without losing any legibility.
//

function applyInternationalSeparatorStyle(formattedNumber) {
  return formattedNumber.replace(new RegExp("[".concat(VALID_PUNCTUATION, "]+"), 'g'), ' ').trim();
}

// first group is not used in the national pattern (e.g. Argentina) so the $1
// group does not match correctly. Therefore, we use `\d`, so that the first
// group actually used in the pattern will be matched.

var FIRST_GROUP_PATTERN = /(\$\d)/;
function formatNationalNumberUsingFormat(number, format, _ref) {
  var useInternationalFormat = _ref.useInternationalFormat,
      withNationalPrefix = _ref.withNationalPrefix;
      _ref.carrierCode;
      _ref.metadata;
  var formattedNumber = number.replace(new RegExp(format.pattern()), useInternationalFormat ? format.internationalFormat() : // This library doesn't use `domestic_carrier_code_formatting_rule`,
  // because that one is only used when formatting phone numbers
  // for dialing from a mobile phone, and this is not a dialing library.
  // carrierCode && format.domesticCarrierCodeFormattingRule()
  // 	// First, replace the $CC in the formatting rule with the desired carrier code.
  // 	// Then, replace the $FG in the formatting rule with the first group
  // 	// and the carrier code combined in the appropriate way.
  // 	? format.format().replace(FIRST_GROUP_PATTERN, format.domesticCarrierCodeFormattingRule().replace('$CC', carrierCode))
  // 	: (
  // 		withNationalPrefix && format.nationalPrefixFormattingRule()
  // 			? format.format().replace(FIRST_GROUP_PATTERN, format.nationalPrefixFormattingRule())
  // 			: format.format()
  // 	)
  withNationalPrefix && format.nationalPrefixFormattingRule() ? format.format().replace(FIRST_GROUP_PATTERN, format.nationalPrefixFormattingRule()) : format.format());

  if (useInternationalFormat) {
    return applyInternationalSeparatorStyle(formattedNumber);
  }

  return formattedNumber;
}

/**
 * Pattern that makes it easy to distinguish whether a region has a single
 * international dialing prefix or not. If a region has a single international
 * prefix (e.g. 011 in USA), it will be represented as a string that contains
 * a sequence of ASCII digits, and possibly a tilde, which signals waiting for
 * the tone. If there are multiple available international prefixes in a
 * region, they will be represented as a regex string that always contains one
 * or more characters that are not ASCII digits or a tilde.
 */

var SINGLE_IDD_PREFIX_REG_EXP = /^[\d]+(?:[~\u2053\u223C\uFF5E][\d]+)?$/; // For regions that have multiple IDD prefixes
// a preferred IDD prefix is returned.

function getIddPrefix(country, callingCode, metadata) {
  var countryMetadata = new Metadata(metadata);
  countryMetadata.selectNumberingPlan(country, callingCode);

  if (countryMetadata.defaultIDDPrefix()) {
    return countryMetadata.defaultIDDPrefix();
  }

  if (SINGLE_IDD_PREFIX_REG_EXP.test(countryMetadata.IDDPrefix())) {
    return countryMetadata.IDDPrefix();
  }
}

function _objectSpread$8(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty$b(target, key, source[key]); }); } return target; }

function _defineProperty$b(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var DEFAULT_OPTIONS = {
  formatExtension: function formatExtension(formattedNumber, extension, metadata) {
    return "".concat(formattedNumber).concat(metadata.ext()).concat(extension);
  } // Formats a phone number
  //
  // Example use cases:
  //
  // ```js
  // formatNumber('8005553535', 'RU', 'INTERNATIONAL')
  // formatNumber('8005553535', 'RU', 'INTERNATIONAL', metadata)
  // formatNumber({ phone: '8005553535', country: 'RU' }, 'INTERNATIONAL')
  // formatNumber({ phone: '8005553535', country: 'RU' }, 'INTERNATIONAL', metadata)
  // formatNumber('+78005553535', 'NATIONAL')
  // formatNumber('+78005553535', 'NATIONAL', metadata)
  // ```
  //

};
function formatNumber(input, format, options, metadata) {
  // Apply default options.
  if (options) {
    options = _objectSpread$8({}, DEFAULT_OPTIONS, options);
  } else {
    options = DEFAULT_OPTIONS;
  }

  metadata = new Metadata(metadata);

  if (input.country && input.country !== '001') {
    // Validate `input.country`.
    if (!metadata.hasCountry(input.country)) {
      throw new Error("Unknown country: ".concat(input.country));
    }

    metadata.country(input.country);
  } else if (input.countryCallingCode) {
    metadata.selectNumberingPlan(input.countryCallingCode);
  } else return input.phone || '';

  var countryCallingCode = metadata.countryCallingCode();
  var nationalNumber = options.v2 ? input.nationalNumber : input.phone; // This variable should have been declared inside `case`s
  // but Babel has a bug and it says "duplicate variable declaration".

  var number;

  switch (format) {
    case 'NATIONAL':
      // Legacy argument support.
      // (`{ country: ..., phone: '' }`)
      if (!nationalNumber) {
        return '';
      }

      number = formatNationalNumber$1(nationalNumber, input.carrierCode, 'NATIONAL', metadata, options);
      return addExtension(number, input.ext, metadata, options.formatExtension);

    case 'INTERNATIONAL':
      // Legacy argument support.
      // (`{ country: ..., phone: '' }`)
      if (!nationalNumber) {
        return "+".concat(countryCallingCode);
      }

      number = formatNationalNumber$1(nationalNumber, null, 'INTERNATIONAL', metadata, options);
      number = "+".concat(countryCallingCode, " ").concat(number);
      return addExtension(number, input.ext, metadata, options.formatExtension);

    case 'E.164':
      // `E.164` doesn't define "phone number extensions".
      return "+".concat(countryCallingCode).concat(nationalNumber);

    case 'RFC3966':
      return formatRFC3966({
        number: "+".concat(countryCallingCode).concat(nationalNumber),
        ext: input.ext
      });
    // For reference, here's Google's IDD formatter:
    // https://github.com/google/libphonenumber/blob/32719cf74e68796788d1ca45abc85dcdc63ba5b9/java/libphonenumber/src/com/google/i18n/phonenumbers/PhoneNumberUtil.java#L1546
    // Not saying that this IDD formatter replicates it 1:1, but it seems to work.
    // Who would even need to format phone numbers in IDD format anyway?

    case 'IDD':
      if (!options.fromCountry) {
        return; // throw new Error('`fromCountry` option not passed for IDD-prefixed formatting.')
      }

      var formattedNumber = formatIDD(nationalNumber, input.carrierCode, countryCallingCode, options.fromCountry, metadata);
      return addExtension(formattedNumber, input.ext, metadata, options.formatExtension);

    default:
      throw new Error("Unknown \"format\" argument passed to \"formatNumber()\": \"".concat(format, "\""));
  }
}

function formatNationalNumber$1(number, carrierCode, formatAs, metadata, options) {
  var format = chooseFormatForNumber(metadata.formats(), number);

  if (!format) {
    return number;
  }

  return formatNationalNumberUsingFormat(number, format, {
    useInternationalFormat: formatAs === 'INTERNATIONAL',
    withNationalPrefix: format.nationalPrefixIsOptionalWhenFormattingInNationalFormat() && options && options.nationalPrefix === false ? false : true,
    carrierCode: carrierCode,
    metadata: metadata
  });
}

function chooseFormatForNumber(availableFormats, nationalNnumber) {
  for (var _iterator = availableFormats, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var format = _ref;

    // Validate leading digits
    if (format.leadingDigitsPatterns().length > 0) {
      // The last leading_digits_pattern is used here, as it is the most detailed
      var lastLeadingDigitsPattern = format.leadingDigitsPatterns()[format.leadingDigitsPatterns().length - 1]; // If leading digits don't match then move on to the next phone number format

      if (nationalNnumber.search(lastLeadingDigitsPattern) !== 0) {
        continue;
      }
    } // Check that the national number matches the phone number format regular expression


    if (matchesEntirely(nationalNnumber, format.pattern())) {
      return format;
    }
  }
}

function addExtension(formattedNumber, ext, metadata, formatExtension) {
  return ext ? formatExtension(formattedNumber, ext, metadata) : formattedNumber;
}

function formatIDD(nationalNumber, carrierCode, countryCallingCode, fromCountry, metadata) {
  var fromCountryCallingCode = getCountryCallingCode$1(fromCountry, metadata.metadata); // When calling within the same country calling code.

  if (fromCountryCallingCode === countryCallingCode) {
    var formattedNumber = formatNationalNumber$1(nationalNumber, carrierCode, 'NATIONAL', metadata); // For NANPA regions, return the national format for these regions
    // but prefix it with the country calling code.

    if (countryCallingCode === '1') {
      return countryCallingCode + ' ' + formattedNumber;
    } // If regions share a country calling code, the country calling code need
    // not be dialled. This also applies when dialling within a region, so this
    // if clause covers both these cases. Technically this is the case for
    // dialling from La Reunion to other overseas departments of France (French
    // Guiana, Martinique, Guadeloupe), but not vice versa - so we don't cover
    // this edge case for now and for those cases return the version including
    // country calling code. Details here:
    // http://www.petitfute.com/voyage/225-info-pratiques-reunion
    //


    return formattedNumber;
  }

  var iddPrefix = getIddPrefix(fromCountry, undefined, metadata.metadata);

  if (iddPrefix) {
    return "".concat(iddPrefix, " ").concat(countryCallingCode, " ").concat(formatNationalNumber$1(nationalNumber, null, 'INTERNATIONAL', metadata));
  }
}

function _objectSpread$7(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty$a(target, key, source[key]); }); } return target; }

function _defineProperty$a(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _classCallCheck$7(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$7(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$7(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$7(Constructor.prototype, protoProps); if (staticProps) _defineProperties$7(Constructor, staticProps); return Constructor; }

var PhoneNumber =
/*#__PURE__*/
function () {
  function PhoneNumber(countryCallingCode, nationalNumber, metadata) {
    _classCallCheck$7(this, PhoneNumber);

    if (!countryCallingCode) {
      throw new TypeError('`country` or `countryCallingCode` not passed');
    }

    if (!nationalNumber) {
      throw new TypeError('`nationalNumber` not passed');
    }

    if (!metadata) {
      throw new TypeError('`metadata` not passed');
    }

    var _metadata = new Metadata(metadata); // If country code is passed then derive `countryCallingCode` from it.
    // Also store the country code as `.country`.


    if (isCountryCode(countryCallingCode)) {
      this.country = countryCallingCode;

      _metadata.country(countryCallingCode);

      countryCallingCode = _metadata.countryCallingCode();
    }

    this.countryCallingCode = countryCallingCode;
    this.nationalNumber = nationalNumber;
    this.number = '+' + this.countryCallingCode + this.nationalNumber;
    this.metadata = metadata;
  }

  _createClass$7(PhoneNumber, [{
    key: "setExt",
    value: function setExt(ext) {
      this.ext = ext;
    }
  }, {
    key: "isPossible",
    value: function isPossible() {
      return isPossiblePhoneNumber$2(this, {
        v2: true
      }, this.metadata);
    }
  }, {
    key: "isValid",
    value: function isValid() {
      return isValidNumber(this, {
        v2: true
      }, this.metadata);
    }
  }, {
    key: "isNonGeographic",
    value: function isNonGeographic() {
      var metadata = new Metadata(this.metadata);
      return metadata.isNonGeographicCallingCode(this.countryCallingCode);
    }
  }, {
    key: "isEqual",
    value: function isEqual(phoneNumber) {
      return this.number === phoneNumber.number && this.ext === phoneNumber.ext;
    } // // Is just an alias for `this.isValid() && this.country === country`.
    // // https://github.com/googlei18n/libphonenumber/blob/master/FAQ.md#when-should-i-use-isvalidnumberforregion
    // isValidForRegion(country) {
    // 	return isValidNumberForRegion(this, country, { v2: true }, this.metadata)
    // }

  }, {
    key: "getType",
    value: function getType() {
      return getNumberType(this, {
        v2: true
      }, this.metadata);
    }
  }, {
    key: "format",
    value: function format(_format, options) {
      return formatNumber(this, _format, options ? _objectSpread$7({}, options, {
        v2: true
      }) : {
        v2: true
      }, this.metadata);
    }
  }, {
    key: "formatNational",
    value: function formatNational(options) {
      return this.format('NATIONAL', options);
    }
  }, {
    key: "formatInternational",
    value: function formatInternational(options) {
      return this.format('INTERNATIONAL', options);
    }
  }, {
    key: "getURI",
    value: function getURI(options) {
      return this.format('RFC3966', options);
    }
  }]);

  return PhoneNumber;
}();

var isCountryCode = function isCountryCode(value) {
  return /^[A-Z]{2}$/.test(value);
};

var CAPTURING_DIGIT_PATTERN = new RegExp('([' + VALID_DIGITS + '])');
function stripIddPrefix(number, country, callingCode, metadata) {
  if (!country) {
    return;
  } // Check if the number is IDD-prefixed.


  var countryMetadata = new Metadata(metadata);
  countryMetadata.selectNumberingPlan(country, callingCode);
  var IDDPrefixPattern = new RegExp(countryMetadata.IDDPrefix());

  if (number.search(IDDPrefixPattern) !== 0) {
    return;
  } // Strip IDD prefix.


  number = number.slice(number.match(IDDPrefixPattern)[0].length); // If there're any digits after an IDD prefix,
  // then those digits are a country calling code.
  // Since no country code starts with a `0`,
  // the code below validates that the next digit (if present) is not `0`.

  var matchedGroups = number.match(CAPTURING_DIGIT_PATTERN);

  if (matchedGroups && matchedGroups[1] != null && matchedGroups[1].length > 0) {
    if (matchedGroups[1] === '0') {
      return;
    }
  }

  return number;
}

/**
 * Strips any national prefix (such as 0, 1) present in a
 * (possibly incomplete) number provided.
 * "Carrier codes" are only used  in Colombia and Brazil,
 * and only when dialing within those countries from a mobile phone to a fixed line number.
 * Sometimes it won't actually strip national prefix
 * and will instead prepend some digits to the `number`:
 * for example, when number `2345678` is passed with `VI` country selected,
 * it will return `{ number: "3402345678" }`, because `340` area code is prepended.
 * @param {string} number — National number digits.
 * @param {object} metadata — Metadata with country selected.
 * @return {object} `{ nationalNumber: string, nationalPrefix: string? carrierCode: string? }`.
 */
function extractNationalNumberFromPossiblyIncompleteNumber(number, metadata) {
  if (number && metadata.numberingPlan.nationalPrefixForParsing()) {
    // See METADATA.md for the description of
    // `national_prefix_for_parsing` and `national_prefix_transform_rule`.
    // Attempt to parse the first digits as a national prefix.
    var prefixPattern = new RegExp('^(?:' + metadata.numberingPlan.nationalPrefixForParsing() + ')');
    var prefixMatch = prefixPattern.exec(number);

    if (prefixMatch) {
      var nationalNumber;
      var carrierCode; // https://gitlab.com/catamphetamine/libphonenumber-js/-/blob/master/METADATA.md#national_prefix_for_parsing--national_prefix_transform_rule
      // If a `national_prefix_for_parsing` has any "capturing groups"
      // then it means that the national (significant) number is equal to
      // those "capturing groups" transformed via `national_prefix_transform_rule`,
      // and nothing could be said about the actual national prefix:
      // what is it and was it even there.
      // If a `national_prefix_for_parsing` doesn't have any "capturing groups",
      // then everything it matches is a national prefix.
      // To determine whether `national_prefix_for_parsing` matched any
      // "capturing groups", the value of the result of calling `.exec()`
      // is looked at, and if it has non-undefined values where there're
      // "capturing groups" in the regular expression, then it means
      // that "capturing groups" have been matched.
      // It's not possible to tell whether there'll be any "capturing gropus"
      // before the matching process, because a `national_prefix_for_parsing`
      // could exhibit both behaviors.

      var capturedGroupsCount = prefixMatch.length - 1;
      var hasCapturedGroups = capturedGroupsCount > 0 && prefixMatch[capturedGroupsCount];

      if (metadata.nationalPrefixTransformRule() && hasCapturedGroups) {
        nationalNumber = number.replace(prefixPattern, metadata.nationalPrefixTransformRule()); // If there's more than one captured group,
        // then carrier code is the second one.

        if (capturedGroupsCount > 1) {
          carrierCode = prefixMatch[1];
        }
      } // If there're no "capturing groups",
      // or if there're "capturing groups" but no
      // `national_prefix_transform_rule`,
      // then just strip the national prefix from the number,
      // and possibly a carrier code.
      // Seems like there could be more.
      else {
          // `prefixBeforeNationalNumber` is the whole substring matched by
          // the `national_prefix_for_parsing` regular expression.
          // There seem to be no guarantees that it's just a national prefix.
          // For example, if there's a carrier code, it's gonna be a
          // part of `prefixBeforeNationalNumber` too.
          var prefixBeforeNationalNumber = prefixMatch[0];
          nationalNumber = number.slice(prefixBeforeNationalNumber.length); // If there's at least one captured group,
          // then carrier code is the first one.

          if (hasCapturedGroups) {
            carrierCode = prefixMatch[1];
          }
        } // Tries to guess whether a national prefix was present in the input.
      // This is not something copy-pasted from Google's library:
      // they don't seem to have an equivalent for that.
      // So this isn't an "officially approved" way of doing something like that.
      // But since there seems no other existing method, this library uses it.


      var nationalPrefix;

      if (hasCapturedGroups) {
        var possiblePositionOfTheFirstCapturedGroup = number.indexOf(prefixMatch[1]);
        var possibleNationalPrefix = number.slice(0, possiblePositionOfTheFirstCapturedGroup); // Example: an Argentinian (AR) phone number `0111523456789`.
        // `prefixMatch[0]` is `01115`, and `$1` is `11`,
        // and the rest of the phone number is `23456789`.
        // The national number is transformed via `9$1` to `91123456789`.
        // National prefix `0` is detected being present at the start.
        // if (possibleNationalPrefix.indexOf(metadata.numberingPlan.nationalPrefix()) === 0) {

        if (possibleNationalPrefix === metadata.numberingPlan.nationalPrefix()) {
          nationalPrefix = metadata.numberingPlan.nationalPrefix();
        }
      } else {
        nationalPrefix = prefixMatch[0];
      }

      return {
        nationalNumber: nationalNumber,
        nationalPrefix: nationalPrefix,
        carrierCode: carrierCode
      };
    }
  }

  return {
    nationalNumber: number
  };
}

/**
 * Strips national prefix and carrier code from a complete phone number.
 * The difference from the non-"FromCompleteNumber" function is that
 * it won't extract national prefix if the resultant number is too short
 * to be a complete number for the selected phone numbering plan.
 * @param  {string} number — Complete phone number digits.
 * @param  {Metadata} metadata — Metadata with a phone numbering plan selected.
 * @return {object} `{ nationalNumber: string, carrierCode: string? }`.
 */

function extractNationalNumber(number, metadata) {
  // Parsing national prefixes and carrier codes
  // is only required for local phone numbers
  // but some people don't understand that
  // and sometimes write international phone numbers
  // with national prefixes (or maybe even carrier codes).
  // http://ucken.blogspot.ru/2016/03/trunk-prefixes-in-skype4b.html
  // Google's original library forgives such mistakes
  // and so does this library, because it has been requested:
  // https://github.com/catamphetamine/libphonenumber-js/issues/127
  var _extractNationalNumbe = extractNationalNumberFromPossiblyIncompleteNumber(number, metadata),
      nationalNumber = _extractNationalNumbe.nationalNumber,
      carrierCode = _extractNationalNumbe.carrierCode;

  if (!shouldExtractNationalPrefix(number, nationalNumber, metadata)) {
    // Don't strip the national prefix.
    return {
      nationalNumber: number
    };
  } // If a national prefix has been extracted, check to see
  // if the resultant number isn't too short.
  // Same code in Google's `libphonenumber`:
  // https://github.com/google/libphonenumber/blob/e326fa1fc4283bb05eb35cb3c15c18f98a31af33/java/libphonenumber/src/com/google/i18n/phonenumbers/PhoneNumberUtil.java#L3291-L3302
  // For some reason, they do this check right after the `national_number_pattern` check
  // this library does in `shouldExtractNationalPrefix()` function.
  // Why is there a second "resultant" number validity check?
  // They don't provide an explanation.
  // This library just copies the behavior.


  if (number.length !== nationalNumber.length + (carrierCode ? carrierCode.length : 0)) {
    // If not using legacy generated metadata (before version `1.0.18`)
    // then it has "possible lengths", so use those to validate the number length.
    if (metadata.possibleLengths()) {
      // "We require that the NSN remaining after stripping the national prefix and
      // carrier code be long enough to be a possible length for the region.
      // Otherwise, we don't do the stripping, since the original number could be
      // a valid short number."
      // https://github.com/google/libphonenumber/blob/876268eb1ad6cdc1b7b5bef17fc5e43052702d57/java/libphonenumber/src/com/google/i18n/phonenumbers/PhoneNumberUtil.java#L3236-L3250
      switch (checkNumberLength(nationalNumber, metadata)) {
        case 'TOO_SHORT':
        case 'INVALID_LENGTH':
          // case 'IS_POSSIBLE_LOCAL_ONLY':
          // Don't strip the national prefix.
          return {
            nationalNumber: number
          };
      }
    }
  }

  return {
    nationalNumber: nationalNumber,
    carrierCode: carrierCode
  };
} // In some countries, the same digit could be a national prefix
// or a leading digit of a valid phone number.
// For example, in Russia, national prefix is `8`,
// and also `800 555 35 35` is a valid number
// in which `8` is not a national prefix, but the first digit
// of a national (significant) number.
// Same's with Belarus:
// `82004910060` is a valid national (significant) number,
// but `2004910060` is not.
// To support such cases (to prevent the code from always stripping
// national prefix), a condition is imposed: a national prefix
// is not extracted when the original number is "viable" and the
// resultant number is not, a "viable" national number being the one
// that matches `national_number_pattern`.

function shouldExtractNationalPrefix(number, nationalSignificantNumber, metadata) {
  // The equivalent in Google's code is:
  // https://github.com/google/libphonenumber/blob/e326fa1fc4283bb05eb35cb3c15c18f98a31af33/java/libphonenumber/src/com/google/i18n/phonenumbers/PhoneNumberUtil.java#L2969-L3004
  if (matchesEntirely(number, metadata.nationalNumberPattern()) && !matchesEntirely(nationalSignificantNumber, metadata.nationalNumberPattern())) {
    return false;
  } // Just "possible" number check would be more relaxed, so it's not used.
  // if (isPossibleNumber(number, metadata) &&
  // 	!isPossibleNumber(numberWithNationalPrefixExtracted, metadata)) {
  // 	return false
  // }


  return true;
}

/**
 * Sometimes some people incorrectly input international phone numbers
 * without the leading `+`. This function corrects such input.
 * @param  {string} number — Phone number digits.
 * @param  {string?} country
 * @param  {string?} callingCode
 * @param  {object} metadata
 * @return {object} `{ countryCallingCode: string?, number: string }`.
 */

function extractCountryCallingCodeFromInternationalNumberWithoutPlusSign(number, country, callingCode, metadata) {
  var countryCallingCode = country ? getCountryCallingCode$1(country, metadata) : callingCode;

  if (number.indexOf(countryCallingCode) === 0) {
    metadata = new Metadata(metadata);
    metadata.selectNumberingPlan(country, callingCode);
    var possibleShorterNumber = number.slice(countryCallingCode.length);

    var _extractNationalNumbe = extractNationalNumber(possibleShorterNumber, metadata),
        possibleShorterNationalNumber = _extractNationalNumbe.nationalNumber;

    var _extractNationalNumbe2 = extractNationalNumber(number, metadata),
        nationalNumber = _extractNationalNumbe2.nationalNumber; // If the number was not valid before but is valid now,
    // or if it was too long before, we consider the number
    // with the country calling code stripped to be a better result
    // and keep that instead.
    // For example, in Germany (+49), `49` is a valid area code,
    // so if a number starts with `49`, it could be both a valid
    // national German number or an international number without
    // a leading `+`.


    if (!matchesEntirely(nationalNumber, metadata.nationalNumberPattern()) && matchesEntirely(possibleShorterNationalNumber, metadata.nationalNumberPattern()) || checkNumberLength(nationalNumber, metadata) === 'TOO_LONG') {
      return {
        countryCallingCode: countryCallingCode,
        number: possibleShorterNumber
      };
    }
  }

  return {
    number: number
  };
}

/**
 * Converts a phone number digits (possibly with a `+`)
 * into a calling code and the rest phone number digits.
 * The "rest phone number digits" could include
 * a national prefix, carrier code, and national
 * (significant) number.
 * @param  {string} number — Phone number digits (possibly with a `+`).
 * @param  {string} [country] — Default country.
 * @param  {string} [callingCode] — Default calling code (some phone numbering plans are non-geographic).
 * @param  {object} metadata
 * @return {object} `{ countryCallingCode: string?, number: string }`
 * @example
 * // Returns `{ countryCallingCode: "1", number: "2133734253" }`.
 * extractCountryCallingCode('2133734253', 'US', null, metadata)
 * extractCountryCallingCode('2133734253', null, '1', metadata)
 * extractCountryCallingCode('+12133734253', null, null, metadata)
 * extractCountryCallingCode('+12133734253', 'RU', null, metadata)
 */

function extractCountryCallingCode(number, country, callingCode, metadata) {
  if (!number) {
    return {};
  } // If this is not an international phone number,
  // then either extract an "IDD" prefix, or extract a
  // country calling code from a number by autocorrecting it
  // by prepending a leading `+` in cases when it starts
  // with the country calling code.
  // https://wikitravel.org/en/International_dialling_prefix
  // https://github.com/catamphetamine/libphonenumber-js/issues/376


  if (number[0] !== '+') {
    // Convert an "out-of-country" dialing phone number
    // to a proper international phone number.
    var numberWithoutIDD = stripIddPrefix(number, country, callingCode, metadata); // If an IDD prefix was stripped then
    // convert the number to international one
    // for subsequent parsing.

    if (numberWithoutIDD && numberWithoutIDD !== number) {
      number = '+' + numberWithoutIDD;
    } else {
      // Check to see if the number starts with the country calling code
      // for the default country. If so, we remove the country calling code,
      // and do some checks on the validity of the number before and after.
      // https://github.com/catamphetamine/libphonenumber-js/issues/376
      if (country || callingCode) {
        var _extractCountryCallin = extractCountryCallingCodeFromInternationalNumberWithoutPlusSign(number, country, callingCode, metadata),
            countryCallingCode = _extractCountryCallin.countryCallingCode,
            shorterNumber = _extractCountryCallin.number;

        if (countryCallingCode) {
          return {
            countryCallingCode: countryCallingCode,
            number: shorterNumber
          };
        }
      }

      return {
        number: number
      };
    }
  } // Fast abortion: country codes do not begin with a '0'


  if (number[1] === '0') {
    return {};
  }

  metadata = new Metadata(metadata); // The thing with country phone codes
  // is that they are orthogonal to each other
  // i.e. there's no such country phone code A
  // for which country phone code B exists
  // where B starts with A.
  // Therefore, while scanning digits,
  // if a valid country code is found,
  // that means that it is the country code.
  //

  var i = 2;

  while (i - 1 <= MAX_LENGTH_COUNTRY_CODE && i <= number.length) {
    var _countryCallingCode = number.slice(1, i);

    if (metadata.hasCallingCode(_countryCallingCode)) {
      metadata.selectNumberingPlan(_countryCallingCode);
      return {
        countryCallingCode: _countryCallingCode,
        number: number.slice(i)
      };
    }

    i++;
  }

  return {};
}

var USE_NON_GEOGRAPHIC_COUNTRY_CODE = false;
function getCountryByCallingCode(callingCode, nationalPhoneNumber, metadata) {
  /* istanbul ignore if */
  if (USE_NON_GEOGRAPHIC_COUNTRY_CODE) {
    if (metadata.isNonGeographicCallingCode(callingCode)) {
      return '001';
    }
  } // Is always non-empty, because `callingCode` is always valid


  var possibleCountries = metadata.getCountryCodesForCallingCode(callingCode);

  if (!possibleCountries) {
    return;
  } // If there's just one country corresponding to the country code,
  // then just return it, without further phone number digits validation.


  if (possibleCountries.length === 1) {
    return possibleCountries[0];
  }

  return selectCountryFromList(possibleCountries, nationalPhoneNumber, metadata.metadata);
}

function selectCountryFromList(possibleCountries, nationalPhoneNumber, metadata) {
  // Re-create `metadata` because it will be selecting a `country`.
  metadata = new Metadata(metadata);

  for (var _iterator = possibleCountries, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var country = _ref;
    metadata.country(country); // Leading digits check would be the simplest and fastest one.
    // Leading digits patterns are only defined for about 20% of all countries.
    // https://gitlab.com/catamphetamine/libphonenumber-js/blob/master/METADATA.md#leading_digits
    // Matching "leading digits" is a sufficient but not necessary condition.

    if (metadata.leadingDigits()) {
      if (nationalPhoneNumber && nationalPhoneNumber.search(metadata.leadingDigits()) === 0) {
        return country;
      }
    } // Else perform full validation with all of those
    // fixed-line/mobile/etc regular expressions.
    else if (getNumberType({
        phone: nationalPhoneNumber,
        country: country
      }, undefined, metadata.metadata)) {
        return country;
      }
  }
}

// This is a port of Google Android `libphonenumber`'s
// This prevents malicious input from consuming CPU.

var MAX_INPUT_STRING_LENGTH = 250; // This consists of the plus symbol, digits, and arabic-indic digits.

var PHONE_NUMBER_START_PATTERN = new RegExp('[' + PLUS_CHARS + VALID_DIGITS + ']'); // Regular expression of trailing characters that we want to remove.
// A trailing `#` is sometimes used when writing phone numbers with extensions in US.
// Example: "+1 (645) 123 1234-910#" number has extension "910".

var AFTER_PHONE_NUMBER_END_PATTERN = new RegExp('[^' + VALID_DIGITS + '#' + ']+$');
//
// ```js
// parse('8 (800) 555-35-35', 'RU')
// parse('8 (800) 555-35-35', 'RU', metadata)
// parse('8 (800) 555-35-35', { country: { default: 'RU' } })
// parse('8 (800) 555-35-35', { country: { default: 'RU' } }, metadata)
// parse('+7 800 555 35 35')
// parse('+7 800 555 35 35', metadata)
// ```
//

function parse(text, options, metadata) {
  // If assigning the `{}` default value is moved to the arguments above,
  // code coverage would decrease for some weird reason.
  options = options || {};
  metadata = new Metadata(metadata); // Validate `defaultCountry`.

  if (options.defaultCountry && !metadata.hasCountry(options.defaultCountry)) {
    if (options.v2) {
      throw new ParseError('INVALID_COUNTRY');
    }

    throw new Error("Unknown country: ".concat(options.defaultCountry));
  } // Parse the phone number.


  var _parseInput = parseInput(text, options.v2, options.extract),
      formattedPhoneNumber = _parseInput.number,
      ext = _parseInput.ext,
      error = _parseInput.error; // If the phone number is not viable then return nothing.


  if (!formattedPhoneNumber) {
    if (options.v2) {
      if (error === 'TOO_SHORT') {
        throw new ParseError('TOO_SHORT');
      }

      throw new ParseError('NOT_A_NUMBER');
    }

    return {};
  }

  var _parsePhoneNumber = parsePhoneNumber$2(formattedPhoneNumber, options.defaultCountry, options.defaultCallingCode, metadata),
      country = _parsePhoneNumber.country,
      nationalNumber = _parsePhoneNumber.nationalNumber,
      countryCallingCode = _parsePhoneNumber.countryCallingCode,
      carrierCode = _parsePhoneNumber.carrierCode;

  if (!metadata.hasSelectedNumberingPlan()) {
    if (options.v2) {
      throw new ParseError('INVALID_COUNTRY');
    }

    return {};
  } // Validate national (significant) number length.


  if (!nationalNumber || nationalNumber.length < MIN_LENGTH_FOR_NSN) {
    // Won't throw here because the regexp already demands length > 1.

    /* istanbul ignore if */
    if (options.v2) {
      throw new ParseError('TOO_SHORT');
    } // Google's demo just throws an error in this case.


    return {};
  } // Validate national (significant) number length.
  //
  // A sidenote:
  //
  // They say that sometimes national (significant) numbers
  // can be longer than `MAX_LENGTH_FOR_NSN` (e.g. in Germany).
  // https://github.com/googlei18n/libphonenumber/blob/7e1748645552da39c4e1ba731e47969d97bdb539/resources/phonenumber.proto#L36
  // Such numbers will just be discarded.
  //


  if (nationalNumber.length > MAX_LENGTH_FOR_NSN) {
    if (options.v2) {
      throw new ParseError('TOO_LONG');
    } // Google's demo just throws an error in this case.


    return {};
  }

  if (options.v2) {
    var phoneNumber = new PhoneNumber(countryCallingCode, nationalNumber, metadata.metadata);

    if (country) {
      phoneNumber.country = country;
    }

    if (carrierCode) {
      phoneNumber.carrierCode = carrierCode;
    }

    if (ext) {
      phoneNumber.ext = ext;
    }

    return phoneNumber;
  } // Check if national phone number pattern matches the number.
  // National number pattern is different for each country,
  // even for those ones which are part of the "NANPA" group.


  var valid = (options.extended ? metadata.hasSelectedNumberingPlan() : country) ? matchesEntirely(nationalNumber, metadata.nationalNumberPattern()) : false;

  if (!options.extended) {
    return valid ? result(country, nationalNumber, ext) : {};
  } // isInternational: countryCallingCode !== undefined


  return {
    country: country,
    countryCallingCode: countryCallingCode,
    carrierCode: carrierCode,
    valid: valid,
    possible: valid ? true : options.extended === true && metadata.possibleLengths() && isPossibleNumber(nationalNumber, metadata) ? true : false,
    phone: nationalNumber,
    ext: ext
  };
}
/**
 * Extracts a formatted phone number from text.
 * Doesn't guarantee that the extracted phone number
 * is a valid phone number (for example, doesn't validate its length).
 * @param  {string} text
 * @param  {boolean} [extract] — If `false`, then will parse the entire `text` as a phone number.
 * @param  {boolean} [throwOnError] — By default, it won't throw if the text is too long.
 * @return {string}
 * @example
 * // Returns "(213) 373-4253".
 * extractFormattedPhoneNumber("Call (213) 373-4253 for assistance.")
 */

function extractFormattedPhoneNumber$1(text, extract, throwOnError) {
  if (!text) {
    return;
  }

  if (text.length > MAX_INPUT_STRING_LENGTH) {
    if (throwOnError) {
      throw new ParseError('TOO_LONG');
    }

    return;
  }

  if (extract === false) {
    return text;
  } // Attempt to extract a possible number from the string passed in


  var startsAt = text.search(PHONE_NUMBER_START_PATTERN);

  if (startsAt < 0) {
    return;
  }

  return text // Trim everything to the left of the phone number
  .slice(startsAt) // Remove trailing non-numerical characters
  .replace(AFTER_PHONE_NUMBER_END_PATTERN, '');
}
/**
 * @param  {string} text - Input.
 * @param  {boolean} v2 - Legacy API functions don't pass `v2: true` flag.
 * @param  {boolean} [extract] - Whether to extract a phone number from `text`, or attempt to parse the entire text as a phone number.
 * @return {object} `{ ?number, ?ext }`.
 */


function parseInput(text, v2, extract) {
  // Parse RFC 3966 phone number URI.
  if (text && text.indexOf('tel:') === 0) {
    return parseRFC3966(text);
  }

  var number = extractFormattedPhoneNumber$1(text, extract, v2); // If the phone number is not viable, then abort.

  if (!number) {
    return {};
  }

  if (!isViablePhoneNumber(number)) {
    if (isViablePhoneNumberStart(number)) {
      return {
        error: 'TOO_SHORT'
      };
    }

    return {};
  } // Attempt to parse extension first, since it doesn't require region-specific
  // data and we want to have the non-normalised number here.


  var withExtensionStripped = extractExtension(number);

  if (withExtensionStripped.ext) {
    return withExtensionStripped;
  }

  return {
    number: number
  };
}
/**
 * Creates `parse()` result object.
 */


function result(country, nationalNumber, ext) {
  var result = {
    country: country,
    phone: nationalNumber
  };

  if (ext) {
    result.ext = ext;
  }

  return result;
}
/**
 * Parses a viable phone number.
 * @param {string} formattedPhoneNumber — Example: "(213) 373-4253".
 * @param {string} [defaultCountry]
 * @param {string} [defaultCallingCode]
 * @param {Metadata} metadata
 * @return {object} Returns `{ country: string?, countryCallingCode: string?, nationalNumber: string? }`.
 */


function parsePhoneNumber$2(formattedPhoneNumber, defaultCountry, defaultCallingCode, metadata) {
  // Extract calling code from phone number.
  var _extractCountryCallin = extractCountryCallingCode(parseIncompletePhoneNumber(formattedPhoneNumber), defaultCountry, defaultCallingCode, metadata.metadata),
      countryCallingCode = _extractCountryCallin.countryCallingCode,
      number = _extractCountryCallin.number; // Choose a country by `countryCallingCode`.


  var country;

  if (countryCallingCode) {
    metadata.selectNumberingPlan(countryCallingCode);
  } // If `formattedPhoneNumber` is in "national" format
  // then `number` is defined and `countryCallingCode` isn't.
  else if (number && (defaultCountry || defaultCallingCode)) {
      metadata.selectNumberingPlan(defaultCountry, defaultCallingCode);

      if (defaultCountry) {
        country = defaultCountry;
      }

      countryCallingCode = defaultCallingCode || getCountryCallingCode$1(defaultCountry, metadata.metadata);
    } else return {};

  if (!number) {
    return {
      countryCallingCode: countryCallingCode
    };
  }

  var _extractNationalNumbe = extractNationalNumber(parseIncompletePhoneNumber(number), metadata),
      nationalNumber = _extractNationalNumbe.nationalNumber,
      carrierCode = _extractNationalNumbe.carrierCode; // Sometimes there are several countries
  // corresponding to the same country phone code
  // (e.g. NANPA countries all having `1` country phone code).
  // Therefore, to reliably determine the exact country,
  // national (significant) number should have been parsed first.
  //
  // When `metadata.json` is generated, all "ambiguous" country phone codes
  // get their countries populated with the full set of
  // "phone number type" regular expressions.
  //


  var exactCountry = getCountryByCallingCode(countryCallingCode, nationalNumber, metadata);

  if (exactCountry) {
    country = exactCountry;
    /* istanbul ignore if */

    if (exactCountry === '001') ; else {
      metadata.country(country);
    }
  }

  return {
    country: country,
    countryCallingCode: countryCallingCode,
    nationalNumber: nationalNumber,
    carrierCode: carrierCode
  };
}

function _objectSpread$6(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty$9(target, key, source[key]); }); } return target; }

function _defineProperty$9(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function parsePhoneNumber$1(text, options, metadata) {
  return parse(text, _objectSpread$6({}, options, {
    v2: true
  }), metadata);
}

function _typeof$4(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$4 = function _typeof(obj) { return typeof obj; }; } else { _typeof$4 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$4(obj); }

function _objectSpread$5(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty$8(target, key, source[key]); }); } return target; }

function _defineProperty$8(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray$2(arr, i) { return _arrayWithHoles$2(arr) || _iterableToArrayLimit$2(arr, i) || _nonIterableRest$2(); }

function _nonIterableRest$2() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit$2(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$2(arr) { if (Array.isArray(arr)) return arr; }
function normalizeArguments(args) {
  var _Array$prototype$slic = Array.prototype.slice.call(args),
      _Array$prototype$slic2 = _slicedToArray$2(_Array$prototype$slic, 4),
      arg_1 = _Array$prototype$slic2[0],
      arg_2 = _Array$prototype$slic2[1],
      arg_3 = _Array$prototype$slic2[2],
      arg_4 = _Array$prototype$slic2[3];

  var text;
  var options;
  var metadata; // If the phone number is passed as a string.
  // `parsePhoneNumber('88005553535', ...)`.

  if (typeof arg_1 === 'string') {
    text = arg_1;
  } else throw new TypeError('A text for parsing must be a string.'); // If "default country" argument is being passed then move it to `options`.
  // `parsePhoneNumber('88005553535', 'RU', [options], metadata)`.


  if (!arg_2 || typeof arg_2 === 'string') {
    if (arg_4) {
      options = arg_3;
      metadata = arg_4;
    } else {
      options = undefined;
      metadata = arg_3;
    }

    if (arg_2) {
      options = _objectSpread$5({
        defaultCountry: arg_2
      }, options);
    }
  } // `defaultCountry` is not passed.
  // Example: `parsePhoneNumber('+78005553535', [options], metadata)`.
  else if (isObject$3(arg_2)) {
      if (arg_3) {
        options = arg_2;
        metadata = arg_3;
      } else {
        metadata = arg_2;
      }
    } else throw new Error("Invalid second argument: ".concat(arg_2));

  return {
    text: text,
    options: options,
    metadata: metadata
  };
} // Otherwise istanbul would show this as "branch not covered".

/* istanbul ignore next */

var isObject$3 = function isObject(_) {
  return _typeof$4(_) === 'object';
};

function _objectSpread$4(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty$7(target, key, source[key]); }); } return target; }

function _defineProperty$7(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function parsePhoneNumberFromString$1(text, options, metadata) {
  // Validate `defaultCountry`.
  if (options && options.defaultCountry && !isSupportedCountry(options.defaultCountry, metadata)) {
    options = _objectSpread$4({}, options, {
      defaultCountry: undefined
    });
  } // Parse phone number.


  try {
    return parsePhoneNumber$1(text, options, metadata);
  } catch (error) {
    /* istanbul ignore else */
    if (error instanceof ParseError) ; else {
      throw error;
    }
  }
}

function parsePhoneNumberFromString() {
  var _normalizeArguments = normalizeArguments(arguments),
      text = _normalizeArguments.text,
      options = _normalizeArguments.options,
      metadata = _normalizeArguments.metadata;

  return parsePhoneNumberFromString$1(text, options, metadata);
}

function _objectSpread$3(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty$6(target, key, source[key]); }); } return target; }

function _defineProperty$6(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function isValidPhoneNumber$1() {
  var _normalizeArguments = normalizeArguments(arguments),
      text = _normalizeArguments.text,
      options = _normalizeArguments.options,
      metadata = _normalizeArguments.metadata;

  options = _objectSpread$3({}, options, {
    extract: false
  });
  var phoneNumber = parsePhoneNumberFromString$1(text, options, metadata);
  return phoneNumber && phoneNumber.isValid() || false;
}

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty$5(target, key, source[key]); }); } return target; }

function _defineProperty$5(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function isPossiblePhoneNumber$1() {
  var _normalizeArguments = normalizeArguments(arguments),
      text = _normalizeArguments.text,
      options = _normalizeArguments.options,
      metadata = _normalizeArguments.metadata;

  options = _objectSpread$2({}, options, {
    extract: false
  });
  var phoneNumber = parsePhoneNumberFromString$1(text, options, metadata);
  return phoneNumber && phoneNumber.isPossible() || false;
}

function _classCallCheck$6(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$6(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$6(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$6(Constructor.prototype, protoProps); if (staticProps) _defineProperties$6(Constructor, staticProps); return Constructor; }

function _defineProperty$4(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var AsYouTypeState =
/*#__PURE__*/
function () {
  function AsYouTypeState(_ref) {
    var _this = this;

    var onCountryChange = _ref.onCountryChange,
        onCallingCodeChange = _ref.onCallingCodeChange;

    _classCallCheck$6(this, AsYouTypeState);

    _defineProperty$4(this, "update", function (properties) {
      for (var _i = 0, _Object$keys = Object.keys(properties); _i < _Object$keys.length; _i++) {
        var key = _Object$keys[_i];
        _this[key] = properties[key];
      }
    });

    this.onCountryChange = onCountryChange;
    this.onCallingCodeChange = onCallingCodeChange;
  }

  _createClass$6(AsYouTypeState, [{
    key: "reset",
    value: function reset(defaultCountry, defaultCallingCode) {
      this.international = false;
      this.IDDPrefix = undefined;
      this.missingPlus = undefined;
      this.callingCode = undefined;
      this.digits = '';
      this.resetNationalSignificantNumber();
      this.initCountryAndCallingCode(defaultCountry, defaultCallingCode);
    }
  }, {
    key: "resetNationalSignificantNumber",
    value: function resetNationalSignificantNumber() {
      this.nationalSignificantNumber = this.getNationalDigits();
      this.nationalSignificantNumberMatchesInput = true;
      this.nationalPrefix = undefined;
      this.carrierCode = undefined;
      this.complexPrefixBeforeNationalSignificantNumber = undefined;
    }
  }, {
    key: "initCountryAndCallingCode",
    value: function initCountryAndCallingCode(country, callingCode) {
      this.setCountry(country);
      this.setCallingCode(callingCode);
    }
  }, {
    key: "setCountry",
    value: function setCountry(country) {
      this.country = country;
      this.onCountryChange(country);
    }
  }, {
    key: "setCallingCode",
    value: function setCallingCode(callingCode) {
      this.callingCode = callingCode;
      return this.onCallingCodeChange(this.country, callingCode);
    }
  }, {
    key: "startInternationalNumber",
    value: function startInternationalNumber() {
      // Prepend the `+` to parsed input.
      this.international = true; // If a default country was set then reset it
      // because an explicitly international phone
      // number is being entered.

      this.initCountryAndCallingCode();
    }
  }, {
    key: "appendDigits",
    value: function appendDigits(nextDigits) {
      this.digits += nextDigits;
    }
  }, {
    key: "appendNationalSignificantNumberDigits",
    value: function appendNationalSignificantNumberDigits(nextDigits) {
      this.nationalSignificantNumber += nextDigits;
    }
    /**
     * Returns the part of `this.digits` that corresponds to the national number.
     * Basically, all digits that have been input by the user, except for the
     * international prefix and the country calling code part
     * (if the number is an international one).
     * @return {string}
     */

  }, {
    key: "getNationalDigits",
    value: function getNationalDigits() {
      if (this.international) {
        return this.digits.slice((this.IDDPrefix ? this.IDDPrefix.length : 0) + (this.callingCode ? this.callingCode.length : 0));
      }

      return this.digits;
    }
  }, {
    key: "getDigitsWithoutInternationalPrefix",
    value: function getDigitsWithoutInternationalPrefix() {
      if (this.international) {
        if (this.IDDPrefix) {
          return this.digits.slice(this.IDDPrefix.length);
        }
      }

      return this.digits;
    }
  }]);

  return AsYouTypeState;
}();

// Should be the same as `DIGIT_PLACEHOLDER` in `libphonenumber-metadata-generator`.
var DIGIT_PLACEHOLDER = 'x'; // '\u2008' (punctuation space)

var DIGIT_PLACEHOLDER_MATCHER = new RegExp(DIGIT_PLACEHOLDER); // Counts all occurences of a symbol in a string.
// http://stackoverflow.com/questions/202605/repeat-string-javascript

function repeat(string, times) {
  if (times < 1) {
    return '';
  }

  var result = '';

  while (times > 1) {
    if (times & 1) {
      result += string;
    }

    times >>= 1;
    string += string;
  }

  return result + string;
}
function cutAndStripNonPairedParens(string, cutBeforeIndex) {
  if (string[cutBeforeIndex] === ')') {
    cutBeforeIndex++;
  }

  return stripNonPairedParens(string.slice(0, cutBeforeIndex));
}
function stripNonPairedParens(string) {
  var dangling_braces = [];
  var i = 0;

  while (i < string.length) {
    if (string[i] === '(') {
      dangling_braces.push(i);
    } else if (string[i] === ')') {
      dangling_braces.pop();
    }

    i++;
  }

  var start = 0;
  var cleared_string = '';
  dangling_braces.push(string.length);

  for (var _i2 = 0, _dangling_braces = dangling_braces; _i2 < _dangling_braces.length; _i2++) {
    var index = _dangling_braces[_i2];
    cleared_string += string.slice(start, index);
    start = index + 1;
  }

  return cleared_string;
}
function populateTemplateWithDigits(template, position, digits) {
  // Using `.split('')` to iterate through a string here
  // to avoid requiring `Symbol.iterator` polyfill.
  // `.split('')` is generally not safe for Unicode,
  // but in this particular case for `digits` it is safe.
  // for (const digit of digits)
  for (var _iterator2 = digits.split(''), _isArray2 = Array.isArray(_iterator2), _i3 = 0, _iterator2 = _isArray2 ? _iterator2 : _iterator2[Symbol.iterator]();;) {
    var _ref2;

    if (_isArray2) {
      if (_i3 >= _iterator2.length) break;
      _ref2 = _iterator2[_i3++];
    } else {
      _i3 = _iterator2.next();
      if (_i3.done) break;
      _ref2 = _i3.value;
    }

    var digit = _ref2;

    // If there is room for more digits in current `template`,
    // then set the next digit in the `template`,
    // and return the formatted digits so far.
    // If more digits are entered than the current format could handle.
    if (template.slice(position + 1).search(DIGIT_PLACEHOLDER_MATCHER) < 0) {
      return;
    }

    position = template.search(DIGIT_PLACEHOLDER_MATCHER);
    template = template.replace(DIGIT_PLACEHOLDER_MATCHER, digit);
  }

  return [template, position];
}

function formatCompleteNumber(state, format, _ref) {
  var metadata = _ref.metadata,
      shouldTryNationalPrefixFormattingRule = _ref.shouldTryNationalPrefixFormattingRule,
      getSeparatorAfterNationalPrefix = _ref.getSeparatorAfterNationalPrefix;
  var matcher = new RegExp("^(?:".concat(format.pattern(), ")$"));

  if (matcher.test(state.nationalSignificantNumber)) {
    return formatNationalNumberWithAndWithoutNationalPrefixFormattingRule(state, format, {
      metadata: metadata,
      shouldTryNationalPrefixFormattingRule: shouldTryNationalPrefixFormattingRule,
      getSeparatorAfterNationalPrefix: getSeparatorAfterNationalPrefix
    });
  }
}
function canFormatCompleteNumber(nationalSignificantNumber, metadata) {
  return checkNumberLength(nationalSignificantNumber, metadata) === 'IS_POSSIBLE';
}

function formatNationalNumberWithAndWithoutNationalPrefixFormattingRule(state, format, _ref2) {
  var metadata = _ref2.metadata,
      shouldTryNationalPrefixFormattingRule = _ref2.shouldTryNationalPrefixFormattingRule,
      getSeparatorAfterNationalPrefix = _ref2.getSeparatorAfterNationalPrefix;
  // `format` has already been checked for `nationalPrefix` requirement.
  state.nationalSignificantNumber;
      state.international;
      state.nationalPrefix;
      state.carrierCode; // Format the number with using `national_prefix_formatting_rule`.
  // If the resulting formatted number is a valid formatted number, then return it.
  //
  // Google's AsYouType formatter is different in a way that it doesn't try
  // to format using the "national prefix formatting rule", and instead it
  // simply prepends a national prefix followed by a " " character.
  // This code does that too, but as a fallback.
  // The reason is that "national prefix formatting rule" may use parentheses,
  // which wouldn't be included has it used the simpler Google's way.
  //

  if (shouldTryNationalPrefixFormattingRule(format)) {
    var formattedNumber = formatNationalNumber(state, format, {
      useNationalPrefixFormattingRule: true,
      getSeparatorAfterNationalPrefix: getSeparatorAfterNationalPrefix,
      metadata: metadata
    });

    if (formattedNumber) {
      return formattedNumber;
    }
  } // Format the number without using `national_prefix_formatting_rule`.


  return formatNationalNumber(state, format, {
    useNationalPrefixFormattingRule: false,
    getSeparatorAfterNationalPrefix: getSeparatorAfterNationalPrefix,
    metadata: metadata
  });
}

function formatNationalNumber(state, format, _ref3) {
  var metadata = _ref3.metadata,
      useNationalPrefixFormattingRule = _ref3.useNationalPrefixFormattingRule,
      getSeparatorAfterNationalPrefix = _ref3.getSeparatorAfterNationalPrefix;
  var formattedNationalNumber = formatNationalNumberUsingFormat(state.nationalSignificantNumber, format, {
    carrierCode: state.carrierCode,
    useInternationalFormat: state.international,
    withNationalPrefix: useNationalPrefixFormattingRule,
    metadata: metadata
  });

  if (!useNationalPrefixFormattingRule) {
    if (state.nationalPrefix) {
      // If a national prefix was extracted, then just prepend it,
      // followed by a " " character.
      formattedNationalNumber = state.nationalPrefix + getSeparatorAfterNationalPrefix(format) + formattedNationalNumber;
    } else if (state.complexPrefixBeforeNationalSignificantNumber) {
      formattedNationalNumber = state.complexPrefixBeforeNationalSignificantNumber + ' ' + formattedNationalNumber;
    }
  }

  if (isValidFormattedNationalNumber(formattedNationalNumber, state)) {
    return formattedNationalNumber;
  }
} // Check that the formatted phone number contains exactly
// the same digits that have been input by the user.
// For example, when "0111523456789" is input for `AR` country,
// the extracted `this.nationalSignificantNumber` is "91123456789",
// which means that the national part of `this.digits` isn't simply equal to
// `this.nationalPrefix` + `this.nationalSignificantNumber`.
//
// Also, a `format` can add extra digits to the `this.nationalSignificantNumber`
// being formatted via `metadata[country].national_prefix_transform_rule`.
// For example, for `VI` country, it prepends `340` to the national number,
// and if this check hasn't been implemented, then there would be a bug
// when `340` "area coude" is "duplicated" during input for `VI` country:
// https://github.com/catamphetamine/libphonenumber-js/issues/318
//
// So, all these "gotchas" are filtered out.
//
// In the original Google's code, the comments say:
// "Check that we didn't remove nor add any extra digits when we matched
// this formatting pattern. This usually happens after we entered the last
// digit during AYTF. Eg: In case of MX, we swallow mobile token (1) when
// formatted but AYTF should retain all the number entered and not change
// in order to match a format (of same leading digits and length) display
// in that way."
// "If it's the same (i.e entered number and format is same), then it's
// safe to return this in formatted number as nothing is lost / added."
// Otherwise, don't use this format.
// https://github.com/google/libphonenumber/commit/3e7c1f04f5e7200f87fb131e6f85c6e99d60f510#diff-9149457fa9f5d608a11bb975c6ef4bc5
// https://github.com/google/libphonenumber/commit/3ac88c7106e7dcb553bcc794b15f19185928a1c6#diff-2dcb77e833422ee304da348b905cde0b
//


function isValidFormattedNationalNumber(formattedNationalNumber, state) {
  return parseDigits(formattedNationalNumber) === state.getNationalDigits();
}

function _classCallCheck$5(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$5(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$5(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$5(Constructor.prototype, protoProps); if (staticProps) _defineProperties$5(Constructor, staticProps); return Constructor; }

var PatternMatcher =
/*#__PURE__*/
function () {
  function PatternMatcher(pattern) {
    _classCallCheck$5(this, PatternMatcher);

    this.matchTree = new PatternParser().parse(pattern); // console.log(JSON.stringify(this.matchTree, null, 2))
  }

  _createClass$5(PatternMatcher, [{
    key: "match",
    value: function match(string) {
      var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
          allowOverflow = _ref.allowOverflow;

      if (!string) {
        throw new Error('String is required');
      }

      var result = _match(string.split(''), this.matchTree, true);

      if (result && result.match) {
        delete result.matchedChars;
      }

      if (result && result.overflow) {
        if (!allowOverflow) {
          return;
        }
      }

      return result;
    }
  }]);

  return PatternMatcher;
}();

function _match(characters, tree, last) {
  if (typeof tree === 'string') {
    if (last) {
      if (characters.length > tree.length) {
        return {
          overflow: true
        };
      }
    }

    var characterString = characters.join('');

    if (tree.indexOf(characterString) === 0) {
      if (characters.length === tree.length) {
        return {
          match: true,
          matchedChars: characters
        };
      }

      return {
        partialMatch: true // matchedChars: characters

      };
    }

    if (characterString.indexOf(tree) === 0) {
      return {
        match: true,
        matchedChars: characters.slice(0, tree.length)
      };
    }

    return;
  }

  if (Array.isArray(tree)) {
    var restCharacters = characters.slice();
    var i = 0;

    while (i < tree.length) {
      var subtree = tree[i];

      var result = _match(restCharacters, subtree, last && i === tree.length - 1);

      if (!result || result.overflow) {
        return result;
      }

      if (result.match) {
        // Continue with the next subtree with the rest of the characters.
        restCharacters = restCharacters.slice(result.matchedChars.length);

        if (restCharacters.length === 0) {
          if (i === tree.length - 1) {
            return {
              match: true,
              matchedChars: characters
            };
          } else {
            return {
              partialMatch: true // matchedChars: characters

            };
          }
        }
      } else if (result.partialMatch) {
        return {
          partialMatch: true // matchedChars: characters

        };
      } else {
        throw new Error("Unsupported match result:\n".concat(JSON.stringify(result, null, 2)));
      }

      i++;
    }

    if (last) {
      return {
        overflow: true
      };
    }

    return {
      match: true,
      matchedChars: characters.slice(0, characters.length - restCharacters.length)
    };
  }

  switch (tree.op) {
    case '|':
      var partialMatch;

      for (var _iterator = tree.args, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
        var _ref2;

        if (_isArray) {
          if (_i >= _iterator.length) break;
          _ref2 = _iterator[_i++];
        } else {
          _i = _iterator.next();
          if (_i.done) break;
          _ref2 = _i.value;
        }

        var branch = _ref2;

        var _result = _match(characters, branch, last);

        if (_result) {
          if (_result.overflow) {
            return _result;
          } else if (_result.match) {
            return {
              match: true,
              matchedChars: _result.matchedChars
            };
          } else if (_result.partialMatch) {
            partialMatch = true;
          }
        }
      }

      if (partialMatch) {
        return {
          partialMatch: true // matchedChars: ...

        };
      } // Not even a partial match.


      return;

    case '[]':
      for (var _iterator2 = tree.args, _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _iterator2[Symbol.iterator]();;) {
        var _ref3;

        if (_isArray2) {
          if (_i2 >= _iterator2.length) break;
          _ref3 = _iterator2[_i2++];
        } else {
          _i2 = _iterator2.next();
          if (_i2.done) break;
          _ref3 = _i2.value;
        }

        var _char = _ref3;

        if (characters[0] === _char) {
          if (characters.length === 1) {
            return {
              match: true,
              matchedChars: characters
            };
          }

          if (last) {
            return {
              overflow: true
            };
          }

          return {
            match: true,
            matchedChars: [_char]
          };
        }
      } // No character matches.


      return;

    default:
      throw new Error("Unsupported instruction tree: ".concat(tree));
  }
}

var OPERATOR = new RegExp( // any of:
'(' + // or operator
'\\|' + // or
'|' + // or group start
'\\(\\?\\:' + // or
'|' + // or group end
'\\)' + // or
'|' + // one-of set start
'\\[' + // or
'|' + // one-of set end
'\\]' + ')');

var PatternParser =
/*#__PURE__*/
function () {
  function PatternParser() {
    _classCallCheck$5(this, PatternParser);
  }

  _createClass$5(PatternParser, [{
    key: "parse",
    value: function parse(pattern) {
      this.context = [{
        or: true,
        instructions: []
      }];
      this.parsePattern(pattern);

      if (this.context.length !== 1) {
        throw new Error('Non-finalized contexts left when pattern parse ended');
      }

      var _this$context$ = this.context[0],
          branches = _this$context$.branches,
          instructions = _this$context$.instructions;

      if (branches) {
        return [{
          op: '|',
          args: branches.concat([instructions])
        }];
      }

      if (instructions.length === 0) {
        throw new Error('Pattern is required');
      }

      return instructions;
    }
  }, {
    key: "startContext",
    value: function startContext(context) {
      this.context.push(context);
    }
  }, {
    key: "endContext",
    value: function endContext() {
      this.context.pop();
    }
  }, {
    key: "getContext",
    value: function getContext() {
      return this.context[this.context.length - 1];
    }
  }, {
    key: "parsePattern",
    value: function parsePattern(pattern) {
      if (!pattern) {
        throw new Error('Empty pattern passed');
      }

      var match = pattern.match(OPERATOR);

      if (!match) {
        this.getContext().instructions = this.getContext().instructions.concat(pattern.split(''));
        return;
      }

      var operator = match[1];
      var before = pattern.slice(0, match.index);
      var rightPart = pattern.slice(match.index + operator.length);

      switch (operator) {
        case '(?:':
          if (before) {
            this.parsePattern(before);
          }

          this.startContext({
            or: true,
            instructions: [],
            branches: []
          });
          break;

        case ')':
          if (!this.getContext().or) {
            throw new Error('")" operator must be preceded by "(?:" operator');
          }

          if (before) {
            this.parsePattern(before);
          }

          if (this.getContext().instructions.length === 0) {
            throw new Error('No instructions found after "|" operator in an "or" group');
          }

          var _this$getContext = this.getContext(),
              branches = _this$getContext.branches;

          branches.push(this.getContext().instructions);
          this.endContext();
          this.getContext().instructions.push({
            op: '|',
            args: branches
          });
          break;

        case '|':
          if (!this.getContext().or) {
            throw new Error('"|" operator can only be used inside "or" groups');
          }

          if (before) {
            this.parsePattern(before);
          } // The top-level is an implicit "or" group, if required.


          if (!this.getContext().branches) {
            if (this.context.length === 1) {
              this.getContext().branches = [];
            } else {
              throw new Error('"branches" not found in an "or" group context');
            }
          }

          this.getContext().branches.push(this.getContext().instructions);
          this.getContext().instructions = [];
          break;

        case '[':
          if (before) {
            this.parsePattern(before);
          }

          this.startContext({
            oneOfSet: true
          });
          break;

        case ']':
          if (!this.getContext().oneOfSet) {
            throw new Error('"]" operator must be preceded by "[" operator');
          }

          this.endContext();
          this.getContext().instructions.push({
            op: '[]',
            args: parseOneOfSet(before)
          });
          break;

        default:
          throw new Error("Unknown operator: ".concat(operator));
      }

      if (rightPart) {
        this.parsePattern(rightPart);
      }
    }
  }]);

  return PatternParser;
}();

function parseOneOfSet(pattern) {
  var values = [];
  var i = 0;

  while (i < pattern.length) {
    if (pattern[i] === '-') {
      if (i === 0 || i === pattern.length - 1) {
        throw new Error("Couldn't parse a one-of set pattern: ".concat(pattern));
      }

      var prevValue = pattern[i - 1].charCodeAt(0) + 1;
      var nextValue = pattern[i + 1].charCodeAt(0) - 1;
      var value = prevValue;

      while (value <= nextValue) {
        values.push(String.fromCharCode(value));
        value++;
      }
    } else {
      values.push(pattern[i]);
    }

    i++;
  }

  return values;
}

function _classCallCheck$4(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$4(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$4(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$4(Constructor.prototype, protoProps); if (staticProps) _defineProperties$4(Constructor, staticProps); return Constructor; }

function _defineProperty$3(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Could be any digit, I guess.

var DUMMY_DIGIT = '9'; // I don't know why is it exactly `15`

var LONGEST_NATIONAL_PHONE_NUMBER_LENGTH = 15; // Create a phone number consisting only of the digit 9 that matches the
// `number_pattern` by applying the pattern to the "longest phone number" string.

var LONGEST_DUMMY_PHONE_NUMBER = repeat(DUMMY_DIGIT, LONGEST_NATIONAL_PHONE_NUMBER_LENGTH); // A set of characters that, if found in a national prefix formatting rules, are an indicator to
// us that we should separate the national prefix from the number when formatting.

var NATIONAL_PREFIX_SEPARATORS_PATTERN = /[- ]/; // Deprecated: Google has removed some formatting pattern related code from their repo.
// An example of a character class is "[1-4]".

var CREATE_CHARACTER_CLASS_PATTERN = function () {
  return /\[([^\[\]])*\]/g;
}; // Any digit in a regular expression that actually denotes a digit. For
// example, in the regular expression "80[0-2]\d{6,10}", the first 2 digits
// (8 and 0) are standalone digits, but the rest are not.
// Two look-aheads are needed because the number following \\d could be a
// two-digit number, since the phone number can be as long as 15 digits.


var CREATE_STANDALONE_DIGIT_PATTERN = function () {
  return /\d(?=[^,}][^,}])/g;
}; // A regular expression that is used to determine if a `format` is
// suitable to be used in the "as you type formatter".
// A `format` is suitable when the resulting formatted number has
// the same digits as the user has entered.
//
// In the simplest case, that would mean that the format
// doesn't add any additional digits when formatting a number.
// Google says that it also shouldn't add "star" (`*`) characters,
// like it does in some Israeli formats.
// Such basic format would only contain "valid punctuation"
// and "captured group" identifiers ($1, $2, etc).
//
// An example of a format that adds additional digits:
//
// Country: `AR` (Argentina).
// Format:
// {
//    "pattern": "(\\d)(\\d{2})(\\d{4})(\\d{4})",
//    "leading_digits_patterns": ["91"],
//    "national_prefix_formatting_rule": "0$1",
//    "format": "$2 15-$3-$4",
//    "international_format": "$1 $2 $3-$4"
// }
//
// In the format above, the `format` adds `15` to the digits when formatting a number.
// A sidenote: this format actually is suitable because `national_prefix_for_parsing`
// has previously removed `15` from a national number, so re-adding `15` in `format`
// doesn't actually result in any extra digits added to user's input.
// But verifying that would be a complex procedure, so the code chooses a simpler path:
// it simply filters out all `format`s that contain anything but "captured group" ids.
//
// This regular expression is called `ELIGIBLE_FORMAT_PATTERN` in Google's
// `libphonenumber` code.
//


var NON_ALTERING_FORMAT_REG_EXP = new RegExp('^' + '[' + VALID_PUNCTUATION + ']*' + '(\\$\\d[' + VALID_PUNCTUATION + ']*)+' + '$'); // This is the minimum length of the leading digits of a phone number
// to guarantee the first "leading digits pattern" for a phone number format
// to be preemptive.

var MIN_LEADING_DIGITS_LENGTH = 3;

var AsYouTypeFormatter =
/*#__PURE__*/
function () {
  function AsYouTypeFormatter(_ref) {
    var _this = this;

    _ref.state;
        var metadata = _ref.metadata;

    _classCallCheck$4(this, AsYouTypeFormatter);

    _defineProperty$3(this, "getSeparatorAfterNationalPrefix", function (format) {
      // `US` metadata doesn't have a `national_prefix_formatting_rule`,
      // so the `if` condition below doesn't apply to `US`,
      // but in reality there shoudl be a separator
      // between a national prefix and a national (significant) number.
      // So `US` national prefix separator is a "special" "hardcoded" case.
      if (_this.isNANP) {
        return ' ';
      } // If a `format` has a `national_prefix_formatting_rule`
      // and that rule has a separator after a national prefix,
      // then it means that there should be a separator
      // between a national prefix and a national (significant) number.


      if (format && format.nationalPrefixFormattingRule() && NATIONAL_PREFIX_SEPARATORS_PATTERN.test(format.nationalPrefixFormattingRule())) {
        return ' ';
      } // At this point, there seems to be no clear evidence that
      // there should be a separator between a national prefix
      // and a national (significant) number. So don't insert one.


      return '';
    });

    _defineProperty$3(this, "shouldTryNationalPrefixFormattingRule", function (format, _ref2) {
      var international = _ref2.international,
          nationalPrefix = _ref2.nationalPrefix;

      if (format.nationalPrefixFormattingRule()) {
        // In some countries, `national_prefix_formatting_rule` is `($1)`,
        // so it applies even if the user hasn't input a national prefix.
        // `format.usesNationalPrefix()` detects such cases.
        var usesNationalPrefix = format.usesNationalPrefix();

        if (usesNationalPrefix && nationalPrefix || !usesNationalPrefix && !international) {
          return true;
        }
      }
    });

    this.metadata = metadata;
    this.resetFormat();
  }

  _createClass$4(AsYouTypeFormatter, [{
    key: "resetFormat",
    value: function resetFormat() {
      this.chosenFormat = undefined;
      this.template = undefined;
      this.nationalNumberTemplate = undefined;
      this.populatedNationalNumberTemplate = undefined;
      this.populatedNationalNumberTemplatePosition = -1;
    }
  }, {
    key: "reset",
    value: function reset(numberingPlan, state) {
      this.resetFormat();

      if (numberingPlan) {
        this.isNANP = numberingPlan.callingCode() === '1';
        this.matchingFormats = numberingPlan.formats();

        if (state.nationalSignificantNumber) {
          this.narrowDownMatchingFormats(state);
        }
      } else {
        this.isNANP = undefined;
        this.matchingFormats = [];
      }
    }
    /**
     * Formats an updated phone number.
     * @param  {string} nextDigits — Additional phone number digits.
     * @param  {object} state — `AsYouType` state.
     * @return {[string]} Returns undefined if the updated phone number can't be formatted using any of the available formats.
     */

  }, {
    key: "format",
    value: function format(nextDigits, state) {
      var _this2 = this;

      // See if the phone number digits can be formatted as a complete phone number.
      // If not, use the results from `formatNationalNumberWithNextDigits()`,
      // which formats based on the chosen formatting pattern.
      //
      // Attempting to format complete phone number first is how it's done
      // in Google's `libphonenumber`, so this library just follows it.
      // Google's `libphonenumber` code doesn't explain in detail why does it
      // attempt to format digits as a complete phone number
      // instead of just going with a previoulsy (or newly) chosen `format`:
      //
      // "Checks to see if there is an exact pattern match for these digits.
      //  If so, we should use this instead of any other formatting template
      //  whose leadingDigitsPattern also matches the input."
      //
      if (canFormatCompleteNumber(state.nationalSignificantNumber, this.metadata)) {
        for (var _iterator = this.matchingFormats, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
          var _ref3;

          if (_isArray) {
            if (_i >= _iterator.length) break;
            _ref3 = _iterator[_i++];
          } else {
            _i = _iterator.next();
            if (_i.done) break;
            _ref3 = _i.value;
          }

          var format = _ref3;
          var formattedCompleteNumber = formatCompleteNumber(state, format, {
            metadata: this.metadata,
            shouldTryNationalPrefixFormattingRule: function shouldTryNationalPrefixFormattingRule(format) {
              return _this2.shouldTryNationalPrefixFormattingRule(format, {
                international: state.international,
                nationalPrefix: state.nationalPrefix
              });
            },
            getSeparatorAfterNationalPrefix: this.getSeparatorAfterNationalPrefix
          });

          if (formattedCompleteNumber) {
            this.resetFormat();
            this.chosenFormat = format;
            this.setNationalNumberTemplate(formattedCompleteNumber.replace(/\d/g, DIGIT_PLACEHOLDER), state);
            this.populatedNationalNumberTemplate = formattedCompleteNumber; // With a new formatting template, the matched position
            // using the old template needs to be reset.

            this.populatedNationalNumberTemplatePosition = this.template.lastIndexOf(DIGIT_PLACEHOLDER);
            return formattedCompleteNumber;
          }
        }
      } // Format the digits as a partial (incomplete) phone number
      // using the previously chosen formatting pattern (or a newly chosen one).


      return this.formatNationalNumberWithNextDigits(nextDigits, state);
    } // Formats the next phone number digits.

  }, {
    key: "formatNationalNumberWithNextDigits",
    value: function formatNationalNumberWithNextDigits(nextDigits, state) {
      var previouslyChosenFormat = this.chosenFormat; // Choose a format from the list of matching ones.

      var newlyChosenFormat = this.chooseFormat(state);

      if (newlyChosenFormat) {
        if (newlyChosenFormat === previouslyChosenFormat) {
          // If it can format the next (current) digits
          // using the previously chosen phone number format
          // then return the updated formatted number.
          return this.formatNextNationalNumberDigits(nextDigits);
        } else {
          // If a more appropriate phone number format
          // has been chosen for these "leading digits",
          // then re-format the national phone number part
          // using the newly selected format.
          return this.formatNextNationalNumberDigits(state.getNationalDigits());
        }
      }
    }
  }, {
    key: "narrowDownMatchingFormats",
    value: function narrowDownMatchingFormats(_ref4) {
      var _this3 = this;

      var nationalSignificantNumber = _ref4.nationalSignificantNumber,
          nationalPrefix = _ref4.nationalPrefix,
          international = _ref4.international;
      var leadingDigits = nationalSignificantNumber; // "leading digits" pattern list starts with a
      // "leading digits" pattern fitting a maximum of 3 leading digits.
      // So, after a user inputs 3 digits of a national (significant) phone number
      // this national (significant) number can already be formatted.
      // The next "leading digits" pattern is for 4 leading digits max,
      // and the "leading digits" pattern after it is for 5 leading digits max, etc.
      // This implementation is different from Google's
      // in that it searches for a fitting format
      // even if the user has entered less than
      // `MIN_LEADING_DIGITS_LENGTH` digits of a national number.
      // Because some leading digit patterns already match for a single first digit.

      var leadingDigitsPatternIndex = leadingDigits.length - MIN_LEADING_DIGITS_LENGTH;

      if (leadingDigitsPatternIndex < 0) {
        leadingDigitsPatternIndex = 0;
      }

      this.matchingFormats = this.matchingFormats.filter(function (format) {
        return _this3.formatSuits(format, international, nationalPrefix) && _this3.formatMatches(format, leadingDigits, leadingDigitsPatternIndex);
      }); // If there was a phone number format chosen
      // and it no longer holds given the new leading digits then reset it.
      // The test for this `if` condition is marked as:
      // "Reset a chosen format when it no longer holds given the new leading digits".
      // To construct a valid test case for this one can find a country
      // in `PhoneNumberMetadata.xml` yielding one format for 3 `<leadingDigits>`
      // and yielding another format for 4 `<leadingDigits>` (Australia in this case).

      if (this.chosenFormat && this.matchingFormats.indexOf(this.chosenFormat) === -1) {
        this.resetFormat();
      }
    }
  }, {
    key: "formatSuits",
    value: function formatSuits(format, international, nationalPrefix) {
      // When a prefix before a national (significant) number is
      // simply a national prefix, then it's parsed as `this.nationalPrefix`.
      // In more complex cases, a prefix before national (significant) number
      // could include a national prefix as well as some "capturing groups",
      // and in that case there's no info whether a national prefix has been parsed.
      // If national prefix is not used when formatting a phone number
      // using this format, but a national prefix has been entered by the user,
      // and was extracted, then discard such phone number format.
      // In Google's "AsYouType" formatter code, the equivalent would be this part:
      // https://github.com/google/libphonenumber/blob/0a45cfd96e71cad8edb0e162a70fcc8bd9728933/java/libphonenumber/src/com/google/i18n/phonenumbers/AsYouTypeFormatter.java#L175-L184
      if (nationalPrefix && !format.usesNationalPrefix() && // !format.domesticCarrierCodeFormattingRule() &&
      !format.nationalPrefixIsOptionalWhenFormattingInNationalFormat()) {
        return false;
      } // If national prefix is mandatory for this phone number format
      // and there're no guarantees that a national prefix is present in user input
      // then discard this phone number format as not suitable.
      // In Google's "AsYouType" formatter code, the equivalent would be this part:
      // https://github.com/google/libphonenumber/blob/0a45cfd96e71cad8edb0e162a70fcc8bd9728933/java/libphonenumber/src/com/google/i18n/phonenumbers/AsYouTypeFormatter.java#L185-L193


      if (!international && !nationalPrefix && format.nationalPrefixIsMandatoryWhenFormattingInNationalFormat()) {
        return false;
      }

      return true;
    }
  }, {
    key: "formatMatches",
    value: function formatMatches(format, leadingDigits, leadingDigitsPatternIndex) {
      var leadingDigitsPatternsCount = format.leadingDigitsPatterns().length; // If this format is not restricted to a certain
      // leading digits pattern then it fits.

      if (leadingDigitsPatternsCount === 0) {
        return true;
      } // Start narrowing down the list of possible formats based on the leading digits.
      // (only previously matched formats take part in the narrowing down process)
      // `leading_digits_patterns` start with 3 digits min
      // and then go up from there one digit at a time.


      leadingDigitsPatternIndex = Math.min(leadingDigitsPatternIndex, leadingDigitsPatternsCount - 1);
      var leadingDigitsPattern = format.leadingDigitsPatterns()[leadingDigitsPatternIndex]; // Google imposes a requirement on the leading digits
      // to be minimum 3 digits long in order to be eligible
      // for checking those with a leading digits pattern.
      //
      // Since `leading_digits_patterns` start with 3 digits min,
      // Google's original `libphonenumber` library only starts
      // excluding any non-matching formats only when the
      // national number entered so far is at least 3 digits long,
      // otherwise format matching would give false negatives.
      //
      // For example, when the digits entered so far are `2`
      // and the leading digits pattern is `21` –
      // it's quite obvious in this case that the format could be the one
      // but due to the absence of further digits it would give false negative.
      //
      // Also, `leading_digits_patterns` doesn't always correspond to a single
      // digits count. For example, `60|8` pattern would already match `8`
      // but the `60` part would require having at least two leading digits,
      // so the whole pattern would require inputting two digits first in order to
      // decide on whether it matches the input, even when the input is "80".
      //
      // This library — `libphonenumber-js` — allows filtering by `leading_digits_patterns`
      // even when there's only 1 or 2 digits of the national (significant) number.
      // To do that, it uses a non-strict pattern matcher written specifically for that.
      //

      if (leadingDigits.length < MIN_LEADING_DIGITS_LENGTH) {
        // Before leading digits < 3 matching was implemented:
        // return true
        //
        // After leading digits < 3 matching was implemented:
        try {
          return new PatternMatcher(leadingDigitsPattern).match(leadingDigits, {
            allowOverflow: true
          }) !== undefined;
        } catch (error) {
          // There's a slight possibility that there could be some undiscovered bug
          // in the pattern matcher code. Since the "leading digits < 3 matching"
          // feature is not "essential" for operation, it can fall back to the old way
          // in case of any issues rather than halting the application's execution.
          console.error(error);
          return true;
        }
      } // If at least `MIN_LEADING_DIGITS_LENGTH` digits of a national number are
      // available then use the usual regular expression matching.
      //
      // The whole pattern is wrapped in round brackets (`()`) because
      // the pattern can use "or" operator (`|`) at the top level of the pattern.
      //


      return new RegExp("^(".concat(leadingDigitsPattern, ")")).test(leadingDigits);
    }
  }, {
    key: "getFormatFormat",
    value: function getFormatFormat(format, international) {
      return international ? format.internationalFormat() : format.format();
    }
  }, {
    key: "chooseFormat",
    value: function chooseFormat(state) {
      var _this4 = this;

      var _loop2 = function _loop2() {
        if (_isArray2) {
          if (_i2 >= _iterator2.length) return "break";
          _ref5 = _iterator2[_i2++];
        } else {
          _i2 = _iterator2.next();
          if (_i2.done) return "break";
          _ref5 = _i2.value;
        }

        var format = _ref5;

        // If this format is currently being used
        // and is still suitable, then stick to it.
        if (_this4.chosenFormat === format) {
          return "break";
        } // Sometimes, a formatting rule inserts additional digits in a phone number,
        // and "as you type" formatter can't do that: it should only use the digits
        // that the user has input.
        //
        // For example, in Argentina, there's a format for mobile phone numbers:
        //
        // {
        //    "pattern": "(\\d)(\\d{2})(\\d{4})(\\d{4})",
        //    "leading_digits_patterns": ["91"],
        //    "national_prefix_formatting_rule": "0$1",
        //    "format": "$2 15-$3-$4",
        //    "international_format": "$1 $2 $3-$4"
        // }
        //
        // In that format, `international_format` is used instead of `format`
        // because `format` inserts `15` in the formatted number,
        // and `AsYouType` formatter should only use the digits
        // the user has actually input, without adding any extra digits.
        // In this case, it wouldn't make a difference, because the `15`
        // is first stripped when applying `national_prefix_for_parsing`
        // and then re-added when using `format`, so in reality it doesn't
        // add any new digits to the number, but to detect that, the code
        // would have to be more complex: it would have to try formatting
        // the digits using the format and then see if any digits have
        // actually been added or removed, and then, every time a new digit
        // is input, it should re-check whether the chosen format doesn't
        // alter the digits.
        //
        // Google's code doesn't go that far, and so does this library:
        // it simply requires that a `format` doesn't add any additonal
        // digits to user's input.
        //
        // Also, people in general should move from inputting phone numbers
        // in national format (possibly with national prefixes)
        // and use international phone number format instead:
        // it's a logical thing in the modern age of mobile phones,
        // globalization and the internet.
        //

        /* istanbul ignore if */


        if (!NON_ALTERING_FORMAT_REG_EXP.test(_this4.getFormatFormat(format, state.international))) {
          return "continue";
        }

        if (!_this4.createTemplateForFormat(format, state)) {
          // Remove the format if it can't generate a template.
          _this4.matchingFormats = _this4.matchingFormats.filter(function (_) {
            return _ !== format;
          });
          return "continue";
        }

        _this4.chosenFormat = format;
        return "break";
      };

      // When there are multiple available formats, the formatter uses the first
      // format where a formatting template could be created.
      _loop: for (var _iterator2 = this.matchingFormats.slice(), _isArray2 = Array.isArray(_iterator2), _i2 = 0, _iterator2 = _isArray2 ? _iterator2 : _iterator2[Symbol.iterator]();;) {
        var _ref5;

        var _ret = _loop2();

        switch (_ret) {
          case "break":
            break _loop;

          case "continue":
            continue;
        }
      }

      if (!this.chosenFormat) {
        // No format matches the national (significant) phone number.
        this.resetFormat();
      }

      return this.chosenFormat;
    }
  }, {
    key: "createTemplateForFormat",
    value: function createTemplateForFormat(format, state) {
      // The formatter doesn't format numbers when numberPattern contains '|', e.g.
      // (20|3)\d{4}. In those cases we quickly return.
      // (Though there's no such format in current metadata)

      /* istanbul ignore if */
      if (format.pattern().indexOf('|') >= 0) {
        return;
      } // Get formatting template for this phone number format


      var template = this.getTemplateForFormat(format, state); // If the national number entered is too long
      // for any phone number format, then abort.

      if (template) {
        this.setNationalNumberTemplate(template, state);
        return true;
      }
    }
  }, {
    key: "getInternationalPrefixBeforeCountryCallingCode",
    value: function getInternationalPrefixBeforeCountryCallingCode(_ref6, options) {
      var IDDPrefix = _ref6.IDDPrefix,
          missingPlus = _ref6.missingPlus;

      if (IDDPrefix) {
        return options && options.spacing === false ? IDDPrefix : IDDPrefix + ' ';
      }

      if (missingPlus) {
        return '';
      }

      return '+';
    }
  }, {
    key: "getTemplate",
    value: function getTemplate(state) {
      if (!this.template) {
        return;
      } // `this.template` holds the template for a "complete" phone number.
      // The currently entered phone number is most likely not "complete",
      // so trim all non-populated digits.


      var index = -1;
      var i = 0;
      var internationalPrefix = state.international ? this.getInternationalPrefixBeforeCountryCallingCode(state, {
        spacing: false
      }) : '';

      while (i < internationalPrefix.length + state.getDigitsWithoutInternationalPrefix().length) {
        index = this.template.indexOf(DIGIT_PLACEHOLDER, index + 1);
        i++;
      }

      return cutAndStripNonPairedParens(this.template, index + 1);
    }
  }, {
    key: "setNationalNumberTemplate",
    value: function setNationalNumberTemplate(template, state) {
      this.nationalNumberTemplate = template;
      this.populatedNationalNumberTemplate = template; // With a new formatting template, the matched position
      // using the old template needs to be reset.

      this.populatedNationalNumberTemplatePosition = -1; // For convenience, the public `.template` property
      // contains the whole international number
      // if the phone number being input is international:
      // 'x' for the '+' sign, 'x'es for the country phone code,
      // a spacebar and then the template for the formatted national number.

      if (state.international) {
        this.template = this.getInternationalPrefixBeforeCountryCallingCode(state).replace(/[\d\+]/g, DIGIT_PLACEHOLDER) + repeat(DIGIT_PLACEHOLDER, state.callingCode.length) + ' ' + template;
      } else {
        this.template = template;
      }
    }
    /**
     * Generates formatting template for a national phone number,
     * optionally containing a national prefix, for a format.
     * @param  {Format} format
     * @param  {string} nationalPrefix
     * @return {string}
     */

  }, {
    key: "getTemplateForFormat",
    value: function getTemplateForFormat(format, _ref7) {
      var nationalSignificantNumber = _ref7.nationalSignificantNumber,
          international = _ref7.international,
          nationalPrefix = _ref7.nationalPrefix,
          complexPrefixBeforeNationalSignificantNumber = _ref7.complexPrefixBeforeNationalSignificantNumber;
      var pattern = format.pattern();
      /* istanbul ignore else */

      {
        pattern = pattern // Replace anything in the form of [..] with \d
        .replace(CREATE_CHARACTER_CLASS_PATTERN(), '\\d') // Replace any standalone digit (not the one in `{}`) with \d
        .replace(CREATE_STANDALONE_DIGIT_PATTERN(), '\\d');
      } // Generate a dummy national number (consisting of `9`s)
      // that fits this format's `pattern`.
      //
      // This match will always succeed,
      // because the "longest dummy phone number"
      // has enough length to accomodate any possible
      // national phone number format pattern.
      //


      var digits = LONGEST_DUMMY_PHONE_NUMBER.match(pattern)[0]; // If the national number entered is too long
      // for any phone number format, then abort.

      if (nationalSignificantNumber.length > digits.length) {
        return;
      } // Get a formatting template which can be used to efficiently format
      // a partial number where digits are added one by one.
      // Below `strictPattern` is used for the
      // regular expression (with `^` and `$`).
      // This wasn't originally in Google's `libphonenumber`
      // and I guess they don't really need it
      // because they're not using "templates" to format phone numbers
      // but I added `strictPattern` after encountering
      // South Korean phone number formatting bug.
      //
      // Non-strict regular expression bug demonstration:
      //
      // this.nationalSignificantNumber : `111111111` (9 digits)
      //
      // pattern : (\d{2})(\d{3,4})(\d{4})
      // format : `$1 $2 $3`
      // digits : `9999999999` (10 digits)
      //
      // '9999999999'.replace(new RegExp(/(\d{2})(\d{3,4})(\d{4})/g), '$1 $2 $3') = "99 9999 9999"
      //
      // template : xx xxxx xxxx
      //
      // But the correct template in this case is `xx xxx xxxx`.
      // The template was generated incorrectly because of the
      // `{3,4}` variability in the `pattern`.
      //
      // The fix is, if `this.nationalSignificantNumber` has already sufficient length
      // to satisfy the `pattern` completely then `this.nationalSignificantNumber`
      // is used instead of `digits`.


      var strictPattern = new RegExp('^' + pattern + '$');
      var nationalNumberDummyDigits = nationalSignificantNumber.replace(/\d/g, DUMMY_DIGIT); // If `this.nationalSignificantNumber` has already sufficient length
      // to satisfy the `pattern` completely then use it
      // instead of `digits`.

      if (strictPattern.test(nationalNumberDummyDigits)) {
        digits = nationalNumberDummyDigits;
      }

      var numberFormat = this.getFormatFormat(format, international);
      var nationalPrefixIncludedInTemplate; // If a user did input a national prefix (and that's guaranteed),
      // and if a `format` does have a national prefix formatting rule,
      // then see if that national prefix formatting rule
      // prepends exactly the same national prefix the user has input.
      // If that's the case, then use the `format` with the national prefix formatting rule.
      // Otherwise, use  the `format` without the national prefix formatting rule,
      // and prepend a national prefix manually to it.

      if (this.shouldTryNationalPrefixFormattingRule(format, {
        international: international,
        nationalPrefix: nationalPrefix
      })) {
        var numberFormatWithNationalPrefix = numberFormat.replace(FIRST_GROUP_PATTERN, format.nationalPrefixFormattingRule()); // If `national_prefix_formatting_rule` of a `format` simply prepends
        // national prefix at the start of a national (significant) number,
        // then such formatting can be used with `AsYouType` formatter.
        // There seems to be no `else` case: everywhere in metadata,
        // national prefix formatting rule is national prefix + $1,
        // or `($1)`, in which case such format isn't even considered
        // when the user has input a national prefix.

        /* istanbul ignore else */

        if (parseDigits(format.nationalPrefixFormattingRule()) === (nationalPrefix || '') + parseDigits('$1')) {
          numberFormat = numberFormatWithNationalPrefix;
          nationalPrefixIncludedInTemplate = true; // Replace all digits of the national prefix in the formatting template
          // with `DIGIT_PLACEHOLDER`s.

          if (nationalPrefix) {
            var i = nationalPrefix.length;

            while (i > 0) {
              numberFormat = numberFormat.replace(/\d/, DIGIT_PLACEHOLDER);
              i--;
            }
          }
        }
      } // Generate formatting template for this phone number format.


      var template = digits // Format the dummy phone number according to the format.
      .replace(new RegExp(pattern), numberFormat) // Replace each dummy digit with a DIGIT_PLACEHOLDER.
      .replace(new RegExp(DUMMY_DIGIT, 'g'), DIGIT_PLACEHOLDER); // If a prefix of a national (significant) number is not as simple
      // as just a basic national prefix, then just prepend such prefix
      // before the national (significant) number, optionally spacing
      // the two with a whitespace.

      if (!nationalPrefixIncludedInTemplate) {
        if (complexPrefixBeforeNationalSignificantNumber) {
          // Prepend the prefix to the template manually.
          template = repeat(DIGIT_PLACEHOLDER, complexPrefixBeforeNationalSignificantNumber.length) + ' ' + template;
        } else if (nationalPrefix) {
          // Prepend national prefix to the template manually.
          template = repeat(DIGIT_PLACEHOLDER, nationalPrefix.length) + this.getSeparatorAfterNationalPrefix(format) + template;
        }
      }

      if (international) {
        template = applyInternationalSeparatorStyle(template);
      }

      return template;
    }
  }, {
    key: "formatNextNationalNumberDigits",
    value: function formatNextNationalNumberDigits(digits) {
      var result = populateTemplateWithDigits(this.populatedNationalNumberTemplate, this.populatedNationalNumberTemplatePosition, digits);

      if (!result) {
        // Reset the format.
        this.resetFormat();
        return;
      }

      this.populatedNationalNumberTemplate = result[0];
      this.populatedNationalNumberTemplatePosition = result[1]; // Return the formatted phone number so far.

      return cutAndStripNonPairedParens(this.populatedNationalNumberTemplate, this.populatedNationalNumberTemplatePosition + 1); // The old way which was good for `input-format` but is not so good
      // for `react-phone-number-input`'s default input (`InputBasic`).
      // return closeNonPairedParens(this.populatedNationalNumberTemplate, this.populatedNationalNumberTemplatePosition + 1)
      // 	.replace(new RegExp(DIGIT_PLACEHOLDER, 'g'), ' ')
    }
  }]);

  return AsYouTypeFormatter;
}();

function _slicedToArray$1(arr, i) { return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _nonIterableRest$1(); }

function _nonIterableRest$1() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit$1(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles$1(arr) { if (Array.isArray(arr)) return arr; }

function _classCallCheck$3(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$3(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$3(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$3(Constructor.prototype, protoProps); if (staticProps) _defineProperties$3(Constructor, staticProps); return Constructor; }
var VALID_FORMATTED_PHONE_NUMBER_DIGITS_PART = '[' + VALID_PUNCTUATION + VALID_DIGITS + ']+';
var VALID_FORMATTED_PHONE_NUMBER_DIGITS_PART_PATTERN = new RegExp('^' + VALID_FORMATTED_PHONE_NUMBER_DIGITS_PART + '$', 'i');
var VALID_FORMATTED_PHONE_NUMBER_PART = '(?:' + '[' + PLUS_CHARS + ']' + '[' + VALID_PUNCTUATION + VALID_DIGITS + ']*' + '|' + '[' + VALID_PUNCTUATION + VALID_DIGITS + ']+' + ')';
var AFTER_PHONE_NUMBER_DIGITS_END_PATTERN = new RegExp('[^' + VALID_PUNCTUATION + VALID_DIGITS + ']+' + '.*' + '$'); // Tests whether `national_prefix_for_parsing` could match
// different national prefixes.
// Matches anything that's not a digit or a square bracket.

var COMPLEX_NATIONAL_PREFIX = /[^\d\[\]]/;

var AsYouTypeParser =
/*#__PURE__*/
function () {
  function AsYouTypeParser(_ref) {
    var defaultCountry = _ref.defaultCountry,
        defaultCallingCode = _ref.defaultCallingCode,
        metadata = _ref.metadata,
        onNationalSignificantNumberChange = _ref.onNationalSignificantNumberChange;

    _classCallCheck$3(this, AsYouTypeParser);

    this.defaultCountry = defaultCountry;
    this.defaultCallingCode = defaultCallingCode;
    this.metadata = metadata;
    this.onNationalSignificantNumberChange = onNationalSignificantNumberChange;
  }

  _createClass$3(AsYouTypeParser, [{
    key: "input",
    value: function input(text, state) {
      var _extractFormattedDigi = extractFormattedDigitsAndPlus(text),
          _extractFormattedDigi2 = _slicedToArray$1(_extractFormattedDigi, 2),
          formattedDigits = _extractFormattedDigi2[0],
          hasPlus = _extractFormattedDigi2[1];

      var digits = parseDigits(formattedDigits); // Checks for a special case: just a leading `+` has been entered.

      var justLeadingPlus;

      if (hasPlus) {
        if (!state.digits) {
          state.startInternationalNumber();

          if (!digits) {
            justLeadingPlus = true;
          }
        }
      }

      if (digits) {
        this.inputDigits(digits, state);
      }

      return {
        digits: digits,
        justLeadingPlus: justLeadingPlus
      };
    }
    /**
     * Inputs "next" phone number digits.
     * @param  {string} digits
     * @return {string} [formattedNumber] Formatted national phone number (if it can be formatted at this stage). Returning `undefined` means "don't format the national phone number at this stage".
     */

  }, {
    key: "inputDigits",
    value: function inputDigits(nextDigits, state) {
      var digits = state.digits;
      var hasReceivedThreeLeadingDigits = digits.length < 3 && digits.length + nextDigits.length >= 3; // Append phone number digits.

      state.appendDigits(nextDigits); // Attempt to extract IDD prefix:
      // Some users input their phone number in international format,
      // but in an "out-of-country" dialing format instead of using the leading `+`.
      // https://github.com/catamphetamine/libphonenumber-js/issues/185
      // Detect such numbers as soon as there're at least 3 digits.
      // Google's library attempts to extract IDD prefix at 3 digits,
      // so this library just copies that behavior.
      // I guess that's because the most commot IDD prefixes are
      // `00` (Europe) and `011` (US).
      // There exist really long IDD prefixes too:
      // for example, in Australia the default IDD prefix is `0011`,
      // and it could even be as long as `14880011`.
      // An IDD prefix is extracted here, and then every time when
      // there's a new digit and the number couldn't be formatted.

      if (hasReceivedThreeLeadingDigits) {
        this.extractIddPrefix(state);
      }

      if (this.isWaitingForCountryCallingCode(state)) {
        if (!this.extractCountryCallingCode(state)) {
          return;
        }
      } else {
        state.appendNationalSignificantNumberDigits(nextDigits);
      } // If a phone number is being input in international format,
      // then it's not valid for it to have a national prefix.
      // Still, some people incorrectly input such numbers with a national prefix.
      // In such cases, only attempt to strip a national prefix if the number becomes too long.
      // (but that is done later, not here)


      if (!state.international) {
        if (!this.hasExtractedNationalSignificantNumber) {
          this.extractNationalSignificantNumber(state.getNationalDigits(), state.update);
        }
      }
    }
  }, {
    key: "isWaitingForCountryCallingCode",
    value: function isWaitingForCountryCallingCode(_ref2) {
      var international = _ref2.international,
          callingCode = _ref2.callingCode;
      return international && !callingCode;
    } // Extracts a country calling code from a number
    // being entered in internatonal format.

  }, {
    key: "extractCountryCallingCode",
    value: function extractCountryCallingCode$1(state) {
      var _extractCountryCallin = extractCountryCallingCode('+' + state.getDigitsWithoutInternationalPrefix(), this.defaultCountry, this.defaultCallingCode, this.metadata.metadata),
          countryCallingCode = _extractCountryCallin.countryCallingCode,
          number = _extractCountryCallin.number;

      if (countryCallingCode) {
        state.setCallingCode(countryCallingCode);
        state.update({
          nationalSignificantNumber: number
        });
        return true;
      }
    }
  }, {
    key: "reset",
    value: function reset(numberingPlan) {
      if (numberingPlan) {
        this.hasSelectedNumberingPlan = true;

        var nationalPrefixForParsing = numberingPlan._nationalPrefixForParsing();

        this.couldPossiblyExtractAnotherNationalSignificantNumber = nationalPrefixForParsing && COMPLEX_NATIONAL_PREFIX.test(nationalPrefixForParsing);
      } else {
        this.hasSelectedNumberingPlan = undefined;
        this.couldPossiblyExtractAnotherNationalSignificantNumber = undefined;
      }
    }
    /**
     * Extracts a national (significant) number from user input.
     * Google's library is different in that it only applies `national_prefix_for_parsing`
     * and doesn't apply `national_prefix_transform_rule` after that.
     * https://github.com/google/libphonenumber/blob/a3d70b0487875475e6ad659af404943211d26456/java/libphonenumber/src/com/google/i18n/phonenumbers/AsYouTypeFormatter.java#L539
     * @return {boolean} [extracted]
     */

  }, {
    key: "extractNationalSignificantNumber",
    value: function extractNationalSignificantNumber(nationalDigits, setState) {
      if (!this.hasSelectedNumberingPlan) {
        return;
      }

      var _extractNationalNumbe = extractNationalNumberFromPossiblyIncompleteNumber(nationalDigits, this.metadata),
          nationalPrefix = _extractNationalNumbe.nationalPrefix,
          nationalNumber = _extractNationalNumbe.nationalNumber,
          carrierCode = _extractNationalNumbe.carrierCode;

      if (nationalNumber === nationalDigits) {
        return;
      }

      this.onExtractedNationalNumber(nationalPrefix, carrierCode, nationalNumber, nationalDigits, setState);
      return true;
    }
    /**
     * In Google's code this function is called "attempt to extract longer NDD".
     * "Some national prefixes are a substring of others", they say.
     * @return {boolean} [result] — Returns `true` if extracting a national prefix produced different results from what they were.
     */

  }, {
    key: "extractAnotherNationalSignificantNumber",
    value: function extractAnotherNationalSignificantNumber(nationalDigits, prevNationalSignificantNumber, setState) {
      if (!this.hasExtractedNationalSignificantNumber) {
        return this.extractNationalSignificantNumber(nationalDigits, setState);
      }

      if (!this.couldPossiblyExtractAnotherNationalSignificantNumber) {
        return;
      }

      var _extractNationalNumbe2 = extractNationalNumberFromPossiblyIncompleteNumber(nationalDigits, this.metadata),
          nationalPrefix = _extractNationalNumbe2.nationalPrefix,
          nationalNumber = _extractNationalNumbe2.nationalNumber,
          carrierCode = _extractNationalNumbe2.carrierCode; // If a national prefix has been extracted previously,
      // then it's always extracted as additional digits are added.
      // That's assuming `extractNationalNumberFromPossiblyIncompleteNumber()`
      // doesn't do anything different from what it currently does.
      // So, just in case, here's this check, though it doesn't occur.

      /* istanbul ignore if */


      if (nationalNumber === prevNationalSignificantNumber) {
        return;
      }

      this.onExtractedNationalNumber(nationalPrefix, carrierCode, nationalNumber, nationalDigits, setState);
      return true;
    }
  }, {
    key: "onExtractedNationalNumber",
    value: function onExtractedNationalNumber(nationalPrefix, carrierCode, nationalSignificantNumber, nationalDigits, setState) {
      var complexPrefixBeforeNationalSignificantNumber;
      var nationalSignificantNumberMatchesInput; // This check also works with empty `this.nationalSignificantNumber`.

      var nationalSignificantNumberIndex = nationalDigits.lastIndexOf(nationalSignificantNumber); // If the extracted national (significant) number is the
      // last substring of the `digits`, then it means that it hasn't been altered:
      // no digits have been removed from the national (significant) number
      // while applying `national_prefix_transform_rule`.
      // https://gitlab.com/catamphetamine/libphonenumber-js/-/blob/master/METADATA.md#national_prefix_for_parsing--national_prefix_transform_rule

      if (nationalSignificantNumberIndex >= 0 && nationalSignificantNumberIndex === nationalDigits.length - nationalSignificantNumber.length) {
        nationalSignificantNumberMatchesInput = true; // If a prefix of a national (significant) number is not as simple
        // as just a basic national prefix, then such prefix is stored in
        // `this.complexPrefixBeforeNationalSignificantNumber` property and will be
        // prepended "as is" to the national (significant) number to produce
        // a formatted result.

        var prefixBeforeNationalNumber = nationalDigits.slice(0, nationalSignificantNumberIndex); // `prefixBeforeNationalNumber` is always non-empty,
        // because `onExtractedNationalNumber()` isn't called
        // when a national (significant) number hasn't been actually "extracted":
        // when a national (significant) number is equal to the national part of `digits`,
        // then `onExtractedNationalNumber()` doesn't get called.

        if (prefixBeforeNationalNumber !== nationalPrefix) {
          complexPrefixBeforeNationalSignificantNumber = prefixBeforeNationalNumber;
        }
      }

      setState({
        nationalPrefix: nationalPrefix,
        carrierCode: carrierCode,
        nationalSignificantNumber: nationalSignificantNumber,
        nationalSignificantNumberMatchesInput: nationalSignificantNumberMatchesInput,
        complexPrefixBeforeNationalSignificantNumber: complexPrefixBeforeNationalSignificantNumber
      }); // `onExtractedNationalNumber()` is only called when
      // the national (significant) number actually did change.

      this.hasExtractedNationalSignificantNumber = true;
      this.onNationalSignificantNumberChange();
    }
  }, {
    key: "reExtractNationalSignificantNumber",
    value: function reExtractNationalSignificantNumber(state) {
      // Attempt to extract a national prefix.
      //
      // Some people incorrectly input national prefix
      // in an international phone number.
      // For example, some people write British phone numbers as `+44(0)...`.
      //
      // Also, in some rare cases, it is valid for a national prefix
      // to be a part of an international phone number.
      // For example, mobile phone numbers in Mexico are supposed to be
      // dialled internationally using a `1` national prefix,
      // so the national prefix will be part of an international number.
      //
      // Quote from:
      // https://www.mexperience.com/dialing-cell-phones-in-mexico/
      //
      // "Dialing a Mexican cell phone from abroad
      // When you are calling a cell phone number in Mexico from outside Mexico,
      // it’s necessary to dial an additional “1” after Mexico’s country code
      // (which is “52”) and before the area code.
      // You also ignore the 045, and simply dial the area code and the
      // cell phone’s number.
      //
      // If you don’t add the “1”, you’ll receive a recorded announcement
      // asking you to redial using it.
      //
      // For example, if you are calling from the USA to a cell phone
      // in Mexico City, you would dial +52 – 1 – 55 – 1234 5678.
      // (Note that this is different to calling a land line in Mexico City
      // from abroad, where the number dialed would be +52 – 55 – 1234 5678)".
      //
      // Google's demo output:
      // https://libphonenumber.appspot.com/phonenumberparser?number=%2b5215512345678&country=MX
      //
      if (this.extractAnotherNationalSignificantNumber(state.getNationalDigits(), state.nationalSignificantNumber, state.update)) {
        return true;
      } // If no format matches the phone number, then it could be
      // "a really long IDD" (quote from a comment in Google's library).
      // An IDD prefix is first extracted when the user has entered at least 3 digits,
      // and then here — every time when there's a new digit and the number
      // couldn't be formatted.
      // For example, in Australia the default IDD prefix is `0011`,
      // and it could even be as long as `14880011`.
      //
      // Could also check `!hasReceivedThreeLeadingDigits` here
      // to filter out the case when this check duplicates the one
      // already performed when there're 3 leading digits,
      // but it's not a big deal, and in most cases there
      // will be a suitable `format` when there're 3 leading digits.
      //


      if (this.extractIddPrefix(state)) {
        this.extractCallingCodeAndNationalSignificantNumber(state);
        return true;
      } // Google's AsYouType formatter supports sort of an "autocorrection" feature
      // when it "autocorrects" numbers that have been input for a country
      // with that country's calling code.
      // Such "autocorrection" feature looks weird, but different people have been requesting it:
      // https://github.com/catamphetamine/libphonenumber-js/issues/376
      // https://github.com/catamphetamine/libphonenumber-js/issues/375
      // https://github.com/catamphetamine/libphonenumber-js/issues/316


      if (this.fixMissingPlus(state)) {
        this.extractCallingCodeAndNationalSignificantNumber(state);
        return true;
      }
    }
  }, {
    key: "extractIddPrefix",
    value: function extractIddPrefix(state) {
      // An IDD prefix can't be present in a number written with a `+`.
      // Also, don't re-extract an IDD prefix if has already been extracted.
      var international = state.international,
          IDDPrefix = state.IDDPrefix,
          digits = state.digits;
          state.nationalSignificantNumber;

      if (international || IDDPrefix) {
        return;
      } // Some users input their phone number in "out-of-country"
      // dialing format instead of using the leading `+`.
      // https://github.com/catamphetamine/libphonenumber-js/issues/185
      // Detect such numbers.


      var numberWithoutIDD = stripIddPrefix(digits, this.defaultCountry, this.defaultCallingCode, this.metadata.metadata);

      if (numberWithoutIDD !== undefined && numberWithoutIDD !== digits) {
        // If an IDD prefix was stripped then convert the IDD-prefixed number
        // to international number for subsequent parsing.
        state.update({
          IDDPrefix: digits.slice(0, digits.length - numberWithoutIDD.length)
        });
        this.startInternationalNumber(state);
        return true;
      }
    }
  }, {
    key: "fixMissingPlus",
    value: function fixMissingPlus(state) {
      if (!state.international) {
        var _extractCountryCallin2 = extractCountryCallingCodeFromInternationalNumberWithoutPlusSign(state.digits, this.defaultCountry, this.defaultCallingCode, this.metadata.metadata),
            newCallingCode = _extractCountryCallin2.countryCallingCode;
            _extractCountryCallin2.number;

        if (newCallingCode) {
          state.update({
            missingPlus: true
          });
          this.startInternationalNumber(state);
          return true;
        }
      }
    }
  }, {
    key: "startInternationalNumber",
    value: function startInternationalNumber(state) {
      state.startInternationalNumber(); // If a national (significant) number has been extracted before, reset it.

      if (state.nationalSignificantNumber) {
        state.resetNationalSignificantNumber();
        this.onNationalSignificantNumberChange();
        this.hasExtractedNationalSignificantNumber = undefined;
      }
    }
  }, {
    key: "extractCallingCodeAndNationalSignificantNumber",
    value: function extractCallingCodeAndNationalSignificantNumber(state) {
      if (this.extractCountryCallingCode(state)) {
        // `this.extractCallingCode()` is currently called when the number
        // couldn't be formatted during the standard procedure.
        // Normally, the national prefix would be re-extracted
        // for an international number if such number couldn't be formatted,
        // but since it's already not able to be formatted,
        // there won't be yet another retry, so also extract national prefix here.
        this.extractNationalSignificantNumber(state.getNationalDigits(), state.update);
      }
    }
  }]);

  return AsYouTypeParser;
}();

function extractFormattedPhoneNumber(text) {
  // Attempt to extract a possible number from the string passed in.
  var startsAt = text.search(VALID_FORMATTED_PHONE_NUMBER_PART);

  if (startsAt < 0) {
    return;
  } // Trim everything to the left of the phone number.


  text = text.slice(startsAt); // Trim the `+`.

  var hasPlus;

  if (text[0] === '+') {
    hasPlus = true;
    text = text.slice('+'.length);
  } // Trim everything to the right of the phone number.


  text = text.replace(AFTER_PHONE_NUMBER_DIGITS_END_PATTERN, ''); // Re-add the previously trimmed `+`.

  if (hasPlus) {
    text = '+' + text;
  }

  return text;
}
/**
 * Extracts formatted phone number digits (and a `+`) from text (if there're any).
 * @param  {string} text
 * @return {any[]}
 */


function _extractFormattedDigitsAndPlus(text) {
  // Extract a formatted phone number part from text.
  var extractedNumber = extractFormattedPhoneNumber(text) || ''; // Trim a `+`.

  if (extractedNumber[0] === '+') {
    return [extractedNumber.slice('+'.length), true];
  }

  return [extractedNumber];
}
/**
 * Extracts formatted phone number digits (and a `+`) from text (if there're any).
 * @param  {string} text
 * @return {any[]}
 */


function extractFormattedDigitsAndPlus(text) {
  var _extractFormattedDigi3 = _extractFormattedDigitsAndPlus(text),
      _extractFormattedDigi4 = _slicedToArray$1(_extractFormattedDigi3, 2),
      formattedDigits = _extractFormattedDigi4[0],
      hasPlus = _extractFormattedDigi4[1]; // If the extracted phone number part
  // can possibly be a part of some valid phone number
  // then parse phone number characters from a formatted phone number.


  if (!VALID_FORMATTED_PHONE_NUMBER_DIGITS_PART_PATTERN.test(formattedDigits)) {
    formattedDigits = '';
  }

  return [formattedDigits, hasPlus];
}

function _typeof$3(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$3 = function _typeof(obj) { return typeof obj; }; } else { _typeof$3 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$3(obj); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance"); }

function _iterableToArrayLimit(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _classCallCheck$2(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$2(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$2(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$2(Constructor.prototype, protoProps); if (staticProps) _defineProperties$2(Constructor, staticProps); return Constructor; }

var AsYouType =
/*#__PURE__*/
function () {
  /**
   * @param {(string|object)?} [optionsOrDefaultCountry] - The default country used for parsing non-international phone numbers. Can also be an `options` object.
   * @param {Object} metadata
   */
  function AsYouType(optionsOrDefaultCountry, metadata) {
    _classCallCheck$2(this, AsYouType);

    this.metadata = new Metadata(metadata);

    var _this$getCountryAndCa = this.getCountryAndCallingCode(optionsOrDefaultCountry),
        _this$getCountryAndCa2 = _slicedToArray(_this$getCountryAndCa, 2),
        defaultCountry = _this$getCountryAndCa2[0],
        defaultCallingCode = _this$getCountryAndCa2[1];

    this.defaultCountry = defaultCountry;
    this.defaultCallingCode = defaultCallingCode;
    this.reset();
  }

  _createClass$2(AsYouType, [{
    key: "getCountryAndCallingCode",
    value: function getCountryAndCallingCode(optionsOrDefaultCountry) {
      // Set `defaultCountry` and `defaultCallingCode` options.
      var defaultCountry;
      var defaultCallingCode; // Turns out `null` also has type "object". Weird.

      if (optionsOrDefaultCountry) {
        if (_typeof$3(optionsOrDefaultCountry) === 'object') {
          defaultCountry = optionsOrDefaultCountry.defaultCountry;
          defaultCallingCode = optionsOrDefaultCountry.defaultCallingCode;
        } else {
          defaultCountry = optionsOrDefaultCountry;
        }
      }

      if (defaultCountry && !this.metadata.hasCountry(defaultCountry)) {
        defaultCountry = undefined;
      }

      return [defaultCountry, defaultCallingCode];
    }
    /**
     * Inputs "next" phone number characters.
     * @param  {string} text
     * @return {string} Formatted phone number characters that have been input so far.
     */

  }, {
    key: "input",
    value: function input(text) {
      var _this$parser$input = this.parser.input(text, this.state),
          digits = _this$parser$input.digits,
          justLeadingPlus = _this$parser$input.justLeadingPlus;

      if (justLeadingPlus) {
        this.formattedOutput = '+';
      } else if (digits) {
        this.determineTheCountryIfNeeded(); // Match the available formats by the currently available leading digits.

        if (this.state.nationalSignificantNumber) {
          this.formatter.narrowDownMatchingFormats(this.state);
        }

        var formattedNationalNumber;

        if (this.metadata.hasSelectedNumberingPlan()) {
          formattedNationalNumber = this.formatter.format(digits, this.state);
        }

        if (formattedNationalNumber === undefined) {
          // See if another national (significant) number could be re-extracted.
          if (this.parser.reExtractNationalSignificantNumber(this.state)) {
            this.determineTheCountryIfNeeded(); // If it could, then re-try formatting the new national (significant) number.

            var nationalDigits = this.state.getNationalDigits();

            if (nationalDigits) {
              formattedNationalNumber = this.formatter.format(nationalDigits, this.state);
            }
          }
        }

        this.formattedOutput = formattedNationalNumber ? this.getFullNumber(formattedNationalNumber) : this.getNonFormattedNumber();
      }

      return this.formattedOutput;
    }
  }, {
    key: "reset",
    value: function reset() {
      var _this = this;

      this.state = new AsYouTypeState({
        onCountryChange: function onCountryChange(country) {
          // Before version `1.6.0`, the official `AsYouType` formatter API
          // included the `.country` property of an `AsYouType` instance.
          // Since that property (along with the others) have been moved to
          // `this.state`, `this.country` property is emulated for compatibility
          // with the old versions.
          _this.country = country;
        },
        onCallingCodeChange: function onCallingCodeChange(country, callingCode) {
          _this.metadata.selectNumberingPlan(country, callingCode);

          _this.formatter.reset(_this.metadata.numberingPlan, _this.state);

          _this.parser.reset(_this.metadata.numberingPlan);
        }
      });
      this.formatter = new AsYouTypeFormatter({
        state: this.state,
        metadata: this.metadata
      });
      this.parser = new AsYouTypeParser({
        defaultCountry: this.defaultCountry,
        defaultCallingCode: this.defaultCallingCode,
        metadata: this.metadata,
        state: this.state,
        onNationalSignificantNumberChange: function onNationalSignificantNumberChange() {
          _this.determineTheCountryIfNeeded();

          _this.formatter.reset(_this.metadata.numberingPlan, _this.state);
        }
      });
      this.state.reset(this.defaultCountry, this.defaultCallingCode);
      this.formattedOutput = '';
      return this;
    }
    /**
     * Returns `true` if the phone number is being input in international format.
     * In other words, returns `true` if and only if the parsed phone number starts with a `"+"`.
     * @return {boolean}
     */

  }, {
    key: "isInternational",
    value: function isInternational() {
      return this.state.international;
    }
    /**
     * Returns the "country calling code" part of the phone number.
     * Returns `undefined` if the number is not being input in international format.
     * Returns "country calling code" for "non-geographic" phone numbering plans too.
     * @return {string} [callingCode]
     */

  }, {
    key: "getCallingCode",
    value: function getCallingCode() {
      return this.state.callingCode;
    } // A legacy alias.

  }, {
    key: "getCountryCallingCode",
    value: function getCountryCallingCode() {
      return this.getCallingCode();
    }
    /**
     * Returns a two-letter country code of the phone number.
     * Returns `undefined` for "non-geographic" phone numbering plans.
     * Returns `undefined` if no phone number has been input yet.
     * @return {string} [country]
     */

  }, {
    key: "getCountry",
    value: function getCountry() {
      var _this$state = this.state,
          digits = _this$state.digits,
          country = _this$state.country; // If no digits have been input yet,
      // then `this.country` is the `defaultCountry`.
      // Won't return the `defaultCountry` in such case.

      if (!digits) {
        return;
      }

      var countryCode = country;

      return countryCode;
    }
  }, {
    key: "determineTheCountryIfNeeded",
    value: function determineTheCountryIfNeeded() {
      // Suppose a user enters a phone number in international format,
      // and there're several countries corresponding to that country calling code,
      // and a country has been derived from the number, and then
      // a user enters one more digit and the number is no longer
      // valid for the derived country, so the country should be re-derived
      // on every new digit in those cases.
      //
      // If the phone number is being input in national format,
      // then it could be a case when `defaultCountry` wasn't specified
      // when creating `AsYouType` instance, and just `defaultCallingCode` was specified,
      // and that "calling code" could correspond to a "non-geographic entity",
      // or there could be several countries corresponding to that country calling code.
      // In those cases, `this.country` is `undefined` and should be derived
      // from the number. Again, if country calling code is ambiguous, then
      // `this.country` should be re-derived with each new digit.
      //
      if (!this.state.country || this.isCountryCallingCodeAmbiguous()) {
        this.determineTheCountry();
      }
    } // Prepends `+CountryCode ` in case of an international phone number

  }, {
    key: "getFullNumber",
    value: function getFullNumber(formattedNationalNumber) {
      var _this2 = this;

      if (this.isInternational()) {
        var prefix = function prefix(text) {
          return _this2.formatter.getInternationalPrefixBeforeCountryCallingCode(_this2.state, {
            spacing: text ? true : false
          }) + text;
        };

        var callingCode = this.state.callingCode;

        if (!callingCode) {
          return prefix("".concat(this.state.getDigitsWithoutInternationalPrefix()));
        }

        if (!formattedNationalNumber) {
          return prefix(callingCode);
        }

        return prefix("".concat(callingCode, " ").concat(formattedNationalNumber));
      }

      return formattedNationalNumber;
    }
  }, {
    key: "getNonFormattedNationalNumberWithPrefix",
    value: function getNonFormattedNationalNumberWithPrefix() {
      var _this$state2 = this.state,
          nationalSignificantNumber = _this$state2.nationalSignificantNumber,
          complexPrefixBeforeNationalSignificantNumber = _this$state2.complexPrefixBeforeNationalSignificantNumber,
          nationalPrefix = _this$state2.nationalPrefix;
      var number = nationalSignificantNumber;
      var prefix = complexPrefixBeforeNationalSignificantNumber || nationalPrefix;

      if (prefix) {
        number = prefix + number;
      }

      return number;
    }
  }, {
    key: "getNonFormattedNumber",
    value: function getNonFormattedNumber() {
      var nationalSignificantNumberMatchesInput = this.state.nationalSignificantNumberMatchesInput;
      return this.getFullNumber(nationalSignificantNumberMatchesInput ? this.getNonFormattedNationalNumberWithPrefix() : this.state.getNationalDigits());
    }
  }, {
    key: "getNonFormattedTemplate",
    value: function getNonFormattedTemplate() {
      var number = this.getNonFormattedNumber();

      if (number) {
        return number.replace(/[\+\d]/g, DIGIT_PLACEHOLDER);
      }
    }
  }, {
    key: "isCountryCallingCodeAmbiguous",
    value: function isCountryCallingCodeAmbiguous() {
      var callingCode = this.state.callingCode;
      var countryCodes = this.metadata.getCountryCodesForCallingCode(callingCode);
      return countryCodes && countryCodes.length > 1;
    } // Determines the country of the phone number
    // entered so far based on the country phone code
    // and the national phone number.

  }, {
    key: "determineTheCountry",
    value: function determineTheCountry() {
      this.state.setCountry(getCountryByCallingCode(this.isInternational() ? this.state.callingCode : this.defaultCallingCode, this.state.nationalSignificantNumber, this.metadata));
    }
    /**
     * Returns an instance of `PhoneNumber` class.
     * Will return `undefined` if no national (significant) number
     * digits have been entered so far, or if no `defaultCountry` has been
     * set and the user enters a phone number not in international format.
     */

  }, {
    key: "getNumber",
    value: function getNumber() {
      var _this$state3 = this.state,
          nationalSignificantNumber = _this$state3.nationalSignificantNumber,
          carrierCode = _this$state3.carrierCode;

      if (this.isInternational()) {
        if (!this.state.callingCode) {
          return;
        }
      } else {
        if (!this.state.country && !this.defaultCallingCode) {
          return;
        }
      }

      if (!nationalSignificantNumber) {
        return;
      }

      var countryCode = this.getCountry();
      var callingCode = this.getCountryCallingCode() || this.defaultCallingCode;
      var phoneNumber = new PhoneNumber(countryCode || callingCode, nationalSignificantNumber, this.metadata.metadata);

      if (carrierCode) {
        phoneNumber.carrierCode = carrierCode;
      } // Phone number extensions are not supported by "As You Type" formatter.


      return phoneNumber;
    }
    /**
     * Returns `true` if the phone number is "possible".
     * Is just a shortcut for `PhoneNumber.isPossible()`.
     * @return {boolean}
     */

  }, {
    key: "isPossible",
    value: function isPossible() {
      var phoneNumber = this.getNumber();

      if (!phoneNumber) {
        return false;
      }

      return phoneNumber.isPossible();
    }
    /**
     * Returns `true` if the phone number is "valid".
     * Is just a shortcut for `PhoneNumber.isValid()`.
     * @return {boolean}
     */

  }, {
    key: "isValid",
    value: function isValid() {
      var phoneNumber = this.getNumber();

      if (!phoneNumber) {
        return false;
      }

      return phoneNumber.isValid();
    }
    /**
     * @deprecated
     * This method is used in `react-phone-number-input/source/input-control.js`
     * in versions before `3.0.16`.
     */

  }, {
    key: "getNationalNumber",
    value: function getNationalNumber() {
      return this.state.nationalSignificantNumber;
    }
    /**
     * Returns the phone number characters entered by the user.
     * @return {string}
     */

  }, {
    key: "getChars",
    value: function getChars() {
      return (this.state.international ? '+' : '') + this.state.digits;
    }
    /**
     * Returns the template for the formatted phone number.
     * @return {string}
     */

  }, {
    key: "getTemplate",
    value: function getTemplate() {
      return this.formatter.getTemplate(this.state) || this.getNonFormattedTemplate() || '';
    }
  }]);

  return AsYouType;
}();

function getCountries(metadata) {
  return new Metadata(metadata).getCountries();
}

/**
 * Formats a (possibly incomplete) phone number.
 * The phone number can be either in E.164 format
 * or in a form of national number digits.
 * @param {string} value - A possibly incomplete phone number. Either in E.164 format or in a form of national number digits.
 * @param {string?} country - Two-letter ("ISO 3166-1 alpha-2") country code.
 * @return {string} Formatted (possibly incomplete) phone number.
 */

function formatIncompletePhoneNumber(value, country, metadata) {
  if (!metadata) {
    metadata = country;
    country = undefined;
  }

  return new AsYouType(country, metadata).input(value);
}

function getInputValuePrefix(_ref) {
  var country = _ref.country,
      international = _ref.international,
      withCountryCallingCode = _ref.withCountryCallingCode,
      metadata = _ref.metadata;
  return country && international && !withCountryCallingCode ? "+".concat(getCountryCallingCode$1(country, metadata)) : '';
}
function removeInputValuePrefix(value, prefix) {
  if (prefix) {
    value = value.slice(prefix.length);

    if (value[0] === ' ') {
      value = value.slice(1);
    }
  }

  return value;
}

function _extends$l() { _extends$l = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$l.apply(this, arguments); }

function _objectWithoutProperties$6(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$6(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$6(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function createInput$1(defaultMetadata) {
  /**
   * `InputSmart` is a "smarter" implementation of a `Component`
   * that can be passed to `<PhoneInput/>`. It parses and formats
   * the user's and maintains the caret's position in the process.
   * The caret positioning is maintained using `input-format` library.
   * Relies on being run in a DOM environment for calling caret positioning functions.
   */
  function InputSmart(_ref, ref) {
    var country = _ref.country,
        international = _ref.international,
        withCountryCallingCode = _ref.withCountryCallingCode,
        metadata = _ref.metadata,
        rest = _objectWithoutProperties$6(_ref, ["country", "international", "withCountryCallingCode", "metadata"]);

    var format = useCallback(function (value) {
      // "As you type" formatter.
      var formatter = new AsYouType(country, metadata);
      var prefix = getInputValuePrefix({
        country: country,
        international: international,
        withCountryCallingCode: withCountryCallingCode,
        metadata: metadata
      }); // Format the number.

      var text = formatter.input(prefix + value);
      var template = formatter.getTemplate();

      if (prefix) {
        text = removeInputValuePrefix(text, prefix); // `AsYouType.getTemplate()` can be `undefined`.

        if (template) {
          template = removeInputValuePrefix(template, prefix);
        }
      }

      return {
        text: text,
        template: template
      };
    }, [country, metadata]);
    return React.createElement(Input$1, _extends$l({}, rest, {
      ref: ref,
      parse: parsePhoneNumberCharacter,
      format: format
    }));
  }

  InputSmart = React.forwardRef(InputSmart);
  InputSmart.propTypes = {
    /**
     * The parsed phone number.
     * "Parsed" not in a sense of "E.164"
     * but rather in a sense of "having only
     * digits and possibly a leading plus character".
     * Examples: `""`, `"+"`, `"+123"`, `"123"`.
     */
    value: PropTypes.string.isRequired,

    /**
     * A function of `value: string`.
     * Updates the `value` property.
     */
    onChange: PropTypes.func.isRequired,

    /**
     * A two-letter country code for formatting `value`
     * as a national phone number (e.g. `(800) 555 35 35`).
     * E.g. "US", "RU", etc.
     * If no `country` is passed then `value`
     * is formatted as an international phone number.
     * (e.g. `+7 800 555 35 35`)
     * Perhaps the `country` property should have been called `defaultCountry`
     * because if `value` is an international number then `country` is ignored.
     */
    country: PropTypes.string,

    /**
     * If `country` property is passed along with `international={true}` property
     * then the phone number will be input in "international" format for that `country`
     * (without "country calling code").
     * For example, if `country="US"` property is passed to "without country select" input
     * then the phone number will be input in the "national" format for `US` (`(213) 373-4253`).
     * But if both `country="US"` and `international={true}` properties are passed then
     * the phone number will be input in the "international" format for `US` (`213 373 4253`)
     * (without "country calling code" `+1`).
     */
    international: PropTypes.bool,

    /**
     * If `country` and `international` properties are set,
     * then by default it won't include "country calling code" in the input field.
     * To change that, pass `withCountryCallingCode` property,
     * and it will include "country calling code" in the input field.
     */
    withCountryCallingCode: PropTypes.bool,

    /**
     * `libphonenumber-js` metadata.
     */
    metadata: PropTypes.object.isRequired
  };
  InputSmart.defaultProps = {
    metadata: defaultMetadata
  };
  return InputSmart;
}
var InputSmart = createInput$1();

function _extends$k() { _extends$k = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$k.apply(this, arguments); }

function _objectWithoutProperties$5(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$5(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$5(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function createInput(defaultMetadata) {
  /**
   * `InputBasic` is the most basic implementation of a `Component`
   * that can be passed to `<PhoneInput/>`. It parses and formats
   * the user's input but doesn't control the caret in the process:
   * when erasing or inserting digits in the middle of a phone number
   * the caret usually jumps to the end (this is the expected behavior).
   * Why does `InputBasic` exist when there's `InputSmart`?
   * One reason is working around the [Samsung Galaxy smart caret positioning bug]
   * (https://github.com/catamphetamine/react-phone-number-input/issues/75).
   * Another reason is that, unlike `InputSmart`, it doesn't require DOM environment.
   */
  function InputBasic(_ref, ref) {
    var value = _ref.value,
        onChange = _ref.onChange,
        country = _ref.country,
        international = _ref.international,
        withCountryCallingCode = _ref.withCountryCallingCode,
        metadata = _ref.metadata,
        Input = _ref.inputComponent,
        rest = _objectWithoutProperties$5(_ref, ["value", "onChange", "country", "international", "withCountryCallingCode", "metadata", "inputComponent"]);

    var prefix = getInputValuePrefix({
      country: country,
      international: international,
      withCountryCallingCode: withCountryCallingCode,
      metadata: metadata
    });

    var _onChange = useCallback(function (event) {
      var newValue = parseIncompletePhoneNumber(event.target.value); // By default, if a value is something like `"(123)"`
      // then Backspace would only erase the rightmost brace
      // becoming something like `"(123"`
      // which would give the same `"123"` value
      // which would then be formatted back to `"(123)"`
      // and so a user wouldn't be able to erase the phone number.
      // Working around this issue with this simple hack.

      if (newValue === value) {
        var newValueFormatted = format(prefix, newValue, country, metadata);

        if (newValueFormatted.indexOf(event.target.value) === 0) {
          // Trim the last digit (or plus sign).
          newValue = newValue.slice(0, -1);
        }
      }

      onChange(newValue);
    }, [prefix, value, onChange, country, metadata]);

    return React.createElement(Input, _extends$k({}, rest, {
      ref: ref,
      value: format(prefix, value, country, metadata),
      onChange: _onChange
    }));
  }

  InputBasic = React.forwardRef(InputBasic);
  InputBasic.propTypes = {
    /**
     * The parsed phone number.
     * "Parsed" not in a sense of "E.164"
     * but rather in a sense of "having only
     * digits and possibly a leading plus character".
     * Examples: `""`, `"+"`, `"+123"`, `"123"`.
     */
    value: PropTypes.string.isRequired,

    /**
     * A function of `value: string`.
     * Updates the `value` property.
     */
    onChange: PropTypes.func.isRequired,

    /**
     * A two-letter country code for formatting `value`
     * as a national phone number (e.g. `(800) 555 35 35`).
     * E.g. "US", "RU", etc.
     * If no `country` is passed then `value`
     * is formatted as an international phone number.
     * (e.g. `+7 800 555 35 35`)
     * Perhaps the `country` property should have been called `defaultCountry`
     * because if `value` is an international number then `country` is ignored.
     */
    country: PropTypes.string,

    /**
     * If `country` property is passed along with `international={true}` property
     * then the phone number will be input in "international" format for that `country`
     * (without "country calling code").
     * For example, if `country="US"` property is passed to "without country select" input
     * then the phone number will be input in the "national" format for `US` (`(213) 373-4253`).
     * But if both `country="US"` and `international={true}` properties are passed then
     * the phone number will be input in the "international" format for `US` (`213 373 4253`)
     * (without "country calling code" `+1`).
     */
    international: PropTypes.bool,

    /**
     * If `country` and `international` properties are set,
     * then by default it won't include "country calling code" in the input field.
     * To change that, pass `withCountryCallingCode` property,
     * and it will include "country calling code" in the input field.
     */
    withCountryCallingCode: PropTypes.bool,

    /**
     * `libphonenumber-js` metadata.
     */
    metadata: PropTypes.object.isRequired,

    /**
     * The `<input/>` component.
     */
    inputComponent: PropTypes.elementType.isRequired
  };
  InputBasic.defaultProps = {
    metadata: defaultMetadata,
    inputComponent: 'input'
  };
  return InputBasic;
}
var InputBasic = createInput();

function format(prefix, value, country, metadata) {
  return removeInputValuePrefix(formatIncompletePhoneNumber(prefix + value, country, metadata), prefix);
}

/**
 * Creates Unicode flag from a two-letter ISO country code.
 * https://stackoverflow.com/questions/24050671/how-to-put-japan-flag-character-in-a-string
 * @param  {string} country — A two-letter ISO country code (case-insensitive).
 * @return {string}
 */
function getCountryFlag(country) {
  return getRegionalIndicatorSymbol(country[0]) + getRegionalIndicatorSymbol(country[1]);
}
/**
 * Converts a letter to a Regional Indicator Symbol.
 * @param  {string} letter
 * @return {string}
 */

function getRegionalIndicatorSymbol(letter) {
  return String.fromCodePoint(0x1F1E6 - 65 + letter.toUpperCase().charCodeAt(0));
}

function _extends$j() { _extends$j = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$j.apply(this, arguments); }

function _objectWithoutProperties$4(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$4(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$4(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function CountrySelect(_ref) {
  var value = _ref.value,
      onChange = _ref.onChange,
      options = _ref.options,
      rest = _objectWithoutProperties$4(_ref, ["value", "onChange", "options"]);

  var onChange_ = useCallback(function (event) {
    var value = event.target.value;
    onChange(value === 'ZZ' ? undefined : value);
  }, [onChange]);
  useMemo(function () {
    return getSelectedOption(options, value);
  }, [options, value]); // "ZZ" means "International".
  // (HTML requires each `<option/>` have some string `value`).

  return React.createElement("select", _extends$j({}, rest, {
    value: value || 'ZZ',
    onChange: onChange_
  }), options.map(function (_ref2) {
    var value = _ref2.value,
        label = _ref2.label,
        divider = _ref2.divider;
    return React.createElement("option", {
      key: divider ? '|' : value || 'ZZ',
      value: divider ? '|' : value || 'ZZ',
      disabled: divider ? true : false,
      style: divider ? DIVIDER_STYLE : undefined
    }, label);
  }));
}
CountrySelect.propTypes = {
  /**
   * A two-letter country code.
   * Example: "US", "RU", etc.
   */
  value: PropTypes.string,

  /**
   * A function of `value: string`.
   * Updates the `value` property.
   */
  onChange: PropTypes.func.isRequired,
  // `<select/>` options.
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    divider: PropTypes.bool
  })).isRequired
};
var DIVIDER_STYLE = {
  fontSize: '1px',
  backgroundColor: 'currentColor',
  color: 'inherit'
};
function CountrySelectWithIcon(_ref3) {
  var value = _ref3.value,
      options = _ref3.options,
      className = _ref3.className,
      Icon = _ref3.iconComponent;
      _ref3.getIconAspectRatio;
      var Arrow = _ref3.arrowComponent,
      unicodeFlags = _ref3.unicodeFlags,
      rest = _objectWithoutProperties$4(_ref3, ["value", "options", "className", "iconComponent", "getIconAspectRatio", "arrowComponent", "unicodeFlags"]);

  var selectedOption = useMemo(function () {
    return getSelectedOption(options, value);
  }, [options, value]);
  return React.createElement("div", {
    className: "PhoneInputCountry"
  }, React.createElement(CountrySelect, _extends$j({}, rest, {
    value: value,
    options: options,
    className: classnames('PhoneInputCountrySelect', className)
  })), unicodeFlags && value && React.createElement("div", {
    className: "PhoneInputCountryIconUnicode"
  }, getCountryFlag(value)), !(unicodeFlags && value) && React.createElement(Icon, {
    country: value,
    label: selectedOption && selectedOption.label,
    aspectRatio: unicodeFlags ? 1 : undefined
  }), React.createElement(Arrow, null));
}
CountrySelectWithIcon.propTypes = {
  // Country flag component.
  iconComponent: PropTypes.elementType,
  // Select arrow component.
  arrowComponent: PropTypes.elementType.isRequired,
  // Set to `true` to render Unicode flag icons instead of SVG images.
  unicodeFlags: PropTypes.bool
};
CountrySelectWithIcon.defaultProps = {
  arrowComponent: function arrowComponent() {
    return React.createElement("div", {
      className: "PhoneInputCountrySelectArrow"
    });
  }
};

function getSelectedOption(options, value) {
  for (var _iterator = options, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
    var _ref4;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref4 = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref4 = _i.value;
    }

    var option = _ref4;

    if (!option.divider && option.value === value) {
      return option;
    }
  }
}

function _extends$i() { _extends$i = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$i.apply(this, arguments); }

function _objectWithoutProperties$3(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$3(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$3(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
// `<img/>` is wrapped in a `<div/>` to prevent SVGs from exploding in size in IE 11.
// https://github.com/catamphetamine/react-phone-number-input/issues/111

function FlagComponent(_ref) {
  var country = _ref.country,
      countryName = _ref.countryName,
      flags = _ref.flags,
      flagUrl = _ref.flagUrl,
      rest = _objectWithoutProperties$3(_ref, ["country", "countryName", "flags", "flagUrl"]);

  if (flags && flags[country]) {
    return flags[country]({
      title: countryName
    });
  }

  return React.createElement("img", _extends$i({}, rest, {
    alt: countryName,
    role: countryName ? undefined : "presentation",
    src: flagUrl.replace('{XX}', country).replace('{xx}', country.toLowerCase())
  }));
}
FlagComponent.propTypes = {
  // The country to be selected by default.
  // Two-letter country code ("ISO 3166-1 alpha-2").
  country: PropTypes.string.isRequired,
  // Will be HTML `title` attribute of the `<img/>`.
  countryName: PropTypes.string.isRequired,
  // Country flag icon components.
  // By default flag icons are inserted as `<img/>`s
  // with their `src` pointed to `country-flag-icons` gitlab pages website.
  // There might be cases (e.g. an offline application)
  // where having a large (3 megabyte) `<svg/>` flags
  // bundle is more appropriate.
  // `import flags from 'react-phone-number-input/flags'`.
  flags: PropTypes.objectOf(PropTypes.elementType),
  // A URL for a country flag icon.
  // By default it points to `country-flag-icons` gitlab pages website.
  flagUrl: PropTypes.string.isRequired
};

function _extends$h() { _extends$h = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$h.apply(this, arguments); }

function _objectWithoutProperties$2(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$2(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$2(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function InternationalIcon(_ref) {
  var aspectRatio = _ref.aspectRatio,
      rest = _objectWithoutProperties$2(_ref, ["aspectRatio"]);

  if (aspectRatio === 1) {
    return React.createElement(InternationalIcon1x1, rest);
  } else {
    return React.createElement(InternationalIcon3x2, rest);
  }
}
InternationalIcon.propTypes = {
  title: PropTypes.string.isRequired,
  aspectRatio: PropTypes.number
}; // 3x2.
// Using `<title/>` in `<svg/>`s:
// https://developer.mozilla.org/en-US/docs/Web/SVG/Element/title

function InternationalIcon3x2(_ref2) {
  var title = _ref2.title,
      rest = _objectWithoutProperties$2(_ref2, ["title"]);

  return React.createElement("svg", _extends$h({}, rest, {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 75 50"
  }), React.createElement("title", null, title), React.createElement("g", {
    className: "PhoneInputInternationalIconGlobe",
    stroke: "currentColor",
    fill: "none",
    strokeWidth: "2",
    strokeMiterlimit: "10"
  }, React.createElement("path", {
    strokeLinecap: "round",
    d: "M47.2,36.1C48.1,36,49,36,50,36c7.4,0,14,1.7,18.5,4.3"
  }), React.createElement("path", {
    d: "M68.6,9.6C64.2,12.3,57.5,14,50,14c-7.4,0-14-1.7-18.5-4.3"
  }), React.createElement("line", {
    x1: "26",
    y1: "25",
    x2: "74",
    y2: "25"
  }), React.createElement("line", {
    x1: "50",
    y1: "1",
    x2: "50",
    y2: "49"
  }), React.createElement("path", {
    strokeLinecap: "round",
    d: "M46.3,48.7c1.2,0.2,2.5,0.3,3.7,0.3c13.3,0,24-10.7,24-24S63.3,1,50,1S26,11.7,26,25c0,2,0.3,3.9,0.7,5.8"
  }), React.createElement("path", {
    strokeLinecap: "round",
    d: "M46.8,48.2c1,0.6,2.1,0.8,3.2,0.8c6.6,0,12-10.7,12-24S56.6,1,50,1S38,11.7,38,25c0,1.4,0.1,2.7,0.2,4c0,0.1,0,0.2,0,0.2"
  })), React.createElement("path", {
    className: "PhoneInputInternationalIconPhone",
    stroke: "none",
    fill: "currentColor",
    d: "M12.4,17.9c2.9-2.9,5.4-4.8,0.3-11.2S4.1,5.2,1.3,8.1C-2,11.4,1.1,23.5,13.1,35.6s24.3,15.2,27.5,11.9c2.8-2.8,7.8-6.3,1.4-11.5s-8.3-2.6-11.2,0.3c-2,2-7.2-2.2-11.7-6.7S10.4,19.9,12.4,17.9z"
  }));
}

InternationalIcon3x2.propTypes = {
  title: PropTypes.string.isRequired
}; // 1x1.
// Using `<title/>` in `<svg/>`s:
// https://developer.mozilla.org/en-US/docs/Web/SVG/Element/title

function InternationalIcon1x1(_ref3) {
  var title = _ref3.title,
      rest = _objectWithoutProperties$2(_ref3, ["title"]);

  return React.createElement("svg", _extends$h({}, rest, {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 50 50"
  }), React.createElement("title", null, title), React.createElement("g", {
    className: "PhoneInputInternationalIconGlobe",
    stroke: "currentColor",
    fill: "none",
    strokeWidth: "2",
    strokeLinecap: "round"
  }, React.createElement("path", {
    d: "M8.45,13A21.44,21.44,0,1,1,37.08,41.56"
  }), React.createElement("path", {
    d: "M19.36,35.47a36.9,36.9,0,0,1-2.28-13.24C17.08,10.39,21.88.85,27.8.85s10.72,9.54,10.72,21.38c0,6.48-1.44,12.28-3.71,16.21"
  }), React.createElement("path", {
    d: "M17.41,33.4A39,39,0,0,1,27.8,32.06c6.62,0,12.55,1.5,16.48,3.86"
  }), React.createElement("path", {
    d: "M44.29,8.53c-3.93,2.37-9.86,3.88-16.49,3.88S15.25,10.9,11.31,8.54"
  }), React.createElement("line", {
    x1: "27.8",
    y1: "0.85",
    x2: "27.8",
    y2: "34.61"
  }), React.createElement("line", {
    x1: "15.2",
    y1: "22.23",
    x2: "49.15",
    y2: "22.23"
  })), React.createElement("path", {
    className: "PhoneInputInternationalIconPhone",
    stroke: "transparent",
    fill: "currentColor",
    d: "M9.42,26.64c2.22-2.22,4.15-3.59.22-8.49S3.08,17,.93,19.17c-2.49,2.48-.13,11.74,9,20.89s18.41,11.5,20.89,9c2.15-2.15,5.91-4.77,1-8.71s-6.27-2-8.49.22c-1.55,1.55-5.48-1.69-8.86-5.08S7.87,28.19,9.42,26.64Z"
  }));
}

InternationalIcon1x1.propTypes = {
  title: PropTypes.string.isRequired
};

/**
 * Sorts country `<select/>` options.
 * Can move some country `<select/>` options
 * to the top of the list, for example.
 * @param  {object[]} countryOptions — Country `<select/>` options.
 * @param  {string[]} [countryOptionsOrder] — Country `<select/>` options order. Example: `["US", "CA", "AU", "|", "..."]`.
 * @return {object[]}
 */

function sortCountryOptions(options, order) {
  if (!order) {
    return options;
  }

  var optionsOnTop = [];
  var optionsOnBottom = [];
  var appendTo = optionsOnTop;

  for (var _iterator = order, _isArray = Array.isArray(_iterator), _i = 0, _iterator = _isArray ? _iterator : _iterator[Symbol.iterator]();;) {
    var _ref;

    if (_isArray) {
      if (_i >= _iterator.length) break;
      _ref = _iterator[_i++];
    } else {
      _i = _iterator.next();
      if (_i.done) break;
      _ref = _i.value;
    }

    var element = _ref;

    if (element === '|') {
      appendTo.push({
        divider: true
      });
    } else if (element === '...' || element === '…') {
      appendTo = optionsOnBottom;
    } else {
      (function () {
        var countryCode = void 0;

        if (element === '🌐') {
          countryCode = undefined;
        } else {
          countryCode = element;
        } // Find the position of the option.


        var index = options.indexOf(options.filter(function (option) {
          return option.value === countryCode;
        })[0]); // Get the option.

        var option = options[index]; // Remove the option from its default position.

        options.splice(index, 1); // Add the option on top.

        appendTo.push(option);
      })();
    }
  }

  return optionsOnTop.concat(options).concat(optionsOnBottom);
}
function getSupportedCountryOptions(countryOptions, metadata) {
  if (countryOptions) {
    countryOptions = countryOptions.filter(function (option) {
      switch (option) {
        case '🌐':
        case '|':
        case '...':
        case '…':
          return true;

        default:
          return isCountrySupportedWithError(option, metadata);
      }
    });

    if (countryOptions.length > 0) {
      return countryOptions;
    }
  }
}
function isCountrySupportedWithError(country, metadata) {
  if (isSupportedCountry(country, metadata)) {
    return true;
  } else {
    console.error("Country not found: ".concat(country));
    return false;
  }
}
function getSupportedCountries(countries, metadata) {
  if (countries) {
    countries = countries.filter(function (country) {
      return isCountrySupportedWithError(country, metadata);
    });

    if (countries.length === 0) {
      countries = undefined;
    }
  }

  return countries;
}

function createCountryIconComponent(_ref) {
  var flags = _ref.flags,
      flagUrl = _ref.flagUrl,
      FlagComponent = _ref.flagComponent,
      InternationalIcon$1 = _ref.internationalIcon;

  function CountryIcon(_ref2) {
    var country = _ref2.country,
        label = _ref2.label,
        aspectRatio = _ref2.aspectRatio;

    // `aspectRatio` is currently a hack for the default "International" icon
    // to render it as a square when Unicode flag icons are used.
    // So `aspectRatio` property is only used with the default "International" icon.
    var _aspectRatio = InternationalIcon$1 === InternationalIcon ? aspectRatio : undefined;

    return React.createElement("div", {
      className: classnames('PhoneInputCountryIcon', {
        'PhoneInputCountryIcon--square': _aspectRatio === 1,
        'PhoneInputCountryIcon--border': country
      })
    }, country ? React.createElement(FlagComponent, {
      country: country,
      countryName: label,
      flags: flags,
      flagUrl: flagUrl,
      className: "PhoneInputCountryIconImg"
    }) : React.createElement(InternationalIcon$1, {
      title: label,
      aspectRatio: _aspectRatio,
      className: "PhoneInputCountryIconImg"
    }));
  }

  CountryIcon.propTypes = {
    country: PropTypes.string,
    label: PropTypes.string.isRequired,
    aspectRatio: PropTypes.number
  };
  return CountryIcon;
}
createCountryIconComponent({
  // Must be equal to `defaultProps.flagUrl` in `./PhoneInputWithCountry.js`.
  flagUrl: 'https://purecatamphetamine.github.io/country-flag-icons/3x2/{XX}.svg',
  flagComponent: FlagComponent,
  internationalIcon: InternationalIcon
});

var metadata = PropTypes.shape({
  country_calling_codes: PropTypes.object.isRequired,
  countries: PropTypes.object.isRequired
});
var labels$1 = PropTypes.objectOf(PropTypes.string);

var ONLY_DIGITS_REGEXP = /^\d+$/;
function getInternationalPhoneNumberPrefix(country, metadata) {
  // Standard international phone number prefix: "+" and "country calling code".
  var prefix = '+' + getCountryCallingCode$1(country, metadata); // Get "leading digits" for a phone number of the country.
  // If there're "leading digits" then they can be part of the prefix too.

  metadata = new Metadata(metadata);
  metadata.selectNumberingPlan(country);

  if (metadata.numberingPlan.leadingDigits() && ONLY_DIGITS_REGEXP.test(metadata.numberingPlan.leadingDigits())) {
    prefix += metadata.numberingPlan.leadingDigits();
  }

  return prefix;
}

/**
 * Decides which country should be pre-selected
 * when the phone number input component is first mounted.
 * @param  {object?} phoneNumber - An instance of `PhoneNumber` class.
 * @param  {string?} country - Pre-defined country (two-letter code).
 * @param  {string[]?} countries - A list of countries available.
 * @param  {object} metadata - `libphonenumber-js` metadata
 * @return {string?}
 */

function getPreSelectedCountry(_ref) {
  var value = _ref.value,
      phoneNumber = _ref.phoneNumber,
      defaultCountry = _ref.defaultCountry,
      getAnyCountry = _ref.getAnyCountry,
      countries = _ref.countries,
      required = _ref.required,
      metadata = _ref.metadata;
  var country; // If can get country from E.164 phone number
  // then it overrides the `country` passed (or not passed).

  if (phoneNumber && phoneNumber.country) {
    // `country` will be left `undefined` in case of non-detection.
    country = phoneNumber.country;
  } else if (defaultCountry) {
    if (!value || couldNumberBelongToCountry(value, defaultCountry, metadata)) {
      country = defaultCountry;
    }
  } // Only pre-select a country if it's in the available `countries` list.


  if (countries && countries.indexOf(country) < 0) {
    country = undefined;
  } // If there will be no "International" option
  // then some `country` must be selected.
  // It will still be the wrong country though.
  // But still country `<select/>` can't be left in a broken state.


  if (!country && required && countries && countries.length > 0) {
    country = getAnyCountry(); // noCountryMatchesTheNumber = true
  }

  return country;
}
/**
 * Generates a sorted list of country `<select/>` options.
 * @param  {string[]} countries - A list of two-letter ("ISO 3166-1 alpha-2") country codes.
 * @param  {object} labels - Custom country labels. E.g. `{ RU: 'Россия', US: 'США', ... }`.
 * @param  {boolean} addInternationalOption - Whether should include "International" option at the top of the list.
 * @return {object[]} A list of objects having shape `{ value : string, label : string }`.
 */

function getCountrySelectOptions(_ref2) {
  var countries = _ref2.countries,
      countryNames = _ref2.countryNames,
      addInternationalOption = _ref2.addInternationalOption,
      compareStringsLocales = _ref2.compareStringsLocales,
      _compareStrings = _ref2.compareStrings;

  // Default country name comparator uses `String.localeCompare()`.
  if (!_compareStrings) {
    _compareStrings = compareStrings;
  } // Generates a `<Select/>` option for each country.


  var countrySelectOptions = countries.map(function (country) {
    return {
      value: country,
      // All `locale` country names included in this library
      // include all countries (this is checked at build time).
      // The only case when a country name might be missing
      // is when a developer supplies their own `labels` property.
      // To guard against such cases, a missing country name
      // is substituted by country code.
      label: countryNames[country] || country
    };
  }); // Sort the list of countries alphabetically.

  countrySelectOptions.sort(function (a, b) {
    return _compareStrings(a.label, b.label, compareStringsLocales);
  }); // Add the "International" option to the country list (if suitable)

  if (addInternationalOption) {
    countrySelectOptions.unshift({
      label: countryNames.ZZ
    });
  }

  return countrySelectOptions;
}
/**
 * Parses a E.164 phone number to an instance of `PhoneNumber` class.
 * @param {string?} value = E.164 phone number.
 * @param  {object} metadata - `libphonenumber-js` metadata
 * @return {object} Object having shape `{ country: string?, countryCallingCode: string, number: string }`. `PhoneNumber`: https://gitlab.com/catamphetamine/libphonenumber-js#phonenumber.
 * @example
 * parsePhoneNumber('+78005553535')
 */

function parsePhoneNumber(value, metadata) {
  return parsePhoneNumberFromString(value || '', metadata);
}
/**
 * Generates national number digits for a parsed phone.
 * May prepend national prefix.
 * The phone number must be a complete and valid phone number.
 * @param  {object} phoneNumber - An instance of `PhoneNumber` class.
 * @param  {object} metadata - `libphonenumber-js` metadata
 * @return {string}
 * @example
 * getNationalNumberDigits({ country: 'RU', phone: '8005553535' })
 * // returns '88005553535'
 */

function generateNationalNumberDigits(phoneNumber) {
  return phoneNumber.formatNational().replace(/\D/g, '');
}
/**
 * Migrates parsed `<input/>` `value` for the newly selected `country`.
 * @param {string?} phoneDigits - Phone number digits (and `+`) parsed from phone number `<input/>` (it's not the same as the `value` property).
 * @param {string?} prevCountry - Previously selected country.
 * @param {string?} newCountry - Newly selected country. Can't be same as previously selected country.
 * @param {object} metadata - `libphonenumber-js` metadata.
 * @param {boolean} useNationalFormat - whether should attempt to convert from international to national number for the new country.
 * @return {string?}
 */

function getPhoneDigitsForNewCountry(phoneDigits, _ref3) {
  var prevCountry = _ref3.prevCountry,
      newCountry = _ref3.newCountry,
      metadata = _ref3.metadata,
      useNationalFormat = _ref3.useNationalFormat;

  // If `parsed_input` is empty
  // then no need to migrate anything.
  if (!phoneDigits) {
    if (useNationalFormat) {
      return '';
    } else {
      // If `phoneDigits` is empty then set `phoneDigits` to
      // `+{getCountryCallingCode(newCountry)}`.
      return getInternationalPhoneNumberPrefix(newCountry, metadata);
    }
  } // If switching to some country.
  // (from "International" or another country)
  // If switching from "International" then `phoneDigits` starts with a `+`.
  // Otherwise it may or may not start with a `+`.


  if (newCountry) {
    // If the phone number was entered in international format
    // then migrate it to the newly selected country.
    // The phone number may be incomplete.
    // The phone number entered not necessarily starts with
    // the previously selected country phone prefix.
    if (phoneDigits[0] === '+') {
      // If the international phone number is for the new country
      // then convert it to local if required.
      if (useNationalFormat) {
        // // If a phone number is being input in international form
        // // and the country can already be derived from it,
        // // and if it is the new country, then format as a national number.
        // const derived_country = getCountryFromPossiblyIncompleteInternationalPhoneNumber(phoneDigits, metadata)
        // if (derived_country === newCountry) {
        // 	return stripCountryCallingCode(phoneDigits, derived_country, metadata)
        // }
        // Actually, the two countries don't necessarily need to match:
        // the condition could be looser here, because several countries
        // might share the same international phone number format
        // (for example, "NANPA" countries like US, Canada, etc).
        // The looser condition would be just "same nternational phone number format"
        // which would mean "same country calling code" in the context of `libphonenumber-js`.
        if (phoneDigits.indexOf('+' + getCountryCallingCode$1(newCountry, metadata)) === 0) {
          return stripCountryCallingCode(phoneDigits, newCountry, metadata);
        } // Simply discard the previously entered international phone number,
        // because otherwise any "smart" transformation like getting the
        // "national (significant) number" part and then prepending the
        // newly selected country's "country calling code" to it
        // would just be confusing for a user without being actually useful.


        return ''; // // Simply strip the leading `+` character
        // // therefore simply converting all digits into a "local" phone number.
        // // https://github.com/catamphetamine/react-phone-number-input/issues/287
        // return phoneDigits.slice(1)
      }

      if (prevCountry) {
        var newCountryPrefix = getInternationalPhoneNumberPrefix(newCountry, metadata);

        if (phoneDigits.indexOf(newCountryPrefix) === 0) {
          return phoneDigits;
        } else {
          return newCountryPrefix;
        }
      } else {
        var defaultValue = getInternationalPhoneNumberPrefix(newCountry, metadata); // If `phoneDigits`'s country calling code part is the same
        // as for the new `country`, then leave `phoneDigits` as is.

        if (phoneDigits.indexOf(defaultValue) === 0) {
          return phoneDigits;
        } // If `phoneDigits`'s country calling code part is not the same
        // as for the new `country`, then set `phoneDigits` to
        // `+{getCountryCallingCode(newCountry)}`.


        return defaultValue;
      } // // If the international phone number already contains
      // // any country calling code then trim the country calling code part.
      // // (that could also be the newly selected country phone code prefix as well)
      // // `phoneDigits` doesn't neccessarily belong to `prevCountry`.
      // // (e.g. if a user enters an international number
      // //  not belonging to any of the reduced `countries` list).
      // phoneDigits = stripCountryCallingCode(phoneDigits, prevCountry, metadata)
      // // Prepend country calling code prefix
      // // for the newly selected country.
      // return e164(phoneDigits, newCountry, metadata) || `+${getCountryCallingCode(newCountry, metadata)}`

    }
  } // If switching to "International" from a country.
  else {
      // If the phone number was entered in national format.
      if (phoneDigits[0] !== '+') {
        // Format the national phone number as an international one.
        // The phone number entered not necessarily even starts with
        // the previously selected country phone prefix.
        // Even if the phone number belongs to whole another country
        // it will still be parsed into some national phone number.
        return e164(phoneDigits, prevCountry, metadata) || '';
      }
    }

  return phoneDigits;
}
/**
 * Converts phone number digits to a (possibly incomplete) E.164 phone number.
 * @param  {string?} number - A possibly incomplete phone number digits string. Can be a possibly incomplete E.164 phone number.
 * @param  {string?} country
 * @param  {[object} metadata - `libphonenumber-js` metadata.
 * @return {string?}
 */

function e164(number, country, metadata) {
  if (!number) {
    return;
  } // If the phone number is being input in international format.


  if (number[0] === '+') {
    // If it's just the `+` sign then return nothing.
    if (number === '+') {
      return;
    } // If there are any digits then the `value` is returned as is.


    return number;
  } // For non-international phone numbers
  // an accompanying country code is required.


  if (!country) {
    return;
  }

  var partial_national_significant_number = getNationalSignificantNumberDigits(number, country, metadata);

  if (partial_national_significant_number) {
    return "+".concat(getCountryCallingCode$1(country, metadata)).concat(partial_national_significant_number);
  }
}
/**
 * Trims phone number digits if they exceed the maximum possible length
 * for a national (significant) number for the country.
 * @param  {string} number - A possibly incomplete phone number digits string. Can be a possibly incomplete E.164 phone number.
 * @param  {string} country
 * @param  {object} metadata - `libphonenumber-js` metadata.
 * @return {string} Can be empty.
 */

function trimNumber(number, country, metadata) {
  var nationalSignificantNumberPart = getNationalSignificantNumberDigits(number, country, metadata);

  if (nationalSignificantNumberPart) {
    var overflowDigitsCount = nationalSignificantNumberPart.length - getMaxNumberLength(country, metadata);

    if (overflowDigitsCount > 0) {
      return number.slice(0, number.length - overflowDigitsCount);
    }
  }

  return number;
}

function getMaxNumberLength(country, metadata) {
  // Get "possible lengths" for a phone number of the country.
  metadata = new Metadata(metadata);
  metadata.selectNumberingPlan(country); // Return the last "possible length".

  return metadata.numberingPlan.possibleLengths()[metadata.numberingPlan.possibleLengths().length - 1];
} // If the phone number being input is an international one
// then tries to derive the country from the phone number.
// (regardless of whether there's any country currently selected)

/**
 * @param {string} partialE164Number - A possibly incomplete E.164 phone number.
 * @param {string?} country - Currently selected country.
 * @param {string[]?} countries - A list of available countries. If not passed then "all countries" are assumed.
 * @param  {object} metadata - `libphonenumber-js` metadata.
 * @return {string?}
 */


function getCountryForPartialE164Number(partialE164Number, _ref4) {
  var country = _ref4.country,
      countries = _ref4.countries,
      required = _ref4.required,
      metadata = _ref4.metadata;

  if (partialE164Number === '+') {
    // Don't change the currently selected country yet.
    return country;
  }

  var derived_country = getCountryFromPossiblyIncompleteInternationalPhoneNumber(partialE164Number, metadata); // If a phone number is being input in international form
  // and the country can already be derived from it,
  // then select that country.

  if (derived_country && (!countries || countries.indexOf(derived_country) >= 0)) {
    return derived_country;
  } // If "International" country option has not been disabled
  // and the international phone number entered doesn't correspond
  // to the currently selected country then reset the currently selected country.
  else if (country && !required && !couldNumberBelongToCountry(partialE164Number, country, metadata)) {
      return undefined;
    } // Don't change the currently selected country.


  return country;
}
/**
 * Parses `<input/>` value. Derives `country` from `input`. Derives an E.164 `value`.
 * @param  {string?} phoneDigits — Parsed `<input/>` value. Examples: `""`, `"+"`, `"+123"`, `"123"`.
 * @param  {string?} prevPhoneDigits — Previous parsed `<input/>` value. Examples: `""`, `"+"`, `"+123"`, `"123"`.
 * @param  {string?} country - Currently selected country.
 * @param  {boolean} countryRequired - Is selecting some country required.
 * @param  {function} getAnyCountry - Can be used to get any country when selecting some country required.
 * @param  {string[]?} countries - A list of available countries. If not passed then "all countries" are assumed.
 * @param  {boolean} international - Set to `true` to force international phone number format (leading `+`). Set to `false` to force "national" phone number format. Is `undefined` by default.
 * @param  {boolean} limitMaxLength — Whether to enable limiting phone number max length.
 * @param  {object} metadata - `libphonenumber-js` metadata.
 * @return {object} An object of shape `{ input, country, value }`.
 */

function onPhoneDigitsChange(phoneDigits, _ref5) {
  var prevPhoneDigits = _ref5.prevPhoneDigits,
      country = _ref5.country,
      defaultCountry = _ref5.defaultCountry,
      countryRequired = _ref5.countryRequired,
      getAnyCountry = _ref5.getAnyCountry,
      countries = _ref5.countries,
      international = _ref5.international,
      limitMaxLength = _ref5.limitMaxLength,
      countryCallingCodeEditable = _ref5.countryCallingCodeEditable,
      metadata = _ref5.metadata;

  if (international && countryCallingCodeEditable === false) {
    var prefix = getInternationalPhoneNumberPrefix(country, metadata); // The `<input/>` value must start with the country calling code.

    if (phoneDigits.indexOf(prefix) !== 0) {
      var _value; // If a phone number input is declared as
      // `international` and `withCountryCallingCode`,
      // then it's gonna be non-empty even before the user
      // has input anything in it.
      // This will result in its contents (the country calling code part)
      // being selected when the user tabs into such field.
      // If the user then starts inputting the national part digits,
      // then `<input/>` value changes from `+xxx` to `y`
      // because inputting anything while having the `<input/>` value
      // selected results in erasing the `<input/>` value
      // So, the component handles such case by restoring
      // the intended `<input/>`` value: `+xxxy`.
      // https://gitlab.com/catamphetamine/react-phone-number-input/-/issues/43


      if (phoneDigits && phoneDigits[0] !== '+') {
        phoneDigits = prefix + phoneDigits;
        _value = phoneDigits;
      } else {
        phoneDigits = prefix;
      }

      return {
        phoneDigits: phoneDigits,
        value: _value,
        country: country
      };
    }
  } // If `international` property is `false`, then it means
  // "enforce national-only format during input",
  // so, if that's the case, then remove all `+` characters,
  // but only if some country is currently selected.
  // (not if "International" country is selected).


  if (international === false && country && phoneDigits && phoneDigits[0] === '+') {
    phoneDigits = convertInternationalPhoneDigitsToNational(phoneDigits, country, metadata);
  } // Trim the input to not exceed the maximum possible number length.


  if (phoneDigits && country && limitMaxLength) {
    phoneDigits = trimNumber(phoneDigits, country, metadata);
  } // If this `onChange()` event was triggered
  // as a result of selecting "International" country,
  // then force-prepend a `+` sign if the phone number
  // `<input/>` value isn't in international format.
  // Also, force-prepend a `+` sign if international
  // phone number input format is set.


  if (phoneDigits && phoneDigits[0] !== '+' && (!country || international)) {
    phoneDigits = '+' + phoneDigits;
  } // If the previously entered phone number
  // has been entered in international format
  // and the user decides to erase it,
  // then also reset the `country`
  // because it was most likely automatically selected
  // while the user was typing in the phone number
  // in international format.
  // This fixes the issue when a user is presented
  // with a phone number input with no country selected
  // and then types in their local phone number
  // then discovers that the input's messed up
  // (a `+` has been prepended at the start of their input
  //  and a random country has been selected),
  // decides to undo it all by erasing everything
  // and then types in their local phone number again
  // resulting in a seemingly correct phone number
  // but in reality that phone number has incorrect country.
  // https://github.com/catamphetamine/react-phone-number-input/issues/273


  if (!phoneDigits && prevPhoneDigits && prevPhoneDigits[0] === '+') {
    if (international) {
      country = undefined;
    } else {
      country = defaultCountry;
    }
  } // Also resets such "randomly" selected country
  // as soon as the user erases the number
  // digit-by-digit up to the leading `+` sign.


  if (phoneDigits === '+' && prevPhoneDigits && prevPhoneDigits[0] === '+' && prevPhoneDigits.length > '+'.length) {
    country = undefined;
  } // Generate the new `value` property.


  var value;

  if (phoneDigits) {
    if (phoneDigits[0] === '+') {
      if (phoneDigits === '+') {
        value = undefined;
      } else if (country && getInternationalPhoneNumberPrefix(country, metadata).indexOf(phoneDigits) === 0) {
        value = undefined;
      } else {
        value = phoneDigits;
      }
    } else {
      value = e164(phoneDigits, country, metadata);
    }
  } // Derive the country from the phone number.
  // (regardless of whether there's any country currently selected,
  //  because there could be several countries corresponding to one country calling code)


  if (value) {
    country = getCountryForPartialE164Number(value, {
      country: country,
      countries: countries,
      metadata: metadata
    }); // If `international` property is `false`, then it means
    // "enforce national-only format during input",
    // so, if that's the case, then remove all `+` characters,
    // but only if some country is currently selected.
    // (not if "International" country is selected).

    if (international === false && country && phoneDigits && phoneDigits[0] === '+') {
      phoneDigits = convertInternationalPhoneDigitsToNational(phoneDigits, country, metadata); // Re-calculate `value` because `phoneDigits` has changed.

      value = e164(phoneDigits, country, metadata);
    }
  }

  if (!country && countryRequired) {
    country = defaultCountry || getAnyCountry();
  }

  return {
    phoneDigits: phoneDigits,
    country: country,
    value: value
  };
}

function convertInternationalPhoneDigitsToNational(input, country, metadata) {
  // Handle the case when a user might have pasted
  // a phone number in international format.
  if (input.indexOf(getInternationalPhoneNumberPrefix(country, metadata)) === 0) {
    // Create "as you type" formatter.
    var formatter = new AsYouType(country, metadata); // Input partial national phone number.

    formatter.input(input); // Return the parsed partial national phone number.

    var phoneNumber = formatter.getNumber();

    if (phoneNumber) {
      // Transform the number to a national one,
      // and remove all non-digits.
      return phoneNumber.formatNational().replace(/\D/g, '');
    } else {
      return '';
    }
  } else {
    // Just remove the `+` sign.
    return input.replace(/\D/g, '');
  }
}
/**
 * Determines the country for a given (possibly incomplete) E.164 phone number.
 * @param  {string} number - A possibly incomplete E.164 phone number.
 * @param  {object} metadata - `libphonenumber-js` metadata.
 * @return {string?}
 */


function getCountryFromPossiblyIncompleteInternationalPhoneNumber(number, metadata) {
  var formatter = new AsYouType(null, metadata);
  formatter.input(number); // // `001` is a special "non-geograpical entity" code
  // // in Google's `libphonenumber` library.
  // if (formatter.getCountry() === '001') {
  // 	return
  // }

  return formatter.getCountry();
}
/**
 * Compares two strings.
 * A helper for `Array.sort()`.
 * @param {string} a — First string.
 * @param {string} b — Second string.
 * @param {(string[]|string)} [locales] — The `locales` argument of `String.localeCompare`.
 */

function compareStrings(a, b, locales) {
  // Use `String.localeCompare` if it's available.
  // https://developer.mozilla.org/ru/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare
  // Which means everyone except IE <= 10 and Safari <= 10.
  // `localeCompare()` is available in latest Node.js versions.

  /* istanbul ignore else */
  if (String.prototype.localeCompare) {
    return a.localeCompare(b, locales);
  }
  /* istanbul ignore next */


  return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * Strips `+${countryCallingCode}` prefix from an E.164 phone number.
 * @param {string} number - (possibly incomplete) E.164 phone number.
 * @param {string?} country - A possible country for this phone number.
 * @param {object} metadata - `libphonenumber-js` metadata.
 * @return {string}
 */

function stripCountryCallingCode(number, country, metadata) {
  // Just an optimization, so that it
  // doesn't have to iterate through all country calling codes.
  if (country) {
    var countryCallingCodePrefix = '+' + getCountryCallingCode$1(country, metadata); // If `country` fits the actual `number`.

    if (number.length < countryCallingCodePrefix.length) {
      if (countryCallingCodePrefix.indexOf(number) === 0) {
        return '';
      }
    } else {
      if (number.indexOf(countryCallingCodePrefix) === 0) {
        return number.slice(countryCallingCodePrefix.length);
      }
    }
  } // If `country` doesn't fit the actual `number`.
  // Try all available country calling codes.


  for (var _i = 0, _Object$keys = Object.keys(metadata.country_calling_codes); _i < _Object$keys.length; _i++) {
    var country_calling_code = _Object$keys[_i];

    if (number.indexOf(country_calling_code) === '+'.length) {
      return number.slice('+'.length + country_calling_code.length);
    }
  }

  return '';
}
/**
 * Parses a partially entered national phone number digits
 * (or a partially entered E.164 international phone number)
 * and returns the national significant number part.
 * National significant number returned doesn't come with a national prefix.
 * @param {string} number - National number digits. Or possibly incomplete E.164 phone number.
 * @param {string?} country
 * @param {object} metadata - `libphonenumber-js` metadata.
 * @return {string} [result]
 */

function getNationalSignificantNumberDigits(number, country, metadata) {
  // Create "as you type" formatter.
  var formatter = new AsYouType(country, metadata); // Input partial national phone number.

  formatter.input(number); // Return the parsed partial national phone number.

  var phoneNumber = formatter.getNumber();
  return phoneNumber && phoneNumber.nationalNumber;
}
/**
 * Checks if a partially entered E.164 phone number could belong to a country.
 * @param  {string} number
 * @param  {string} country
 * @return {boolean}
 */

function couldNumberBelongToCountry(number, country, metadata) {
  var intlPhoneNumberPrefix = getInternationalPhoneNumberPrefix(country, metadata);
  var i = 0;

  while (i < number.length && i < intlPhoneNumberPrefix.length) {
    if (number[i] !== intlPhoneNumberPrefix[i]) {
      return false;
    }

    i++;
  }

  return true;
}
/**
 * Gets initial "phone digits" (including `+`, if using international format).
 * @return {string} [phoneDigits] Returns `undefined` if there should be no initial "phone digits".
 */

function getInitialPhoneDigits(_ref6) {
  var value = _ref6.value,
      phoneNumber = _ref6.phoneNumber,
      defaultCountry = _ref6.defaultCountry,
      international = _ref6.international,
      useNationalFormat = _ref6.useNationalFormat,
      metadata = _ref6.metadata;

  // If the `value` (E.164 phone number)
  // belongs to the currently selected country
  // and `useNationalFormat` is `true`
  // then convert `value` (E.164 phone number)
  // to a local phone number digits.
  // E.g. '+78005553535' -> '88005553535'.
  if ((international === false || useNationalFormat) && phoneNumber && phoneNumber.country) {
    return generateNationalNumberDigits(phoneNumber);
  } // If `international` property is `true`,
  // meaning "enforce international phone number format",
  // then always show country calling code in the input field.


  if (!value && international && defaultCountry) {
    return getInternationalPhoneNumberPrefix(defaultCountry, metadata);
  }

  return value;
}

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(Object(source), true).forEach(function (key) { _defineProperty$2(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$2(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function getPhoneInputWithCountryStateUpdateFromNewProps(props, prevProps, state) {
  var metadata = props.metadata,
      countries = props.countries,
      newDefaultCountry = props.defaultCountry,
      newValue = props.value,
      newReset = props.reset,
      international = props.international,
      displayInitialValueAsLocalNumber = props.displayInitialValueAsLocalNumber,
      initialValueFormat = props.initialValueFormat;
  var prevDefaultCountry = prevProps.defaultCountry,
      prevValue = prevProps.value,
      prevReset = prevProps.reset;
  state.country;
      var value = state.value,
      hasUserSelectedACountry = state.hasUserSelectedACountry;

  var _getInitialPhoneDigits = function _getInitialPhoneDigits(parameters) {
    return getInitialPhoneDigits(_objectSpread$1({}, parameters, {
      international: international,
      useNationalFormat: displayInitialValueAsLocalNumber || initialValueFormat === 'national',
      metadata: metadata
    }));
  }; // Some users requested a way to reset the component
  // (both number `<input/>` and country `<select/>`).
  // Whenever `reset` property changes both number `<input/>`
  // and country `<select/>` are reset.
  // It's not implemented as some instance `.reset()` method
  // because `ref` is forwarded to `<input/>`.
  // It's also not replaced with just resetting `country` on
  // external `value` reset, because a user could select a country
  // and then not input any `value`, and so the selected country
  // would be "stuck", if not using this `reset` property.
  // https://github.com/catamphetamine/react-phone-number-input/issues/300


  if (newReset !== prevReset) {
    return {
      phoneDigits: _getInitialPhoneDigits({
        value: undefined,
        defaultCountry: newDefaultCountry
      }),
      value: undefined,
      country: newDefaultCountry,
      hasUserSelectedACountry: undefined
    };
  } // `value` is the value currently shown in the component:
  // it's stored in the component's `state`, and it's not the `value` property.
  // `prevValue` is "previous `value` property".
  // `newValue` is "new `value` property".
  // If the default country changed
  // (e.g. in case of ajax GeoIP detection after page loaded)
  // then select it, but only if the user hasn't already manually
  // selected a country, and no phone number has been manually entered so far.
  // Because if the user has already started inputting a phone number
  // then they're okay with no country being selected at all ("International")
  // and they don't want to be disturbed, don't want their input to be screwed, etc.


  if (newDefaultCountry !== prevDefaultCountry) {
    var isNewDefaultCountrySupported = !newDefaultCountry || isCountrySupportedWithError(newDefaultCountry, metadata);

    var noValueHasBeenEnteredByTheUser = // By default, "no value has been entered" means `value` is `undefined`.
    !value || // When `international` is `true`, and some country has been pre-selected,
    // then the `<input/>` contains a pre-filled value of `+${countryCallingCode}${leadingDigits}`,
    // so in case of `international` being `true`, "the user hasn't entered anything" situation
    // doesn't just mean `value` is `undefined`, but could also mean `value` is `+${countryCallingCode}`.
    international && value === _getInitialPhoneDigits({
      value: undefined,
      defaultCountry: prevDefaultCountry
    }); // Only update the `defaultCountry` property if no phone number
    // has been entered by the user or pre-set by the application.


    var noValueHasBeenEntered = !newValue && noValueHasBeenEnteredByTheUser;

    if (!hasUserSelectedACountry && isNewDefaultCountrySupported && noValueHasBeenEntered) {
      return {
        country: newDefaultCountry,
        // If `phoneDigits` is empty, then automatically select the new `country`
        // and set `phoneDigits` to `+{getCountryCallingCode(newCountry)}`.
        // The code assumes that "no phone number has been entered by the user",
        // and no `value` property has been passed, so the `phoneNumber` parameter
        // of `_getInitialPhoneDigits({ value, phoneNumber, ... })` is `undefined`.
        phoneDigits: _getInitialPhoneDigits({
          value: undefined,
          defaultCountry: newDefaultCountry
        }),
        // `value` is `undefined` and it stays so.
        value: undefined
      };
    }
  } // If a new `value` is set externally.
  // (e.g. as a result of an ajax API request
  //  to get user's phone after page loaded)
  // The first part — `newValue !== prevValue` —
  // is basically `props.value !== prevProps.value`
  // so it means "if value property was changed externally".
  // The second part — `newValue !== value` —
  // is for ignoring the `getDerivedStateFromProps()` call
  // which happens in `this.onChange()` right after `this.setState()`.
  // If this `getDerivedStateFromProps()` call isn't ignored
  // then the country flag would reset on each input.


  if (newValue !== prevValue && newValue !== value) {
    var phoneNumber;
    var parsedCountry;

    if (newValue) {
      phoneNumber = parsePhoneNumber(newValue, metadata);
      var supportedCountries = getSupportedCountries(countries, metadata);

      if (phoneNumber && phoneNumber.country) {
        // Ignore `else` because all countries are supported in metadata.

        /* istanbul ignore next */
        if (!supportedCountries || supportedCountries.indexOf(phoneNumber.country) >= 0) {
          parsedCountry = phoneNumber.country;
        }
      } else {
        parsedCountry = getCountryForPartialE164Number(newValue, {
          country: undefined,
          countries: supportedCountries,
          metadata: metadata
        });
      }
    }

    var hasUserSelectedACountryUpdate;

    if (!newValue) {
      // Reset `hasUserSelectedACountry` flag in `state`.
      hasUserSelectedACountryUpdate = {
        hasUserSelectedACountry: undefined
      };
    }

    return _objectSpread$1({}, hasUserSelectedACountryUpdate, {
      phoneDigits: _getInitialPhoneDigits({
        phoneNumber: phoneNumber,
        value: newValue,
        defaultCountry: newDefaultCountry
      }),
      value: newValue,
      country: newValue ? parsedCountry : newDefaultCountry
    });
  } // `defaultCountry` didn't change.
  // `value` didn't change.
  // `phoneDigits` didn't change, because `value` didn't change.
  //
  // So no need to update state.

}

function _typeof$2(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$2 = function _typeof(obj) { return typeof obj; }; } else { _typeof$2 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$2(obj); }

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty$1(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _extends$g() { _extends$g = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$g.apply(this, arguments); }

function _objectWithoutProperties$1(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose$1(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose$1(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$1(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$1(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$1(Constructor.prototype, protoProps); if (staticProps) _defineProperties$1(Constructor, staticProps); return Constructor; }

function _possibleConstructorReturn$1(self, call) { if (call && (_typeof$2(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$1(self); }

function _getPrototypeOf$1(o) { _getPrototypeOf$1 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$1(o); }

function _assertThisInitialized$1(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$1(subClass, superClass); }

function _setPrototypeOf$1(o, p) { _setPrototypeOf$1 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$1(o, p); }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var PhoneNumberInput_ =
/*#__PURE__*/
function (_React$PureComponent) {
  _inherits$1(PhoneNumberInput_, _React$PureComponent);

  function PhoneNumberInput_(props) {
    var _this;

    _classCallCheck$1(this, PhoneNumberInput_);

    _this = _possibleConstructorReturn$1(this, _getPrototypeOf$1(PhoneNumberInput_).call(this, props));

    _defineProperty$1(_assertThisInitialized$1(_this), "inputRef", React.createRef());

    _defineProperty$1(_assertThisInitialized$1(_this), "setInputRef", function (instance) {
      _this.inputRef.current = instance;
      var ref = _this.props.inputRef;

      if (ref) {
        if (typeof ref === 'function') {
          ref(instance);
        } else {
          ref.current = instance;
        }
      }
    });

    _defineProperty$1(_assertThisInitialized$1(_this), "isCountrySupportedWithError", function (country) {
      var metadata = _this.props.metadata;
      return isCountrySupportedWithError(country, metadata);
    });

    _defineProperty$1(_assertThisInitialized$1(_this), "onCountryChange", function (newCountry) {
      var _this$props = _this.props,
          international = _this$props.international,
          metadata = _this$props.metadata,
          onChange = _this$props.onChange,
          focusInputOnCountrySelection = _this$props.focusInputOnCountrySelection;
      var _this$state = _this.state,
          prevPhoneDigits = _this$state.phoneDigits,
          prevCountry = _this$state.country; // After the new `country` has been selected,
      // if the phone number `<input/>` holds any digits
      // then migrate those digits for the new `country`.

      var newPhoneDigits = getPhoneDigitsForNewCountry(prevPhoneDigits, {
        prevCountry: prevCountry,
        newCountry: newCountry,
        metadata: metadata,
        // Convert the phone number to "national" format
        // when the user changes the selected country by hand.
        useNationalFormat: !international
      });
      var newValue = e164(newPhoneDigits, newCountry, metadata); // Focus phone number `<input/>` upon country selection.

      if (focusInputOnCountrySelection) {
        _this.inputRef.current.focus();
      } // If the user has already manually selected a country
      // then don't override that already selected country
      // if the `defaultCountry` property changes.
      // That's what `hasUserSelectedACountry` flag is for.


      _this.setState({
        country: newCountry,
        hasUserSelectedACountry: true,
        phoneDigits: newPhoneDigits,
        value: newValue
      }, function () {
        // Update the new `value` property.
        // Doing it after the `state` has been updated
        // because `onChange()` will trigger `getDerivedStateFromProps()`
        // with the new `value` which will be compared to `state.value` there.
        onChange(newValue);
      });
    });

    _defineProperty$1(_assertThisInitialized$1(_this), "onChange", function (_phoneDigits) {
      var _this$props2 = _this.props,
          defaultCountry = _this$props2.defaultCountry,
          onChange = _this$props2.onChange,
          addInternationalOption = _this$props2.addInternationalOption,
          international = _this$props2.international,
          limitMaxLength = _this$props2.limitMaxLength,
          countryCallingCodeEditable = _this$props2.countryCallingCodeEditable,
          metadata = _this$props2.metadata;
      var _this$state2 = _this.state,
          countries = _this$state2.countries,
          prevPhoneDigits = _this$state2.phoneDigits,
          currentlySelectedCountry = _this$state2.country;

      var _onPhoneDigitsChange = onPhoneDigitsChange(_phoneDigits, {
        prevPhoneDigits: prevPhoneDigits,
        country: currentlySelectedCountry,
        countryRequired: !addInternationalOption,
        defaultCountry: defaultCountry,
        getAnyCountry: function getAnyCountry() {
          return _this.getFirstSupportedCountry({
            countries: countries
          });
        },
        countries: countries,
        international: international,
        limitMaxLength: limitMaxLength,
        countryCallingCodeEditable: countryCallingCodeEditable,
        metadata: metadata
      }),
          phoneDigits = _onPhoneDigitsChange.phoneDigits,
          country = _onPhoneDigitsChange.country,
          value = _onPhoneDigitsChange.value;

      var stateUpdate = {
        phoneDigits: phoneDigits,
        value: value,
        country: country
      };

      if (countryCallingCodeEditable === false) {
        // If it simply did `setState({ phoneDigits: intlPrefix })` here,
        // then it would have no effect when erasing an inital international prefix
        // via Backspace, because `phoneDigits` in `state` wouldn't change
        // as a result, because it was `prefix` and it became `prefix`,
        // so the component wouldn't rerender, and the user would be able
        // to erase the country calling code part, and that part is
        // assumed to be non-eraseable. That's why the component is
        // forcefully rerendered here.
        // https://github.com/catamphetamine/react-phone-number-input/issues/367#issuecomment-721703501
        if (!value && phoneDigits === _this.state.phoneDigits) {
          // Force a re-render of the `<input/>` in order to reset its value.
          stateUpdate.forceRerender = {};
        }
      }

      _this.setState(stateUpdate, // Update the new `value` property.
      // Doing it after the `state` has been updated
      // because `onChange()` will trigger `getDerivedStateFromProps()`
      // with the new `value` which will be compared to `state.value` there.
      function () {
        return onChange(value);
      });
    });

    _defineProperty$1(_assertThisInitialized$1(_this), "_onFocus", function () {
      return _this.setState({
        isFocused: true
      });
    });

    _defineProperty$1(_assertThisInitialized$1(_this), "_onBlur", function () {
      return _this.setState({
        isFocused: false
      });
    });

    _defineProperty$1(_assertThisInitialized$1(_this), "onFocus", function (event) {
      _this._onFocus();

      var onFocus = _this.props.onFocus;

      if (onFocus) {
        onFocus(event);
      }
    });

    _defineProperty$1(_assertThisInitialized$1(_this), "onBlur", function (event) {
      var onBlur = _this.props.onBlur;

      _this._onBlur();

      if (onBlur) {
        onBlur(event);
      }
    });

    _defineProperty$1(_assertThisInitialized$1(_this), "onCountryFocus", function (event) {
      _this._onFocus(); // this.setState({ countrySelectFocused: true })


      var countrySelectProps = _this.props.countrySelectProps;

      if (countrySelectProps) {
        var onFocus = countrySelectProps.onFocus;

        if (onFocus) {
          onFocus(event);
        }
      }
    });

    _defineProperty$1(_assertThisInitialized$1(_this), "onCountryBlur", function (event) {
      _this._onBlur(); // this.setState({ countrySelectFocused: false })


      var countrySelectProps = _this.props.countrySelectProps;

      if (countrySelectProps) {
        var onBlur = countrySelectProps.onBlur;

        if (onBlur) {
          onBlur(event);
        }
      }
    });

    var _this$props3 = _this.props,
        _value = _this$props3.value;
        _this$props3.labels;
        var _international = _this$props3.international,
        _addInternationalOption = _this$props3.addInternationalOption,
        displayInitialValueAsLocalNumber = _this$props3.displayInitialValueAsLocalNumber,
        initialValueFormat = _this$props3.initialValueFormat,
        _metadata = _this$props3.metadata;
    var _this$props4 = _this.props,
        _defaultCountry = _this$props4.defaultCountry,
        _countries = _this$props4.countries; // Validate `defaultCountry`.

    if (_defaultCountry) {
      if (!_this.isCountrySupportedWithError(_defaultCountry)) {
        _defaultCountry = undefined;
      }
    } // Validate `countries`.


    _countries = getSupportedCountries(_countries, _metadata);
    var phoneNumber = parsePhoneNumber(_value, _metadata);
    _this.CountryIcon = createCountryIconComponent(_this.props);
    var preSelectedCountry = getPreSelectedCountry({
      value: _value,
      phoneNumber: phoneNumber,
      defaultCountry: _defaultCountry,
      required: !_addInternationalOption,
      countries: _countries || getCountries(_metadata),
      getAnyCountry: function getAnyCountry() {
        return _this.getFirstSupportedCountry({
          countries: _countries
        });
      },
      metadata: _metadata
    });
    _this.state = {
      // Workaround for `this.props` inside `getDerivedStateFromProps()`.
      props: _this.props,
      // The country selected.
      country: preSelectedCountry,
      // `countries` are stored in `this.state` because they're filtered.
      // For example, a developer might theoretically pass some unsupported
      // countries as part of the `countries` property, and because of that
      // the component uses `this.state.countries` (which are filtered)
      // instead of `this.props.countries`
      // (which could potentially contain unsupported countries).
      countries: _countries,
      // `phoneDigits` state property holds non-formatted user's input.
      // The reason is that there's no way of finding out
      // in which form should `value` be displayed: international or national.
      // E.g. if `value` is `+78005553535` then it could be input
      // by a user both as `8 (800) 555-35-35` and `+7 800 555 35 35`.
      // Hence storing just `value` is not sufficient for correct formatting.
      // E.g. if a user entered `8 (800) 555-35-35`
      // then value is `+78005553535` and `phoneDigits` are `88005553535`
      // and if a user entered `+7 800 555 35 35`
      // then value is `+78005553535` and `phoneDigits` are `+78005553535`.
      phoneDigits: getInitialPhoneDigits({
        value: _value,
        phoneNumber: phoneNumber,
        defaultCountry: _defaultCountry,
        international: _international,
        useNationalFormat: displayInitialValueAsLocalNumber || initialValueFormat === 'national',
        metadata: _metadata
      }),
      // `value` property is duplicated in state.
      // The reason is that `getDerivedStateFromProps()`
      // needs this `value` to compare to the new `value` property
      // to find out if `phoneDigits` needs updating:
      // If the `value` property was changed externally
      // then it won't be equal to `state.value`
      // in which case `phoneDigits` and `country` should be updated.
      value: _value
    };
    return _this;
  }

  _createClass$1(PhoneNumberInput_, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var onCountryChange = this.props.onCountryChange;
      var defaultCountry = this.props.defaultCountry;
      var selectedCountry = this.state.country;

      if (onCountryChange) {
        if (defaultCountry) {
          if (!this.isCountrySupportedWithError(defaultCountry)) {
            defaultCountry = undefined;
          }
        }

        if (selectedCountry !== defaultCountry) {
          onCountryChange(selectedCountry);
        }
      }
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps, prevState) {
      var onCountryChange = this.props.onCountryChange;
      var country = this.state.country; // Call `onCountryChange` when user selects another country.

      if (onCountryChange && country !== prevState.country) {
        onCountryChange(country);
      }
    }
  }, {
    key: "getCountrySelectOptions",
    value: function getCountrySelectOptions$1(_ref) {
      var countries = _ref.countries;
      var _this$props5 = this.props,
          international = _this$props5.international,
          countryCallingCodeEditable = _this$props5.countryCallingCodeEditable,
          countryOptionsOrder = _this$props5.countryOptionsOrder,
          addInternationalOption = _this$props5.addInternationalOption,
          labels = _this$props5.labels,
          locales = _this$props5.locales,
          metadata = _this$props5.metadata;
      return useMemoCountrySelectOptions(function () {
        return sortCountryOptions(getCountrySelectOptions({
          countries: countries || getCountries(metadata),
          countryNames: labels,
          addInternationalOption: international && countryCallingCodeEditable === false ? false : addInternationalOption,
          compareStringsLocales: locales // compareStrings

        }), getSupportedCountryOptions(countryOptionsOrder, metadata));
      }, [countries, countryOptionsOrder, addInternationalOption, labels, metadata]);
    }
  }, {
    key: "getFirstSupportedCountry",
    value: function getFirstSupportedCountry(_ref2) {
      var countries = _ref2.countries;
      var countryOptions = this.getCountrySelectOptions({
        countries: countries
      });
      return countryOptions[0].value;
    } // A shorthand for not passing `metadata` as a second argument.

  }, {
    key: "render",
    value: function render() {
      var _this$props6 = this.props,
          name = _this$props6.name,
          disabled = _this$props6.disabled,
          autoComplete = _this$props6.autoComplete,
          style = _this$props6.style,
          className = _this$props6.className;
          _this$props6.inputRef;
          var inputComponent = _this$props6.inputComponent,
          numberInputProps = _this$props6.numberInputProps,
          smartCaret = _this$props6.smartCaret,
          CountrySelectComponent = _this$props6.countrySelectComponent,
          countrySelectProps = _this$props6.countrySelectProps,
          ContainerComponent = _this$props6.containerComponent;
          _this$props6.defaultCountry;
          _this$props6.countries;
          _this$props6.countryOptionsOrder;
          var labels = _this$props6.labels;
          _this$props6.flags;
          _this$props6.flagComponent;
          _this$props6.flagUrl;
          _this$props6.addInternationalOption;
          _this$props6.internationalIcon;
          _this$props6.displayInitialValueAsLocalNumber;
          _this$props6.initialValueFormat;
          _this$props6.onCountryChange;
          _this$props6.limitMaxLength;
          _this$props6.countryCallingCodeEditable;
          _this$props6.focusInputOnCountrySelection;
          _this$props6.reset;
          var metadata = _this$props6.metadata;
          _this$props6.international;
          _this$props6.locales;
          var rest = _objectWithoutProperties$1(_this$props6, ["name", "disabled", "autoComplete", "style", "className", "inputRef", "inputComponent", "numberInputProps", "smartCaret", "countrySelectComponent", "countrySelectProps", "containerComponent", "defaultCountry", "countries", "countryOptionsOrder", "labels", "flags", "flagComponent", "flagUrl", "addInternationalOption", "internationalIcon", "displayInitialValueAsLocalNumber", "initialValueFormat", "onCountryChange", "limitMaxLength", "countryCallingCodeEditable", "focusInputOnCountrySelection", "reset", "metadata", "international", "locales"]);

      var _this$state3 = this.state,
          country = _this$state3.country,
          countries = _this$state3.countries,
          phoneDigits = _this$state3.phoneDigits,
          isFocused = _this$state3.isFocused;
      var InputComponent = smartCaret ? InputSmart : InputBasic;
      var countrySelectOptions = this.getCountrySelectOptions({
        countries: countries
      });
      return React.createElement(ContainerComponent, {
        style: style,
        className: classnames(className, 'PhoneInput', {
          'PhoneInput--focus': isFocused
        })
      }, React.createElement(CountrySelectComponent, _extends$g({
        name: name ? "".concat(name, "Country") : undefined,
        "aria-label": labels.country
      }, countrySelectProps, {
        value: country,
        options: countrySelectOptions,
        onChange: this.onCountryChange,
        onFocus: this.onCountryFocus,
        onBlur: this.onCountryBlur,
        disabled: disabled || countrySelectProps && countrySelectProps.disabled,
        iconComponent: this.CountryIcon
      })), React.createElement(InputComponent, _extends$g({
        ref: this.setInputRef,
        type: "tel",
        autoComplete: autoComplete
      }, numberInputProps, rest, {
        name: name,
        metadata: metadata,
        country: country,
        value: phoneDigits || '',
        onChange: this.onChange,
        onFocus: this.onFocus,
        onBlur: this.onBlur,
        disabled: disabled,
        inputComponent: inputComponent,
        className: classnames('PhoneInputInput', numberInputProps && numberInputProps.className, rest.className)
      })));
    }
  }], [{
    key: "getDerivedStateFromProps",
    // `state` holds previous props as `props`, and also:
    // * `country` — The currently selected country, e.g. `"RU"`.
    // * `value` — The currently entered phone number (E.164), e.g. `+78005553535`.
    // * `phoneDigits` — The parsed `<input/>` value, e.g. `8005553535`.
    // (and a couple of other less significant properties)
    value: function getDerivedStateFromProps(props, state) {
      return _objectSpread({
        // Emulate `prevProps` via `state.props`.
        props: props
      }, getPhoneInputWithCountryStateUpdateFromNewProps(props, state.props, state));
    }
  }]);

  return PhoneNumberInput_;
}(React.PureComponent); // This wrapper is only to `.forwardRef()` to the `<input/>`.


var PhoneNumberInput$1 = React.forwardRef(function (props, ref) {
  return React.createElement(PhoneNumberInput_, _extends$g({}, props, {
    inputRef: ref
  }));
});
PhoneNumberInput$1.propTypes = {
  /**
   * Phone number in `E.164` format.
   *
   * Example:
   *
   * `"+12223333333"`
   */
  value: PropTypes.string,

  /**
   * A function of `value: string?`.
   *
   * Updates the `value` property as the user inputs a phone number.
   */
  onChange: PropTypes.func.isRequired,

  /**
   * Toggles the `--focus` CSS class.
   * @ignore
   */
  onFocus: PropTypes.func,

  /**
   * `onBlur` is usually passed by `redux-form`.
   * @ignore
   */
  onBlur: PropTypes.func,

  /**
   * Set to `true` to disable both the phone number `<input/>`
   * and the country `<select/>`.
   */
  disabled: PropTypes.bool,

  /**
   * Sets `autoComplete` property for phone number `<input/>`.
   *
   * Web browser's "autocomplete" feature
   * remembers the phone number being input
   * and can also autofill the `<input/>`
   * with previously remembered phone numbers.
   *
   * https://developers.google.com
   * /web/updates/2015/06/checkout-faster-with-autofill
   *
   * For example, can be used to turn it off:
   *
   * "So when should you use `autocomplete="off"`?
   *  One example is when you've implemented your own version
   *  of autocomplete for search. Another example is any form field
   *  where users will input and submit different kinds of information
   *  where it would not be useful to have the browser remember
   *  what was submitted previously".
   */
  // (is `"tel"` by default)
  autoComplete: PropTypes.string.isRequired,

  /**
   * Set to `"national"` to show the initial `value` in
   * "national" format rather than "international".
   *
   * For example, if `initialValueFormat` is `"national"`
   * and the initial `value="+12133734253"` is passed
   * then the `<input/>` value will be `"(213) 373-4253"`.
   *
   * By default, `initialValueFormat` is `undefined`,
   * meaning that if the initial `value="+12133734253"` is passed
   * then the `<input/>` value will be `"+1 213 373 4253"`.
   *
   * The reason for such default behaviour is that
   * the newer generation grows up when there are no stationary phones
   * and therefore everyone inputs phone numbers in international format
   * in their smartphones so people gradually get more accustomed to
   * writing phone numbers in international format rather than in local format.
   * Future people won't be using "national" format, only "international".
   */
  // (is `undefined` by default)
  initialValueFormat: PropTypes.oneOf(['national']),
  // `displayInitialValueAsLocalNumber` property has been
  // superceded by `initialValueFormat` property.
  displayInitialValueAsLocalNumber: PropTypes.bool,

  /**
   * The country to be selected by default.
   * For example, can be set after a GeoIP lookup.
   *
   * Example: `"US"`.
   */
  // A two-letter country code ("ISO 3166-1 alpha-2").
  defaultCountry: PropTypes.string,

  /**
   * If specified, only these countries will be available for selection.
   *
   * Example:
   *
   * `["RU", "UA", "KZ"]`
   */
  countries: PropTypes.arrayOf(PropTypes.string),

  /**
   * Custom country `<select/>` option names.
   * Also some labels like "ext" and country `<select/>` `aria-label`.
   *
   * Example:
   *
   * `{ "ZZ": "Международный", RU: "Россия", US: "США", ... }`
   *
   * See the `locales` directory for examples.
   */
  labels: labels$1.isRequired,

  /**
   * Country `<select/>` options are sorted by their labels.
   * The default sorting function uses `a.localeCompare(b, locales)`,
   * and, if that's not available, falls back to simple `a > b` / `a < b`.
   * Some languages, like Chinese, support multiple sorting variants
   * (called "collations"), and the user might prefer one or another.
   * Also, sometimes the Operating System language is not always
   * the preferred language for a person using a website or an application,
   * so there should be a way to specify custom locale.
   * This `locales` property mimicks the `locales` argument of `Intl` constructors,
   * and can be either a Unicode BCP 47 locale identifier or an array of such locale identifiers.
   * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument
   */
  locales: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),

  /*
   * Custom country `<select/>` options sorting function.
   * The default one uses `a.localeCompare(b)`, and,
   * if that's not available, falls back to simple `a > b`/`a < b`.
   * There have been requests to add custom sorter for cases
   * like Chinese language and "pinyin" (non-default) sorting order.
   * https://stackoverflow.com/questions/22907288/chinese-sorting-by-pinyin-in-javascript-with-localecompare
  compareStrings: PropTypes.func,
   */

  /**
   * A URL template of a country flag, where
   * "{XX}" is a two-letter country code in upper case,
   * or where "{xx}" is a two-letter country code in lower case.
   * By default it points to `country-flag-icons` gitlab pages website.
   * I imagine someone might want to download those country flag icons
   * and host them on their own servers instead
   * (all flags are available in the `country-flag-icons` library).
   * There's a catch though: new countries may be added in future,
   * so when hosting country flag icons on your own server
   * one should check the `CHANGELOG.md` every time before updating this library,
   * otherwise there's a possibility that some new country flag would be missing.
   */
  flagUrl: PropTypes.string.isRequired,

  /**
   * Custom country flag icon components.
   * These flags will be used instead of the default ones.
   * The the "Flags" section of the readme for more info.
   *
   * The shape is an object where keys are country codes
   * and values are flag icon components.
   * Flag icon components receive the same properties
   * as `flagComponent` (see below).
   *
   * Example:
   *
   * `{ "RU": (props) => <img src="..."/> }`
   *
   * Example:
   *
   * `import flags from 'country-flag-icons/react/3x2'`
   *
   * `import PhoneInput from 'react-phone-number-input'`
   *
   * `<PhoneInput flags={flags} .../>`
   */
  flags: PropTypes.objectOf(PropTypes.elementType),

  /**
   * Country flag icon component.
   *
   * Takes properties:
   *
   * * `country: string` — The country code.
   * * `countryName: string` — The country name.
   * * `flagUrl: string` — The `flagUrl` property (see above).
   * * `flags: object` — The `flags` property (see above).
   */
  flagComponent: PropTypes.elementType.isRequired,

  /**
   * Set to `false` to remove the "International" option from country `<select/>`.
   */
  addInternationalOption: PropTypes.bool.isRequired,

  /**
   * "International" icon component.
   * Should have the same aspect ratio.
   *
   * Receives properties:
   *
   * * `title: string` — "International" country option label.
   */
  internationalIcon: PropTypes.elementType.isRequired,

  /**
   * Can be used to place some countries on top of the list of country `<select/>` options.
   *
   * * `"XX"` — inserts an option for "XX" country.
   * * `"🌐"` — inserts "International" option.
   * * `"|"` — inserts a separator.
   * * `"..."` — inserts options for the rest of the countries (can be omitted, in which case it will be automatically added at the end).
   *
   * Example:
   *
   * `["US", "CA", "AU", "|", "..."]`
   */
  countryOptionsOrder: PropTypes.arrayOf(PropTypes.string),

  /**
   * `<Phone/>` component CSS style object.
   */
  style: PropTypes.object,

  /**
   * `<Phone/>` component CSS class.
   */
  className: PropTypes.string,

  /**
   * Country `<select/>` component.
   *
   * Receives properties:
   *
   * * `name: string?` — HTML `name` attribute.
   * * `value: string?` — The currently selected country code.
   * * `onChange(value: string?)` — Updates the `value`.
   * * `onFocus()` — Is used to toggle the `--focus` CSS class.
   * * `onBlur()` — Is used to toggle the `--focus` CSS class.
   * * `options: object[]` — The list of all selectable countries (including "International") each being an object of shape `{ value: string?, label: string }`.
   * * `iconComponent: PropTypes.elementType` — React component that renders a country icon: `<Icon country={value}/>`. If `country` is `undefined` then it renders an "International" icon.
   * * `disabled: boolean?` — HTML `disabled` attribute.
   * * `tabIndex: (number|string)?` — HTML `tabIndex` attribute.
   * * `className: string` — CSS class name.
   */
  countrySelectComponent: PropTypes.elementType.isRequired,

  /**
   * Country `<select/>` component props.
   * Along with the usual DOM properties such as `aria-label` and `tabIndex`,
   * some custom properties are supported, such as `arrowComponent` and `unicodeFlags`.
   */
  countrySelectProps: PropTypes.object,

  /**
   * Phone number `<input/>` component.
   *
   * Receives properties:
   *
   * * `value: string` — The formatted `value`.
   * * `onChange(event: Event)` — Updates the formatted `value` from `event.target.value`.
   * * `onFocus()` — Is used to toggle the `--focus` CSS class.
   * * `onBlur()` — Is used to toggle the `--focus` CSS class.
   * * Other properties like `type="tel"` or `autoComplete="tel"` that should be passed through to the DOM `<input/>`.
   *
   * Must also either use `React.forwardRef()` to "forward" `ref` to the `<input/>` or implement `.focus()` method.
   */
  inputComponent: PropTypes.elementType.isRequired,

  /**
   * Wrapping `<div/>` component.
   *
   * Receives properties:
   *
   * * `style: object` — A component CSS style object.
   * * `className: string` — Classes to attach to the component, typically changes when component focuses or blurs.
   */
  containerComponent: PropTypes.elementType.isRequired,

  /**
   * Phone number `<input/>` component props.
   */
  numberInputProps: PropTypes.object,

  /**
   * By default, the caret position is being "intelligently" managed
   * while a user inputs a phone number.
   * This "smart" caret behavior can be turned off
   * by passing `smartCaret={false}` property.
   * This is just an "escape hatch" for any possible caret position issues.
   */
  // Is `true` by default.
  smartCaret: PropTypes.bool.isRequired,

  /**
   * Set to `true` to force "international" phone number format.
   * Set to `false` to force "national" phone number format.
   * By default it's `undefined` meaning that it doesn't enforce any phone number format.
   */
  international: PropTypes.bool,

  /**
   * If set to `true`, the phone number input will get trimmed
   * if it exceeds the maximum length for the country.
   */
  limitMaxLength: PropTypes.bool.isRequired,

  /**
   * If set to `false`, and `international` is `true`, then
   * users won't be able to erase the "country calling part"
   * of a phone number in the `<input/>`.
   */
  countryCallingCodeEditable: PropTypes.bool.isRequired,

  /**
   * `libphonenumber-js` metadata.
   *
   * Can be used to pass custom `libphonenumber-js` metadata
   * to reduce the overall bundle size for those who compile "custom" metadata.
   */
  metadata: metadata.isRequired,

  /**
   * Is called every time the selected country changes:
   * either programmatically or when user selects it manually from the list.
   */
  // People have been asking for a way to get the selected country.
  // @see  https://github.com/catamphetamine/react-phone-number-input/issues/128
  // For some it's just a "business requirement".
  // I guess it's about gathering as much info on the user as a website can
  // without introducing any addional fields that would complicate the form
  // therefore reducing "conversion" (that's a marketing term).
  // Assuming that the phone number's country is the user's country
  // is not 100% correct but in most cases I guess it's valid.
  onCountryChange: PropTypes.func,

  /**
   * If set to `false`, will not focus the `<input/>` component
   * when the user selects a country from the list of countries.
   * This can be used to conform to the Web Content Accessibility Guidelines (WCAG).
   * Quote:
   * "On input: Changing the setting of any user interface component
   *  does not automatically cause a change of context unless the user
   *  has been advised of the behaviour before using the component."
   */
  focusInputOnCountrySelection: PropTypes.bool.isRequired
};
PhoneNumberInput$1.defaultProps = {
  /**
   * Remember (and autofill) the value as a phone number.
   */
  autoComplete: 'tel',

  /**
   * Country `<select/>` component.
   */
  countrySelectComponent: CountrySelectWithIcon,

  /**
   * Flag icon component.
   */
  flagComponent: FlagComponent,

  /**
   * By default, uses icons from `country-flag-icons` gitlab pages website.
   */
  // Must be equal to `flagUrl` in `./CountryIcon.js`.
  flagUrl: 'https://purecatamphetamine.github.io/country-flag-icons/3x2/{XX}.svg',

  /**
   * Default "International" country `<select/>` option icon.
   */
  internationalIcon: InternationalIcon,

  /**
   * Phone number `<input/>` component.
   */
  inputComponent: 'input',

  /**
   * Wrapping `<div/>` component.
   */
  containerComponent: 'div',

  /**
   * Some users requested a way to reset the component:
   * both number `<input/>` and country `<select/>`.
   * Whenever `reset` property changes both number `<input/>`
   * and country `<select/>` are reset.
   * It's not implemented as some instance `.reset()` method
   * because `ref` is forwarded to `<input/>`.
   * It's also not replaced with just resetting `country` on
   * external `value` reset, because a user could select a country
   * and then not input any `value`, and so the selected country
   * would be "stuck", if not using this `reset` property.
   */
  // https://github.com/catamphetamine/react-phone-number-input/issues/300
  reset: PropTypes.any,

  /**
   *
   */

  /**
   * Set to `false` to use "basic" caret instead of the "smart" one.
   */
  smartCaret: true,

  /**
   * Whether to add the "International" option
   * to the list of countries.
   */
  addInternationalOption: true,

  /**
   * If set to `true` the phone number input will get trimmed
   * if it exceeds the maximum length for the country.
   */
  limitMaxLength: false,

  /**
   * If set to `false`, and `international` is `true`, then
   * users won't be able to erase the "country calling part"
   * of a phone number in the `<input/>`.
   */
  countryCallingCodeEditable: true,

  /**
   * If set to `false`, will not focus the `<input/>` component
   * when the user selects a country from the list of countries.
   * This can be used to conform to the Web Content Accessibility Guidelines (WCAG).
   * Quote:
   * "On input: Changing the setting of any user interface component
   *  does not automatically cause a change of context unless the user
   *  has been advised of the behaviour before using the component."
   */
  focusInputOnCountrySelection: true
};
var PhoneInput = PhoneNumberInput$1;
var countrySelectOptionsMemo;
var countrySelectOptionsMemoDependencies;

function useMemoCountrySelectOptions(generator, dependencies) {
  if (!countrySelectOptionsMemoDependencies || !areEqualArrays(dependencies, countrySelectOptionsMemoDependencies)) {
    countrySelectOptionsMemo = generator();
    countrySelectOptionsMemoDependencies = dependencies;
  }

  return countrySelectOptionsMemo;
}

function areEqualArrays(a, b) {
  if (a.length !== b.length) {
    return false;
  }

  var i = 0;

  while (i < a.length) {
    if (a[i] !== b[i]) {
      return false;
    }

    i++;
  }

  return true;
}

function _typeof$1(obj) { if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$1 = function _typeof(obj) { return typeof obj; }; } else { _typeof$1 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$1(obj); }
/**
 * Formats a phone number.
 * Is a proxy for `libphonenumber-js`'s `.format()` function of a parsed `PhoneNumber`.
 * @param  {string} value
 * @param  {string} [format]
 * @param  {object} metadata
 * @return {string}
 */

function formatPhoneNumber$1(value, format, metadata) {
  if (!metadata) {
    if (_typeof$1(format) === 'object') {
      metadata = format;
      format = 'NATIONAL';
    }
  }

  if (!value) {
    return '';
  }

  var phoneNumber = parsePhoneNumberFromString(value, metadata);

  if (!phoneNumber) {
    return '';
  } // Deprecated.
  // Legacy `format`s.


  switch (format) {
    case 'National':
      format = 'NATIONAL';
      break;

    case 'International':
      format = 'INTERNATIONAL';
      break;
  }

  return phoneNumber.format(format);
}
function formatPhoneNumberIntl$1(value, metadata) {
  return formatPhoneNumber$1(value, 'INTERNATIONAL', metadata);
}

var ext = "ext.";
var country = "Phone number country";
var phone = "Phone";
var AB = "Abkhazia";
var AC = "Ascension Island";
var AD = "Andorra";
var AE = "United Arab Emirates";
var AF = "Afghanistan";
var AG = "Antigua and Barbuda";
var AI = "Anguilla";
var AL = "Albania";
var AM = "Armenia";
var AO = "Angola";
var AQ = "Antarctica";
var AR = "Argentina";
var AS = "American Samoa";
var AT = "Austria";
var AU = "Australia";
var AW = "Aruba";
var AX = "Åland Islands";
var AZ = "Azerbaijan";
var BA = "Bosnia and Herzegovina";
var BB = "Barbados";
var BD = "Bangladesh";
var BE = "Belgium";
var BF = "Burkina Faso";
var BG = "Bulgaria";
var BH = "Bahrain";
var BI = "Burundi";
var BJ = "Benin";
var BL = "Saint Barthélemy";
var BM = "Bermuda";
var BN = "Brunei Darussalam";
var BO = "Bolivia";
var BQ = "Bonaire, Sint Eustatius and Saba";
var BR = "Brazil";
var BS = "Bahamas";
var BT = "Bhutan";
var BV = "Bouvet Island";
var BW = "Botswana";
var BY = "Belarus";
var BZ = "Belize";
var CA = "Canada";
var CC = "Cocos (Keeling) Islands";
var CD = "Congo, Democratic Republic of the";
var CF = "Central African Republic";
var CG = "Congo";
var CH = "Switzerland";
var CI = "Cote d'Ivoire";
var CK = "Cook Islands";
var CL = "Chile";
var CM = "Cameroon";
var CN = "China";
var CO = "Colombia";
var CR = "Costa Rica";
var CU = "Cuba";
var CV = "Cape Verde";
var CW = "Curaçao";
var CX = "Christmas Island";
var CY = "Cyprus";
var CZ = "Czech Republic";
var DE = "Germany";
var DJ = "Djibouti";
var DK = "Denmark";
var DM = "Dominica";
var DO = "Dominican Republic";
var DZ = "Algeria";
var EC = "Ecuador";
var EE = "Estonia";
var EG = "Egypt";
var EH = "Western Sahara";
var ER = "Eritrea";
var ES = "Spain";
var ET = "Ethiopia";
var FI = "Finland";
var FJ = "Fiji";
var FK = "Falkland Islands";
var FM = "Federated States of Micronesia";
var FO = "Faroe Islands";
var FR = "France";
var GA = "Gabon";
var GB = "United Kingdom";
var GD = "Grenada";
var GE = "Georgia";
var GF = "French Guiana";
var GG = "Guernsey";
var GH = "Ghana";
var GI = "Gibraltar";
var GL = "Greenland";
var GM = "Gambia";
var GN = "Guinea";
var GP = "Guadeloupe";
var GQ = "Equatorial Guinea";
var GR = "Greece";
var GS = "South Georgia and the South Sandwich Islands";
var GT = "Guatemala";
var GU = "Guam";
var GW = "Guinea-Bissau";
var GY = "Guyana";
var HK = "Hong Kong";
var HM = "Heard Island and McDonald Islands";
var HN = "Honduras";
var HR = "Croatia";
var HT = "Haiti";
var HU = "Hungary";
var ID = "Indonesia";
var IE = "Ireland";
var IL = "Israel";
var IM = "Isle of Man";
var IN = "India";
var IO = "British Indian Ocean Territory";
var IQ = "Iraq";
var IR = "Iran";
var IS = "Iceland";
var IT = "Italy";
var JE = "Jersey";
var JM = "Jamaica";
var JO = "Jordan";
var JP = "Japan";
var KE = "Kenya";
var KG = "Kyrgyzstan";
var KH = "Cambodia";
var KI = "Kiribati";
var KM = "Comoros";
var KN = "Saint Kitts and Nevis";
var KP = "North Korea";
var KR = "South Korea";
var KW = "Kuwait";
var KY = "Cayman Islands";
var KZ = "Kazakhstan";
var LA = "Laos";
var LB = "Lebanon";
var LC = "Saint Lucia";
var LI = "Liechtenstein";
var LK = "Sri Lanka";
var LR = "Liberia";
var LS = "Lesotho";
var LT = "Lithuania";
var LU = "Luxembourg";
var LV = "Latvia";
var LY = "Libya";
var MA = "Morocco";
var MC = "Monaco";
var MD = "Moldova";
var ME = "Montenegro";
var MF = "Saint Martin (French Part)";
var MG = "Madagascar";
var MH = "Marshall Islands";
var MK = "North Macedonia";
var ML = "Mali";
var MM = "Burma";
var MN = "Mongolia";
var MO = "Macao";
var MP = "Northern Mariana Islands";
var MQ = "Martinique";
var MR = "Mauritania";
var MS = "Montserrat";
var MT = "Malta";
var MU = "Mauritius";
var MV = "Maldives";
var MW = "Malawi";
var MX = "Mexico";
var MY = "Malaysia";
var MZ = "Mozambique";
var NA = "Namibia";
var NC = "New Caledonia";
var NE = "Niger";
var NF = "Norfolk Island";
var NG = "Nigeria";
var NI = "Nicaragua";
var NL = "Netherlands";
var NO = "Norway";
var NP = "Nepal";
var NR = "Nauru";
var NU = "Niue";
var NZ = "New Zealand";
var OM = "Oman";
var OS = "South Ossetia";
var PA = "Panama";
var PE = "Peru";
var PF = "French Polynesia";
var PG = "Papua New Guinea";
var PH = "Philippines";
var PK = "Pakistan";
var PL = "Poland";
var PM = "Saint Pierre and Miquelon";
var PN = "Pitcairn";
var PR = "Puerto Rico";
var PS = "Palestine";
var PT = "Portugal";
var PW = "Palau";
var PY = "Paraguay";
var QA = "Qatar";
var RE = "Reunion";
var RO = "Romania";
var RS = "Serbia";
var RU = "Russia";
var RW = "Rwanda";
var SA = "Saudi Arabia";
var SB = "Solomon Islands";
var SC = "Seychelles";
var SD = "Sudan";
var SE = "Sweden";
var SG = "Singapore";
var SH = "Saint Helena";
var SI = "Slovenia";
var SJ = "Svalbard and Jan Mayen";
var SK = "Slovakia";
var SL = "Sierra Leone";
var SM = "San Marino";
var SN = "Senegal";
var SO = "Somalia";
var SR = "Suriname";
var SS = "South Sudan";
var ST = "Sao Tome and Principe";
var SV = "El Salvador";
var SX = "Sint Maarten";
var SY = "Syria";
var SZ = "Swaziland";
var TA = "Tristan da Cunha";
var TC = "Turks and Caicos Islands";
var TD = "Chad";
var TF = "French Southern Territories";
var TG = "Togo";
var TH = "Thailand";
var TJ = "Tajikistan";
var TK = "Tokelau";
var TL = "Timor-Leste";
var TM = "Turkmenistan";
var TN = "Tunisia";
var TO = "Tonga";
var TR = "Turkey";
var TT = "Trinidad and Tobago";
var TV = "Tuvalu";
var TW = "Taiwan";
var TZ = "Tanzania";
var UA = "Ukraine";
var UG = "Uganda";
var UM = "United States Minor Outlying Islands";
var US = "United States";
var UY = "Uruguay";
var UZ = "Uzbekistan";
var VA = "Holy See (Vatican City State)";
var VC = "Saint Vincent and the Grenadines";
var VE = "Venezuela";
var VG = "Virgin Islands, British";
var VI = "Virgin Islands, U.S.";
var VN = "Vietnam";
var VU = "Vanuatu";
var WF = "Wallis and Futuna";
var WS = "Samoa";
var XK = "Kosovo";
var YE = "Yemen";
var YT = "Mayotte";
var ZA = "South Africa";
var ZM = "Zambia";
var ZW = "Zimbabwe";
var ZZ = "International";
var labels = {
	ext: ext,
	country: country,
	phone: phone,
	AB: AB,
	AC: AC,
	AD: AD,
	AE: AE,
	AF: AF,
	AG: AG,
	AI: AI,
	AL: AL,
	AM: AM,
	AO: AO,
	AQ: AQ,
	AR: AR,
	AS: AS,
	AT: AT,
	AU: AU,
	AW: AW,
	AX: AX,
	AZ: AZ,
	BA: BA,
	BB: BB,
	BD: BD,
	BE: BE,
	BF: BF,
	BG: BG,
	BH: BH,
	BI: BI,
	BJ: BJ,
	BL: BL,
	BM: BM,
	BN: BN,
	BO: BO,
	BQ: BQ,
	BR: BR,
	BS: BS,
	BT: BT,
	BV: BV,
	BW: BW,
	BY: BY,
	BZ: BZ,
	CA: CA,
	CC: CC,
	CD: CD,
	CF: CF,
	CG: CG,
	CH: CH,
	CI: CI,
	CK: CK,
	CL: CL,
	CM: CM,
	CN: CN,
	CO: CO,
	CR: CR,
	CU: CU,
	CV: CV,
	CW: CW,
	CX: CX,
	CY: CY,
	CZ: CZ,
	DE: DE,
	DJ: DJ,
	DK: DK,
	DM: DM,
	DO: DO,
	DZ: DZ,
	EC: EC,
	EE: EE,
	EG: EG,
	EH: EH,
	ER: ER,
	ES: ES,
	ET: ET,
	FI: FI,
	FJ: FJ,
	FK: FK,
	FM: FM,
	FO: FO,
	FR: FR,
	GA: GA,
	GB: GB,
	GD: GD,
	GE: GE,
	GF: GF,
	GG: GG,
	GH: GH,
	GI: GI,
	GL: GL,
	GM: GM,
	GN: GN,
	GP: GP,
	GQ: GQ,
	GR: GR,
	GS: GS,
	GT: GT,
	GU: GU,
	GW: GW,
	GY: GY,
	HK: HK,
	HM: HM,
	HN: HN,
	HR: HR,
	HT: HT,
	HU: HU,
	ID: ID,
	IE: IE,
	IL: IL,
	IM: IM,
	IN: IN,
	IO: IO,
	IQ: IQ,
	IR: IR,
	IS: IS,
	IT: IT,
	JE: JE,
	JM: JM,
	JO: JO,
	JP: JP,
	KE: KE,
	KG: KG,
	KH: KH,
	KI: KI,
	KM: KM,
	KN: KN,
	KP: KP,
	KR: KR,
	KW: KW,
	KY: KY,
	KZ: KZ,
	LA: LA,
	LB: LB,
	LC: LC,
	LI: LI,
	LK: LK,
	LR: LR,
	LS: LS,
	LT: LT,
	LU: LU,
	LV: LV,
	LY: LY,
	MA: MA,
	MC: MC,
	MD: MD,
	ME: ME,
	MF: MF,
	MG: MG,
	MH: MH,
	MK: MK,
	ML: ML,
	MM: MM,
	MN: MN,
	MO: MO,
	MP: MP,
	MQ: MQ,
	MR: MR,
	MS: MS,
	MT: MT,
	MU: MU,
	MV: MV,
	MW: MW,
	MX: MX,
	MY: MY,
	MZ: MZ,
	NA: NA,
	NC: NC,
	NE: NE,
	NF: NF,
	NG: NG,
	NI: NI,
	NL: NL,
	NO: NO,
	NP: NP,
	NR: NR,
	NU: NU,
	NZ: NZ,
	OM: OM,
	OS: OS,
	PA: PA,
	PE: PE,
	PF: PF,
	PG: PG,
	PH: PH,
	PK: PK,
	PL: PL,
	PM: PM,
	PN: PN,
	PR: PR,
	PS: PS,
	PT: PT,
	PW: PW,
	PY: PY,
	QA: QA,
	RE: RE,
	RO: RO,
	RS: RS,
	RU: RU,
	RW: RW,
	SA: SA,
	SB: SB,
	SC: SC,
	SD: SD,
	SE: SE,
	SG: SG,
	SH: SH,
	SI: SI,
	SJ: SJ,
	SK: SK,
	SL: SL,
	SM: SM,
	SN: SN,
	SO: SO,
	SR: SR,
	SS: SS,
	ST: ST,
	SV: SV,
	SX: SX,
	SY: SY,
	SZ: SZ,
	TA: TA,
	TC: TC,
	TD: TD,
	TF: TF,
	TG: TG,
	TH: TH,
	TJ: TJ,
	TK: TK,
	TL: TL,
	TM: TM,
	TN: TN,
	TO: TO,
	TR: TR,
	TT: TT,
	TV: TV,
	TW: TW,
	TZ: TZ,
	UA: UA,
	UG: UG,
	UM: UM,
	US: US,
	UY: UY,
	UZ: UZ,
	VA: VA,
	VC: VC,
	VE: VE,
	VG: VG,
	VI: VI,
	VN: VN,
	VU: VU,
	WF: WF,
	WS: WS,
	XK: XK,
	YE: YE,
	YT: YT,
	ZA: ZA,
	ZM: ZM,
	ZW: ZW,
	ZZ: ZZ
};

function _extends$f() { _extends$f = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$f.apply(this, arguments); }
function createPhoneInput(defaultMetadata) {
  var PhoneInputDefault = React.forwardRef(function (props, ref) {
    return React.createElement(PhoneInput, _extends$f({
      ref: ref
    }, props));
  });
  PhoneInputDefault.propTypes = {
    metadata: metadata.isRequired,
    labels: labels$1.isRequired
  };
  PhoneInputDefault.defaultProps = {
    metadata: defaultMetadata,
    labels: labels
  };
  return PhoneInputDefault;
}
createPhoneInput();

function call(func, _arguments) {
	var args = Array.prototype.slice.call(_arguments);
	args.push(metadata$1);
	return func.apply(this, args)
}

createPhoneInput(metadata$1);

function formatPhoneNumber() {
	return call(formatPhoneNumber$1, arguments)
}

function formatPhoneNumberIntl() {
	return call(formatPhoneNumberIntl$1, arguments)
}

function isValidPhoneNumber() {
	return call(isValidPhoneNumber$1, arguments)
}

function isPossiblePhoneNumber() {
	return call(isPossiblePhoneNumber$1, arguments)
}

function getCountryCallingCode() {
	return call(getCountryCallingCode$1, arguments)
}

const PhoneNumberInputStyled = styled.div`
  width: 100%;

  .PhoneNumberInput {
    &__input {
      border-color: #d9d9d9;

      &:focus-within {
        border-color: var(--palette-primary, #4611cf);
      }
    }

    &__label {
      display: block;
      padding-bottom: 4px;
      font-size: 12px;
      color: #11111180;
    }

    &__select-and-input {
      display: flex;
    }

    &__select {
      .SelectCountry {
        &__flag {
          display: flex;
          align-items: center;
          height: 100%;
        }

        &__select {
          background-color: transparent;
          height: 100%;
          font-size: 16px;
        }
      }

      &:hover {
        border-color: var(--palette-primary, #4611cf);
      }

      &:focus-within {
        border-color: var(--palette-primary, #4611cf);
      }
    }

    &__input {
      width: 100%;
      flex-grow: 1;
      padding-left: 8px;
      padding-right: 8px;
      border-right: 1px solid #d9d9d9;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      border-top: 1px solid #d9d9d9;
      border-bottom: 1px solid #d9d9d9;
      border-left: none;
      font-family: inherit;
      font-size: 16px;
      line-height: 2.4;
      background-color: transparent;
      /* Remove input arrows in Firefox */
      -moz-appearance: textfield;

      /* Remove input arrows in Chrome, Safari, Edge, Opera */
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &:disabled {
        cursor: default;
        color: #d9d9d9;
      }

      &:hover {
        border-color: var(--palette-primary, #4611cf);
        border-width: 1px;
        border-style: solid;
        margin-left: -1px;
        z-index: 1;
      }

      &:focus {
        border-color: var(--palette-primary, #4611cf);
        outline: none;
        border-width: 1px;
        border-style: solid;
        margin-left: -1px;
        z-index: 1;
      }
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

const SelectCountryStyled = styled.div`
  width: max-content;
  display: flex;
  align-items: center;
  align-self: stretch;
  padding: 0 8px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  border: 1px solid #d9d9d9;
  isolation: isolate;

  .SelectCountry {
    &__select {
      height: 100%;
      padding-left: 4px;
      font-family: inherit;
      border: none;
      cursor: pointer;
      z-index: 1;

      &:focus-visible {
        outline: none;
      }

      &:disabled {
        cursor: default;
        color: #d9d9d9;
      }
    }

    &__option {
    }

    &__flag {
      height: 32px;

      & .Flag {
        width: 30px;
        height: 30px;
      }
    }
  }
`;

function _extends$e() { _extends$e = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$e.apply(this, arguments); }
var ArgSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$e({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#75AADB",
  d: "M36 27a4 4 0 01-4 4H4a4 4 0 01-4-4V9a4 4 0 014-4h28a4 4 0 014 4v18z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#EEE",
  d: "M0 13h36v10H0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FCBF49",
  d: "M18 13l.488 2.548 1.426-2.167-.525 2.54 2.146-1.457-1.457 2.147 2.541-.524-2.167 1.425L23 18l-2.548.488 2.167 1.426-2.541-.525 1.457 2.146-2.146-1.457.525 2.541-1.426-2.167L18 23l-.488-2.548-1.425 2.167.524-2.541-2.147 1.457 1.457-2.146-2.54.525 2.167-1.426L13 18l2.548-.488-2.167-1.425 2.54.524-1.457-2.147 2.147 1.457-.524-2.54 1.425 2.167z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#843511",
  d: "M18 14.33l.242 1.265.116.605.339-.514.708-1.076-.261 1.261-.125.604.51-.346 1.066-.723-.723 1.066-.346.51.603-.125 1.262-.26-1.076.708-.515.337.605.116L21.67 18l-1.265.242-.605.116.514.339 1.076.708-1.262-.261-.604-.125.346.51.723 1.065-1.065-.723-.51-.346.125.604.261 1.262-.708-1.076-.338-.515-.116.605L18 21.67l-.242-1.265-.116-.605-.339.515-.708 1.076.26-1.262.125-.603-.51.346-1.066.723.723-1.066.346-.51-.604.125-1.261.261 1.076-.708.514-.339-.605-.116L14.33 18l1.265-.242.605-.116-.515-.339-1.076-.708 1.261.26.603.125-.346-.51-.724-1.066 1.066.724.51.346-.125-.603-.26-1.261.708 1.076.339.515.116-.605L18 14.33M18 13l-.488 2.548-1.425-2.167.524 2.541-2.147-1.457 1.457 2.147-2.54-.524 2.167 1.425L13 18l2.548.488-2.167 1.426 2.54-.525-1.457 2.146 2.147-1.457-.524 2.541 1.425-2.167L18 23l.488-2.548 1.426 2.167-.525-2.541 2.146 1.457-1.457-2.146 2.541.525-2.167-1.426L23 18l-2.548-.488 2.167-1.425-2.541.524 1.457-2.147-2.146 1.457.525-2.541-1.426 2.167L18 13zm1.914.381h.005-.005zm1.621 1.083h.005-.005zm1.084 1.623h.005-.005z"
}), /*#__PURE__*/React.createElement("circle", {
  fill: "#FCBF49",
  cx: "18",
  cy: "18",
  r: "2"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#843511",
  d: "M18 20.125c-1.172 0-2.125-.953-2.125-2.125s.953-2.125 2.125-2.125 2.125.953 2.125 2.125-.953 2.125-2.125 2.125zm0-4c-1.034 0-1.875.841-1.875 1.875s.841 1.875 1.875 1.875 1.875-.841 1.875-1.875-.841-1.875-1.875-1.875z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#C16540",
  d: "M17.801 17.774c0 .155-.261.28-.583.28-.323 0-.584-.125-.584-.28 0-.155.261-.28.584-.28.322 0 .583.125.583.28zm1.553-.024c0-.161-.266-.292-.594-.292-.328 0-.594.13-.594.292s.266.292.594.292c.329 0 .594-.131.594-.292z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#ED8662",
  d: "M17.463 18.874c0-.126.246-.229.548-.229.303 0 .548.102.548.229 0 .126-.246.229-.548.229-.303 0-.548-.103-.548-.229z"
})));

function _extends$d() { _extends$d = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$d.apply(this, arguments); }
var BolSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$d({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#007934",
  d: "M36 27a4 4 0 01-4 4H4a4 4 0 01-4-4V9a4 4 0 014-4h28a4 4 0 014 4v18z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFE000",
  d: "M0 13h36v10H0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#D52B1E",
  d: "M32 5H4a4 4 0 00-4 4v4h36V9a4 4 0 00-4-4z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFF",
  d: "M12.894 16.335c.041.062.381.55.381.55h.489l-.2-.275-.67-.275zm.733-.488c.041.061.381.55.381.55h.489l-.2-.275-.67-.275zm.682-.614c.019.071.185.643.185.643l.462.157-.1-.324-.547-.476z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#A05A2D",
  d: "M13.953 21.498a.08.08 0 01-.109-.036.08.08 0 01.035-.109l2.467-1.26a.08.08 0 01.109.034.082.082 0 01-.035.11l-2.467 1.261zm.494.456a.083.083 0 01-.113-.024.082.082 0 01.023-.113l2.31-1.527a.08.08 0 11.09.134l-2.31 1.53z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#A05A2D",
  d: "M14.947 22.377a.083.083 0 01-.115.008.082.082 0 01-.007-.116l1.831-2.078a.082.082 0 01.115-.007.08.08 0 01.007.114l-1.831 2.079z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#A05A2D",
  d: "M17.348 20.715l-.57 1.181-.102-.104-.222.307-.603-.328.749-1.464z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#D52B1E",
  d: "M17.593 22.525L18 20.592l-.042-1.691-2.931-2.8-.204.642-.407-.326-.122.672s-.51-.366-.53-.305c-.02.061-.143 1.548-.184 2.383-.04.834-.074 2.076.835 2.24 1.588.285 2.769-.896 2.769-.896s.387.367.407.428-.143.773-.204 1.059c-.059.283.206.527.206.527z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFE000",
  d: "M15.555 16.644l-.162.465-.265-.143s-.675 3.401.426 3.666c1.101.265 1.224-.184 1.224-.184l-.53-.712-.397.183s-.193-.526-.193-1.24c0-.637.102-1.265.102-1.265l.202.142.042-.468-.449-.444z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#007934",
  d: "M16.676 17.659l-.672-.571-.042.468-.263-.183s-.146.489-.144 1.12c.002.631.083 1.243.083 1.243s.468.162.712 0c.245-.163.326-2.077.326-2.077z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#E8A30F",
  d: "M16.454 19.573c-.529.227-1.611.635-1.326 1.142.286.508 1.12.305 1.65-.267.53-.57-.324-.875-.324-.875z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFF",
  d: "M23.105 16.335c-.04.062-.381.55-.381.55h-.489l.199-.275.671-.275zm-.733-.488c-.04.061-.38.55-.38.55h-.489l.199-.275.67-.275zm-.681-.614l-.185.643-.463.157.101-.324.547-.476z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#A05A2D",
  d: "M22.047 21.498a.08.08 0 00.109-.036.08.08 0 00-.035-.109l-2.466-1.26a.082.082 0 00-.11.034.08.08 0 00.036.11l2.466 1.261zm-.494.456a.083.083 0 00.113-.024.081.081 0 00-.023-.113l-2.309-1.527a.08.08 0 00-.112.022.08.08 0 00.022.112l2.309 1.53z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#A05A2D",
  d: "M21.054 22.377a.082.082 0 00.114.008.083.083 0 00.008-.116l-1.831-2.078c-.028-.034-.08-.036-.114-.007a.08.08 0 00-.008.114l1.831 2.079z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#A05A2D",
  d: "M18.652 20.715l.57 1.181.102-.104.222.307.603-.328-.749-1.464z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#D52B1E",
  d: "M18.407 22.525L18 20.592l.042-1.691 2.931-2.8.205.642.407-.326.122.672s.508-.366.528-.305c.021.061.144 1.548.184 2.383.041.834.075 2.076-.834 2.24-1.589.285-2.77-.896-2.77-.896s-.387.367-.407.428.143.773.204 1.059c.06.283-.205.527-.205.527z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFE000",
  d: "M20.445 16.644l.161.465.265-.143s.676 3.401-.426 3.666c-1.101.265-1.224-.184-1.224-.184l.529-.712.398.183s.192-.526.192-1.24c0-.637-.102-1.265-.102-1.265l-.202.142-.042-.468.451-.444z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#007934",
  d: "M19.324 17.659l.672-.571.042.468.263-.183s.146.489.144 1.12c-.002.631-.083 1.243-.083 1.243s-.469.162-.713 0c-.245-.163-.325-2.077-.325-2.077z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#E8A30F",
  d: "M19.546 19.573c.529.227 1.61.635 1.325 1.142-.285.508-1.12.305-1.649-.267-.53-.57.324-.875.324-.875z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#D52B1E",
  d: "M16.331 15.825c.171.202.163.489-.018.642-.18.153-.464.113-.635-.089-.17-.202-.163-.489.017-.642.18-.153.465-.113.636.089z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#E7E7E7",
  d: "M19.75 15.825c-.17.202-.162.489.019.642.181.153.464.113.636-.089.171-.202.163-.489-.018-.642-.181-.153-.465-.113-.637.089z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#007934",
  d: "M19.1 13.484c-.143.102.265.753.184 1.466-.083.713-1.284 1.283-1.284 1.283s-1.202-.57-1.283-1.283c-.082-.712.326-1.364.183-1.466-.143-.102-.896.55-.936 1.242-.041.692.66 1.735 1.12 1.935.46.2.917.305.917.305s.456-.106.917-.305c.461-.2 1.162-1.243 1.12-1.935-.042-.692-.796-1.344-.938-1.242z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFF",
  d: "M18 16.213s.122-.428.917-.672c.794-.245 1.547-.428 1.833-.448.286-.021-1.13-.57-1.466-.611-.336-.04-.753.041-.815.286-.061.244-.163.163-.468.204-.306.04-.509-.245-.652-.448-.142-.204-.683-.102-.947-.081-.265.02-1.273.651-1.273.651s1.752.285 2.118.468c.366.184.02.835-.081 1.019-.103.182.605-.057.834-.368z"
}), /*#__PURE__*/React.createElement("path", {
  d: "M18 16.213s.122-.428.917-.672c.794-.245 1.547-.428 1.833-.448.286-.021-1.038-.428-1.375-.469-.336-.04-.6-.04-.661.204-.061.244-.407.102-.713.143-.306.04-.652-.061-.794-.265-.143-.204-.387-.122-.652-.102-.264.02-1.425.489-1.425.489s1.752.285 2.118.468c.366.184.02.835-.081 1.019-.104.183.604-.056.833-.367z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#00A6DE",
  d: "M15.821 18.656c0-1.316.976-2.382 2.179-2.382 1.204 0 2.179 1.067 2.179 2.382 0 1.316-.975 2.383-2.179 2.383-1.203 0-2.179-1.066-2.179-2.383z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#E8A30F",
  d: "M16.401 18.656c0-1.034.716-1.873 1.599-1.873s1.599.839 1.599 1.873c0 1.036-.716 1.874-1.599 1.874s-1.599-.838-1.599-1.874z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#D52B1E",
  d: "M17.308 17.842a.692.692 0 111.384 0 .692.692 0 01-1.384 0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#007934",
  d: "M18 20.53c.849 0 1.544-.775 1.596-1.755-.521-.193-1.382-.933-1.596-.933-.224 0-.509.245-.875.57-.188.168-.48.303-.718.395.065.964.753 1.723 1.593 1.723z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#E8A30F",
  d: "M18.204 15.133c0 .102-.21.184-.468.184-.259 0-.469-.082-.469-.184 0-.101.21-.183.469-.183.258 0 .468.083.468.183z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFF",
  d: "M17.185 19.573h.489l.142-.407.184.223-.204.591h-.448z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#B37D0A",
  d: "M18.427 19.919c0 .191-.095.347-.213.347S18 20.111 18 19.919c0-.191.096-.346.214-.346s.213.155.213.346zm.306.347a.04.04 0 01-.041.041.04.04 0 01-.041-.041v-1.059c0-.022.018-.041.041-.041.022 0 .041.019.041.041v1.059z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#32B266",
  d: "M19.038 19.104c0 .18-.159.326-.356.326-.197 0-.356-.146-.356-.326 0-.18.16-.326.356-.326.197.001.356.146.356.326z"
})));

function _extends$c() { _extends$c = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$c.apply(this, arguments); }
var BraSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$c({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#009B3A",
  d: "M36 27a4 4 0 01-4 4H4a4 4 0 01-4-4V9a4 4 0 014-4h28a4 4 0 014 4v18z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FEDF01",
  d: "M32.728 18L18 29.124 3.272 18 18 6.875z"
}), /*#__PURE__*/React.createElement("circle", {
  fill: "#002776",
  cx: "17.976",
  cy: "17.924",
  r: "6.458"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#CBE9D4",
  d: "M12.277 14.887a6.406 6.406 0 00-.672 2.023c3.995-.29 9.417 1.891 11.744 4.595.402-.604.7-1.28.883-2.004-2.872-2.808-7.917-4.63-11.955-4.614z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#88C9F9",
  d: "M12 18.233h1v1h-1zm1 2h1v1h-1z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#55ACEE",
  d: "M15 18.233h1v1h-1zm2 1h1v1h-1zm4 2h1v1h-1zm-3 1h1v1h-1zm3-6h1v1h-1z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#3B88C3",
  d: "M19 20.233h1v1h-1z"
})));

function _extends$b() { _extends$b = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b.apply(this, arguments); }
var ChlSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$b({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#1F429B",
  d: "M13 5H4a4 4 0 00-4 4v9h13V5zm-4.663 9.292l-1.882-1.367-1.882 1.367.719-2.212-1.882-1.368h2.326L6.455 8.5l.719 2.212H9.5L7.618 12.08l.719 2.212z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#EEE",
  d: "M32 5H13v13h23V9a4 4 0 00-4-4z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#D42D27",
  d: "M0 18v9a4 4 0 004 4h28a4 4 0 004-4v-9H0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFF",
  d: "M7.174 10.712L6.455 8.5l-.719 2.212H3.41l1.882 1.368-.719 2.212 1.882-1.367 1.882 1.367-.719-2.212L9.5 10.712z"
})));

function _extends$a() { _extends$a = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a.apply(this, arguments); }
var ColSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$a({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#FBD116",
  d: "M32 5H4a4 4 0 00-4 4v9h36V9a4 4 0 00-4-4z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#22408C",
  d: "M0 18h36v7H0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#CE2028",
  d: "M0 27a4 4 0 004 4h28a4 4 0 004-4v-2H0v2z"
})));

function _extends$9() { _extends$9 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9.apply(this, arguments); }
var EcuSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$9({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#ED1C23",
  d: "M36 27a4 4 0 01-4 4H4a4 4 0 01-4-4V9a4 4 0 014-4h28a4 4 0 014 4v18z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#034EA2",
  d: "M0 17h36v7H0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FD0",
  d: "M36 17V9a4 4 0 00-4-4H4a4 4 0 00-4 4v8h36z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FD0",
  d: "M23 19a2 2 0 01-2 2h-6a2 2 0 01-2-2v-3a2 2 0 012-2h6a2 2 0 012 2v3z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#034EA2",
  d: "M22 18c0 1.104-.896 3-2 3h-4c-1.104 0-2-1.896-2-3v-1a2 2 0 012-2h4a2 2 0 012 2v1z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#ED1C23",
  d: "M21 18c0 1.104-.896 3-2 3h-2c-1.104 0-2-1.896-2-3v-1a2 2 0 012-2h2a2 2 0 012 2v1z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#6DBE46",
  d: "M19.973 14.135c-.133.488-.961.865-1.973.865s-1.84-.377-1.973-.865C14.822 14.307 15 14.628 15 15c0 .552.791 1 3 1s3-.448 3-1c0-.372.178-.693-1.027-.865z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#A6D388",
  d: "M19 19a1 1 0 11-2 0v-3a1 1 0 012 0v3z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#662113",
  d: "M21.5 12c.44 0 .858.052 1.246.137C22.123 11.061 20 10 18 12c-2-2-4.123-.939-4.746.137A5.822 5.822 0 0114.5 12c1.933 0 3.5.896 3.5 2 0-1.104 1.566-2 3.5-2z"
}), /*#__PURE__*/React.createElement("ellipse", {
  fill: "#8ED2E7",
  cx: "18",
  cy: "17.5",
  rx: "2",
  ry: "3.5"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#6DBE46",
  d: "M17.5 17c-.536 0-1.037.13-1.491.345-.001.052-.009.102-.009.155 0 1.933.896 3.5 2 3.5 1.003 0 1.825-1.295 1.97-2.979A3.492 3.492 0 0017.5 17z"
}), /*#__PURE__*/React.createElement("ellipse", {
  fill: "#FFCC4D",
  cx: "18",
  cy: "22",
  rx: "3",
  ry: "1"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#034EA2",
  d: "M20 23c0 .828-.896 1-2 1s-2-.172-2-1 .896-2 2-2 2 1.172 2 2z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#ED1C23",
  d: "M17 23c0-.828.448-2 1-2s1 1.172 1 2-.448 1-1 1-1-.172-1-1z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#CCD6DD",
  d: "M17 11c0-.552.448 0 1 0s1-.552 1 0a1 1 0 01-2 0z"
}), /*#__PURE__*/React.createElement("ellipse", {
  fill: "#FFF",
  cx: "18",
  cy: "17.5",
  rx: "1",
  ry: ".5"
}), /*#__PURE__*/React.createElement("ellipse", {
  fill: "#C1694F",
  cx: "13",
  cy: "15.5",
  rx: "1",
  ry: ".5"
}), /*#__PURE__*/React.createElement("ellipse", {
  fill: "#C1694F",
  cx: "14",
  cy: "13.5",
  rx: "1",
  ry: ".5"
}), /*#__PURE__*/React.createElement("ellipse", {
  fill: "#C1694F",
  cx: "23",
  cy: "15.5",
  rx: "1",
  ry: ".5"
}), /*#__PURE__*/React.createElement("ellipse", {
  fill: "#C1694F",
  cx: "22",
  cy: "13.5",
  rx: "1",
  ry: ".5"
})));

function _extends$8() { _extends$8 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8.apply(this, arguments); }
var PrySVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$8({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#0038A8",
  d: "M0 27a4 4 0 004 4h28a4 4 0 004-4v-4H0v4z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#EEE",
  d: "M0 13h36v10H0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#D52B1E",
  d: "M32 5H4a4 4 0 00-4 4v4h36V9a4 4 0 00-4-4z"
}), /*#__PURE__*/React.createElement("path", {
  d: "M22 18a4 4 0 11-8 0 4 4 0 018 0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#EEE",
  d: "M21.75 18a3.75 3.75 0 11-7.501-.001A3.75 3.75 0 0121.75 18z"
}), /*#__PURE__*/React.createElement("path", {
  d: "M21.25 18a3.25 3.25 0 11-6.5 0 3.25 3.25 0 016.5 0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#EEE",
  d: "M21.126 18a3.125 3.125 0 11-6.25.002 3.125 3.125 0 016.25-.002z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#99AAB5",
  d: "M21.132 17.98a3.132 3.132 0 10-5.841 1.573l2.712-1.491 2.707 1.491c.268-.463.422-1 .422-1.573z"
}), /*#__PURE__*/React.createElement("path", {
  d: "M20.5 18a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
}), /*#__PURE__*/React.createElement("circle", {
  fill: "#EEE",
  cx: "18",
  cy: "18",
  r: "2.4"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFAC33",
  d: "M18 17.182l.184.565h.594l-.481.35.183.565-.48-.349-.48.349.183-.565-.481-.35h.594z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#007127",
  d: "M18.497 16.517s.3-.07.476-.088c.176-.018.334.388.545.458.214.072.212.282.3.529.088.247.035.299.035.581s.158.423.123.634c-.035.211-.211.264-.316.405-.105.142-.018.317-.124.423-.105.105-.458.088-.581.194-.123.105-.352.264-.511.176-.158-.088-.493-.123-.493-.123s.088-.317.335-.423c.247-.105.352-.088.581-.158.229-.071.211-.177.37-.317.158-.141.176-.335.264-.476.089-.141.106-.247.071-.441a3.298 3.298 0 00-.177-.582c-.07-.176-.229-.387-.335-.511-.105-.123-.563-.281-.563-.281z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#007127",
  d: "M17.405 16.622s.175-.383 0-.405c-.705-.088-.846.546-1.057.616-.213.072-.247.423-.334.669-.088.247-.053.299-.053.582 0 .282.005.334.041.546.035.211.083.37.188.511.106.141.146.211.252.316.105.105.312.212.435.317.123.105.498.141.657.053.158-.088.435-.123.435-.123s-.118-.212-.364-.317c-.247-.105-.264-.193-.493-.264-.229-.071-.211-.177-.37-.317-.158-.141-.176-.335-.264-.476-.088-.141-.041-.176-.005-.37.035-.194.105-.352.176-.528.07-.176.247-.405.352-.528.105-.123.404-.282.404-.282z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#007127",
  d: "M17.423 19.863l.229.158.299-.211.282.194.37-.141-.37.265-.264-.212-.282.194-.282-.159z"
})));

function _extends$7() { _extends$7 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }
var PerSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$7({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#D91023",
  d: "M4 5a4 4 0 00-4 4v18a4 4 0 004 4h8V5H4z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#EEE",
  d: "M12 5h12v26H12z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#D91023",
  d: "M32 5h-8v26h8a4 4 0 004-4V9a4 4 0 00-4-4z"
})));

function _extends$6() { _extends$6 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }
var UrySVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$6({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#0038A8",
  d: "M0 27a4 4 0 004 4h28a4 4 0 004-4v-1H0v1zm0-8h36v4H0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#EEE",
  d: "M0 23h36v3H0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#0038A8",
  d: "M36 9a4 4 0 00-4-4H16v4h20zm-20 3h20v4H16z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#EEE",
  d: "M16 9V5H4a4 4 0 00-4 4v10h36v-3H16v-4h20V9H16z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFD983",
  d: "M13 12a5 5 0 10-10 0 5 5 0 0010 0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFCC4D",
  d: "M12 12a4 4 0 10-8 0 4 4 0 008 0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFAC33",
  d: "M8 5.597l.633 4.041 2.569-3.183-1.473 3.816 3.816-1.473-3.183 2.569 4.041.633-4.041.633 3.183 2.569-3.816-1.473 1.473 3.816-2.569-3.183L8 18.403l-.633-4.041-2.569 3.183 1.473-3.816-3.816 1.473 3.183-2.569L1.597 12l4.041-.633-3.183-2.569 3.816 1.473-1.473-3.816 2.569 3.183z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#C1694F",
  d: "M11 12a3 3 0 10-6 0 3 3 0 006 0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFAC33",
  d: "M5.834 12.004a2.153 2.153 0 114.307-.001 2.153 2.153 0 01-4.307.001z"
})));

function _extends$5() { _extends$5 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }
var VenSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$5({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#CF142B",
  d: "M0 27a4 4 0 004 4h28a4 4 0 004-4v-4H0v4z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#00247D",
  d: "M0 23h36V13H0v10zm25.316-3.55l.269.74.787-.027-.62.485.269.74-.653-.44-.62.485.217-.757-.653-.44.787-.027.217-.759zM24.1 17.785l.73-.295-.417.667.506.603-.764-.19-.417.667-.055-.785-.764-.19.73-.295-.055-.785.506.603zm-2.9-2.145l.682.394.585-.527-.164.77.682.394-.783.082-.164.77-.32-.719-.783.082.585-.527-.32-.719zm-2.002-.493l.37-.695.11.779.775.137-.707.345.11.779-.547-.566-.709.345.37-.695-.547-.566.775.137zm-2.875.084l.11-.779.37.695.775-.137-.547.566.37.695-.707-.345-.547.566.11-.779-.707-.345.773-.137zm-2.789.276l.585.527.681-.394-.32.719.585.527-.783-.082-.32.719-.164-.77-.783-.082.682-.394-.163-.77zM11.17 17.49l.73.295.506-.603-.055.785.73.295-.764.19-.055.785-.417-.667-.764.19.506-.603-.417-.667zm-.756 2.699l.269-.74.217.757.787.027-.653.44.217.757-.62-.485-.653.44.269-.74-.62-.485.787.029z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FC0",
  d: "M32 5H4a4 4 0 00-4 4v4h36V9a4 4 0 00-4-4z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFF",
  d: "M18.6 16.271l.707-.345.547.566-.11-.779.708-.345-.775-.137-.11-.779-.369.695-.776-.137.547.566zm3.118.532l.32.719.163-.769.783-.083-.681-.393.163-.77-.585.526-.681-.393.32.719-.585.527zm1.201 1.459l.764.19.055.785.417-.667.763.19-.506-.603.418-.667-.73.295-.506-.603.055.785zm2.046 2.412l-.217.756.621-.484.652.44-.269-.74.62-.484-.786.027-.27-.739-.217.756-.786.028zm-8.819-4.182l.547-.566.707.345-.369-.695.547-.566-.776.137-.369-.695-.11.779-.775.137.708.345zm-2.347.261l.163.769.32-.719.783.083-.585-.527.32-.719-.681.393-.585-.526.163.77-.681.393zm-1.954 1.817l.417.667.055-.785.764-.19-.73-.295.055-.785-.506.603-.73-.295.418.667-.506.603zm-1.866 2.816l.652-.44.621.484-.217-.756.652-.44-.786-.028-.217-.756-.27.739-.786-.027.62.484z"
})));

function _extends$4() { _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }
var USASVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$4({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 36 36"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "#B22334",
  d: "M35.445 7C34.752 5.809 33.477 5 32 5H18v2h17.445zM0 25h36v2H0zm18-8h18v2H18zm0-4h18v2H18zM0 21h36v2H0zm4 10h28c1.477 0 2.752-.809 3.445-2H.555c.693 1.191 1.968 2 3.445 2zM18 9h18v2H18z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#EEE",
  d: "M.068 27.679c.017.093.036.186.059.277.026.101.058.198.092.296.089.259.197.509.333.743L.555 29h34.89l.002-.004a4.22 4.22 0 00.332-.741 3.75 3.75 0 00.152-.576c.041-.22.069-.446.069-.679H0c0 .233.028.458.068.679zM0 23h36v2H0zm0-4v2h36v-2H18zm18-4h18v2H18zm0-4h18v2H18zM0 9zm.555-2l-.003.005L.555 7zM.128 8.044c.025-.102.06-.199.092-.297a3.78 3.78 0 00-.092.297zM18 9h18c0-.233-.028-.459-.069-.68a3.606 3.606 0 00-.153-.576A4.21 4.21 0 0035.445 7H18v2z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#3C3B6E",
  d: "M18 5H4a4 4 0 00-4 4v10h18V5z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#FFF",
  d: "M2.001 7.726l.618.449-.236.725L3 8.452l.618.448-.236-.725L4 7.726h-.764L3 7l-.235.726zm2 2l.618.449-.236.725.617-.448.618.448-.236-.725L6 9.726h-.764L5 9l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L9 9l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L13 9l-.235.726zm-8 4l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L5 13l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L9 13l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L13 13l-.235.726zm-6-6l.618.449-.236.725L7 8.452l.618.448-.236-.725L8 7.726h-.764L7 7l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L11 7l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L15 7l-.235.726zm-12 4l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L3 11l-.235.726zM6.383 12.9L7 12.452l.618.448-.236-.725.618-.449h-.764L7 11l-.235.726h-.764l.618.449zm3.618-1.174l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L11 11l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L15 11l-.235.726zm-12 4l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L3 15l-.235.726zM6.383 16.9L7 16.452l.618.448-.236-.725.618-.449h-.764L7 15l-.235.726h-.764l.618.449zm3.618-1.174l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L11 15l-.235.726zm4 0l.618.449-.236.725.617-.448.618.448-.236-.725.618-.449h-.764L15 15l-.235.726z"
})));

const getFlagIcon = () => {
  const countries = [
    {
      countryCode: "AR",
      icon: /* @__PURE__ */ React.createElement(ArgSVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "BO",
      icon: /* @__PURE__ */ React.createElement(BolSVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "BR",
      icon: /* @__PURE__ */ React.createElement(BraSVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "CL",
      icon: /* @__PURE__ */ React.createElement(ChlSVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "CO",
      icon: /* @__PURE__ */ React.createElement(ColSVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "EC",
      icon: /* @__PURE__ */ React.createElement(EcuSVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "PY",
      icon: /* @__PURE__ */ React.createElement(PrySVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "PE",
      icon: /* @__PURE__ */ React.createElement(PerSVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "UY",
      icon: /* @__PURE__ */ React.createElement(UrySVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "VE",
      icon: /* @__PURE__ */ React.createElement(VenSVG, {
        className: "Flag"
      })
    },
    {
      countryCode: "US",
      icon: /* @__PURE__ */ React.createElement(USASVG, {
        className: "Flag"
      })
    }
  ];
  return countries;
};
const findFlagIconByCountryCode = (countryCode) => {
  if (!countryCode) {
    console.warn("PhoneNumberInputUI: No country code was provided");
    return null;
  }
  const country = getFlagIcon().find((item) => item.countryCode === countryCode);
  if (!country) {
    console.warn("PhoneNumberInputUI: No country flag found based on country code provided");
    return null;
  }
  return country.icon;
};

var __defProp$f = Object.defineProperty;
var __getOwnPropSymbols$f = Object.getOwnPropertySymbols;
var __hasOwnProp$f = Object.prototype.hasOwnProperty;
var __propIsEnum$f = Object.prototype.propertyIsEnumerable;
var __defNormalProp$f = (obj, key, value) => key in obj ? __defProp$f(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$f = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$f.call(b, prop))
      __defNormalProp$f(a, prop, b[prop]);
  if (__getOwnPropSymbols$f)
    for (var prop of __getOwnPropSymbols$f(b)) {
      if (__propIsEnum$f.call(b, prop))
        __defNormalProp$f(a, prop, b[prop]);
    }
  return a;
};
var __objRest$6 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$f.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$f)
    for (var prop of __getOwnPropSymbols$f(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$f.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const SelectCountry = (props) => {
  const _a = props, {
    value,
    options,
    className,
    disabled,
    onChange = defaultFunction
  } = _a, rest = __objRest$6(_a, [
    "value",
    "options",
    "className",
    "disabled",
    "onChange"
  ]);
  return /* @__PURE__ */ React.createElement(SelectCountryStyled, {
    className: `SelectCountry ${className}`
  }, /* @__PURE__ */ React.createElement("div", {
    className: "SelectCountry__flag"
  }, findFlagIconByCountryCode(value)), /* @__PURE__ */ React.createElement("select", __spreadValues$f({
    className: "SelectCountry__select",
    disabled,
    onChange,
    value
  }, rest), options == null ? void 0 : options.map((value2, index) => {
    return /* @__PURE__ */ React.createElement("option", {
      key: index,
      value: value2,
      className: "SelectCountry__option"
    }, `+${getCountryCallingCode(value2 != null ? value2 : "EC")}`);
  })));
};
SelectCountry.defaultProps = {
  className: ""
};

const transformPhoneNumber = (phone) => {
  const noSpaces = phone.replace(/\s/g, "");
  return noSpaces.replace(/\d{3,4}?(?=.\d\d)/g, "$& ");
};

var __defProp$e = Object.defineProperty;
var __defProps$8 = Object.defineProperties;
var __getOwnPropDescs$8 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$e = Object.getOwnPropertySymbols;
var __hasOwnProp$e = Object.prototype.hasOwnProperty;
var __propIsEnum$e = Object.prototype.propertyIsEnumerable;
var __defNormalProp$e = (obj, key, value) => key in obj ? __defProp$e(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$e = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$e.call(b, prop))
      __defNormalProp$e(a, prop, b[prop]);
  if (__getOwnPropSymbols$e)
    for (var prop of __getOwnPropSymbols$e(b)) {
      if (__propIsEnum$e.call(b, prop))
        __defNormalProp$e(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$8 = (a, b) => __defProps$8(a, __getOwnPropDescs$8(b));
var __objRest$5 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$e.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$e)
    for (var prop of __getOwnPropSymbols$e(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$e.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const PhoneNumberInput = forwardRef((props, ref) => {
  const _a = props, {
    id,
    name,
    label,
    className,
    inputValue,
    selectValue,
    initialCountryCode,
    disabled,
    minLength,
    maxLength,
    placeholder,
    errorMessage,
    countriesCodes,
    hideCountryCode,
    onChange = defaultFunction
  } = _a, rest = __objRest$5(_a, [
    "id",
    "name",
    "label",
    "className",
    "inputValue",
    "selectValue",
    "initialCountryCode",
    "disabled",
    "minLength",
    "maxLength",
    "placeholder",
    "errorMessage",
    "countriesCodes",
    "hideCountryCode",
    "onChange"
  ]);
  const [, setFlagDisplayed] = useState();
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState(() => transformPhoneNumber(inputValue != null ? inputValue : ""));
  const errorMessageId = `${id != null ? id : name}-error`;
  const countriesCodesIsEmpty = countriesCodes.length === 0;
  const [currentCountryCode, setCurrentCountryCode] = useState(initialCountryCode != null ? initialCountryCode : countriesCodes[0]);
  const warningByInitialCountryCodeWasThrown = useRef(false);
  const firstCountryCode = useMemo(() => countriesCodes.length > 0 ? countriesCodes[0] : void 0, [countriesCodes]);
  const countryCodeIsInCountriesCodeList = useMemo(() => countriesCodes.some((countryCode) => countryCode === initialCountryCode), [countriesCodes, initialCountryCode]);
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "PhoneNumberInput__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "PhoneNumberInput__errorMessage"
    }, errorMessage);
  };
  const changeHandler = (inputValue2, selectValue2) => {
    const hasLetters = RegExp(/[A-Za-z]/);
    if (disabled || inputValue2 && hasLetters.test(inputValue2))
      return;
    const newSelectedCountry = selectValue2 ? selectValue2 : currentCountryCode;
    const countryCallingCode = getCountryCallingCode(newSelectedCountry);
    const newPhoneNumber = typeof inputValue2 === "string" ? inputValue2 : currentPhoneNumber;
    const phoneNumberWithCode = `+${countryCallingCode}${newPhoneNumber}`;
    onChange({
      isPossible: isPossiblePhoneNumber(phoneNumberWithCode),
      isValid: isValidPhoneNumber(phoneNumberWithCode),
      international: formatPhoneNumberIntl(phoneNumberWithCode).replace(/\s/g, ""),
      national: newPhoneNumber.replace(/ /g, ""),
      internationalRaw: formatPhoneNumberIntl(phoneNumberWithCode),
      nationalRaw: formatPhoneNumber(phoneNumberWithCode),
      countryCode: newSelectedCountry,
      countryCallingCode: `+${countryCallingCode}`
    });
    setCurrentPhoneNumber(transformPhoneNumber(newPhoneNumber));
  };
  const selectChangeHandler = (value) => {
    if (typeof value === "undefined" || disabled)
      return;
    setCurrentCountryCode(value);
    changeHandler(void 0, value);
    setFlagDisplayed(currentCountryCode);
  };
  const renderLabel = () => {
    if (!label)
      return null;
    return /* @__PURE__ */ React.createElement("label", {
      className: "PhoneNumberInput__label",
      htmlFor: id != null ? id : name
    }, label);
  };
  const renderSelectCountry = () => {
    if (hideCountryCode)
      return null;
    return /* @__PURE__ */ React.createElement(SelectCountry, {
      className: "PhoneNumberInput__select",
      disabled,
      options: countriesCodes,
      value: currentCountryCode,
      onChange: (event) => selectChangeHandler(event.target.value)
    });
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("PhoneNumberInputUI: When using the label tag consider passing the 'name' prop");
  }, [name, label]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn("PhoneNumberInputUI: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, errorMessage]);
  useEffect(() => {
    if (countryCodeIsInCountriesCodeList || warningByInitialCountryCodeWasThrown.current || countriesCodesIsEmpty || !firstCountryCode || !initialCountryCode) {
      return;
    }
    console.warn(`PhoneNumberInputUI: The initial value for the country code dropdown "${initialCountryCode}" is not in the list of codes of the countriesCodes prop. Will be using "${firstCountryCode}" as the initial value for the prop initialCountryCode.`);
    warningByInitialCountryCodeWasThrown.current = true;
    setCurrentCountryCode(firstCountryCode);
    setFlagDisplayed(firstCountryCode);
  }, [
    countriesCodesIsEmpty,
    countryCodeIsInCountriesCodeList,
    firstCountryCode,
    initialCountryCode
  ]);
  if (countriesCodesIsEmpty)
    return null;
  return /* @__PURE__ */ React.createElement(PhoneNumberInputStyled, {
    className: `PhoneNumberInput ${className}`
  }, renderLabel(), /* @__PURE__ */ React.createElement("div", {
    className: "PhoneNumberInput__select-and-input"
  }, renderSelectCountry(), /* @__PURE__ */ React.createElement("input", __spreadProps$8(__spreadValues$e({
    id: id != null ? id : name,
    type: "tel",
    inputMode: "numeric",
    className: "PhoneNumberInput__input",
    name,
    disabled,
    placeholder,
    maxLength,
    minLength,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId
  }, rest), {
    value: currentPhoneNumber,
    onChange: (e) => changeHandler(e.target.value),
    ref
  }))), renderErrorMessage());
});
PhoneNumberInput.defaultProps = {
  className: "",
  maxLength: 20,
  minLength: 4
};

const ProductCardStyled = styled.div`
  cursor: pointer;
  position: relative;

  .ProductCard {
  }
`;
const ProductCardContainerStyled = styled.div`
  width: ${(props) => props.width};
  height: ${(props) => props.height};

  .ProductCardContainer {
  }
`;

const ProductCardDescriptionStyled = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 12;

  .ProductCardDescription {
  }
`;

const ProductCardDescription = (props) => {
  const { className, product } = props;
  const { description } = product;
  return /* @__PURE__ */ React.createElement(ProductCardDescriptionStyled, {
    className: `ProductCardDescription ${className}`,
    title: description
  }, description);
};
ProductCardDescription.defaultProps = {
  className: ""
};

const ProductCardImageStyled = styled.div`
  .ProductCardImage {
  }
`;

const ProductCardImage = (props) => {
  const { className, product, errorImage, height = 168, width = 168 } = props;
  const { animated, infinite, alt, controls, pagination, scrollSpeed } = props;
  const { initialSlide, nextIcon, prevIcon, onChange, fallbackImage } = props;
  const { draggable } = props;
  const { name, images } = product;
  const localFallbackImage = fallbackImage != null ? fallbackImage : "https://res.cloudinary.com/dovstvjim/image/upload/v1627933617/Propeller/product_false_kxyniy.jpg";
  return /* @__PURE__ */ React.createElement(ProductCardImageStyled, {
    className: `ProductCardImage ${className}`
  }, images.length > 0 ? /* @__PURE__ */ React.createElement(Carousel, {
    className: "ProductCardImage__carousel",
    animated,
    infinite,
    controls,
    pagination,
    scrollSpeed,
    initialSlide,
    nextIcon,
    prevIcon,
    onChange,
    draggable
  }, images.map((image, index) => /* @__PURE__ */ React.createElement(Image$1, {
    key: index,
    className: "ProductCardImage__image",
    alt: alt != null ? alt : name,
    config: { width, height },
    image,
    errorImage,
    width,
    height,
    draggable: "false"
  }))) : null, !images.length ? /* @__PURE__ */ React.createElement(Image$1, {
    className: "ProductCardImage__image",
    alt: alt != null ? alt : name,
    config: { width, height },
    image: localFallbackImage,
    errorImage,
    width,
    height,
    draggable: "false"
  }) : null);
};
ProductCardImage.defaultProps = {
  className: "",
  width: 168,
  height: 168,
  controls: false,
  pagination: false
};

const ProductCardNameStyled = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 12;

  .ProductCardName {
  }
`;

const ProductCardName = (props) => {
  const { className, product } = props;
  const { name } = product;
  return /* @__PURE__ */ React.createElement(ProductCardNameStyled, {
    title: name,
    className: `ProductCardName ${className}`
  }, name);
};
ProductCardName.defaultProps = {
  className: ""
};

const ProductCardUI = (props) => {
  const { className, product, children, onClick } = props;
  const { attributes } = product;
  const renderChildren = () => {
    return getChildren(children).map((child, index) => {
      if (!child) {
        return null;
      }
      if (!isValidElement(child)) {
        return child;
      }
      return cloneElement(child, {
        key: index,
        product
      });
    });
  };
  if (!attributes.showInMenu) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(ProductCardStyled, {
    className: `ProductCard  ${className}`,
    onClick
  }, renderChildren());
};
ProductCardUI.Description = ProductCardDescription;
ProductCardUI.Image = ProductCardImage;
ProductCardUI.Name = ProductCardName;
ProductCardUI.defaultProps = {
  className: ""
};

var __defProp$d = Object.defineProperty;
var __getOwnPropSymbols$d = Object.getOwnPropertySymbols;
var __hasOwnProp$d = Object.prototype.hasOwnProperty;
var __propIsEnum$d = Object.prototype.propertyIsEnumerable;
var __defNormalProp$d = (obj, key, value) => key in obj ? __defProp$d(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$d = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$d.call(b, prop))
      __defNormalProp$d(a, prop, b[prop]);
  if (__getOwnPropSymbols$d)
    for (var prop of __getOwnPropSymbols$d(b)) {
      if (__propIsEnum$d.call(b, prop))
        __defNormalProp$d(a, prop, b[prop]);
    }
  return a;
};
const { Description, Image, Name } = ProductCardUI;
const ProductCard = (props) => {
  const { product, placeholder, width = "100%", height = "100%" } = props;
  if (!product) {
    return placeholder ? /* @__PURE__ */ React.createElement(ProductCardContainerStyled, {
      width,
      height
    }, placeholder) : /* @__PURE__ */ React.createElement("div", null);
  }
  return /* @__PURE__ */ React.createElement(ProductCardContainerStyled, {
    width,
    height
  }, /* @__PURE__ */ React.createElement(ProductCardUI, __spreadValues$d({}, props)));
};
ProductCard.Description = Description;
ProductCard.Image = Image;
ProductCard.Name = Name;
ProductCard.defaultProps = {
  width: "100%",
  height: "initial"
};

var __defProp$c = Object.defineProperty;
var __defProps$7 = Object.defineProperties;
var __getOwnPropDescs$7 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$c = Object.getOwnPropertySymbols;
var __hasOwnProp$c = Object.prototype.hasOwnProperty;
var __propIsEnum$c = Object.prototype.propertyIsEnumerable;
var __defNormalProp$c = (obj, key, value) => key in obj ? __defProp$c(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$c = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$c.call(b, prop))
      __defNormalProp$c(a, prop, b[prop]);
  if (__getOwnPropSymbols$c)
    for (var prop of __getOwnPropSymbols$c(b)) {
      if (__propIsEnum$c.call(b, prop))
        __defNormalProp$c(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$7 = (a, b) => __defProps$7(a, __getOwnPropDescs$7(b));
const useProductCard = (product, config = {}) => {
  const { amount, decimals } = config;
  const { prices, available, priceCategory } = product;
  const { NORMAL, POINTS } = prices;
  const productTotals = getProductTotals(product, amount);
  const sanitizedProductTotals = Object.entries(productTotals).reduce((acc, field) => {
    const [key, value] = field;
    if (typeof value === "string" || value === null) {
      return __spreadProps$7(__spreadValues$c({}, acc), { [key]: value });
    }
    return __spreadProps$7(__spreadValues$c({}, acc), { [key]: +value.toFixed(decimals) });
  }, {});
  const totals = __spreadProps$7(__spreadValues$c({}, sanitizedProductTotals), {
    symbol: NORMAL.symbol,
    unitPoints: +(POINTS == null ? void 0 : POINTS.netPrice.toFixed(decimals)),
    points: +((POINTS == null ? void 0 : POINTS.netPrice) * productTotals.amount).toFixed(decimals),
    priceCategory
  });
  return { totals, available };
};

const ShoppingCartSummaryStyled = styled.div`
  padding: 24px 16px;
  background-color: #fafafa;

  .ShoppingCartSummary {
    &__title {
      font-size: 12px;
      color: #6b6b6b;
      padding-bottom: 12px;
    }

    &__summary {
      .ShoppingCartItem {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 0;
        font-size: 12px;

        &:last-child {
          padding-bottom: 0;
          font-size: 12.8px;
          font-weight: 600;
        }
      }
    }
  }
`;

const ShoppingCartSummaryDiscountStyled = styled.div`
  .ShoppingCartSummaryDiscount {
  }
`;

var __defProp$b = Object.defineProperty;
var __getOwnPropSymbols$b = Object.getOwnPropertySymbols;
var __hasOwnProp$b = Object.prototype.hasOwnProperty;
var __propIsEnum$b = Object.prototype.propertyIsEnumerable;
var __defNormalProp$b = (obj, key, value) => key in obj ? __defProp$b(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$b = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$b.call(b, prop))
      __defNormalProp$b(a, prop, b[prop]);
  if (__getOwnPropSymbols$b)
    for (var prop of __getOwnPropSymbols$b(b)) {
      if (__propIsEnum$b.call(b, prop))
        __defNormalProp$b(a, prop, b[prop]);
    }
  return a;
};
var __objRest$4 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$b.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$b)
    for (var prop of __getOwnPropSymbols$b(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$b.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const formatByCurrency = (value, options, points = false) => {
  const _a = options, {
    locale = "en",
    symbol,
    pointsSymbol = "PTS",
    minimumFractionDigits = points ? 0 : void 0
  } = _a, rest = __objRest$4(_a, [
    "locale",
    "symbol",
    "pointsSymbol",
    "minimumFractionDigits"
  ]);
  const formatter = new Intl.NumberFormat(locale, __spreadValues$b({
    style: "currency",
    minimumFractionDigits
  }, rest));
  return formatter.formatToParts(value).map(({ type, value: value2 }) => {
    if (!symbol)
      return value2;
    switch (type) {
      case "currency":
        return points && pointsSymbol ? pointsSymbol : symbol;
      default:
        return value2;
    }
  }).reduce((string, part) => string + part);
};

const defaultFormatter = { currency: "USD" };

const ShoppingCartSummaryDiscount = (props) => {
  const { className, shoppingCartTotals, title } = props;
  const { formatter = defaultFormatter } = props;
  const { totalDiscounts } = shoppingCartTotals;
  if (totalDiscounts === null)
    return null;
  return /* @__PURE__ */ React.createElement(ShoppingCartSummaryDiscountStyled, {
    className: `ShoppingCartSummaryDiscount ShoppingCartItem ${className}`
  }, /* @__PURE__ */ React.createElement("p", {
    className: "ShoppingCartSummaryDiscount__title"
  }, title, ":"), /* @__PURE__ */ React.createElement("div", {
    className: "ShoppingCartSummaryDiscount__value"
  }, formatByCurrency(totalDiscounts, formatter)));
};
ShoppingCartSummaryDiscount.defaultProps = {
  className: "",
  title: "Descuentos",
  formatter: defaultFormatter
};

const ShoppingCartSummaryPointsStyled = styled.div`
  .ShoppingCartSummaryPoints {
  }
`;

const ShoppingCartSummaryPoints = (props) => {
  const { className, shoppingCartTotals, title } = props;
  const { formatter = defaultFormatter } = props;
  const { totalPoints } = shoppingCartTotals;
  if (totalPoints === null)
    return null;
  return /* @__PURE__ */ React.createElement(ShoppingCartSummaryPointsStyled, {
    className: `ShoppingCartSummaryPoints ShoppingCartItem ${className}`
  }, /* @__PURE__ */ React.createElement("p", {
    className: "ShoppingCartSummaryPoints__title"
  }, title, ":"), /* @__PURE__ */ React.createElement("div", {
    className: "ShoppingCartSummaryPoints__value"
  }, formatByCurrency(totalPoints, formatter, true)));
};
ShoppingCartSummaryPoints.defaultProps = {
  className: "",
  title: "Puntos",
  formatter: defaultFormatter
};

const ShoppingCartSummaryShippingCostStyled = styled.div`
  .ShoppingCartSummaryShippingCost {
    &__value {
      display: flex;
    }
  }
`;

const ShoppingCartSummaryShippingCost = (props) => {
  const { className, shoppingCartTotals, title, fallback } = props;
  const { formatter = defaultFormatter } = props;
  const { shippingCost = 0 } = shoppingCartTotals;
  return /* @__PURE__ */ React.createElement(ShoppingCartSummaryShippingCostStyled, {
    className: `ShoppingCartSummaryShippingCost ShoppingCartItem ${className}`
  }, fallback && shippingCost === null ? fallback : null, shippingCost !== null ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", {
    className: "ShoppingCartSummaryShippingCost__title"
  }, title, ":"), /* @__PURE__ */ React.createElement("div", {
    className: "ShoppingCartSummaryShippingCost__value"
  }, formatByCurrency(shippingCost, formatter))) : null);
};
ShoppingCartSummaryShippingCost.defaultProps = {
  className: "",
  title: "Env\xEDo",
  formatter: defaultFormatter
};

const ShoppingCartSummarySubtotalStyled = styled.div`
  .ShoppingCartSummarySubtotal {
  }
`;

const ShoppingCartSummarySubtotal = (props) => {
  const { className, shoppingCartTotals, title, showPoints } = props;
  const { formatter = defaultFormatter } = props;
  const { subtotal, totalPoints } = shoppingCartTotals;
  if (totalPoints === null)
    return null;
  return /* @__PURE__ */ React.createElement(ShoppingCartSummarySubtotalStyled, {
    className: `ShoppingCartSummarySubtotal ShoppingCartItem ${className}`
  }, /* @__PURE__ */ React.createElement("p", {
    className: "ShoppingCartSummarySubtotal__title"
  }, title, ":"), /* @__PURE__ */ React.createElement("div", {
    className: "ShoppingCartSummarySubtotal__value"
  }, formatByCurrency(subtotal, formatter), showPoints ? /* @__PURE__ */ React.createElement(React.Fragment, null, " | ", formatByCurrency(totalPoints, formatter, true)) : null));
};
ShoppingCartSummarySubtotal.defaultProps = {
  className: "",
  title: "Subtotal",
  formatter: defaultFormatter
};

const ShoppingCartSummaryTaxesStyled = styled.div`
  .ShoppingCartSummaryTaxes {
  }
`;

const ShoppingCartSummaryTaxes = (props) => {
  const { className, shoppingCartTotals, title } = props;
  const { formatter = defaultFormatter } = props;
  const { totalTaxes } = shoppingCartTotals;
  if (totalTaxes === null)
    return null;
  return /* @__PURE__ */ React.createElement(ShoppingCartSummaryTaxesStyled, {
    className: `ShoppingCartSummaryTaxes ShoppingCartItem ${className}`
  }, /* @__PURE__ */ React.createElement("p", {
    className: "ShoppingCartSummaryTaxes__title"
  }, title, ":"), /* @__PURE__ */ React.createElement("div", {
    className: "ShoppingCartSummaryTaxes__value"
  }, formatByCurrency(totalTaxes, formatter)));
};
ShoppingCartSummaryTaxes.defaultProps = {
  className: "",
  title: "Impuestos",
  formatter: defaultFormatter
};

const ShoppingCartSummaryTotalStyled = styled.div`
  .ShoppingCartSummaryTotal {
  }
`;

const ShoppingCartSummaryTotal = (props) => {
  const { className, shoppingCartTotals, title, showPoints } = props;
  const { formatter = defaultFormatter } = props;
  const { total, totalPoints } = shoppingCartTotals;
  if (totalPoints === null)
    return null;
  return /* @__PURE__ */ React.createElement(ShoppingCartSummaryTotalStyled, {
    className: `ShoppingCartSummaryTotal ShoppingCartItem ${className}`
  }, /* @__PURE__ */ React.createElement("p", {
    className: "ShoppingCartSummaryTotal__title"
  }, title, ":"), /* @__PURE__ */ React.createElement("div", {
    className: "ShoppingCartSummaryTotal__value"
  }, formatByCurrency(total, formatter), showPoints ? /* @__PURE__ */ React.createElement(React.Fragment, null, " | ", formatByCurrency(totalPoints, formatter, true)) : null));
};
ShoppingCartSummaryTotal.defaultProps = {
  className: "",
  title: "Total del pedido",
  formatter: defaultFormatter
};

var __defProp$a = Object.defineProperty;
var __getOwnPropSymbols$a = Object.getOwnPropertySymbols;
var __hasOwnProp$a = Object.prototype.hasOwnProperty;
var __propIsEnum$a = Object.prototype.propertyIsEnumerable;
var __defNormalProp$a = (obj, key, value) => key in obj ? __defProp$a(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$a = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$a.call(b, prop))
      __defNormalProp$a(a, prop, b[prop]);
  if (__getOwnPropSymbols$a)
    for (var prop of __getOwnPropSymbols$a(b)) {
      if (__propIsEnum$a.call(b, prop))
        __defNormalProp$a(a, prop, b[prop]);
    }
  return a;
};
const ShoppingCartSummary = (props) => {
  const { className, title = "Resumen del carrito" } = props;
  const { shoppingCartTotals, formatter, children } = props;
  const renderChildren = () => {
    return getChildren(children).map((child, index) => {
      if (!child) {
        return null;
      }
      if (!isValidElement(child)) {
        return child;
      }
      const parentFormatter = formatter ? formatter : {};
      const childFormatter = child.props.formatter ? child.props.formatter : {};
      return cloneElement(child, {
        key: index,
        shoppingCartTotals,
        formatter: __spreadValues$a(__spreadValues$a({}, parentFormatter), childFormatter)
      });
    });
  };
  return /* @__PURE__ */ React.createElement(ShoppingCartSummaryStyled, {
    className: `ShoppingCartSummary ${className}`
  }, /* @__PURE__ */ React.createElement("div", {
    className: "ShoppingCartSummary__title"
  }, title), /* @__PURE__ */ React.createElement("div", {
    className: "ShoppingCartSummary__summary"
  }, renderChildren()));
};
ShoppingCartSummary.defaultProps = {
  className: "",
  title: "Resumen del carrito",
  formatter: defaultFormatter
};
ShoppingCartSummary.Subtotal = ShoppingCartSummarySubtotal;
ShoppingCartSummary.ShippingCost = ShoppingCartSummaryShippingCost;
ShoppingCartSummary.Points = ShoppingCartSummaryPoints;
ShoppingCartSummary.Taxes = ShoppingCartSummaryTaxes;
ShoppingCartSummary.Discount = ShoppingCartSummaryDiscount;
ShoppingCartSummary.Total = ShoppingCartSummaryTotal;

const ScrollableTabsStyled = styled.div`
  overflow: auto hidden;
  width: 100%;
  display: flex;
  scroll-snap-type: x proximity;
  position: relative;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .ScrollableTabs {
    &__item {
      scroll-snap-align: center;
      cursor: pointer;

      &--active {
        border-bottom: 1px solid #000000;
      }
    }
  }
`;

var __defProp$9 = Object.defineProperty;
var __defProps$6 = Object.defineProperties;
var __getOwnPropDescs$6 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$9 = Object.getOwnPropertySymbols;
var __hasOwnProp$9 = Object.prototype.hasOwnProperty;
var __propIsEnum$9 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$9 = (obj, key, value) => key in obj ? __defProp$9(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$9 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$9.call(b, prop))
      __defNormalProp$9(a, prop, b[prop]);
  if (__getOwnPropSymbols$9)
    for (var prop of __getOwnPropSymbols$9(b)) {
      if (__propIsEnum$9.call(b, prop))
        __defNormalProp$9(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$6 = (a, b) => __defProps$6(a, __getOwnPropDescs$6(b));
const ScrollableTabs = (props) => {
  const { className, children } = props;
  const { activeClassName: activeClassNameProp, defaultActiveItem } = props;
  const [activeItem, setActiveItem] = useState(defaultActiveItem != null ? defaultActiveItem : 0);
  const activeClassName = activeClassNameProp ? activeClassNameProp : "ScrollableTabs__item--active";
  return /* @__PURE__ */ React.createElement(ScrollableTabsStyled, {
    className: `ScrollableTabs ${className}`
  }, Children.map(children, (child, index) => {
    if (!isValidElement(child)) {
      return child;
    }
    const { props: props2 } = child;
    const { className: originalClassName } = props2;
    return cloneElement(child, __spreadProps$6(__spreadValues$9({}, props2), {
      onClick: () => {
        var _a;
        setActiveItem(index);
        (_a = props2 == null ? void 0 : props2.onClick) == null ? void 0 : _a.call(props2);
      },
      className: `ScrollableTabs__item ${originalClassName} ${activeItem === index ? activeClassName : ""}`
    }));
  }));
};
ScrollableTabs.defaultProps = {
  className: "",
  activeClassName: "",
  defaultActiveItem: 0
};

const ScrollToTopStyled = styled.div`
  display: flex;
  background-color: #000000;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  position: fixed;
  justify-content: center;
  align-items: center;
  bottom: 36px;
  right: 36px;
  z-index: 2;
  cursor: pointer;

  .ScrollToTop {
  }
`;

const ScrollToTop = (props) => {
  const { children, screen = 2, distance, animated, className } = props;
  const [show, setShow] = useState(false);
  const { height: windowHeight } = useWindowSize();
  const checkScrollTop = useCallback(() => {
    const distanceToShow = distance ? distance : windowHeight * screen;
    if (window.pageYOffset > distanceToShow) {
      setShow(true);
    } else {
      setShow(false);
    }
  }, [screen, distance, windowHeight]);
  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: animated ? "smooth" : "auto" });
  };
  useIsomorphicLayoutEffect(() => {
    window.addEventListener("scroll", checkScrollTop);
    return () => {
      window.removeEventListener("scroll", checkScrollTop);
    };
  }, [checkScrollTop]);
  if (Children.count(children) > 1) {
    console.error("You must provide only one children element");
    return null;
  }
  if (!show)
    return null;
  return /* @__PURE__ */ React.createElement(ScrollToTopStyled, {
    className: `ScrollToTop ${className}`,
    onClick: scrollTop
  }, children);
};
ScrollToTop.defaultProps = {
  className: "",
  screen: 2,
  animated: true
};

const SearchBarStyled = styled.form`
  position: relative;
  display: flex;
  width: 100%;

  .SearchBar {
    &__dropdown {
      display: flex;
      flex: 1;
    }

    &__wrapper {
      position: relative;
      display: flex;
      background-color: #fafafa;
      flex: 1;
      width: 100%;
    }

    &__input {
      border: none;
      padding: 12px 8px;
      background-color: #fafafa;
      flex: 1;
      font-family: inherit;

      &:focus {
        outline: none;
      }
    }

    &:focus {
      outline: none;
    }

    &__btn {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 28px;
      background-color: #fafafa;
      box-shadow: none;
      outline: none;
      border: none;
      padding: 0;

      & > .Button__loading {
        stroke: #000000;
        padding-left: 8px;
      }
    }

    &__btn-search {
      padding-left: 8px;
    }

    &__btn-cross {
      padding-right: 8px;
      right: 8px;
      height: 100%;
      display: flex;
      align-items: center;
    }

    &__btn-container {
      width: 36px;
    }
  }
`;

const SearchBarItemStyled = styled.div`
  .SearchBarItem {
  }
`;

var __defProp$8 = Object.defineProperty;
var __defProps$5 = Object.defineProperties;
var __getOwnPropDescs$5 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$8 = Object.getOwnPropertySymbols;
var __hasOwnProp$8 = Object.prototype.hasOwnProperty;
var __propIsEnum$8 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$8 = (obj, key, value) => key in obj ? __defProp$8(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$8 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$8.call(b, prop))
      __defNormalProp$8(a, prop, b[prop]);
  if (__getOwnPropSymbols$8)
    for (var prop of __getOwnPropSymbols$8(b)) {
      if (__propIsEnum$8.call(b, prop))
        __defNormalProp$8(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$5 = (a, b) => __defProps$5(a, __getOwnPropDescs$5(b));
var __objRest$3 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$8.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$8)
    for (var prop of __getOwnPropSymbols$8(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$8.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const SearchBarItem = forwardRef((props, ref) => {
  const _a = props, { children, onClick, className } = _a, rest = __objRest$3(_a, ["children", "onClick", "className"]);
  return /* @__PURE__ */ React.createElement(SearchBarItemStyled, __spreadProps$5(__spreadValues$8({}, rest), {
    className: `SearchBarItem ${className} SearchBarItem__selectable`,
    onMouseDown: onClick
  }), children);
});
SearchBarItem.defaultProps = {
  className: ""
};

var __defProp$7 = Object.defineProperty;
var __defProps$4 = Object.defineProperties;
var __getOwnPropDescs$4 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$7 = Object.getOwnPropertySymbols;
var __hasOwnProp$7 = Object.prototype.hasOwnProperty;
var __propIsEnum$7 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$7 = (obj, key, value) => key in obj ? __defProp$7(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$7 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$7.call(b, prop))
      __defNormalProp$7(a, prop, b[prop]);
  if (__getOwnPropSymbols$7)
    for (var prop of __getOwnPropSymbols$7(b)) {
      if (__propIsEnum$7.call(b, prop))
        __defNormalProp$7(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$4 = (a, b) => __defProps$4(a, __getOwnPropDescs$4(b));
const addOnClickPropertyToChild = (children, onClickItem) => {
  return Children.map(children, (child) => {
    var _a, _b;
    if (!child) {
      return child;
    }
    if (!isValidElement(child)) {
      return child;
    }
    if (child.type === SearchBarItem) {
      return createElement(child.type, __spreadProps$4(__spreadValues$7({}, child.props), {
        id: child.props.query,
        onClick: (event) => {
          var _a2, _b2;
          event.preventDefault();
          if (child.props.query) {
            onClickItem(child.props.query);
          }
          (_b2 = (_a2 = child.props) == null ? void 0 : _a2.onClick) == null ? void 0 : _b2.call(_a2);
        }
      }), addOnClickPropertyToChild((_a = child.props) == null ? void 0 : _a.children, onClickItem));
    }
    if (!((_b = child.props) == null ? void 0 : _b.children)) {
      return child;
    }
    return createElement(child.type, __spreadProps$4(__spreadValues$7({}, child.props), {
      onMouseDown: (event) => {
        var _a2, _b2;
        event.preventDefault();
        (_b2 = (_a2 = child.props) == null ? void 0 : _a2.onClick) == null ? void 0 : _b2.call(_a2);
      },
      onClick: void 0
    }), addOnClickPropertyToChild(child.props.children, onClickItem));
  });
};

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }
var SearchIconSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$3({
  width: "22",
  height: "22",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M7.782.5c4.014 0 7.283 3.267 7.283 7.28 0 1.713-.62 3.274-1.619 4.519l8.054 8.05-1.138 1.151-8.06-8.057c-1.245.998-2.807 1.617-4.52 1.617C3.768 15.06.5 11.793.5 7.78S3.768.5 7.782.5zm0 1.618A5.654 5.654 0 002.118 7.78a5.654 5.654 0 005.664 5.663 5.654 5.654 0 005.664-5.663 5.654 5.654 0 00-5.664-5.662z",
  fill: "#6B6B6B"
})));

var __defProp$6 = Object.defineProperty;
var __defProps$3 = Object.defineProperties;
var __getOwnPropDescs$3 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$6 = Object.getOwnPropertySymbols;
var __hasOwnProp$6 = Object.prototype.hasOwnProperty;
var __propIsEnum$6 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$6 = (obj, key, value) => key in obj ? __defProp$6(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$6 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$6.call(b, prop))
      __defNormalProp$6(a, prop, b[prop]);
  if (__getOwnPropSymbols$6)
    for (var prop of __getOwnPropSymbols$6(b)) {
      if (__propIsEnum$6.call(b, prop))
        __defNormalProp$6(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$3 = (a, b) => __defProps$3(a, __getOwnPropDescs$3(b));
const SearchBar = (props) => {
  const { className, onSubmit = defaultPromises, SearchIcon } = props;
  const { placeholder, children, onChange = defaultFunction } = props;
  const { submitOnChange, dropdownConfigs } = props;
  const { onClear = defaultFunction, instance } = props;
  const { onInputBlur = defaultFunction } = props;
  const { onInputFocus = defaultFunction } = props;
  const { onInputClick = defaultFunction } = props;
  const { backdropConfig } = props;
  const [query, setQuery] = useState("");
  const [showCrossIcon, setShowCrossIcon] = useState(false);
  const searchBarWrapperRef = useRef(null);
  const [childIndex, setChildIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const actualFocusableElement = useRef(null);
  const { showDropdown: showDropdownConfig } = dropdownConfigs != null ? dropdownConfigs : {};
  const { target = ["focus"] } = dropdownConfigs != null ? dropdownConfigs : {};
  const showOverlay = typeof showDropdownConfig !== "undefined" ? showDropdownConfig : showDropdown;
  const search = async (searchTerm) => {
    if (!searchTerm) {
      return;
    }
    try {
      await onSubmit(searchTerm);
      setShowDropdown(true);
    } catch (error) {
      console.error(error.message);
      setShowDropdown(false);
    }
  };
  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      instance == null ? void 0 : instance.saveSearchLocal(query);
    } catch (e2) {
      console.error("SearchBar: Cannot add the query to local storage");
    }
    search(query);
    setShowCrossIcon(true);
  };
  const changeHandler = (e) => {
    const { value } = e.target;
    setQuery(value);
    onChange(value);
    if (submitOnChange) {
      submitOnChange(value);
      search(value);
      try {
        instance == null ? void 0 : instance.saveSearchLocal(query);
      } catch (e2) {
        console.error("SearchBar: Cannot add the query to local storage");
      }
    }
    setShowCrossIcon(!!value);
  };
  const onBlur = () => {
    setChildIndex(-1);
    setShowDropdown(false);
    setShowCrossIcon(false);
    onInputBlur();
  };
  const inputFocusHandler = (event) => {
    setShowDropdown(true);
    if (!query) {
      return;
    }
    setChildIndex(-1);
    setShowCrossIcon(true);
    onInputFocus();
  };
  const clearInput = (event) => {
    event.preventDefault();
    setQuery("");
    setChildIndex(-1);
    setShowCrossIcon(false);
    setShowDropdown(false);
    onClear();
  };
  const onKeyPressHandler = (event) => {
    var _a, _b;
    const focusableElements = document.querySelectorAll(".SearchBarItem__selectable");
    const { key } = event;
    if (!(focusableElements == null ? void 0 : focusableElements.length)) {
      setChildIndex(-1);
      return;
    }
    if (key === "ArrowUp") {
      event.preventDefault();
      const prev = childIndex;
      (_a = actualFocusableElement.current) == null ? void 0 : _a.classList.remove("SearchBar__item--active");
      const prevIndex = prev - 1 < 0 ? 0 : prev - 1;
      const actualItem = focusableElements == null ? void 0 : focusableElements[prevIndex];
      actualItem == null ? void 0 : actualItem.classList.add("SearchBar__item--active");
      const idAttribute = actualItem == null ? void 0 : actualItem.getAttribute("id");
      if (idAttribute) {
        setQuery(idAttribute);
      }
      actualFocusableElement.current = actualItem;
      setChildIndex(prevIndex);
    }
    if (key === "ArrowDown") {
      event.preventDefault();
      const prev = childIndex;
      (_b = actualFocusableElement.current) == null ? void 0 : _b.classList.remove("SearchBar__item--active");
      const nextItem = prev + 1 > (focusableElements == null ? void 0 : focusableElements.length) - 1 ? (focusableElements == null ? void 0 : focusableElements.length) - 1 : prev + 1;
      const actualItem = focusableElements == null ? void 0 : focusableElements[nextItem];
      actualItem == null ? void 0 : actualItem.classList.add("SearchBar__item--active");
      const idAttribute = actualItem == null ? void 0 : actualItem.getAttribute("id");
      if (idAttribute) {
        setQuery(idAttribute);
      }
      actualFocusableElement.current = actualItem;
      setChildIndex(nextItem);
    }
  };
  return /* @__PURE__ */ React.createElement(SearchBarStyled, {
    className: `SearchBar ${className}`
  }, /* @__PURE__ */ React.createElement(Dropdown, __spreadProps$3(__spreadValues$6({}, dropdownConfigs), {
    className: "SearchBar__dropdown",
    overlay: addOnClickPropertyToChild(children, setQuery),
    target,
    showDropdown: showOverlay,
    backdropConfig
  }), /* @__PURE__ */ React.createElement("div", {
    className: "SearchBar__wrapper",
    ref: searchBarWrapperRef,
    onKeyDown: onKeyPressHandler
  }, /* @__PURE__ */ React.createElement("button", {
    onClick: submitHandler,
    className: "SearchBar__btn SearchBar__btn-search",
    disabled: !query,
    tabIndex: 0,
    "aria-label": "search"
  }, SearchIcon ? SearchIcon : /* @__PURE__ */ React.createElement(SearchIconSVG, null)), /* @__PURE__ */ React.createElement("input", {
    type: "text",
    placeholder,
    value: query,
    className: "SearchBar__input",
    onChange: changeHandler,
    onFocus: inputFocusHandler,
    onBlur,
    onClick: onInputClick,
    tabIndex: 0
  }), /* @__PURE__ */ React.createElement("div", {
    tabIndex: -1,
    className: "SearchBar__btn-container"
  }, showCrossIcon ? /* @__PURE__ */ React.createElement("button", {
    onMouseDown: clearInput,
    type: "button",
    className: "SearchBar__btn SearchBar__btn-cross"
  }, /* @__PURE__ */ React.createElement(CrossCircleSVG, null)) : null))));
};
SearchBar.defaultProps = {
  className: "",
  placeholder: "Buscar...",
  onChange: defaultFunction,
  onClear: defaultFunction,
  onSubmit: defaultPromises,
  onInputBlur: defaultFunction,
  onInputFocus: defaultFunction,
  onInputClick: defaultFunction,
  dropdownConfigs: {
    target: ["focus"]
  }
};
SearchBar.Item = SearchBarItem;

/*! Capacitor: https://capacitorjs.com/ - MIT License */
const createCapacitorPlatforms = (win) => {
    const defaultPlatformMap = new Map();
    defaultPlatformMap.set('web', { name: 'web' });
    const capPlatforms = win.CapacitorPlatforms || {
        currentPlatform: { name: 'web' },
        platforms: defaultPlatformMap,
    };
    const addPlatform = (name, platform) => {
        capPlatforms.platforms.set(name, platform);
    };
    const setPlatform = (name) => {
        if (capPlatforms.platforms.has(name)) {
            capPlatforms.currentPlatform = capPlatforms.platforms.get(name);
        }
    };
    capPlatforms.addPlatform = addPlatform;
    capPlatforms.setPlatform = setPlatform;
    return capPlatforms;
};
const initPlatforms = (win) => (win.CapacitorPlatforms = createCapacitorPlatforms(win));
/**
 * @deprecated Set `CapacitorCustomPlatform` on the window object prior to runtime executing in the web app instead
 */
const CapacitorPlatforms = /*#__PURE__*/ initPlatforms((typeof globalThis !== 'undefined'
    ? globalThis
    : typeof self !== 'undefined'
        ? self
        : typeof window !== 'undefined'
            ? window
            : typeof global !== 'undefined'
                ? global
                : {}));
/**
 * @deprecated Set `CapacitorCustomPlatform` on the window object prior to runtime executing in the web app instead
 */
CapacitorPlatforms.addPlatform;
/**
 * @deprecated Set `CapacitorCustomPlatform` on the window object prior to runtime executing in the web app instead
 */
CapacitorPlatforms.setPlatform;

var ExceptionCode;
(function (ExceptionCode) {
    /**
     * API is not implemented.
     *
     * This usually means the API can't be used because it is not implemented for
     * the current platform.
     */
    ExceptionCode["Unimplemented"] = "UNIMPLEMENTED";
    /**
     * API is not available.
     *
     * This means the API can't be used right now because:
     *   - it is currently missing a prerequisite, such as network connectivity
     *   - it requires a particular platform or browser version
     */
    ExceptionCode["Unavailable"] = "UNAVAILABLE";
})(ExceptionCode || (ExceptionCode = {}));
class CapacitorException extends Error {
    constructor(message, code) {
        super(message);
        this.message = message;
        this.code = code;
    }
}
const getPlatformId = (win) => {
    var _a, _b;
    if (win === null || win === void 0 ? void 0 : win.androidBridge) {
        return 'android';
    }
    else if ((_b = (_a = win === null || win === void 0 ? void 0 : win.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.bridge) {
        return 'ios';
    }
    else {
        return 'web';
    }
};

const createCapacitor = (win) => {
    var _a, _b, _c, _d, _e;
    const capCustomPlatform = win.CapacitorCustomPlatform || null;
    const cap = win.Capacitor || {};
    const Plugins = (cap.Plugins = cap.Plugins || {});
    /**
     * @deprecated Use `capCustomPlatform` instead, default functions like registerPlugin will function with the new object.
     */
    const capPlatforms = win.CapacitorPlatforms;
    const defaultGetPlatform = () => {
        return capCustomPlatform !== null
            ? capCustomPlatform.name
            : getPlatformId(win);
    };
    const getPlatform = ((_a = capPlatforms === null || capPlatforms === void 0 ? void 0 : capPlatforms.currentPlatform) === null || _a === void 0 ? void 0 : _a.getPlatform) || defaultGetPlatform;
    const defaultIsNativePlatform = () => getPlatform() !== 'web';
    const isNativePlatform = ((_b = capPlatforms === null || capPlatforms === void 0 ? void 0 : capPlatforms.currentPlatform) === null || _b === void 0 ? void 0 : _b.isNativePlatform) || defaultIsNativePlatform;
    const defaultIsPluginAvailable = (pluginName) => {
        const plugin = registeredPlugins.get(pluginName);
        if (plugin === null || plugin === void 0 ? void 0 : plugin.platforms.has(getPlatform())) {
            // JS implementation available for the current platform.
            return true;
        }
        if (getPluginHeader(pluginName)) {
            // Native implementation available.
            return true;
        }
        return false;
    };
    const isPluginAvailable = ((_c = capPlatforms === null || capPlatforms === void 0 ? void 0 : capPlatforms.currentPlatform) === null || _c === void 0 ? void 0 : _c.isPluginAvailable) ||
        defaultIsPluginAvailable;
    const defaultGetPluginHeader = (pluginName) => { var _a; return (_a = cap.PluginHeaders) === null || _a === void 0 ? void 0 : _a.find(h => h.name === pluginName); };
    const getPluginHeader = ((_d = capPlatforms === null || capPlatforms === void 0 ? void 0 : capPlatforms.currentPlatform) === null || _d === void 0 ? void 0 : _d.getPluginHeader) || defaultGetPluginHeader;
    const handleError = (err) => win.console.error(err);
    const pluginMethodNoop = (_target, prop, pluginName) => {
        return Promise.reject(`${pluginName} does not have an implementation of "${prop}".`);
    };
    const registeredPlugins = new Map();
    const defaultRegisterPlugin = (pluginName, jsImplementations = {}) => {
        const registeredPlugin = registeredPlugins.get(pluginName);
        if (registeredPlugin) {
            console.warn(`Capacitor plugin "${pluginName}" already registered. Cannot register plugins twice.`);
            return registeredPlugin.proxy;
        }
        const platform = getPlatform();
        const pluginHeader = getPluginHeader(pluginName);
        let jsImplementation;
        const loadPluginImplementation = async () => {
            if (!jsImplementation && platform in jsImplementations) {
                jsImplementation =
                    typeof jsImplementations[platform] === 'function'
                        ? (jsImplementation = await jsImplementations[platform]())
                        : (jsImplementation = jsImplementations[platform]);
            }
            else if (capCustomPlatform !== null &&
                !jsImplementation &&
                'web' in jsImplementations) {
                jsImplementation =
                    typeof jsImplementations['web'] === 'function'
                        ? (jsImplementation = await jsImplementations['web']())
                        : (jsImplementation = jsImplementations['web']);
            }
            return jsImplementation;
        };
        const createPluginMethod = (impl, prop) => {
            var _a, _b;
            if (pluginHeader) {
                const methodHeader = pluginHeader === null || pluginHeader === void 0 ? void 0 : pluginHeader.methods.find(m => prop === m.name);
                if (methodHeader) {
                    if (methodHeader.rtype === 'promise') {
                        return (options) => cap.nativePromise(pluginName, prop.toString(), options);
                    }
                    else {
                        return (options, callback) => cap.nativeCallback(pluginName, prop.toString(), options, callback);
                    }
                }
                else if (impl) {
                    return (_a = impl[prop]) === null || _a === void 0 ? void 0 : _a.bind(impl);
                }
            }
            else if (impl) {
                return (_b = impl[prop]) === null || _b === void 0 ? void 0 : _b.bind(impl);
            }
            else {
                throw new CapacitorException(`"${pluginName}" plugin is not implemented on ${platform}`, ExceptionCode.Unimplemented);
            }
        };
        const createPluginMethodWrapper = (prop) => {
            let remove;
            const wrapper = (...args) => {
                const p = loadPluginImplementation().then(impl => {
                    const fn = createPluginMethod(impl, prop);
                    if (fn) {
                        const p = fn(...args);
                        remove = p === null || p === void 0 ? void 0 : p.remove;
                        return p;
                    }
                    else {
                        throw new CapacitorException(`"${pluginName}.${prop}()" is not implemented on ${platform}`, ExceptionCode.Unimplemented);
                    }
                });
                if (prop === 'addListener') {
                    p.remove = async () => remove();
                }
                return p;
            };
            // Some flair ✨
            wrapper.toString = () => `${prop.toString()}() { [capacitor code] }`;
            Object.defineProperty(wrapper, 'name', {
                value: prop,
                writable: false,
                configurable: false,
            });
            return wrapper;
        };
        const addListener = createPluginMethodWrapper('addListener');
        const removeListener = createPluginMethodWrapper('removeListener');
        const addListenerNative = (eventName, callback) => {
            const call = addListener({ eventName }, callback);
            const remove = async () => {
                const callbackId = await call;
                removeListener({
                    eventName,
                    callbackId,
                }, callback);
            };
            const p = new Promise(resolve => call.then(() => resolve({ remove })));
            p.remove = async () => {
                console.warn(`Using addListener() without 'await' is deprecated.`);
                await remove();
            };
            return p;
        };
        const proxy = new Proxy({}, {
            get(_, prop) {
                switch (prop) {
                    // https://github.com/facebook/react/issues/20030
                    case '$$typeof':
                        return undefined;
                    case 'toJSON':
                        return () => ({});
                    case 'addListener':
                        return pluginHeader ? addListenerNative : addListener;
                    case 'removeListener':
                        return removeListener;
                    default:
                        return createPluginMethodWrapper(prop);
                }
            },
        });
        Plugins[pluginName] = proxy;
        registeredPlugins.set(pluginName, {
            name: pluginName,
            proxy,
            platforms: new Set([
                ...Object.keys(jsImplementations),
                ...(pluginHeader ? [platform] : []),
            ]),
        });
        return proxy;
    };
    const registerPlugin = ((_e = capPlatforms === null || capPlatforms === void 0 ? void 0 : capPlatforms.currentPlatform) === null || _e === void 0 ? void 0 : _e.registerPlugin) || defaultRegisterPlugin;
    // Add in convertFileSrc for web, it will already be available in native context
    if (!cap.convertFileSrc) {
        cap.convertFileSrc = filePath => filePath;
    }
    cap.getPlatform = getPlatform;
    cap.handleError = handleError;
    cap.isNativePlatform = isNativePlatform;
    cap.isPluginAvailable = isPluginAvailable;
    cap.pluginMethodNoop = pluginMethodNoop;
    cap.registerPlugin = registerPlugin;
    cap.Exception = CapacitorException;
    cap.DEBUG = !!cap.DEBUG;
    cap.isLoggingEnabled = !!cap.isLoggingEnabled;
    // Deprecated props
    cap.platform = cap.getPlatform();
    cap.isNative = cap.isNativePlatform();
    return cap;
};
const initCapacitorGlobal = (win) => (win.Capacitor = createCapacitor(win));

const Capacitor = /*#__PURE__*/ initCapacitorGlobal(typeof globalThis !== 'undefined'
    ? globalThis
    : typeof self !== 'undefined'
        ? self
        : typeof window !== 'undefined'
            ? window
            : typeof global !== 'undefined'
                ? global
                : {});
const registerPlugin = Capacitor.registerPlugin;
/**
 * @deprecated Provided for backwards compatibility for Capacitor v2 plugins.
 * Capacitor v3 plugins should import the plugin directly. This "Plugins"
 * export is deprecated in v3, and will be removed in v4.
 */
Capacitor.Plugins;

/**
 * Base class web plugins should extend.
 */
class WebPlugin {
    constructor(config) {
        this.listeners = {};
        this.windowListeners = {};
        if (config) {
            // TODO: add link to upgrade guide
            console.warn(`Capacitor WebPlugin "${config.name}" config object was deprecated in v3 and will be removed in v4.`);
            this.config = config;
        }
    }
    addListener(eventName, listenerFunc) {
        const listeners = this.listeners[eventName];
        if (!listeners) {
            this.listeners[eventName] = [];
        }
        this.listeners[eventName].push(listenerFunc);
        // If we haven't added a window listener for this event and it requires one,
        // go ahead and add it
        const windowListener = this.windowListeners[eventName];
        if (windowListener && !windowListener.registered) {
            this.addWindowListener(windowListener);
        }
        const remove = async () => this.removeListener(eventName, listenerFunc);
        const p = Promise.resolve({ remove });
        Object.defineProperty(p, 'remove', {
            value: async () => {
                console.warn(`Using addListener() without 'await' is deprecated.`);
                await remove();
            },
        });
        return p;
    }
    async removeAllListeners() {
        this.listeners = {};
        for (const listener in this.windowListeners) {
            this.removeWindowListener(this.windowListeners[listener]);
        }
        this.windowListeners = {};
    }
    notifyListeners(eventName, data) {
        const listeners = this.listeners[eventName];
        if (listeners) {
            listeners.forEach(listener => listener(data));
        }
    }
    hasListeners(eventName) {
        return !!this.listeners[eventName].length;
    }
    registerWindowListener(windowEventName, pluginEventName) {
        this.windowListeners[pluginEventName] = {
            registered: false,
            windowEventName,
            pluginEventName,
            handler: event => {
                this.notifyListeners(pluginEventName, event);
            },
        };
    }
    unimplemented(msg = 'not implemented') {
        return new Capacitor.Exception(msg, ExceptionCode.Unimplemented);
    }
    unavailable(msg = 'not available') {
        return new Capacitor.Exception(msg, ExceptionCode.Unavailable);
    }
    async removeListener(eventName, listenerFunc) {
        const listeners = this.listeners[eventName];
        if (!listeners) {
            return;
        }
        const index = listeners.indexOf(listenerFunc);
        this.listeners[eventName].splice(index, 1);
        // If there are no more listeners for this type of event,
        // remove the window listener
        if (!this.listeners[eventName].length) {
            this.removeWindowListener(this.windowListeners[eventName]);
        }
    }
    addWindowListener(handle) {
        window.addEventListener(handle.windowEventName, handle.handler);
        handle.registered = true;
    }
    removeWindowListener(handle) {
        if (!handle) {
            return;
        }
        window.removeEventListener(handle.windowEventName, handle.handler);
        handle.registered = false;
    }
}

const Storage = registerPlugin('Storage', {
    web: () => import('./web-387a6d81.js').then(m => new m.StorageWeb()),
});

const useSearchBar = (configs) => {
  const { name, maxRecentSearches = 3 } = configs;
  const [recentSearches, setRecentSearches] = useState([]);
  const isMounted = useRef(true);
  if (!name) {
    throw new Error("useSearchBar needs a localStorage name");
  }
  const getRecentSearches = useCallback(async () => {
    try {
      const { value: lastSearchesString } = await Storage.get({ key: name });
      const lastSearches = lastSearchesString ? JSON.parse(lastSearchesString) : [];
      return lastSearches;
    } catch (e) {
      return [];
    }
  }, [name]);
  const saveSearchLocal = useCallback(async (search) => {
    if (maxRecentSearches <= 0) {
      return;
    }
    try {
      const { value: recentSearchesString } = await Storage.get({
        key: name
      });
      if (!recentSearchesString) {
        const searchArray = [search];
        await Storage.set({
          key: name,
          value: JSON.stringify(searchArray)
        });
        setRecentSearches(searchArray);
        return;
      }
      const recentSearches2 = JSON.parse(recentSearchesString);
      if (recentSearches2.includes(search))
        return;
      if (recentSearches2.length >= maxRecentSearches) {
        recentSearches2.pop();
      }
      recentSearches2.unshift(search);
      setRecentSearches(recentSearches2);
      await Storage.set({
        key: name,
        value: JSON.stringify(recentSearches2)
      });
    } catch (e) {
      throw new Error(`Cannot set ${name} in Storage`);
    }
  }, [maxRecentSearches, name]);
  const cleanSearchHistory = useCallback(async () => {
    try {
      await Storage.remove({ key: name });
      setRecentSearches([]);
    } catch (e) {
      throw new Error(`Cannot remove ${name} from Storage`);
    }
  }, [name]);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    (async () => {
      const lastSearches = await getRecentSearches();
      if (!isMounted.current)
        return;
      setRecentSearches(lastSearches);
    })();
  }, [getRecentSearches]);
  return useMemo(() => ({
    cleanSearchHistory,
    saveSearchLocal,
    recentSearches
  }), [cleanSearchHistory, recentSearches, saveSearchLocal]);
};
useSearchBar.defaultProps = {
  config: {
    maxRecentSearches: 3
  }
};

const Share = registerPlugin('Share', {
    web: () => import('./web-7a0c96d9.js').then(m => new m.ShareWeb()),
});

class ClipboardWeb extends WebPlugin {
    async write(options) {
        if (typeof navigator === 'undefined' || !navigator.clipboard) {
            throw this.unavailable('Clipboard API not available in this browser');
        }
        if (options.string !== undefined) {
            await this.writeText(options.string);
        }
        else if (options.url) {
            await this.writeText(options.url);
        }
        else if (options.image) {
            if (typeof ClipboardItem !== 'undefined') {
                try {
                    const blob = await (await fetch(options.image)).blob();
                    const clipboardItemInput = new ClipboardItem({ [blob.type]: blob });
                    await navigator.clipboard.write([clipboardItemInput]);
                }
                catch (err) {
                    throw new Error('Failed to write image');
                }
            }
            else {
                throw this.unavailable('Writing images to the clipboard is not supported in this browser');
            }
        }
        else {
            throw new Error('Nothing to write');
        }
    }
    async read() {
        if (typeof navigator === 'undefined' || !navigator.clipboard) {
            throw this.unavailable('Clipboard API not available in this browser');
        }
        if (typeof ClipboardItem !== 'undefined') {
            try {
                const clipboardItems = await navigator.clipboard.read();
                const type = clipboardItems[0].types[0];
                const clipboardBlob = await clipboardItems[0].getType(type);
                const data = await this._getBlobData(clipboardBlob, type);
                return { value: data, type };
            }
            catch (err) {
                return this.readText();
            }
        }
        else {
            return this.readText();
        }
    }
    async readText() {
        if (typeof navigator === 'undefined' ||
            !navigator.clipboard ||
            !navigator.clipboard.readText) {
            throw this.unavailable('Reading from clipboard not supported in this browser');
        }
        const text = await navigator.clipboard.readText();
        return { value: text, type: 'text/plain' };
    }
    async writeText(text) {
        if (typeof navigator === 'undefined' ||
            !navigator.clipboard ||
            !navigator.clipboard.writeText) {
            throw this.unavailable('Writting to clipboard not supported in this browser');
        }
        await navigator.clipboard.writeText(text);
    }
    _getBlobData(clipboardBlob, type) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            if (type.includes('image')) {
                reader.readAsDataURL(clipboardBlob);
            }
            else {
                reader.readAsText(clipboardBlob);
            }
            reader.onloadend = () => {
                const r = reader.result;
                resolve(r);
            };
            reader.onerror = e => {
                reject(e);
            };
        });
    }
}

const Clipboard = registerPlugin('Clipboard', {
    web: () => new ClipboardWeb(),
});

const mapTransformX = (props) => {
  const { position } = props;
  const right = position.includes("right");
  const center = position.includes("center");
  const left = position.includes("left");
  if (right)
    return "translateX(calc(100% + 10px))";
  if (left)
    return "translateX(calc(-100% - 10px))";
  if (center)
    return "translateX(-50%)";
  return "translateX(0)";
};
const mapLeft = (props) => {
  const { position } = props;
  const center = position.includes("center");
  const left = position.includes("left");
  if (left)
    return "0";
  if (center)
    return "50%";
  return "initial";
};
const ShareProductButtonStyled = styled.div`
  cursor: pointer;
  position: relative;
  display: inline-flex;

  .ShareButton {
    &__tooltip {
      background: #000000;
      color: #ffffff;
      padding: 4px 8px;
      border-radius: 4px;
      position: absolute;
      white-space: nowrap;
      font-size: 12px;
      top: ${(props) => props.position.includes("top") ? "calc(-100% - 10px)" : "initial"};
      right: ${(props) => props.position.includes("right") ? "0" : "initial"};
      bottom: ${(props) => props.position.includes("bottom") ? "calc(-100% - 10px)" : "initial"};
      left: ${mapLeft};
      transform: ${mapTransformX};
      z-index: 2;
    }
  }

  .Icon {
    svg {
      width: 100%;
    }
  }
`;

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }
var ShareSVG = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends$2({
  width: "17",
  height: "22",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M8.906.5L5.164 4.242l1.124 1.124 1.823-1.823V15.14h1.59V3.543l1.823 1.823 1.125-1.124L8.906.5zM3.341 7.19A2.397 2.397 0 00.956 9.574v9.54A2.397 2.397 0 003.341 21.5h11.13a2.397 2.397 0 002.386-2.385v-9.54a2.397 2.397 0 00-2.385-2.386h-2.386v1.59h2.386c.449 0 .795.346.795.796v9.54c0 .45-.346.795-.795.795H3.342a.782.782 0 01-.796-.795v-9.54c0-.45.346-.795.795-.795h2.385V7.19H3.341z",
  fill: "#111"
})));

const ShareButton = (props) => {
  const { children, className, config } = props;
  const { onCancel, onSuccess, onError, onNotSupported } = props;
  const { tooltip = defaultObject } = props;
  const { successMessage = "Copied to the clipboard!", errorMessage } = tooltip;
  const { className: tooltipClassName = "", position = "top-center" } = tooltip;
  const { time = 1e3 } = tooltip;
  const [showTooltip, setShowTooltip] = useState(false);
  const timeoutRef = useRef();
  const handleClick = async () => {
    try {
      await Share.share(config);
      onSuccess == null ? void 0 : onSuccess();
    } catch (e) {
      if (e.message === "Share canceled") {
        onCancel == null ? void 0 : onCancel(config.url);
        return;
      }
      notSupportedHandler(config.url);
    }
  };
  const showTooltipHandler = () => {
    setShowTooltip(true);
    const tooltipTimeout = setTimeout(() => {
      hideTooltipHandler();
    }, time);
    timeoutRef.current = tooltipTimeout;
  };
  const hideTooltipHandler = () => {
    setShowTooltip(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };
  const notSupportedHandler = async (url) => {
    try {
      await Clipboard.write({
        url
      });
      showTooltipHandler();
      onNotSupported == null ? void 0 : onNotSupported();
    } catch (e) {
      onError == null ? void 0 : onError("Browser does not support the share API or clipboard API");
      if (!errorMessage)
        return;
      showTooltipHandler();
    }
  };
  if (Children.count(children) > 1) {
    console.error("You must provide only one children element");
    return null;
  }
  return /* @__PURE__ */ React.createElement(ShareProductButtonStyled, {
    className: `ShareButton ${className}`,
    position: getItemPositioning("top-right", position)
  }, /* @__PURE__ */ React.createElement(Clickable, {
    onClick: handleClick
  }, children != null ? children : /* @__PURE__ */ React.createElement(ShareSVG, null)), showTooltip ? /* @__PURE__ */ React.createElement("span", {
    className: `ShareButton__tooltip ${tooltipClassName}`
  }, successMessage) : null);
};
ShareButton.defaultProps = {
  className: "",
  tooltip: {
    successMessage: "Copied to the clipboard!",
    className: "",
    position: "top-center",
    errorMessage: void 0,
    time: 1e3
  }
};

function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1.apply(this, arguments);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function _typeof(obj) {
      return typeof obj;
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _possibleConstructorReturn(self, call) {
  if (call && (_typeof(call) === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }

  return _assertThisInitialized(self);
}

function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();
  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
        result;

    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;
      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var initialState = {
  animating: false,
  autoplaying: null,
  currentDirection: 0,
  currentLeft: null,
  currentSlide: 0,
  direction: 1,
  dragging: false,
  edgeDragged: false,
  initialized: false,
  lazyLoadedList: [],
  listHeight: null,
  listWidth: null,
  scrolling: false,
  slideCount: null,
  slideHeight: null,
  slideWidth: null,
  swipeLeft: null,
  swiped: false,
  // used by swipeEvent. differentites between touch and swipe.
  swiping: false,
  touchObject: {
    startX: 0,
    startY: 0,
    curX: 0,
    curY: 0
  },
  trackStyle: {},
  trackWidth: 0,
  targetSlide: 0
};

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$2(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject$2;

/** Detect free variable `global` from Node.js. */

var freeGlobal$1 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal$1;

var freeGlobal = _freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$2 = freeGlobal || freeSelf || Function('return this')();

var _root = root$2;

var root$1 = _root;

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now$1 = function() {
  return root$1.Date.now();
};

var now_1 = now$1;

/** Used to match a single whitespace character. */

var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex$1(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex$1;

var trimmedEndIndex = _trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim$1(string) {
  return string
    ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim$1;

var root = _root;

/** Built-in value references. */
var Symbol$3 = root.Symbol;

var _Symbol = Symbol$3;

var Symbol$2 = _Symbol;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto$1.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$2 ? Symbol$2.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag$1(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

var _getRawTag = getRawTag$1;

/** Used for built-in method references. */

var objectProto = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString$1(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString$1;

var Symbol$1 = _Symbol,
    getRawTag = _getRawTag,
    objectToString = _objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol$1 ? Symbol$1.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag$1(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

var _baseGetTag = baseGetTag$1;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$1(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike$1;

var baseGetTag = _baseGetTag,
    isObjectLike = isObjectLike_1;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol$1(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && baseGetTag(value) == symbolTag);
}

var isSymbol_1 = isSymbol$1;

var baseTrim = _baseTrim,
    isObject$1 = isObject_1,
    isSymbol = isSymbol_1;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber$1(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject$1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject$1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber$1;

var isObject = isObject_1,
    now = now_1,
    toNumber = toNumber_1;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max,
    nativeMin = Math.min;

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  wait = toNumber(wait) || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now());
  }

  function debounced() {
    var time = now(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

var debounce_1 = debounce;

function clamp(number, lowerBound, upperBound) {
  return Math.max(lowerBound, Math.min(number, upperBound));
}
var safePreventDefault = function safePreventDefault(event) {
  var passiveEvents = ["onTouchStart", "onTouchMove", "onWheel"];

  if (!passiveEvents.includes(event._reactName)) {
    event.preventDefault();
  }
};
var getOnDemandLazySlides = function getOnDemandLazySlides(spec) {
  var onDemandSlides = [];
  var startIndex = lazyStartIndex(spec);
  var endIndex = lazyEndIndex(spec);

  for (var slideIndex = startIndex; slideIndex < endIndex; slideIndex++) {
    if (spec.lazyLoadedList.indexOf(slideIndex) < 0) {
      onDemandSlides.push(slideIndex);
    }
  }

  return onDemandSlides;
}; // return list of slides that need to be present

var lazyStartIndex = function lazyStartIndex(spec) {
  return spec.currentSlide - lazySlidesOnLeft(spec);
};
var lazyEndIndex = function lazyEndIndex(spec) {
  return spec.currentSlide + lazySlidesOnRight(spec);
};
var lazySlidesOnLeft = function lazySlidesOnLeft(spec) {
  return spec.centerMode ? Math.floor(spec.slidesToShow / 2) + (parseInt(spec.centerPadding) > 0 ? 1 : 0) : 0;
};
var lazySlidesOnRight = function lazySlidesOnRight(spec) {
  return spec.centerMode ? Math.floor((spec.slidesToShow - 1) / 2) + 1 + (parseInt(spec.centerPadding) > 0 ? 1 : 0) : spec.slidesToShow;
}; // get width of an element

var getWidth = function getWidth(elem) {
  return elem && elem.offsetWidth || 0;
};
var getHeight = function getHeight(elem) {
  return elem && elem.offsetHeight || 0;
};
var getSwipeDirection = function getSwipeDirection(touchObject) {
  var verticalSwiping = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var xDist, yDist, r, swipeAngle;
  xDist = touchObject.startX - touchObject.curX;
  yDist = touchObject.startY - touchObject.curY;
  r = Math.atan2(yDist, xDist);
  swipeAngle = Math.round(r * 180 / Math.PI);

  if (swipeAngle < 0) {
    swipeAngle = 360 - Math.abs(swipeAngle);
  }

  if (swipeAngle <= 45 && swipeAngle >= 0 || swipeAngle <= 360 && swipeAngle >= 315) {
    return "left";
  }

  if (swipeAngle >= 135 && swipeAngle <= 225) {
    return "right";
  }

  if (verticalSwiping === true) {
    if (swipeAngle >= 35 && swipeAngle <= 135) {
      return "up";
    } else {
      return "down";
    }
  }

  return "vertical";
}; // whether or not we can go next

var canGoNext = function canGoNext(spec) {
  var canGo = true;

  if (!spec.infinite) {
    if (spec.centerMode && spec.currentSlide >= spec.slideCount - 1) {
      canGo = false;
    } else if (spec.slideCount <= spec.slidesToShow || spec.currentSlide >= spec.slideCount - spec.slidesToShow) {
      canGo = false;
    }
  }

  return canGo;
}; // given an object and a list of keys, return new object with given keys

var extractObject = function extractObject(spec, keys) {
  var newObject = {};
  keys.forEach(function (key) {
    return newObject[key] = spec[key];
  });
  return newObject;
}; // get initialized state

var initializedState = function initializedState(spec) {
  // spec also contains listRef, trackRef
  var slideCount = React.Children.count(spec.children);
  var listNode = spec.listRef;
  var listWidth = Math.ceil(getWidth(listNode));
  var trackNode = spec.trackRef && spec.trackRef.node;
  var trackWidth = Math.ceil(getWidth(trackNode));
  var slideWidth;

  if (!spec.vertical) {
    var centerPaddingAdj = spec.centerMode && parseInt(spec.centerPadding) * 2;

    if (typeof spec.centerPadding === "string" && spec.centerPadding.slice(-1) === "%") {
      centerPaddingAdj *= listWidth / 100;
    }

    slideWidth = Math.ceil((listWidth - centerPaddingAdj) / spec.slidesToShow);
  } else {
    slideWidth = listWidth;
  }

  var slideHeight = listNode && getHeight(listNode.querySelector('[data-index="0"]'));
  var listHeight = slideHeight * spec.slidesToShow;
  var currentSlide = spec.currentSlide === undefined ? spec.initialSlide : spec.currentSlide;

  if (spec.rtl && spec.currentSlide === undefined) {
    currentSlide = slideCount - 1 - spec.initialSlide;
  }

  var lazyLoadedList = spec.lazyLoadedList || [];
  var slidesToLoad = getOnDemandLazySlides(_objectSpread2(_objectSpread2({}, spec), {}, {
    currentSlide: currentSlide,
    lazyLoadedList: lazyLoadedList
  }));
  lazyLoadedList = lazyLoadedList.concat(slidesToLoad);
  var state = {
    slideCount: slideCount,
    slideWidth: slideWidth,
    listWidth: listWidth,
    trackWidth: trackWidth,
    currentSlide: currentSlide,
    slideHeight: slideHeight,
    listHeight: listHeight,
    lazyLoadedList: lazyLoadedList
  };

  if (spec.autoplaying === null && spec.autoplay) {
    state["autoplaying"] = "playing";
  }

  return state;
};
var slideHandler = function slideHandler(spec) {
  var waitForAnimate = spec.waitForAnimate,
      animating = spec.animating,
      fade = spec.fade,
      infinite = spec.infinite,
      index = spec.index,
      slideCount = spec.slideCount,
      lazyLoad = spec.lazyLoad,
      currentSlide = spec.currentSlide,
      centerMode = spec.centerMode,
      slidesToScroll = spec.slidesToScroll,
      slidesToShow = spec.slidesToShow,
      useCSS = spec.useCSS;
  var lazyLoadedList = spec.lazyLoadedList;
  if (waitForAnimate && animating) return {};
  var animationSlide = index,
      finalSlide,
      animationLeft,
      finalLeft;
  var state = {},
      nextState = {};
  var targetSlide = infinite ? index : clamp(index, 0, slideCount - 1);

  if (fade) {
    if (!infinite && (index < 0 || index >= slideCount)) return {};

    if (index < 0) {
      animationSlide = index + slideCount;
    } else if (index >= slideCount) {
      animationSlide = index - slideCount;
    }

    if (lazyLoad && lazyLoadedList.indexOf(animationSlide) < 0) {
      lazyLoadedList = lazyLoadedList.concat(animationSlide);
    }

    state = {
      animating: true,
      currentSlide: animationSlide,
      lazyLoadedList: lazyLoadedList,
      targetSlide: animationSlide
    };
    nextState = {
      animating: false,
      targetSlide: animationSlide
    };
  } else {
    finalSlide = animationSlide;

    if (animationSlide < 0) {
      finalSlide = animationSlide + slideCount;
      if (!infinite) finalSlide = 0;else if (slideCount % slidesToScroll !== 0) finalSlide = slideCount - slideCount % slidesToScroll;
    } else if (!canGoNext(spec) && animationSlide > currentSlide) {
      animationSlide = finalSlide = currentSlide;
    } else if (centerMode && animationSlide >= slideCount) {
      animationSlide = infinite ? slideCount : slideCount - 1;
      finalSlide = infinite ? 0 : slideCount - 1;
    } else if (animationSlide >= slideCount) {
      finalSlide = animationSlide - slideCount;
      if (!infinite) finalSlide = slideCount - slidesToShow;else if (slideCount % slidesToScroll !== 0) finalSlide = 0;
    }

    if (!infinite && animationSlide + slidesToShow >= slideCount) {
      finalSlide = slideCount - slidesToShow;
    }

    animationLeft = getTrackLeft(_objectSpread2(_objectSpread2({}, spec), {}, {
      slideIndex: animationSlide
    }));
    finalLeft = getTrackLeft(_objectSpread2(_objectSpread2({}, spec), {}, {
      slideIndex: finalSlide
    }));

    if (!infinite) {
      if (animationLeft === finalLeft) animationSlide = finalSlide;
      animationLeft = finalLeft;
    }

    if (lazyLoad) {
      lazyLoadedList = lazyLoadedList.concat(getOnDemandLazySlides(_objectSpread2(_objectSpread2({}, spec), {}, {
        currentSlide: animationSlide
      })));
    }

    if (!useCSS) {
      state = {
        currentSlide: finalSlide,
        trackStyle: getTrackCSS(_objectSpread2(_objectSpread2({}, spec), {}, {
          left: finalLeft
        })),
        lazyLoadedList: lazyLoadedList,
        targetSlide: targetSlide
      };
    } else {
      state = {
        animating: true,
        currentSlide: finalSlide,
        trackStyle: getTrackAnimateCSS(_objectSpread2(_objectSpread2({}, spec), {}, {
          left: animationLeft
        })),
        lazyLoadedList: lazyLoadedList,
        targetSlide: targetSlide
      };
      nextState = {
        animating: false,
        currentSlide: finalSlide,
        trackStyle: getTrackCSS(_objectSpread2(_objectSpread2({}, spec), {}, {
          left: finalLeft
        })),
        swipeLeft: null,
        targetSlide: targetSlide
      };
    }
  }

  return {
    state: state,
    nextState: nextState
  };
};
var changeSlide = function changeSlide(spec, options) {
  var indexOffset, previousInt, slideOffset, unevenOffset, targetSlide;
  var slidesToScroll = spec.slidesToScroll,
      slidesToShow = spec.slidesToShow,
      slideCount = spec.slideCount,
      currentSlide = spec.currentSlide,
      previousTargetSlide = spec.targetSlide,
      lazyLoad = spec.lazyLoad,
      infinite = spec.infinite;
  unevenOffset = slideCount % slidesToScroll !== 0;
  indexOffset = unevenOffset ? 0 : (slideCount - currentSlide) % slidesToScroll;

  if (options.message === "previous") {
    slideOffset = indexOffset === 0 ? slidesToScroll : slidesToShow - indexOffset;
    targetSlide = currentSlide - slideOffset;

    if (lazyLoad && !infinite) {
      previousInt = currentSlide - slideOffset;
      targetSlide = previousInt === -1 ? slideCount - 1 : previousInt;
    }

    if (!infinite) {
      targetSlide = previousTargetSlide - slidesToScroll;
    }
  } else if (options.message === "next") {
    slideOffset = indexOffset === 0 ? slidesToScroll : indexOffset;
    targetSlide = currentSlide + slideOffset;

    if (lazyLoad && !infinite) {
      targetSlide = (currentSlide + slidesToScroll) % slideCount + indexOffset;
    }

    if (!infinite) {
      targetSlide = previousTargetSlide + slidesToScroll;
    }
  } else if (options.message === "dots") {
    // Click on dots
    targetSlide = options.index * options.slidesToScroll;
  } else if (options.message === "children") {
    // Click on the slides
    targetSlide = options.index;

    if (infinite) {
      var direction = siblingDirection(_objectSpread2(_objectSpread2({}, spec), {}, {
        targetSlide: targetSlide
      }));

      if (targetSlide > options.currentSlide && direction === "left") {
        targetSlide = targetSlide - slideCount;
      } else if (targetSlide < options.currentSlide && direction === "right") {
        targetSlide = targetSlide + slideCount;
      }
    }
  } else if (options.message === "index") {
    targetSlide = Number(options.index);
  }

  return targetSlide;
};
var keyHandler = function keyHandler(e, accessibility, rtl) {
  if (e.target.tagName.match("TEXTAREA|INPUT|SELECT") || !accessibility) return "";
  if (e.keyCode === 37) return rtl ? "next" : "previous";
  if (e.keyCode === 39) return rtl ? "previous" : "next";
  return "";
};
var swipeStart = function swipeStart(e, swipe, draggable) {
  e.target.tagName === "IMG" && safePreventDefault(e);
  if (!swipe || !draggable && e.type.indexOf("mouse") !== -1) return "";
  return {
    dragging: true,
    touchObject: {
      startX: e.touches ? e.touches[0].pageX : e.clientX,
      startY: e.touches ? e.touches[0].pageY : e.clientY,
      curX: e.touches ? e.touches[0].pageX : e.clientX,
      curY: e.touches ? e.touches[0].pageY : e.clientY
    }
  };
};
var swipeMove = function swipeMove(e, spec) {
  // spec also contains, trackRef and slideIndex
  var scrolling = spec.scrolling,
      animating = spec.animating,
      vertical = spec.vertical,
      swipeToSlide = spec.swipeToSlide,
      verticalSwiping = spec.verticalSwiping,
      rtl = spec.rtl,
      currentSlide = spec.currentSlide,
      edgeFriction = spec.edgeFriction,
      edgeDragged = spec.edgeDragged,
      onEdge = spec.onEdge,
      swiped = spec.swiped,
      swiping = spec.swiping,
      slideCount = spec.slideCount,
      slidesToScroll = spec.slidesToScroll,
      infinite = spec.infinite,
      touchObject = spec.touchObject,
      swipeEvent = spec.swipeEvent,
      listHeight = spec.listHeight,
      listWidth = spec.listWidth;
  if (scrolling) return;
  if (animating) return safePreventDefault(e);
  if (vertical && swipeToSlide && verticalSwiping) safePreventDefault(e);
  var swipeLeft,
      state = {};
  var curLeft = getTrackLeft(spec);
  touchObject.curX = e.touches ? e.touches[0].pageX : e.clientX;
  touchObject.curY = e.touches ? e.touches[0].pageY : e.clientY;
  touchObject.swipeLength = Math.round(Math.sqrt(Math.pow(touchObject.curX - touchObject.startX, 2)));
  var verticalSwipeLength = Math.round(Math.sqrt(Math.pow(touchObject.curY - touchObject.startY, 2)));

  if (!verticalSwiping && !swiping && verticalSwipeLength > 10) {
    return {
      scrolling: true
    };
  }

  if (verticalSwiping) touchObject.swipeLength = verticalSwipeLength;
  var positionOffset = (!rtl ? 1 : -1) * (touchObject.curX > touchObject.startX ? 1 : -1);
  if (verticalSwiping) positionOffset = touchObject.curY > touchObject.startY ? 1 : -1;
  var dotCount = Math.ceil(slideCount / slidesToScroll);
  var swipeDirection = getSwipeDirection(spec.touchObject, verticalSwiping);
  var touchSwipeLength = touchObject.swipeLength;

  if (!infinite) {
    if (currentSlide === 0 && (swipeDirection === "right" || swipeDirection === "down") || currentSlide + 1 >= dotCount && (swipeDirection === "left" || swipeDirection === "up") || !canGoNext(spec) && (swipeDirection === "left" || swipeDirection === "up")) {
      touchSwipeLength = touchObject.swipeLength * edgeFriction;

      if (edgeDragged === false && onEdge) {
        onEdge(swipeDirection);
        state["edgeDragged"] = true;
      }
    }
  }

  if (!swiped && swipeEvent) {
    swipeEvent(swipeDirection);
    state["swiped"] = true;
  }

  if (!vertical) {
    if (!rtl) {
      swipeLeft = curLeft + touchSwipeLength * positionOffset;
    } else {
      swipeLeft = curLeft - touchSwipeLength * positionOffset;
    }
  } else {
    swipeLeft = curLeft + touchSwipeLength * (listHeight / listWidth) * positionOffset;
  }

  if (verticalSwiping) {
    swipeLeft = curLeft + touchSwipeLength * positionOffset;
  }

  state = _objectSpread2(_objectSpread2({}, state), {}, {
    touchObject: touchObject,
    swipeLeft: swipeLeft,
    trackStyle: getTrackCSS(_objectSpread2(_objectSpread2({}, spec), {}, {
      left: swipeLeft
    }))
  });

  if (Math.abs(touchObject.curX - touchObject.startX) < Math.abs(touchObject.curY - touchObject.startY) * 0.8) {
    return state;
  }

  if (touchObject.swipeLength > 10) {
    state["swiping"] = true;
    safePreventDefault(e);
  }

  return state;
};
var swipeEnd = function swipeEnd(e, spec) {
  var dragging = spec.dragging,
      swipe = spec.swipe,
      touchObject = spec.touchObject,
      listWidth = spec.listWidth,
      touchThreshold = spec.touchThreshold,
      verticalSwiping = spec.verticalSwiping,
      listHeight = spec.listHeight,
      swipeToSlide = spec.swipeToSlide,
      scrolling = spec.scrolling,
      onSwipe = spec.onSwipe,
      targetSlide = spec.targetSlide,
      currentSlide = spec.currentSlide,
      infinite = spec.infinite;

  if (!dragging) {
    if (swipe) safePreventDefault(e);
    return {};
  }

  var minSwipe = verticalSwiping ? listHeight / touchThreshold : listWidth / touchThreshold;
  var swipeDirection = getSwipeDirection(touchObject, verticalSwiping); // reset the state of touch related state variables.

  var state = {
    dragging: false,
    edgeDragged: false,
    scrolling: false,
    swiping: false,
    swiped: false,
    swipeLeft: null,
    touchObject: {}
  };

  if (scrolling) {
    return state;
  }

  if (!touchObject.swipeLength) {
    return state;
  }

  if (touchObject.swipeLength > minSwipe) {
    safePreventDefault(e);

    if (onSwipe) {
      onSwipe(swipeDirection);
    }

    var slideCount, newSlide;
    var activeSlide = infinite ? currentSlide : targetSlide;

    switch (swipeDirection) {
      case "left":
      case "up":
        newSlide = activeSlide + getSlideCount(spec);
        slideCount = swipeToSlide ? checkNavigable(spec, newSlide) : newSlide;
        state["currentDirection"] = 0;
        break;

      case "right":
      case "down":
        newSlide = activeSlide - getSlideCount(spec);
        slideCount = swipeToSlide ? checkNavigable(spec, newSlide) : newSlide;
        state["currentDirection"] = 1;
        break;

      default:
        slideCount = activeSlide;
    }

    state["triggerSlideHandler"] = slideCount;
  } else {
    // Adjust the track back to it's original position.
    var currentLeft = getTrackLeft(spec);
    state["trackStyle"] = getTrackAnimateCSS(_objectSpread2(_objectSpread2({}, spec), {}, {
      left: currentLeft
    }));
  }

  return state;
};
var getNavigableIndexes = function getNavigableIndexes(spec) {
  var max = spec.infinite ? spec.slideCount * 2 : spec.slideCount;
  var breakpoint = spec.infinite ? spec.slidesToShow * -1 : 0;
  var counter = spec.infinite ? spec.slidesToShow * -1 : 0;
  var indexes = [];

  while (breakpoint < max) {
    indexes.push(breakpoint);
    breakpoint = counter + spec.slidesToScroll;
    counter += Math.min(spec.slidesToScroll, spec.slidesToShow);
  }

  return indexes;
};
var checkNavigable = function checkNavigable(spec, index) {
  var navigables = getNavigableIndexes(spec);
  var prevNavigable = 0;

  if (index > navigables[navigables.length - 1]) {
    index = navigables[navigables.length - 1];
  } else {
    for (var n in navigables) {
      if (index < navigables[n]) {
        index = prevNavigable;
        break;
      }

      prevNavigable = navigables[n];
    }
  }

  return index;
};
var getSlideCount = function getSlideCount(spec) {
  var centerOffset = spec.centerMode ? spec.slideWidth * Math.floor(spec.slidesToShow / 2) : 0;

  if (spec.swipeToSlide) {
    var swipedSlide;
    var slickList = spec.listRef;
    var slides = slickList.querySelectorAll && slickList.querySelectorAll(".slick-slide") || [];
    Array.from(slides).every(function (slide) {
      if (!spec.vertical) {
        if (slide.offsetLeft - centerOffset + getWidth(slide) / 2 > spec.swipeLeft * -1) {
          swipedSlide = slide;
          return false;
        }
      } else {
        if (slide.offsetTop + getHeight(slide) / 2 > spec.swipeLeft * -1) {
          swipedSlide = slide;
          return false;
        }
      }

      return true;
    });

    if (!swipedSlide) {
      return 0;
    }

    var currentIndex = spec.rtl === true ? spec.slideCount - spec.currentSlide : spec.currentSlide;
    var slidesTraversed = Math.abs(swipedSlide.dataset.index - currentIndex) || 1;
    return slidesTraversed;
  } else {
    return spec.slidesToScroll;
  }
};
var checkSpecKeys = function checkSpecKeys(spec, keysArray) {
  return (// eslint-disable-next-line no-prototype-builtins
    keysArray.reduce(function (value, key) {
      return value && spec.hasOwnProperty(key);
    }, true) ? null : console.error("Keys Missing:", spec)
  );
};
var getTrackCSS = function getTrackCSS(spec) {
  checkSpecKeys(spec, ["left", "variableWidth", "slideCount", "slidesToShow", "slideWidth"]);
  var trackWidth, trackHeight;
  var trackChildren = spec.slideCount + 2 * spec.slidesToShow;

  if (!spec.vertical) {
    trackWidth = getTotalSlides(spec) * spec.slideWidth;
  } else {
    trackHeight = trackChildren * spec.slideHeight;
  }

  var style = {
    opacity: 1,
    transition: "",
    WebkitTransition: ""
  };

  if (spec.useTransform) {
    var WebkitTransform = !spec.vertical ? "translate3d(" + spec.left + "px, 0px, 0px)" : "translate3d(0px, " + spec.left + "px, 0px)";
    var transform = !spec.vertical ? "translate3d(" + spec.left + "px, 0px, 0px)" : "translate3d(0px, " + spec.left + "px, 0px)";
    var msTransform = !spec.vertical ? "translateX(" + spec.left + "px)" : "translateY(" + spec.left + "px)";
    style = _objectSpread2(_objectSpread2({}, style), {}, {
      WebkitTransform: WebkitTransform,
      transform: transform,
      msTransform: msTransform
    });
  } else {
    if (spec.vertical) {
      style["top"] = spec.left;
    } else {
      style["left"] = spec.left;
    }
  }

  if (spec.fade) style = {
    opacity: 1
  };
  if (trackWidth) style.width = trackWidth;
  if (trackHeight) style.height = trackHeight; // Fallback for IE8

  if (window && !window.addEventListener && window.attachEvent) {
    if (!spec.vertical) {
      style.marginLeft = spec.left + "px";
    } else {
      style.marginTop = spec.left + "px";
    }
  }

  return style;
};
var getTrackAnimateCSS = function getTrackAnimateCSS(spec) {
  checkSpecKeys(spec, ["left", "variableWidth", "slideCount", "slidesToShow", "slideWidth", "speed", "cssEase"]);
  var style = getTrackCSS(spec); // useCSS is true by default so it can be undefined

  if (spec.useTransform) {
    style.WebkitTransition = "-webkit-transform " + spec.speed + "ms " + spec.cssEase;
    style.transition = "transform " + spec.speed + "ms " + spec.cssEase;
  } else {
    if (spec.vertical) {
      style.transition = "top " + spec.speed + "ms " + spec.cssEase;
    } else {
      style.transition = "left " + spec.speed + "ms " + spec.cssEase;
    }
  }

  return style;
};
var getTrackLeft = function getTrackLeft(spec) {
  if (spec.unslick) {
    return 0;
  }

  checkSpecKeys(spec, ["slideIndex", "trackRef", "infinite", "centerMode", "slideCount", "slidesToShow", "slidesToScroll", "slideWidth", "listWidth", "variableWidth", "slideHeight"]);
  var slideIndex = spec.slideIndex,
      trackRef = spec.trackRef,
      infinite = spec.infinite,
      centerMode = spec.centerMode,
      slideCount = spec.slideCount,
      slidesToShow = spec.slidesToShow,
      slidesToScroll = spec.slidesToScroll,
      slideWidth = spec.slideWidth,
      listWidth = spec.listWidth,
      variableWidth = spec.variableWidth,
      slideHeight = spec.slideHeight,
      fade = spec.fade,
      vertical = spec.vertical;
  var slideOffset = 0;
  var targetLeft;
  var targetSlide;
  var verticalOffset = 0;

  if (fade || spec.slideCount === 1) {
    return 0;
  }

  var slidesToOffset = 0;

  if (infinite) {
    slidesToOffset = -getPreClones(spec); // bring active slide to the beginning of visual area
    // if next scroll doesn't have enough children, just reach till the end of original slides instead of shifting slidesToScroll children

    if (slideCount % slidesToScroll !== 0 && slideIndex + slidesToScroll > slideCount) {
      slidesToOffset = -(slideIndex > slideCount ? slidesToShow - (slideIndex - slideCount) : slideCount % slidesToScroll);
    } // shift current slide to center of the frame


    if (centerMode) {
      slidesToOffset += parseInt(slidesToShow / 2);
    }
  } else {
    if (slideCount % slidesToScroll !== 0 && slideIndex + slidesToScroll > slideCount) {
      slidesToOffset = slidesToShow - slideCount % slidesToScroll;
    }

    if (centerMode) {
      slidesToOffset = parseInt(slidesToShow / 2);
    }
  }

  slideOffset = slidesToOffset * slideWidth;
  verticalOffset = slidesToOffset * slideHeight;

  if (!vertical) {
    targetLeft = slideIndex * slideWidth * -1 + slideOffset;
  } else {
    targetLeft = slideIndex * slideHeight * -1 + verticalOffset;
  }

  if (variableWidth === true) {
    var targetSlideIndex;
    var trackElem = trackRef && trackRef.node;
    targetSlideIndex = slideIndex + getPreClones(spec);
    targetSlide = trackElem && trackElem.childNodes[targetSlideIndex];
    targetLeft = targetSlide ? targetSlide.offsetLeft * -1 : 0;

    if (centerMode === true) {
      targetSlideIndex = infinite ? slideIndex + getPreClones(spec) : slideIndex;
      targetSlide = trackElem && trackElem.children[targetSlideIndex];
      targetLeft = 0;

      for (var slide = 0; slide < targetSlideIndex; slide++) {
        targetLeft -= trackElem && trackElem.children[slide] && trackElem.children[slide].offsetWidth;
      }

      targetLeft -= parseInt(spec.centerPadding);
      targetLeft += targetSlide && (listWidth - targetSlide.offsetWidth) / 2;
    }
  }

  return targetLeft;
};
var getPreClones = function getPreClones(spec) {
  if (spec.unslick || !spec.infinite) {
    return 0;
  }

  if (spec.variableWidth) {
    return spec.slideCount;
  }

  return spec.slidesToShow + (spec.centerMode ? 1 : 0);
};
var getPostClones = function getPostClones(spec) {
  if (spec.unslick || !spec.infinite) {
    return 0;
  }

  return spec.slideCount;
};
var getTotalSlides = function getTotalSlides(spec) {
  return spec.slideCount === 1 ? 1 : getPreClones(spec) + spec.slideCount + getPostClones(spec);
};
var siblingDirection = function siblingDirection(spec) {
  if (spec.targetSlide > spec.currentSlide) {
    if (spec.targetSlide > spec.currentSlide + slidesOnRight(spec)) {
      return "left";
    }

    return "right";
  } else {
    if (spec.targetSlide < spec.currentSlide - slidesOnLeft(spec)) {
      return "right";
    }

    return "left";
  }
};
var slidesOnRight = function slidesOnRight(_ref) {
  var slidesToShow = _ref.slidesToShow,
      centerMode = _ref.centerMode,
      rtl = _ref.rtl,
      centerPadding = _ref.centerPadding;

  // returns no of slides on the right of active slide
  if (centerMode) {
    var right = (slidesToShow - 1) / 2 + 1;
    if (parseInt(centerPadding) > 0) right += 1;
    if (rtl && slidesToShow % 2 === 0) right += 1;
    return right;
  }

  if (rtl) {
    return 0;
  }

  return slidesToShow - 1;
};
var slidesOnLeft = function slidesOnLeft(_ref2) {
  var slidesToShow = _ref2.slidesToShow,
      centerMode = _ref2.centerMode,
      rtl = _ref2.rtl,
      centerPadding = _ref2.centerPadding;

  // returns no of slides on the left of active slide
  if (centerMode) {
    var left = (slidesToShow - 1) / 2 + 1;
    if (parseInt(centerPadding) > 0) left += 1;
    if (!rtl && slidesToShow % 2 === 0) left += 1;
    return left;
  }

  if (rtl) {
    return slidesToShow - 1;
  }

  return 0;
};
var canUseDOM = function canUseDOM() {
  return !!(typeof window !== "undefined" && window.document && window.document.createElement);
};

var getSlideClasses = function getSlideClasses(spec) {
  var slickActive, slickCenter, slickCloned;
  var centerOffset, index;

  if (spec.rtl) {
    index = spec.slideCount - 1 - spec.index;
  } else {
    index = spec.index;
  }

  slickCloned = index < 0 || index >= spec.slideCount;

  if (spec.centerMode) {
    centerOffset = Math.floor(spec.slidesToShow / 2);
    slickCenter = (index - spec.currentSlide) % spec.slideCount === 0;

    if (index > spec.currentSlide - centerOffset - 1 && index <= spec.currentSlide + centerOffset) {
      slickActive = true;
    }
  } else {
    slickActive = spec.currentSlide <= index && index < spec.currentSlide + spec.slidesToShow;
  }

  var focusedSlide;

  if (spec.targetSlide < 0) {
    focusedSlide = spec.targetSlide + spec.slideCount;
  } else if (spec.targetSlide >= spec.slideCount) {
    focusedSlide = spec.targetSlide - spec.slideCount;
  } else {
    focusedSlide = spec.targetSlide;
  }

  var slickCurrent = index === focusedSlide;
  return {
    "slick-slide": true,
    "slick-active": slickActive,
    "slick-center": slickCenter,
    "slick-cloned": slickCloned,
    "slick-current": slickCurrent // dubious in case of RTL

  };
};

var getSlideStyle = function getSlideStyle(spec) {
  var style = {};

  if (spec.variableWidth === undefined || spec.variableWidth === false) {
    style.width = spec.slideWidth;
  }

  if (spec.fade) {
    style.position = "relative";

    if (spec.vertical) {
      style.top = -spec.index * parseInt(spec.slideHeight);
    } else {
      style.left = -spec.index * parseInt(spec.slideWidth);
    }

    style.opacity = spec.currentSlide === spec.index ? 1 : 0;

    if (spec.useCSS) {
      style.transition = "opacity " + spec.speed + "ms " + spec.cssEase + ", " + "visibility " + spec.speed + "ms " + spec.cssEase;
    }
  }

  return style;
};

var getKey = function getKey(child, fallbackKey) {
  return child.key + "-" + fallbackKey;
};

var renderSlides = function renderSlides(spec) {
  var key;
  var slides = [];
  var preCloneSlides = [];
  var postCloneSlides = [];
  var childrenCount = React.Children.count(spec.children);
  var startIndex = lazyStartIndex(spec);
  var endIndex = lazyEndIndex(spec);
  React.Children.forEach(spec.children, function (elem, index) {
    var child;
    var childOnClickOptions = {
      message: "children",
      index: index,
      slidesToScroll: spec.slidesToScroll,
      currentSlide: spec.currentSlide
    }; // in case of lazyLoad, whether or not we want to fetch the slide

    if (!spec.lazyLoad || spec.lazyLoad && spec.lazyLoadedList.indexOf(index) >= 0) {
      child = elem;
    } else {
      child = /*#__PURE__*/React.createElement("div", null);
    }

    var childStyle = getSlideStyle(_objectSpread2(_objectSpread2({}, spec), {}, {
      index: index
    }));
    var slideClass = child.props.className || "";
    var slideClasses = getSlideClasses(_objectSpread2(_objectSpread2({}, spec), {}, {
      index: index
    })); // push a cloned element of the desired slide

    slides.push( /*#__PURE__*/React.cloneElement(child, {
      key: "original" + getKey(child, index),
      "data-index": index,
      className: classnames(slideClasses, slideClass),
      tabIndex: "-1",
      "aria-hidden": !slideClasses["slick-active"],
      style: _objectSpread2(_objectSpread2({
        outline: "none"
      }, child.props.style || {}), childStyle),
      onClick: function onClick(e) {
        child.props && child.props.onClick && child.props.onClick(e);

        if (spec.focusOnSelect) {
          spec.focusOnSelect(childOnClickOptions);
        }
      }
    })); // if slide needs to be precloned or postcloned

    if (spec.infinite && spec.fade === false) {
      var preCloneNo = childrenCount - index;

      if (preCloneNo <= getPreClones(spec) && childrenCount !== spec.slidesToShow) {
        key = -preCloneNo;

        if (key >= startIndex) {
          child = elem;
        }

        slideClasses = getSlideClasses(_objectSpread2(_objectSpread2({}, spec), {}, {
          index: key
        }));
        preCloneSlides.push( /*#__PURE__*/React.cloneElement(child, {
          key: "precloned" + getKey(child, key),
          "data-index": key,
          tabIndex: "-1",
          className: classnames(slideClasses, slideClass),
          "aria-hidden": !slideClasses["slick-active"],
          style: _objectSpread2(_objectSpread2({}, child.props.style || {}), childStyle),
          onClick: function onClick(e) {
            child.props && child.props.onClick && child.props.onClick(e);

            if (spec.focusOnSelect) {
              spec.focusOnSelect(childOnClickOptions);
            }
          }
        }));
      }

      if (childrenCount !== spec.slidesToShow) {
        key = childrenCount + index;

        if (key < endIndex) {
          child = elem;
        }

        slideClasses = getSlideClasses(_objectSpread2(_objectSpread2({}, spec), {}, {
          index: key
        }));
        postCloneSlides.push( /*#__PURE__*/React.cloneElement(child, {
          key: "postcloned" + getKey(child, key),
          "data-index": key,
          tabIndex: "-1",
          className: classnames(slideClasses, slideClass),
          "aria-hidden": !slideClasses["slick-active"],
          style: _objectSpread2(_objectSpread2({}, child.props.style || {}), childStyle),
          onClick: function onClick(e) {
            child.props && child.props.onClick && child.props.onClick(e);

            if (spec.focusOnSelect) {
              spec.focusOnSelect(childOnClickOptions);
            }
          }
        }));
      }
    }
  });

  if (spec.rtl) {
    return preCloneSlides.concat(slides, postCloneSlides).reverse();
  } else {
    return preCloneSlides.concat(slides, postCloneSlides);
  }
};

var Track = /*#__PURE__*/function (_React$PureComponent) {
  _inherits(Track, _React$PureComponent);

  var _super = _createSuper(Track);

  function Track() {
    var _this;

    _classCallCheck(this, Track);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this), "node", null);

    _defineProperty(_assertThisInitialized(_this), "handleRef", function (ref) {
      _this.node = ref;
    });

    return _this;
  }

  _createClass(Track, [{
    key: "render",
    value: function render() {
      var slides = renderSlides(this.props);
      var _this$props = this.props,
          onMouseEnter = _this$props.onMouseEnter,
          onMouseOver = _this$props.onMouseOver,
          onMouseLeave = _this$props.onMouseLeave;
      var mouseEvents = {
        onMouseEnter: onMouseEnter,
        onMouseOver: onMouseOver,
        onMouseLeave: onMouseLeave
      };
      return /*#__PURE__*/React.createElement("div", _extends$1({
        ref: this.handleRef,
        className: "slick-track",
        style: this.props.trackStyle
      }, mouseEvents), slides);
    }
  }]);

  return Track;
}(React.PureComponent);

var getDotCount = function getDotCount(spec) {
  var dots;

  if (spec.infinite) {
    dots = Math.ceil(spec.slideCount / spec.slidesToScroll);
  } else {
    dots = Math.ceil((spec.slideCount - spec.slidesToShow) / spec.slidesToScroll) + 1;
  }

  return dots;
};

var Dots = /*#__PURE__*/function (_React$PureComponent) {
  _inherits(Dots, _React$PureComponent);

  var _super = _createSuper(Dots);

  function Dots() {
    _classCallCheck(this, Dots);

    return _super.apply(this, arguments);
  }

  _createClass(Dots, [{
    key: "clickHandler",
    value: function clickHandler(options, e) {
      // In Autoplay the focus stays on clicked button even after transition
      // to next slide. That only goes away by click somewhere outside
      e.preventDefault();
      this.props.clickHandler(options);
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
          onMouseEnter = _this$props.onMouseEnter,
          onMouseOver = _this$props.onMouseOver,
          onMouseLeave = _this$props.onMouseLeave,
          infinite = _this$props.infinite,
          slidesToScroll = _this$props.slidesToScroll,
          slidesToShow = _this$props.slidesToShow,
          slideCount = _this$props.slideCount,
          currentSlide = _this$props.currentSlide;
      var dotCount = getDotCount({
        slideCount: slideCount,
        slidesToScroll: slidesToScroll,
        slidesToShow: slidesToShow,
        infinite: infinite
      });
      var mouseEvents = {
        onMouseEnter: onMouseEnter,
        onMouseOver: onMouseOver,
        onMouseLeave: onMouseLeave
      };
      var dots = [];

      for (var i = 0; i < dotCount; i++) {
        var _rightBound = (i + 1) * slidesToScroll - 1;

        var rightBound = infinite ? _rightBound : clamp(_rightBound, 0, slideCount - 1);

        var _leftBound = rightBound - (slidesToScroll - 1);

        var leftBound = infinite ? _leftBound : clamp(_leftBound, 0, slideCount - 1);
        var className = classnames({
          "slick-active": infinite ? currentSlide >= leftBound && currentSlide <= rightBound : currentSlide === leftBound
        });
        var dotOptions = {
          message: "dots",
          index: i,
          slidesToScroll: slidesToScroll,
          currentSlide: currentSlide
        };
        var onClick = this.clickHandler.bind(this, dotOptions);
        dots = dots.concat( /*#__PURE__*/React.createElement("li", {
          key: i,
          className: className
        }, /*#__PURE__*/React.cloneElement(this.props.customPaging(i), {
          onClick: onClick
        })));
      }

      return /*#__PURE__*/React.cloneElement(this.props.appendDots(dots), _objectSpread2({
        className: this.props.dotsClass
      }, mouseEvents));
    }
  }]);

  return Dots;
}(React.PureComponent);

var PrevArrow = /*#__PURE__*/function (_React$PureComponent) {
  _inherits(PrevArrow, _React$PureComponent);

  var _super = _createSuper(PrevArrow);

  function PrevArrow() {
    _classCallCheck(this, PrevArrow);

    return _super.apply(this, arguments);
  }

  _createClass(PrevArrow, [{
    key: "clickHandler",
    value: function clickHandler(options, e) {
      if (e) {
        e.preventDefault();
      }

      this.props.clickHandler(options, e);
    }
  }, {
    key: "render",
    value: function render() {
      var prevClasses = {
        "slick-arrow": true,
        "slick-prev": true
      };
      var prevHandler = this.clickHandler.bind(this, {
        message: "previous"
      });

      if (!this.props.infinite && (this.props.currentSlide === 0 || this.props.slideCount <= this.props.slidesToShow)) {
        prevClasses["slick-disabled"] = true;
        prevHandler = null;
      }

      var prevArrowProps = {
        key: "0",
        "data-role": "none",
        className: classnames(prevClasses),
        style: {
          display: "block"
        },
        onClick: prevHandler
      };
      var customProps = {
        currentSlide: this.props.currentSlide,
        slideCount: this.props.slideCount
      };
      var prevArrow;

      if (this.props.prevArrow) {
        prevArrow = /*#__PURE__*/React.cloneElement(this.props.prevArrow, _objectSpread2(_objectSpread2({}, prevArrowProps), customProps));
      } else {
        prevArrow = /*#__PURE__*/React.createElement("button", _extends$1({
          key: "0",
          type: "button"
        }, prevArrowProps), " ", "Previous");
      }

      return prevArrow;
    }
  }]);

  return PrevArrow;
}(React.PureComponent);
var NextArrow = /*#__PURE__*/function (_React$PureComponent2) {
  _inherits(NextArrow, _React$PureComponent2);

  var _super2 = _createSuper(NextArrow);

  function NextArrow() {
    _classCallCheck(this, NextArrow);

    return _super2.apply(this, arguments);
  }

  _createClass(NextArrow, [{
    key: "clickHandler",
    value: function clickHandler(options, e) {
      if (e) {
        e.preventDefault();
      }

      this.props.clickHandler(options, e);
    }
  }, {
    key: "render",
    value: function render() {
      var nextClasses = {
        "slick-arrow": true,
        "slick-next": true
      };
      var nextHandler = this.clickHandler.bind(this, {
        message: "next"
      });

      if (!canGoNext(this.props)) {
        nextClasses["slick-disabled"] = true;
        nextHandler = null;
      }

      var nextArrowProps = {
        key: "1",
        "data-role": "none",
        className: classnames(nextClasses),
        style: {
          display: "block"
        },
        onClick: nextHandler
      };
      var customProps = {
        currentSlide: this.props.currentSlide,
        slideCount: this.props.slideCount
      };
      var nextArrow;

      if (this.props.nextArrow) {
        nextArrow = /*#__PURE__*/React.cloneElement(this.props.nextArrow, _objectSpread2(_objectSpread2({}, nextArrowProps), customProps));
      } else {
        nextArrow = /*#__PURE__*/React.createElement("button", _extends$1({
          key: "1",
          type: "button"
        }, nextArrowProps), " ", "Next");
      }

      return nextArrow;
    }
  }]);

  return NextArrow;
}(React.PureComponent);

/**
 * A collection of shims that provide minimal functionality of the ES6 collections.
 *
 * These implementations are not meant to be used outside of the ResizeObserver
 * modules as they cover only a limited range of use cases.
 */
/* eslint-disable require-jsdoc, valid-jsdoc */
var MapShim = (function () {
    if (typeof Map !== 'undefined') {
        return Map;
    }
    /**
     * Returns index in provided array that matches the specified key.
     *
     * @param {Array<Array>} arr
     * @param {*} key
     * @returns {number}
     */
    function getIndex(arr, key) {
        var result = -1;
        arr.some(function (entry, index) {
            if (entry[0] === key) {
                result = index;
                return true;
            }
            return false;
        });
        return result;
    }
    return /** @class */ (function () {
        function class_1() {
            this.__entries__ = [];
        }
        Object.defineProperty(class_1.prototype, "size", {
            /**
             * @returns {boolean}
             */
            get: function () {
                return this.__entries__.length;
            },
            enumerable: true,
            configurable: true
        });
        /**
         * @param {*} key
         * @returns {*}
         */
        class_1.prototype.get = function (key) {
            var index = getIndex(this.__entries__, key);
            var entry = this.__entries__[index];
            return entry && entry[1];
        };
        /**
         * @param {*} key
         * @param {*} value
         * @returns {void}
         */
        class_1.prototype.set = function (key, value) {
            var index = getIndex(this.__entries__, key);
            if (~index) {
                this.__entries__[index][1] = value;
            }
            else {
                this.__entries__.push([key, value]);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.delete = function (key) {
            var entries = this.__entries__;
            var index = getIndex(entries, key);
            if (~index) {
                entries.splice(index, 1);
            }
        };
        /**
         * @param {*} key
         * @returns {void}
         */
        class_1.prototype.has = function (key) {
            return !!~getIndex(this.__entries__, key);
        };
        /**
         * @returns {void}
         */
        class_1.prototype.clear = function () {
            this.__entries__.splice(0);
        };
        /**
         * @param {Function} callback
         * @param {*} [ctx=null]
         * @returns {void}
         */
        class_1.prototype.forEach = function (callback, ctx) {
            if (ctx === void 0) { ctx = null; }
            for (var _i = 0, _a = this.__entries__; _i < _a.length; _i++) {
                var entry = _a[_i];
                callback.call(ctx, entry[1], entry[0]);
            }
        };
        return class_1;
    }());
})();

/**
 * Detects whether window and document objects are available in current environment.
 */
var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined' && window.document === document;

// Returns global object of a current environment.
var global$1 = (function () {
    if (typeof global !== 'undefined' && global.Math === Math) {
        return global;
    }
    if (typeof self !== 'undefined' && self.Math === Math) {
        return self;
    }
    if (typeof window !== 'undefined' && window.Math === Math) {
        return window;
    }
    // eslint-disable-next-line no-new-func
    return Function('return this')();
})();

/**
 * A shim for the requestAnimationFrame which falls back to the setTimeout if
 * first one is not supported.
 *
 * @returns {number} Requests' identifier.
 */
var requestAnimationFrame$1 = (function () {
    if (typeof requestAnimationFrame === 'function') {
        // It's required to use a bounded function because IE sometimes throws
        // an "Invalid calling object" error if rAF is invoked without the global
        // object on the left hand side.
        return requestAnimationFrame.bind(global$1);
    }
    return function (callback) { return setTimeout(function () { return callback(Date.now()); }, 1000 / 60); };
})();

// Defines minimum timeout before adding a trailing call.
var trailingTimeout = 2;
/**
 * Creates a wrapper function which ensures that provided callback will be
 * invoked only once during the specified delay period.
 *
 * @param {Function} callback - Function to be invoked after the delay period.
 * @param {number} delay - Delay after which to invoke callback.
 * @returns {Function}
 */
function throttle$1 (callback, delay) {
    var leadingCall = false, trailingCall = false, lastCallTime = 0;
    /**
     * Invokes the original callback function and schedules new invocation if
     * the "proxy" was called during current request.
     *
     * @returns {void}
     */
    function resolvePending() {
        if (leadingCall) {
            leadingCall = false;
            callback();
        }
        if (trailingCall) {
            proxy();
        }
    }
    /**
     * Callback invoked after the specified delay. It will further postpone
     * invocation of the original function delegating it to the
     * requestAnimationFrame.
     *
     * @returns {void}
     */
    function timeoutCallback() {
        requestAnimationFrame$1(resolvePending);
    }
    /**
     * Schedules invocation of the original function.
     *
     * @returns {void}
     */
    function proxy() {
        var timeStamp = Date.now();
        if (leadingCall) {
            // Reject immediately following calls.
            if (timeStamp - lastCallTime < trailingTimeout) {
                return;
            }
            // Schedule new call to be in invoked when the pending one is resolved.
            // This is important for "transitions" which never actually start
            // immediately so there is a chance that we might miss one if change
            // happens amids the pending invocation.
            trailingCall = true;
        }
        else {
            leadingCall = true;
            trailingCall = false;
            setTimeout(timeoutCallback, delay);
        }
        lastCallTime = timeStamp;
    }
    return proxy;
}

// Minimum delay before invoking the update of observers.
var REFRESH_DELAY = 20;
// A list of substrings of CSS properties used to find transition events that
// might affect dimensions of observed elements.
var transitionKeys = ['top', 'right', 'bottom', 'left', 'width', 'height', 'size', 'weight'];
// Check if MutationObserver is available.
var mutationObserverSupported = typeof MutationObserver !== 'undefined';
/**
 * Singleton controller class which handles updates of ResizeObserver instances.
 */
var ResizeObserverController = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserverController.
     *
     * @private
     */
    function ResizeObserverController() {
        /**
         * Indicates whether DOM listeners have been added.
         *
         * @private {boolean}
         */
        this.connected_ = false;
        /**
         * Tells that controller has subscribed for Mutation Events.
         *
         * @private {boolean}
         */
        this.mutationEventsAdded_ = false;
        /**
         * Keeps reference to the instance of MutationObserver.
         *
         * @private {MutationObserver}
         */
        this.mutationsObserver_ = null;
        /**
         * A list of connected observers.
         *
         * @private {Array<ResizeObserverSPI>}
         */
        this.observers_ = [];
        this.onTransitionEnd_ = this.onTransitionEnd_.bind(this);
        this.refresh = throttle$1(this.refresh.bind(this), REFRESH_DELAY);
    }
    /**
     * Adds observer to observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be added.
     * @returns {void}
     */
    ResizeObserverController.prototype.addObserver = function (observer) {
        if (!~this.observers_.indexOf(observer)) {
            this.observers_.push(observer);
        }
        // Add listeners if they haven't been added yet.
        if (!this.connected_) {
            this.connect_();
        }
    };
    /**
     * Removes observer from observers list.
     *
     * @param {ResizeObserverSPI} observer - Observer to be removed.
     * @returns {void}
     */
    ResizeObserverController.prototype.removeObserver = function (observer) {
        var observers = this.observers_;
        var index = observers.indexOf(observer);
        // Remove observer if it's present in registry.
        if (~index) {
            observers.splice(index, 1);
        }
        // Remove listeners if controller has no connected observers.
        if (!observers.length && this.connected_) {
            this.disconnect_();
        }
    };
    /**
     * Invokes the update of observers. It will continue running updates insofar
     * it detects changes.
     *
     * @returns {void}
     */
    ResizeObserverController.prototype.refresh = function () {
        var changesDetected = this.updateObservers_();
        // Continue running updates if changes have been detected as there might
        // be future ones caused by CSS transitions.
        if (changesDetected) {
            this.refresh();
        }
    };
    /**
     * Updates every observer from observers list and notifies them of queued
     * entries.
     *
     * @private
     * @returns {boolean} Returns "true" if any observer has detected changes in
     *      dimensions of it's elements.
     */
    ResizeObserverController.prototype.updateObservers_ = function () {
        // Collect observers that have active observations.
        var activeObservers = this.observers_.filter(function (observer) {
            return observer.gatherActive(), observer.hasActive();
        });
        // Deliver notifications in a separate cycle in order to avoid any
        // collisions between observers, e.g. when multiple instances of
        // ResizeObserver are tracking the same element and the callback of one
        // of them changes content dimensions of the observed target. Sometimes
        // this may result in notifications being blocked for the rest of observers.
        activeObservers.forEach(function (observer) { return observer.broadcastActive(); });
        return activeObservers.length > 0;
    };
    /**
     * Initializes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.connect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already added.
        if (!isBrowser || this.connected_) {
            return;
        }
        // Subscription to the "Transitionend" event is used as a workaround for
        // delayed transitions. This way it's possible to capture at least the
        // final state of an element.
        document.addEventListener('transitionend', this.onTransitionEnd_);
        window.addEventListener('resize', this.refresh);
        if (mutationObserverSupported) {
            this.mutationsObserver_ = new MutationObserver(this.refresh);
            this.mutationsObserver_.observe(document, {
                attributes: true,
                childList: true,
                characterData: true,
                subtree: true
            });
        }
        else {
            document.addEventListener('DOMSubtreeModified', this.refresh);
            this.mutationEventsAdded_ = true;
        }
        this.connected_ = true;
    };
    /**
     * Removes DOM listeners.
     *
     * @private
     * @returns {void}
     */
    ResizeObserverController.prototype.disconnect_ = function () {
        // Do nothing if running in a non-browser environment or if listeners
        // have been already removed.
        if (!isBrowser || !this.connected_) {
            return;
        }
        document.removeEventListener('transitionend', this.onTransitionEnd_);
        window.removeEventListener('resize', this.refresh);
        if (this.mutationsObserver_) {
            this.mutationsObserver_.disconnect();
        }
        if (this.mutationEventsAdded_) {
            document.removeEventListener('DOMSubtreeModified', this.refresh);
        }
        this.mutationsObserver_ = null;
        this.mutationEventsAdded_ = false;
        this.connected_ = false;
    };
    /**
     * "Transitionend" event handler.
     *
     * @private
     * @param {TransitionEvent} event
     * @returns {void}
     */
    ResizeObserverController.prototype.onTransitionEnd_ = function (_a) {
        var _b = _a.propertyName, propertyName = _b === void 0 ? '' : _b;
        // Detect whether transition may affect dimensions of an element.
        var isReflowProperty = transitionKeys.some(function (key) {
            return !!~propertyName.indexOf(key);
        });
        if (isReflowProperty) {
            this.refresh();
        }
    };
    /**
     * Returns instance of the ResizeObserverController.
     *
     * @returns {ResizeObserverController}
     */
    ResizeObserverController.getInstance = function () {
        if (!this.instance_) {
            this.instance_ = new ResizeObserverController();
        }
        return this.instance_;
    };
    /**
     * Holds reference to the controller's instance.
     *
     * @private {ResizeObserverController}
     */
    ResizeObserverController.instance_ = null;
    return ResizeObserverController;
}());

/**
 * Defines non-writable/enumerable properties of the provided target object.
 *
 * @param {Object} target - Object for which to define properties.
 * @param {Object} props - Properties to be defined.
 * @returns {Object} Target object.
 */
var defineConfigurable = (function (target, props) {
    for (var _i = 0, _a = Object.keys(props); _i < _a.length; _i++) {
        var key = _a[_i];
        Object.defineProperty(target, key, {
            value: props[key],
            enumerable: false,
            writable: false,
            configurable: true
        });
    }
    return target;
});

/**
 * Returns the global object associated with provided element.
 *
 * @param {Object} target
 * @returns {Object}
 */
var getWindowOf = (function (target) {
    // Assume that the element is an instance of Node, which means that it
    // has the "ownerDocument" property from which we can retrieve a
    // corresponding global object.
    var ownerGlobal = target && target.ownerDocument && target.ownerDocument.defaultView;
    // Return the local global object if it's not possible extract one from
    // provided element.
    return ownerGlobal || global$1;
});

// Placeholder of an empty content rectangle.
var emptyRect = createRectInit(0, 0, 0, 0);
/**
 * Converts provided string to a number.
 *
 * @param {number|string} value
 * @returns {number}
 */
function toFloat(value) {
    return parseFloat(value) || 0;
}
/**
 * Extracts borders size from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @param {...string} positions - Borders positions (top, right, ...)
 * @returns {number}
 */
function getBordersSize(styles) {
    var positions = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        positions[_i - 1] = arguments[_i];
    }
    return positions.reduce(function (size, position) {
        var value = styles['border-' + position + '-width'];
        return size + toFloat(value);
    }, 0);
}
/**
 * Extracts paddings sizes from provided styles.
 *
 * @param {CSSStyleDeclaration} styles
 * @returns {Object} Paddings box.
 */
function getPaddings(styles) {
    var positions = ['top', 'right', 'bottom', 'left'];
    var paddings = {};
    for (var _i = 0, positions_1 = positions; _i < positions_1.length; _i++) {
        var position = positions_1[_i];
        var value = styles['padding-' + position];
        paddings[position] = toFloat(value);
    }
    return paddings;
}
/**
 * Calculates content rectangle of provided SVG element.
 *
 * @param {SVGGraphicsElement} target - Element content rectangle of which needs
 *      to be calculated.
 * @returns {DOMRectInit}
 */
function getSVGContentRect(target) {
    var bbox = target.getBBox();
    return createRectInit(0, 0, bbox.width, bbox.height);
}
/**
 * Calculates content rectangle of provided HTMLElement.
 *
 * @param {HTMLElement} target - Element for which to calculate the content rectangle.
 * @returns {DOMRectInit}
 */
function getHTMLElementContentRect(target) {
    // Client width & height properties can't be
    // used exclusively as they provide rounded values.
    var clientWidth = target.clientWidth, clientHeight = target.clientHeight;
    // By this condition we can catch all non-replaced inline, hidden and
    // detached elements. Though elements with width & height properties less
    // than 0.5 will be discarded as well.
    //
    // Without it we would need to implement separate methods for each of
    // those cases and it's not possible to perform a precise and performance
    // effective test for hidden elements. E.g. even jQuery's ':visible' filter
    // gives wrong results for elements with width & height less than 0.5.
    if (!clientWidth && !clientHeight) {
        return emptyRect;
    }
    var styles = getWindowOf(target).getComputedStyle(target);
    var paddings = getPaddings(styles);
    var horizPad = paddings.left + paddings.right;
    var vertPad = paddings.top + paddings.bottom;
    // Computed styles of width & height are being used because they are the
    // only dimensions available to JS that contain non-rounded values. It could
    // be possible to utilize the getBoundingClientRect if only it's data wasn't
    // affected by CSS transformations let alone paddings, borders and scroll bars.
    var width = toFloat(styles.width), height = toFloat(styles.height);
    // Width & height include paddings and borders when the 'border-box' box
    // model is applied (except for IE).
    if (styles.boxSizing === 'border-box') {
        // Following conditions are required to handle Internet Explorer which
        // doesn't include paddings and borders to computed CSS dimensions.
        //
        // We can say that if CSS dimensions + paddings are equal to the "client"
        // properties then it's either IE, and thus we don't need to subtract
        // anything, or an element merely doesn't have paddings/borders styles.
        if (Math.round(width + horizPad) !== clientWidth) {
            width -= getBordersSize(styles, 'left', 'right') + horizPad;
        }
        if (Math.round(height + vertPad) !== clientHeight) {
            height -= getBordersSize(styles, 'top', 'bottom') + vertPad;
        }
    }
    // Following steps can't be applied to the document's root element as its
    // client[Width/Height] properties represent viewport area of the window.
    // Besides, it's as well not necessary as the <html> itself neither has
    // rendered scroll bars nor it can be clipped.
    if (!isDocumentElement(target)) {
        // In some browsers (only in Firefox, actually) CSS width & height
        // include scroll bars size which can be removed at this step as scroll
        // bars are the only difference between rounded dimensions + paddings
        // and "client" properties, though that is not always true in Chrome.
        var vertScrollbar = Math.round(width + horizPad) - clientWidth;
        var horizScrollbar = Math.round(height + vertPad) - clientHeight;
        // Chrome has a rather weird rounding of "client" properties.
        // E.g. for an element with content width of 314.2px it sometimes gives
        // the client width of 315px and for the width of 314.7px it may give
        // 314px. And it doesn't happen all the time. So just ignore this delta
        // as a non-relevant.
        if (Math.abs(vertScrollbar) !== 1) {
            width -= vertScrollbar;
        }
        if (Math.abs(horizScrollbar) !== 1) {
            height -= horizScrollbar;
        }
    }
    return createRectInit(paddings.left, paddings.top, width, height);
}
/**
 * Checks whether provided element is an instance of the SVGGraphicsElement.
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
var isSVGGraphicsElement = (function () {
    // Some browsers, namely IE and Edge, don't have the SVGGraphicsElement
    // interface.
    if (typeof SVGGraphicsElement !== 'undefined') {
        return function (target) { return target instanceof getWindowOf(target).SVGGraphicsElement; };
    }
    // If it's so, then check that element is at least an instance of the
    // SVGElement and that it has the "getBBox" method.
    // eslint-disable-next-line no-extra-parens
    return function (target) { return (target instanceof getWindowOf(target).SVGElement &&
        typeof target.getBBox === 'function'); };
})();
/**
 * Checks whether provided element is a document element (<html>).
 *
 * @param {Element} target - Element to be checked.
 * @returns {boolean}
 */
function isDocumentElement(target) {
    return target === getWindowOf(target).document.documentElement;
}
/**
 * Calculates an appropriate content rectangle for provided html or svg element.
 *
 * @param {Element} target - Element content rectangle of which needs to be calculated.
 * @returns {DOMRectInit}
 */
function getContentRect(target) {
    if (!isBrowser) {
        return emptyRect;
    }
    if (isSVGGraphicsElement(target)) {
        return getSVGContentRect(target);
    }
    return getHTMLElementContentRect(target);
}
/**
 * Creates rectangle with an interface of the DOMRectReadOnly.
 * Spec: https://drafts.fxtf.org/geometry/#domrectreadonly
 *
 * @param {DOMRectInit} rectInit - Object with rectangle's x/y coordinates and dimensions.
 * @returns {DOMRectReadOnly}
 */
function createReadOnlyRect(_a) {
    var x = _a.x, y = _a.y, width = _a.width, height = _a.height;
    // If DOMRectReadOnly is available use it as a prototype for the rectangle.
    var Constr = typeof DOMRectReadOnly !== 'undefined' ? DOMRectReadOnly : Object;
    var rect = Object.create(Constr.prototype);
    // Rectangle's properties are not writable and non-enumerable.
    defineConfigurable(rect, {
        x: x, y: y, width: width, height: height,
        top: y,
        right: x + width,
        bottom: height + y,
        left: x
    });
    return rect;
}
/**
 * Creates DOMRectInit object based on the provided dimensions and the x/y coordinates.
 * Spec: https://drafts.fxtf.org/geometry/#dictdef-domrectinit
 *
 * @param {number} x - X coordinate.
 * @param {number} y - Y coordinate.
 * @param {number} width - Rectangle's width.
 * @param {number} height - Rectangle's height.
 * @returns {DOMRectInit}
 */
function createRectInit(x, y, width, height) {
    return { x: x, y: y, width: width, height: height };
}

/**
 * Class that is responsible for computations of the content rectangle of
 * provided DOM element and for keeping track of it's changes.
 */
var ResizeObservation = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObservation.
     *
     * @param {Element} target - Element to be observed.
     */
    function ResizeObservation(target) {
        /**
         * Broadcasted width of content rectangle.
         *
         * @type {number}
         */
        this.broadcastWidth = 0;
        /**
         * Broadcasted height of content rectangle.
         *
         * @type {number}
         */
        this.broadcastHeight = 0;
        /**
         * Reference to the last observed content rectangle.
         *
         * @private {DOMRectInit}
         */
        this.contentRect_ = createRectInit(0, 0, 0, 0);
        this.target = target;
    }
    /**
     * Updates content rectangle and tells whether it's width or height properties
     * have changed since the last broadcast.
     *
     * @returns {boolean}
     */
    ResizeObservation.prototype.isActive = function () {
        var rect = getContentRect(this.target);
        this.contentRect_ = rect;
        return (rect.width !== this.broadcastWidth ||
            rect.height !== this.broadcastHeight);
    };
    /**
     * Updates 'broadcastWidth' and 'broadcastHeight' properties with a data
     * from the corresponding properties of the last observed content rectangle.
     *
     * @returns {DOMRectInit} Last observed content rectangle.
     */
    ResizeObservation.prototype.broadcastRect = function () {
        var rect = this.contentRect_;
        this.broadcastWidth = rect.width;
        this.broadcastHeight = rect.height;
        return rect;
    };
    return ResizeObservation;
}());

var ResizeObserverEntry = /** @class */ (function () {
    /**
     * Creates an instance of ResizeObserverEntry.
     *
     * @param {Element} target - Element that is being observed.
     * @param {DOMRectInit} rectInit - Data of the element's content rectangle.
     */
    function ResizeObserverEntry(target, rectInit) {
        var contentRect = createReadOnlyRect(rectInit);
        // According to the specification following properties are not writable
        // and are also not enumerable in the native implementation.
        //
        // Property accessors are not being used as they'd require to define a
        // private WeakMap storage which may cause memory leaks in browsers that
        // don't support this type of collections.
        defineConfigurable(this, { target: target, contentRect: contentRect });
    }
    return ResizeObserverEntry;
}());

var ResizeObserverSPI = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback function that is invoked
     *      when one of the observed elements changes it's content dimensions.
     * @param {ResizeObserverController} controller - Controller instance which
     *      is responsible for the updates of observer.
     * @param {ResizeObserver} callbackCtx - Reference to the public
     *      ResizeObserver instance which will be passed to callback function.
     */
    function ResizeObserverSPI(callback, controller, callbackCtx) {
        /**
         * Collection of resize observations that have detected changes in dimensions
         * of elements.
         *
         * @private {Array<ResizeObservation>}
         */
        this.activeObservations_ = [];
        /**
         * Registry of the ResizeObservation instances.
         *
         * @private {Map<Element, ResizeObservation>}
         */
        this.observations_ = new MapShim();
        if (typeof callback !== 'function') {
            throw new TypeError('The callback provided as parameter 1 is not a function.');
        }
        this.callback_ = callback;
        this.controller_ = controller;
        this.callbackCtx_ = callbackCtx;
    }
    /**
     * Starts observing provided element.
     *
     * @param {Element} target - Element to be observed.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.observe = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is already being observed.
        if (observations.has(target)) {
            return;
        }
        observations.set(target, new ResizeObservation(target));
        this.controller_.addObserver(this);
        // Force the update of observations.
        this.controller_.refresh();
    };
    /**
     * Stops observing provided element.
     *
     * @param {Element} target - Element to stop observing.
     * @returns {void}
     */
    ResizeObserverSPI.prototype.unobserve = function (target) {
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        // Do nothing if current environment doesn't have the Element interface.
        if (typeof Element === 'undefined' || !(Element instanceof Object)) {
            return;
        }
        if (!(target instanceof getWindowOf(target).Element)) {
            throw new TypeError('parameter 1 is not of type "Element".');
        }
        var observations = this.observations_;
        // Do nothing if element is not being observed.
        if (!observations.has(target)) {
            return;
        }
        observations.delete(target);
        if (!observations.size) {
            this.controller_.removeObserver(this);
        }
    };
    /**
     * Stops observing all elements.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.disconnect = function () {
        this.clearActive();
        this.observations_.clear();
        this.controller_.removeObserver(this);
    };
    /**
     * Collects observation instances the associated element of which has changed
     * it's content rectangle.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.gatherActive = function () {
        var _this = this;
        this.clearActive();
        this.observations_.forEach(function (observation) {
            if (observation.isActive()) {
                _this.activeObservations_.push(observation);
            }
        });
    };
    /**
     * Invokes initial callback function with a list of ResizeObserverEntry
     * instances collected from active resize observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.broadcastActive = function () {
        // Do nothing if observer doesn't have active observations.
        if (!this.hasActive()) {
            return;
        }
        var ctx = this.callbackCtx_;
        // Create ResizeObserverEntry instance for every active observation.
        var entries = this.activeObservations_.map(function (observation) {
            return new ResizeObserverEntry(observation.target, observation.broadcastRect());
        });
        this.callback_.call(ctx, entries, ctx);
        this.clearActive();
    };
    /**
     * Clears the collection of active observations.
     *
     * @returns {void}
     */
    ResizeObserverSPI.prototype.clearActive = function () {
        this.activeObservations_.splice(0);
    };
    /**
     * Tells whether observer has active observations.
     *
     * @returns {boolean}
     */
    ResizeObserverSPI.prototype.hasActive = function () {
        return this.activeObservations_.length > 0;
    };
    return ResizeObserverSPI;
}());

// Registry of internal observers. If WeakMap is not available use current shim
// for the Map collection as it has all required methods and because WeakMap
// can't be fully polyfilled anyway.
var observers = typeof WeakMap !== 'undefined' ? new WeakMap() : new MapShim();
/**
 * ResizeObserver API. Encapsulates the ResizeObserver SPI implementation
 * exposing only those methods and properties that are defined in the spec.
 */
var ResizeObserver = /** @class */ (function () {
    /**
     * Creates a new instance of ResizeObserver.
     *
     * @param {ResizeObserverCallback} callback - Callback that is invoked when
     *      dimensions of the observed elements change.
     */
    function ResizeObserver(callback) {
        if (!(this instanceof ResizeObserver)) {
            throw new TypeError('Cannot call a class as a function.');
        }
        if (!arguments.length) {
            throw new TypeError('1 argument required, but only 0 present.');
        }
        var controller = ResizeObserverController.getInstance();
        var observer = new ResizeObserverSPI(callback, controller, this);
        observers.set(this, observer);
    }
    return ResizeObserver;
}());
// Expose public methods of ResizeObserver.
[
    'observe',
    'unobserve',
    'disconnect'
].forEach(function (method) {
    ResizeObserver.prototype[method] = function () {
        var _a;
        return (_a = observers.get(this))[method].apply(_a, arguments);
    };
});

var index = (function () {
    // Export existing implementation if available.
    if (typeof global$1.ResizeObserver !== 'undefined') {
        return global$1.ResizeObserver;
    }
    return ResizeObserver;
})();

var InnerSlider = /*#__PURE__*/function (_React$Component) {
  _inherits(InnerSlider, _React$Component);

  var _super = _createSuper(InnerSlider);

  function InnerSlider(props) {
    var _this;

    _classCallCheck(this, InnerSlider);

    _this = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this), "listRefHandler", function (ref) {
      return _this.list = ref;
    });

    _defineProperty(_assertThisInitialized(_this), "trackRefHandler", function (ref) {
      return _this.track = ref;
    });

    _defineProperty(_assertThisInitialized(_this), "adaptHeight", function () {
      if (_this.props.adaptiveHeight && _this.list) {
        var elem = _this.list.querySelector("[data-index=\"".concat(_this.state.currentSlide, "\"]"));

        _this.list.style.height = getHeight(elem) + "px";
      }
    });

    _defineProperty(_assertThisInitialized(_this), "componentDidMount", function () {
      _this.props.onInit && _this.props.onInit();

      if (_this.props.lazyLoad) {
        var slidesToLoad = getOnDemandLazySlides(_objectSpread2(_objectSpread2({}, _this.props), _this.state));

        if (slidesToLoad.length > 0) {
          _this.setState(function (prevState) {
            return {
              lazyLoadedList: prevState.lazyLoadedList.concat(slidesToLoad)
            };
          });

          if (_this.props.onLazyLoad) {
            _this.props.onLazyLoad(slidesToLoad);
          }
        }
      }

      var spec = _objectSpread2({
        listRef: _this.list,
        trackRef: _this.track
      }, _this.props);

      _this.updateState(spec, true, function () {
        _this.adaptHeight();

        _this.props.autoplay && _this.autoPlay("playing");
      });

      if (_this.props.lazyLoad === "progressive") {
        _this.lazyLoadTimer = setInterval(_this.progressiveLazyLoad, 1000);
      }

      _this.ro = new index(function () {
        if (_this.state.animating) {
          _this.onWindowResized(false); // don't set trackStyle hence don't break animation


          _this.callbackTimers.push(setTimeout(function () {
            return _this.onWindowResized();
          }, _this.props.speed));
        } else {
          _this.onWindowResized();
        }
      });

      _this.ro.observe(_this.list);

      document.querySelectorAll && Array.prototype.forEach.call(document.querySelectorAll(".slick-slide"), function (slide) {
        slide.onfocus = _this.props.pauseOnFocus ? _this.onSlideFocus : null;
        slide.onblur = _this.props.pauseOnFocus ? _this.onSlideBlur : null;
      });

      if (window.addEventListener) {
        window.addEventListener("resize", _this.onWindowResized);
      } else {
        window.attachEvent("onresize", _this.onWindowResized);
      }
    });

    _defineProperty(_assertThisInitialized(_this), "componentWillUnmount", function () {
      if (_this.animationEndCallback) {
        clearTimeout(_this.animationEndCallback);
      }

      if (_this.lazyLoadTimer) {
        clearInterval(_this.lazyLoadTimer);
      }

      if (_this.callbackTimers.length) {
        _this.callbackTimers.forEach(function (timer) {
          return clearTimeout(timer);
        });

        _this.callbackTimers = [];
      }

      if (window.addEventListener) {
        window.removeEventListener("resize", _this.onWindowResized);
      } else {
        window.detachEvent("onresize", _this.onWindowResized);
      }

      if (_this.autoplayTimer) {
        clearInterval(_this.autoplayTimer);
      }

      _this.ro.disconnect();
    });

    _defineProperty(_assertThisInitialized(_this), "componentDidUpdate", function (prevProps) {
      _this.checkImagesLoad();

      _this.props.onReInit && _this.props.onReInit();

      if (_this.props.lazyLoad) {
        var slidesToLoad = getOnDemandLazySlides(_objectSpread2(_objectSpread2({}, _this.props), _this.state));

        if (slidesToLoad.length > 0) {
          _this.setState(function (prevState) {
            return {
              lazyLoadedList: prevState.lazyLoadedList.concat(slidesToLoad)
            };
          });

          if (_this.props.onLazyLoad) {
            _this.props.onLazyLoad(slidesToLoad);
          }
        }
      } // if (this.props.onLazyLoad) {
      //   this.props.onLazyLoad([leftMostSlide])
      // }


      _this.adaptHeight();

      var spec = _objectSpread2(_objectSpread2({
        listRef: _this.list,
        trackRef: _this.track
      }, _this.props), _this.state);

      var setTrackStyle = _this.didPropsChange(prevProps);

      setTrackStyle && _this.updateState(spec, setTrackStyle, function () {
        if (_this.state.currentSlide >= React.Children.count(_this.props.children)) {
          _this.changeSlide({
            message: "index",
            index: React.Children.count(_this.props.children) - _this.props.slidesToShow,
            currentSlide: _this.state.currentSlide
          });
        }

        if (prevProps.autoplay !== _this.props.autoplay || prevProps.autoplaySpeed !== _this.props.autoplaySpeed) {
          if (!prevProps.autoplay && _this.props.autoplay) {
            _this.autoPlay("playing");
          } else if (_this.props.autoplay) {
            _this.autoPlay("update");
          } else {
            _this.pause("paused");
          }
        }
      });
    });

    _defineProperty(_assertThisInitialized(_this), "onWindowResized", function (setTrackStyle) {
      if (_this.debouncedResize) _this.debouncedResize.cancel();
      _this.debouncedResize = debounce_1(function () {
        return _this.resizeWindow(setTrackStyle);
      }, 50);

      _this.debouncedResize();
    });

    _defineProperty(_assertThisInitialized(_this), "resizeWindow", function () {
      var setTrackStyle = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      var isTrackMounted = Boolean(_this.track && _this.track.node); // prevent warning: setting state on unmounted component (server side rendering)

      if (!isTrackMounted) return;

      var spec = _objectSpread2(_objectSpread2({
        listRef: _this.list,
        trackRef: _this.track
      }, _this.props), _this.state);

      _this.updateState(spec, setTrackStyle, function () {
        if (_this.props.autoplay) _this.autoPlay("update");else _this.pause("paused");
      }); // animating state should be cleared while resizing, otherwise autoplay stops working


      _this.setState({
        animating: false
      });

      clearTimeout(_this.animationEndCallback);
      delete _this.animationEndCallback;
    });

    _defineProperty(_assertThisInitialized(_this), "updateState", function (spec, setTrackStyle, callback) {
      var updatedState = initializedState(spec);
      spec = _objectSpread2(_objectSpread2(_objectSpread2({}, spec), updatedState), {}, {
        slideIndex: updatedState.currentSlide
      });
      var targetLeft = getTrackLeft(spec);
      spec = _objectSpread2(_objectSpread2({}, spec), {}, {
        left: targetLeft
      });
      var trackStyle = getTrackCSS(spec);

      if (setTrackStyle || React.Children.count(_this.props.children) !== React.Children.count(spec.children)) {
        updatedState["trackStyle"] = trackStyle;
      }

      _this.setState(updatedState, callback);
    });

    _defineProperty(_assertThisInitialized(_this), "ssrInit", function () {
      if (_this.props.variableWidth) {
        var _trackWidth = 0,
            _trackLeft = 0;
        var childrenWidths = [];
        var preClones = getPreClones(_objectSpread2(_objectSpread2(_objectSpread2({}, _this.props), _this.state), {}, {
          slideCount: _this.props.children.length
        }));
        var postClones = getPostClones(_objectSpread2(_objectSpread2(_objectSpread2({}, _this.props), _this.state), {}, {
          slideCount: _this.props.children.length
        }));

        _this.props.children.forEach(function (child) {
          childrenWidths.push(child.props.style.width);
          _trackWidth += child.props.style.width;
        });

        for (var i = 0; i < preClones; i++) {
          _trackLeft += childrenWidths[childrenWidths.length - 1 - i];
          _trackWidth += childrenWidths[childrenWidths.length - 1 - i];
        }

        for (var _i = 0; _i < postClones; _i++) {
          _trackWidth += childrenWidths[_i];
        }

        for (var _i2 = 0; _i2 < _this.state.currentSlide; _i2++) {
          _trackLeft += childrenWidths[_i2];
        }

        var _trackStyle = {
          width: _trackWidth + "px",
          left: -_trackLeft + "px"
        };

        if (_this.props.centerMode) {
          var currentWidth = "".concat(childrenWidths[_this.state.currentSlide], "px");
          _trackStyle.left = "calc(".concat(_trackStyle.left, " + (100% - ").concat(currentWidth, ") / 2 ) ");
        }

        return {
          trackStyle: _trackStyle
        };
      }

      var childrenCount = React.Children.count(_this.props.children);

      var spec = _objectSpread2(_objectSpread2(_objectSpread2({}, _this.props), _this.state), {}, {
        slideCount: childrenCount
      });

      var slideCount = getPreClones(spec) + getPostClones(spec) + childrenCount;
      var trackWidth = 100 / _this.props.slidesToShow * slideCount;
      var slideWidth = 100 / slideCount;
      var trackLeft = -slideWidth * (getPreClones(spec) + _this.state.currentSlide) * trackWidth / 100;

      if (_this.props.centerMode) {
        trackLeft += (100 - slideWidth * trackWidth / 100) / 2;
      }

      var trackStyle = {
        width: trackWidth + "%",
        left: trackLeft + "%"
      };
      return {
        slideWidth: slideWidth + "%",
        trackStyle: trackStyle
      };
    });

    _defineProperty(_assertThisInitialized(_this), "checkImagesLoad", function () {
      var images = _this.list && _this.list.querySelectorAll && _this.list.querySelectorAll(".slick-slide img") || [];
      var imagesCount = images.length,
          loadedCount = 0;
      Array.prototype.forEach.call(images, function (image) {
        var handler = function handler() {
          return ++loadedCount && loadedCount >= imagesCount && _this.onWindowResized();
        };

        if (!image.onclick) {
          image.onclick = function () {
            return image.parentNode.focus();
          };
        } else {
          var prevClickHandler = image.onclick;

          image.onclick = function () {
            prevClickHandler();
            image.parentNode.focus();
          };
        }

        if (!image.onload) {
          if (_this.props.lazyLoad) {
            image.onload = function () {
              _this.adaptHeight();

              _this.callbackTimers.push(setTimeout(_this.onWindowResized, _this.props.speed));
            };
          } else {
            image.onload = handler;

            image.onerror = function () {
              handler();
              _this.props.onLazyLoadError && _this.props.onLazyLoadError();
            };
          }
        }
      });
    });

    _defineProperty(_assertThisInitialized(_this), "progressiveLazyLoad", function () {
      var slidesToLoad = [];

      var spec = _objectSpread2(_objectSpread2({}, _this.props), _this.state);

      for (var index = _this.state.currentSlide; index < _this.state.slideCount + getPostClones(spec); index++) {
        if (_this.state.lazyLoadedList.indexOf(index) < 0) {
          slidesToLoad.push(index);
          break;
        }
      }

      for (var _index = _this.state.currentSlide - 1; _index >= -getPreClones(spec); _index--) {
        if (_this.state.lazyLoadedList.indexOf(_index) < 0) {
          slidesToLoad.push(_index);
          break;
        }
      }

      if (slidesToLoad.length > 0) {
        _this.setState(function (state) {
          return {
            lazyLoadedList: state.lazyLoadedList.concat(slidesToLoad)
          };
        });

        if (_this.props.onLazyLoad) {
          _this.props.onLazyLoad(slidesToLoad);
        }
      } else {
        if (_this.lazyLoadTimer) {
          clearInterval(_this.lazyLoadTimer);
          delete _this.lazyLoadTimer;
        }
      }
    });

    _defineProperty(_assertThisInitialized(_this), "slideHandler", function (index) {
      var dontAnimate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      var _this$props = _this.props,
          asNavFor = _this$props.asNavFor,
          beforeChange = _this$props.beforeChange,
          onLazyLoad = _this$props.onLazyLoad,
          speed = _this$props.speed,
          afterChange = _this$props.afterChange; // capture currentslide before state is updated

      var currentSlide = _this.state.currentSlide;

      var _slideHandler = slideHandler(_objectSpread2(_objectSpread2(_objectSpread2({
        index: index
      }, _this.props), _this.state), {}, {
        trackRef: _this.track,
        useCSS: _this.props.useCSS && !dontAnimate
      })),
          state = _slideHandler.state,
          nextState = _slideHandler.nextState;

      if (!state) return;
      beforeChange && beforeChange(currentSlide, state.currentSlide);
      var slidesToLoad = state.lazyLoadedList.filter(function (value) {
        return _this.state.lazyLoadedList.indexOf(value) < 0;
      });
      onLazyLoad && slidesToLoad.length > 0 && onLazyLoad(slidesToLoad);

      if (!_this.props.waitForAnimate && _this.animationEndCallback) {
        clearTimeout(_this.animationEndCallback);
        afterChange && afterChange(currentSlide);
        delete _this.animationEndCallback;
      }

      _this.setState(state, function () {
        // asNavForIndex check is to avoid recursive calls of slideHandler in waitForAnimate=false mode
        if (asNavFor && _this.asNavForIndex !== index) {
          _this.asNavForIndex = index;
          asNavFor.innerSlider.slideHandler(index);
        }

        if (!nextState) return;
        _this.animationEndCallback = setTimeout(function () {
          var animating = nextState.animating,
              firstBatch = _objectWithoutProperties(nextState, ["animating"]);

          _this.setState(firstBatch, function () {
            _this.callbackTimers.push(setTimeout(function () {
              return _this.setState({
                animating: animating
              });
            }, 10));

            afterChange && afterChange(state.currentSlide);
            delete _this.animationEndCallback;
          });
        }, speed);
      });
    });

    _defineProperty(_assertThisInitialized(_this), "changeSlide", function (options) {
      var dontAnimate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      var spec = _objectSpread2(_objectSpread2({}, _this.props), _this.state);

      var targetSlide = changeSlide(spec, options);
      if (targetSlide !== 0 && !targetSlide) return;

      if (dontAnimate === true) {
        _this.slideHandler(targetSlide, dontAnimate);
      } else {
        _this.slideHandler(targetSlide);
      }

      _this.props.autoplay && _this.autoPlay("update");

      if (_this.props.focusOnSelect) {
        var nodes = _this.list.querySelectorAll(".slick-current");

        nodes[0] && nodes[0].focus();
      }
    });

    _defineProperty(_assertThisInitialized(_this), "clickHandler", function (e) {
      if (_this.clickable === false) {
        e.stopPropagation();
        e.preventDefault();
      }

      _this.clickable = true;
    });

    _defineProperty(_assertThisInitialized(_this), "keyHandler", function (e) {
      var dir = keyHandler(e, _this.props.accessibility, _this.props.rtl);
      dir !== "" && _this.changeSlide({
        message: dir
      });
    });

    _defineProperty(_assertThisInitialized(_this), "selectHandler", function (options) {
      _this.changeSlide(options);
    });

    _defineProperty(_assertThisInitialized(_this), "disableBodyScroll", function () {
      var preventDefault = function preventDefault(e) {
        e = e || window.event;
        if (e.preventDefault) e.preventDefault();
        e.returnValue = false;
      };

      window.ontouchmove = preventDefault;
    });

    _defineProperty(_assertThisInitialized(_this), "enableBodyScroll", function () {
      window.ontouchmove = null;
    });

    _defineProperty(_assertThisInitialized(_this), "swipeStart", function (e) {
      if (_this.props.verticalSwiping) {
        _this.disableBodyScroll();
      }

      var state = swipeStart(e, _this.props.swipe, _this.props.draggable);
      state !== "" && _this.setState(state);
    });

    _defineProperty(_assertThisInitialized(_this), "swipeMove", function (e) {
      var state = swipeMove(e, _objectSpread2(_objectSpread2(_objectSpread2({}, _this.props), _this.state), {}, {
        trackRef: _this.track,
        listRef: _this.list,
        slideIndex: _this.state.currentSlide
      }));
      if (!state) return;

      if (state["swiping"]) {
        _this.clickable = false;
      }

      _this.setState(state);
    });

    _defineProperty(_assertThisInitialized(_this), "swipeEnd", function (e) {
      var state = swipeEnd(e, _objectSpread2(_objectSpread2(_objectSpread2({}, _this.props), _this.state), {}, {
        trackRef: _this.track,
        listRef: _this.list,
        slideIndex: _this.state.currentSlide
      }));
      if (!state) return;
      var triggerSlideHandler = state["triggerSlideHandler"];
      delete state["triggerSlideHandler"];

      _this.setState(state);

      if (triggerSlideHandler === undefined) return;

      _this.slideHandler(triggerSlideHandler);

      if (_this.props.verticalSwiping) {
        _this.enableBodyScroll();
      }
    });

    _defineProperty(_assertThisInitialized(_this), "touchEnd", function (e) {
      _this.swipeEnd(e);

      _this.clickable = true;
    });

    _defineProperty(_assertThisInitialized(_this), "slickPrev", function () {
      // this and fellow methods are wrapped in setTimeout
      // to make sure initialize setState has happened before
      // any of such methods are called
      _this.callbackTimers.push(setTimeout(function () {
        return _this.changeSlide({
          message: "previous"
        });
      }, 0));
    });

    _defineProperty(_assertThisInitialized(_this), "slickNext", function () {
      _this.callbackTimers.push(setTimeout(function () {
        return _this.changeSlide({
          message: "next"
        });
      }, 0));
    });

    _defineProperty(_assertThisInitialized(_this), "slickGoTo", function (slide) {
      var dontAnimate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      slide = Number(slide);
      if (isNaN(slide)) return "";

      _this.callbackTimers.push(setTimeout(function () {
        return _this.changeSlide({
          message: "index",
          index: slide,
          currentSlide: _this.state.currentSlide
        }, dontAnimate);
      }, 0));
    });

    _defineProperty(_assertThisInitialized(_this), "play", function () {
      var nextIndex;

      if (_this.props.rtl) {
        nextIndex = _this.state.currentSlide - _this.props.slidesToScroll;
      } else {
        if (canGoNext(_objectSpread2(_objectSpread2({}, _this.props), _this.state))) {
          nextIndex = _this.state.currentSlide + _this.props.slidesToScroll;
        } else {
          return false;
        }
      }

      _this.slideHandler(nextIndex);
    });

    _defineProperty(_assertThisInitialized(_this), "autoPlay", function (playType) {
      if (_this.autoplayTimer) {
        clearInterval(_this.autoplayTimer);
      }

      var autoplaying = _this.state.autoplaying;

      if (playType === "update") {
        if (autoplaying === "hovered" || autoplaying === "focused" || autoplaying === "paused") {
          return;
        }
      } else if (playType === "leave") {
        if (autoplaying === "paused" || autoplaying === "focused") {
          return;
        }
      } else if (playType === "blur") {
        if (autoplaying === "paused" || autoplaying === "hovered") {
          return;
        }
      }

      _this.autoplayTimer = setInterval(_this.play, _this.props.autoplaySpeed + 50);

      _this.setState({
        autoplaying: "playing"
      });
    });

    _defineProperty(_assertThisInitialized(_this), "pause", function (pauseType) {
      if (_this.autoplayTimer) {
        clearInterval(_this.autoplayTimer);
        _this.autoplayTimer = null;
      }

      var autoplaying = _this.state.autoplaying;

      if (pauseType === "paused") {
        _this.setState({
          autoplaying: "paused"
        });
      } else if (pauseType === "focused") {
        if (autoplaying === "hovered" || autoplaying === "playing") {
          _this.setState({
            autoplaying: "focused"
          });
        }
      } else {
        // pauseType  is 'hovered'
        if (autoplaying === "playing") {
          _this.setState({
            autoplaying: "hovered"
          });
        }
      }
    });

    _defineProperty(_assertThisInitialized(_this), "onDotsOver", function () {
      return _this.props.autoplay && _this.pause("hovered");
    });

    _defineProperty(_assertThisInitialized(_this), "onDotsLeave", function () {
      return _this.props.autoplay && _this.state.autoplaying === "hovered" && _this.autoPlay("leave");
    });

    _defineProperty(_assertThisInitialized(_this), "onTrackOver", function () {
      return _this.props.autoplay && _this.pause("hovered");
    });

    _defineProperty(_assertThisInitialized(_this), "onTrackLeave", function () {
      return _this.props.autoplay && _this.state.autoplaying === "hovered" && _this.autoPlay("leave");
    });

    _defineProperty(_assertThisInitialized(_this), "onSlideFocus", function () {
      return _this.props.autoplay && _this.pause("focused");
    });

    _defineProperty(_assertThisInitialized(_this), "onSlideBlur", function () {
      return _this.props.autoplay && _this.state.autoplaying === "focused" && _this.autoPlay("blur");
    });

    _defineProperty(_assertThisInitialized(_this), "render", function () {
      var className = classnames("slick-slider", _this.props.className, {
        "slick-vertical": _this.props.vertical,
        "slick-initialized": true
      });

      var spec = _objectSpread2(_objectSpread2({}, _this.props), _this.state);

      var trackProps = extractObject(spec, ["fade", "cssEase", "speed", "infinite", "centerMode", "focusOnSelect", "currentSlide", "lazyLoad", "lazyLoadedList", "rtl", "slideWidth", "slideHeight", "listHeight", "vertical", "slidesToShow", "slidesToScroll", "slideCount", "trackStyle", "variableWidth", "unslick", "centerPadding", "targetSlide", "useCSS"]);
      var pauseOnHover = _this.props.pauseOnHover;
      trackProps = _objectSpread2(_objectSpread2({}, trackProps), {}, {
        onMouseEnter: pauseOnHover ? _this.onTrackOver : null,
        onMouseLeave: pauseOnHover ? _this.onTrackLeave : null,
        onMouseOver: pauseOnHover ? _this.onTrackOver : null,
        focusOnSelect: _this.props.focusOnSelect && _this.clickable ? _this.selectHandler : null
      });
      var dots;

      if (_this.props.dots === true && _this.state.slideCount >= _this.props.slidesToShow) {
        var dotProps = extractObject(spec, ["dotsClass", "slideCount", "slidesToShow", "currentSlide", "slidesToScroll", "clickHandler", "children", "customPaging", "infinite", "appendDots"]);
        var pauseOnDotsHover = _this.props.pauseOnDotsHover;
        dotProps = _objectSpread2(_objectSpread2({}, dotProps), {}, {
          clickHandler: _this.changeSlide,
          onMouseEnter: pauseOnDotsHover ? _this.onDotsLeave : null,
          onMouseOver: pauseOnDotsHover ? _this.onDotsOver : null,
          onMouseLeave: pauseOnDotsHover ? _this.onDotsLeave : null
        });
        dots = /*#__PURE__*/React.createElement(Dots, dotProps);
      }

      var prevArrow, nextArrow;
      var arrowProps = extractObject(spec, ["infinite", "centerMode", "currentSlide", "slideCount", "slidesToShow", "prevArrow", "nextArrow"]);
      arrowProps.clickHandler = _this.changeSlide;

      if (_this.props.arrows) {
        prevArrow = /*#__PURE__*/React.createElement(PrevArrow, arrowProps);
        nextArrow = /*#__PURE__*/React.createElement(NextArrow, arrowProps);
      }

      var verticalHeightStyle = null;

      if (_this.props.vertical) {
        verticalHeightStyle = {
          height: _this.state.listHeight
        };
      }

      var centerPaddingStyle = null;

      if (_this.props.vertical === false) {
        if (_this.props.centerMode === true) {
          centerPaddingStyle = {
            padding: "0px " + _this.props.centerPadding
          };
        }
      } else {
        if (_this.props.centerMode === true) {
          centerPaddingStyle = {
            padding: _this.props.centerPadding + " 0px"
          };
        }
      }

      var listStyle = _objectSpread2(_objectSpread2({}, verticalHeightStyle), centerPaddingStyle);

      var touchMove = _this.props.touchMove;
      var listProps = {
        className: "slick-list",
        style: listStyle,
        onClick: _this.clickHandler,
        onMouseDown: touchMove ? _this.swipeStart : null,
        onMouseMove: _this.state.dragging && touchMove ? _this.swipeMove : null,
        onMouseUp: touchMove ? _this.swipeEnd : null,
        onMouseLeave: _this.state.dragging && touchMove ? _this.swipeEnd : null,
        onTouchStart: touchMove ? _this.swipeStart : null,
        onTouchMove: _this.state.dragging && touchMove ? _this.swipeMove : null,
        onTouchEnd: touchMove ? _this.touchEnd : null,
        onTouchCancel: _this.state.dragging && touchMove ? _this.swipeEnd : null,
        onKeyDown: _this.props.accessibility ? _this.keyHandler : null
      };
      var innerSliderProps = {
        className: className,
        dir: "ltr",
        style: _this.props.style
      };

      if (_this.props.unslick) {
        listProps = {
          className: "slick-list"
        };
        innerSliderProps = {
          className: className
        };
      }

      return /*#__PURE__*/React.createElement("div", innerSliderProps, !_this.props.unslick ? prevArrow : "", /*#__PURE__*/React.createElement("div", _extends$1({
        ref: _this.listRefHandler
      }, listProps), /*#__PURE__*/React.createElement(Track, _extends$1({
        ref: _this.trackRefHandler
      }, trackProps), _this.props.children)), !_this.props.unslick ? nextArrow : "", !_this.props.unslick ? dots : "");
    });

    _this.list = null;
    _this.track = null;
    _this.state = _objectSpread2(_objectSpread2({}, initialState), {}, {
      currentSlide: _this.props.initialSlide,
      slideCount: React.Children.count(_this.props.children)
    });
    _this.callbackTimers = [];
    _this.clickable = true;
    _this.debouncedResize = null;

    var ssrState = _this.ssrInit();

    _this.state = _objectSpread2(_objectSpread2({}, _this.state), ssrState);
    return _this;
  }

  _createClass(InnerSlider, [{
    key: "didPropsChange",
    value: function didPropsChange(prevProps) {
      var setTrackStyle = false;

      for (var _i3 = 0, _Object$keys = Object.keys(this.props); _i3 < _Object$keys.length; _i3++) {
        var key = _Object$keys[_i3];

        // eslint-disable-next-line no-prototype-builtins
        if (!prevProps.hasOwnProperty(key)) {
          setTrackStyle = true;
          break;
        }

        if (_typeof(prevProps[key]) === "object" || typeof prevProps[key] === "function") {
          continue;
        }

        if (prevProps[key] !== this.props[key]) {
          setTrackStyle = true;
          break;
        }
      }

      return setTrackStyle || React.Children.count(this.props.children) !== React.Children.count(prevProps.children);
    }
  }]);

  return InnerSlider;
}(React.Component);

var camel2hyphen$1 = function (str) {
  return str
          .replace(/[A-Z]/g, function (match) {
            return '-' + match.toLowerCase();
          })
          .toLowerCase();
};

var camel2hyphen_1 = camel2hyphen$1;

var camel2hyphen = camel2hyphen_1;

var isDimension = function (feature) {
  var re = /[height|width]$/;
  return re.test(feature);
};

var obj2mq = function (obj) {
  var mq = '';
  var features = Object.keys(obj);
  features.forEach(function (feature, index) {
    var value = obj[feature];
    feature = camel2hyphen(feature);
    // Add px to dimension features
    if (isDimension(feature) && typeof value === 'number') {
      value = value + 'px';
    }
    if (value === true) {
      mq += feature;
    } else if (value === false) {
      mq += 'not ' + feature;
    } else {
      mq += '(' + feature + ': ' + value + ')';
    }
    if (index < features.length-1) {
      mq += ' and ';
    }
  });
  return mq;
};

var json2mq = function (query) {
  var mq = '';
  if (typeof query === 'string') {
    return query;
  }
  // Handling array of media queries
  if (query instanceof Array) {
    query.forEach(function (q, index) {
      mq += obj2mq(q);
      if (index < query.length-1) {
        mq += ', ';
      }
    });
    return mq;
  }
  // Handling single media query
  return obj2mq(query);
};

var json2mq_1 = json2mq;

var defaultProps = {
  accessibility: true,
  adaptiveHeight: false,
  afterChange: null,
  appendDots: function appendDots(dots) {
    return /*#__PURE__*/React.createElement("ul", {
      style: {
        display: "block"
      }
    }, dots);
  },
  arrows: true,
  autoplay: false,
  autoplaySpeed: 3000,
  beforeChange: null,
  centerMode: false,
  centerPadding: "50px",
  className: "",
  cssEase: "ease",
  customPaging: function customPaging(i) {
    return /*#__PURE__*/React.createElement("button", null, i + 1);
  },
  dots: false,
  dotsClass: "slick-dots",
  draggable: true,
  easing: "linear",
  edgeFriction: 0.35,
  fade: false,
  focusOnSelect: false,
  infinite: true,
  initialSlide: 0,
  lazyLoad: null,
  nextArrow: null,
  onEdge: null,
  onInit: null,
  onLazyLoadError: null,
  onReInit: null,
  pauseOnDotsHover: false,
  pauseOnFocus: false,
  pauseOnHover: true,
  prevArrow: null,
  responsive: null,
  rows: 1,
  rtl: false,
  slide: "div",
  slidesPerRow: 1,
  slidesToScroll: 1,
  slidesToShow: 1,
  speed: 500,
  swipe: true,
  swipeEvent: null,
  swipeToSlide: false,
  touchMove: true,
  touchThreshold: 5,
  useCSS: true,
  useTransform: true,
  variableWidth: false,
  vertical: false,
  waitForAnimate: true
};

var Slider$1 = /*#__PURE__*/function (_React$Component) {
  _inherits(Slider, _React$Component);

  var _super = _createSuper(Slider);

  function Slider(props) {
    var _this;

    _classCallCheck(this, Slider);

    _this = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this), "innerSliderRefHandler", function (ref) {
      return _this.innerSlider = ref;
    });

    _defineProperty(_assertThisInitialized(_this), "slickPrev", function () {
      return _this.innerSlider.slickPrev();
    });

    _defineProperty(_assertThisInitialized(_this), "slickNext", function () {
      return _this.innerSlider.slickNext();
    });

    _defineProperty(_assertThisInitialized(_this), "slickGoTo", function (slide) {
      var dontAnimate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      return _this.innerSlider.slickGoTo(slide, dontAnimate);
    });

    _defineProperty(_assertThisInitialized(_this), "slickPause", function () {
      return _this.innerSlider.pause("paused");
    });

    _defineProperty(_assertThisInitialized(_this), "slickPlay", function () {
      return _this.innerSlider.autoPlay("play");
    });

    _this.state = {
      breakpoint: null
    };
    _this._responsiveMediaHandlers = [];
    return _this;
  }

  _createClass(Slider, [{
    key: "media",
    value: function media(query, handler) {
      // javascript handler for  css media query
      var mql = window.matchMedia(query);

      var listener = function listener(_ref) {
        var matches = _ref.matches;

        if (matches) {
          handler();
        }
      };

      mql.addListener(listener);
      listener(mql);

      this._responsiveMediaHandlers.push({
        mql: mql,
        query: query,
        listener: listener
      });
    } // handles responsive breakpoints

  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      var _this2 = this;

      // performance monitoring
      //if (process.env.NODE_ENV !== 'production') {
      //const { whyDidYouUpdate } = require('why-did-you-update')
      //whyDidYouUpdate(React)
      //}
      if (this.props.responsive) {
        var breakpoints = this.props.responsive.map(function (breakpt) {
          return breakpt.breakpoint;
        }); // sort them in increasing order of their numerical value

        breakpoints.sort(function (x, y) {
          return x - y;
        });
        breakpoints.forEach(function (breakpoint, index) {
          // media query for each breakpoint
          var bQuery;

          if (index === 0) {
            bQuery = json2mq_1({
              minWidth: 0,
              maxWidth: breakpoint
            });
          } else {
            bQuery = json2mq_1({
              minWidth: breakpoints[index - 1] + 1,
              maxWidth: breakpoint
            });
          } // when not using server side rendering


          canUseDOM() && _this2.media(bQuery, function () {
            _this2.setState({
              breakpoint: breakpoint
            });
          });
        }); // Register media query for full screen. Need to support resize from small to large
        // convert javascript object to media query string

        var query = json2mq_1({
          minWidth: breakpoints.slice(-1)[0]
        });
        canUseDOM() && this.media(query, function () {
          _this2.setState({
            breakpoint: null
          });
        });
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this._responsiveMediaHandlers.forEach(function (obj) {
        obj.mql.removeListener(obj.listener);
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      var settings;
      var newProps;

      if (this.state.breakpoint) {
        newProps = this.props.responsive.filter(function (resp) {
          return resp.breakpoint === _this3.state.breakpoint;
        });
        settings = newProps[0].settings === "unslick" ? "unslick" : _objectSpread2(_objectSpread2(_objectSpread2({}, defaultProps), this.props), newProps[0].settings);
      } else {
        settings = _objectSpread2(_objectSpread2({}, defaultProps), this.props);
      } // force scrolling by one if centerMode is on


      if (settings.centerMode) {
        if (settings.slidesToScroll > 1 && process.env.NODE_ENV !== "production") {
          console.warn("slidesToScroll should be equal to 1 in centerMode, you are using ".concat(settings.slidesToScroll));
        }

        settings.slidesToScroll = 1;
      } // force showing one slide and scrolling by one if the fade mode is on


      if (settings.fade) {
        if (settings.slidesToShow > 1 && process.env.NODE_ENV !== "production") {
          console.warn("slidesToShow should be equal to 1 when fade is true, you're using ".concat(settings.slidesToShow));
        }

        if (settings.slidesToScroll > 1 && process.env.NODE_ENV !== "production") {
          console.warn("slidesToScroll should be equal to 1 when fade is true, you're using ".concat(settings.slidesToScroll));
        }

        settings.slidesToShow = 1;
        settings.slidesToScroll = 1;
      } // makes sure that children is an array, even when there is only 1 child


      var children = React.Children.toArray(this.props.children); // Children may contain false or null, so we should filter them
      // children may also contain string filled with spaces (in certain cases where we use jsx strings)

      children = children.filter(function (child) {
        if (typeof child === "string") {
          return !!child.trim();
        }

        return !!child;
      }); // rows and slidesPerRow logic is handled here

      if (settings.variableWidth && (settings.rows > 1 || settings.slidesPerRow > 1)) {
        console.warn("variableWidth is not supported in case of rows > 1 or slidesPerRow > 1");
        settings.variableWidth = false;
      }

      var newChildren = [];
      var currentWidth = null;

      for (var i = 0; i < children.length; i += settings.rows * settings.slidesPerRow) {
        var newSlide = [];

        for (var j = i; j < i + settings.rows * settings.slidesPerRow; j += settings.slidesPerRow) {
          var row = [];

          for (var k = j; k < j + settings.slidesPerRow; k += 1) {
            if (settings.variableWidth && children[k].props.style) {
              currentWidth = children[k].props.style.width;
            }

            if (k >= children.length) break;
            row.push( /*#__PURE__*/React.cloneElement(children[k], {
              key: 100 * i + 10 * j + k,
              tabIndex: -1,
              style: {
                width: "".concat(100 / settings.slidesPerRow, "%"),
                display: "inline-block"
              }
            }));
          }

          newSlide.push( /*#__PURE__*/React.createElement("div", {
            key: 10 * i + j
          }, row));
        }

        if (settings.variableWidth) {
          newChildren.push( /*#__PURE__*/React.createElement("div", {
            key: i,
            style: {
              width: currentWidth
            }
          }, newSlide));
        } else {
          newChildren.push( /*#__PURE__*/React.createElement("div", {
            key: i
          }, newSlide));
        }
      }

      if (settings === "unslick") {
        var className = "regular slider " + (this.props.className || "");
        return /*#__PURE__*/React.createElement("div", {
          className: className
        }, children);
      } else if (newChildren.length <= settings.slidesToShow) {
        settings.unslick = true;
      }

      return /*#__PURE__*/React.createElement(InnerSlider, _extends$1({
        style: this.props.style,
        ref: this.innerSliderRefHandler
      }, settings), newChildren);
    }
  }]);

  return Slider;
}(React.Component);

var __defProp$5 = Object.defineProperty;
var __defProps$2 = Object.defineProperties;
var __getOwnPropDescs$2 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$5 = Object.getOwnPropertySymbols;
var __hasOwnProp$5 = Object.prototype.hasOwnProperty;
var __propIsEnum$5 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$5 = (obj, key, value) => key in obj ? __defProp$5(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$5 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$5.call(b, prop))
      __defNormalProp$5(a, prop, b[prop]);
  if (__getOwnPropSymbols$5)
    for (var prop of __getOwnPropSymbols$5(b)) {
      if (__propIsEnum$5.call(b, prop))
        __defNormalProp$5(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$2 = (a, b) => __defProps$2(a, __getOwnPropDescs$2(b));
const defaultSettings = {
  arrows: false,
  infinite: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false,
  autoplaySpeed: 0
};
const getSelectedDimension = (responsive, windowWidth) => {
  const responsiveCandidate = responsive.find((item) => item.breakpoint > windowWidth);
  const { config } = responsiveCandidate != null ? responsiveCandidate : {};
  if (!config) {
    return;
  }
  const { width } = config;
  if (typeof width !== "number") {
    return __spreadProps$2(__spreadValues$5({}, config), { width: windowWidth });
  }
  return config;
};
const getSettings = (overrides = {}) => {
  return __spreadValues$5(__spreadValues$5({}, defaultSettings), overrides);
};

const SliderStyled = styled.div`
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  overflow: hidden;
  max-width: min(
    ${(props) => (props.slideWidth + props.gap) * props.slidesCount - props.gap}px,
    100%,
    ${getScreenMaxWidth()}
  );

  .slick-list {
    overflow: initial;
    width: ${(props) => props.slideWidth + props.gap}px;
  }

  .slick-slide {
    &:not(:last-child) {
      .Slider__slide {
        margin-right: ${(props) => props.gap}px;
      }
    }
  }

  .slick-prev,
  .slick-next {
    z-index: 2;
  }

  .Slider {
    &__slide {
      overflow: hidden;
      max-width: ${(props) => props.slideWidth}px;
      height: ${(props) => props.height}px;
    }
  }
`;

var __defProp$4 = Object.defineProperty;
var __getOwnPropSymbols$4 = Object.getOwnPropertySymbols;
var __hasOwnProp$4 = Object.prototype.hasOwnProperty;
var __propIsEnum$4 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$4 = (obj, key, value) => key in obj ? __defProp$4(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$4 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$4.call(b, prop))
      __defNormalProp$4(a, prop, b[prop]);
  if (__getOwnPropSymbols$4)
    for (var prop of __getOwnPropSymbols$4(b)) {
      if (__propIsEnum$4.call(b, prop))
        __defNormalProp$4(a, prop, b[prop]);
    }
  return a;
};
const Slider = (props) => {
  const { className, width, children, height, responsive = [], gap } = props;
  const { slideWidth, animated, infinite, arrows } = props;
  const { prevArrow, nextArrow } = props;
  const { width: windowWidth } = useWindowSize();
  const defaultDimensions = useMemo(() => ({ height, width: width != null ? width : windowWidth, slideWidth, gap }), [width, height, slideWidth, windowWidth, gap]);
  const responsiveCandidate = getSelectedDimension(responsive, windowWidth);
  const selectedConfig = responsiveCandidate != null ? responsiveCandidate : defaultDimensions;
  const { width: selectedWidth, height: selectedHeight } = selectedConfig;
  const actualChildren = getChildren(children);
  const settingOverrides = {
    autoplay: !!animated,
    autoplaySpeed: animated,
    infinite: !!infinite,
    arrows: !!arrows,
    prevArrow,
    nextArrow
  };
  const settings = getSettings(settingOverrides);
  return /* @__PURE__ */ React.createElement(SliderStyled, {
    className: `Slider ${className}`,
    slideWidth,
    width: selectedWidth != null ? selectedWidth : windowWidth,
    height: selectedHeight,
    gap,
    slidesCount: actualChildren.length
  }, /* @__PURE__ */ React.createElement(Slider$1, __spreadValues$4({}, settings), actualChildren.map((slide, index) => {
    return /* @__PURE__ */ React.createElement("div", {
      className: "Slider__slide",
      key: index
    }, slide);
  })));
};
Slider.defaultProps = {
  className: "",
  arrows: false,
  responsive: []
};

const StaticMapStyled = styled.div`
  .StaticMap {
    &__image {
      object-fit: cover;
    }
  }
`;

const generateStaticMap = (config) => {
  const { markers, width, height, googleMapsKey, zoom, center, poi } = config;
  const markersString = markers == null ? void 0 : markers.reduce((acc, marker) => {
    const { icon, coordinates } = marker;
    return acc + `&markers=icon:${icon}|${coordinatesToText(coordinates)}`;
  }, "");
  const baseUrl = "https://maps.googleapis.com/maps/api/staticmap";
  const commonParameters = `size=${width}x${height}&key=${googleMapsKey}`;
  const zoomParameter = zoom ? `&zoom=${zoom}` : "";
  const centerParameter = center ? `&center=${coordinatesToText(center)}` : "";
  const poiParameter = !poi ? `&style=feature:poi|visibility:off` : "";
  return `${baseUrl}?${commonParameters}${markersString != null ? markersString : ""}${zoomParameter}${centerParameter}${poiParameter}`;
};
const coordinatesToText = (coordinates) => {
  if (!coordinates) {
    return void 0;
  }
  const { lat, lng } = coordinates;
  return `${lat},${lng}`;
};

var __defProp$3 = Object.defineProperty;
var __defProps$1 = Object.defineProperties;
var __getOwnPropDescs$1 = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$3 = Object.getOwnPropertySymbols;
var __hasOwnProp$3 = Object.prototype.hasOwnProperty;
var __propIsEnum$3 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$3 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$3.call(b, prop))
      __defNormalProp$3(a, prop, b[prop]);
  if (__getOwnPropSymbols$3)
    for (var prop of __getOwnPropSymbols$3(b)) {
      if (__propIsEnum$3.call(b, prop))
        __defNormalProp$3(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps$1 = (a, b) => __defProps$1(a, __getOwnPropDescs$1(b));
var __objRest$2 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$3.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$3)
    for (var prop of __getOwnPropSymbols$3(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$3.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const StaticMap = forwardRef((props, ref) => {
  const _a = props, {
    googleMapsKey,
    markers,
    alt = "static map",
    zoom,
    center,
    className,
    width,
    height,
    poi = true
  } = _a, rest = __objRest$2(_a, [
    "googleMapsKey",
    "markers",
    "alt",
    "zoom",
    "center",
    "className",
    "width",
    "height",
    "poi"
  ]);
  if (!center && (!markers || !(markers == null ? void 0 : markers.length))) {
    console.error("You must provide markers prop or center prop and zoom prop to StaticMap component");
  }
  return /* @__PURE__ */ React.createElement(StaticMapStyled, {
    className: `StaticMap ${className}`
  }, /* @__PURE__ */ React.createElement(Image$1, __spreadProps$1(__spreadValues$3({
    className: "StaticMap__image"
  }, rest), {
    ref,
    alt,
    image: generateStaticMap({
      height,
      width,
      markers,
      googleMapsKey,
      zoom,
      center,
      poi
    }),
    width,
    height
  })));
});
StaticMap.defaultProps = {
  className: "",
  alt: "static map",
  poi: true
};

const SwitchStyled = styled.div`
  .Switch {
    &__wrapper {
      opacity: ${(props) => props.isDisabled ? "0.6" : "1"};
      position: relative;
      width: 48px;
      height: 24px;
      border: none;
      border-radius: 96px;
      overflow: hidden;
      margin-top: 4px;

      &:hover {
        opacity: 0.6;
      }

      &:focus-within {
        box-shadow: ${(props) => props.checked ? "0 0 2px 0 var(--palette-primary, #4611cf)" : "0 0 2px 0 #707070"};
        border-radius: 96px;
      }
    }

    &__label {
      padding-bottom: 4px;
      font-size: 12px;
      color: #11111180;
    }

    &__description {
      font-size: 10px;
      color: #11111180;
    }

    &__input {
      width: 100%;
      height: 100%;
      border-radius: 96px;
      cursor: ${(props) => props.isDisabled ? "default" : "pointer"};

      &:checked + .Switch__slider {
        background-color: var(--palette-primary, #4611cf);
      }

      &:checked + .Switch__slider:before {
        transform: translate(24px, -50%);
        background-color: #ffffff;
      }
    }

    &__slider {
      pointer-events: none;
      position: absolute;
      cursor: pointer;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: #707070;
      transition: 0.4s;

      &:before {
        pointer-events: none;
        position: absolute;
        content: "";
        height: 16px;
        width: 16px;
        left: 4px;
        top: 50%;
        transform: translateY(-50%);
        background-color: #ffffff;
        transition: 0.4s;
        border-radius: 50%;
      }
    }

    &__icon {
      position: absolute;
      display: flex;
      align-items: center;
      transform: translateY(-50%);
      top: 50%;

      &--on {
        left: 4px;
      }

      &--off {
        right: 4px;
      }
    }

    &__errorMessage {
      display: inline-block;
      padding-top: 4px;
      font-size: 12px;
      color: #eb5757;
    }
  }
`;

var __defProp$2 = Object.defineProperty;
var __getOwnPropSymbols$2 = Object.getOwnPropertySymbols;
var __hasOwnProp$2 = Object.prototype.hasOwnProperty;
var __propIsEnum$2 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$2 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$2.call(b, prop))
      __defNormalProp$2(a, prop, b[prop]);
  if (__getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(b)) {
      if (__propIsEnum$2.call(b, prop))
        __defNormalProp$2(a, prop, b[prop]);
    }
  return a;
};
var __objRest$1 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$2.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$2)
    for (var prop of __getOwnPropSymbols$2(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$2.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Switch = forwardRef((props, ref) => {
  const _a = props, {
    label,
    className,
    name,
    id,
    checked,
    checkedIcon,
    description,
    unCheckedIcon,
    disabled = false,
    isDisabled: forceDisabled,
    errorMessage,
    onChange
  } = _a, rest = __objRest$1(_a, [
    "label",
    "className",
    "name",
    "id",
    "checked",
    "checkedIcon",
    "description",
    "unCheckedIcon",
    "disabled",
    "isDisabled",
    "errorMessage",
    "onChange"
  ]);
  const [disable, setDisable] = useState(false);
  const ariaLabel = checked ? "checked" : "unchecked";
  const errorMessageId = `${id != null ? id : name}-error`;
  const changeHandler = async (check) => {
    if (disable || forceDisabled)
      return;
    setDisable(forceDisabled != null ? forceDisabled : true);
    await onChange(check);
    setDisable(forceDisabled != null ? forceDisabled : false);
  };
  const renderLabel = () => {
    if (!label)
      return null;
    return /* @__PURE__ */ React.createElement("label", {
      className: "Switch__label",
      htmlFor: id != null ? id : name
    }, label);
  };
  const renderDescription = () => {
    if (!description)
      return null;
    return /* @__PURE__ */ React.createElement("p", {
      className: "Switch__description"
    }, description);
  };
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "Switch__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "Switch__errorMessage"
    }, errorMessage);
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn("SwitchUI: when using the label tag consider passing the 'name' prop");
  }, [name, label]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn("Switch: If you want to display an error consider passing the 'name' prop or 'id' prop or both");
  }, [name, id, errorMessage]);
  return /* @__PURE__ */ React.createElement(SwitchStyled, __spreadValues$2({
    className: `Switch ${className}`,
    isDisabled: forceDisabled != null ? forceDisabled : disabled,
    checked
  }, rest), renderLabel(), renderDescription(), /* @__PURE__ */ React.createElement("div", {
    className: "Switch__wrapper"
  }, /* @__PURE__ */ React.createElement("input", {
    className: "Switch__input",
    id: id != null ? id : name,
    name,
    type: "checkbox",
    ref,
    checked,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId,
    "aria-label": ariaLabel,
    disabled: forceDisabled != null ? forceDisabled : disabled,
    onChange: (e) => changeHandler(e.target.checked)
  }), /* @__PURE__ */ React.createElement("div", {
    className: "Switch__slider"
  }, checked ? /* @__PURE__ */ React.createElement("span", {
    role: "img",
    "aria-label": ariaLabel,
    className: "Switch__icon Switch__icon--on"
  }, checkedIcon) : /* @__PURE__ */ React.createElement("span", {
    role: "img",
    "aria-label": ariaLabel,
    className: "Switch__icon Switch__icon--off"
  }, unCheckedIcon))), renderErrorMessage());
});
Switch.defaultProps = {
  className: "",
  checkedIcon: /* @__PURE__ */ React.createElement(React.Fragment, null),
  unCheckedIcon: /* @__PURE__ */ React.createElement(React.Fragment, null)
};

const TabsMenuStyled = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  isolation: isolate;

  .TabsMenu {
    &__page {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: ${getScreenMaxHeight()};

      & > *:last-child {
        padding-bottom: ${(props) => props.barHeight}px;
      }
    }

    &__bar {
      background-color: #fff;
      box-shadow: 0px -2px 4px rgba(0, 0, 0, 0.1);
      bottom: 0;
      left: 0;
      position: fixed;
      width: ${getScreenMaxWidth()};
      z-index: 4;
    }

    &__tabs {
      display: flex;
      align-items: center;
      justify-content: space-evenly;
      padding: 0.8rem 0;

      &__item {
        list-style: none;
        cursor: pointer;
      }
    }
  }
`;

const TabsMenuItemStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .TabsMenuItem {
    &__icon {
    }

    &__title {
      padding-top: ${(props) => props.title ? 0.4 : 0}rem;
      color: #000;
      font-size: 1.2rem;
    }
  }
`;

const TabsMenuItem = (props) => {
  const { icon, title, className, onClick } = props;
  const getIcon = () => {
    if (typeof icon === "string") {
      return /* @__PURE__ */ React.createElement("img", {
        src: icon,
        alt: title,
        className: "TabsMenuItem__icon"
      });
    }
    const Icon = icon;
    return /* @__PURE__ */ React.createElement(Icon, {
      className: "TabsMenuItem__icon"
    });
  };
  return /* @__PURE__ */ React.createElement(TabsMenuItemStyled, {
    className: `TabsMenuItem ${className}`,
    onClick,
    title
  }, getIcon(), /* @__PURE__ */ React.createElement("p", {
    className: "TabsMenuItem__title"
  }, title));
};
TabsMenuItem.defaultProps = {
  className: ""
};

const { BREAKPOINTS } = CONSTANTS;
const TabsMenu = (props) => {
  const { children, className, maxWidth = 480, sidesPadding = 16 } = props;
  const { tabs, maxMobileWidth = BREAKPOINTS.tablet } = props;
  const { width } = useWindowSize();
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(32);
  const isDesktop = width >= maxMobileWidth;
  const getTabs = () => {
    if (Array.isArray(tabs))
      return tabs.filter((tab) => !!tab);
    return [tabs].filter((tab) => !!tab);
  };
  useIsomorphicLayoutEffect(() => {
    if (!barRef.current)
      return;
    const newHeight = barRef.current.getBoundingClientRect().height;
    setBarHeight(newHeight);
  }, []);
  if (isDesktop)
    return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
  return /* @__PURE__ */ React.createElement(TabsMenuStyled, {
    className: `TabsMenu ${className}`,
    barHeight
  }, /* @__PURE__ */ React.createElement("div", {
    className: "TabsMenu__page"
  }, children), /* @__PURE__ */ React.createElement("div", {
    className: "TabsMenu__bar",
    ref: barRef
  }, /* @__PURE__ */ React.createElement(MaxWidth, {
    size: maxWidth,
    minPadding: sidesPadding
  }, /* @__PURE__ */ React.createElement("ul", {
    className: "TabsMenu__tabs"
  }, getTabs().map((child, index) => {
    return /* @__PURE__ */ React.createElement("li", {
      key: index,
      className: "TabsMenu__tabs__item"
    }, child);
  })))));
};
TabsMenu.Item = TabsMenuItem;
TabsMenu.defaultProps = {
  className: ""
};

const calculateSizes = (props) => {
  const { vertical, width, height } = props;
  if (vertical) {
    return css`
      width: ${width ? `${width}px` : `auto`};
      height: ${height ? `${height}px` : `100%`};
    `;
  }
  return css`
    width: ${width ? `${width}px` : `100%`};
    height: ${height ? `${height}px` : `auto`};
  `;
};
const ThumbnailCarouselStyled = styled.div`
  display: flex;
  flex-direction: ${(props) => props.vertical ? `column` : `row`};
  justify-content: center;
  align-items: center;

  .ThumbnailCarousel {
    &__slider {
      display: flex;
      overflow: scroll;
      flex-direction: ${(props) => props.vertical ? `column` : `row`};
      ${calculateSizes}
      scroll-snap-type: both mandatory;
      gap: ${(props) => `${props.gap}px`};
      -ms-overflow-style: none;
      scrollbar-width: none;
      align-items: center;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    &__action {
      display: flex;
      justify-content: center;
      align-items: center;

      &--90 {
        transform: rotate(90deg);
      }

      &--180 {
        transform: rotate(180deg);
      }

      &--270 {
        transform: rotate(270deg);
      }
    }

    &__slider-item {
      height: ${(props) => `${props.itemHeight}px`};
      width: ${(props) => `${props.itemWidth}px`};
      scroll-snap-align: start;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
`;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
var Arrow = (({
  styles = {},
  ...props
}) => /*#__PURE__*/React.createElement("svg", _extends({
  width: "6",
  height: "13",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}, props), /*#__PURE__*/React.createElement("path", {
  d: "M5.185.662l.815.92-4.37 4.924L6 11.431l-.815.92L0 6.505 5.185.662z",
  fill: "#000"
})));

var __defProp$1 = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const ThumbnailCarousel = (props) => {
  var _a, _b;
  const { images, className, gap = 8, height, width } = props;
  const { onClickItem, onHoverItem, previousIcon: previousIconProp } = props;
  const { vertical, imageConfig, nextIcon: nextIconProp } = props;
  const { showNavigationButtons, slideWidth, slideHeight } = props;
  const { slideClassName, onDoubleClickItem } = props;
  const { activeSlideClassName } = props;
  const cdnWidth = (_a = imageConfig == null ? void 0 : imageConfig.width) != null ? _a : slideWidth;
  const cdnHeight = (_b = imageConfig == null ? void 0 : imageConfig.height) != null ? _b : slideHeight;
  const sliderRef = useRef(null);
  const [localIndex, setLocalIndex] = useState(0);
  const defaultPrevIcon = !vertical ? /* @__PURE__ */ React.createElement(Arrow, null) : /* @__PURE__ */ React.createElement(Arrow, {
    className: "ThumbnailCarousel__action--90"
  });
  const defaultNextIcon = !vertical ? /* @__PURE__ */ React.createElement(Arrow, {
    className: "ThumbnailCarousel__action--180"
  }) : /* @__PURE__ */ React.createElement(Arrow, {
    className: "ThumbnailCarousel__action--270"
  });
  const previousIcon = previousIconProp ? previousIconProp : defaultPrevIcon;
  const nextIcon = nextIconProp ? nextIconProp : defaultNextIcon;
  if (width && slideWidth > width + gap) {
    console.warn("width must be greater than slideWidth");
  }
  if (slideHeight && height && slideHeight > height + gap) {
    console.warn("height must be greater than slideHeight");
  }
  const clickHandler = useCallback((action) => {
    const slider = sliderRef.current;
    if (!slider)
      return;
    const factor = action === "previous" ? -1 : 1;
    const actualScroll = vertical ? slider.scrollTop : slider.scrollLeft;
    const slideSize = (vertical ? slideHeight : slideWidth) + gap;
    let newScroll = actualScroll + slideSize * factor;
    if (newScroll < 0) {
      newScroll = 0;
    }
    const scrollLimit = vertical ? slider.scrollHeight - slider.offsetHeight : slider.scrollWidth - slider.offsetWidth;
    if (newScroll >= scrollLimit) {
      newScroll = vertical ? slider.scrollHeight - slideHeight - gap : slider.scrollWidth - slideWidth - gap;
    }
    const scrollDirection = vertical ? { top: newScroll } : { left: newScroll };
    slider.scrollTo(__spreadProps(__spreadValues$1({}, scrollDirection), { behavior: "smooth" }));
  }, [gap, slideHeight, slideWidth, vertical]);
  const clickItemHandler = (index) => {
    onClickItem == null ? void 0 : onClickItem(index);
    setLocalIndex(index);
  };
  const mouseEnterItemHandler = (index) => {
    onHoverItem == null ? void 0 : onHoverItem(index);
    setLocalIndex(index);
  };
  const doubleClickItemHandler = (index) => {
    onDoubleClickItem == null ? void 0 : onDoubleClickItem(index);
    setLocalIndex(index);
  };
  return /* @__PURE__ */ React.createElement(ThumbnailCarouselStyled, {
    className: `ThumbnailCarousel ${className}`,
    itemWidth: slideWidth,
    itemHeight: slideHeight,
    width,
    height,
    gap,
    vertical
  }, showNavigationButtons ? /* @__PURE__ */ React.createElement(Clickable, {
    className: "ThumbnailCarousel__action",
    onClick: () => clickHandler("previous")
  }, previousIcon) : null, /* @__PURE__ */ React.createElement("ul", {
    className: "ThumbnailCarousel__slider",
    ref: sliderRef
  }, images == null ? void 0 : images.map((image, index) => {
    return /* @__PURE__ */ React.createElement("li", {
      onClick: () => clickItemHandler(index),
      onMouseEnter: () => mouseEnterItemHandler(index),
      onDoubleClick: () => doubleClickItemHandler(index),
      className: `ThumbnailCarousel__slider-item ${slideClassName} ${localIndex === index && activeSlideClassName ? activeSlideClassName : ""}`,
      key: index
    }, /* @__PURE__ */ React.createElement(Image$1, {
      draggable: false,
      image,
      width: slideWidth,
      height: slideHeight,
      config: __spreadValues$1({
        width: cdnWidth,
        height: cdnHeight
      }, imageConfig),
      alt: `slide-${index}`
    }));
  })), showNavigationButtons ? /* @__PURE__ */ React.createElement(Clickable, {
    className: "ThumbnailCarousel__action",
    onClick: () => clickHandler("next")
  }, nextIcon) : null);
};
ThumbnailCarousel.defaultProps = {
  className: "",
  slideClassName: "",
  activeSlideClassName: "",
  showNavigationButtons: true,
  gap: 8
};

const ZoomImageStyled = styled.div`
  position: relative;
  z-index: 1;
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;

  .ZoomImage {
    &__lens {
      position: absolute;
      z-index: 2;
      width: ${(props) => {
  var _a, _b;
  return (_b = (_a = props.lensConfig) == null ? void 0 : _a.width) != null ? _b : 200;
}}px;
      height: ${(props) => {
  var _a, _b;
  return (_b = (_a = props.lensConfig) == null ? void 0 : _a.height) != null ? _b : 200;
}}px;
      border: 2px solid #000000;
      cursor: ${(props) => props.isLoading ? "initial" : "none"};
      background-repeat: no-repeat;
      display: none;
      opacity: ${(props) => props.isLoading ? 0 : 1};
    }

    &__image {
      border-radius: 0;
    }
  }
`;

const getCursorPosition = (event, element) => {
  const elementBounds = element.getBoundingClientRect();
  const { left: elementLeftBound, top: elementTopBound } = elementBounds;
  const { pageX, pageY } = event;
  const cursorPosition = {
    x: pageX - elementLeftBound - window.pageXOffset,
    y: pageY - elementTopBound - window.pageYOffset
  };
  return cursorPosition;
};
const defaultLensConfig = {
  zoom: 2,
  width: 200,
  height: 200
};

const throttle = (fn, delay) => {
  let lastCall = 0;
  return function(...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
};

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const ZoomImage = (props) => {
  const _a = props, {
    className,
    lensConfig = defaultLensConfig,
    config,
    width = config == null ? void 0 : config.width,
    height = config == null ? void 0 : config.height
  } = _a, rest = __objRest(_a, [
    "className",
    "lensConfig",
    "config",
    "width",
    "height"
  ]);
  const { zoom = 2 } = lensConfig;
  const [error, setError] = useState(false);
  const lensRef = useRef(null);
  const imageRef = useRef(null);
  const [imgLoading, setImgLoading] = useState(true);
  const imgLoadHandler = (event) => {
    var _a2;
    setImgLoading(false);
    (_a2 = rest == null ? void 0 : rest.onLoad) == null ? void 0 : _a2.call(rest, event);
  };
  const moveLens = useCallback((event) => {
    const lens = lensRef.current;
    const image = imageRef.current;
    if (!lens || !image || error)
      return;
    const cursorPosition = getCursorPosition(event, image);
    const { x: cursorXPosition, y: cursorYPosition } = cursorPosition;
    let newCursorXPosition = cursorXPosition;
    let newCursorYPosition = cursorYPosition;
    let positionLeft = newCursorXPosition - lens.offsetWidth / 2;
    let positionTop = newCursorYPosition - lens.offsetHeight / 2;
    if (positionLeft < 0)
      positionLeft = 0;
    if (positionTop < 0)
      positionTop = 0;
    if (positionLeft > image.width - lens.offsetWidth) {
      positionLeft = image.width - lens.offsetWidth;
    }
    if (positionTop > image.height - lens.offsetHeight) {
      positionTop = image.height - lens.offsetHeight;
    }
    lens.style.left = `${positionLeft}px`;
    lens.style.top = `${positionTop}px`;
    if (cursorXPosition >= image.width - lens.offsetWidth / 2) {
      newCursorXPosition = image.width - lens.offsetWidth / 2;
    }
    if (cursorYPosition > image.height - lens.offsetHeight / 2) {
      newCursorYPosition = image.height - lens.offsetHeight / 2;
    }
    lens.style.backgroundPositionX = `-${newCursorXPosition * zoom}px`;
    lens.style.backgroundPositionY = `-${newCursorYPosition * zoom}px`;
  }, [error, zoom]);
  const zoomImage = () => {
    const lens = lensRef.current;
    const image = imageRef.current;
    if (!lens || !image || error)
      return;
    const { src, width: width2, height: height2 } = image;
    lens.style.backgroundImage = `url(${src})`;
    const backgroundSize = `${width2 * zoom}px ${height2 * zoom}px`;
    lens.style.backgroundSize = backgroundSize;
    lens.style.display = "initial";
  };
  const hideLens = () => {
    if (!lensRef.current)
      return;
    lensRef.current.style.display = "none";
  };
  const mouseOverHandler = () => {
    zoomImage();
  };
  const mouseLeaveHandler = () => {
    hideLens();
  };
  const errorHandler = useCallback(() => {
    hideLens();
    setError(true);
  }, []);
  useEffect(() => {
    const lens = lensRef.current;
    const image = imageRef.current;
    const moveLensThrottled = throttle(moveLens, 25);
    lens == null ? void 0 : lens.addEventListener("mousemove", moveLensThrottled);
    image == null ? void 0 : image.addEventListener("mousemove", moveLensThrottled);
    image == null ? void 0 : image.addEventListener("touchmove", moveLensThrottled);
    image == null ? void 0 : image.addEventListener("error", errorHandler);
    return () => {
      lens == null ? void 0 : lens.removeEventListener("mousemove", moveLensThrottled);
      image == null ? void 0 : image.removeEventListener("mousemove", moveLensThrottled);
      image == null ? void 0 : image.removeEventListener("touchmove", moveLensThrottled);
      image == null ? void 0 : image.removeEventListener("error", errorHandler);
    };
  }, [errorHandler, moveLens]);
  if (!config)
    return null;
  return /* @__PURE__ */ React.createElement(ZoomImageStyled, {
    className: `ZoomImage ${className}`,
    lensConfig,
    width,
    height,
    isLoading: imgLoading,
    onMouseOver: mouseOverHandler,
    onMouseLeave: mouseLeaveHandler,
    onTouchEnd: mouseLeaveHandler
  }, /* @__PURE__ */ React.createElement("div", {
    ref: lensRef,
    className: "ZoomImage__lens"
  }), /* @__PURE__ */ React.createElement(Image$1, __spreadValues({
    ref: imageRef,
    className: "ZoomImage__image",
    config,
    width,
    height,
    onLoad: imgLoadHandler
  }, rest)));
};
ZoomImage.defaultProps = {
  className: "",
  lensConfig: defaultLensConfig
};

export { Accordion as A, Breadcrumb as B, CardForm as C, DateInput as D, EmailInput as E, ShareButton as F, Slider as G, StaticMap as H, Image$1 as I, Switch as J, TextInput as K, LightboxImage as L, MaxWidth as M, NotificationsContainer$1 as N, ThumbnailCarousel as O, PasswordInput as P, formatByCurrency as Q, ShoppingCartSummary as S, TabsMenu as T, WebPlugin as W, ZoomImage as Z, Backdrop as a, Badge as b, Button$1 as c, ButtonGroup as d, Carousel as e, CommentBox as f, Counter as g, DateOfBirthInput as h, DocumentInput as i, DocumentTypeSelect as j, Drawer as k, Dropdown as l, Clickable as m, InfiniteScroll as n, Modal as o, Notification$1 as p, PhoneNumberInput as q, renderIssuer as r, Pocket as s, ProductCard as t, useCounter as u, useProductCard as v, ScrollableTabs as w, ScrollToTop as x, SearchBar as y, useSearchBar as z };
//# sourceMappingURL=index-7eb519da.js.map
